import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const FONT_LINK = `@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=DM+Serif+Display:ital@0;1&display=swap');`;

const GLOBAL_CSS = `
${FONT_LINK}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --ink:            #F0FAFA;
  --ink2:           #FFFFFF;
  --ink3:           #E8F7F6;
  --ink4:           #DDF2F0;
  --ink5:           #D0EDEB;

  --teal:           #0AADA1;
  --teal2:          #0EA89C;
  --teal3:          #0C9189;
  --teal-dim:       rgba(10,173,161,0.14);
  --teal-subtle:    rgba(10,173,161,0.08);
  --teal-subtle2:   rgba(10,173,161,0.14);
  --teal-glow:      rgba(10,173,161,0.22);

  --amber:          #E8920A;
  --amber2:         #D4820A;
  --amber-dim:      rgba(232,146,10,0.14);
  --amber-subtle:   rgba(232,146,10,0.08);

  --coral:          #FF6B6B;

  --text-primary:   #0D2B28;
  --text-muted:     rgba(15,50,45,0.62);
  --text-muted2:    rgba(15,50,45,0.38);

  --border:         rgba(10,173,161,0.14);
  --border2:        rgba(10,173,161,0.10);
  --border-teal:    rgba(10,173,161,0.22);
  --border-amber:   rgba(232,146,10,0.22);

  --surface:        #FFFFFF;
  --surface2:       #F5FDFC;
  --surface3:       #EBF9F8;

  --serif:  'DM Serif Display', serif;
  --sans:   'Outfit', sans-serif;
}

html { scroll-behavior: smooth; overflow-x: hidden; }
body { background: var(--ink); color: var(--text-primary); font-family: var(--sans); overflow-x: hidden; }
::-webkit-scrollbar { width: 4px; }
::-webkit-scrollbar-track { background: var(--ink3); }
::-webkit-scrollbar-thumb { background: rgba(10,173,161,0.35); border-radius: 2px; }

/* REVEALS */
.reveal { opacity: 0; transition: opacity 0.75s cubic-bezier(0.22,1,0.36,1), transform 0.75s cubic-bezier(0.22,1,0.36,1); }
.reveal.from-up    { transform: translateY(36px); }
.reveal.from-down  { transform: translateY(-24px); }
.reveal.from-left  { transform: translateX(-44px); }
.reveal.from-right { transform: translateX(44px); }
.reveal.d1{transition-delay:0.05s;} .reveal.d2{transition-delay:0.13s;} .reveal.d3{transition-delay:0.21s;}
.reveal.d4{transition-delay:0.29s;} .reveal.d5{transition-delay:0.37s;} .reveal.d6{transition-delay:0.45s;}
.reveal.visible { opacity: 1; transform: none; }

/* NAV */
.lp-nav { position:fixed; top:0; left:0; right:0; z-index:200; padding:1.4rem 3rem; transition:all 0.4s; }
.lp-nav.scrolled { background:rgba(240,250,250,0.96); backdrop-filter:blur(20px); border-bottom:0.5px solid var(--border); padding:0.9rem 3rem; box-shadow:0 4px 32px rgba(10,173,161,0.06); }
.lp-nav-inner { max-width:1180px; margin:0 auto; display:flex; align-items:center; justify-content:space-between; }
.lp-logo { display:flex; align-items:center; gap:10px; font-family:var(--sans); font-size:15px; font-weight:700; color:var(--text-primary); text-decoration:none; flex-shrink:0; }
.lp-logo em { color:var(--teal); font-style:normal; }
.lp-nav-links { display:flex; align-items:center; gap:2.5rem; }
.lp-nav-link { font-size:13.5px; font-weight:500; color:var(--text-muted); text-decoration:none; transition:color 0.2s; }
.lp-nav-link:hover { color:var(--teal); }
.lp-nav-cta { padding:10px 22px; background:linear-gradient(135deg,var(--teal3),var(--teal)); color:#fff; border:none; border-radius:10px; font-family:var(--sans); font-size:13px; font-weight:700; cursor:pointer; position:relative; overflow:hidden; transition:transform 0.15s,box-shadow 0.15s; white-space:nowrap; }
.lp-nav-cta::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.18),transparent 60%); pointer-events:none; }
.lp-nav-cta:hover { transform:translateY(-1px); box-shadow:0 6px 20px var(--teal-glow); }

/* HAMBURGER */
.lp-hamburger { display:none; flex-direction:column; justify-content:center; align-items:center; gap:5px; width:40px; height:40px; border-radius:10px; border:0.5px solid var(--border-teal); background:var(--teal-subtle); cursor:pointer; flex-shrink:0; transition:background 0.2s; }
.lp-hamburger:hover { background:var(--teal-subtle2); }
.lp-hamburger span { display:block; width:18px; height:1.5px; background:var(--teal); border-radius:2px; transition:all 0.3s cubic-bezier(0.22,1,0.36,1); transform-origin:center; }
.lp-hamburger.open span:nth-child(1){transform:translateY(6.5px) rotate(45deg);}
.lp-hamburger.open span:nth-child(2){opacity:0;transform:scaleX(0);}
.lp-hamburger.open span:nth-child(3){transform:translateY(-6.5px) rotate(-45deg);}

/* MOBILE MENU */
.lp-mobile-menu { position:fixed; top:0; left:0; right:0; bottom:0; z-index:210; display:flex; flex-direction:column; pointer-events:none; }
.lp-mobile-menu-backdrop { position:absolute; inset:0; background:rgba(5,10,20,0.6); backdrop-filter:blur(6px); opacity:0; transition:opacity 0.35s ease; }
.lp-mobile-menu.open .lp-mobile-menu-backdrop { opacity:1; pointer-events:all; }
.lp-mobile-menu-panel { position:absolute; top:0; right:0; width:min(320px,85vw); height:100%; background:#FFFFFF; border-left:0.5px solid var(--border-teal); display:flex; flex-direction:column; transform:translateX(100%); transition:transform 0.4s cubic-bezier(0.22,1,0.36,1); box-shadow:-20px 0 60px rgba(10,173,161,0.08); pointer-events:none; overflow:hidden; }
.lp-mobile-menu.open .lp-mobile-menu-panel { transform:translateX(0); pointer-events:all; }
.lp-mobile-menu-header { display:flex; align-items:center; justify-content:space-between; padding:1.25rem 1.5rem; border-bottom:0.5px solid var(--border2); }
.lp-mobile-close { width:36px; height:36px; border-radius:9px; border:0.5px solid var(--border2); background:var(--surface2); display:flex; align-items:center; justify-content:center; cursor:pointer; color:var(--text-muted); font-size:16px; transition:background 0.2s; }
.lp-mobile-close:hover { background:var(--surface3); color:var(--text-primary); }
.lp-mobile-links { flex:1; display:flex; flex-direction:column; padding:1.5rem; gap:0.25rem; }
.lp-mobile-link { display:flex; align-items:center; gap:12px; padding:1rem 1.25rem; border-radius:14px; font-family:var(--sans); font-size:15px; font-weight:600; color:var(--text-muted); text-decoration:none; border:0.5px solid transparent; opacity:0; transform:translateX(20px); transition:background 0.2s,color 0.2s,border-color 0.2s,opacity 0.35s ease,transform 0.35s cubic-bezier(0.22,1,0.36,1); }
.lp-mobile-link:nth-child(1){transition-delay:0.06s;} .lp-mobile-link:nth-child(2){transition-delay:0.11s;} .lp-mobile-link:nth-child(3){transition-delay:0.16s;}
.lp-mobile-menu.open .lp-mobile-link { opacity:1; transform:translateX(0); }
.lp-mobile-link:hover { background:var(--surface3); color:var(--teal); border-color:var(--border-teal); }
.lp-mobile-link-icon { font-size:16px; width:20px; text-align:center; }
.lp-mobile-divider { height:0.5px; background:linear-gradient(90deg,transparent,var(--border-teal),transparent); margin:0.5rem 1.5rem; }
.lp-mobile-menu-footer { padding:1.5rem; border-top:0.5px solid var(--border2); display:flex; flex-direction:column; gap:0.75rem; opacity:0; transform:translateY(12px); transition:opacity 0.35s ease 0.22s,transform 0.35s ease 0.22s; }
.lp-mobile-menu.open .lp-mobile-menu-footer { opacity:1; transform:translateY(0); }
.lp-mobile-cta { width:100%; padding:14px; background:linear-gradient(135deg,var(--teal3),var(--teal)); color:#fff; border:none; border-radius:12px; font-family:var(--sans); font-size:14px; font-weight:700; cursor:pointer; position:relative; overflow:hidden; }
.lp-mobile-cta::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.15),transparent 60%); pointer-events:none; }
.lp-mobile-tagline { text-align:center; font-size:11px; font-weight:500; color:var(--text-muted2); letter-spacing:0.5px; }
.lp-mobile-cluster { display:none; align-items:center; gap:10px; }

/* HERO */
.lp-hero-wrap { position:relative; overflow:hidden; background:var(--ink); }
.lp-hero-mesh { position:absolute; inset:0; pointer-events:none; overflow:hidden; }
.lp-hero-mesh::before { content:''; position:absolute; width:900px; height:900px; border-radius:50%; background:radial-gradient(circle,rgba(10,173,161,0.10) 0%,rgba(10,173,161,0.04) 40%,transparent 70%); top:-300px; left:-200px; }
.lp-hero-mesh::after { content:''; position:absolute; width:700px; height:700px; border-radius:50%; background:radial-gradient(circle,rgba(232,146,10,0.07) 0%,transparent 65%); top:-100px; right:-150px; }
.lp-hero-mesh-bottom { position:absolute; width:500px; height:500px; border-radius:50%; background:radial-gradient(circle,rgba(13,189,176,0.05) 0%,transparent 70%); bottom:-200px; right:20%; pointer-events:none; }
.lp-hero-grid { position:absolute; inset:0; background-image:radial-gradient(circle,rgba(10,173,161,0.18) 1px,transparent 1px); background-size:48px 48px; opacity:0.4; pointer-events:none; }

.lp-hero { min-height:100vh; display:grid; grid-template-columns:1fr 1fr; align-items:center; gap:4rem; max-width:1180px; margin:0 auto; padding:7rem 3rem 4rem; position:relative; z-index:2; }

/* Hero eyebrow */
.lp-hero-eyebrow { display:inline-flex; align-items:center; gap:8px; padding:7px 16px; background:rgba(13,189,176,0.08); border:0.5px solid rgba(13,189,176,0.3); border-radius:100px; font-size:11.5px; font-weight:700; color:var(--teal); letter-spacing:1px; text-transform:uppercase; margin-bottom:2rem; }
.lp-hero-eyebrow-dot { width:6px; height:6px; border-radius:50%; background:var(--teal); animation:pulse-teal 2s ease-in-out infinite; }
@keyframes pulse-teal { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:0.3;transform:scale(0.65);} }

/* Hero title */
.lp-hero-title { font-family:var(--sans); font-size:clamp(3rem,5.5vw,5.2rem); font-weight:800; line-height:1.05; letter-spacing:-1.5px; color:var(--text-primary); margin-bottom:1.75rem; }
.lp-hero-title .gradient-line { display:block; background:linear-gradient(100deg,#0DBDB0 0%,#0DBDB0 35%,#F5A623 80%,#FF9A3C 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }

.lp-hero-sub { font-size:16px; font-weight:400; color:var(--text-muted); line-height:1.85; max-width:480px; margin-bottom:1rem; }
.lp-hero-ctas { display:flex; gap:1rem; flex-wrap:wrap; }

/* BUTTONS */
.lp-btn-primary { padding:17px 34px; background:linear-gradient(135deg,var(--teal3),var(--teal)); color:#fff; border:none; border-radius:14px; font-family:var(--sans); font-size:14px; font-weight:800; letter-spacing:0.3px; cursor:pointer; position:relative; overflow:hidden; transition:transform 0.2s,box-shadow 0.2s; display:flex; align-items:center; gap:8px; white-space:nowrap; }
.lp-btn-primary::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.18),transparent 55%); pointer-events:none; }
.lp-btn-primary:hover { transform:translateY(-2px); box-shadow:0 10px 32px var(--teal-glow); }
.lp-btn-ghost { padding:17px 28px; background:rgba(13,189,176,0.06); color:var(--text-muted); border:0.5px solid rgba(13,189,176,0.2); border-radius:14px; font-family:var(--sans); font-size:14px; font-weight:600; cursor:pointer; text-decoration:none; display:inline-flex; align-items:center; gap:8px; transition:border-color 0.2s,color 0.2s,background 0.2s; white-space:nowrap; }
.lp-btn-ghost:hover { border-color:var(--border-teal); color:var(--teal); background:var(--teal-subtle2); }

/* STATS */
.lp-hero-stats { display:flex; gap:2.5rem; padding-bottom:1.5rem; margin-bottom:1.5rem; }
.lp-stat { position:relative; }
.lp-stat-num { font-family:var(--sans); font-size:2.4rem; font-weight:800; color:var(--teal); line-height:1; letter-spacing:-1px; }
.lp-stat-num.counting { animation:stat-shimmer 0.08s ease infinite alternate; }
@keyframes stat-shimmer { from{opacity:0.6;} to{opacity:1;} }
.lp-stat-label { font-size:11px; font-weight:500; color:var(--text-muted2); letter-spacing:0.4px; margin-top:5px; }
.lp-stat-bar { height:2px; background:rgba(13,189,176,0.12); margin-top:8px; border-radius:1px; overflow:hidden; }
.lp-stat-bar-fill { height:100%; background:linear-gradient(90deg,var(--teal),var(--amber)); border-radius:1px; width:0%; transition:width 0.05s linear; }

/* SCAN SCENE */
.lp-hero-right { position:relative; display:flex; align-items:center; justify-content:center; z-index:2; }
.scan-scene { position:relative; width:380px; height:480px; }
.scan-ripple { position:absolute; right:-10px; top:50px; width:60px; height:60px; border-radius:50%; border:1.5px solid rgba(13,189,176,0); z-index:10; pointer-events:none; }
.scan-ripple.r1{animation:ripple-out 2.5s ease-out infinite;animation-delay:3.2s;}
.scan-ripple.r2{animation:ripple-out 2.5s ease-out infinite;animation-delay:3.6s;}
.scan-ripple.r3{animation:ripple-out 2.5s ease-out infinite;animation-delay:4.0s;}
@keyframes ripple-out { 0%{transform:scale(1);border-color:rgba(13,189,176,0.5);opacity:1;} 100%{transform:scale(3.5);border-color:rgba(13,189,176,0);opacity:0;} }
.scan-success { position:absolute; right:8px; top:38px; background:linear-gradient(135deg,var(--teal3),var(--teal)); color:var(--ink); border-radius:100px; padding:5px 12px 5px 9px; font-family:var(--sans); font-size:9.5px; font-weight:700; letter-spacing:0.3px; display:flex; align-items:center; gap:6px; z-index:12; white-space:nowrap; opacity:0; animation:success-pop 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards; animation-delay:3.1s; box-shadow:0 4px 18px var(--teal-glow); }
.scan-success-dot { width:6px; height:6px; border-radius:50%; background:rgba(11,18,33,0.4); }
@keyframes success-pop { 0%{opacity:0;transform:scale(0.5) translateY(6px);} 100%{opacity:1;transform:scale(1) translateY(0);} }
.phone-scanner { position:absolute; left:0; top:60px; width:155px; height:270px; background:#fff; border-radius:22px; border:1px solid rgba(10,173,161,0.2); overflow:hidden; box-shadow:0 24px 60px rgba(10,173,161,0.12); z-index:3; animation:phone-a-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both; animation-delay:0.3s; }
@keyframes phone-a-intro { from{opacity:0;transform:translateX(-30px) rotate(-6deg);} to{opacity:1;transform:translateX(0) rotate(-6deg);} }
.phone-scanner-screen { width:100%; height:100%; background:var(--ink3); display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; padding:12px; }
.scanner-viewfinder { width:100px; height:100px; border-radius:10px; position:relative; background:rgba(13,189,176,0.05); }
.scanner-viewfinder::before,.scanner-viewfinder::after { content:''; position:absolute; width:20px; height:20px; border-color:var(--teal); border-style:solid; }
.scanner-viewfinder::before { top:0;left:0;border-width:2px 0 0 2px;border-radius:6px 0 0 0; }
.scanner-viewfinder::after { bottom:0;right:0;border-width:0 2px 2px 0;border-radius:0 0 6px 0; }
.vf-corner-tr { position:absolute;top:0;right:0;width:20px;height:20px;border-top:2px solid var(--teal);border-right:2px solid var(--teal);border-radius:0 6px 0 0; }
.vf-corner-bl { position:absolute;bottom:0;left:0;width:20px;height:20px;border-bottom:2px solid var(--teal);border-left:2px solid var(--teal);border-radius:0 0 0 6px; }
.scan-laser { position:absolute;left:4px;right:4px;height:2px;background:linear-gradient(90deg,transparent,var(--teal),transparent);border-radius:1px;top:4px;animation:laser-sweep 2s ease-in-out infinite;animation-delay:1.2s; }
@keyframes laser-sweep { 0%{top:8px;opacity:0;} 10%{opacity:1;} 90%{opacity:1;} 100%{top:88px;opacity:0;} }
.scanner-success-flash { position:absolute; inset:0; background:rgba(13,189,176,0.12); opacity:0; animation:flash-once 0.4s ease forwards; animation-delay:3.0s; pointer-events:none; z-index:10; }
@keyframes flash-once { 0%{opacity:0;} 40%{opacity:1;} 100%{opacity:0;} }
.scanner-label { font-size:9px;font-weight:600;color:var(--text-muted2);letter-spacing:1px;text-align:center;text-transform:uppercase; }
.mini-qr { position:absolute;inset:10px;display:grid;grid-template-columns:repeat(6,1fr);grid-template-rows:repeat(6,1fr);gap:1.5px;opacity:0.6; }
.mini-qr-cell { border-radius:1px; }
.phone-profile { position:absolute;right:0;top:0;width:175px;height:310px;background:#fff;border-radius:24px;border:1px solid rgba(10,173,161,0.22);overflow:hidden;box-shadow:0 32px 80px rgba(10,173,161,0.10),0 0 0 1px rgba(10,173,161,0.06);z-index:4;animation:phone-b-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:0.5s; }
@keyframes phone-b-intro { from{opacity:0;transform:translateY(30px) rotate(4deg);} to{opacity:1;transform:translateY(0) rotate(4deg);} }
.profile-card-wrap { width:100%;height:100%;animation:card-reveal 0.7s cubic-bezier(0.22,1,0.36,1) both;animation-delay:2s; }
@keyframes card-reveal { from{transform:translateY(40px);opacity:0;} to{transform:translateY(0);opacity:1;} }
@keyframes ai-scan { 0%{transform:translateX(-100%);} 100%{transform:translateX(100%);} }
@keyframes ai-pulse { 0%,100%{opacity:0.4;transform:translateY(0);} 50%{opacity:1;transform:translateY(4px);} }
@keyframes ai-field-in { from{opacity:0;transform:translateX(8px);} to{opacity:1;transform:translateX(0);} }
.profile-banner { width:100%;height:110px;background:linear-gradient(160deg,#d4f5f2,#c8f0ed);position:relative;overflow:hidden; }
.profile-banner-img { width:100%;height:100%;background:linear-gradient(160deg,rgba(10,173,161,0.18) 0%,rgba(10,173,161,0.08) 50%,rgba(232,146,10,0.10) 100%); }
.profile-banner-fade { position:absolute;bottom:0;left:0;right:0;height:50px;background:linear-gradient(transparent,#fff); }
.profile-avatar-ring { position:absolute;bottom:2px;left:14px;width:44px;height:44px;border-radius:50%;border:2px solid #fff;background:var(--ink3);overflow:hidden;box-shadow:0 0 0 1.5px rgba(10,173,161,0.4);animation:avatar-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:2.4s; }
@keyframes avatar-pop { from{transform:scale(0.3);opacity:0;} to{transform:scale(1);opacity:1;} }
.profile-avatar-inner { width:100%;height:100%;background:linear-gradient(145deg,var(--teal3),var(--teal));display:flex;align-items:center;justify-content:center;font-family:var(--sans);font-size:13px;font-weight:700;color:var(--ink);letter-spacing:1px; }
.profile-info { padding:28px 14px 12px; }
.profile-info-name { font-family:var(--sans);font-size:15px;font-weight:700;color:var(--text-primary);line-height:1.2;margin-bottom:2px; }
.profile-info-name em { font-style:normal;color:var(--teal); }
.profile-info-role { font-size:8px;font-weight:600;color:var(--text-muted2);letter-spacing:0.3px;margin-bottom:10px; }
.profile-tiles { display:grid;grid-template-columns:1fr 1fr 1fr;gap:5px;margin-bottom:8px; }
.profile-tile { background:rgba(10,173,161,0.06);border:0.5px solid rgba(10,173,161,0.14);border-radius:8px;padding:7px 6px;display:flex;flex-direction:column;gap:4px;animation:tile-slide 0.3s ease both; }
.profile-tile:nth-child(1){animation-delay:2.5s;} .profile-tile:nth-child(2){animation-delay:2.65s;} .profile-tile:nth-child(3){animation-delay:2.8s;}
@keyframes tile-slide { from{opacity:0;transform:translateY(6px);} to{opacity:1;transform:translateY(0);} }
.tile-dot { width:12px;height:12px;border-radius:50%;background:rgba(13,189,176,0.12);display:flex;align-items:center;justify-content:center; }
.tile-dot-inner { width:5px;height:5px;border-radius:50%;background:var(--teal); }
.tile-text { font-size:7px;color:var(--text-muted2);line-height:1.3;font-weight:500; }
.profile-save-btn { width:100%;padding:8px;background:linear-gradient(135deg,var(--teal3),var(--teal));color:#fff;border:none;border-radius:8px;font-size:8px;font-weight:700;letter-spacing:0.5px;text-transform:uppercase;cursor:default;display:flex;align-items:center;justify-content:center;gap:4px;animation:tile-slide 0.3s ease both;animation-delay:2.95s; }
.beam-line { position:absolute;top:165px;left:148px;width:90px;height:1px;background:linear-gradient(90deg,var(--teal),rgba(13,189,176,0));transform-origin:left center;animation:beam-appear 0.4s ease both;animation-delay:1.5s;z-index:5; }
@keyframes beam-appear { from{opacity:0;} to{opacity:1;} }
.beam-particle { position:absolute;top:160px;left:148px;width:6px;height:6px;border-radius:50%;background:var(--teal);animation:particle-travel 2s ease-in-out infinite;animation-delay:1.6s;z-index:6; }
@keyframes particle-travel { 0%{left:148px;opacity:0;transform:scale(0.5);} 10%{opacity:1;transform:scale(1);} 90%{opacity:1;} 100%{left:228px;opacity:0;transform:scale(0.5);} }
.hero-orb { position:absolute;width:500px;height:500px;border-radius:50%;background:radial-gradient(circle,rgba(13,189,176,0.04) 0%,transparent 65%);top:-100px;right:-100px;pointer-events:none;z-index:0; }

/* DIVIDER */
.lp-rule { height:0.5px; background:linear-gradient(90deg,transparent,rgba(13,189,176,0.18),transparent); }

/* TRUST BAR */
.lp-trust { padding:1.5rem 2rem; background:#fff; display:flex; flex-direction:column; align-items:center; gap:1rem; }
.lp-trust-label { font-size:10px;font-weight:600;color:var(--text-muted2);letter-spacing:2.5px;text-transform:uppercase; }
.lp-trust-items { display:flex;align-items:stretch;justify-content:center;flex-wrap:nowrap;width:100%;max-width:580px; }
.lp-trust-item { display:flex;flex-direction:column;align-items:center;gap:5px;flex:1;padding:0.6rem 0.75rem;border-right:0.5px solid var(--border2);font-size:11.5px;font-weight:600;color:var(--text-muted);text-align:center; }
.lp-trust-item:last-child { border-right:none; }
.lp-trust-icon { font-size:1.2rem;line-height:1;width:36px;height:36px;border-radius:10px;background:var(--teal-subtle);border:0.5px solid var(--border-teal);display:flex;align-items:center;justify-content:center;margin-bottom:2px; }

/* SECTIONS */
.lp-section { padding:5rem 3rem; position:relative; }
.lp-section-inner { max-width:1180px; margin:0 auto; }
.lp-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--teal);margin-bottom:1.25rem;text-align:center; }
.lp-section-title { font-family:var(--sans);font-size:clamp(2.2rem,4vw,3.4rem);font-weight:800;line-height:1.08;letter-spacing:-1px;color:var(--text-primary);text-align:center;margin-bottom:1.25rem; }
.lp-section-title em { font-style:italic;font-family:var(--serif);font-weight:400;color:var(--teal); }
.lp-section-sub { font-size:15px;font-weight:400;color:var(--text-muted);text-align:center;max-width:520px;margin:0 auto 4rem;line-height:1.9; }

/* HOW IT WORKS */
.lp-how-timeline { display:grid;grid-template-columns:1fr 72px 1fr 72px 1fr;align-items:start;gap:0;margin-top:1rem; }
.lp-how-bridge { display:flex;align-items:flex-start;justify-content:center;padding-top:2.6rem; }
.how-arrow-path { stroke:rgba(13,189,176,0.15);stroke-dasharray:60;stroke-dashoffset:60;transition:stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1),stroke 0.4s ease; }
.how-arrow-path.filled { stroke-dashoffset:0;stroke:var(--teal); }
.lp-how-card { background:#fff;border:0.5px solid var(--border2);border-radius:20px;padding:2.2rem 1.8rem 2rem;position:relative;overflow:hidden;transition:border-color 0.3s,transform 0.3s,box-shadow 0.3s;cursor:default; }
.lp-how-card::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 80% 60% at 50% -10%,var(--teal-subtle2) 0%,transparent 70%);opacity:0;transition:opacity 0.3s; }
.lp-how-card:hover { border-color:var(--border-teal);transform:translateY(-5px);box-shadow:0 12px 32px rgba(13,189,176,0.08); }
.lp-how-card:hover::before { opacity:1; }
.lp-how-num { font-family:var(--sans);font-size:7rem;font-weight:800;color:rgba(13,189,176,0.05);line-height:1;position:absolute;bottom:-0.5rem;right:0.8rem;letter-spacing:-3px;pointer-events:none;user-select:none; }
.lp-how-icon-ring { width:56px;height:56px;border-radius:16px;background:var(--teal-subtle);border:1px solid var(--border-teal);display:flex;align-items:center;justify-content:center;font-size:1.6rem;margin-bottom:1.5rem;position:relative; }
.lp-how-icon-ring::after { content:'';position:absolute;inset:-4px;border-radius:20px;border:1px dashed var(--border-teal);animation:spin-slow 12s linear infinite; }
@keyframes spin-slow { to{transform:rotate(360deg);} }
.lp-how-step-label { font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:var(--teal);margin-bottom:0.6rem; }
.lp-how-title { font-family:var(--sans);font-size:1.55rem;font-weight:700;color:var(--text-primary);line-height:1.15;margin-bottom:0.75rem; }
.lp-how-desc { font-size:13.5px;color:var(--text-muted);line-height:1.8; }

/* FEATURES */
.lp-feat-bento { display:grid;grid-template-columns:repeat(4,1fr);grid-template-rows:auto auto;gap:10px;margin-top:1rem; }
.lp-feat-bcard { background:#fff;border:0.5px solid var(--border2);border-radius:18px;padding:1.8rem 1.6rem;position:relative;overflow:hidden;transition:border-color 0.3s,transform 0.25s,background 0.3s,box-shadow 0.3s;cursor:default; }
.lp-feat-bcard::before { content:'';position:absolute;top:0;left:-100%;width:60%;height:100%;background:linear-gradient(105deg,transparent,rgba(13,189,176,0.04),transparent);transition:left 0s;pointer-events:none;z-index:1; }
.lp-feat-bcard:hover::before { left:160%;transition:left 0.6s ease; }
.lp-feat-bcard:hover { border-color:var(--border-teal);background:var(--surface2);transform:translateY(-3px);box-shadow:0 8px 24px rgba(10,173,161,0.08); }
.lp-feat-bcard.hero-card { grid-column:span 2;background:var(--surface2);border-color:var(--border-teal);padding:2.4rem 2.2rem; }
.lp-feat-bcard.tall { grid-row:span 2;display:flex;flex-direction:column; }
.lp-feat-bcard::after { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:transparent;transition:background 0.3s; }
.lp-feat-bcard:hover::after,.lp-feat-bcard.hero-card::after { background:linear-gradient(90deg,transparent,var(--border-teal),transparent); }
.lp-feat-bicon { width:44px;height:44px;border-radius:12px;background:var(--teal-subtle);border:0.5px solid var(--border-teal);display:flex;align-items:center;justify-content:center;margin-bottom:1.25rem;flex-shrink:0; }
.lp-feat-bcard.hero-card .lp-feat-bicon { width:52px;height:52px;border-radius:14px; }
.lp-feat-btag { display:inline-block;font-size:8.5px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--teal);margin-bottom:0.5rem;opacity:0.7; }
.lp-feat-btitle { font-family:var(--sans);font-size:1.2rem;font-weight:700;color:var(--text-primary);line-height:1.15;margin-bottom:0.5rem; }
.lp-feat-bcard.hero-card .lp-feat-btitle { font-size:1.55rem; }
.lp-feat-bdesc { font-size:13px;color:var(--text-muted);line-height:1.8;flex:1; }
.lp-feat-wallet { display:flex;gap:6px;margin-top:1.25rem; }
.lp-wallet-chip { height:26px;border-radius:6px;flex:1;background:var(--teal-subtle);border:0.5px solid var(--border-teal);display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:600;color:var(--teal);letter-spacing:0.5px;position:relative;overflow:hidden; }
.lp-wallet-chip::after { content:'';position:absolute;top:0;left:-100%;width:50%;height:100%;background:linear-gradient(90deg,transparent,rgba(13,189,176,0.15),transparent);animation:chip-shine 3s ease-in-out infinite; }
.lp-wallet-chip:nth-child(2)::after{animation-delay:0.4s;} .lp-wallet-chip:nth-child(3)::after{animation-delay:0.8s;}
@keyframes chip-shine { 0%,70%{left:-100%;} 100%{left:200%;} }
.lp-feat-socials { display:flex;flex-wrap:wrap;gap:6px;margin-top:1.1rem; }
.lp-social-pill { padding:4px 10px;background:var(--teal-subtle);border:0.5px solid var(--border-teal);border-radius:100px;font-size:10px;font-weight:500;color:var(--text-muted);opacity:0;transform:translateY(6px) scale(0.94);transition:opacity 0.35s ease,transform 0.35s ease,background 0.2s,color 0.2s; }
.lp-social-pill:hover { background:var(--teal-subtle2);color:var(--teal); }
.lp-social-pill:nth-child(1){transition-delay:0.05s;} .lp-social-pill:nth-child(2){transition-delay:0.12s;} .lp-social-pill:nth-child(3){transition-delay:0.19s;} .lp-social-pill:nth-child(4){transition-delay:0.26s;} .lp-social-pill:nth-child(5){transition-delay:0.33s;}
.lp-feat-bcard.pills-visible .lp-social-pill { opacity:1;transform:translateY(0) scale(1); }

/* EDIT ANYTIME VISUAL */
.edit-timeline { display:flex; flex-direction:column; gap:10px; margin-top:auto; padding-top:1.5rem; }
.edit-row { display:flex; align-items:center; gap:10px; padding:8px 10px; border-radius:10px; background:rgba(13,189,176,0.06); border:0.5px solid rgba(13,189,176,0.1); position:relative; overflow:hidden; }
.edit-row::after { content:''; position:absolute; left:0; top:0; height:100%; background:linear-gradient(90deg,rgba(13,189,176,0.12),rgba(13,189,176,0.06)); border-radius:10px; transition:width 0.5s ease; }
.edit-row.synced::after { width:100%; }
.edit-row:not(.synced)::after { width:0%; }
.edit-row-dot { width:7px; height:7px; border-radius:50%; flex-shrink:0; position:relative; z-index:1; }
.edit-row-label { font-size:9px; font-weight:600; color:var(--text-muted2); text-transform:uppercase; letter-spacing:0.8px; flex:1; position:relative; z-index:1; }
.edit-row-badge { font-size:8px; font-weight:700; padding:2px 7px; border-radius:100px; position:relative; z-index:1; }
.edit-row-badge.live { background:rgba(13,189,176,0.15); color:var(--teal); }
.edit-row-badge.updated { background:rgba(245,166,35,0.15); color:var(--amber); }
.edit-pencil-icon { display:flex; align-items:center; justify-content:center; margin-bottom:1.25rem; }

/* USE CASES */
.lp-use-tabs { display:flex;flex-wrap:wrap;gap:0.5rem;justify-content:center;margin-bottom:3rem; }
.lp-use-tab { padding:8px 18px;border-radius:100px;font-size:13px;font-weight:600;cursor:pointer;transition:all 0.2s;font-family:var(--sans);display:flex;align-items:center;gap:6px; }
.lp-use-panel { display:grid;grid-template-columns:1fr 1fr;gap:3rem;align-items:center;background:#fff;border:0.5px solid var(--border2);border-radius:24px;padding:3rem;position:relative;overflow:hidden; }
.lp-use-panel::before { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,var(--border-teal),transparent); }
.lp-use-badge { display:inline-flex;align-items:center;gap:8px;padding:6px 14px;border-radius:100px;margin-bottom:1.5rem;font-size:12px;font-weight:700;background:var(--teal-subtle);color:var(--teal);border:0.5px solid var(--border-teal); }
.lp-use-tagline { font-family:var(--sans);font-size:2.1rem;font-weight:800;color:var(--text-primary);line-height:1.1;margin-bottom:1rem;letter-spacing:-0.5px; }
.lp-use-desc { font-size:14px;font-weight:400;color:var(--text-muted);line-height:1.9;margin-bottom:2rem; }
.lp-use-content { animation:tab-fade 0.35s cubic-bezier(0.22,1,0.36,1) both; }
@keyframes tab-fade { from{opacity:0;transform:translateY(10px);} to{opacity:1;transform:translateY(0);} }
.lp-use-highlights { display:flex;flex-direction:column;gap:0.75rem; }
.lp-use-hl { display:flex;align-items:center;gap:12px;background:var(--surface3);border:0.5px solid var(--border2);border-radius:12px;padding:1rem 1.25rem;animation:hl-slide 0.3s ease both; }
.lp-use-hl:nth-child(1){animation-delay:0.05s;} .lp-use-hl:nth-child(2){animation-delay:0.12s;} .lp-use-hl:nth-child(3){animation-delay:0.19s;} .lp-use-hl:nth-child(4){animation-delay:0.26s;}
@keyframes hl-slide { from{opacity:0;transform:translateX(12px);} to{opacity:1;transform:translateX(0);} }
.lp-use-hl-check { width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;background:var(--teal-subtle);color:var(--teal); }
.lp-use-hl-text { font-size:13px;font-weight:500;color:var(--text-muted); }

/* PRICING */
.lp-pricing-cta { padding:5rem 3rem;position:relative;overflow:hidden;background:var(--ink3); }
.lp-pricing-cta::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 70% 80% at 50% 50%,rgba(13,189,176,0.05) 0%,transparent 70%);pointer-events:none; }
.lp-pricing-cta-inner { max-width:1180px;margin:0 auto;display:grid;grid-template-columns:1fr 1.1fr;gap:4rem;align-items:center;position:relative; }
.lp-pricing-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--teal);margin-bottom:1.25rem; }
.lp-pricing-headline { font-family:var(--sans);font-size:clamp(2.8rem,5vw,4.2rem);font-weight:800;line-height:1.05;color:var(--text-primary);margin-bottom:1.25rem;letter-spacing:-1px; }
.lp-pricing-headline em { font-style:italic;font-family:var(--serif);font-weight:400;color:var(--amber); }
.lp-pricing-sub { font-size:15px;color:var(--text-muted);line-height:1.9;margin-bottom:2.5rem;max-width:400px; }
.lp-pricing-perks { display:flex;flex-direction:column;gap:0.6rem;margin-bottom:2.5rem; }
.lp-pricing-perk { display:flex;align-items:center;gap:10px;font-size:14px;font-weight:500;color:var(--text-muted); }
.lp-perk-tick { width:20px;height:20px;border-radius:50%;flex-shrink:0;background:var(--teal-subtle);border:0.5px solid var(--border-teal);display:flex;align-items:center;justify-content:center;font-size:9px;color:var(--teal);font-weight:700; }
.lp-price-card { background:var(--ink2);border:0.5px solid var(--border-teal);border-radius:24px;padding:2.5rem;position:relative;overflow:hidden;box-shadow:0 16px 48px rgba(13,189,176,0.07); }
.lp-price-card::before { content:'';position:absolute;top:0;left:10%;right:10%;height:1px;background:linear-gradient(90deg,transparent,var(--teal),transparent); }
.lp-price-card-top { display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:1.5rem; }
.lp-price-badge { display:inline-block;background:linear-gradient(135deg,var(--teal3),var(--teal));color:var(--ink);border-radius:100px;padding:5px 14px;font-size:10px;font-weight:700;letter-spacing:0.5px;text-transform:uppercase; }
.lp-price-amount { text-align:right; }
.lp-price-num { font-family:var(--sans);font-size:4rem;font-weight:800;color:var(--teal);line-height:1;letter-spacing:-2px; }
.lp-price-period { font-size:10px;font-weight:600;color:var(--text-muted2);letter-spacing:2px;text-transform:uppercase;margin-top:4px; }
.lp-price-divider { height:0.5px;background:var(--border2);margin:1.25rem 0; }
.lp-price-list { list-style:none;display:flex;flex-direction:column;gap:0;margin-bottom:1.75rem; }
.lp-price-item { display:flex;align-items:center;gap:10px;padding:0.55rem 0;font-size:13px;font-weight:500;color:var(--text-muted);border-bottom:0.5px solid var(--border2); }
.lp-price-item:last-child { border-bottom:none; }
.lp-price-check { color:var(--teal);font-size:11px;flex-shrink:0; }

/* PLAN TOGGLE */
.lp-plan-toggle-wrap { display:flex;justify-content:center;margin-bottom:1.25rem; }
.lp-plan-toggle { display:inline-flex;background:var(--surface3);border:0.5px solid var(--border-teal);border-radius:100px;padding:4px;gap:2px; }
.lp-plan-toggle-btn { padding:7px 18px;border:none;border-radius:100px;font-family:var(--sans);font-size:13px;font-weight:600;cursor:pointer;background:transparent;color:var(--text-muted);transition:all 0.2s;display:flex;align-items:center;gap:7px; }
.lp-plan-toggle-btn.active { background:var(--teal);color:#fff; }
.lp-plan-save-pill { background:rgba(232,146,10,0.15);color:var(--amber);border-radius:100px;padding:2px 8px;font-size:10px;font-weight:700; }

/* PLAN CARDS */
.lp-plan-cards { display:grid;grid-template-columns:1fr 1.2fr;gap:1rem;align-items:stretch; }
.lp-plan-card { border-radius:20px;padding:1.5rem;display:flex;flex-direction:column; }
.lp-plan-card-free { background:var(--surface3);border:0.5px solid var(--border); }
.lp-plan-card-premium { background:linear-gradient(145deg,#0D2018,#0B1A14);border:0.5px solid rgba(10,173,161,0.3);position:relative;overflow:hidden;box-shadow:0 16px 48px rgba(10,173,161,0.14); }
.lp-plan-premium-beam { position:absolute;top:0;left:12%;right:12%;height:1px;background:linear-gradient(90deg,transparent,var(--teal),transparent); }
.lp-plan-top-row { display:flex;justify-content:space-between;align-items:center;margin-bottom:0.75rem; }
.lp-plan-card-label { font-size:10px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:var(--text-muted2);margin-bottom:0.75rem; }
.lp-plan-card-label-prem { font-size:10px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:var(--teal); }
.lp-plan-hot-badge { background:linear-gradient(135deg,var(--teal3),var(--teal));color:#fff;border-radius:100px;padding:3px 10px;font-size:9px;font-weight:700;letter-spacing:0.5px;text-transform:uppercase; }
.lp-plan-price-num { font-family:var(--sans);font-size:2.6rem;font-weight:800;color:var(--text-primary);line-height:1;letter-spacing:-1px;margin-bottom:3px; }
.lp-plan-price-period { font-size:10px;font-weight:600;color:var(--text-muted2);letter-spacing:1.5px;text-transform:uppercase;margin-bottom:0.75rem; }
.lp-plan-price-num-prem { font-family:var(--sans);font-size:2.6rem;font-weight:800;color:var(--teal);line-height:1;letter-spacing:-1px;margin-bottom:3px; }
.lp-plan-price-period-prem { font-size:10px;font-weight:600;color:rgba(10,173,161,0.5);letter-spacing:1.5px;text-transform:uppercase;margin-bottom:0.5rem; }
.lp-plan-save-tag { display:inline-block;background:rgba(232,146,10,0.15);color:var(--amber);border-radius:100px;padding:2px 10px;font-size:10px;font-weight:700;margin-bottom:0.5rem; }
.lp-plan-divider { height:0.5px;background:var(--border2);margin:0.75rem 0; }
.lp-plan-divider-prem { height:0.5px;background:rgba(10,173,161,0.15);margin:0.75rem 0; }
.lp-plan-list { list-style:none;display:flex;flex-direction:column;margin-bottom:1.25rem;flex:1; }
.lp-plan-list li { display:flex;align-items:center;gap:8px;padding:0.42rem 0;font-size:12px;font-weight:500;color:var(--text-muted);border-bottom:0.5px solid var(--border2); }
.lp-plan-list li:last-child { border-bottom:none; }
.lp-plan-list-prem li { color:rgba(210,245,240,0.7);border-bottom-color:rgba(10,173,161,0.1); }
.lp-plan-check { color:var(--teal);font-size:10px;flex-shrink:0; }
.lp-plan-btn-free { width:100%;padding:11px;background:var(--surface);border:1px solid var(--border-teal);border-radius:12px;font-family:var(--sans);font-size:13px;font-weight:700;color:var(--teal);cursor:pointer;transition:all 0.2s;margin-top:auto; }
.lp-plan-btn-free:hover { background:var(--teal-subtle);transform:translateY(-1px); }

/* TESTIMONIALS */
.lp-testi-grid { display:grid;grid-template-columns:repeat(3,1fr);gap:1.25rem;margin-top:3rem; }
.lp-testi-card { background:var(--ink2);border:0.5px solid var(--border2);border-radius:20px;padding:1.75rem;display:flex;flex-direction:column;gap:1rem;position:relative;overflow:hidden;transition:border-color 0.25s,box-shadow 0.25s; }
.lp-testi-card:hover { border-color:var(--border-teal);box-shadow:0 8px 32px rgba(10,173,161,0.07); }
.lp-testi-card::before { content:'"';position:absolute;top:1rem;right:1.5rem;font-family:var(--serif);font-size:5rem;line-height:1;color:var(--teal);opacity:0.08;pointer-events:none; }
.lp-testi-stars { display:flex;gap:3px; }
.lp-testi-star { font-size:13px;color:var(--amber); }
.lp-testi-quote { font-size:14px;line-height:1.75;color:var(--text-muted);flex:1; }
.lp-testi-author { display:flex;align-items:center;gap:12px; }
.lp-testi-avatar { width:40px;height:40px;border-radius:50%;background:linear-gradient(135deg,var(--teal3),var(--teal));display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:#fff;flex-shrink:0;letter-spacing:0.5px; }
.lp-testi-name { font-size:13px;font-weight:700;color:var(--text-primary);line-height:1.3; }
.lp-testi-role { font-size:11px;color:var(--text-muted2);font-weight:500; }

/* FOOTER */
.lp-footer { padding:3rem 3rem 4rem;border-top:0.5px solid var(--border2);background:var(--ink2); }
.lp-footer-inner { max-width:1180px;margin:0 auto;display:flex;flex-direction:column;align-items:center;gap:1rem; }
.lp-footer-logo { display:flex;align-items:center;gap:10px;font-family:var(--sans);font-size:15px;font-weight:700;color:var(--text-primary); }
.lp-footer-logo em { color:var(--teal);font-style:normal; }
.lp-footer-tagline { font-size:13px;color:var(--text-muted2); }
.lp-footer-copy { font-size:12px;color:var(--text-muted2);opacity:0.6; }

/* RESPONSIVE */
@media (min-width:901px) {
  .lp-trust{flex-direction:row;padding:1.5rem 3rem;gap:2.5rem;}
  .lp-trust-items{width:auto;max-width:none;}
  .lp-trust-item{flex-direction:row;flex:none;padding:0 1.5rem;border-right:0.5px solid var(--border2);border-bottom:none;gap:8px;}
  .lp-trust-item:last-child{border-right:none;}
  .lp-trust-icon{width:28px;height:28px;border-radius:8px;font-size:1rem;flex-shrink:0;margin-bottom:0;}
}
@media (max-width:900px) {
  .lp-hero{grid-template-columns:1fr;text-align:center;padding:8rem 1.5rem 4rem;}
  .lp-hero-sub{margin:0 auto 1rem;}
  .lp-hero-ctas{justify-content:center;}
  .lp-hero-stats{justify-content:center;}
  .lp-hero-right{margin-top:3rem;}
  .scan-scene{width:300px;height:380px;transform:scale(0.85);}
  .lp-how-timeline{grid-template-columns:1fr;}
  .lp-how-bridge{display:none;}
  .lp-feat-bento{grid-template-columns:1fr 1fr;}
  .lp-feat-bcard.hero-card{grid-column:span 2;}
  .lp-feat-bcard.tall{grid-row:span 1;}
  .ai-section-grid{grid-template-columns:1fr !important;}
  .lp-use-panel{grid-template-columns:1fr;}
  .lp-pricing-cta-inner{grid-template-columns:1fr;}
  .lp-plan-cards{grid-template-columns:1fr;}
  .lp-nav{padding:1rem 1.5rem;}
  .lp-nav.scrolled{padding:0.75rem 1.5rem;}
  .lp-section{padding:4rem 1.5rem;}
  .lp-pricing-cta{padding:4rem 1.5rem;}
  .lp-hamburger{display:flex;}
  .lp-nav-links{display:none;}
  .lp-mobile-cluster{display:flex;}
  .lp-testi-grid{grid-template-columns:1fr 1fr;}
}
@media (max-width:600px) {
  .lp-feat-bento{grid-template-columns:1fr;}
  .lp-feat-bcard.hero-card{grid-column:span 1;}
  .ai-section-grid{grid-template-columns:1fr !important;}
  .lp-hero-title{font-size:clamp(2.5rem,9vw,3.4rem);}
  .lp-hero-stats{gap:1.5rem;}
  .lp-stat-num{font-size:2rem;}
  .lp-section{padding:3.5rem 1.25rem;}
  .lp-section-title{font-size:clamp(2rem,7vw,2.7rem);}
  .lp-use-panel{padding:1.75rem 1.25rem;}
  .lp-pricing-cta{padding:3rem 1.25rem;}
  .lp-pricing-headline{font-size:clamp(2.4rem,8vw,3rem);}
  .lp-hero-ctas{flex-direction:column;align-items:center;}
  .lp-hero-ctas .lp-btn-primary,.lp-hero-ctas .lp-btn-ghost{width:100%;justify-content:center;}
  .lp-testi-grid{grid-template-columns:1fr;}
}
`;

/* ── Dual-tone SVG icons ── */
function IconWallet() {
    return (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <rect x="2" y="6" width="18" height="13" rx="3" stroke="#0DBDB0" strokeWidth="1.5" />
            <path d="M2 10h18" stroke="#0DBDB0" strokeWidth="1.5" />
            <path d="M6 2l10 0" stroke="#F5A623" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="15.5" cy="14.5" r="1.5" fill="#F5A623" />
        </svg>
    );
}
function IconQR() {
    return (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <rect x="2" y="2" width="7" height="7" rx="1.5" stroke="#0DBDB0" strokeWidth="1.5" />
            <rect x="13" y="2" width="7" height="7" rx="1.5" stroke="#0DBDB0" strokeWidth="1.5" />
            <rect x="2" y="13" width="7" height="7" rx="1.5" stroke="#0DBDB0" strokeWidth="1.5" />
            <rect x="4" y="4" width="3" height="3" rx="0.5" fill="#F5A623" />
            <rect x="15" y="4" width="3" height="3" rx="0.5" fill="#F5A623" />
            <rect x="4" y="15" width="3" height="3" rx="0.5" fill="#F5A623" />
            <path d="M13 13h3v3" stroke="#0DBDB0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M16 19h3" stroke="#0DBDB0" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M19 13v3" stroke="#0DBDB0" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    );
}
function IconPhoto() {
    return (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <rect x="2" y="4" width="18" height="14" rx="3" stroke="#0DBDB0" strokeWidth="1.5" />
            <circle cx="8" cy="10" r="2.5" stroke="#F5A623" strokeWidth="1.5" />
            <path d="M2 16l4-4 3 3 4-5 7 7" stroke="#0DBDB0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
function IconContact() {
    return (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path d="M11 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" stroke="#0DBDB0" strokeWidth="1.5" />
            <path d="M3 19c0-4 3.6-7 8-7s8 3 8 7" stroke="#0DBDB0" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M15 5h5M15 8h5" stroke="#F5A623" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
    );
}
function IconGlobe() {
    return (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <circle cx="11" cy="11" r="9" stroke="#0DBDB0" strokeWidth="1.5" />
            <path d="M11 2c-3 3-3 6 0 9s3 6 0 9" stroke="#F5A623" strokeWidth="1.3" strokeLinecap="round" />
            <path d="M2 11h18" stroke="#0DBDB0" strokeWidth="1.3" strokeLinecap="round" />
            <path d="M3.5 7h15M3.5 15h15" stroke="#0DBDB0" strokeWidth="1" strokeLinecap="round" opacity="0.4" />
        </svg>
    );
}
function IconLink() {
    return (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path d="M9 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="#0DBDB0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M13 9a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="#F5A623" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
function IconResume() {
    return (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <rect x="4" y="2" width="14" height="18" rx="2.5" stroke="#0DBDB0" strokeWidth="1.5" />
            <path d="M8 7h6M8 11h6M8 15h4" stroke="#F5A623" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
    );
}
function IconAnalytics() {
    return (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <rect x="3" y="13" width="3" height="6" rx="1" fill="#0DBDB0" opacity="0.5" />
            <rect x="8" y="9" width="3" height="10" rx="1" fill="#0DBDB0" opacity="0.7" />
            <rect x="13" y="5" width="3" height="14" rx="1" fill="#0DBDB0" />
            <path d="M4 8l4-3 4 2 4-4" stroke="#F5A623" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="16" cy="3" r="1.2" fill="#F5A623" />
        </svg>
    );
}
function IconAI() {
    return (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <circle cx="11" cy="11" r="8" stroke="#0DBDB0" strokeWidth="1.5" />
            <path d="M7 11c0-2.2 1.8-4 4-4s4 1.8 4 4-1.8 4-4 4" stroke="#F5A623" strokeWidth="1.3" strokeLinecap="round" />
            <circle cx="11" cy="11" r="1.5" fill="#0DBDB0" />
            <path d="M11 4V2M11 20v-2M4 11H2M20 11h-2" stroke="#0DBDB0" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
    );
}
function IconEdit() {
    return (
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path d="M14.5 3.5a2.121 2.121 0 0 1 3 3L7 17l-4 1 1-4L14.5 3.5z" stroke="#0DBDB0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M12 6l3 3" stroke="#F5A623" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
    );
}

/* ── Edit Anytime animated visual ── */
function EditAnytimeVisual() {
    const rows = [
        { label: "Phone Number", badge: "Live", dot: "#0DBDB0", synced: true },
        { label: "Job Title", badge: "Updated", dot: "#F5A623", synced: true },
        { label: "LinkedIn URL", badge: "Live", dot: "#0DBDB0", synced: true },
        { label: "Resume PDF", badge: "Updated", dot: "#F5A623", synced: false },
    ];

    return (
        <div style={{ marginTop: "auto", paddingTop: "1.4rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="7" stroke="#0DBDB0" strokeWidth="1.2" />
                    <path d="M5 8l2 2 4-4" stroke="#F5A623" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{ fontSize: 9, fontWeight: 700, color: "var(--teal)", letterSpacing: "1px", textTransform: "uppercase" }}>
                    Same link · always fresh
                </span>
            </div>
            {rows.map((r, i) => (
                <div key={i} className={`edit-row${r.synced ? " synced" : ""}`}
                    style={{ animationDelay: `${i * 0.15}s`, marginBottom: 7 }}>
                    <div className="edit-row-dot" style={{ background: r.dot, boxShadow: `0 0 6px ${r.dot}66` }} />
                    <span className="edit-row-label">{r.label}</span>
                    <span className={`edit-row-badge ${r.badge === "Live" ? "live" : "updated"}`}>{r.badge}</span>
                </div>
            ))}
            <div style={{ marginTop: 10, height: 2, borderRadius: 2, background: "linear-gradient(90deg, var(--teal), var(--amber))", opacity: 0.3 }} />
            <div style={{ marginTop: 6, fontSize: 9, color: "var(--text-muted2)", fontWeight: 500 }}>
                Your QR code & link never change — only the content updates
            </div>
        </div>
    );
}

/* COUNT-UP HOOK */
function useCountUp(target, duration = 1800, suffix = "", started = false) {
    const [display, setDisplay] = useState("0");
    const [progress, setProgress] = useState(0);
    const frameRef = useRef(null);
    useEffect(() => {
        if (!started) return;
        const num = parseFloat(target.replace(/[^0-9.]/g, ""));
        if (!num) { setDisplay(target); setProgress(100); return; }
        const startTime = performance.now();
        const animate = (now) => {
            const pct = Math.min((now - startTime) / duration, 1);
            const eased = pct === 1 ? 1 : 1 - Math.pow(2, -10 * pct);
            setDisplay((Number.isInteger(num) ? Math.round(num * eased).toString() : (num * eased).toFixed(0)) + suffix);
            setProgress(Math.round(pct * 100));
            if (pct < 1) frameRef.current = requestAnimationFrame(animate);
        };
        frameRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(frameRef.current);
    }, [started, target, duration, suffix]);
    return { display, progress };
}

function StatItem({ value, suffix, label, started, delay = 0 }) {
    const [go, setGo] = useState(false);
    useEffect(() => {
        if (!started) return;
        const t = setTimeout(() => setGo(true), delay);
        return () => clearTimeout(t);
    }, [started, delay]);
    const { display, progress } = useCountUp(value, 1600, suffix, go);
    return (
        <div className="lp-stat">
            <div className={`lp-stat-num${go && progress < 100 ? " counting" : ""}`}>{value === "0" ? "0" : display}</div>
            <div className="lp-stat-label">{label}</div>
            <div className="lp-stat-bar"><div className="lp-stat-bar-fill" style={{ width: `${progress}%` }} /></div>
        </div>
    );
}

const QR_PATTERN = [[1, 1, 1, 0, 1, 0], [1, 0, 1, 1, 0, 1], [1, 1, 1, 0, 1, 1], [0, 1, 0, 1, 0, 0], [1, 0, 1, 1, 1, 0], [0, 1, 0, 0, 1, 1]];

function ScanScene() {
    return (
        <div className="scan-scene">
            <div className="hero-orb" />
            <div className="phone-scanner">
                <div className="phone-scanner-screen">
                    <div className="scanner-viewfinder">
                        <div className="vf-corner-tr" /><div className="vf-corner-bl" />
                        <div className="scan-laser" />
                        <div className="mini-qr">
                            {QR_PATTERN.map((row, ri) => row.map((cell, ci) => (
                                <div key={`${ri}-${ci}`} className="mini-qr-cell"
                                    style={{ background: cell ? "rgba(13,189,176,0.5)" : "transparent" }} />
                            )))}
                        </div>
                        <div className="scanner-success-flash" />
                    </div>
                    <div className="scanner-label">Point at QR code</div>
                </div>
            </div>
            <div className="beam-line" /><div className="beam-particle" />
            <div className="scan-ripple r1" /><div className="scan-ripple r2" /><div className="scan-ripple r3" />
            <div className="scan-success"><div className="scan-success-dot" />Contact Saved ✓</div>
            <div className="phone-profile">
                <div className="profile-card-wrap">
                    <div className="profile-banner">
                        <div className="profile-banner-img" /><div className="profile-banner-fade" />
                        <div className="profile-avatar-ring"><div className="profile-avatar-inner">UN</div></div>
                    </div>
                    <div className="profile-info">
                        <div className="profile-info-name">User <em>Name</em></div>
                        <div className="profile-info-role">Director · Company Name</div>
                        <div className="profile-tiles">
                            {[["📞", "Phone"], ["✉️", "Email"], ["📍", "Delhi"]].map(([, l]) => (
                                <div className="profile-tile" key={l}>
                                    <div className="tile-dot"><div className="tile-dot-inner" /></div>
                                    <div className="tile-text">{l}</div>
                                </div>
                            ))}
                        </div>
                        <button className="profile-save-btn">↓ Save Contact</button>
                        <div className="profile-tiles" style={{ marginTop: "1rem" }}>
                            {[["🌐", "Website"], ["📄", "Resume"], ["ⓕ", "Facebook"]].map(([, l]) => (
                                <div className="profile-tile" key={l}>
                                    <div className="tile-dot"><div className="tile-dot-inner" /></div>
                                    <div className="tile-text">{l}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ArrowBridge({ filled }) {
    return (
        <div className="lp-how-bridge">
            <svg width="36" height="28" viewBox="0 0 36 28" fill="none" overflow="visible">
                <path d="M2 14 L26 14" className={`how-arrow-path${filled ? " filled" : ""}`} strokeWidth="1.5" strokeLinecap="round" fill="none"
                    style={{ strokeDasharray: 28, strokeDashoffset: filled ? 0 : 28, transition: "stroke-dashoffset 0.8s cubic-bezier(0.22,1,0.36,1),stroke 0.4s ease" }} />
                <path d="M20 7 L28 14 L20 21" className={`how-arrow-path${filled ? " filled" : ""}`} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"
                    style={{ strokeDasharray: 24, strokeDashoffset: filled ? 0 : 24, transition: "stroke-dashoffset 0.6s cubic-bezier(0.22,1,0.36,1) 0.3s,stroke 0.4s ease 0.3s" }} />
            </svg>
        </div>
    );
}

function LogoSVG({ size = 34 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 34 34" fill="none">
            <rect width="34" height="34" rx="9" fill="rgba(13,189,176,0.10)" />
            <rect width="34" height="34" rx="9" stroke="rgba(13,189,176,0.30)" strokeWidth="0.5" />
            <circle cx="15" cy="13" r="5" stroke="#0DBDB0" strokeWidth="1.5" fill="none" />
            <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="#0DBDB0" strokeWidth="1.5" strokeLinecap="round" fill="none" />
            <path d="M23 11 L27 11 L27 23" stroke="#F5A623" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            <circle cx="27" cy="23" r="1.8" fill="#F5A623" />
        </svg>
    );
}

const USE_CASES = [
    { icon: "👔", role: "Job Seekers", tagline: "Land your next opportunity faster", desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one elegant scan.", highlights: ["Resume attached & downloadable", "LinkedIn & GitHub links", "QR code for instant sharing", "Save contact in one tap"] },
    { icon: "💼", role: "Freelancers", tagline: "Win clients before you even speak", desc: "Share everything a client needs — portfolio, social proof, and contact — all in a single professional link.", highlights: ["Portfolio & website link", "WhatsApp quick contact", "Instagram & social proof", "Professional digital presence"] },
    { icon: "🏢", role: "Business Owners", tagline: "Network smarter, not harder", desc: "Stop spending on printed cards that get lost. Share via QR at events — always updated, never outdated.", highlights: ["Always up-to-date details", "Google Wallet for offline use", "QR code for events & meetups", "Share via WhatsApp or email"] },
    { icon: "🎓", role: "Students", tagline: "Build your professional identity early", desc: "Stand out in campus placements and internship drives. Show your skills, projects, and social profiles.", highlights: ["GitHub & project links", "Resume upload for placements", "LinkedIn profile showcase", "Free forever — no cost"] },
    { icon: "📣", role: "Sales Pros", tagline: "Close the follow-up gap instantly", desc: "At trade shows or cold visits — one QR scan and your prospect has everything they need.", highlights: ["Instant QR share at events", "Website & product links", "Save contact in one tap", "Google Wallet for quick access"] },
    { icon: "🧑‍💻", role: "Developers", tagline: "Your entire dev profile in one link", desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share at hackathons.", highlights: ["GitHub & portfolio links", "Twitter / X profile", "Resume for job applications", "Professional dev identity"] },
];

function UseCasesSection({ navigate }) {
    const [active, setActive] = useState(0);
    const [tabKey, setTabKey] = useState(0);
    const cur = USE_CASES[active];
    const switchTab = (i) => { setActive(i); setTabKey(k => k + 1); };
    return (
        <section className="lp-section">
            <div className="lp-section-inner">
                <span className="lp-eyebrow reveal from-up">Who is it for</span>
                <h2 className="lp-section-title reveal from-up d1">Built for every <em>professional</em></h2>
                <p className="lp-section-sub reveal from-up d2">Whether you're a student or a CEO — your digital card works for you.</p>
                <div className="lp-use-tabs reveal from-up d3">
                    {USE_CASES.map((u, i) => (
                        <button key={i} className="lp-use-tab" onClick={() => switchTab(i)}
                            style={{ border: active === i ? "0.5px solid var(--border-teal)" : "0.5px solid var(--border2)", background: active === i ? "var(--teal-subtle2)" : "transparent", color: active === i ? "var(--teal)" : "var(--text-muted)" }}>
                            {u.icon} {u.role}
                        </button>
                    ))}
                </div>
                <div className="lp-use-panel reveal from-up d4">
                    <div key={`l-${tabKey}`} className="lp-use-content">
                        <div className="lp-use-badge"><span>{cur.icon}</span><span style={{ fontSize: "12px" }}>{cur.role}</span></div>
                        <div className="lp-use-tagline">{cur.tagline}</div>
                        <p className="lp-use-desc">{cur.desc}</p>
                        <button className="lp-btn-primary" onClick={() => navigate("/animation/card")}>Create My Card — Free →</button>
                    </div>
                    <div key={`r-${tabKey}`} className="lp-use-highlights">
                        {cur.highlights.map((h, i) => (
                            <div className="lp-use-hl" key={i} style={{ animationDelay: `${0.05 + i * 0.07}s` }}>
                                <div className="lp-use-hl-check">✓</div>
                                <span className="lp-use-hl-text">{h}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

const TESTIMONIALS = [
    { initials: "RS", name: "Shabd Singh", role: "Sales Manager, Delhi", quote: "I used to hand out 50 paper cards at every trade show. Now I just show my QR code and everyone has my details instantly. Game changer." },
    { initials: "PS", name: "Priya Sharma", role: "Freelance Designer, Mumbai", quote: "Clients can see my portfolio, Instagram, and WhatsApp all in one tap. I've closed 3 projects just from sharing my digital card in a WhatsApp group." },
    { initials: "AM", name: "Arjun Mehta", role: "Software Developer, Bangalore", quote: "Shared my card at a hackathon and got 12 LinkedIn connections in one day. The Google Wallet pass is a great touch — feels very premium." },
    { initials: "SP", name: "Shreya Patel", role: "MBA Student, Ahmedabad", quote: "Campus placement season was so much easier. Instead of printing 200 cards, I just shared one link. My resume is right there too." },
    { initials: "VK", name: "Vivek Kumar", role: "Business Owner, Hyderabad", quote: "I update my phone number and it reflects everywhere instantly. No reprinting, no waste. My whole team uses it now." },
    { initials: "NR", name: "Neha Reddy", role: "HR Recruiter, Pune", quote: "The analytics showed me 40+ people viewed my card after a networking event. I had no idea a free tool could give me this kind of insight." },
];

function TestimonialsSection() {
    return (
        <section className="lp-section">
            <div className="lp-section-inner">
                <span className="lp-eyebrow reveal from-up">Testimonials</span>
                <h2 className="lp-section-title reveal from-up d1">Loved by <em>professionals</em></h2>
                <p className="lp-section-sub reveal from-up d2">Real people, real results. Here's what they say.</p>
                <div className="lp-testi-grid">
                    {TESTIMONIALS.map((t, i) => (
                        <div key={i} className={`lp-testi-card reveal from-up d${(i % 3) + 1}`}>
                            <div className="lp-testi-stars">{[1, 2, 3, 4, 5].map(s => <span key={s} className="lp-testi-star">★</span>)}</div>
                            <p className="lp-testi-quote">"{t.quote}"</p>
                            <div className="lp-testi-author">
                                <div className="lp-testi-avatar">{t.initials}</div>
                                <div>
                                    <div className="lp-testi-name">{t.name}</div>
                                    <div className="lp-testi-role">{t.role}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

const SERVER = "https://myprofilecorner.com/api";

function PricingPlans() {
    const navigate = useNavigate();
    const [billingPeriod, setBillingPeriod] = useState("yearly");
    return (
        <>
            <div className="lp-plan-toggle-wrap reveal from-right d1">
                <div className="lp-plan-toggle">
                    <button
                        className={`lp-plan-toggle-btn${billingPeriod === "monthly" ? " active" : ""}`}
                        onClick={() => setBillingPeriod("monthly")}
                    >Monthly</button>
                    <button
                        className={`lp-plan-toggle-btn${billingPeriod === "yearly" ? " active" : ""}`}
                        onClick={() => setBillingPeriod("yearly")}
                    >Yearly <span className="lp-plan-save-pill">Save ₹289</span></button>
                </div>
            </div>
            <div className="lp-plan-cards reveal from-right d2">
                <div className="lp-plan-card lp-plan-card-free">
                    <div className="lp-plan-card-label">Free</div>
                    <div className="lp-plan-price-num">₹0</div>
                    <div className="lp-plan-price-period">Forever</div>
                    <div className="lp-plan-divider" />
                    <ul className="lp-plan-list">
                        {["1 Digital Card", "QR Code & Link", "Photo Upload", "Resume / CV", "Social Links", "Google Wallet", "5 Free Themes"].map(f => (
                            <li key={f}><span className="lp-plan-check">✓</span>{f}</li>
                        ))}
                    </ul>
                    <button className="lp-plan-btn-free" onClick={() => navigate("/animation/card")}>Get Started</button>
                </div>
                <div className="lp-plan-card lp-plan-card-premium">
                    <div className="lp-plan-premium-beam" />
                    <div className="lp-plan-top-row">
                        <div className="lp-plan-card-label-prem">Premium</div>
                        <div className="lp-plan-hot-badge">Best Value</div>
                    </div>
                    <div className="lp-plan-price-num-prem">
                        {billingPeriod === "yearly" ? "₹299" : "₹49"}
                    </div>
                    <div className="lp-plan-price-period-prem">
                        {billingPeriod === "yearly" ? "per year" : "per month"}
                    </div>
                    <div className="lp-plan-save-tag" style={{ visibility: billingPeriod === "yearly" ? "visible" : "hidden" }}>Save ₹289 vs monthly</div>
                    <div className="lp-plan-divider-prem" />
                    <ul className="lp-plan-list lp-plan-list-prem">
                        {["Everything in Free", "40+ Premium Themes", "Promo Video on Avatar", "Priority Support"].map(f => (
                            <li key={f}><span className="lp-plan-check">✦</span>{f}</li>
                        ))}
                    </ul>
                    <button
                        className="lp-btn-primary"
                        style={{ width: "100%", justifyContent: "center", marginTop: "auto" }}
                        onClick={() => navigate("/animation/card")}
                    >Unlock Premium →</button>
                </div>
            </div>
        </>
    );
}

export default function LandingPage() {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [statsStarted, setStatsStarted] = useState(false);
    const [howVisible, setHowVisible] = useState(false);
    const [liveStats, setLiveStats] = useState({ cards: null, views: null });
    const statsRef = useRef(null);
    const howRef = useRef(null);

    useEffect(() => {
        fetch(`${SERVER}/stats`)
            .then(r => r.json())
            .then(d => setLiveStats({ cards: d.cards || 0, views: d.views || 0 }))
            .catch(() => { });
    }, []);

    useEffect(() => {
        const onResize = () => { if (window.innerWidth > 900) setMenuOpen(false); };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const obs = [];
        const revObs = new IntersectionObserver(
            entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
            { threshold: 0.1 }
        );
        document.querySelectorAll(".reveal").forEach(el => revObs.observe(el));
        obs.push(revObs);
        if (statsRef.current) {
            const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsStarted(true); }, { threshold: 0.5 });
            o.observe(statsRef.current); obs.push(o);
        }
        if (howRef.current) {
            const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setHowVisible(true); }, { threshold: 0.3 });
            o.observe(howRef.current); obs.push(o);
        }
        const pillObs = new IntersectionObserver(
            entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("pills-visible"); }),
            { threshold: 0.4 }
        );
        document.querySelectorAll(".lp-feat-bcard.has-pills").forEach(c => pillObs.observe(c));
        obs.push(pillObs);
        return () => obs.forEach(o => o.disconnect());
    }, []);

    return (
        <div style={{ background: "var(--ink)", minHeight: "100vh" }}>
            <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

            {/* MOBILE DRAWER */}
            <div className={`lp-mobile-menu${menuOpen ? " open" : ""}`}>
                <div className="lp-mobile-menu-backdrop" onClick={() => setMenuOpen(false)} />
                <div className="lp-mobile-menu-panel">
                    <div className="lp-mobile-menu-header">
                        <a href="/" className="lp-logo" onClick={() => setMenuOpen(false)}><LogoSVG size={28} />myprofile<em>corner</em></a>
                        <button className="lp-mobile-close" onClick={() => setMenuOpen(false)}>✕</button>
                    </div>
                    <div className="lp-mobile-links">
                        <a href="#how" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">⚡</span>How it works</a>
                        <a href="#features" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">✦</span>Features</a>
                        <a href="#pricing" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">₹</span>Pricing</a>
                        <a href="/contact" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">✉️</span>Contact</a>
                    </div>
                    <div className="lp-mobile-divider" />
                    <div className="lp-mobile-menu-footer">
                        <button className="lp-mobile-cta" onClick={() => { setMenuOpen(false); navigate("/animation/card"); }}>Create My Card — Free →</button>
                        <div className="lp-mobile-tagline">100% Free · No app required · 2 min setup</div>
                    </div>
                </div>
            </div>

            {/* NAV */}
            <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
                <div className="lp-nav-inner">
                    <a href="/" className="lp-logo"><LogoSVG size={34} />myprofile<em>corner</em></a>
                    <div className="lp-nav-links">
                        <a href="#how" className="lp-nav-link">How it works</a>
                        <a href="#features" className="lp-nav-link">Features</a>
                        <a href="#pricing" className="lp-nav-link">Pricing</a>
                        <a href="/contact" className="lp-nav-link">Contact</a>
                        <button className="lp-nav-cta" onClick={() => navigate("/animation/card")}>Create Free Card</button>
                    </div>
                    <div className="lp-mobile-cluster">
                        <button className={`lp-hamburger${menuOpen ? " open" : ""}`} onClick={() => setMenuOpen(p => !p)} aria-label="Menu">
                            <span /><span /><span />
                        </button>
                    </div>
                </div>
            </nav>

            {/* HERO */}
            <div className="lp-hero-wrap">
                <div className="lp-hero-mesh" /><div className="lp-hero-mesh-bottom" /><div className="lp-hero-grid" />
                <div className="lp-hero">
                    <div className="lp-hero-left">
                        <div className="lp-hero-eyebrow reveal from-down">
                            <div className="lp-hero-eyebrow-dot" />100% Free · No App Needed
                        </div>
                        <h1 className="lp-hero-title reveal from-left">
                            Your Digital Identity,<br />
                            <span className="gradient-line">Always in Your Pocket</span>
                        </h1>
                        <p className="lp-hero-sub reveal from-left d1">
                            Replace paper visiting cards with a smart digital card. Share your profile instantly via QR code, link, or Google Wallet — no printing, no waste.
                        </p>
                        <div className="lp-hero-stats" ref={statsRef}>
                            <StatItem value="100" suffix="%" label="Free forever" started={statsStarted} delay={0} />
                            <StatItem value="2" suffix=" min" label="Setup time" started={statsStarted} delay={200} />
                            <StatItem value="0" suffix="" label="Paper waste" started={statsStarted} delay={400} />
                        </div>
                        <div className="lp-hero-ctas reveal from-up d2">
                            <button className="lp-btn-primary" onClick={() => navigate("/animation/card")}>Create My Card — It's Free →</button>
                            <a href="#how" className="lp-btn-ghost">See how it works</a>
                        </div>
                    </div>
                    <div className="lp-hero-right reveal from-right"><ScanScene /></div>
                </div>
            </div>

            <div className="lp-rule" />

            {/* TRUST BAR */}
            <div className="lp-trust">
                <span className="lp-trust-label">Works seamlessly with</span>
                <div className="lp-trust-items">
                    {[["🔥", "Firebase"], ["🪙", "Google Wallet"], ["📱", "Android & iOS"], ["🔗", "Any Browser"]].map(([icon, label]) => (
                        <div className="lp-trust-item" key={label}>
                            <div className="lp-trust-icon">{icon}</div><span>{label}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="lp-rule" />

            {/* HOW IT WORKS */}
            <section id="how" className="lp-section" ref={howRef}>
                <div className="lp-section-inner">
                    <span className="lp-eyebrow reveal from-up">How it works</span>
                    <h2 className="lp-section-title reveal from-up d1">From setup to sharing <em>in 3 steps</em></h2>
                    <p className="lp-section-sub reveal from-up d2">No app downloads. No tech skills required. Just fill, share, impress.</p>
                    <div className="lp-how-timeline">
                        <div className="lp-how-card reveal from-left d1">
                            <div className="lp-how-num">01</div>
                            <div className="lp-how-icon-ring">✏️</div>
                            <div className="lp-how-step-label">Step 01</div>
                            <div className="lp-how-title">Fill Your Profile</div>
                            <p className="lp-how-desc">Add your name, photo, contact info, social links, resume & website. Takes less than 2 minutes.</p>
                        </div>
                        <ArrowBridge filled={howVisible} />
                        <div className="lp-how-card reveal from-up d3">
                            <div className="lp-how-num">02</div>
                            <div className="lp-how-icon-ring">🎴</div>
                            <div className="lp-how-step-label">Step 02</div>
                            <div className="lp-how-title">Get Your Digital Card</div>
                            <p className="lp-how-desc">Instantly get a beautiful card with a unique QR code & shareable link — no design skills needed.</p>
                        </div>
                        <ArrowBridge filled={howVisible} />
                        <div className="lp-how-card reveal from-right d5">
                            <div className="lp-how-num">03</div>
                            <div className="lp-how-icon-ring">🚀</div>
                            <div className="lp-how-step-label">Step 03</div>
                            <div className="lp-how-title">Share Anywhere</div>
                            <p className="lp-how-desc">Show QR in meetings, add to Google Wallet, or send your link via WhatsApp, email, or LinkedIn.</p>
                        </div>
                    </div>
                </div>
            </section>

            <div className="lp-rule" />

            {/* FEATURES */}
            <section id="features" className="lp-section" style={{ background: "var(--ink3)" }}>
                <div className="lp-section-inner">
                    <span className="lp-eyebrow reveal from-up">Features</span>
                    <h2 className="lp-section-title reveal from-up d1">Everything a modern <em>professional</em> needs</h2>
                    <p className="lp-section-sub reveal from-up d2">One card. Every detail. Always up-to-date.</p>
                    <div className="lp-feat-bento">

                        {/* Hero card — Google Wallet */}
                        <div className="lp-feat-bcard hero-card reveal from-left d1">
                            <div className="lp-feat-bicon"><IconWallet /></div>
                            <div className="lp-feat-btag">Premium Feature</div>
                            <div className="lp-feat-btitle">Google Wallet Pass</div>
                            <p className="lp-feat-bdesc">Add your card to Google Wallet and access it offline — at meetings, events, or anywhere without internet.</p>
                            <div className="lp-feat-wallet">
                                <div className="lp-wallet-chip">Online</div>
                                <div className="lp-wallet-chip">Wallet</div>
                                <div className="lp-wallet-chip">Offline ✓</div>
                            </div>
                        </div>

                        {/* Tall — Edit Anytime */}
                        <div className="lp-feat-bcard tall reveal from-right d2">
                            <div className="lp-feat-bicon"><IconEdit /></div>
                            <div className="lp-feat-btag">Always Fresh</div>
                            <div className="lp-feat-btitle">Edit Anytime</div>
                            <p className="lp-feat-bdesc">Changed jobs? New number? Update instantly — your QR code & link stay the same forever.</p>
                            <EditAnytimeVisual />
                        </div>

                        {/* Profile Photo */}
                        <div className="lp-feat-bcard reveal from-up d3">
                            <div className="lp-feat-bicon"><IconPhoto /></div>
                            <div className="lp-feat-btag">Identity</div>
                            <div className="lp-feat-btitle">Profile Photo</div>
                            <p className="lp-feat-bdesc">Your face on your card — and inside Google Wallet too.</p>
                        </div>

                        {/* Save Contact */}
                        <div className="lp-feat-bcard reveal from-up d4">
                            <div className="lp-feat-bicon"><IconContact /></div>
                            <div className="lp-feat-btag">One Tap</div>
                            <div className="lp-feat-btitle">Save Contact</div>
                            <p className="lp-feat-bdesc">Visitors save you directly to their phone contacts with a single tap.</p>
                        </div>

                        {/* Works Everywhere */}
                        <div className="lp-feat-bcard reveal from-right d5">
                            <div className="lp-feat-bicon"><IconGlobe /></div>
                            <div className="lp-feat-btag">Universal</div>
                            <div className="lp-feat-btitle">Works Everywhere</div>
                            <p className="lp-feat-bdesc">Opens in any browser on any device. No app install ever required.</p>
                        </div>

                        {/* Tall — QR Code */}
                        <div className="lp-feat-bcard tall reveal from-right d2">
                            <div className="lp-feat-bicon"><IconQR /></div>
                            <div className="lp-feat-btag">Instant Share</div>
                            <div className="lp-feat-btitle">Unique QR Code</div>
                            <p className="lp-feat-bdesc">Every card gets a dedicated QR. One scan opens your full profile — no typing, no friction.</p>
                            <div style={{ marginTop: "auto", paddingTop: "1.5rem" }}>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gridTemplateRows: "repeat(5,1fr)", gap: 4, opacity: 0.25 }}>
                                    {[1, 1, 1, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 1].map((v, i) => (
                                        <div key={i} style={{ background: v ? "var(--teal)" : "var(--teal-subtle)", borderRadius: 2, aspectRatio: "1" }} />
                                    ))}
                                </div>
                                {/* Decorative scan line */}
                                <div style={{ marginTop: 10, height: 1.5, borderRadius: 2, background: "linear-gradient(90deg,transparent,var(--teal),transparent)", opacity: 0.5, animation: "laser-sweep 2.5s ease-in-out infinite" }} />
                            </div>
                        </div>

                        {/* Hero card — Social Links */}
                        <div className="lp-feat-bcard hero-card has-pills reveal from-left d2">
                            <div className="lp-feat-bicon"><IconLink /></div>
                            <div className="lp-feat-btag">Connections</div>
                            <div className="lp-feat-btitle">All Social Links</div>
                            <p className="lp-feat-bdesc">LinkedIn, GitHub, WhatsApp, YouTube, Instagram — all linked from a single professional card.</p>
                            <div className="lp-feat-socials">
                                {["in LinkedIn", "⌥ GitHub", "✉ WhatsApp", "▶ YouTube", "📷 Insta"].map(s => (
                                    <span key={s} className="lp-social-pill">{s}</span>
                                ))}
                            </div>
                        </div>

                        {/* Resume Upload */}
                        <div className="lp-feat-bcard reveal from-up d3">
                            <div className="lp-feat-bicon"><IconResume /></div>
                            <div className="lp-feat-btag">Career</div>
                            <div className="lp-feat-btitle">Resume Upload</div>
                            <p className="lp-feat-bdesc">Attach your PDF resume. Recruiters can download it instantly.</p>
                        </div>

                        {/* AI Auto-Fill — hero card */}
                        <div className="lp-feat-bcard hero-card reveal from-left d2" style={{ background: "linear-gradient(145deg,#f0fdfc,#fffcf0)", borderColor: "rgba(10,173,161,0.22)" }}>
                            <div className="lp-feat-bicon" style={{ background: "linear-gradient(135deg,rgba(10,173,161,0.15),rgba(232,146,10,0.12))" }}><IconAI /></div>
                            <div className="lp-feat-btag" style={{ color: "var(--amber)", borderColor: "rgba(232,146,10,0.25)", background: "rgba(232,146,10,0.08)" }}>✨ AI Powered</div>
                            <div className="lp-feat-btitle">Resume Auto-Fill</div>
                            <p className="lp-feat-bdesc">Upload your PDF resume and AI instantly reads it — filling your name, job title, company, bio, social links and more in seconds.</p>
                            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: "1.2rem" }}>
                                {[
                                    { emoji: "📄", text: "Upload PDF resume" },
                                    { emoji: "🤖", text: "AI extracts your info" },
                                    { emoji: "⚡", text: "Fields fill automatically" },
                                ].map((s, i) => (
                                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 13px", background: "rgba(10,173,161,0.06)", border: "0.5px solid rgba(10,173,161,0.12)", borderRadius: 10, fontSize: 13, fontWeight: 500, color: "var(--text-muted)" }}>
                                        <span style={{ fontSize: 15 }}>{s.emoji}</span> {s.text}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Profile Insights */}
                        <div className="lp-feat-bcard tall reveal from-right d4" style={{ display: "flex", flexDirection: "column" }}>
                            <div className="lp-feat-bicon"><IconAnalytics /></div>
                            <div className="lp-feat-btag" style={{ color: "var(--amber)", borderColor: "rgba(232,146,10,0.25)", background: "rgba(232,146,10,0.08)" }}>New</div>
                            <div className="lp-feat-btitle">Profile Insights</div>
                            <p className="lp-feat-bdesc">See exactly how many people viewed your card and when — with daily traffic tracking built in.</p>
                            <div style={{ marginTop: "auto", paddingTop: "1.2rem", display: "flex", flexDirection: "column", gap: 8 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "rgba(10,173,161,0.06)", borderRadius: 10, border: "0.5px solid rgba(10,173,161,0.12)" }}>
                                    <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>👁 Total Views</span>
                                    <span style={{ fontSize: 14, fontWeight: 800, color: "var(--teal)" }}>1,284</span>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "rgba(232,146,10,0.06)", borderRadius: 10, border: "0.5px solid rgba(232,146,10,0.12)" }}>
                                    <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>📈 Today</span>
                                    <span style={{ fontSize: 14, fontWeight: 800, color: "var(--amber)" }}>+23</span>
                                </div>
                                <div style={{ display: "flex", gap: 4, marginTop: 4, alignItems: "flex-end", height: 36 }}>
                                    {[4, 7, 5, 9, 6, 11, 8, 13, 10, 15, 12, 17].map((h, i) => (
                                        <div key={i} style={{ flex: 1, borderRadius: "3px 3px 0 0", background: i === 11 ? "var(--teal)" : "rgba(10,173,161,0.2)", height: `${(h / 17) * 100}%`, transition: "height 0.3s" }} />
                                    ))}
                                </div>
                                <div style={{ fontSize: 10, color: "var(--text-muted2)", textAlign: "center", letterSpacing: "0.04em" }}>LAST 12 DAYS</div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <div className="lp-rule" />
            <UseCasesSection navigate={navigate} />
            <div className="lp-rule" />
            <TestimonialsSection />
            <div className="lp-rule" />

            {/* PRICING */}
            <section id="pricing" className="lp-pricing-cta">
                <div className="lp-pricing-cta-inner">
                    {/* Left — copy */}
                    <div>
                        <span className="lp-pricing-eyebrow reveal from-left">Pricing</span>
                        <h2 className="lp-pricing-headline reveal from-left d1">Stop carrying<br /><em>paper cards.</em></h2>
                        <p className="lp-pricing-sub reveal from-left d2">Start free in under 2 minutes. Upgrade anytime to unlock premium themes & features.</p>
                        <div className="lp-pricing-perks reveal from-left d3">
                            {["No app download needed", "Unique QR code & shareable link", "Google Wallet pass included", "Edit anytime — same link forever", "Works on every device & browser"].map(p => (
                                <div className="lp-pricing-perk" key={p}><div className="lp-perk-tick">✓</div><span>{p}</span></div>
                            ))}
                        </div>
                        <button className="lp-btn-primary reveal from-up d4" onClick={() => navigate("/animation/card")}>Create My Card Now →</button>
                    </div>

                    {/* Right — plans */}
                    <div>
                        <PricingPlans />
                    </div>
                </div>
            </section>

            <div className="lp-rule" />

            {/* FOOTER */}
            <footer className="lp-footer">
                <div className="lp-footer-inner">
                    <div className="lp-footer-logo"><LogoSVG size={28} />myprofile<em>corner</em></div>
                    <p className="lp-footer-tagline">Your digital identity, always in your pocket.</p>
                    <p className="lp-footer-copy">© {new Date().getFullYear()} MyProfileCorner · Made with ♥ in India</p>
                </div>
            </footer>
        </div>
    );
}


// import React, { useState, useEffect, useRef } from "react";
// import { useNavigate } from "react-router-dom";

// const FONT_LINK = `@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=DM+Serif+Display:ital@0;1&display=swap');`;

// const GLOBAL_CSS = `
// ${FONT_LINK}

// *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

// :root {
//   --ink:            #0B1221;
//   --ink2:           #0F1A2E;
//   --ink3:           #162033;
//   --ink4:           #1C2A40;
//   --ink5:           #243348;

//   --teal:           #0DBDB0;
//   --teal2:          #0EA89C;
//   --teal3:          #0C9189;
//   --teal-dim:       rgba(13,189,176,0.14);
//   --teal-subtle:    rgba(13,189,176,0.08);
//   --teal-subtle2:   rgba(13,189,176,0.15);
//   --teal-glow:      rgba(13,189,176,0.25);

//   --amber:          #F5A623;
//   --amber2:         #F0931A;
//   --amber-dim:      rgba(245,166,35,0.14);
//   --amber-subtle:   rgba(245,166,35,0.08);

//   --coral:          #FF6B6B;

//   --text-primary:   #E8F0F8;
//   --text-muted:     rgba(200,220,240,0.65);
//   --text-muted2:    rgba(200,220,240,0.38);

//   --border:         rgba(13,189,176,0.12);
//   --border2:        rgba(13,189,176,0.08);
//   --border-teal:    rgba(13,189,176,0.22);
//   --border-amber:   rgba(245,166,35,0.22);

//   --surface:        rgba(15,26,46,0.95);
//   --surface2:       rgba(22,32,51,0.9);
//   --surface3:       rgba(28,42,64,0.8);

//   --serif:  'DM Serif Display', serif;
//   --sans:   'Outfit', sans-serif;
// }

// html { scroll-behavior: smooth; }
// body { background: var(--ink); color: var(--text-primary); font-family: var(--sans); overflow-x: hidden; }
// ::-webkit-scrollbar { width: 4px; }
// ::-webkit-scrollbar-track { background: var(--ink); }
// ::-webkit-scrollbar-thumb { background: rgba(13,189,176,0.35); border-radius: 2px; }

// /* REVEALS */
// .reveal { opacity: 0; transition: opacity 0.75s cubic-bezier(0.22,1,0.36,1), transform 0.75s cubic-bezier(0.22,1,0.36,1); }
// .reveal.from-up    { transform: translateY(36px); }
// .reveal.from-down  { transform: translateY(-24px); }
// .reveal.from-left  { transform: translateX(-44px); }
// .reveal.from-right { transform: translateX(44px); }
// .reveal.d1{transition-delay:0.05s;} .reveal.d2{transition-delay:0.13s;} .reveal.d3{transition-delay:0.21s;}
// .reveal.d4{transition-delay:0.29s;} .reveal.d5{transition-delay:0.37s;} .reveal.d6{transition-delay:0.45s;}
// .reveal.visible { opacity: 1; transform: none; }

// /* NAV */
// .lp-nav { position:fixed; top:0; left:0; right:0; z-index:200; padding:1.4rem 3rem; transition:all 0.4s; }
// .lp-nav.scrolled { background:rgba(11,18,33,0.96); backdrop-filter:blur(20px); border-bottom:0.5px solid var(--border); padding:0.9rem 3rem; box-shadow:0 4px 32px rgba(13,189,176,0.06); }
// .lp-nav-inner { max-width:1180px; margin:0 auto; display:flex; align-items:center; justify-content:space-between; }
// .lp-logo { display:flex; align-items:center; gap:10px; font-family:var(--sans); font-size:15px; font-weight:700; color:var(--text-primary); text-decoration:none; flex-shrink:0; }
// .lp-logo em { color:var(--teal); font-style:normal; }
// .lp-nav-links { display:flex; align-items:center; gap:2.5rem; }
// .lp-nav-link { font-size:13.5px; font-weight:500; color:var(--text-muted); text-decoration:none; transition:color 0.2s; }
// .lp-nav-link:hover { color:var(--teal); }
// .lp-nav-cta { padding:10px 22px; background:linear-gradient(135deg,var(--teal3),var(--teal)); color:var(--ink); border:none; border-radius:10px; font-family:var(--sans); font-size:13px; font-weight:700; cursor:pointer; position:relative; overflow:hidden; transition:transform 0.15s,box-shadow 0.15s; white-space:nowrap; }
// .lp-nav-cta::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.18),transparent 60%); pointer-events:none; }
// .lp-nav-cta:hover { transform:translateY(-1px); box-shadow:0 6px 20px var(--teal-glow); }

// /* HAMBURGER */
// .lp-hamburger { display:none; flex-direction:column; justify-content:center; align-items:center; gap:5px; width:40px; height:40px; border-radius:10px; border:0.5px solid var(--border-teal); background:var(--teal-subtle); cursor:pointer; flex-shrink:0; transition:background 0.2s; }
// .lp-hamburger:hover { background:var(--teal-subtle2); }
// .lp-hamburger span { display:block; width:18px; height:1.5px; background:var(--teal); border-radius:2px; transition:all 0.3s cubic-bezier(0.22,1,0.36,1); transform-origin:center; }
// .lp-hamburger.open span:nth-child(1){transform:translateY(6.5px) rotate(45deg);}
// .lp-hamburger.open span:nth-child(2){opacity:0;transform:scaleX(0);}
// .lp-hamburger.open span:nth-child(3){transform:translateY(-6.5px) rotate(-45deg);}

// /* MOBILE MENU */
// .lp-mobile-menu { position:fixed; top:0; left:0; right:0; bottom:0; z-index:190; display:flex; flex-direction:column; pointer-events:none; }
// .lp-mobile-menu-backdrop { position:absolute; inset:0; background:rgba(5,10,20,0.6); backdrop-filter:blur(6px); opacity:0; transition:opacity 0.35s ease; }
// .lp-mobile-menu.open .lp-mobile-menu-backdrop { opacity:1; pointer-events:all; }
// .lp-mobile-menu-panel { position:absolute; top:0; right:0; width:min(320px,85vw); height:100%; background:var(--ink2); border-left:0.5px solid var(--border-teal); display:flex; flex-direction:column; transform:translateX(100%); transition:transform 0.4s cubic-bezier(0.22,1,0.36,1); box-shadow:-20px 0 60px rgba(13,189,176,0.08); pointer-events:none; overflow:hidden; }
// .lp-mobile-menu.open .lp-mobile-menu-panel { transform:translateX(0); pointer-events:all; }
// .lp-mobile-menu-header { display:flex; align-items:center; justify-content:space-between; padding:1.25rem 1.5rem; border-bottom:0.5px solid var(--border2); }
// .lp-mobile-close { width:36px; height:36px; border-radius:9px; border:0.5px solid var(--border2); background:var(--surface2); display:flex; align-items:center; justify-content:center; cursor:pointer; color:var(--text-muted); font-size:16px; transition:background 0.2s; }
// .lp-mobile-close:hover { background:var(--surface3); color:var(--text-primary); }
// .lp-mobile-links { flex:1; display:flex; flex-direction:column; padding:1.5rem; gap:0.25rem; }
// .lp-mobile-link { display:flex; align-items:center; gap:12px; padding:1rem 1.25rem; border-radius:14px; font-family:var(--sans); font-size:15px; font-weight:600; color:var(--text-muted); text-decoration:none; border:0.5px solid transparent; opacity:0; transform:translateX(20px); transition:background 0.2s,color 0.2s,border-color 0.2s,opacity 0.35s ease,transform 0.35s cubic-bezier(0.22,1,0.36,1); }
// .lp-mobile-link:nth-child(1){transition-delay:0.06s;} .lp-mobile-link:nth-child(2){transition-delay:0.11s;} .lp-mobile-link:nth-child(3){transition-delay:0.16s;}
// .lp-mobile-menu.open .lp-mobile-link { opacity:1; transform:translateX(0); }
// .lp-mobile-link:hover { background:var(--surface3); color:var(--teal); border-color:var(--border-teal); }
// .lp-mobile-link-icon { font-size:16px; width:20px; text-align:center; }
// .lp-mobile-divider { height:0.5px; background:linear-gradient(90deg,transparent,var(--border-teal),transparent); margin:0.5rem 1.5rem; }
// .lp-mobile-menu-footer { padding:1.5rem; border-top:0.5px solid var(--border2); display:flex; flex-direction:column; gap:0.75rem; opacity:0; transform:translateY(12px); transition:opacity 0.35s ease 0.22s,transform 0.35s ease 0.22s; }
// .lp-mobile-menu.open .lp-mobile-menu-footer { opacity:1; transform:translateY(0); }
// .lp-mobile-cta { width:100%; padding:14px; background:linear-gradient(135deg,var(--teal3),var(--teal)); color:var(--ink); border:none; border-radius:12px; font-family:var(--sans); font-size:14px; font-weight:700; cursor:pointer; position:relative; overflow:hidden; }
// .lp-mobile-cta::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.15),transparent 60%); pointer-events:none; }
// .lp-mobile-tagline { text-align:center; font-size:11px; font-weight:500; color:var(--text-muted2); letter-spacing:0.5px; }
// .lp-mobile-cluster { display:none; align-items:center; gap:10px; }

// /* HERO */
// .lp-hero-wrap { position:relative; overflow:hidden; background:var(--ink); }
// .lp-hero-mesh { position:absolute; inset:0; pointer-events:none; overflow:hidden; }
// .lp-hero-mesh::before { content:''; position:absolute; width:900px; height:900px; border-radius:50%; background:radial-gradient(circle,rgba(13,189,176,0.09) 0%,rgba(13,189,176,0.03) 40%,transparent 70%); top:-300px; left:-200px; }
// .lp-hero-mesh::after { content:''; position:absolute; width:700px; height:700px; border-radius:50%; background:radial-gradient(circle,rgba(245,166,35,0.06) 0%,transparent 65%); top:-100px; right:-150px; }
// .lp-hero-mesh-bottom { position:absolute; width:500px; height:500px; border-radius:50%; background:radial-gradient(circle,rgba(13,189,176,0.05) 0%,transparent 70%); bottom:-200px; right:20%; pointer-events:none; }
// .lp-hero-grid { position:absolute; inset:0; background-image:radial-gradient(circle,rgba(13,189,176,0.12) 1px,transparent 1px); background-size:48px 48px; opacity:0.35; pointer-events:none; }

// .lp-hero { min-height:100vh; display:grid; grid-template-columns:1fr 1fr; align-items:center; gap:4rem; max-width:1180px; margin:0 auto; padding:9rem 3rem 4rem; position:relative; z-index:2; }

// /* Hero eyebrow */
// .lp-hero-eyebrow { display:inline-flex; align-items:center; gap:8px; padding:7px 16px; background:rgba(13,189,176,0.08); border:0.5px solid rgba(13,189,176,0.3); border-radius:100px; font-size:11.5px; font-weight:700; color:var(--teal); letter-spacing:1px; text-transform:uppercase; margin-bottom:2rem; }
// .lp-hero-eyebrow-dot { width:6px; height:6px; border-radius:50%; background:var(--teal); animation:pulse-teal 2s ease-in-out infinite; }
// @keyframes pulse-teal { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:0.3;transform:scale(0.65);} }

// /* Hero title */
// .lp-hero-title { font-family:var(--sans); font-size:clamp(3rem,5.5vw,5.2rem); font-weight:800; line-height:1.05; letter-spacing:-1.5px; color:var(--text-primary); margin-bottom:1.75rem; }
// .lp-hero-title .gradient-line { display:block; background:linear-gradient(100deg,#0DBDB0 0%,#0DBDB0 35%,#F5A623 80%,#FF9A3C 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }

// .lp-hero-sub { font-size:16px; font-weight:400; color:var(--text-muted); line-height:1.85; max-width:480px; margin-bottom:1rem; }
// .lp-hero-ctas { display:flex; gap:1rem; flex-wrap:wrap; }

// /* BUTTONS */
// .lp-btn-primary { padding:17px 34px; background:linear-gradient(135deg,var(--teal3),var(--teal)); color:var(--ink); border:none; border-radius:14px; font-family:var(--sans); font-size:14px; font-weight:800; letter-spacing:0.3px; cursor:pointer; position:relative; overflow:hidden; transition:transform 0.2s,box-shadow 0.2s; display:flex; align-items:center; gap:8px; white-space:nowrap; }
// .lp-btn-primary::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.18),transparent 55%); pointer-events:none; }
// .lp-btn-primary:hover { transform:translateY(-2px); box-shadow:0 10px 32px var(--teal-glow); }
// .lp-btn-ghost { padding:17px 28px; background:rgba(13,189,176,0.06); color:var(--text-muted); border:0.5px solid rgba(13,189,176,0.2); border-radius:14px; font-family:var(--sans); font-size:14px; font-weight:600; cursor:pointer; text-decoration:none; display:inline-flex; align-items:center; gap:8px; transition:border-color 0.2s,color 0.2s,background 0.2s; white-space:nowrap; }
// .lp-btn-ghost:hover { border-color:var(--border-teal); color:var(--teal); background:var(--teal-subtle2); }

// /* STATS */
// .lp-hero-stats { display:flex; gap:2.5rem; padding-bottom:1.5rem; margin-bottom:1.5rem; }
// .lp-stat { position:relative; }
// .lp-stat-num { font-family:var(--sans); font-size:2.4rem; font-weight:800; color:var(--teal); line-height:1; letter-spacing:-1px; }
// .lp-stat-num.counting { animation:stat-shimmer 0.08s ease infinite alternate; }
// @keyframes stat-shimmer { from{opacity:0.6;} to{opacity:1;} }
// .lp-stat-label { font-size:11px; font-weight:500; color:var(--text-muted2); letter-spacing:0.4px; margin-top:5px; }
// .lp-stat-bar { height:2px; background:rgba(13,189,176,0.12); margin-top:8px; border-radius:1px; overflow:hidden; }
// .lp-stat-bar-fill { height:100%; background:linear-gradient(90deg,var(--teal),var(--amber)); border-radius:1px; width:0%; transition:width 0.05s linear; }

// /* SCAN SCENE */
// .lp-hero-right { position:relative; display:flex; align-items:center; justify-content:center; z-index:2; }
// .scan-scene { position:relative; width:380px; height:480px; }
// .scan-ripple { position:absolute; right:-10px; top:50px; width:60px; height:60px; border-radius:50%; border:1.5px solid rgba(13,189,176,0); z-index:10; pointer-events:none; }
// .scan-ripple.r1{animation:ripple-out 2.5s ease-out infinite;animation-delay:3.2s;}
// .scan-ripple.r2{animation:ripple-out 2.5s ease-out infinite;animation-delay:3.6s;}
// .scan-ripple.r3{animation:ripple-out 2.5s ease-out infinite;animation-delay:4.0s;}
// @keyframes ripple-out { 0%{transform:scale(1);border-color:rgba(13,189,176,0.5);opacity:1;} 100%{transform:scale(3.5);border-color:rgba(13,189,176,0);opacity:0;} }
// .scan-success { position:absolute; right:8px; top:38px; background:linear-gradient(135deg,var(--teal3),var(--teal)); color:var(--ink); border-radius:100px; padding:5px 12px 5px 9px; font-family:var(--sans); font-size:9.5px; font-weight:700; letter-spacing:0.3px; display:flex; align-items:center; gap:6px; z-index:12; white-space:nowrap; opacity:0; animation:success-pop 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards; animation-delay:3.1s; box-shadow:0 4px 18px var(--teal-glow); }
// .scan-success-dot { width:6px; height:6px; border-radius:50%; background:rgba(11,18,33,0.4); }
// @keyframes success-pop { 0%{opacity:0;transform:scale(0.5) translateY(6px);} 100%{opacity:1;transform:scale(1) translateY(0);} }
// .phone-scanner { position:absolute; left:0; top:60px; width:155px; height:270px; background:var(--ink2); border-radius:22px; border:1px solid rgba(13,189,176,0.2); overflow:hidden; box-shadow:0 24px 60px rgba(13,189,176,0.1); z-index:3; animation:phone-a-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both; animation-delay:0.3s; }
// @keyframes phone-a-intro { from{opacity:0;transform:translateX(-30px) rotate(-6deg);} to{opacity:1;transform:translateX(0) rotate(-6deg);} }
// .phone-scanner-screen { width:100%; height:100%; background:var(--ink3); display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; padding:12px; }
// .scanner-viewfinder { width:100px; height:100px; border-radius:10px; position:relative; background:rgba(13,189,176,0.05); }
// .scanner-viewfinder::before,.scanner-viewfinder::after { content:''; position:absolute; width:20px; height:20px; border-color:var(--teal); border-style:solid; }
// .scanner-viewfinder::before { top:0;left:0;border-width:2px 0 0 2px;border-radius:6px 0 0 0; }
// .scanner-viewfinder::after { bottom:0;right:0;border-width:0 2px 2px 0;border-radius:0 0 6px 0; }
// .vf-corner-tr { position:absolute;top:0;right:0;width:20px;height:20px;border-top:2px solid var(--teal);border-right:2px solid var(--teal);border-radius:0 6px 0 0; }
// .vf-corner-bl { position:absolute;bottom:0;left:0;width:20px;height:20px;border-bottom:2px solid var(--teal);border-left:2px solid var(--teal);border-radius:0 0 0 6px; }
// .scan-laser { position:absolute;left:4px;right:4px;height:2px;background:linear-gradient(90deg,transparent,var(--teal),transparent);border-radius:1px;top:4px;animation:laser-sweep 2s ease-in-out infinite;animation-delay:1.2s; }
// @keyframes laser-sweep { 0%{top:8px;opacity:0;} 10%{opacity:1;} 90%{opacity:1;} 100%{top:88px;opacity:0;} }
// .scanner-success-flash { position:absolute; inset:0; background:rgba(13,189,176,0.12); opacity:0; animation:flash-once 0.4s ease forwards; animation-delay:3.0s; pointer-events:none; z-index:10; }
// @keyframes flash-once { 0%{opacity:0;} 40%{opacity:1;} 100%{opacity:0;} }
// .scanner-label { font-size:9px;font-weight:600;color:var(--text-muted2);letter-spacing:1px;text-align:center;text-transform:uppercase; }
// .mini-qr { position:absolute;inset:10px;display:grid;grid-template-columns:repeat(6,1fr);grid-template-rows:repeat(6,1fr);gap:1.5px;opacity:0.6; }
// .mini-qr-cell { border-radius:1px; }
// .phone-profile { position:absolute;right:0;top:0;width:175px;height:310px;background:var(--ink2);border-radius:24px;border:1px solid rgba(13,189,176,0.22);overflow:hidden;box-shadow:0 32px 80px rgba(13,189,176,0.08),0 0 0 1px rgba(13,189,176,0.06);z-index:4;animation:phone-b-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:0.5s; }
// @keyframes phone-b-intro { from{opacity:0;transform:translateY(30px) rotate(4deg);} to{opacity:1;transform:translateY(0) rotate(4deg);} }
// .profile-card-wrap { width:100%;height:100%;animation:card-reveal 0.7s cubic-bezier(0.22,1,0.36,1) both;animation-delay:2s; }
// @keyframes card-reveal { from{transform:translateY(40px);opacity:0;} to{transform:translateY(0);opacity:1;} }
// .profile-banner { width:100%;height:110px;background:linear-gradient(160deg,#0d2535,#0c3340);position:relative;overflow:hidden; }
// .profile-banner-img { width:100%;height:100%;background:linear-gradient(160deg,rgba(13,189,176,0.15) 0%,rgba(13,189,176,0.06) 50%,rgba(245,166,35,0.08) 100%); }
// .profile-banner-fade { position:absolute;bottom:0;left:0;right:0;height:50px;background:linear-gradient(transparent,var(--ink2)); }
// .profile-avatar-ring { position:absolute;bottom:2px;left:14px;width:44px;height:44px;border-radius:50%;border:2px solid var(--ink2);background:var(--ink3);overflow:hidden;box-shadow:0 0 0 1.5px rgba(13,189,176,0.4);animation:avatar-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:2.4s; }
// @keyframes avatar-pop { from{transform:scale(0.3);opacity:0;} to{transform:scale(1);opacity:1;} }
// .profile-avatar-inner { width:100%;height:100%;background:linear-gradient(145deg,var(--teal3),var(--teal));display:flex;align-items:center;justify-content:center;font-family:var(--sans);font-size:13px;font-weight:700;color:var(--ink);letter-spacing:1px; }
// .profile-info { padding:28px 14px 12px; }
// .profile-info-name { font-family:var(--sans);font-size:15px;font-weight:700;color:var(--text-primary);line-height:1.2;margin-bottom:2px; }
// .profile-info-name em { font-style:normal;color:var(--teal); }
// .profile-info-role { font-size:8px;font-weight:600;color:var(--text-muted2);letter-spacing:0.3px;margin-bottom:10px; }
// .profile-tiles { display:grid;grid-template-columns:1fr 1fr 1fr;gap:5px;margin-bottom:8px; }
// .profile-tile { background:rgba(13,189,176,0.06);border:0.5px solid rgba(13,189,176,0.14);border-radius:8px;padding:7px 6px;display:flex;flex-direction:column;gap:4px;animation:tile-slide 0.3s ease both; }
// .profile-tile:nth-child(1){animation-delay:2.5s;} .profile-tile:nth-child(2){animation-delay:2.65s;} .profile-tile:nth-child(3){animation-delay:2.8s;}
// @keyframes tile-slide { from{opacity:0;transform:translateY(6px);} to{opacity:1;transform:translateY(0);} }
// .tile-dot { width:12px;height:12px;border-radius:50%;background:rgba(13,189,176,0.12);display:flex;align-items:center;justify-content:center; }
// .tile-dot-inner { width:5px;height:5px;border-radius:50%;background:var(--teal); }
// .tile-text { font-size:7px;color:var(--text-muted2);line-height:1.3;font-weight:500; }
// .profile-save-btn { width:100%;padding:8px;background:linear-gradient(135deg,var(--teal3),var(--teal));color:var(--ink);border:none;border-radius:8px;font-size:8px;font-weight:700;letter-spacing:0.5px;text-transform:uppercase;cursor:default;display:flex;align-items:center;justify-content:center;gap:4px;animation:tile-slide 0.3s ease both;animation-delay:2.95s; }
// .beam-line { position:absolute;top:165px;left:148px;width:90px;height:1px;background:linear-gradient(90deg,var(--teal),rgba(13,189,176,0));transform-origin:left center;animation:beam-appear 0.4s ease both;animation-delay:1.5s;z-index:5; }
// @keyframes beam-appear { from{opacity:0;} to{opacity:1;} }
// .beam-particle { position:absolute;top:160px;left:148px;width:6px;height:6px;border-radius:50%;background:var(--teal);animation:particle-travel 2s ease-in-out infinite;animation-delay:1.6s;z-index:6; }
// @keyframes particle-travel { 0%{left:148px;opacity:0;transform:scale(0.5);} 10%{opacity:1;transform:scale(1);} 90%{opacity:1;} 100%{left:228px;opacity:0;transform:scale(0.5);} }
// .hero-orb { position:absolute;width:500px;height:500px;border-radius:50%;background:radial-gradient(circle,rgba(13,189,176,0.04) 0%,transparent 65%);top:-100px;right:-100px;pointer-events:none;z-index:0; }

// /* DIVIDER */
// .lp-rule { height:0.5px; background:linear-gradient(90deg,transparent,rgba(13,189,176,0.18),transparent); }

// /* TRUST BAR */
// .lp-trust { padding:1.5rem 2rem; background:var(--ink2); display:flex; flex-direction:column; align-items:center; gap:1rem; }
// .lp-trust-label { font-size:10px;font-weight:600;color:var(--text-muted2);letter-spacing:2.5px;text-transform:uppercase; }
// .lp-trust-items { display:flex;align-items:stretch;justify-content:center;flex-wrap:nowrap;width:100%;max-width:580px; }
// .lp-trust-item { display:flex;flex-direction:column;align-items:center;gap:5px;flex:1;padding:0.6rem 0.75rem;border-right:0.5px solid var(--border2);font-size:11.5px;font-weight:600;color:var(--text-muted);text-align:center; }
// .lp-trust-item:last-child { border-right:none; }
// .lp-trust-icon { font-size:1.2rem;line-height:1;width:36px;height:36px;border-radius:10px;background:var(--teal-subtle);border:0.5px solid var(--border-teal);display:flex;align-items:center;justify-content:center;margin-bottom:2px; }

// /* SECTIONS */
// .lp-section { padding:5rem 3rem; position:relative; }
// .lp-section-inner { max-width:1180px; margin:0 auto; }
// .lp-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--teal);margin-bottom:1.25rem;text-align:center; }
// .lp-section-title { font-family:var(--sans);font-size:clamp(2.2rem,4vw,3.4rem);font-weight:800;line-height:1.08;letter-spacing:-1px;color:var(--text-primary);text-align:center;margin-bottom:1.25rem; }
// .lp-section-title em { font-style:italic;font-family:var(--serif);font-weight:400;color:var(--teal); }
// .lp-section-sub { font-size:15px;font-weight:400;color:var(--text-muted);text-align:center;max-width:520px;margin:0 auto 4rem;line-height:1.9; }

// /* HOW IT WORKS */
// .lp-how-timeline { display:grid;grid-template-columns:1fr 72px 1fr 72px 1fr;align-items:start;gap:0;margin-top:1rem; }
// .lp-how-bridge { display:flex;align-items:flex-start;justify-content:center;padding-top:2.6rem; }
// .how-arrow-path { stroke:rgba(13,189,176,0.15);stroke-dasharray:60;stroke-dashoffset:60;transition:stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1),stroke 0.4s ease; }
// .how-arrow-path.filled { stroke-dashoffset:0;stroke:var(--teal); }
// .lp-how-card { background:var(--ink2);border:0.5px solid var(--border2);border-radius:20px;padding:2.2rem 1.8rem 2rem;position:relative;overflow:hidden;transition:border-color 0.3s,transform 0.3s,box-shadow 0.3s;cursor:default; }
// .lp-how-card::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 80% 60% at 50% -10%,var(--teal-subtle2) 0%,transparent 70%);opacity:0;transition:opacity 0.3s; }
// .lp-how-card:hover { border-color:var(--border-teal);transform:translateY(-5px);box-shadow:0 12px 32px rgba(13,189,176,0.08); }
// .lp-how-card:hover::before { opacity:1; }
// .lp-how-num { font-family:var(--sans);font-size:7rem;font-weight:800;color:rgba(13,189,176,0.05);line-height:1;position:absolute;bottom:-0.5rem;right:0.8rem;letter-spacing:-3px;pointer-events:none;user-select:none; }
// .lp-how-icon-ring { width:56px;height:56px;border-radius:16px;background:var(--teal-subtle);border:1px solid var(--border-teal);display:flex;align-items:center;justify-content:center;font-size:1.6rem;margin-bottom:1.5rem;position:relative; }
// .lp-how-icon-ring::after { content:'';position:absolute;inset:-4px;border-radius:20px;border:1px dashed var(--border-teal);animation:spin-slow 12s linear infinite; }
// @keyframes spin-slow { to{transform:rotate(360deg);} }
// .lp-how-step-label { font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:var(--teal);margin-bottom:0.6rem; }
// .lp-how-title { font-family:var(--sans);font-size:1.55rem;font-weight:700;color:var(--text-primary);line-height:1.15;margin-bottom:0.75rem; }
// .lp-how-desc { font-size:13.5px;color:var(--text-muted);line-height:1.8; }

// /* FEATURES */
// .lp-feat-bento { display:grid;grid-template-columns:repeat(4,1fr);grid-template-rows:auto auto;gap:10px;margin-top:1rem; }
// .lp-feat-bcard { background:var(--ink2);border:0.5px solid var(--border2);border-radius:18px;padding:1.8rem 1.6rem;position:relative;overflow:hidden;transition:border-color 0.3s,transform 0.25s,background 0.3s,box-shadow 0.3s;cursor:default; }
// .lp-feat-bcard::before { content:'';position:absolute;top:0;left:-100%;width:60%;height:100%;background:linear-gradient(105deg,transparent,rgba(13,189,176,0.04),transparent);transition:left 0s;pointer-events:none;z-index:1; }
// .lp-feat-bcard:hover::before { left:160%;transition:left 0.6s ease; }
// .lp-feat-bcard:hover { border-color:var(--border-teal);background:var(--surface2);transform:translateY(-3px);box-shadow:0 8px 24px rgba(13,189,176,0.06); }
// .lp-feat-bcard.hero-card { grid-column:span 2;background:var(--surface2);border-color:var(--border-teal);padding:2.4rem 2.2rem; }
// .lp-feat-bcard.tall { grid-row:span 2;display:flex;flex-direction:column; }
// .lp-feat-bcard::after { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:transparent;transition:background 0.3s; }
// .lp-feat-bcard:hover::after,.lp-feat-bcard.hero-card::after { background:linear-gradient(90deg,transparent,var(--border-teal),transparent); }
// .lp-feat-bicon { width:44px;height:44px;border-radius:12px;background:var(--teal-subtle);border:0.5px solid var(--border-teal);display:flex;align-items:center;justify-content:center;margin-bottom:1.25rem;flex-shrink:0; }
// .lp-feat-bcard.hero-card .lp-feat-bicon { width:52px;height:52px;border-radius:14px; }
// .lp-feat-btag { display:inline-block;font-size:8.5px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--teal);margin-bottom:0.5rem;opacity:0.7; }
// .lp-feat-btitle { font-family:var(--sans);font-size:1.2rem;font-weight:700;color:var(--text-primary);line-height:1.15;margin-bottom:0.5rem; }
// .lp-feat-bcard.hero-card .lp-feat-btitle { font-size:1.55rem; }
// .lp-feat-bdesc { font-size:13px;color:var(--text-muted);line-height:1.8;flex:1; }
// .lp-feat-wallet { display:flex;gap:6px;margin-top:1.25rem; }
// .lp-wallet-chip { height:26px;border-radius:6px;flex:1;background:var(--teal-subtle);border:0.5px solid var(--border-teal);display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:600;color:var(--teal);letter-spacing:0.5px;position:relative;overflow:hidden; }
// .lp-wallet-chip::after { content:'';position:absolute;top:0;left:-100%;width:50%;height:100%;background:linear-gradient(90deg,transparent,rgba(13,189,176,0.15),transparent);animation:chip-shine 3s ease-in-out infinite; }
// .lp-wallet-chip:nth-child(2)::after{animation-delay:0.4s;} .lp-wallet-chip:nth-child(3)::after{animation-delay:0.8s;}
// @keyframes chip-shine { 0%,70%{left:-100%;} 100%{left:200%;} }
// .lp-feat-socials { display:flex;flex-wrap:wrap;gap:6px;margin-top:1.1rem; }
// .lp-social-pill { padding:4px 10px;background:var(--teal-subtle);border:0.5px solid var(--border-teal);border-radius:100px;font-size:10px;font-weight:500;color:var(--text-muted);opacity:0;transform:translateY(6px) scale(0.94);transition:opacity 0.35s ease,transform 0.35s ease,background 0.2s,color 0.2s; }
// .lp-social-pill:hover { background:var(--teal-subtle2);color:var(--teal); }
// .lp-social-pill:nth-child(1){transition-delay:0.05s;} .lp-social-pill:nth-child(2){transition-delay:0.12s;} .lp-social-pill:nth-child(3){transition-delay:0.19s;} .lp-social-pill:nth-child(4){transition-delay:0.26s;} .lp-social-pill:nth-child(5){transition-delay:0.33s;}
// .lp-feat-bcard.pills-visible .lp-social-pill { opacity:1;transform:translateY(0) scale(1); }

// /* EDIT ANYTIME VISUAL */
// .edit-timeline { display:flex; flex-direction:column; gap:10px; margin-top:auto; padding-top:1.5rem; }
// .edit-row { display:flex; align-items:center; gap:10px; padding:8px 10px; border-radius:10px; background:rgba(13,189,176,0.06); border:0.5px solid rgba(13,189,176,0.1); position:relative; overflow:hidden; }
// .edit-row::after { content:''; position:absolute; left:0; top:0; height:100%; background:linear-gradient(90deg,rgba(13,189,176,0.12),rgba(13,189,176,0.06)); border-radius:10px; transition:width 0.5s ease; }
// .edit-row.synced::after { width:100%; }
// .edit-row:not(.synced)::after { width:0%; }
// .edit-row-dot { width:7px; height:7px; border-radius:50%; flex-shrink:0; position:relative; z-index:1; }
// .edit-row-label { font-size:9px; font-weight:600; color:var(--text-muted2); text-transform:uppercase; letter-spacing:0.8px; flex:1; position:relative; z-index:1; }
// .edit-row-badge { font-size:8px; font-weight:700; padding:2px 7px; border-radius:100px; position:relative; z-index:1; }
// .edit-row-badge.live { background:rgba(13,189,176,0.15); color:var(--teal); }
// .edit-row-badge.updated { background:rgba(245,166,35,0.15); color:var(--amber); }
// .edit-pencil-icon { display:flex; align-items:center; justify-content:center; margin-bottom:1.25rem; }

// /* USE CASES */
// .lp-use-tabs { display:flex;flex-wrap:wrap;gap:0.5rem;justify-content:center;margin-bottom:3rem; }
// .lp-use-tab { padding:8px 18px;border-radius:100px;font-size:13px;font-weight:600;cursor:pointer;transition:all 0.2s;font-family:var(--sans);display:flex;align-items:center;gap:6px; }
// .lp-use-panel { display:grid;grid-template-columns:1fr 1fr;gap:3rem;align-items:center;background:var(--ink2);border:0.5px solid var(--border2);border-radius:24px;padding:3rem;position:relative;overflow:hidden; }
// .lp-use-panel::before { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,var(--border-teal),transparent); }
// .lp-use-badge { display:inline-flex;align-items:center;gap:8px;padding:6px 14px;border-radius:100px;margin-bottom:1.5rem;font-size:12px;font-weight:700;background:var(--teal-subtle);color:var(--teal);border:0.5px solid var(--border-teal); }
// .lp-use-tagline { font-family:var(--sans);font-size:2.1rem;font-weight:800;color:var(--text-primary);line-height:1.1;margin-bottom:1rem;letter-spacing:-0.5px; }
// .lp-use-desc { font-size:14px;font-weight:400;color:var(--text-muted);line-height:1.9;margin-bottom:2rem; }
// .lp-use-content { animation:tab-fade 0.35s cubic-bezier(0.22,1,0.36,1) both; }
// @keyframes tab-fade { from{opacity:0;transform:translateY(10px);} to{opacity:1;transform:translateY(0);} }
// .lp-use-highlights { display:flex;flex-direction:column;gap:0.75rem; }
// .lp-use-hl { display:flex;align-items:center;gap:12px;background:var(--surface3);border:0.5px solid var(--border2);border-radius:12px;padding:1rem 1.25rem;animation:hl-slide 0.3s ease both; }
// .lp-use-hl:nth-child(1){animation-delay:0.05s;} .lp-use-hl:nth-child(2){animation-delay:0.12s;} .lp-use-hl:nth-child(3){animation-delay:0.19s;} .lp-use-hl:nth-child(4){animation-delay:0.26s;}
// @keyframes hl-slide { from{opacity:0;transform:translateX(12px);} to{opacity:1;transform:translateX(0);} }
// .lp-use-hl-check { width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;background:var(--teal-subtle);color:var(--teal); }
// .lp-use-hl-text { font-size:13px;font-weight:500;color:var(--text-muted); }

// /* PRICING */
// .lp-pricing-cta { padding:5rem 3rem;position:relative;overflow:hidden;background:var(--ink3); }
// .lp-pricing-cta::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 70% 80% at 50% 50%,rgba(13,189,176,0.05) 0%,transparent 70%);pointer-events:none; }
// .lp-pricing-cta-inner { max-width:1180px;margin:0 auto;display:grid;grid-template-columns:1fr 1.1fr;gap:4rem;align-items:center;position:relative; }
// .lp-pricing-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--teal);margin-bottom:1.25rem; }
// .lp-pricing-headline { font-family:var(--sans);font-size:clamp(2.8rem,5vw,4.2rem);font-weight:800;line-height:1.05;color:var(--text-primary);margin-bottom:1.25rem;letter-spacing:-1px; }
// .lp-pricing-headline em { font-style:italic;font-family:var(--serif);font-weight:400;color:var(--amber); }
// .lp-pricing-sub { font-size:15px;color:var(--text-muted);line-height:1.9;margin-bottom:2.5rem;max-width:400px; }
// .lp-pricing-perks { display:flex;flex-direction:column;gap:0.6rem;margin-bottom:2.5rem; }
// .lp-pricing-perk { display:flex;align-items:center;gap:10px;font-size:14px;font-weight:500;color:var(--text-muted); }
// .lp-perk-tick { width:20px;height:20px;border-radius:50%;flex-shrink:0;background:var(--teal-subtle);border:0.5px solid var(--border-teal);display:flex;align-items:center;justify-content:center;font-size:9px;color:var(--teal);font-weight:700; }
// .lp-price-card { background:var(--ink2);border:0.5px solid var(--border-teal);border-radius:24px;padding:2.5rem;position:relative;overflow:hidden;box-shadow:0 16px 48px rgba(13,189,176,0.07); }
// .lp-price-card::before { content:'';position:absolute;top:0;left:10%;right:10%;height:1px;background:linear-gradient(90deg,transparent,var(--teal),transparent); }
// .lp-price-card-top { display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:1.5rem; }
// .lp-price-badge { display:inline-block;background:linear-gradient(135deg,var(--teal3),var(--teal));color:var(--ink);border-radius:100px;padding:5px 14px;font-size:10px;font-weight:700;letter-spacing:0.5px;text-transform:uppercase; }
// .lp-price-amount { text-align:right; }
// .lp-price-num { font-family:var(--sans);font-size:4rem;font-weight:800;color:var(--teal);line-height:1;letter-spacing:-2px; }
// .lp-price-period { font-size:10px;font-weight:600;color:var(--text-muted2);letter-spacing:2px;text-transform:uppercase;margin-top:4px; }
// .lp-price-divider { height:0.5px;background:var(--border2);margin:1.25rem 0; }
// .lp-price-list { list-style:none;display:flex;flex-direction:column;gap:0;margin-bottom:1.75rem; }
// .lp-price-item { display:flex;align-items:center;gap:10px;padding:0.55rem 0;font-size:13px;font-weight:500;color:var(--text-muted);border-bottom:0.5px solid var(--border2); }
// .lp-price-item:last-child { border-bottom:none; }
// .lp-price-check { color:var(--teal);font-size:11px;flex-shrink:0; }

// /* FOOTER */
// .lp-footer { padding:3rem 3rem 4rem;border-top:0.5px solid var(--border2);background:var(--ink2); }
// .lp-footer-inner { max-width:1180px;margin:0 auto;display:flex;flex-direction:column;align-items:center;gap:1rem; }
// .lp-footer-logo { display:flex;align-items:center;gap:10px;font-family:var(--sans);font-size:15px;font-weight:700;color:var(--text-primary); }
// .lp-footer-logo em { color:var(--teal);font-style:normal; }
// .lp-footer-tagline { font-size:13px;color:var(--text-muted2); }
// .lp-footer-copy { font-size:12px;color:var(--text-muted2);opacity:0.6; }

// /* RESPONSIVE */
// @media (min-width:901px) {
//   .lp-trust{flex-direction:row;padding:1.5rem 3rem;gap:2.5rem;}
//   .lp-trust-items{width:auto;max-width:none;}
//   .lp-trust-item{flex-direction:row;flex:none;padding:0 1.5rem;border-right:0.5px solid var(--border2);border-bottom:none;gap:8px;}
//   .lp-trust-item:last-child{border-right:none;}
//   .lp-trust-icon{width:28px;height:28px;border-radius:8px;font-size:1rem;flex-shrink:0;margin-bottom:0;}
// }
// @media (max-width:900px) {
//   .lp-hero{grid-template-columns:1fr;text-align:center;padding:8rem 1.5rem 4rem;}
//   .lp-hero-sub{margin:0 auto 1rem;}
//   .lp-hero-ctas{justify-content:center;}
//   .lp-hero-stats{justify-content:center;}
//   .lp-hero-right{margin-top:3rem;}
//   .scan-scene{width:300px;height:380px;transform:scale(0.85);}
//   .lp-how-timeline{grid-template-columns:1fr;}
//   .lp-how-bridge{display:none;}
//   .lp-feat-bento{grid-template-columns:1fr 1fr;}
//   .lp-feat-bcard.hero-card{grid-column:span 2;}
//   .lp-feat-bcard.tall{grid-row:span 1;}
//   .lp-use-panel{grid-template-columns:1fr;}
//   .lp-pricing-cta-inner{grid-template-columns:1fr;}
//   .lp-nav{padding:1rem 1.5rem;}
//   .lp-nav.scrolled{padding:0.75rem 1.5rem;}
//   .lp-section{padding:4rem 1.5rem;}
//   .lp-pricing-cta{padding:4rem 1.5rem;}
//   .lp-hamburger{display:flex;}
//   .lp-nav-links{display:none;}
//   .lp-mobile-cluster{display:flex;}
// }
// @media (max-width:600px) {
//   .lp-feat-bento{grid-template-columns:1fr;}
//   .lp-feat-bcard.hero-card{grid-column:span 1;}
//   .lp-hero-title{font-size:clamp(2.5rem,9vw,3.4rem);}
//   .lp-hero-stats{gap:1.5rem;}
//   .lp-stat-num{font-size:2rem;}
//   .lp-section{padding:3.5rem 1.25rem;}
//   .lp-section-title{font-size:clamp(2rem,7vw,2.7rem);}
//   .lp-use-panel{padding:1.75rem 1.25rem;}
//   .lp-pricing-cta{padding:3rem 1.25rem;}
//   .lp-pricing-headline{font-size:clamp(2.4rem,8vw,3rem);}
//   .lp-hero-ctas{flex-direction:column;align-items:center;}
//   .lp-hero-ctas .lp-btn-primary,.lp-hero-ctas .lp-btn-ghost{width:100%;justify-content:center;}
// }
// `;

// /* ── Dual-tone SVG icons ── */
// function IconWallet() {
//     return (
//         <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
//             <rect x="2" y="6" width="18" height="13" rx="3" stroke="#0DBDB0" strokeWidth="1.5" />
//             <path d="M2 10h18" stroke="#0DBDB0" strokeWidth="1.5" />
//             <path d="M6 2l10 0" stroke="#F5A623" strokeWidth="1.5" strokeLinecap="round" />
//             <circle cx="15.5" cy="14.5" r="1.5" fill="#F5A623" />
//         </svg>
//     );
// }
// function IconQR() {
//     return (
//         <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
//             <rect x="2" y="2" width="7" height="7" rx="1.5" stroke="#0DBDB0" strokeWidth="1.5" />
//             <rect x="13" y="2" width="7" height="7" rx="1.5" stroke="#0DBDB0" strokeWidth="1.5" />
//             <rect x="2" y="13" width="7" height="7" rx="1.5" stroke="#0DBDB0" strokeWidth="1.5" />
//             <rect x="4" y="4" width="3" height="3" rx="0.5" fill="#F5A623" />
//             <rect x="15" y="4" width="3" height="3" rx="0.5" fill="#F5A623" />
//             <rect x="4" y="15" width="3" height="3" rx="0.5" fill="#F5A623" />
//             <path d="M13 13h3v3" stroke="#0DBDB0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
//             <path d="M16 19h3" stroke="#0DBDB0" strokeWidth="1.5" strokeLinecap="round" />
//             <path d="M19 13v3" stroke="#0DBDB0" strokeWidth="1.5" strokeLinecap="round" />
//         </svg>
//     );
// }
// function IconPhoto() {
//     return (
//         <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
//             <rect x="2" y="4" width="18" height="14" rx="3" stroke="#0DBDB0" strokeWidth="1.5" />
//             <circle cx="8" cy="10" r="2.5" stroke="#F5A623" strokeWidth="1.5" />
//             <path d="M2 16l4-4 3 3 4-5 7 7" stroke="#0DBDB0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
//         </svg>
//     );
// }
// function IconContact() {
//     return (
//         <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
//             <path d="M11 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" stroke="#0DBDB0" strokeWidth="1.5" />
//             <path d="M3 19c0-4 3.6-7 8-7s8 3 8 7" stroke="#0DBDB0" strokeWidth="1.5" strokeLinecap="round" />
//             <path d="M15 5h5M15 8h5" stroke="#F5A623" strokeWidth="1.3" strokeLinecap="round" />
//         </svg>
//     );
// }
// function IconGlobe() {
//     return (
//         <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
//             <circle cx="11" cy="11" r="9" stroke="#0DBDB0" strokeWidth="1.5" />
//             <path d="M11 2c-3 3-3 6 0 9s3 6 0 9" stroke="#F5A623" strokeWidth="1.3" strokeLinecap="round" />
//             <path d="M2 11h18" stroke="#0DBDB0" strokeWidth="1.3" strokeLinecap="round" />
//             <path d="M3.5 7h15M3.5 15h15" stroke="#0DBDB0" strokeWidth="1" strokeLinecap="round" opacity="0.4" />
//         </svg>
//     );
// }
// function IconLink() {
//     return (
//         <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
//             <path d="M9 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="#0DBDB0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
//             <path d="M13 9a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="#F5A623" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
//         </svg>
//     );
// }
// function IconResume() {
//     return (
//         <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
//             <rect x="4" y="2" width="14" height="18" rx="2.5" stroke="#0DBDB0" strokeWidth="1.5" />
//             <path d="M8 7h6M8 11h6M8 15h4" stroke="#F5A623" strokeWidth="1.3" strokeLinecap="round" />
//         </svg>
//     );
// }
// function IconEdit() {
//     return (
//         <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
//             <path d="M14.5 3.5a2.121 2.121 0 0 1 3 3L7 17l-4 1 1-4L14.5 3.5z" stroke="#0DBDB0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
//             <path d="M12 6l3 3" stroke="#F5A623" strokeWidth="1.3" strokeLinecap="round" />
//         </svg>
//     );
// }

// /* ── Edit Anytime animated visual ── */
// function EditAnytimeVisual() {
//     const rows = [
//         { label: "Phone Number", badge: "Live", dot: "#0DBDB0", synced: true },
//         { label: "Job Title", badge: "Updated", dot: "#F5A623", synced: true },
//         { label: "LinkedIn URL", badge: "Live", dot: "#0DBDB0", synced: true },
//         { label: "Resume PDF", badge: "Updated", dot: "#F5A623", synced: false },
//     ];

//     return (
//         <div style={{ marginTop: "auto", paddingTop: "1.4rem" }}>
//             <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
//                 <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
//                     <circle cx="8" cy="8" r="7" stroke="#0DBDB0" strokeWidth="1.2" />
//                     <path d="M5 8l2 2 4-4" stroke="#F5A623" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
//                 </svg>
//                 <span style={{ fontSize: 9, fontWeight: 700, color: "var(--teal)", letterSpacing: "1px", textTransform: "uppercase" }}>
//                     Same link · always fresh
//                 </span>
//             </div>
//             {rows.map((r, i) => (
//                 <div key={i} className={`edit-row${r.synced ? " synced" : ""}`}
//                     style={{ animationDelay: `${i * 0.15}s`, marginBottom: 7 }}>
//                     <div className="edit-row-dot" style={{ background: r.dot, boxShadow: `0 0 6px ${r.dot}66` }} />
//                     <span className="edit-row-label">{r.label}</span>
//                     <span className={`edit-row-badge ${r.badge === "Live" ? "live" : "updated"}`}>{r.badge}</span>
//                 </div>
//             ))}
//             <div style={{ marginTop: 10, height: 2, borderRadius: 2, background: "linear-gradient(90deg, var(--teal), var(--amber))", opacity: 0.3 }} />
//             <div style={{ marginTop: 6, fontSize: 9, color: "var(--text-muted2)", fontWeight: 500 }}>
//                 Your QR code & link never change — only the content updates
//             </div>
//         </div>
//     );
// }

// /* COUNT-UP HOOK */
// function useCountUp(target, duration = 1800, suffix = "", started = false) {
//     const [display, setDisplay] = useState("0");
//     const [progress, setProgress] = useState(0);
//     const frameRef = useRef(null);
//     useEffect(() => {
//         if (!started) return;
//         const num = parseFloat(target.replace(/[^0-9.]/g, ""));
//         if (!num) { setDisplay(target); setProgress(100); return; }
//         const startTime = performance.now();
//         const animate = (now) => {
//             const pct = Math.min((now - startTime) / duration, 1);
//             const eased = pct === 1 ? 1 : 1 - Math.pow(2, -10 * pct);
//             setDisplay((Number.isInteger(num) ? Math.round(num * eased).toString() : (num * eased).toFixed(0)) + suffix);
//             setProgress(Math.round(pct * 100));
//             if (pct < 1) frameRef.current = requestAnimationFrame(animate);
//         };
//         frameRef.current = requestAnimationFrame(animate);
//         return () => cancelAnimationFrame(frameRef.current);
//     }, [started, target, duration, suffix]);
//     return { display, progress };
// }

// function StatItem({ value, suffix, label, started, delay = 0 }) {
//     const [go, setGo] = useState(false);
//     useEffect(() => {
//         if (!started) return;
//         const t = setTimeout(() => setGo(true), delay);
//         return () => clearTimeout(t);
//     }, [started, delay]);
//     const { display, progress } = useCountUp(value, 1600, suffix, go);
//     return (
//         <div className="lp-stat">
//             <div className={`lp-stat-num${go && progress < 100 ? " counting" : ""}`}>{value === "0" ? "0" : display}</div>
//             <div className="lp-stat-label">{label}</div>
//             <div className="lp-stat-bar"><div className="lp-stat-bar-fill" style={{ width: `${progress}%` }} /></div>
//         </div>
//     );
// }

// const QR_PATTERN = [[1, 1, 1, 0, 1, 0], [1, 0, 1, 1, 0, 1], [1, 1, 1, 0, 1, 1], [0, 1, 0, 1, 0, 0], [1, 0, 1, 1, 1, 0], [0, 1, 0, 0, 1, 1]];

// function ScanScene() {
//     return (
//         <div className="scan-scene">
//             <div className="hero-orb" />
//             <div className="phone-scanner">
//                 <div className="phone-scanner-screen">
//                     <div className="scanner-viewfinder">
//                         <div className="vf-corner-tr" /><div className="vf-corner-bl" />
//                         <div className="scan-laser" />
//                         <div className="mini-qr">
//                             {QR_PATTERN.map((row, ri) => row.map((cell, ci) => (
//                                 <div key={`${ri}-${ci}`} className="mini-qr-cell"
//                                     style={{ background: cell ? "rgba(13,189,176,0.5)" : "transparent" }} />
//                             )))}
//                         </div>
//                         <div className="scanner-success-flash" />
//                     </div>
//                     <div className="scanner-label">Point at QR code</div>
//                 </div>
//             </div>
//             <div className="beam-line" /><div className="beam-particle" />
//             <div className="scan-ripple r1" /><div className="scan-ripple r2" /><div className="scan-ripple r3" />
//             <div className="scan-success"><div className="scan-success-dot" />Contact Saved ✓</div>
//             <div className="phone-profile">
//                 <div className="profile-card-wrap">
//                     <div className="profile-banner">
//                         <div className="profile-banner-img" /><div className="profile-banner-fade" />
//                         <div className="profile-avatar-ring"><div className="profile-avatar-inner">UN</div></div>
//                     </div>
//                     <div className="profile-info">
//                         <div className="profile-info-name">User <em>Name</em></div>
//                         <div className="profile-info-role">Director · Company Name</div>
//                         <div className="profile-tiles">
//                             {[["📞", "Phone"], ["✉️", "Email"], ["📍", "Delhi"]].map(([, l]) => (
//                                 <div className="profile-tile" key={l}>
//                                     <div className="tile-dot"><div className="tile-dot-inner" /></div>
//                                     <div className="tile-text">{l}</div>
//                                 </div>
//                             ))}
//                         </div>
//                         <button className="profile-save-btn">↓ Save Contact</button>
//                         <div className="profile-tiles" style={{ marginTop: "1rem" }}>
//                             {[["🌐", "Website"], ["📄", "Resume"], ["ⓕ", "Facebook"]].map(([, l]) => (
//                                 <div className="profile-tile" key={l}>
//                                     <div className="tile-dot"><div className="tile-dot-inner" /></div>
//                                     <div className="tile-text">{l}</div>
//                                 </div>
//                             ))}
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// }

// function ArrowBridge({ filled }) {
//     return (
//         <div className="lp-how-bridge">
//             <svg width="36" height="28" viewBox="0 0 36 28" fill="none" overflow="visible">
//                 <path d="M2 14 L26 14" className={`how-arrow-path${filled ? " filled" : ""}`} strokeWidth="1.5" strokeLinecap="round" fill="none"
//                     style={{ strokeDasharray: 28, strokeDashoffset: filled ? 0 : 28, transition: "stroke-dashoffset 0.8s cubic-bezier(0.22,1,0.36,1),stroke 0.4s ease" }} />
//                 <path d="M20 7 L28 14 L20 21" className={`how-arrow-path${filled ? " filled" : ""}`} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"
//                     style={{ strokeDasharray: 24, strokeDashoffset: filled ? 0 : 24, transition: "stroke-dashoffset 0.6s cubic-bezier(0.22,1,0.36,1) 0.3s,stroke 0.4s ease 0.3s" }} />
//             </svg>
//         </div>
//     );
// }

// function LogoSVG({ size = 34 }) {
//     return (
//         <svg width={size} height={size} viewBox="0 0 34 34" fill="none">
//             <rect width="34" height="34" rx="9" fill="rgba(13,189,176,0.10)" />
//             <rect width="34" height="34" rx="9" stroke="rgba(13,189,176,0.30)" strokeWidth="0.5" />
//             <circle cx="15" cy="13" r="5" stroke="#0DBDB0" strokeWidth="1.5" fill="none" />
//             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="#0DBDB0" strokeWidth="1.5" strokeLinecap="round" fill="none" />
//             <path d="M23 11 L27 11 L27 23" stroke="#F5A623" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
//             <circle cx="27" cy="23" r="1.8" fill="#F5A623" />
//         </svg>
//     );
// }

// const USE_CASES = [
//     { icon: "👔", role: "Job Seekers", tagline: "Land your next opportunity faster", desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one elegant scan.", highlights: ["Resume attached & downloadable", "LinkedIn & GitHub links", "QR code for instant sharing", "Save contact in one tap"] },
//     { icon: "💼", role: "Freelancers", tagline: "Win clients before you even speak", desc: "Share everything a client needs — portfolio, social proof, and contact — all in a single professional link.", highlights: ["Portfolio & website link", "WhatsApp quick contact", "Instagram & social proof", "Professional digital presence"] },
//     { icon: "🏢", role: "Business Owners", tagline: "Network smarter, not harder", desc: "Stop spending on printed cards that get lost. Share via QR at events — always updated, never outdated.", highlights: ["Always up-to-date details", "Google Wallet for offline use", "QR code for events & meetups", "Share via WhatsApp or email"] },
//     { icon: "🎓", role: "Students", tagline: "Build your professional identity early", desc: "Stand out in campus placements and internship drives. Show your skills, projects, and social profiles.", highlights: ["GitHub & project links", "Resume upload for placements", "LinkedIn profile showcase", "Free forever — no cost"] },
//     { icon: "📣", role: "Sales Pros", tagline: "Close the follow-up gap instantly", desc: "At trade shows or cold visits — one QR scan and your prospect has everything they need.", highlights: ["Instant QR share at events", "Website & product links", "Save contact in one tap", "Google Wallet for quick access"] },
//     { icon: "🧑‍💻", role: "Developers", tagline: "Your entire dev profile in one link", desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share at hackathons.", highlights: ["GitHub & portfolio links", "Twitter / X profile", "Resume for job applications", "Professional dev identity"] },
// ];

// function UseCasesSection({ navigate }) {
//     const [active, setActive] = useState(0);
//     const [tabKey, setTabKey] = useState(0);
//     const cur = USE_CASES[active];
//     const switchTab = (i) => { setActive(i); setTabKey(k => k + 1); };
//     return (
//         <section className="lp-section">
//             <div className="lp-section-inner">
//                 <span className="lp-eyebrow reveal from-up">Who is it for</span>
//                 <h2 className="lp-section-title reveal from-up d1">Built for every <em>professional</em></h2>
//                 <p className="lp-section-sub reveal from-up d2">Whether you're a student or a CEO — your digital card works for you.</p>
//                 <div className="lp-use-tabs reveal from-up d3">
//                     {USE_CASES.map((u, i) => (
//                         <button key={i} className="lp-use-tab" onClick={() => switchTab(i)}
//                             style={{ border: active === i ? "0.5px solid var(--border-teal)" : "0.5px solid var(--border2)", background: active === i ? "var(--teal-subtle2)" : "transparent", color: active === i ? "var(--teal)" : "var(--text-muted)" }}>
//                             {u.icon} {u.role}
//                         </button>
//                     ))}
//                 </div>
//                 <div className="lp-use-panel reveal from-up d4">
//                     <div key={`l-${tabKey}`} className="lp-use-content">
//                         <div className="lp-use-badge"><span>{cur.icon}</span><span style={{ fontSize: "12px" }}>{cur.role}</span></div>
//                         <div className="lp-use-tagline">{cur.tagline}</div>
//                         <p className="lp-use-desc">{cur.desc}</p>
//                         <button className="lp-btn-primary" onClick={() => navigate("/animation/card")}>Create My Card — Free →</button>
//                     </div>
//                     <div key={`r-${tabKey}`} className="lp-use-highlights">
//                         {cur.highlights.map((h, i) => (
//                             <div className="lp-use-hl" key={i} style={{ animationDelay: `${0.05 + i * 0.07}s` }}>
//                                 <div className="lp-use-hl-check">✓</div>
//                                 <span className="lp-use-hl-text">{h}</span>
//                             </div>
//                         ))}
//                     </div>
//                 </div>
//             </div>
//         </section>
//     );
// }

// export default function LandingPage() {
//     const navigate = useNavigate();
//     const [scrolled, setScrolled] = useState(false);
//     const [menuOpen, setMenuOpen] = useState(false);
//     const [statsStarted, setStatsStarted] = useState(false);
//     const [howVisible, setHowVisible] = useState(false);
//     const statsRef = useRef(null);
//     const howRef = useRef(null);

//     useEffect(() => {
//         const onResize = () => { if (window.innerWidth > 900) setMenuOpen(false); };
//         window.addEventListener("resize", onResize);
//         return () => window.removeEventListener("resize", onResize);
//     }, []);

//     useEffect(() => {
//         const onScroll = () => setScrolled(window.scrollY > 40);
//         window.addEventListener("scroll", onScroll);
//         return () => window.removeEventListener("scroll", onScroll);
//     }, []);

//     useEffect(() => {
//         const obs = [];
//         const revObs = new IntersectionObserver(
//             entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
//             { threshold: 0.1 }
//         );
//         document.querySelectorAll(".reveal").forEach(el => revObs.observe(el));
//         obs.push(revObs);
//         if (statsRef.current) {
//             const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsStarted(true); }, { threshold: 0.5 });
//             o.observe(statsRef.current); obs.push(o);
//         }
//         if (howRef.current) {
//             const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setHowVisible(true); }, { threshold: 0.3 });
//             o.observe(howRef.current); obs.push(o);
//         }
//         const pillObs = new IntersectionObserver(
//             entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("pills-visible"); }),
//             { threshold: 0.4 }
//         );
//         document.querySelectorAll(".lp-feat-bcard.has-pills").forEach(c => pillObs.observe(c));
//         obs.push(pillObs);
//         return () => obs.forEach(o => o.disconnect());
//     }, []);

//     return (
//         <div style={{ background: "var(--ink)", minHeight: "100vh" }}>
//             <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

//             {/* MOBILE DRAWER */}
//             <div className={`lp-mobile-menu${menuOpen ? " open" : ""}`}>
//                 <div className="lp-mobile-menu-backdrop" onClick={() => setMenuOpen(false)} />
//                 <div className="lp-mobile-menu-panel">
//                     <div className="lp-mobile-menu-header">
//                         <a href="/" className="lp-logo" onClick={() => setMenuOpen(false)}><LogoSVG size={28} />myprofile<em>corner</em></a>
//                         <button className="lp-mobile-close" onClick={() => setMenuOpen(false)}>✕</button>
//                     </div>
//                     <div className="lp-mobile-links">
//                         <a href="#how" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">⚡</span>How it works</a>
//                         <a href="#features" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">✦</span>Features</a>
//                         <a href="#pricing" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">₹</span>Pricing</a>
//                     </div>
//                     <div className="lp-mobile-divider" />
//                     <div className="lp-mobile-menu-footer">
//                         <button className="lp-mobile-cta" onClick={() => { setMenuOpen(false); navigate("/animation/card"); }}>Create My Card — Free →</button>
//                         <div className="lp-mobile-tagline">100% Free · No app required · 2 min setup</div>
//                     </div>
//                 </div>
//             </div>

//             {/* NAV */}
//             <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
//                 <div className="lp-nav-inner">
//                     <a href="/" className="lp-logo"><LogoSVG size={34} />myprofile<em>corner</em></a>
//                     <div className="lp-nav-links">
//                         <a href="#how" className="lp-nav-link">How it works</a>
//                         <a href="#features" className="lp-nav-link">Features</a>
//                         <a href="#pricing" className="lp-nav-link">Pricing</a>
//                         <button className="lp-nav-cta" onClick={() => navigate("/animation/card")}>Create Free Card</button>
//                     </div>
//                     <div className="lp-mobile-cluster">
//                         <button className={`lp-hamburger${menuOpen ? " open" : ""}`} onClick={() => setMenuOpen(p => !p)} aria-label="Menu">
//                             <span /><span /><span />
//                         </button>
//                     </div>
//                 </div>
//             </nav>

//             {/* HERO */}
//             <div className="lp-hero-wrap">
//                 <div className="lp-hero-mesh" /><div className="lp-hero-mesh-bottom" /><div className="lp-hero-grid" />
//                 <div className="lp-hero">
//                     <div className="lp-hero-left">
//                         <div className="lp-hero-eyebrow reveal from-down">
//                             <div className="lp-hero-eyebrow-dot" />100% Free · No App Needed
//                         </div>
//                         <h1 className="lp-hero-title reveal from-left">
//                             Your Digital Identity,<br />
//                             <span className="gradient-line">Always in Your Pocket</span>
//                         </h1>
//                         <p className="lp-hero-sub reveal from-left d1">
//                             Replace paper visiting cards with a smart digital card. Share your profile instantly via QR code, link, or Google Wallet — no printing, no waste.
//                         </p>
//                         <div className="lp-hero-stats" ref={statsRef}>
//                             <StatItem value="100" suffix="%" label="Free forever" started={statsStarted} delay={0} />
//                             <StatItem value="2" suffix=" min" label="Setup time" started={statsStarted} delay={200} />
//                             <StatItem value="0" suffix="" label="Paper waste" started={statsStarted} delay={400} />
//                         </div>
//                         <div className="lp-hero-ctas reveal from-up d2">
//                             <button className="lp-btn-primary" onClick={() => navigate("/animation/card")}>Create My Card — It's Free →</button>
//                             <a href="#how" className="lp-btn-ghost">See how it works</a>
//                         </div>
//                     </div>
//                     <div className="lp-hero-right reveal from-right"><ScanScene /></div>
//                 </div>
//             </div>

//             <div className="lp-rule" />

//             {/* TRUST BAR */}
//             <div className="lp-trust">
//                 <span className="lp-trust-label">Works seamlessly with</span>
//                 <div className="lp-trust-items">
//                     {[["🔥", "Firebase"], ["🪙", "Google Wallet"], ["📱", "Android & iOS"], ["🔗", "Any Browser"]].map(([icon, label]) => (
//                         <div className="lp-trust-item" key={label}>
//                             <div className="lp-trust-icon">{icon}</div><span>{label}</span>
//                         </div>
//                     ))}
//                 </div>
//             </div>

//             <div className="lp-rule" />

//             {/* HOW IT WORKS */}
//             <section id="how" className="lp-section" ref={howRef}>
//                 <div className="lp-section-inner">
//                     <span className="lp-eyebrow reveal from-up">How it works</span>
//                     <h2 className="lp-section-title reveal from-up d1">From setup to sharing <em>in 3 steps</em></h2>
//                     <p className="lp-section-sub reveal from-up d2">No app downloads. No tech skills required. Just fill, share, impress.</p>
//                     <div className="lp-how-timeline">
//                         <div className="lp-how-card reveal from-left d1">
//                             <div className="lp-how-num">01</div>
//                             <div className="lp-how-icon-ring">✏️</div>
//                             <div className="lp-how-step-label">Step 01</div>
//                             <div className="lp-how-title">Fill Your Profile</div>
//                             <p className="lp-how-desc">Add your name, photo, contact info, social links, resume & website. Takes less than 2 minutes.</p>
//                         </div>
//                         <ArrowBridge filled={howVisible} />
//                         <div className="lp-how-card reveal from-up d3">
//                             <div className="lp-how-num">02</div>
//                             <div className="lp-how-icon-ring">🎴</div>
//                             <div className="lp-how-step-label">Step 02</div>
//                             <div className="lp-how-title">Get Your Digital Card</div>
//                             <p className="lp-how-desc">Instantly get a beautiful card with a unique QR code & shareable link — no design skills needed.</p>
//                         </div>
//                         <ArrowBridge filled={howVisible} />
//                         <div className="lp-how-card reveal from-right d5">
//                             <div className="lp-how-num">03</div>
//                             <div className="lp-how-icon-ring">🚀</div>
//                             <div className="lp-how-step-label">Step 03</div>
//                             <div className="lp-how-title">Share Anywhere</div>
//                             <p className="lp-how-desc">Show QR in meetings, add to Google Wallet, or send your link via WhatsApp, email, or LinkedIn.</p>
//                         </div>
//                     </div>
//                 </div>
//             </section>

//             <div className="lp-rule" />

//             {/* FEATURES */}
//             <section id="features" className="lp-section" style={{ background: "var(--ink3)" }}>
//                 <div className="lp-section-inner">
//                     <span className="lp-eyebrow reveal from-up">Features</span>
//                     <h2 className="lp-section-title reveal from-up d1">Everything a modern <em>professional</em> needs</h2>
//                     <p className="lp-section-sub reveal from-up d2">One card. Every detail. Always up-to-date.</p>
//                     <div className="lp-feat-bento">

//                         {/* Hero card — Google Wallet */}
//                         <div className="lp-feat-bcard hero-card reveal from-left d1">
//                             <div className="lp-feat-bicon"><IconWallet /></div>
//                             <div className="lp-feat-btag">Premium Feature</div>
//                             <div className="lp-feat-btitle">Google Wallet Pass</div>
//                             <p className="lp-feat-bdesc">Add your card to Google Wallet and access it offline — at meetings, events, or anywhere without internet.</p>
//                             <div className="lp-feat-wallet">
//                                 <div className="lp-wallet-chip">G Pay</div>
//                                 <div className="lp-wallet-chip">Wallet</div>
//                                 <div className="lp-wallet-chip">Offline ✓</div>
//                             </div>
//                         </div>

//                         {/* Tall — Edit Anytime */}
//                         <div className="lp-feat-bcard tall reveal from-right d2">
//                             <div className="lp-feat-bicon"><IconEdit /></div>
//                             <div className="lp-feat-btag">Always Fresh</div>
//                             <div className="lp-feat-btitle">Edit Anytime</div>
//                             <p className="lp-feat-bdesc">Changed jobs? New number? Update instantly — your QR code & link stay the same forever.</p>
//                             <EditAnytimeVisual />
//                         </div>

//                         {/* Profile Photo */}
//                         <div className="lp-feat-bcard reveal from-up d3">
//                             <div className="lp-feat-bicon"><IconPhoto /></div>
//                             <div className="lp-feat-btag">Identity</div>
//                             <div className="lp-feat-btitle">Profile Photo</div>
//                             <p className="lp-feat-bdesc">Your face on your card — and inside Google Wallet too.</p>
//                         </div>

//                         {/* Save Contact */}
//                         <div className="lp-feat-bcard reveal from-up d4">
//                             <div className="lp-feat-bicon"><IconContact /></div>
//                             <div className="lp-feat-btag">One Tap</div>
//                             <div className="lp-feat-btitle">Save Contact</div>
//                             <p className="lp-feat-bdesc">Visitors save you directly to their phone contacts with a single tap.</p>
//                         </div>

//                         {/* Works Everywhere */}
//                         <div className="lp-feat-bcard reveal from-right d5">
//                             <div className="lp-feat-bicon"><IconGlobe /></div>
//                             <div className="lp-feat-btag">Universal</div>
//                             <div className="lp-feat-btitle">Works Everywhere</div>
//                             <p className="lp-feat-bdesc">Opens in any browser on any device. No app install ever required.</p>
//                         </div>

//                         {/* Tall — QR Code */}
//                         <div className="lp-feat-bcard tall reveal from-right d2">
//                             <div className="lp-feat-bicon"><IconQR /></div>
//                             <div className="lp-feat-btag">Instant Share</div>
//                             <div className="lp-feat-btitle">Unique QR Code</div>
//                             <p className="lp-feat-bdesc">Every card gets a dedicated QR. One scan opens your full profile — no typing, no friction.</p>
//                             <div style={{ marginTop: "auto", paddingTop: "1.5rem" }}>
//                                 <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gridTemplateRows: "repeat(5,1fr)", gap: 4, opacity: 0.25 }}>
//                                     {[1, 1, 1, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 1].map((v, i) => (
//                                         <div key={i} style={{ background: v ? "var(--teal)" : "var(--teal-subtle)", borderRadius: 2, aspectRatio: "1" }} />
//                                     ))}
//                                 </div>
//                                 {/* Decorative scan line */}
//                                 <div style={{ marginTop: 10, height: 1.5, borderRadius: 2, background: "linear-gradient(90deg,transparent,var(--teal),transparent)", opacity: 0.5, animation: "laser-sweep 2.5s ease-in-out infinite" }} />
//                             </div>
//                         </div>

//                         {/* Hero card — Social Links */}
//                         <div className="lp-feat-bcard hero-card has-pills reveal from-left d2">
//                             <div className="lp-feat-bicon"><IconLink /></div>
//                             <div className="lp-feat-btag">Connections</div>
//                             <div className="lp-feat-btitle">All Social Links</div>
//                             <p className="lp-feat-bdesc">LinkedIn, GitHub, WhatsApp, YouTube, Instagram — all linked from a single professional card.</p>
//                             <div className="lp-feat-socials">
//                                 {["in LinkedIn", "⌥ GitHub", "✉ WhatsApp", "▶ YouTube", "📷 Insta"].map(s => (
//                                     <span key={s} className="lp-social-pill">{s}</span>
//                                 ))}
//                             </div>
//                         </div>

//                         {/* Resume Upload */}
//                         <div className="lp-feat-bcard reveal from-up d3">
//                             <div className="lp-feat-bicon"><IconResume /></div>
//                             <div className="lp-feat-btag">Career</div>
//                             <div className="lp-feat-btitle">Resume Upload</div>
//                             <p className="lp-feat-bdesc">Attach your PDF resume. Recruiters can download it instantly.</p>
//                         </div>

//                     </div>
//                 </div>
//             </section>

//             <div className="lp-rule" />
//             <UseCasesSection navigate={navigate} />
//             <div className="lp-rule" />

//             {/* PRICING */}
//             <section id="pricing" className="lp-pricing-cta">
//                 <div className="lp-pricing-cta-inner">
//                     <div>
//                         <span className="lp-pricing-eyebrow reveal from-left">Pricing</span>
//                         <h2 className="lp-pricing-headline reveal from-left d1">Stop carrying<br /><em>paper cards.</em></h2>
//                         <p className="lp-pricing-sub reveal from-left d2">Create your digital card in under 2 minutes. No hidden charges. No credit card. Free forever.</p>
//                         <div className="lp-pricing-perks reveal from-left d3">
//                             {["No app download needed", "Unique QR code & shareable link", "Google Wallet pass included", "Edit anytime — same link forever", "Works on every device & browser"].map(p => (
//                                 <div className="lp-pricing-perk" key={p}><div className="lp-perk-tick">✓</div><span>{p}</span></div>
//                             ))}
//                         </div>
//                         <button className="lp-btn-primary reveal from-up d4" onClick={() => navigate("/animation/card")}>Create My Card Now →</button>
//                     </div>
//                     <div className="lp-price-card reveal from-right d2">
//                         <div className="lp-price-card-top">
//                             <div className="lp-price-badge">Most Popular</div>
//                             <div className="lp-price-amount">
//                                 <div className="lp-price-num">₹0</div>
//                                 <div className="lp-price-period">Forever Free</div>
//                             </div>
//                         </div>
//                         <div className="lp-price-divider" />
//                         <ul className="lp-price-list">
//                             {["1 Digital Visiting Card", "Unique QR Code & Link", "Profile Photo Upload", "Resume Upload (PDF)", "All Social Media Links", "Google Wallet Pass", "Save Contact (.vcf)", "Edit Anytime", "No App Required"].map(item => (
//                                 <li className="lp-price-item" key={item}><span className="lp-price-check">✦</span>{item}</li>
//                             ))}
//                         </ul>
//                         <button className="lp-btn-primary" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/animation/card")}>Get Started Free →</button>
//                     </div>
//                 </div>
//             </section>

//             <div className="lp-rule" />

//             {/* FOOTER */}
//             <footer className="lp-footer">
//                 <div className="lp-footer-inner">
//                     <div className="lp-footer-logo"><LogoSVG size={28} />myprofile<em>corner</em></div>
//                     <p className="lp-footer-tagline">Your digital identity, always in your pocket.</p>
//                     <p className="lp-footer-copy">© {new Date().getFullYear()} MyProfileCorner · Made with ♥ in India</p>
//                 </div>
//             </footer>
//         </div>
//     );
// }



// // import React, { useState, useEffect, useRef } from "react";
// // import { useNavigate } from "react-router-dom";

// // const FONT_LINK = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,600&display=swap');`;

// // const GLOBAL_CSS = `
// // ${FONT_LINK}

// // *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

// // :root {
// //   --navy:           #F4F5FF;
// //   --navy2:          #ECEEFF;
// //   --navy3:          #E6E8FF;
// //   --navy4:          #DDE0FF;
// //   --navy5:          #D4D7FF;
// //   --violet:         #6355E8;
// //   --violet2:        #7C6FF7;
// //   --violet3:        #9B8CFF;
// //   --violet-dim:     rgba(99,85,232,0.12);
// //   --violet-subtle:  rgba(99,85,232,0.07);
// //   --violet-subtle2: rgba(99,85,232,0.13);
// //   --blue:           #4F63D2;
// //   --blue2:          #5B72F0;
// //   --blue3:          #3A4DBB;
// //   --text-primary:   #1A1640;
// //   --text-muted:     rgba(40,35,90,0.65);
// //   --text-muted2:    rgba(40,35,90,0.40);
// //   --border:         rgba(99,85,232,0.12);
// //   --border2:        rgba(99,85,232,0.10);
// //   --border-violet:  rgba(99,85,232,0.22);
// //   --surface:        #FFFFFF;
// //   --surface2:       #F8F8FF;
// //   --surface3:       #F0F1FF;
// //   --serif:  'Fraunces', serif;
// //   --sans:   'Plus Jakarta Sans', sans-serif;
// // }

// // html { scroll-behavior: smooth; }
// // body { background: var(--navy); color: var(--text-primary); font-family: var(--sans); overflow-x: hidden; }
// // ::-webkit-scrollbar { width: 4px; }
// // ::-webkit-scrollbar-track { background: var(--navy); }
// // ::-webkit-scrollbar-thumb { background: rgba(99,85,232,0.3); border-radius: 2px; }

// // /* REVEALS */
// // .reveal { opacity: 0; transition: opacity 0.75s cubic-bezier(0.22,1,0.36,1), transform 0.75s cubic-bezier(0.22,1,0.36,1); }
// // .reveal.from-up    { transform: translateY(36px); }
// // .reveal.from-down  { transform: translateY(-24px); }
// // .reveal.from-left  { transform: translateX(-44px); }
// // .reveal.from-right { transform: translateX(44px); }
// // .reveal.d1{transition-delay:0.05s;} .reveal.d2{transition-delay:0.13s;} .reveal.d3{transition-delay:0.21s;}
// // .reveal.d4{transition-delay:0.29s;} .reveal.d5{transition-delay:0.37s;} .reveal.d6{transition-delay:0.45s;}
// // .reveal.visible { opacity: 1; transform: none; }

// // /* NAV */
// // .lp-nav { position:fixed; top:0; left:0; right:0; z-index:200; padding:1.4rem 3rem; transition:all 0.4s; }
// // .lp-nav.scrolled { background:rgba(244,245,255,0.94); backdrop-filter:blur(20px); border-bottom:0.5px solid var(--border); padding:0.9rem 3rem; box-shadow:0 4px 32px rgba(99,85,232,0.08); }
// // .lp-nav-inner { max-width:1180px; margin:0 auto; display:flex; align-items:center; justify-content:space-between; }
// // .lp-logo { display:flex; align-items:center; gap:10px; font-family:var(--sans); font-size:15px; font-weight:700; color:var(--text-primary); text-decoration:none; flex-shrink:0; }
// // .lp-logo em { color:var(--violet); font-style:normal; }
// // .lp-nav-links { display:flex; align-items:center; gap:2.5rem; }
// // .lp-nav-link { font-size:13.5px; font-weight:500; color:var(--text-muted); text-decoration:none; transition:color 0.2s; }
// // .lp-nav-link:hover { color:var(--text-primary); }
// // .lp-nav-cta { padding:10px 22px; background:linear-gradient(135deg,var(--blue2),var(--violet)); color:#fff; border:none; border-radius:10px; font-family:var(--sans); font-size:13px; font-weight:700; cursor:pointer; position:relative; overflow:hidden; transition:transform 0.15s,box-shadow 0.15s; white-space:nowrap; }
// // .lp-nav-cta::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.18),transparent 60%); pointer-events:none; }
// // .lp-nav-cta:hover { transform:translateY(-1px); box-shadow:0 6px 20px rgba(99,85,232,0.25); }

// // /* HAMBURGER */
// // .lp-hamburger { display:none; flex-direction:column; justify-content:center; align-items:center; gap:5px; width:40px; height:40px; border-radius:10px; border:0.5px solid var(--border-violet); background:var(--violet-subtle); cursor:pointer; flex-shrink:0; transition:background 0.2s; }
// // .lp-hamburger:hover { background:var(--violet-subtle2); }
// // .lp-hamburger span { display:block; width:18px; height:1.5px; background:var(--violet); border-radius:2px; transition:all 0.3s cubic-bezier(0.22,1,0.36,1); transform-origin:center; }
// // .lp-hamburger.open span:nth-child(1){transform:translateY(6.5px) rotate(45deg);}
// // .lp-hamburger.open span:nth-child(2){opacity:0;transform:scaleX(0);}
// // .lp-hamburger.open span:nth-child(3){transform:translateY(-6.5px) rotate(-45deg);}

// // /* MOBILE MENU */
// // .lp-mobile-menu { position:fixed; top:0; left:0; right:0; bottom:0; z-index:190; display:flex; flex-direction:column; pointer-events:none; }
// // .lp-mobile-menu-backdrop { position:absolute; inset:0; background:rgba(20,15,60,0.3); backdrop-filter:blur(6px); opacity:0; transition:opacity 0.35s ease; }
// // .lp-mobile-menu.open .lp-mobile-menu-backdrop { opacity:1; pointer-events:all; }
// // .lp-mobile-menu-panel { position:absolute; top:0; right:0; width:min(320px,85vw); height:100%; background:var(--surface); border-left:0.5px solid var(--border-violet); display:flex; flex-direction:column; transform:translateX(100%); transition:transform 0.4s cubic-bezier(0.22,1,0.36,1); box-shadow:-20px 0 60px rgba(99,85,232,0.1); pointer-events:none; overflow:hidden; }
// // .lp-mobile-menu.open .lp-mobile-menu-panel { transform:translateX(0); pointer-events:all; }
// // .lp-mobile-menu-header { display:flex; align-items:center; justify-content:space-between; padding:1.25rem 1.5rem; border-bottom:0.5px solid var(--border2); }
// // .lp-mobile-close { width:36px; height:36px; border-radius:9px; border:0.5px solid var(--border2); background:var(--surface2); display:flex; align-items:center; justify-content:center; cursor:pointer; color:var(--text-muted); font-size:16px; transition:background 0.2s; }
// // .lp-mobile-close:hover { background:var(--surface3); color:var(--text-primary); }
// // .lp-mobile-links { flex:1; display:flex; flex-direction:column; padding:1.5rem; gap:0.25rem; }
// // .lp-mobile-link { display:flex; align-items:center; gap:12px; padding:1rem 1.25rem; border-radius:14px; font-family:var(--sans); font-size:15px; font-weight:600; color:var(--text-muted); text-decoration:none; border:0.5px solid transparent; opacity:0; transform:translateX(20px); transition:background 0.2s,color 0.2s,border-color 0.2s,opacity 0.35s ease,transform 0.35s cubic-bezier(0.22,1,0.36,1); }
// // .lp-mobile-link:nth-child(1){transition-delay:0.06s;} .lp-mobile-link:nth-child(2){transition-delay:0.11s;} .lp-mobile-link:nth-child(3){transition-delay:0.16s;}
// // .lp-mobile-menu.open .lp-mobile-link { opacity:1; transform:translateX(0); }
// // .lp-mobile-link:hover { background:var(--surface3); color:var(--text-primary); border-color:var(--border-violet); }
// // .lp-mobile-link-icon { font-size:16px; width:20px; text-align:center; }
// // .lp-mobile-divider { height:0.5px; background:linear-gradient(90deg,transparent,var(--border-violet),transparent); margin:0.5rem 1.5rem; }
// // .lp-mobile-menu-footer { padding:1.5rem; border-top:0.5px solid var(--border2); display:flex; flex-direction:column; gap:0.75rem; opacity:0; transform:translateY(12px); transition:opacity 0.35s ease 0.22s,transform 0.35s ease 0.22s; }
// // .lp-mobile-menu.open .lp-mobile-menu-footer { opacity:1; transform:translateY(0); }
// // .lp-mobile-cta { width:100%; padding:14px; background:linear-gradient(135deg,var(--blue2),var(--violet)); color:#fff; border:none; border-radius:12px; font-family:var(--sans); font-size:14px; font-weight:700; cursor:pointer; position:relative; overflow:hidden; }
// // .lp-mobile-cta::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.15),transparent 60%); pointer-events:none; }
// // .lp-mobile-tagline { text-align:center; font-size:11px; font-weight:500; color:var(--text-muted2); letter-spacing:0.5px; }
// // .lp-mobile-cluster { display:none; align-items:center; gap:10px; }

// // /* HERO */
// // .lp-hero-wrap { position:relative; overflow:hidden; background:var(--navy); }
// // .lp-hero-mesh { position:absolute; inset:0; pointer-events:none; overflow:hidden; }
// // .lp-hero-mesh::before { content:''; position:absolute; width:900px; height:900px; border-radius:50%; background:radial-gradient(circle,rgba(99,85,232,0.10) 0%,rgba(80,65,200,0.04) 40%,transparent 70%); top:-300px; left:-200px; }
// // .lp-hero-mesh::after { content:''; position:absolute; width:700px; height:700px; border-radius:50%; background:radial-gradient(circle,rgba(99,85,232,0.08) 0%,transparent 65%); top:-100px; right:-150px; }
// // .lp-hero-mesh-bottom { position:absolute; width:500px; height:500px; border-radius:50%; background:radial-gradient(circle,rgba(80,65,200,0.06) 0%,transparent 70%); bottom:-200px; right:20%; pointer-events:none; }
// // .lp-hero-grid { position:absolute; inset:0; background-image:radial-gradient(circle,rgba(99,85,232,0.14) 1px,transparent 1px); background-size:48px 48px; opacity:0.4; pointer-events:none; }
// // .lp-hero { min-height:100vh; display:grid; grid-template-columns:1fr 1fr; align-items:center; gap:4rem; max-width:1180px; margin:0 auto; padding:9rem 3rem 4rem; position:relative; z-index:2; }

// // /* Hero eyebrow */
// // .lp-hero-eyebrow { display:inline-flex; align-items:center; gap:8px; padding:7px 16px; background:rgba(99,85,232,0.08); border:0.5px solid rgba(99,85,232,0.25); border-radius:100px; font-size:11.5px; font-weight:700; color:var(--violet); letter-spacing:1px; text-transform:uppercase; margin-bottom:2rem; }
// // .lp-hero-eyebrow-dot { width:6px; height:6px; border-radius:50%; background:var(--violet); animation:pulse-violet 2s ease-in-out infinite; }
// // @keyframes pulse-violet { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:0.3;transform:scale(0.65);} }

// // /* Hero title */
// // .lp-hero-title { font-family:var(--sans); font-size:clamp(3rem,5.5vw,5.2rem); font-weight:800; line-height:1.05; letter-spacing:-1.5px; color:var(--text-primary); margin-bottom:1.75rem; }
// // .lp-hero-title .gradient-line { display:block; background:linear-gradient(100deg,#6355E8 0%,#7C6FF7 35%,#9B8CFF 65%,#B8AEFF 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }

// // .lp-hero-sub { font-size:16px; font-weight:400; color:var(--text-muted); line-height:1.85; max-width:480px; margin-bottom:1rem; }
// // .lp-hero-ctas { display:flex; gap:1rem; flex-wrap:wrap; }

// // /* BUTTONS */
// // .lp-btn-primary { padding:17px 34px; background:linear-gradient(135deg,var(--blue2),var(--violet)); color:#fff; border:none; border-radius:14px; font-family:var(--sans); font-size:14px; font-weight:700; letter-spacing:0.3px; cursor:pointer; position:relative; overflow:hidden; transition:transform 0.2s,box-shadow 0.2s; display:flex; align-items:center; gap:8px; white-space:nowrap; }
// // .lp-btn-primary::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.18),transparent 55%); pointer-events:none; }
// // .lp-btn-primary:hover { transform:translateY(-2px); box-shadow:0 10px 32px rgba(99,85,232,0.28); }
// // .lp-btn-ghost { padding:17px 28px; background:rgba(99,85,232,0.06); color:var(--text-muted); border:0.5px solid rgba(99,85,232,0.2); border-radius:14px; font-family:var(--sans); font-size:14px; font-weight:600; cursor:pointer; text-decoration:none; display:inline-flex; align-items:center; gap:8px; transition:border-color 0.2s,color 0.2s,background 0.2s; white-space:nowrap; }
// // .lp-btn-ghost:hover { border-color:var(--border-violet); color:var(--text-primary); background:var(--violet-subtle2); }

// // /* STATS */
// // .lp-hero-stats { display:flex; gap:2.5rem; padding-bottom:1.5rem; margin-bottom:1.5rem; }
// // .lp-stat { position:relative; }
// // .lp-stat-num { font-family:var(--sans); font-size:2.4rem; font-weight:800; color:var(--violet); line-height:1; letter-spacing:-1px; }
// // .lp-stat-num.counting { animation:stat-shimmer 0.08s ease infinite alternate; }
// // @keyframes stat-shimmer { from{opacity:0.6;} to{opacity:1;} }
// // .lp-stat-label { font-size:11px; font-weight:500; color:var(--text-muted2); letter-spacing:0.4px; margin-top:5px; }
// // .lp-stat-bar { height:2px; background:rgba(99,85,232,0.12); margin-top:8px; border-radius:1px; overflow:hidden; }
// // .lp-stat-bar-fill { height:100%; background:linear-gradient(90deg,var(--violet),var(--violet3)); border-radius:1px; width:0%; transition:width 0.05s linear; }

// // /* SCAN SCENE */
// // .lp-hero-right { position:relative; display:flex; align-items:center; justify-content:center; z-index:2; }
// // .scan-scene { position:relative; width:380px; height:480px; }
// // .scan-ripple { position:absolute; right:-10px; top:50px; width:60px; height:60px; border-radius:50%; border:1.5px solid rgba(99,85,232,0); z-index:10; pointer-events:none; }
// // .scan-ripple.r1{animation:ripple-out 2.5s ease-out infinite;animation-delay:3.2s;}
// // .scan-ripple.r2{animation:ripple-out 2.5s ease-out infinite;animation-delay:3.6s;}
// // .scan-ripple.r3{animation:ripple-out 2.5s ease-out infinite;animation-delay:4.0s;}
// // @keyframes ripple-out { 0%{transform:scale(1);border-color:rgba(99,85,232,0.5);opacity:1;} 100%{transform:scale(3.5);border-color:rgba(99,85,232,0);opacity:0;} }
// // .scan-success { position:absolute; right:8px; top:38px; background:linear-gradient(135deg,var(--violet),var(--violet3)); color:#fff; border-radius:100px; padding:5px 12px 5px 9px; font-family:var(--sans); font-size:9.5px; font-weight:700; letter-spacing:0.3px; display:flex; align-items:center; gap:6px; z-index:12; white-space:nowrap; opacity:0; animation:success-pop 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards; animation-delay:3.1s; box-shadow:0 4px 18px rgba(99,85,232,0.28); }
// // .scan-success-dot { width:6px; height:6px; border-radius:50%; background:rgba(255,255,255,0.7); }
// // @keyframes success-pop { 0%{opacity:0;transform:scale(0.5) translateY(6px);} 100%{opacity:1;transform:scale(1) translateY(0);} }
// // .phone-scanner { position:absolute; left:0; top:60px; width:155px; height:270px; background:#fff; border-radius:22px; border:1px solid rgba(99,85,232,0.18); overflow:hidden; box-shadow:0 24px 60px rgba(99,85,232,0.12); z-index:3; animation:phone-a-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both; animation-delay:0.3s; }
// // @keyframes phone-a-intro { from{opacity:0;transform:translateX(-30px) rotate(-6deg);} to{opacity:1;transform:translateX(0) rotate(-6deg);} }
// // .phone-scanner-screen { width:100%; height:100%; background:var(--navy); display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; padding:12px; }
// // .scanner-viewfinder { width:100px; height:100px; border-radius:10px; position:relative; background:rgba(99,85,232,0.05); }
// // .scanner-viewfinder::before,.scanner-viewfinder::after { content:''; position:absolute; width:20px; height:20px; border-color:var(--violet); border-style:solid; }
// // .scanner-viewfinder::before { top:0;left:0;border-width:2px 0 0 2px;border-radius:6px 0 0 0; }
// // .scanner-viewfinder::after { bottom:0;right:0;border-width:0 2px 2px 0;border-radius:0 0 6px 0; }
// // .vf-corner-tr { position:absolute;top:0;right:0;width:20px;height:20px;border-top:2px solid var(--violet);border-right:2px solid var(--violet);border-radius:0 6px 0 0; }
// // .vf-corner-bl { position:absolute;bottom:0;left:0;width:20px;height:20px;border-bottom:2px solid var(--violet);border-left:2px solid var(--violet);border-radius:0 0 0 6px; }
// // .scan-laser { position:absolute;left:4px;right:4px;height:2px;background:linear-gradient(90deg,transparent,var(--violet),transparent);border-radius:1px;top:4px;animation:laser-sweep 2s ease-in-out infinite;animation-delay:1.2s; }
// // @keyframes laser-sweep { 0%{top:8px;opacity:0;} 10%{opacity:1;} 90%{opacity:1;} 100%{top:88px;opacity:0;} }
// // .scanner-success-flash { position:absolute; inset:0; background:rgba(99,85,232,0.1); opacity:0; animation:flash-once 0.4s ease forwards; animation-delay:3.0s; pointer-events:none; z-index:10; }
// // @keyframes flash-once { 0%{opacity:0;} 40%{opacity:1;} 100%{opacity:0;} }
// // .scanner-label { font-size:9px;font-weight:600;color:var(--text-muted2);letter-spacing:1px;text-align:center;text-transform:uppercase; }
// // .mini-qr { position:absolute;inset:10px;display:grid;grid-template-columns:repeat(6,1fr);grid-template-rows:repeat(6,1fr);gap:1.5px;opacity:0.6; }
// // .mini-qr-cell { border-radius:1px; }
// // .phone-profile { position:absolute;right:0;top:0;width:175px;height:310px;background:#fff;border-radius:24px;border:1px solid rgba(99,85,232,0.20);overflow:hidden;box-shadow:0 32px 80px rgba(99,85,232,0.10),0 0 0 1px rgba(99,85,232,0.06);z-index:4;animation:phone-b-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:0.5s; }
// // @keyframes phone-b-intro { from{opacity:0;transform:translateY(30px) rotate(4deg);} to{opacity:1;transform:translateY(0) rotate(4deg);} }
// // .profile-card-wrap { width:100%;height:100%;animation:card-reveal 0.7s cubic-bezier(0.22,1,0.36,1) both;animation-delay:2s; }
// // @keyframes card-reveal { from{transform:translateY(40px);opacity:0;} to{transform:translateY(0);opacity:1;} }
// // .profile-banner { width:100%;height:110px;background:linear-gradient(160deg,#ede9ff,#dcd3ff);position:relative;overflow:hidden; }
// // .profile-banner-img { width:100%;height:100%;background:linear-gradient(160deg,#e8e2ff 0%,#d4c8ff 50%,#c8b8f0 100%); }
// // .profile-banner-fade { position:absolute;bottom:0;left:0;right:0;height:50px;background:linear-gradient(transparent,#fff); }
// // .profile-avatar-ring { position:absolute;bottom:2px;left:14px;width:44px;height:44px;border-radius:50%;border:2px solid #fff;background:#ede9ff;overflow:hidden;box-shadow:0 0 0 1.5px rgba(99,85,232,0.4);animation:avatar-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:2.4s; }
// // @keyframes avatar-pop { from{transform:scale(0.3);opacity:0;} to{transform:scale(1);opacity:1;} }
// // .profile-avatar-inner { width:100%;height:100%;background:linear-gradient(145deg,#c8b8f0,#9b8cff);display:flex;align-items:center;justify-content:center;font-family:var(--sans);font-size:13px;font-weight:700;color:#fff;letter-spacing:1px; }
// // .profile-info { padding:28px 14px 12px; }
// // .profile-info-name { font-family:var(--sans);font-size:15px;font-weight:700;color:var(--text-primary);line-height:1.2;margin-bottom:2px; }
// // .profile-info-name em { font-style:normal;color:var(--violet); }
// // .profile-info-role { font-size:8px;font-weight:600;color:var(--text-muted2);letter-spacing:0.3px;margin-bottom:10px; }
// // .profile-tiles { display:grid;grid-template-columns:1fr 1fr 1fr;gap:5px;margin-bottom:8px; }
// // .profile-tile { background:rgba(99,85,232,0.06);border:0.5px solid rgba(99,85,232,0.12);border-radius:8px;padding:7px 6px;display:flex;flex-direction:column;gap:4px;animation:tile-slide 0.3s ease both; }
// // .profile-tile:nth-child(1){animation-delay:2.5s;} .profile-tile:nth-child(2){animation-delay:2.65s;} .profile-tile:nth-child(3){animation-delay:2.8s;}
// // @keyframes tile-slide { from{opacity:0;transform:translateY(6px);} to{opacity:1;transform:translateY(0);} }
// // .tile-dot { width:12px;height:12px;border-radius:50%;background:rgba(99,85,232,0.12);display:flex;align-items:center;justify-content:center; }
// // .tile-dot-inner { width:5px;height:5px;border-radius:50%;background:var(--violet); }
// // .tile-text { font-size:7px;color:var(--text-muted2);line-height:1.3;font-weight:500; }
// // .profile-save-btn { width:100%;padding:8px;background:linear-gradient(135deg,var(--blue2),var(--violet));color:#fff;border:none;border-radius:8px;font-size:8px;font-weight:700;letter-spacing:0.5px;text-transform:uppercase;cursor:default;display:flex;align-items:center;justify-content:center;gap:4px;animation:tile-slide 0.3s ease both;animation-delay:2.95s; }
// // .beam-line { position:absolute;top:165px;left:148px;width:90px;height:1px;background:linear-gradient(90deg,var(--violet),rgba(99,85,232,0));transform-origin:left center;animation:beam-appear 0.4s ease both;animation-delay:1.5s;z-index:5; }
// // @keyframes beam-appear { from{opacity:0;} to{opacity:1;} }
// // .beam-particle { position:absolute;top:160px;left:148px;width:6px;height:6px;border-radius:50%;background:var(--violet);animation:particle-travel 2s ease-in-out infinite;animation-delay:1.6s;z-index:6; }
// // @keyframes particle-travel { 0%{left:148px;opacity:0;transform:scale(0.5);} 10%{opacity:1;transform:scale(1);} 90%{opacity:1;} 100%{left:228px;opacity:0;transform:scale(0.5);} }
// // .hero-orb { position:absolute;width:500px;height:500px;border-radius:50%;background:radial-gradient(circle,rgba(99,85,232,0.05) 0%,transparent 65%);top:-100px;right:-100px;pointer-events:none;z-index:0; }

// // /* DIVIDER */
// // .lp-rule { height:0.5px; background:linear-gradient(90deg,transparent,rgba(99,85,232,0.18),transparent); }

// // /* TRUST BAR */
// // .lp-trust { padding:1.5rem 2rem; background:var(--surface); display:flex; flex-direction:column; align-items:center; gap:1rem; }
// // .lp-trust-label { font-size:10px;font-weight:600;color:var(--text-muted2);letter-spacing:2.5px;text-transform:uppercase; }
// // .lp-trust-items { display:flex;align-items:stretch;justify-content:center;flex-wrap:nowrap;width:100%;max-width:580px; }
// // .lp-trust-item { display:flex;flex-direction:column;align-items:center;gap:5px;flex:1;padding:0.6rem 0.75rem;border-right:0.5px solid var(--border2);font-size:11.5px;font-weight:600;color:var(--text-muted);text-align:center; }
// // .lp-trust-item:last-child { border-right:none; }
// // .lp-trust-icon { font-size:1.2rem;line-height:1;width:36px;height:36px;border-radius:10px;background:var(--violet-subtle);border:0.5px solid var(--border-violet);display:flex;align-items:center;justify-content:center;margin-bottom:2px; }

// // /* SECTIONS */
// // .lp-section { padding:5rem 3rem; position:relative; }
// // .lp-section-inner { max-width:1180px; margin:0 auto; }
// // .lp-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--violet);margin-bottom:1.25rem;text-align:center; }
// // .lp-section-title { font-family:var(--sans);font-size:clamp(2.2rem,4vw,3.4rem);font-weight:800;line-height:1.08;letter-spacing:-1px;color:var(--text-primary);text-align:center;margin-bottom:1.25rem; }
// // .lp-section-title em { font-style:italic;font-family:var(--serif);font-weight:600;color:var(--violet); }
// // .lp-section-sub { font-size:15px;font-weight:400;color:var(--text-muted);text-align:center;max-width:520px;margin:0 auto 4rem;line-height:1.9; }

// // /* HOW IT WORKS */
// // .lp-how-timeline { display:grid;grid-template-columns:1fr 72px 1fr 72px 1fr;align-items:start;gap:0;margin-top:1rem; }
// // .lp-how-bridge { display:flex;align-items:flex-start;justify-content:center;padding-top:2.6rem; }
// // .how-arrow-path { stroke:rgba(99,85,232,0.15);stroke-dasharray:60;stroke-dashoffset:60;transition:stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1),stroke 0.4s ease; }
// // .how-arrow-path.filled { stroke-dashoffset:0;stroke:var(--violet); }
// // .lp-how-card { background:var(--surface);border:0.5px solid var(--border2);border-radius:20px;padding:2.2rem 1.8rem 2rem;position:relative;overflow:hidden;transition:border-color 0.3s,transform 0.3s,box-shadow 0.3s;cursor:default; }
// // .lp-how-card::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 80% 60% at 50% -10%,var(--violet-subtle2) 0%,transparent 70%);opacity:0;transition:opacity 0.3s; }
// // .lp-how-card:hover { border-color:var(--border-violet);transform:translateY(-5px);box-shadow:0 12px 32px rgba(99,85,232,0.08); }
// // .lp-how-card:hover::before { opacity:1; }
// // .lp-how-num { font-family:var(--sans);font-size:7rem;font-weight:800;color:rgba(99,85,232,0.05);line-height:1;position:absolute;bottom:-0.5rem;right:0.8rem;letter-spacing:-3px;pointer-events:none;user-select:none; }
// // .lp-how-icon-ring { width:56px;height:56px;border-radius:16px;background:var(--violet-subtle);border:1px solid var(--border-violet);display:flex;align-items:center;justify-content:center;font-size:1.6rem;margin-bottom:1.5rem;position:relative; }
// // .lp-how-icon-ring::after { content:'';position:absolute;inset:-4px;border-radius:20px;border:1px dashed var(--border-violet);animation:spin-slow 12s linear infinite; }
// // @keyframes spin-slow { to{transform:rotate(360deg);} }
// // .lp-how-step-label { font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:var(--violet);margin-bottom:0.6rem; }
// // .lp-how-title { font-family:var(--sans);font-size:1.55rem;font-weight:700;color:var(--text-primary);line-height:1.15;margin-bottom:0.75rem; }
// // .lp-how-desc { font-size:13.5px;color:var(--text-muted);line-height:1.8; }

// // /* FEATURES */
// // .lp-feat-bento { display:grid;grid-template-columns:repeat(4,1fr);grid-template-rows:auto auto;gap:10px;margin-top:1rem; }
// // .lp-feat-bcard { background:var(--surface);border:0.5px solid var(--border2);border-radius:18px;padding:1.8rem 1.6rem;position:relative;overflow:hidden;transition:border-color 0.3s,transform 0.25s,background 0.3s,box-shadow 0.3s;cursor:default; }
// // .lp-feat-bcard::before { content:'';position:absolute;top:0;left:-100%;width:60%;height:100%;background:linear-gradient(105deg,transparent,rgba(99,85,232,0.05),transparent);transition:left 0s;pointer-events:none;z-index:1; }
// // .lp-feat-bcard:hover::before { left:160%;transition:left 0.6s ease; }
// // .lp-feat-bcard:hover { border-color:var(--border-violet);background:var(--surface2);transform:translateY(-3px);box-shadow:0 8px 24px rgba(99,85,232,0.07); }
// // .lp-feat-bcard.hero-card { grid-column:span 2;background:var(--surface2);border-color:var(--border-violet);padding:2.4rem 2.2rem; }
// // .lp-feat-bcard.tall { grid-row:span 2;display:flex;flex-direction:column; }
// // .lp-feat-bcard::after { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:transparent;transition:background 0.3s; }
// // .lp-feat-bcard:hover::after,.lp-feat-bcard.hero-card::after { background:linear-gradient(90deg,transparent,var(--border-violet),transparent); }
// // .lp-feat-bicon { width:44px;height:44px;border-radius:12px;background:var(--violet-subtle);border:0.5px solid var(--border-violet);display:flex;align-items:center;justify-content:center;font-size:1.3rem;margin-bottom:1.25rem;flex-shrink:0; }
// // .lp-feat-bcard.hero-card .lp-feat-bicon { width:52px;height:52px;font-size:1.6rem;border-radius:14px; }
// // .lp-feat-btag { display:inline-block;font-size:8.5px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--violet);margin-bottom:0.5rem;opacity:0.7; }
// // .lp-feat-btitle { font-family:var(--sans);font-size:1.2rem;font-weight:700;color:var(--text-primary);line-height:1.15;margin-bottom:0.5rem; }
// // .lp-feat-bcard.hero-card .lp-feat-btitle { font-size:1.55rem; }
// // .lp-feat-bdesc { font-size:13px;color:var(--text-muted);line-height:1.8;flex:1; }
// // .lp-feat-wallet { display:flex;gap:6px;margin-top:1.25rem; }
// // .lp-wallet-chip { height:26px;border-radius:6px;flex:1;background:var(--violet-subtle);border:0.5px solid var(--border-violet);display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:600;color:var(--violet);letter-spacing:0.5px;position:relative;overflow:hidden; }
// // .lp-wallet-chip::after { content:'';position:absolute;top:0;left:-100%;width:50%;height:100%;background:linear-gradient(90deg,transparent,rgba(99,85,232,0.15),transparent);animation:chip-shine 3s ease-in-out infinite; }
// // .lp-wallet-chip:nth-child(2)::after{animation-delay:0.4s;} .lp-wallet-chip:nth-child(3)::after{animation-delay:0.8s;}
// // @keyframes chip-shine { 0%,70%{left:-100%;} 100%{left:200%;} }
// // .lp-feat-socials { display:flex;flex-wrap:wrap;gap:6px;margin-top:1.1rem; }
// // .lp-social-pill { padding:4px 10px;background:var(--violet-subtle);border:0.5px solid var(--border-violet);border-radius:100px;font-size:10px;font-weight:500;color:var(--text-muted);opacity:0;transform:translateY(6px) scale(0.94);transition:opacity 0.35s ease,transform 0.35s ease,background 0.2s,color 0.2s; }
// // .lp-social-pill:hover { background:var(--violet-subtle2);color:var(--violet); }
// // .lp-social-pill:nth-child(1){transition-delay:0.05s;} .lp-social-pill:nth-child(2){transition-delay:0.12s;} .lp-social-pill:nth-child(3){transition-delay:0.19s;} .lp-social-pill:nth-child(4){transition-delay:0.26s;} .lp-social-pill:nth-child(5){transition-delay:0.33s;}
// // .lp-feat-bcard.pills-visible .lp-social-pill { opacity:1;transform:translateY(0) scale(1); }
// // .lp-qr-art { margin-top:auto;padding-top:1.5rem;display:grid;grid-template-columns:repeat(5,1fr);grid-template-rows:repeat(5,1fr);gap:3px;opacity:0.15;transition:opacity 0.3s; }
// // .lp-feat-bcard.tall:hover .lp-qr-art { opacity:0.35; }
// // .lp-qr-cell { border-radius:2px; }

// // /* USE CASES */
// // .lp-use-tabs { display:flex;flex-wrap:wrap;gap:0.5rem;justify-content:center;margin-bottom:3rem; }
// // .lp-use-tab { padding:8px 18px;border-radius:100px;font-size:13px;font-weight:600;cursor:pointer;transition:all 0.2s;font-family:var(--sans);display:flex;align-items:center;gap:6px; }
// // .lp-use-panel { display:grid;grid-template-columns:1fr 1fr;gap:3rem;align-items:center;background:var(--surface);border:0.5px solid var(--border2);border-radius:24px;padding:3rem;position:relative;overflow:hidden; }
// // .lp-use-panel::before { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,var(--border-violet),transparent); }
// // .lp-use-badge { display:inline-flex;align-items:center;gap:8px;padding:6px 14px;border-radius:100px;margin-bottom:1.5rem;font-size:12px;font-weight:700;background:var(--violet-subtle);color:var(--violet);border:0.5px solid var(--border-violet); }
// // .lp-use-tagline { font-family:var(--sans);font-size:2.1rem;font-weight:800;color:var(--text-primary);line-height:1.1;margin-bottom:1rem;letter-spacing:-0.5px; }
// // .lp-use-desc { font-size:14px;font-weight:400;color:var(--text-muted);line-height:1.9;margin-bottom:2rem; }
// // .lp-use-content { animation:tab-fade 0.35s cubic-bezier(0.22,1,0.36,1) both; }
// // @keyframes tab-fade { from{opacity:0;transform:translateY(10px);} to{opacity:1;transform:translateY(0);} }
// // .lp-use-highlights { display:flex;flex-direction:column;gap:0.75rem; }
// // .lp-use-hl { display:flex;align-items:center;gap:12px;background:var(--surface2);border:0.5px solid var(--border2);border-radius:12px;padding:1rem 1.25rem;animation:hl-slide 0.3s ease both; }
// // .lp-use-hl:nth-child(1){animation-delay:0.05s;} .lp-use-hl:nth-child(2){animation-delay:0.12s;} .lp-use-hl:nth-child(3){animation-delay:0.19s;} .lp-use-hl:nth-child(4){animation-delay:0.26s;}
// // @keyframes hl-slide { from{opacity:0;transform:translateX(12px);} to{opacity:1;transform:translateX(0);} }
// // .lp-use-hl-check { width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;background:var(--violet-subtle);color:var(--violet); }
// // .lp-use-hl-text { font-size:13px;font-weight:500;color:var(--text-muted); }

// // /* PRICING */
// // .lp-pricing-cta { padding:5rem 3rem;position:relative;overflow:hidden;background:var(--navy2); }
// // .lp-pricing-cta::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 70% 80% at 50% 50%,rgba(99,85,232,0.06) 0%,transparent 70%);pointer-events:none; }
// // .lp-pricing-cta-inner { max-width:1180px;margin:0 auto;display:grid;grid-template-columns:1fr 1.1fr;gap:4rem;align-items:center;position:relative; }
// // .lp-pricing-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--violet);margin-bottom:1.25rem; }
// // .lp-pricing-headline { font-family:var(--sans);font-size:clamp(2.8rem,5vw,4.2rem);font-weight:800;line-height:1.05;color:var(--text-primary);margin-bottom:1.25rem;letter-spacing:-1px; }
// // .lp-pricing-headline em { font-style:italic;font-family:var(--serif);font-weight:600;color:var(--violet); }
// // .lp-pricing-sub { font-size:15px;color:var(--text-muted);line-height:1.9;margin-bottom:2.5rem;max-width:400px; }
// // .lp-pricing-perks { display:flex;flex-direction:column;gap:0.6rem;margin-bottom:2.5rem; }
// // .lp-pricing-perk { display:flex;align-items:center;gap:10px;font-size:14px;font-weight:500;color:var(--text-muted); }
// // .lp-perk-tick { width:20px;height:20px;border-radius:50%;flex-shrink:0;background:var(--violet-subtle);border:0.5px solid var(--border-violet);display:flex;align-items:center;justify-content:center;font-size:9px;color:var(--violet);font-weight:700; }
// // .lp-price-card { background:var(--surface);border:0.5px solid var(--border-violet);border-radius:24px;padding:2.5rem;position:relative;overflow:hidden;box-shadow:0 16px 48px rgba(99,85,232,0.08); }
// // .lp-price-card::before { content:'';position:absolute;top:0;left:10%;right:10%;height:1px;background:linear-gradient(90deg,transparent,var(--violet),transparent); }
// // .lp-price-card-top { display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:1.5rem; }
// // .lp-price-badge { display:inline-block;background:linear-gradient(135deg,var(--blue2),var(--violet));color:#fff;border-radius:100px;padding:5px 14px;font-size:10px;font-weight:700;letter-spacing:0.5px;text-transform:uppercase; }
// // .lp-price-amount { text-align:right; }
// // .lp-price-num { font-family:var(--sans);font-size:4rem;font-weight:800;color:var(--text-primary);line-height:1;letter-spacing:-2px; }
// // .lp-price-period { font-size:10px;font-weight:600;color:var(--text-muted2);letter-spacing:2px;text-transform:uppercase;margin-top:4px; }
// // .lp-price-divider { height:0.5px;background:var(--border2);margin:1.25rem 0; }
// // .lp-price-list { list-style:none;display:flex;flex-direction:column;gap:0;margin-bottom:1.75rem; }
// // .lp-price-item { display:flex;align-items:center;gap:10px;padding:0.55rem 0;font-size:13px;font-weight:500;color:var(--text-muted);border-bottom:0.5px solid var(--border2); }
// // .lp-price-item:last-child { border-bottom:none; }
// // .lp-price-check { color:var(--violet);font-size:11px;flex-shrink:0; }

// // /* FOOTER */
// // .lp-footer { padding:3rem 3rem 4rem;border-top:0.5px solid var(--border2);background:var(--surface); }
// // .lp-footer-inner { max-width:1180px;margin:0 auto;display:flex;flex-direction:column;align-items:center;gap:1rem; }
// // .lp-footer-logo { display:flex;align-items:center;gap:10px;font-family:var(--sans);font-size:15px;font-weight:700;color:var(--text-primary); }
// // .lp-footer-logo em { color:var(--violet);font-style:normal; }
// // .lp-footer-tagline { font-size:13px;color:var(--text-muted2); }
// // .lp-footer-copy { font-size:12px;color:var(--text-muted2);opacity:0.6; }

// // /* RESPONSIVE */
// // @media (min-width:901px) {
// //   .lp-trust{flex-direction:row;padding:1.5rem 3rem;gap:2.5rem;}
// //   .lp-trust-items{width:auto;max-width:none;}
// //   .lp-trust-item{flex-direction:row;flex:none;padding:0 1.5rem;border-right:0.5px solid var(--border2);border-bottom:none;gap:8px;}
// //   .lp-trust-item:last-child{border-right:none;}
// //   .lp-trust-icon{width:28px;height:28px;border-radius:8px;font-size:1rem;flex-shrink:0;margin-bottom:0;}
// // }
// // @media (max-width:900px) {
// //   .lp-hero{grid-template-columns:1fr;text-align:center;padding:8rem 1.5rem 4rem;}
// //   .lp-hero-sub{margin:0 auto 1rem;}
// //   .lp-hero-ctas{justify-content:center;}
// //   .lp-hero-stats{justify-content:center;}
// //   .lp-hero-right{margin-top:3rem;}
// //   .scan-scene{width:300px;height:380px;transform:scale(0.85);}
// //   .lp-how-timeline{grid-template-columns:1fr;}
// //   .lp-how-bridge{display:none;}
// //   .lp-feat-bento{grid-template-columns:1fr 1fr;}
// //   .lp-feat-bcard.hero-card{grid-column:span 2;}
// //   .lp-feat-bcard.tall{grid-row:span 1;}
// //   .lp-use-panel{grid-template-columns:1fr;}
// //   .lp-pricing-cta-inner{grid-template-columns:1fr;}
// //   .lp-nav{padding:1rem 1.5rem;}
// //   .lp-nav.scrolled{padding:0.75rem 1.5rem;}
// //   .lp-section{padding:4rem 1.5rem;}
// //   .lp-pricing-cta{padding:4rem 1.5rem;}
// //   .lp-hamburger{display:flex;}
// //   .lp-nav-links{display:none;}
// //   .lp-mobile-cluster{display:flex;}
// // }
// // @media (max-width:600px) {
// //   .lp-feat-bento{grid-template-columns:1fr;}
// //   .lp-feat-bcard.hero-card{grid-column:span 1;}
// //   .lp-hero-title{font-size:clamp(2.5rem,9vw,3.4rem);}
// //   .lp-hero-stats{gap:1.5rem;}
// //   .lp-stat-num{font-size:2rem;}
// //   .lp-section{padding:3.5rem 1.25rem;}
// //   .lp-section-title{font-size:clamp(2rem,7vw,2.7rem);}
// //   .lp-use-panel{padding:1.75rem 1.25rem;}
// //   .lp-pricing-cta{padding:3rem 1.25rem;}
// //   .lp-pricing-headline{font-size:clamp(2.4rem,8vw,3rem);}
// //   .lp-hero-ctas{flex-direction:column;align-items:center;}
// //   .lp-hero-ctas .lp-btn-primary,.lp-hero-ctas .lp-btn-ghost{width:100%;justify-content:center;}
// // }
// // `;

// // /* COUNT-UP HOOK */
// // function useCountUp(target, duration = 1800, suffix = "", started = false) {
// //     const [display, setDisplay] = useState("0");
// //     const [progress, setProgress] = useState(0);
// //     const frameRef = useRef(null);
// //     useEffect(() => {
// //         if (!started) return;
// //         const num = parseFloat(target.replace(/[^0-9.]/g, ""));
// //         if (!num) { setDisplay(target); setProgress(100); return; }
// //         const startTime = performance.now();
// //         const animate = (now) => {
// //             const pct = Math.min((now - startTime) / duration, 1);
// //             const eased = pct === 1 ? 1 : 1 - Math.pow(2, -10 * pct);
// //             setDisplay((Number.isInteger(num) ? Math.round(num * eased).toString() : (num * eased).toFixed(0)) + suffix);
// //             setProgress(Math.round(pct * 100));
// //             if (pct < 1) frameRef.current = requestAnimationFrame(animate);
// //         };
// //         frameRef.current = requestAnimationFrame(animate);
// //         return () => cancelAnimationFrame(frameRef.current);
// //     }, [started, target, duration, suffix]);
// //     return { display, progress };
// // }

// // function StatItem({ value, suffix, label, started, delay = 0 }) {
// //     const [go, setGo] = useState(false);
// //     useEffect(() => {
// //         if (!started) return;
// //         const t = setTimeout(() => setGo(true), delay);
// //         return () => clearTimeout(t);
// //     }, [started, delay]);
// //     const { display, progress } = useCountUp(value, 1600, suffix, go);
// //     return (
// //         <div className="lp-stat">
// //             <div className={`lp-stat-num${go && progress < 100 ? " counting" : ""}`}>{value === "0" ? "0" : display}</div>
// //             <div className="lp-stat-label">{label}</div>
// //             <div className="lp-stat-bar"><div className="lp-stat-bar-fill" style={{ width: `${progress}%` }} /></div>
// //         </div>
// //     );
// // }

// // const QR_PATTERN = [[1, 1, 1, 0, 1, 0], [1, 0, 1, 1, 0, 1], [1, 1, 1, 0, 1, 1], [0, 1, 0, 1, 0, 0], [1, 0, 1, 1, 1, 0], [0, 1, 0, 0, 1, 1]];

// // function ScanScene() {
// //     return (
// //         <div className="scan-scene">
// //             <div className="hero-orb" />
// //             <div className="phone-scanner">
// //                 <div className="phone-scanner-screen">
// //                     <div className="scanner-viewfinder">
// //                         <div className="vf-corner-tr" /><div className="vf-corner-bl" />
// //                         <div className="scan-laser" />
// //                         <div className="mini-qr">
// //                             {QR_PATTERN.map((row, ri) => row.map((cell, ci) => (
// //                                 <div key={`${ri}-${ci}`} className="mini-qr-cell" style={{ background: cell ? "rgba(99,85,232,0.45)" : "transparent" }} />
// //                             )))}
// //                         </div>
// //                         <div className="scanner-success-flash" />
// //                     </div>
// //                     <div className="scanner-label">Point at QR code</div>
// //                 </div>
// //             </div>
// //             <div className="beam-line" /><div className="beam-particle" />
// //             <div className="scan-ripple r1" /><div className="scan-ripple r2" /><div className="scan-ripple r3" />
// //             <div className="scan-success"><div className="scan-success-dot" />Contact Saved ✓</div>
// //             <div className="phone-profile">
// //                 <div className="profile-card-wrap">
// //                     <div className="profile-banner">
// //                         <div className="profile-banner-img" /><div className="profile-banner-fade" />
// //                         <div className="profile-avatar-ring"><div className="profile-avatar-inner">UN</div></div>
// //                     </div>
// //                     <div className="profile-info">
// //                         <div className="profile-info-name">User <em>Name</em></div>
// //                         <div className="profile-info-role">Director · Company Name</div>
// //                         <div className="profile-tiles">
// //                             {[["📞", "Phone"], ["✉️", "Email"], ["📍", "Delhi"]].map(([, l]) => (
// //                                 <div className="profile-tile" key={l}><div className="tile-dot"><div className="tile-dot-inner" /></div><div className="tile-text">{l}</div></div>
// //                             ))}
// //                         </div>
// //                         <button className="profile-save-btn">↓ Save Contact</button>
// //                         <div className="profile-tiles" style={{ marginTop: "1rem" }}>
// //                             {[["🌐", "Website"], ["📄", "Resume"], ["ⓕ", "Facebook"]].map(([, l]) => (
// //                                 <div className="profile-tile" key={l}><div className="tile-dot"><div className="tile-dot-inner" /></div><div className="tile-text">{l}</div></div>
// //                             ))}
// //                         </div>
// //                     </div>
// //                 </div>
// //             </div>
// //         </div>
// //     );
// // }

// // function ArrowBridge({ filled }) {
// //     return (
// //         <div className="lp-how-bridge">
// //             <svg width="36" height="28" viewBox="0 0 36 28" fill="none" overflow="visible">
// //                 <path d="M2 14 L26 14" className={`how-arrow-path${filled ? " filled" : ""}`} strokeWidth="1.5" strokeLinecap="round" fill="none"
// //                     style={{ strokeDasharray: 28, strokeDashoffset: filled ? 0 : 28, transition: "stroke-dashoffset 0.8s cubic-bezier(0.22,1,0.36,1),stroke 0.4s ease" }} />
// //                 <path d="M20 7 L28 14 L20 21" className={`how-arrow-path${filled ? " filled" : ""}`} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"
// //                     style={{ strokeDasharray: 24, strokeDashoffset: filled ? 0 : 24, transition: "stroke-dashoffset 0.6s cubic-bezier(0.22,1,0.36,1) 0.3s,stroke 0.4s ease 0.3s" }} />
// //             </svg>
// //         </div>
// //     );
// // }

// // function LogoSVG({ size = 34 }) {
// //     return (
// //         <svg width={size} height={size} viewBox="0 0 34 34" fill="none">
// //             <rect width="34" height="34" rx="9" fill="rgba(99,85,232,0.10)" />
// //             <rect width="34" height="34" rx="9" stroke="rgba(99,85,232,0.28)" strokeWidth="0.5" />
// //             <circle cx="15" cy="13" r="5" stroke="#6355E8" strokeWidth="1.5" fill="none" />
// //             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="#6355E8" strokeWidth="1.5" strokeLinecap="round" fill="none" />
// //             <path d="M23 11 L27 11 L27 23" stroke="#6355E8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.5" />
// //             <circle cx="27" cy="23" r="1.8" fill="#6355E8" />
// //         </svg>
// //     );
// // }

// // const USE_CASES = [
// //     { icon: "👔", role: "Job Seekers", tagline: "Land your next opportunity faster", desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one elegant scan.", highlights: ["Resume attached & downloadable", "LinkedIn & GitHub links", "QR code for instant sharing", "Save contact in one tap"] },
// //     { icon: "💼", role: "Freelancers", tagline: "Win clients before you even speak", desc: "Share everything a client needs — portfolio, social proof, and contact — all in a single professional link.", highlights: ["Portfolio & website link", "WhatsApp quick contact", "Instagram & social proof", "Professional digital presence"] },
// //     { icon: "🏢", role: "Business Owners", tagline: "Network smarter, not harder", desc: "Stop spending on printed cards that get lost. Share via QR at events — always updated, never outdated.", highlights: ["Always up-to-date details", "Google Wallet for offline use", "QR code for events & meetups", "Share via WhatsApp or email"] },
// //     { icon: "🎓", role: "Students", tagline: "Build your professional identity early", desc: "Stand out in campus placements and internship drives. Show your skills, projects, and social profiles.", highlights: ["GitHub & project links", "Resume upload for placements", "LinkedIn profile showcase", "Free forever — no cost"] },
// //     { icon: "📣", role: "Sales Pros", tagline: "Close the follow-up gap instantly", desc: "At trade shows or cold visits — one QR scan and your prospect has everything they need.", highlights: ["Instant QR share at events", "Website & product links", "Save contact in one tap", "Google Wallet for quick access"] },
// //     { icon: "🧑‍💻", role: "Developers", tagline: "Your entire dev profile in one link", desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share at hackathons.", highlights: ["GitHub & portfolio links", "Twitter / X profile", "Resume for job applications", "Professional dev identity"] },
// // ];

// // function UseCasesSection({ navigate }) {
// //     const [active, setActive] = useState(0);
// //     const [tabKey, setTabKey] = useState(0);
// //     const cur = USE_CASES[active];
// //     const switchTab = (i) => { setActive(i); setTabKey(k => k + 1); };
// //     return (
// //         <section className="lp-section">
// //             <div className="lp-section-inner">
// //                 <span className="lp-eyebrow reveal from-up">Who is it for</span>
// //                 <h2 className="lp-section-title reveal from-up d1">Built for every <em>professional</em></h2>
// //                 <p className="lp-section-sub reveal from-up d2">Whether you're a student or a CEO — your digital card works for you.</p>
// //                 <div className="lp-use-tabs reveal from-up d3">
// //                     {USE_CASES.map((u, i) => (
// //                         <button key={i} className="lp-use-tab" onClick={() => switchTab(i)}
// //                             style={{ border: active === i ? "0.5px solid var(--border-violet)" : "0.5px solid var(--border2)", background: active === i ? "var(--violet-subtle2)" : "transparent", color: active === i ? "var(--violet)" : "var(--text-muted)" }}>
// //                             {u.icon} {u.role}
// //                         </button>
// //                     ))}
// //                 </div>
// //                 <div className="lp-use-panel reveal from-up d4">
// //                     <div key={`l-${tabKey}`} className="lp-use-content">
// //                         <div className="lp-use-badge"><span>{cur.icon}</span><span style={{ fontSize: "12px" }}>{cur.role}</span></div>
// //                         <div className="lp-use-tagline">{cur.tagline}</div>
// //                         <p className="lp-use-desc">{cur.desc}</p>
// //                         <button className="lp-btn-primary" onClick={() => navigate("/animation/card")}>Create My Card — Free →</button>
// //                     </div>
// //                     <div key={`r-${tabKey}`} className="lp-use-highlights">
// //                         {cur.highlights.map((h, i) => (
// //                             <div className="lp-use-hl" key={i} style={{ animationDelay: `${0.05 + i * 0.07}s` }}>
// //                                 <div className="lp-use-hl-check">✓</div>
// //                                 <span className="lp-use-hl-text">{h}</span>
// //                             </div>
// //                         ))}
// //                     </div>
// //                 </div>
// //             </div>
// //         </section>
// //     );
// // }

// // export default function LandingPage() {
// //     const navigate = useNavigate();
// //     const [scrolled, setScrolled] = useState(false);
// //     const [menuOpen, setMenuOpen] = useState(false);
// //     const [statsStarted, setStatsStarted] = useState(false);
// //     const [howVisible, setHowVisible] = useState(false);
// //     const statsRef = useRef(null);
// //     const howRef = useRef(null);

// //     useEffect(() => {
// //         const onResize = () => { if (window.innerWidth > 900) setMenuOpen(false); };
// //         window.addEventListener("resize", onResize);
// //         return () => window.removeEventListener("resize", onResize);
// //     }, []);

// //     useEffect(() => {
// //         const onScroll = () => setScrolled(window.scrollY > 40);
// //         window.addEventListener("scroll", onScroll);
// //         return () => window.removeEventListener("scroll", onScroll);
// //     }, []);

// //     useEffect(() => {
// //         const obs = [];
// //         const revObs = new IntersectionObserver(
// //             entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
// //             { threshold: 0.1 }
// //         );
// //         document.querySelectorAll(".reveal").forEach(el => revObs.observe(el));
// //         obs.push(revObs);

// //         if (statsRef.current) {
// //             const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsStarted(true); }, { threshold: 0.5 });
// //             o.observe(statsRef.current); obs.push(o);
// //         }
// //         if (howRef.current) {
// //             const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setHowVisible(true); }, { threshold: 0.3 });
// //             o.observe(howRef.current); obs.push(o);
// //         }
// //         const pillObs = new IntersectionObserver(
// //             entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("pills-visible"); }),
// //             { threshold: 0.4 }
// //         );
// //         document.querySelectorAll(".lp-feat-bcard.has-pills").forEach(c => pillObs.observe(c));
// //         obs.push(pillObs);

// //         return () => obs.forEach(o => o.disconnect());
// //     }, []);

// //     return (
// //         <div style={{ background: "var(--navy)", minHeight: "100vh" }}>
// //             <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

// //             {/* MOBILE DRAWER */}
// //             <div className={`lp-mobile-menu${menuOpen ? " open" : ""}`}>
// //                 <div className="lp-mobile-menu-backdrop" onClick={() => setMenuOpen(false)} />
// //                 <div className="lp-mobile-menu-panel">
// //                     <div className="lp-mobile-menu-header">
// //                         <a href="/" className="lp-logo" onClick={() => setMenuOpen(false)}><LogoSVG size={28} />myprofile<em>corner</em></a>
// //                         <button className="lp-mobile-close" onClick={() => setMenuOpen(false)}>✕</button>
// //                     </div>
// //                     <div className="lp-mobile-links">
// //                         <a href="#how" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">⚡</span>How it works</a>
// //                         <a href="#features" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">✦</span>Features</a>
// //                         <a href="#pricing" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">₹</span>Pricing</a>
// //                     </div>
// //                     <div className="lp-mobile-divider" />
// //                     <div className="lp-mobile-menu-footer">
// //                         <button className="lp-mobile-cta" onClick={() => { setMenuOpen(false); navigate("/animation/card"); }}>Create My Card — Free →</button>
// //                         <div className="lp-mobile-tagline">100% Free · No app required · 2 min setup</div>
// //                     </div>
// //                 </div>
// //             </div>

// //             {/* NAV */}
// //             <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
// //                 <div className="lp-nav-inner">
// //                     <a href="/" className="lp-logo"><LogoSVG size={34} />myprofile<em>corner</em></a>
// //                     <div className="lp-nav-links">
// //                         <a href="#how" className="lp-nav-link">How it works</a>
// //                         <a href="#features" className="lp-nav-link">Features</a>
// //                         <a href="#pricing" className="lp-nav-link">Pricing</a>
// //                         <button className="lp-nav-cta" onClick={() => navigate("/animation/card")}>Create Free Card</button>
// //                     </div>
// //                     <div className="lp-mobile-cluster">
// //                         <button className={`lp-hamburger${menuOpen ? " open" : ""}`} onClick={() => setMenuOpen(p => !p)} aria-label="Menu">
// //                             <span /><span /><span />
// //                         </button>
// //                     </div>
// //                 </div>
// //             </nav>

// //             {/* HERO */}
// //             <div className="lp-hero-wrap">
// //                 <div className="lp-hero-mesh" /><div className="lp-hero-mesh-bottom" /><div className="lp-hero-grid" />
// //                 <div className="lp-hero">
// //                     <div className="lp-hero-left">
// //                         <div className="lp-hero-eyebrow reveal from-down">
// //                             <div className="lp-hero-eyebrow-dot" />100% Free · No App Needed
// //                         </div>
// //                         <h1 className="lp-hero-title reveal from-left">
// //                             Your Digital Identity,<br />
// //                             <span className="gradient-line">Always in Your Pocket</span>
// //                         </h1>
// //                         <p className="lp-hero-sub reveal from-left d1">
// //                             Replace paper visiting cards with a smart digital card. Share your profile instantly via QR code, link, or Google Wallet — no printing, no waste.
// //                         </p>
// //                         <div className="lp-hero-stats" ref={statsRef}>
// //                             <StatItem value="100" suffix="%" label="Free forever" started={statsStarted} delay={0} />
// //                             <StatItem value="2" suffix=" min" label="Setup time" started={statsStarted} delay={200} />
// //                             <StatItem value="0" suffix="" label="Paper waste" started={statsStarted} delay={400} />
// //                         </div>
// //                         <div className="lp-hero-ctas reveal from-up d2">
// //                             <button className="lp-btn-primary" onClick={() => navigate("/animation/card")}>Create My Card — It's Free →</button>
// //                             <a href="#how" className="lp-btn-ghost">See how it works</a>
// //                         </div>
// //                     </div>
// //                     <div className="lp-hero-right reveal from-right"><ScanScene /></div>
// //                 </div>
// //             </div>

// //             <div className="lp-rule" />

// //             {/* TRUST BAR */}
// //             <div className="lp-trust">
// //                 <span className="lp-trust-label">Works seamlessly with</span>
// //                 <div className="lp-trust-items">
// //                     {[["🔥", "Firebase"], ["🪙", "Google Wallet"], ["📱", "Android & iOS"], ["🔗", "Any Browser"]].map(([icon, label]) => (
// //                         <div className="lp-trust-item" key={label}>
// //                             <div className="lp-trust-icon">{icon}</div><span>{label}</span>
// //                         </div>
// //                     ))}
// //                 </div>
// //             </div>

// //             <div className="lp-rule" />

// //             {/* HOW IT WORKS */}
// //             <section id="how" className="lp-section" ref={howRef}>
// //                 <div className="lp-section-inner">
// //                     <span className="lp-eyebrow reveal from-up">How it works</span>
// //                     <h2 className="lp-section-title reveal from-up d1">From setup to sharing <em>in 3 steps</em></h2>
// //                     <p className="lp-section-sub reveal from-up d2">No app downloads. No tech skills required. Just fill, share, impress.</p>
// //                     <div className="lp-how-timeline">
// //                         <div className="lp-how-card reveal from-left d1">
// //                             <div className="lp-how-num">01</div><div className="lp-how-icon-ring">✏️</div>
// //                             <div className="lp-how-step-label">Step 01</div><div className="lp-how-title">Fill Your Profile</div>
// //                             <p className="lp-how-desc">Add your name, photo, contact info, social links, resume & website. Takes less than 2 minutes.</p>
// //                         </div>
// //                         <ArrowBridge filled={howVisible} />
// //                         <div className="lp-how-card reveal from-up d3">
// //                             <div className="lp-how-num">02</div><div className="lp-how-icon-ring">🎴</div>
// //                             <div className="lp-how-step-label">Step 02</div><div className="lp-how-title">Get Your Digital Card</div>
// //                             <p className="lp-how-desc">Instantly get a beautiful card with a unique QR code & shareable link — no design skills needed.</p>
// //                         </div>
// //                         <ArrowBridge filled={howVisible} />
// //                         <div className="lp-how-card reveal from-right d5">
// //                             <div className="lp-how-num">03</div><div className="lp-how-icon-ring">🚀</div>
// //                             <div className="lp-how-step-label">Step 03</div><div className="lp-how-title">Share Anywhere</div>
// //                             <p className="lp-how-desc">Show QR in meetings, add to Google Wallet, or send your link via WhatsApp, email, or LinkedIn.</p>
// //                         </div>
// //                     </div>
// //                 </div>
// //             </section>

// //             <div className="lp-rule" />

// //             {/* FEATURES */}
// //             <section id="features" className="lp-section" style={{ background: "var(--navy2)" }}>
// //                 <div className="lp-section-inner">
// //                     <span className="lp-eyebrow reveal from-up">Features</span>
// //                     <h2 className="lp-section-title reveal from-up d1">Everything a modern <em>professional</em> needs</h2>
// //                     <p className="lp-section-sub reveal from-up d2">One card. Every detail. Always up-to-date.</p>
// //                     <div className="lp-feat-bento">
// //                         <div className="lp-feat-bcard hero-card reveal from-left d1">
// //                             <div className="lp-feat-bicon">🪙</div><div className="lp-feat-btag">Premium Feature</div>
// //                             <div className="lp-feat-btitle">Google Wallet Pass</div>
// //                             <p className="lp-feat-bdesc">Add your card to Google Wallet and access it offline — at meetings, events, or anywhere without internet.</p>
// //                             <div className="lp-feat-wallet">
// //                                 <div className="lp-wallet-chip">G Pay</div><div className="lp-wallet-chip">Wallet</div><div className="lp-wallet-chip">Offline ✓</div>
// //                             </div>
// //                         </div>
// //                         <div className="lp-feat-bcard tall reveal from-right d2">
// //                             <div className="lp-feat-bicon">📲</div><div className="lp-feat-btag">Instant Share</div>
// //                             <div className="lp-feat-btitle">Unique QR Code</div>
// //                             <p className="lp-feat-bdesc">Every card gets a dedicated QR. One scan opens your full profile — no typing, no friction.</p>
// //                             <div className="lp-qr-art">
// //                                 {[1, 1, 1, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 1].map((v, i) => (
// //                                     <div key={i} className="lp-qr-cell" style={{ background: v ? "var(--violet)" : "var(--violet-subtle)", borderRadius: "2px", aspectRatio: "1" }} />
// //                                 ))}
// //                             </div>
// //                         </div>
// //                         <div className="lp-feat-bcard reveal from-up d3">
// //                             <div className="lp-feat-bicon">📸</div><div className="lp-feat-btag">Identity</div>
// //                             <div className="lp-feat-btitle">Profile Photo</div>
// //                             <p className="lp-feat-bdesc">Your face on your card — and inside Google Wallet too.</p>
// //                         </div>
// //                         <div className="lp-feat-bcard reveal from-up d4">
// //                             <div className="lp-feat-bicon">💾</div><div className="lp-feat-btag">One Tap</div>
// //                             <div className="lp-feat-btitle">Save Contact</div>
// //                             <p className="lp-feat-bdesc">Visitors save you directly to their phone contacts with a single tap.</p>
// //                         </div>

// //                         <div className="lp-feat-bcard reveal from-right d5">
// //                             <div className="lp-feat-bicon">🌐</div><div className="lp-feat-btag">Universal</div>
// //                             <div className="lp-feat-btitle">Works Everywhere</div>
// //                             <p className="lp-feat-bdesc">Opens in any browser on any device. No app install ever required.</p>
// //                         </div>

// //                         {/* ================= */}
// //                         <div className="lp-feat-bcard tall reveal from-right d2">
// //                             <div className="lp-feat-bicon">📲</div><div className="lp-feat-btag">Always Fresh</div>
// //                             <div className="lp-feat-btitle">Edit Anytime</div>
// //                             <p className="lp-feat-bdesc">Every card gets a dedicated QR. One scan opens your full profile — no typing, no friction.</p>
// //                             <div className="lp-qr-art">
// //                                 {[1, 1, 1, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 1].map((v, i) => (
// //                                     <div key={i} className="lp-qr-cell" style={{ background: v ? "var(--violet)" : "var(--violet-subtle)", borderRadius: "2px", aspectRatio: "1" }} />
// //                                 ))}
// //                             </div>
// //                         </div>

// //                         {/* ================= */}
// //                         <div className="lp-feat-bcard hero-card has-pills reveal from-left d2">
// //                             <div className="lp-feat-bicon">🔗</div><div className="lp-feat-btag">Connections</div>
// //                             <div className="lp-feat-btitle">All Social Links</div>
// //                             <p className="lp-feat-bdesc">Changed jobs? New number? Update instantly — same link forever.</p>
// //                             <div className="lp-feat-socials">
// //                                 {["in LinkedIn", "⌥ GitHub", "✉ WhatsApp", "▶ YouTube", "📷 Insta"].map(s => (
// //                                     <span key={s} className="lp-social-pill">{s}</span>
// //                                 ))}
// //                             </div>
// //                         </div>

// //                         <div className="lp-feat-bcard reveal from-up d3">
// //                             <div className="lp-feat-bicon">📄</div><div className="lp-feat-btag">Career</div>
// //                             <div className="lp-feat-btitle">Resume Upload</div>
// //                             <p className="lp-feat-bdesc">Attach your PDF resume. Recruiters can download it instantly.</p>
// //                         </div>
// //                         {/* <div className="lp-feat-bcard reveal from-up d4">
// //                             <div className="lp-feat-bicon">✏️</div><div className="lp-feat-btag">Always Fresh</div>
// //                             <div className="lp-feat-btitle">Edit Anytime</div>
// //                             <p className="lp-feat-bdesc">Changed jobs? New number? Update instantly — same link forever.</p>
// //                         </div> */}


// //                     </div>
// //                 </div>
// //             </section>

// //             <div className="lp-rule" />
// //             <UseCasesSection navigate={navigate} />
// //             <div className="lp-rule" />

// //             {/* PRICING */}
// //             <section id="pricing" className="lp-pricing-cta">
// //                 <div className="lp-pricing-cta-inner">
// //                     <div>
// //                         <span className="lp-pricing-eyebrow reveal from-left">Pricing</span>
// //                         <h2 className="lp-pricing-headline reveal from-left d1">Stop carrying<br /><em>paper cards.</em></h2>
// //                         <p className="lp-pricing-sub reveal from-left d2">Create your digital card in under 2 minutes. No hidden charges. No credit card. Free forever.</p>
// //                         <div className="lp-pricing-perks reveal from-left d3">
// //                             {["No app download needed", "Unique QR code & shareable link", "Google Wallet pass included", "Edit anytime — same link forever", "Works on every device & browser"].map(p => (
// //                                 <div className="lp-pricing-perk" key={p}><div className="lp-perk-tick">✓</div><span>{p}</span></div>
// //                             ))}
// //                         </div>
// //                         <button className="lp-btn-primary reveal from-up d4" onClick={() => navigate("/animation/card")}>Create My Card Now →</button>
// //                     </div>
// //                     <div className="lp-price-card reveal from-right d2">
// //                         <div className="lp-price-card-top">
// //                             <div className="lp-price-badge">Most Popular</div>
// //                             <div className="lp-price-amount">
// //                                 <div className="lp-price-num">₹0</div>
// //                                 <div className="lp-price-period">Forever Free</div>
// //                             </div>
// //                         </div>
// //                         <div className="lp-price-divider" />
// //                         <ul className="lp-price-list">
// //                             {["1 Digital Visiting Card", "Unique QR Code & Link", "Profile Photo Upload", "Resume Upload (PDF)", "All Social Media Links", "Google Wallet Pass", "Save Contact (.vcf)", "Edit Anytime", "No App Required"].map(item => (
// //                                 <li className="lp-price-item" key={item}><span className="lp-price-check">✦</span>{item}</li>
// //                             ))}
// //                         </ul>
// //                         <button className="lp-btn-primary" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/animation/card")}>Get Started Free →</button>
// //                     </div>
// //                 </div>
// //             </section>

// //             <div className="lp-rule" />

// //             {/* FOOTER */}
// //             <footer className="lp-footer">
// //                 <div className="lp-footer-inner">
// //                     <div className="lp-footer-logo"><LogoSVG size={28} />myprofile<em>corner</em></div>
// //                     <p className="lp-footer-tagline">Your digital identity, always in your pocket.</p>
// //                     <p className="lp-footer-copy">© {new Date().getFullYear()} MyProfileCorner · Made with ♥ in India</p>
// //                 </div>
// //             </footer>
// //         </div>
// //     );
// // }

// // // import React, { useState, useEffect, useRef } from "react";
// // // import { useNavigate } from "react-router-dom";

// // // const FONT_LINK = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,600&display=swap');`;

// // // const GLOBAL_CSS = `
// // // ${FONT_LINK}

// // // *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

// // // :root {
// // //   --navy:           #0D0F1A;
// // //   --navy2:          #111428;
// // //   --navy3:          #161930;
// // //   --navy4:          #1C2040;
// // //   --navy5:          #232750;
// // //   --violet:         #7C6FF7;
// // //   --violet2:        #9B8CFF;
// // //   --violet3:        #B8AEFF;
// // //   --violet-dim:     rgba(124,111,247,0.18);
// // //   --violet-subtle:  rgba(124,111,247,0.09);
// // //   --violet-subtle2: rgba(124,111,247,0.16);
// // //   --blue:           #4F63D2;
// // //   --blue2:          #5B72F0;
// // //   --blue3:          #3A4DBB;
// // //   --text-primary:   #FFFFFF;
// // //   --text-muted:     rgba(200,205,230,0.65);
// // //   --text-muted2:    rgba(180,185,220,0.40);
// // //   --border:         rgba(124,111,247,0.14);
// // //   --border2:        rgba(255,255,255,0.07);
// // //   --border-violet:  rgba(124,111,247,0.26);
// // //   --surface:        #13162A;
// // //   --surface2:       #181C35;
// // //   --surface3:       #1E2240;
// // //   --serif:  'Fraunces', serif;
// // //   --sans:   'Plus Jakarta Sans', sans-serif;
// // // }

// // // html { scroll-behavior: smooth; }
// // // body { background: var(--navy); color: var(--text-primary); font-family: var(--sans); overflow-x: hidden; }
// // // ::-webkit-scrollbar { width: 4px; }
// // // ::-webkit-scrollbar-track { background: var(--navy); }
// // // ::-webkit-scrollbar-thumb { background: rgba(124,111,247,0.4); border-radius: 2px; }

// // // /* REVEALS */
// // // .reveal { opacity: 0; transition: opacity 0.75s cubic-bezier(0.22,1,0.36,1), transform 0.75s cubic-bezier(0.22,1,0.36,1); }
// // // .reveal.from-up    { transform: translateY(36px); }
// // // .reveal.from-down  { transform: translateY(-24px); }
// // // .reveal.from-left  { transform: translateX(-44px); }
// // // .reveal.from-right { transform: translateX(44px); }
// // // .reveal.d1{transition-delay:0.05s;} .reveal.d2{transition-delay:0.13s;} .reveal.d3{transition-delay:0.21s;}
// // // .reveal.d4{transition-delay:0.29s;} .reveal.d5{transition-delay:0.37s;} .reveal.d6{transition-delay:0.45s;}
// // // .reveal.visible { opacity: 1; transform: none; }

// // // /* NAV */
// // // .lp-nav { position:fixed; top:0; left:0; right:0; z-index:200; padding:1.4rem 3rem; transition:all 0.4s; }
// // // .lp-nav.scrolled { background:rgba(13,15,26,0.94); backdrop-filter:blur(20px); border-bottom:0.5px solid var(--border); padding:0.9rem 3rem; box-shadow:0 4px 32px rgba(0,0,0,0.35); }
// // // .lp-nav-inner { max-width:1180px; margin:0 auto; display:flex; align-items:center; justify-content:space-between; }
// // // .lp-logo { display:flex; align-items:center; gap:10px; font-family:var(--sans); font-size:15px; font-weight:700; color:var(--text-primary); text-decoration:none; flex-shrink:0; }
// // // .lp-logo em { color:var(--violet2); font-style:normal; }
// // // .lp-nav-links { display:flex; align-items:center; gap:2.5rem; }
// // // .lp-nav-link { font-size:13.5px; font-weight:500; color:var(--text-muted); text-decoration:none; transition:color 0.2s; }
// // // .lp-nav-link:hover { color:var(--text-primary); }
// // // .lp-nav-cta { padding:10px 22px; background:linear-gradient(135deg,var(--blue2),var(--violet)); color:#fff; border:none; border-radius:10px; font-family:var(--sans); font-size:13px; font-weight:700; cursor:pointer; position:relative; overflow:hidden; transition:transform 0.15s,box-shadow 0.15s; white-space:nowrap; }
// // // .lp-nav-cta::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.15),transparent 60%); pointer-events:none; }
// // // .lp-nav-cta:hover { transform:translateY(-1px); box-shadow:0 6px 20px rgba(124,111,247,0.4); }

// // // /* HAMBURGER */
// // // .lp-hamburger { display:none; flex-direction:column; justify-content:center; align-items:center; gap:5px; width:40px; height:40px; border-radius:10px; border:0.5px solid var(--border-violet); background:var(--violet-subtle); cursor:pointer; flex-shrink:0; transition:background 0.2s; }
// // // .lp-hamburger:hover { background:var(--violet-subtle2); }
// // // .lp-hamburger span { display:block; width:18px; height:1.5px; background:var(--violet2); border-radius:2px; transition:all 0.3s cubic-bezier(0.22,1,0.36,1); transform-origin:center; }
// // // .lp-hamburger.open span:nth-child(1){transform:translateY(6.5px) rotate(45deg);}
// // // .lp-hamburger.open span:nth-child(2){opacity:0;transform:scaleX(0);}
// // // .lp-hamburger.open span:nth-child(3){transform:translateY(-6.5px) rotate(-45deg);}

// // // /* MOBILE MENU */
// // // .lp-mobile-menu { position:fixed; top:0; left:0; right:0; bottom:0; z-index:190; display:flex; flex-direction:column; pointer-events:none; }
// // // .lp-mobile-menu-backdrop { position:absolute; inset:0; background:rgba(0,0,0,0.65); backdrop-filter:blur(6px); opacity:0; transition:opacity 0.35s ease; }
// // // .lp-mobile-menu.open .lp-mobile-menu-backdrop { opacity:1; pointer-events:all; }
// // // .lp-mobile-menu-panel { position:absolute; top:0; right:0; width:min(320px,85vw); height:100%; background:var(--navy2); border-left:0.5px solid var(--border-violet); display:flex; flex-direction:column; transform:translateX(100%); transition:transform 0.4s cubic-bezier(0.22,1,0.36,1); box-shadow:-20px 0 60px rgba(0,0,0,0.5); pointer-events:none; overflow:hidden; }
// // // .lp-mobile-menu.open .lp-mobile-menu-panel { transform:translateX(0); pointer-events:all; }
// // // .lp-mobile-menu-header { display:flex; align-items:center; justify-content:space-between; padding:1.25rem 1.5rem; border-bottom:0.5px solid var(--border2); }
// // // .lp-mobile-close { width:36px; height:36px; border-radius:9px; border:0.5px solid var(--border2); background:var(--surface); display:flex; align-items:center; justify-content:center; cursor:pointer; color:var(--text-muted); font-size:16px; transition:background 0.2s; }
// // // .lp-mobile-close:hover { background:var(--surface2); color:var(--text-primary); }
// // // .lp-mobile-links { flex:1; display:flex; flex-direction:column; padding:1.5rem; gap:0.25rem; }
// // // .lp-mobile-link { display:flex; align-items:center; gap:12px; padding:1rem 1.25rem; border-radius:14px; font-family:var(--sans); font-size:15px; font-weight:600; color:var(--text-muted); text-decoration:none; border:0.5px solid transparent; opacity:0; transform:translateX(20px); transition:background 0.2s,color 0.2s,border-color 0.2s,opacity 0.35s ease,transform 0.35s cubic-bezier(0.22,1,0.36,1); }
// // // .lp-mobile-link:nth-child(1){transition-delay:0.06s;} .lp-mobile-link:nth-child(2){transition-delay:0.11s;} .lp-mobile-link:nth-child(3){transition-delay:0.16s;}
// // // .lp-mobile-menu.open .lp-mobile-link { opacity:1; transform:translateX(0); }
// // // .lp-mobile-link:hover { background:var(--surface2); color:var(--text-primary); border-color:var(--border-violet); }
// // // .lp-mobile-link-icon { font-size:16px; width:20px; text-align:center; }
// // // .lp-mobile-divider { height:0.5px; background:linear-gradient(90deg,transparent,var(--border-violet),transparent); margin:0.5rem 1.5rem; }
// // // .lp-mobile-menu-footer { padding:1.5rem; border-top:0.5px solid var(--border2); display:flex; flex-direction:column; gap:0.75rem; opacity:0; transform:translateY(12px); transition:opacity 0.35s ease 0.22s,transform 0.35s ease 0.22s; }
// // // .lp-mobile-menu.open .lp-mobile-menu-footer { opacity:1; transform:translateY(0); }
// // // .lp-mobile-cta { width:100%; padding:14px; background:linear-gradient(135deg,var(--blue2),var(--violet)); color:#fff; border:none; border-radius:12px; font-family:var(--sans); font-size:14px; font-weight:700; cursor:pointer; position:relative; overflow:hidden; }
// // // .lp-mobile-cta::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.15),transparent 60%); pointer-events:none; }
// // // .lp-mobile-tagline { text-align:center; font-size:11px; font-weight:500; color:var(--text-muted2); letter-spacing:0.5px; }
// // // .lp-mobile-cluster { display:none; align-items:center; gap:10px; }

// // // /* HERO */
// // // .lp-hero-wrap { position:relative; overflow:hidden; background:var(--navy); }
// // // .lp-hero-mesh { position:absolute; inset:0; pointer-events:none; overflow:hidden; }
// // // .lp-hero-mesh::before { content:''; position:absolute; width:900px; height:900px; border-radius:50%; background:radial-gradient(circle,rgba(76,60,200,0.24) 0%,rgba(60,40,180,0.08) 40%,transparent 70%); top:-300px; left:-200px; }
// // // .lp-hero-mesh::after { content:''; position:absolute; width:700px; height:700px; border-radius:50%; background:radial-gradient(circle,rgba(100,80,240,0.18) 0%,transparent 65%); top:-100px; right:-150px; }
// // // .lp-hero-mesh-bottom { position:absolute; width:500px; height:500px; border-radius:50%; background:radial-gradient(circle,rgba(60,50,180,0.14) 0%,transparent 70%); bottom:-200px; right:20%; pointer-events:none; }
// // // .lp-hero-grid { position:absolute; inset:0; background-image:radial-gradient(circle,rgba(124,111,247,0.20) 1px,transparent 1px); background-size:48px 48px; opacity:0.3; pointer-events:none; }
// // // .lp-hero { min-height:100vh; display:grid; grid-template-columns:1fr 1fr; align-items:center; gap:4rem; max-width:1180px; margin:0 auto; padding:9rem 3rem 4rem; position:relative; z-index:2; }

// // // /* Hero eyebrow */
// // // .lp-hero-eyebrow { display:inline-flex; align-items:center; gap:8px; padding:7px 16px; background:rgba(124,111,247,0.12); border:0.5px solid rgba(124,111,247,0.32); border-radius:100px; font-size:11.5px; font-weight:700; color:var(--violet2); letter-spacing:1px; text-transform:uppercase; margin-bottom:2rem; }
// // // .lp-hero-eyebrow-dot { width:6px; height:6px; border-radius:50%; background:var(--violet2); animation:pulse-violet 2s ease-in-out infinite; }
// // // @keyframes pulse-violet { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:0.3;transform:scale(0.65);} }

// // // /* Hero title — matching screenshot: big bold + gradient */
// // // .lp-hero-title { font-family:var(--sans); font-size:clamp(3rem,5.5vw,5.2rem); font-weight:800; line-height:1.05; letter-spacing:-1.5px; color:var(--text-primary); margin-bottom:1.75rem; }
// // // .lp-hero-title .gradient-line { display:block; background:linear-gradient(100deg,#7C6FF7 0%,#9B8CFF 35%,#B8AEFF 65%,#D0C8FF 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }

// // // .lp-hero-sub { font-size:16px; font-weight:400; color:var(--text-muted); line-height:1.85; max-width:480px; margin-bottom:1rem; }
// // // .lp-hero-ctas { display:flex; gap:1rem; flex-wrap:wrap; }

// // // /* BUTTONS */
// // // .lp-btn-primary { padding:17px 34px; background:linear-gradient(135deg,var(--blue2),var(--violet)); color:#fff; border:none; border-radius:14px; font-family:var(--sans); font-size:14px; font-weight:700; letter-spacing:0.3px; cursor:pointer; position:relative; overflow:hidden; transition:transform 0.2s,box-shadow 0.2s; display:flex; align-items:center; gap:8px; white-space:nowrap; }
// // // .lp-btn-primary::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.18),transparent 55%); pointer-events:none; }
// // // .lp-btn-primary:hover { transform:translateY(-2px); box-shadow:0 10px 32px rgba(79,99,210,0.5); }
// // // .lp-btn-ghost { padding:17px 28px; background:rgba(255,255,255,0.05); color:var(--text-muted); border:0.5px solid rgba(255,255,255,0.14); border-radius:14px; font-family:var(--sans); font-size:14px; font-weight:600; cursor:pointer; text-decoration:none; display:inline-flex; align-items:center; gap:8px; transition:border-color 0.2s,color 0.2s,background 0.2s; white-space:nowrap; }
// // // .lp-btn-ghost:hover { border-color:var(--border-violet); color:var(--text-primary); background:var(--violet-subtle); }

// // // /* STATS */
// // // .lp-hero-stats { display:flex; gap:2.5rem; padding-bottom:1.5rem; margin-bottom:1.5rem; }
// // // .lp-stat { position:relative; }
// // // .lp-stat-num { font-family:var(--sans); font-size:2.4rem; font-weight:800; color:var(--violet2); line-height:1; letter-spacing:-1px; }
// // // .lp-stat-num.counting { animation:stat-shimmer 0.08s ease infinite alternate; }
// // // @keyframes stat-shimmer { from{opacity:0.6;} to{opacity:1;} }
// // // .lp-stat-label { font-size:11px; font-weight:500; color:var(--text-muted2); letter-spacing:0.4px; margin-top:5px; }
// // // .lp-stat-bar { height:2px; background:rgba(124,111,247,0.15); margin-top:8px; border-radius:1px; overflow:hidden; }
// // // .lp-stat-bar-fill { height:100%; background:linear-gradient(90deg,var(--violet),var(--violet2)); border-radius:1px; width:0%; transition:width 0.05s linear; }

// // // /* SCAN SCENE */
// // // .lp-hero-right { position:relative; display:flex; align-items:center; justify-content:center; z-index:2; }
// // // .scan-scene { position:relative; width:380px; height:480px; }
// // // .scan-ripple { position:absolute; right:-10px; top:50px; width:60px; height:60px; border-radius:50%; border:1.5px solid rgba(124,111,247,0); z-index:10; pointer-events:none; }
// // // .scan-ripple.r1{animation:ripple-out 2.5s ease-out infinite;animation-delay:3.2s;}
// // // .scan-ripple.r2{animation:ripple-out 2.5s ease-out infinite;animation-delay:3.6s;}
// // // .scan-ripple.r3{animation:ripple-out 2.5s ease-out infinite;animation-delay:4.0s;}
// // // @keyframes ripple-out { 0%{transform:scale(1);border-color:rgba(124,111,247,0.7);opacity:1;} 100%{transform:scale(3.5);border-color:rgba(124,111,247,0);opacity:0;} }
// // // .scan-success { position:absolute; right:8px; top:38px; background:linear-gradient(135deg,var(--violet),var(--violet2)); color:#fff; border-radius:100px; padding:5px 12px 5px 9px; font-family:var(--sans); font-size:9.5px; font-weight:700; letter-spacing:0.3px; display:flex; align-items:center; gap:6px; z-index:12; white-space:nowrap; opacity:0; animation:success-pop 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards; animation-delay:3.1s; box-shadow:0 4px 18px rgba(124,111,247,0.5); }
// // // .scan-success-dot { width:6px; height:6px; border-radius:50%; background:rgba(255,255,255,0.7); }
// // // @keyframes success-pop { 0%{opacity:0;transform:scale(0.5) translateY(6px);} 100%{opacity:1;transform:scale(1) translateY(0);} }
// // // .phone-scanner { position:absolute; left:0; top:60px; width:155px; height:270px; background:#181828; border-radius:22px; border:1px solid rgba(124,111,247,0.22); overflow:hidden; box-shadow:0 24px 60px rgba(0,0,0,0.6); z-index:3; animation:phone-a-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both; animation-delay:0.3s; }
// // // @keyframes phone-a-intro { from{opacity:0;transform:translateX(-30px) rotate(-6deg);} to{opacity:1;transform:translateX(0) rotate(-6deg);} }
// // // .phone-scanner-screen { width:100%; height:100%; background:#0F1020; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; padding:12px; }
// // // .scanner-viewfinder { width:100px; height:100px; border-radius:10px; position:relative; background:rgba(124,111,247,0.04); }
// // // .scanner-viewfinder::before,.scanner-viewfinder::after { content:''; position:absolute; width:20px; height:20px; border-color:var(--violet2); border-style:solid; }
// // // .scanner-viewfinder::before { top:0;left:0;border-width:2px 0 0 2px;border-radius:6px 0 0 0; }
// // // .scanner-viewfinder::after { bottom:0;right:0;border-width:0 2px 2px 0;border-radius:0 0 6px 0; }
// // // .vf-corner-tr { position:absolute;top:0;right:0;width:20px;height:20px;border-top:2px solid var(--violet2);border-right:2px solid var(--violet2);border-radius:0 6px 0 0; }
// // // .vf-corner-bl { position:absolute;bottom:0;left:0;width:20px;height:20px;border-bottom:2px solid var(--violet2);border-left:2px solid var(--violet2);border-radius:0 0 0 6px; }
// // // .scan-laser { position:absolute;left:4px;right:4px;height:2px;background:linear-gradient(90deg,transparent,var(--violet2),transparent);border-radius:1px;top:4px;animation:laser-sweep 2s ease-in-out infinite;animation-delay:1.2s; }
// // // @keyframes laser-sweep { 0%{top:8px;opacity:0;} 10%{opacity:1;} 90%{opacity:1;} 100%{top:88px;opacity:0;} }
// // // .scanner-success-flash { position:absolute; inset:0; background:rgba(124,111,247,0.14); opacity:0; animation:flash-once 0.4s ease forwards; animation-delay:3.0s; pointer-events:none; z-index:10; }
// // // @keyframes flash-once { 0%{opacity:0;} 40%{opacity:1;} 100%{opacity:0;} }
// // // .scanner-label { font-size:9px;font-weight:600;color:rgba(180,185,220,0.4);letter-spacing:1px;text-align:center;text-transform:uppercase; }
// // // .mini-qr { position:absolute;inset:10px;display:grid;grid-template-columns:repeat(6,1fr);grid-template-rows:repeat(6,1fr);gap:1.5px;opacity:0.6; }
// // // .mini-qr-cell { border-radius:1px; }
// // // .phone-profile { position:absolute;right:0;top:0;width:175px;height:310px;background:#131625;border-radius:24px;border:1px solid rgba(124,111,247,0.28);overflow:hidden;box-shadow:0 32px 80px rgba(0,0,0,0.7),0 0 0 1px rgba(124,111,247,0.10);z-index:4;animation:phone-b-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:0.5s; }
// // // @keyframes phone-b-intro { from{opacity:0;transform:translateY(30px) rotate(4deg);} to{opacity:1;transform:translateY(0) rotate(4deg);} }
// // // .profile-card-wrap { width:100%;height:100%;animation:card-reveal 0.7s cubic-bezier(0.22,1,0.36,1) both;animation-delay:2s; }
// // // @keyframes card-reveal { from{transform:translateY(40px);opacity:0;} to{transform:translateY(0);opacity:1;} }
// // // .profile-banner { width:100%;height:110px;background:linear-gradient(160deg,#1a1830,#2a2060);position:relative;overflow:hidden; }
// // // .profile-banner-img { width:100%;height:100%;background:linear-gradient(160deg,#1e1840 0%,#2a1870 50%,#180f30 100%); }
// // // .profile-banner-fade { position:absolute;bottom:0;left:0;right:0;height:50px;background:linear-gradient(transparent,#131625); }
// // // .profile-avatar-ring { position:absolute;bottom:-20px;left:14px;width:44px;height:44px;border-radius:50%;border:2px solid #131625;background:#1e1838;overflow:hidden;box-shadow:0 0 0 1.5px rgba(124,111,247,0.65);animation:avatar-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:2.4s; }
// // // @keyframes avatar-pop { from{transform:scale(0.3);opacity:0;} to{transform:scale(1);opacity:1;} }
// // // .profile-avatar-inner { width:100%;height:100%;background:linear-gradient(145deg,#2a1870,#4a30a0);display:flex;align-items:center;justify-content:center;font-family:var(--sans);font-size:13px;font-weight:700;color:rgba(180,170,255,0.9);letter-spacing:1px; }
// // // .profile-info { padding:28px 14px 12px; }
// // // .profile-info-name { font-family:var(--sans);font-size:15px;font-weight:700;color:#fff;line-height:1.2;margin-bottom:2px; }
// // // .profile-info-name em { font-style:normal;color:var(--violet2); }
// // // .profile-info-role { font-size:8px;font-weight:600;color:rgba(180,185,220,0.45);letter-spacing:0.3px;margin-bottom:10px; }
// // // .profile-tiles { display:grid;grid-template-columns:1fr 1fr 1fr;gap:5px;margin-bottom:8px; }
// // // .profile-tile { background:rgba(124,111,247,0.08);border:0.5px solid rgba(124,111,247,0.14);border-radius:8px;padding:7px 6px;display:flex;flex-direction:column;gap:4px;animation:tile-slide 0.3s ease both; }
// // // .profile-tile:nth-child(1){animation-delay:2.5s;} .profile-tile:nth-child(2){animation-delay:2.65s;} .profile-tile:nth-child(3){animation-delay:2.8s;}
// // // @keyframes tile-slide { from{opacity:0;transform:translateY(6px);} to{opacity:1;transform:translateY(0);} }
// // // .tile-dot { width:12px;height:12px;border-radius:50%;background:rgba(124,111,247,0.15);display:flex;align-items:center;justify-content:center; }
// // // .tile-dot-inner { width:5px;height:5px;border-radius:50%;background:var(--violet2); }
// // // .tile-text { font-size:7px;color:rgba(180,185,220,0.45);line-height:1.3;font-weight:500; }
// // // .profile-save-btn { width:100%;padding:8px;background:linear-gradient(135deg,var(--blue2),var(--violet));color:#fff;border:none;border-radius:8px;font-size:8px;font-weight:700;letter-spacing:0.5px;text-transform:uppercase;cursor:default;display:flex;align-items:center;justify-content:center;gap:4px;animation:tile-slide 0.3s ease both;animation-delay:2.95s; }
// // // .beam-line { position:absolute;top:165px;left:148px;width:90px;height:1px;background:linear-gradient(90deg,var(--violet),rgba(124,111,247,0));transform-origin:left center;animation:beam-appear 0.4s ease both;animation-delay:1.5s;z-index:5; }
// // // @keyframes beam-appear { from{opacity:0;} to{opacity:1;} }
// // // .beam-particle { position:absolute;top:160px;left:148px;width:6px;height:6px;border-radius:50%;background:var(--violet2);animation:particle-travel 2s ease-in-out infinite;animation-delay:1.6s;z-index:6; }
// // // @keyframes particle-travel { 0%{left:148px;opacity:0;transform:scale(0.5);} 10%{opacity:1;transform:scale(1);} 90%{opacity:1;} 100%{left:228px;opacity:0;transform:scale(0.5);} }
// // // .hero-orb { position:absolute;width:500px;height:500px;border-radius:50%;background:radial-gradient(circle,rgba(124,111,247,0.08) 0%,transparent 65%);top:-100px;right:-100px;pointer-events:none;z-index:0; }

// // // /* DIVIDER */
// // // .lp-rule { height:0.5px; background:linear-gradient(90deg,transparent,rgba(124,111,247,0.28),transparent); }

// // // /* TRUST BAR */
// // // .lp-trust { padding:1.5rem 2rem; background:var(--surface); display:flex; flex-direction:column; align-items:center; gap:1rem; }
// // // .lp-trust-label { font-size:10px;font-weight:600;color:var(--text-muted2);letter-spacing:2.5px;text-transform:uppercase; }
// // // .lp-trust-items { display:flex;align-items:stretch;justify-content:center;flex-wrap:nowrap;width:100%;max-width:580px; }
// // // .lp-trust-item { display:flex;flex-direction:column;align-items:center;gap:5px;flex:1;padding:0.6rem 0.75rem;border-right:0.5px solid var(--border2);font-size:11.5px;font-weight:600;color:var(--text-muted);text-align:center; }
// // // .lp-trust-item:last-child { border-right:none; }
// // // .lp-trust-icon { font-size:1.2rem;line-height:1;width:36px;height:36px;border-radius:10px;background:var(--violet-subtle);border:0.5px solid var(--border-violet);display:flex;align-items:center;justify-content:center;margin-bottom:2px; }

// // // /* SECTIONS */
// // // .lp-section { padding:5rem 3rem; position:relative; }
// // // .lp-section-inner { max-width:1180px; margin:0 auto; }
// // // .lp-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--violet2);margin-bottom:1.25rem;text-align:center; }
// // // .lp-section-title { font-family:var(--sans);font-size:clamp(2.2rem,4vw,3.4rem);font-weight:800;line-height:1.08;letter-spacing:-1px;color:var(--text-primary);text-align:center;margin-bottom:1.25rem; }
// // // .lp-section-title em { font-style:italic;font-family:var(--serif);font-weight:600;color:var(--violet2); }
// // // .lp-section-sub { font-size:15px;font-weight:400;color:var(--text-muted);text-align:center;max-width:520px;margin:0 auto 4rem;line-height:1.9; }

// // // /* HOW IT WORKS */
// // // .lp-how-timeline { display:grid;grid-template-columns:1fr 72px 1fr 72px 1fr;align-items:start;gap:0;margin-top:1rem; }
// // // .lp-how-bridge { display:flex;align-items:flex-start;justify-content:center;padding-top:2.6rem; }
// // // .how-arrow-path { stroke:rgba(124,111,247,0.2);stroke-dasharray:60;stroke-dashoffset:60;transition:stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1),stroke 0.4s ease; }
// // // .how-arrow-path.filled { stroke-dashoffset:0;stroke:var(--violet2); }
// // // .lp-how-card { background:var(--surface);border:0.5px solid var(--border2);border-radius:20px;padding:2.2rem 1.8rem 2rem;position:relative;overflow:hidden;transition:border-color 0.3s,transform 0.3s;cursor:default; }
// // // .lp-how-card::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 80% 60% at 50% -10%,var(--violet-subtle2) 0%,transparent 70%);opacity:0;transition:opacity 0.3s; }
// // // .lp-how-card:hover { border-color:var(--border-violet);transform:translateY(-5px); }
// // // .lp-how-card:hover::before { opacity:1; }
// // // .lp-how-num { font-family:var(--sans);font-size:7rem;font-weight:800;color:rgba(124,111,247,0.06);line-height:1;position:absolute;bottom:-0.5rem;right:0.8rem;letter-spacing:-3px;pointer-events:none;user-select:none; }
// // // .lp-how-icon-ring { width:56px;height:56px;border-radius:16px;background:var(--violet-subtle);border:1px solid var(--border-violet);display:flex;align-items:center;justify-content:center;font-size:1.6rem;margin-bottom:1.5rem;position:relative; }
// // // .lp-how-icon-ring::after { content:'';position:absolute;inset:-4px;border-radius:20px;border:1px dashed var(--border-violet);animation:spin-slow 12s linear infinite; }
// // // @keyframes spin-slow { to{transform:rotate(360deg);} }
// // // .lp-how-step-label { font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:var(--violet2);margin-bottom:0.6rem; }
// // // .lp-how-title { font-family:var(--sans);font-size:1.55rem;font-weight:700;color:var(--text-primary);line-height:1.15;margin-bottom:0.75rem; }
// // // .lp-how-desc { font-size:13.5px;color:var(--text-muted);line-height:1.8; }

// // // /* FEATURES */
// // // .lp-feat-bento { display:grid;grid-template-columns:repeat(4,1fr);grid-template-rows:auto auto;gap:10px;margin-top:1rem; }
// // // .lp-feat-bcard { background:var(--surface);border:0.5px solid var(--border2);border-radius:18px;padding:1.8rem 1.6rem;position:relative;overflow:hidden;transition:border-color 0.3s,transform 0.25s,background 0.3s;cursor:default; }
// // // .lp-feat-bcard::before { content:'';position:absolute;top:0;left:-100%;width:60%;height:100%;background:linear-gradient(105deg,transparent,rgba(124,111,247,0.07),transparent);transition:left 0s;pointer-events:none;z-index:1; }
// // // .lp-feat-bcard:hover::before { left:160%;transition:left 0.6s ease; }
// // // .lp-feat-bcard:hover { border-color:var(--border-violet);background:var(--surface2);transform:translateY(-3px); }
// // // .lp-feat-bcard.hero-card { grid-column:span 2;background:var(--surface2);border-color:var(--border-violet);padding:2.4rem 2.2rem; }
// // // .lp-feat-bcard.tall { grid-row:span 2;display:flex;flex-direction:column; }
// // // .lp-feat-bcard::after { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:transparent;transition:background 0.3s; }
// // // .lp-feat-bcard:hover::after,.lp-feat-bcard.hero-card::after { background:linear-gradient(90deg,transparent,var(--border-violet),transparent); }
// // // .lp-feat-bicon { width:44px;height:44px;border-radius:12px;background:var(--violet-subtle);border:0.5px solid var(--border-violet);display:flex;align-items:center;justify-content:center;font-size:1.3rem;margin-bottom:1.25rem;flex-shrink:0; }
// // // .lp-feat-bcard.hero-card .lp-feat-bicon { width:52px;height:52px;font-size:1.6rem;border-radius:14px; }
// // // .lp-feat-btag { display:inline-block;font-size:8.5px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--violet2);margin-bottom:0.5rem;opacity:0.8; }
// // // .lp-feat-btitle { font-family:var(--sans);font-size:1.2rem;font-weight:700;color:var(--text-primary);line-height:1.15;margin-bottom:0.5rem; }
// // // .lp-feat-bcard.hero-card .lp-feat-btitle { font-size:1.55rem; }
// // // .lp-feat-bdesc { font-size:13px;color:var(--text-muted);line-height:1.8;flex:1; }
// // // .lp-feat-wallet { display:flex;gap:6px;margin-top:1.25rem; }
// // // .lp-wallet-chip { height:26px;border-radius:6px;flex:1;background:var(--violet-subtle);border:0.5px solid var(--border-violet);display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:600;color:var(--violet2);letter-spacing:0.5px;position:relative;overflow:hidden; }
// // // .lp-wallet-chip::after { content:'';position:absolute;top:0;left:-100%;width:50%;height:100%;background:linear-gradient(90deg,transparent,rgba(124,111,247,0.22),transparent);animation:chip-shine 3s ease-in-out infinite; }
// // // .lp-wallet-chip:nth-child(2)::after{animation-delay:0.4s;} .lp-wallet-chip:nth-child(3)::after{animation-delay:0.8s;}
// // // @keyframes chip-shine { 0%,70%{left:-100%;} 100%{left:200%;} }
// // // .lp-feat-socials { display:flex;flex-wrap:wrap;gap:6px;margin-top:1.1rem; }
// // // .lp-social-pill { padding:4px 10px;background:var(--violet-subtle);border:0.5px solid var(--border-violet);border-radius:100px;font-size:10px;font-weight:500;color:var(--text-muted);opacity:0;transform:translateY(6px) scale(0.94);transition:opacity 0.35s ease,transform 0.35s ease,background 0.2s,color 0.2s; }
// // // .lp-social-pill:hover { background:var(--violet-subtle2);color:var(--violet2); }
// // // .lp-social-pill:nth-child(1){transition-delay:0.05s;} .lp-social-pill:nth-child(2){transition-delay:0.12s;} .lp-social-pill:nth-child(3){transition-delay:0.19s;} .lp-social-pill:nth-child(4){transition-delay:0.26s;} .lp-social-pill:nth-child(5){transition-delay:0.33s;}
// // // .lp-feat-bcard.pills-visible .lp-social-pill { opacity:1;transform:translateY(0) scale(1); }
// // // .lp-qr-art { margin-top:auto;padding-top:1.5rem;display:grid;grid-template-columns:repeat(5,1fr);grid-template-rows:repeat(5,1fr);gap:3px;opacity:0.2;transition:opacity 0.3s; }
// // // .lp-feat-bcard.tall:hover .lp-qr-art { opacity:0.45; }
// // // .lp-qr-cell { border-radius:2px; }

// // // /* USE CASES */
// // // .lp-use-tabs { display:flex;flex-wrap:wrap;gap:0.5rem;justify-content:center;margin-bottom:3rem; }
// // // .lp-use-tab { padding:8px 18px;border-radius:100px;font-size:13px;font-weight:600;cursor:pointer;transition:all 0.2s;font-family:var(--sans);display:flex;align-items:center;gap:6px; }
// // // .lp-use-panel { display:grid;grid-template-columns:1fr 1fr;gap:3rem;align-items:center;background:var(--surface);border:0.5px solid var(--border2);border-radius:24px;padding:3rem;position:relative;overflow:hidden; }
// // // .lp-use-panel::before { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,var(--border-violet),transparent); }
// // // .lp-use-badge { display:inline-flex;align-items:center;gap:8px;padding:6px 14px;border-radius:100px;margin-bottom:1.5rem;font-size:12px;font-weight:700;background:var(--violet-subtle);color:var(--violet2);border:0.5px solid var(--border-violet); }
// // // .lp-use-tagline { font-family:var(--sans);font-size:2.1rem;font-weight:800;color:var(--text-primary);line-height:1.1;margin-bottom:1rem;letter-spacing:-0.5px; }
// // // .lp-use-desc { font-size:14px;font-weight:400;color:var(--text-muted);line-height:1.9;margin-bottom:2rem; }
// // // .lp-use-content { animation:tab-fade 0.35s cubic-bezier(0.22,1,0.36,1) both; }
// // // @keyframes tab-fade { from{opacity:0;transform:translateY(10px);} to{opacity:1;transform:translateY(0);} }
// // // .lp-use-highlights { display:flex;flex-direction:column;gap:0.75rem; }
// // // .lp-use-hl { display:flex;align-items:center;gap:12px;background:var(--surface2);border:0.5px solid var(--border2);border-radius:12px;padding:1rem 1.25rem;animation:hl-slide 0.3s ease both; }
// // // .lp-use-hl:nth-child(1){animation-delay:0.05s;} .lp-use-hl:nth-child(2){animation-delay:0.12s;} .lp-use-hl:nth-child(3){animation-delay:0.19s;} .lp-use-hl:nth-child(4){animation-delay:0.26s;}
// // // @keyframes hl-slide { from{opacity:0;transform:translateX(12px);} to{opacity:1;transform:translateX(0);} }
// // // .lp-use-hl-check { width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;background:var(--violet-subtle);color:var(--violet2); }
// // // .lp-use-hl-text { font-size:13px;font-weight:500;color:var(--text-muted); }

// // // /* PRICING */
// // // .lp-pricing-cta { padding:5rem 3rem;position:relative;overflow:hidden;background:var(--navy2); }
// // // .lp-pricing-cta::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 70% 80% at 50% 50%,rgba(60,50,200,0.12) 0%,transparent 70%);pointer-events:none; }
// // // .lp-pricing-cta-inner { max-width:1180px;margin:0 auto;display:grid;grid-template-columns:1fr 1.1fr;gap:4rem;align-items:center;position:relative; }
// // // .lp-pricing-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--violet2);margin-bottom:1.25rem; }
// // // .lp-pricing-headline { font-family:var(--sans);font-size:clamp(2.8rem,5vw,4.2rem);font-weight:800;line-height:1.05;color:var(--text-primary);margin-bottom:1.25rem;letter-spacing:-1px; }
// // // .lp-pricing-headline em { font-style:italic;font-family:var(--serif);font-weight:600;color:var(--violet2); }
// // // .lp-pricing-sub { font-size:15px;color:var(--text-muted);line-height:1.9;margin-bottom:2.5rem;max-width:400px; }
// // // .lp-pricing-perks { display:flex;flex-direction:column;gap:0.6rem;margin-bottom:2.5rem; }
// // // .lp-pricing-perk { display:flex;align-items:center;gap:10px;font-size:14px;font-weight:500;color:var(--text-muted); }
// // // .lp-perk-tick { width:20px;height:20px;border-radius:50%;flex-shrink:0;background:var(--violet-subtle);border:0.5px solid var(--border-violet);display:flex;align-items:center;justify-content:center;font-size:9px;color:var(--violet2);font-weight:700; }
// // // .lp-price-card { background:var(--surface);border:0.5px solid var(--border-violet);border-radius:24px;padding:2.5rem;position:relative;overflow:hidden; }
// // // .lp-price-card::before { content:'';position:absolute;top:0;left:10%;right:10%;height:1px;background:linear-gradient(90deg,transparent,var(--violet),transparent); }
// // // .lp-price-card-top { display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:1.5rem; }
// // // .lp-price-badge { display:inline-block;background:linear-gradient(135deg,var(--blue2),var(--violet));color:#fff;border-radius:100px;padding:5px 14px;font-size:10px;font-weight:700;letter-spacing:0.5px;text-transform:uppercase; }
// // // .lp-price-amount { text-align:right; }
// // // .lp-price-num { font-family:var(--sans);font-size:4rem;font-weight:800;color:var(--text-primary);line-height:1;letter-spacing:-2px; }
// // // .lp-price-period { font-size:10px;font-weight:600;color:var(--text-muted2);letter-spacing:2px;text-transform:uppercase;margin-top:4px; }
// // // .lp-price-divider { height:0.5px;background:var(--border2);margin:1.25rem 0; }
// // // .lp-price-list { list-style:none;display:flex;flex-direction:column;gap:0;margin-bottom:1.75rem; }
// // // .lp-price-item { display:flex;align-items:center;gap:10px;padding:0.55rem 0;font-size:13px;font-weight:500;color:var(--text-muted);border-bottom:0.5px solid var(--border2); }
// // // .lp-price-item:last-child { border-bottom:none; }
// // // .lp-price-check { color:var(--violet2);font-size:11px;flex-shrink:0; }

// // // /* FOOTER */
// // // .lp-footer { padding:3rem 3rem 4rem;border-top:0.5px solid var(--border2);background:var(--surface); }
// // // .lp-footer-inner { max-width:1180px;margin:0 auto;display:flex;flex-direction:column;align-items:center;gap:1rem; }
// // // .lp-footer-logo { display:flex;align-items:center;gap:10px;font-family:var(--sans);font-size:15px;font-weight:700;color:var(--text-primary); }
// // // .lp-footer-logo em { color:var(--violet2);font-style:normal; }
// // // .lp-footer-tagline { font-size:13px;color:var(--text-muted2); }
// // // .lp-footer-copy { font-size:12px;color:var(--text-muted2);opacity:0.6; }

// // // /* RESPONSIVE */
// // // @media (min-width:901px) {
// // //   .lp-trust{flex-direction:row;padding:1.5rem 3rem;gap:2.5rem;}
// // //   .lp-trust-items{width:auto;max-width:none;}
// // //   .lp-trust-item{flex-direction:row;flex:none;padding:0 1.5rem;border-right:0.5px solid var(--border2);border-bottom:none;gap:8px;}
// // //   .lp-trust-item:last-child{border-right:none;}
// // //   .lp-trust-icon{width:28px;height:28px;border-radius:8px;font-size:1rem;flex-shrink:0;margin-bottom:0;}
// // // }
// // // @media (max-width:900px) {
// // //   .lp-hero{grid-template-columns:1fr;text-align:center;padding:8rem 1.5rem 4rem;}
// // //   .lp-hero-sub{margin:0 auto 1rem;}
// // //   .lp-hero-ctas{justify-content:center;}
// // //   .lp-hero-stats{justify-content:center;}
// // //   .lp-hero-right{margin-top:3rem;}
// // //   .scan-scene{width:300px;height:380px;transform:scale(0.85);}
// // //   .lp-how-timeline{grid-template-columns:1fr;}
// // //   .lp-how-bridge{display:none;}
// // //   .lp-feat-bento{grid-template-columns:1fr 1fr;}
// // //   .lp-feat-bcard.hero-card{grid-column:span 2;}
// // //   .lp-feat-bcard.tall{grid-row:span 1;}
// // //   .lp-use-panel{grid-template-columns:1fr;}
// // //   .lp-pricing-cta-inner{grid-template-columns:1fr;}
// // //   .lp-nav{padding:1rem 1.5rem;}
// // //   .lp-nav.scrolled{padding:0.75rem 1.5rem;}
// // //   .lp-section{padding:4rem 1.5rem;}
// // //   .lp-pricing-cta{padding:4rem 1.5rem;}
// // //   .lp-hamburger{display:flex;}
// // //   .lp-nav-links{display:none;}
// // //   .lp-mobile-cluster{display:flex;}
// // // }
// // // @media (max-width:600px) {
// // //   .lp-feat-bento{grid-template-columns:1fr;}
// // //   .lp-feat-bcard.hero-card{grid-column:span 1;}
// // //   .lp-hero-title{font-size:clamp(2.5rem,9vw,3.4rem);}
// // //   .lp-hero-stats{gap:1.5rem;}
// // //   .lp-stat-num{font-size:2rem;}
// // //   .lp-section{padding:3.5rem 1.25rem;}
// // //   .lp-section-title{font-size:clamp(2rem,7vw,2.7rem);}
// // //   .lp-use-panel{padding:1.75rem 1.25rem;}
// // //   .lp-pricing-cta{padding:3rem 1.25rem;}
// // //   .lp-pricing-headline{font-size:clamp(2.4rem,8vw,3rem);}
// // //   .lp-hero-ctas{flex-direction:column;align-items:center;}
// // //   .lp-hero-ctas .lp-btn-primary,.lp-hero-ctas .lp-btn-ghost{width:100%;justify-content:center;}
// // // }
// // // `;

// // // /* COUNT-UP HOOK */
// // // function useCountUp(target, duration = 1800, suffix = "", started = false) {
// // //     const [display, setDisplay] = useState("0");
// // //     const [progress, setProgress] = useState(0);
// // //     const frameRef = useRef(null);
// // //     useEffect(() => {
// // //         if (!started) return;
// // //         const num = parseFloat(target.replace(/[^0-9.]/g, ""));
// // //         if (!num) { setDisplay(target); setProgress(100); return; }
// // //         const startTime = performance.now();
// // //         const animate = (now) => {
// // //             const pct = Math.min((now - startTime) / duration, 1);
// // //             const eased = pct === 1 ? 1 : 1 - Math.pow(2, -10 * pct);
// // //             setDisplay((Number.isInteger(num) ? Math.round(num * eased).toString() : (num * eased).toFixed(0)) + suffix);
// // //             setProgress(Math.round(pct * 100));
// // //             if (pct < 1) frameRef.current = requestAnimationFrame(animate);
// // //         };
// // //         frameRef.current = requestAnimationFrame(animate);
// // //         return () => cancelAnimationFrame(frameRef.current);
// // //     }, [started, target, duration, suffix]);
// // //     return { display, progress };
// // // }

// // // function StatItem({ value, suffix, label, started, delay = 0 }) {
// // //     const [go, setGo] = useState(false);
// // //     useEffect(() => {
// // //         if (!started) return;
// // //         const t = setTimeout(() => setGo(true), delay);
// // //         return () => clearTimeout(t);
// // //     }, [started, delay]);
// // //     const { display, progress } = useCountUp(value, 1600, suffix, go);
// // //     return (
// // //         <div className="lp-stat">
// // //             <div className={`lp-stat-num${go && progress < 100 ? " counting" : ""}`}>{value === "0" ? "0" : display}</div>
// // //             <div className="lp-stat-label">{label}</div>
// // //             <div className="lp-stat-bar"><div className="lp-stat-bar-fill" style={{ width: `${progress}%` }} /></div>
// // //         </div>
// // //     );
// // // }

// // // const QR_PATTERN = [[1, 1, 1, 0, 1, 0], [1, 0, 1, 1, 0, 1], [1, 1, 1, 0, 1, 1], [0, 1, 0, 1, 0, 0], [1, 0, 1, 1, 1, 0], [0, 1, 0, 0, 1, 1]];

// // // function ScanScene() {
// // //     return (
// // //         <div className="scan-scene">
// // //             <div className="hero-orb" />
// // //             <div className="phone-scanner">
// // //                 <div className="phone-scanner-screen">
// // //                     <div className="scanner-viewfinder">
// // //                         <div className="vf-corner-tr" /><div className="vf-corner-bl" />
// // //                         <div className="scan-laser" />
// // //                         <div className="mini-qr">
// // //                             {QR_PATTERN.map((row, ri) => row.map((cell, ci) => (
// // //                                 <div key={`${ri}-${ci}`} className="mini-qr-cell" style={{ background: cell ? "rgba(124,111,247,0.55)" : "transparent" }} />
// // //                             )))}
// // //                         </div>
// // //                         <div className="scanner-success-flash" />
// // //                     </div>
// // //                     <div className="scanner-label">Point at QR code</div>
// // //                 </div>
// // //             </div>
// // //             <div className="beam-line" /><div className="beam-particle" />
// // //             <div className="scan-ripple r1" /><div className="scan-ripple r2" /><div className="scan-ripple r3" />
// // //             <div className="scan-success"><div className="scan-success-dot" />Contact Saved ✓</div>
// // //             <div className="phone-profile">
// // //                 <div className="profile-card-wrap">
// // //                     <div className="profile-banner">
// // //                         <div className="profile-banner-img" /><div className="profile-banner-fade" />
// // //                         <div className="profile-avatar-ring"><div className="profile-avatar-inner">UN</div></div>
// // //                     </div>
// // //                     <div className="profile-info">
// // //                         <div className="profile-info-name">User <em>Name</em></div>
// // //                         <div className="profile-info-role">Director · Company Name</div>
// // //                         <div className="profile-tiles">
// // //                             {[["📞", "Phone"], ["✉️", "Email"], ["📍", "Delhi"]].map(([, l]) => (
// // //                                 <div className="profile-tile" key={l}><div className="tile-dot"><div className="tile-dot-inner" /></div><div className="tile-text">{l}</div></div>
// // //                             ))}
// // //                         </div>
// // //                         <button className="profile-save-btn">↓ Save Contact</button>
// // //                         <div className="profile-tiles" style={{ marginTop: "1rem" }}>
// // //                             {[["🌐", "Website"], ["📄", "Resume"], ["ⓕ", "Facebook"]].map(([, l]) => (
// // //                                 <div className="profile-tile" key={l}><div className="tile-dot"><div className="tile-dot-inner" /></div><div className="tile-text">{l}</div></div>
// // //                             ))}
// // //                         </div>
// // //                     </div>
// // //                 </div>
// // //             </div>
// // //         </div>
// // //     );
// // // }

// // // function ArrowBridge({ filled }) {
// // //     return (
// // //         <div className="lp-how-bridge">
// // //             <svg width="36" height="28" viewBox="0 0 36 28" fill="none" overflow="visible">
// // //                 <path d="M2 14 L26 14" className={`how-arrow-path${filled ? " filled" : ""}`} strokeWidth="1.5" strokeLinecap="round" fill="none"
// // //                     style={{ strokeDasharray: 28, strokeDashoffset: filled ? 0 : 28, transition: "stroke-dashoffset 0.8s cubic-bezier(0.22,1,0.36,1),stroke 0.4s ease" }} />
// // //                 <path d="M20 7 L28 14 L20 21" className={`how-arrow-path${filled ? " filled" : ""}`} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"
// // //                     style={{ strokeDasharray: 24, strokeDashoffset: filled ? 0 : 24, transition: "stroke-dashoffset 0.6s cubic-bezier(0.22,1,0.36,1) 0.3s,stroke 0.4s ease 0.3s" }} />
// // //             </svg>
// // //         </div>
// // //     );
// // // }

// // // function LogoSVG({ size = 34 }) {
// // //     return (
// // //         <svg width={size} height={size} viewBox="0 0 34 34" fill="none">
// // //             <rect width="34" height="34" rx="9" fill="rgba(124,111,247,0.14)" />
// // //             <rect width="34" height="34" rx="9" stroke="rgba(124,111,247,0.38)" strokeWidth="0.5" />
// // //             <circle cx="15" cy="13" r="5" stroke="#9B8CFF" strokeWidth="1.5" fill="none" />
// // //             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="#9B8CFF" strokeWidth="1.5" strokeLinecap="round" fill="none" />
// // //             <path d="M23 11 L27 11 L27 23" stroke="#9B8CFF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.6" />
// // //             <circle cx="27" cy="23" r="1.8" fill="#9B8CFF" />
// // //         </svg>
// // //     );
// // // }

// // // const USE_CASES = [
// // //     { icon: "👔", role: "Job Seekers", tagline: "Land your next opportunity faster", desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one elegant scan.", highlights: ["Resume attached & downloadable", "LinkedIn & GitHub links", "QR code for instant sharing", "Save contact in one tap"] },
// // //     { icon: "💼", role: "Freelancers", tagline: "Win clients before you even speak", desc: "Share everything a client needs — portfolio, social proof, and contact — all in a single professional link.", highlights: ["Portfolio & website link", "WhatsApp quick contact", "Instagram & social proof", "Professional digital presence"] },
// // //     { icon: "🏢", role: "Business Owners", tagline: "Network smarter, not harder", desc: "Stop spending on printed cards that get lost. Share via QR at events — always updated, never outdated.", highlights: ["Always up-to-date details", "Google Wallet for offline use", "QR code for events & meetups", "Share via WhatsApp or email"] },
// // //     { icon: "🎓", role: "Students", tagline: "Build your professional identity early", desc: "Stand out in campus placements and internship drives. Show your skills, projects, and social profiles.", highlights: ["GitHub & project links", "Resume upload for placements", "LinkedIn profile showcase", "Free forever — no cost"] },
// // //     { icon: "📣", role: "Sales Pros", tagline: "Close the follow-up gap instantly", desc: "At trade shows or cold visits — one QR scan and your prospect has everything they need.", highlights: ["Instant QR share at events", "Website & product links", "Save contact in one tap", "Google Wallet for quick access"] },
// // //     { icon: "🧑‍💻", role: "Developers", tagline: "Your entire dev profile in one link", desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share at hackathons.", highlights: ["GitHub & portfolio links", "Twitter / X profile", "Resume for job applications", "Professional dev identity"] },
// // // ];

// // // function UseCasesSection({ navigate }) {
// // //     const [active, setActive] = useState(0);
// // //     const [tabKey, setTabKey] = useState(0);
// // //     const cur = USE_CASES[active];
// // //     const switchTab = (i) => { setActive(i); setTabKey(k => k + 1); };
// // //     return (
// // //         <section className="lp-section">
// // //             <div className="lp-section-inner">
// // //                 <span className="lp-eyebrow reveal from-up">Who is it for</span>
// // //                 <h2 className="lp-section-title reveal from-up d1">Built for every <em>professional</em></h2>
// // //                 <p className="lp-section-sub reveal from-up d2">Whether you're a student or a CEO — your digital card works for you.</p>
// // //                 <div className="lp-use-tabs reveal from-up d3">
// // //                     {USE_CASES.map((u, i) => (
// // //                         <button key={i} className="lp-use-tab" onClick={() => switchTab(i)}
// // //                             style={{ border: active === i ? "0.5px solid var(--border-violet)" : "0.5px solid var(--border2)", background: active === i ? "var(--violet-subtle2)" : "transparent", color: active === i ? "var(--violet2)" : "var(--text-muted)" }}>
// // //                             {u.icon} {u.role}
// // //                         </button>
// // //                     ))}
// // //                 </div>
// // //                 <div className="lp-use-panel reveal from-up d4">
// // //                     <div key={`l-${tabKey}`} className="lp-use-content">
// // //                         <div className="lp-use-badge"><span>{cur.icon}</span><span style={{ fontSize: "12px" }}>{cur.role}</span></div>
// // //                         <div className="lp-use-tagline">{cur.tagline}</div>
// // //                         <p className="lp-use-desc">{cur.desc}</p>
// // //                         <button className="lp-btn-primary" onClick={() => navigate("/animation/card")}>Create My Card — Free →</button>
// // //                     </div>
// // //                     <div key={`r-${tabKey}`} className="lp-use-highlights">
// // //                         {cur.highlights.map((h, i) => (
// // //                             <div className="lp-use-hl" key={i} style={{ animationDelay: `${0.05 + i * 0.07}s` }}>
// // //                                 <div className="lp-use-hl-check">✓</div>
// // //                                 <span className="lp-use-hl-text">{h}</span>
// // //                             </div>
// // //                         ))}
// // //                     </div>
// // //                 </div>
// // //             </div>
// // //         </section>
// // //     );
// // // }

// // // export default function LandingPage() {
// // //     const navigate = useNavigate();
// // //     const [scrolled, setScrolled] = useState(false);
// // //     const [menuOpen, setMenuOpen] = useState(false);
// // //     const [statsStarted, setStatsStarted] = useState(false);
// // //     const [howVisible, setHowVisible] = useState(false);
// // //     const statsRef = useRef(null);
// // //     const howRef = useRef(null);

// // //     useEffect(() => {
// // //         const onResize = () => { if (window.innerWidth > 900) setMenuOpen(false); };
// // //         window.addEventListener("resize", onResize);
// // //         return () => window.removeEventListener("resize", onResize);
// // //     }, []);

// // //     useEffect(() => {
// // //         const onScroll = () => setScrolled(window.scrollY > 40);
// // //         window.addEventListener("scroll", onScroll);
// // //         return () => window.removeEventListener("scroll", onScroll);
// // //     }, []);

// // //     useEffect(() => {
// // //         const obs = [];
// // //         const revObs = new IntersectionObserver(
// // //             entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
// // //             { threshold: 0.1 }
// // //         );
// // //         document.querySelectorAll(".reveal").forEach(el => revObs.observe(el));
// // //         obs.push(revObs);

// // //         if (statsRef.current) {
// // //             const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsStarted(true); }, { threshold: 0.5 });
// // //             o.observe(statsRef.current); obs.push(o);
// // //         }
// // //         if (howRef.current) {
// // //             const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setHowVisible(true); }, { threshold: 0.3 });
// // //             o.observe(howRef.current); obs.push(o);
// // //         }
// // //         const pillObs = new IntersectionObserver(
// // //             entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("pills-visible"); }),
// // //             { threshold: 0.4 }
// // //         );
// // //         document.querySelectorAll(".lp-feat-bcard.has-pills").forEach(c => pillObs.observe(c));
// // //         obs.push(pillObs);

// // //         return () => obs.forEach(o => o.disconnect());
// // //     }, []);

// // //     return (
// // //         <div style={{ background: "var(--navy)", minHeight: "100vh" }}>
// // //             <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

// // //             {/* MOBILE DRAWER */}
// // //             <div className={`lp-mobile-menu${menuOpen ? " open" : ""}`}>
// // //                 <div className="lp-mobile-menu-backdrop" onClick={() => setMenuOpen(false)} />
// // //                 <div className="lp-mobile-menu-panel">
// // //                     <div className="lp-mobile-menu-header">
// // //                         <a href="/" className="lp-logo" onClick={() => setMenuOpen(false)}><LogoSVG size={28} />myprofile<em>corner</em></a>
// // //                         <button className="lp-mobile-close" onClick={() => setMenuOpen(false)}>✕</button>
// // //                     </div>
// // //                     <div className="lp-mobile-links">
// // //                         <a href="#how" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">⚡</span>How it works</a>
// // //                         <a href="#features" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">✦</span>Features</a>
// // //                         <a href="#pricing" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">₹</span>Pricing</a>
// // //                     </div>
// // //                     <div className="lp-mobile-divider" />
// // //                     <div className="lp-mobile-menu-footer">
// // //                         <button className="lp-mobile-cta" onClick={() => { setMenuOpen(false); navigate("/animation/card"); }}>Create My Card — Free →</button>
// // //                         <div className="lp-mobile-tagline">100% Free · No app required · 2 min setup</div>
// // //                     </div>
// // //                 </div>
// // //             </div>

// // //             {/* NAV */}
// // //             <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
// // //                 <div className="lp-nav-inner">
// // //                     <a href="/" className="lp-logo"><LogoSVG size={34} />myprofile<em>corner</em></a>
// // //                     <div className="lp-nav-links">
// // //                         <a href="#how" className="lp-nav-link">How it works</a>
// // //                         <a href="#features" className="lp-nav-link">Features</a>
// // //                         <a href="#pricing" className="lp-nav-link">Pricing</a>
// // //                         <button className="lp-nav-cta" onClick={() => navigate("/animation/card")}>Create Free Card</button>
// // //                     </div>
// // //                     <div className="lp-mobile-cluster">
// // //                         <button className={`lp-hamburger${menuOpen ? " open" : ""}`} onClick={() => setMenuOpen(p => !p)} aria-label="Menu">
// // //                             <span /><span /><span />
// // //                         </button>
// // //                     </div>
// // //                 </div>
// // //             </nav>

// // //             {/* HERO */}
// // //             <div className="lp-hero-wrap">
// // //                 <div className="lp-hero-mesh" /><div className="lp-hero-mesh-bottom" /><div className="lp-hero-grid" />
// // //                 <div className="lp-hero">
// // //                     <div className="lp-hero-left">
// // //                         <div className="lp-hero-eyebrow reveal from-down">
// // //                             <div className="lp-hero-eyebrow-dot" />100% Free · No App Needed
// // //                         </div>
// // //                         <h1 className="lp-hero-title reveal from-left">
// // //                             Your Digital Identity,<br />
// // //                             <span className="gradient-line">Always in Your Pocket</span>
// // //                         </h1>
// // //                         <p className="lp-hero-sub reveal from-left d1">
// // //                             Replace paper visiting cards with a smart digital card. Share your profile instantly via QR code, link, or Google Wallet — no printing, no waste.
// // //                         </p>
// // //                         <div className="lp-hero-stats" ref={statsRef}>
// // //                             <StatItem value="100" suffix="%" label="Free forever" started={statsStarted} delay={0} />
// // //                             <StatItem value="2" suffix=" min" label="Setup time" started={statsStarted} delay={200} />
// // //                             <StatItem value="0" suffix="" label="Paper waste" started={statsStarted} delay={400} />
// // //                         </div>
// // //                         <div className="lp-hero-ctas reveal from-up d2">
// // //                             <button className="lp-btn-primary" onClick={() => navigate("/animation/card")}>Create My Card — It's Free →</button>
// // //                             <a href="#how" className="lp-btn-ghost">See how it works</a>
// // //                         </div>
// // //                     </div>
// // //                     <div className="lp-hero-right reveal from-right"><ScanScene /></div>
// // //                 </div>
// // //             </div>

// // //             <div className="lp-rule" />

// // //             {/* TRUST BAR */}
// // //             <div className="lp-trust">
// // //                 <span className="lp-trust-label">Works seamlessly with</span>
// // //                 <div className="lp-trust-items">
// // //                     {[["🔥", "Firebase"], ["🪙", "Google Wallet"], ["📱", "Android & iOS"], ["🔗", "Any Browser"]].map(([icon, label]) => (
// // //                         <div className="lp-trust-item" key={label}>
// // //                             <div className="lp-trust-icon">{icon}</div><span>{label}</span>
// // //                         </div>
// // //                     ))}
// // //                 </div>
// // //             </div>

// // //             <div className="lp-rule" />

// // //             {/* HOW IT WORKS */}
// // //             <section id="how" className="lp-section" ref={howRef}>
// // //                 <div className="lp-section-inner">
// // //                     <span className="lp-eyebrow reveal from-up">How it works</span>
// // //                     <h2 className="lp-section-title reveal from-up d1">From setup to sharing <em>in 3 steps</em></h2>
// // //                     <p className="lp-section-sub reveal from-up d2">No app downloads. No tech skills required. Just fill, share, impress.</p>
// // //                     <div className="lp-how-timeline">
// // //                         <div className="lp-how-card reveal from-left d1">
// // //                             <div className="lp-how-num">01</div><div className="lp-how-icon-ring">✏️</div>
// // //                             <div className="lp-how-step-label">Step 01</div><div className="lp-how-title">Fill Your Profile</div>
// // //                             <p className="lp-how-desc">Add your name, photo, contact info, social links, resume & website. Takes less than 2 minutes.</p>
// // //                         </div>
// // //                         <ArrowBridge filled={howVisible} />
// // //                         <div className="lp-how-card reveal from-up d3">
// // //                             <div className="lp-how-num">02</div><div className="lp-how-icon-ring">🎴</div>
// // //                             <div className="lp-how-step-label">Step 02</div><div className="lp-how-title">Get Your Digital Card</div>
// // //                             <p className="lp-how-desc">Instantly get a beautiful card with a unique QR code & shareable link — no design skills needed.</p>
// // //                         </div>
// // //                         <ArrowBridge filled={howVisible} />
// // //                         <div className="lp-how-card reveal from-right d5">
// // //                             <div className="lp-how-num">03</div><div className="lp-how-icon-ring">🚀</div>
// // //                             <div className="lp-how-step-label">Step 03</div><div className="lp-how-title">Share Anywhere</div>
// // //                             <p className="lp-how-desc">Show QR in meetings, add to Google Wallet, or send your link via WhatsApp, email, or LinkedIn.</p>
// // //                         </div>
// // //                     </div>
// // //                 </div>
// // //             </section>

// // //             <div className="lp-rule" />

// // //             {/* FEATURES */}
// // //             <section id="features" className="lp-section" style={{ background: "var(--navy2)" }}>
// // //                 <div className="lp-section-inner">
// // //                     <span className="lp-eyebrow reveal from-up">Features</span>
// // //                     <h2 className="lp-section-title reveal from-up d1">Everything a modern <em>professional</em> needs</h2>
// // //                     <p className="lp-section-sub reveal from-up d2">One card. Every detail. Always up-to-date.</p>
// // //                     <div className="lp-feat-bento">
// // //                         <div className="lp-feat-bcard hero-card reveal from-left d1">
// // //                             <div className="lp-feat-bicon">🪙</div><div className="lp-feat-btag">Premium Feature</div>
// // //                             <div className="lp-feat-btitle">Google Wallet Pass</div>
// // //                             <p className="lp-feat-bdesc">Add your card to Google Wallet and access it offline — at meetings, events, or anywhere without internet.</p>
// // //                             <div className="lp-feat-wallet">
// // //                                 <div className="lp-wallet-chip">G Pay</div><div className="lp-wallet-chip">Wallet</div><div className="lp-wallet-chip">Offline ✓</div>
// // //                             </div>
// // //                         </div>
// // //                         <div className="lp-feat-bcard tall reveal from-right d2">
// // //                             <div className="lp-feat-bicon">📲</div><div className="lp-feat-btag">Instant Share</div>
// // //                             <div className="lp-feat-btitle">Unique QR Code</div>
// // //                             <p className="lp-feat-bdesc">Every card gets a dedicated QR. One scan opens your full profile — no typing, no friction.</p>
// // //                             <div className="lp-qr-art">
// // //                                 {[1, 1, 1, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 1].map((v, i) => (
// // //                                     <div key={i} className="lp-qr-cell" style={{ background: v ? "var(--violet)" : "var(--violet-subtle)", borderRadius: "2px", aspectRatio: "1" }} />
// // //                                 ))}
// // //                             </div>
// // //                         </div>
// // //                         <div className="lp-feat-bcard reveal from-up d3">
// // //                             <div className="lp-feat-bicon">📸</div><div className="lp-feat-btag">Identity</div>
// // //                             <div className="lp-feat-btitle">Profile Photo</div>
// // //                             <p className="lp-feat-bdesc">Your face on your card — and inside Google Wallet too.</p>
// // //                         </div>
// // //                         <div className="lp-feat-bcard reveal from-up d4">
// // //                             <div className="lp-feat-bicon">💾</div><div className="lp-feat-btag">One Tap</div>
// // //                             <div className="lp-feat-btitle">Save Contact</div>
// // //                             <p className="lp-feat-bdesc">Visitors save you directly to their phone contacts with a single tap.</p>
// // //                         </div>
// // //                         <div className="lp-feat-bcard hero-card has-pills reveal from-left d2">
// // //                             <div className="lp-feat-bicon">🔗</div><div className="lp-feat-btag">Connections</div>
// // //                             <div className="lp-feat-btitle">All Social Links</div>
// // //                             <p className="lp-feat-bdesc">LinkedIn, GitHub, Instagram, Twitter, WhatsApp, YouTube — everything that matters in one place.</p>
// // //                             <div className="lp-feat-socials">
// // //                                 {["in LinkedIn", "⌥ GitHub", "✉ WhatsApp", "▶ YouTube", "📷 Insta"].map(s => (
// // //                                     <span key={s} className="lp-social-pill">{s}</span>
// // //                                 ))}
// // //                             </div>
// // //                         </div>
// // //                         <div className="lp-feat-bcard reveal from-up d3">
// // //                             <div className="lp-feat-bicon">📄</div><div className="lp-feat-btag">Career</div>
// // //                             <div className="lp-feat-btitle">Resume Upload</div>
// // //                             <p className="lp-feat-bdesc">Attach your PDF resume. Recruiters can download it instantly.</p>
// // //                         </div>
// // //                         <div className="lp-feat-bcard reveal from-up d4">
// // //                             <div className="lp-feat-bicon">✏️</div><div className="lp-feat-btag">Always Fresh</div>
// // //                             <div className="lp-feat-btitle">Edit Anytime</div>
// // //                             <p className="lp-feat-bdesc">Changed jobs? New number? Update instantly — same link forever.</p>
// // //                         </div>
// // //                         <div className="lp-feat-bcard reveal from-right d5">
// // //                             <div className="lp-feat-bicon">🌐</div><div className="lp-feat-btag">Universal</div>
// // //                             <div className="lp-feat-btitle">Works Everywhere</div>
// // //                             <p className="lp-feat-bdesc">Opens in any browser on any device. No app install ever required.</p>
// // //                         </div>
// // //                     </div>
// // //                 </div>
// // //             </section>

// // //             <div className="lp-rule" />
// // //             <UseCasesSection navigate={navigate} />
// // //             <div className="lp-rule" />

// // //             {/* PRICING */}
// // //             <section id="pricing" className="lp-pricing-cta">
// // //                 <div className="lp-pricing-cta-inner">
// // //                     <div>
// // //                         <span className="lp-pricing-eyebrow reveal from-left">Pricing</span>
// // //                         <h2 className="lp-pricing-headline reveal from-left d1">Stop carrying<br /><em>paper cards.</em></h2>
// // //                         <p className="lp-pricing-sub reveal from-left d2">Create your digital card in under 2 minutes. No hidden charges. No credit card. Free forever.</p>
// // //                         <div className="lp-pricing-perks reveal from-left d3">
// // //                             {["No app download needed", "Unique QR code & shareable link", "Google Wallet pass included", "Edit anytime — same link forever", "Works on every device & browser"].map(p => (
// // //                                 <div className="lp-pricing-perk" key={p}><div className="lp-perk-tick">✓</div><span>{p}</span></div>
// // //                             ))}
// // //                         </div>
// // //                         <button className="lp-btn-primary reveal from-up d4" onClick={() => navigate("/animation/card")}>Create My Card Now →</button>
// // //                     </div>
// // //                     <div className="lp-price-card reveal from-right d2">
// // //                         <div className="lp-price-card-top">
// // //                             <div className="lp-price-badge">Most Popular</div>
// // //                             <div className="lp-price-amount">
// // //                                 <div className="lp-price-num">₹0</div>
// // //                                 <div className="lp-price-period">Forever Free</div>
// // //                             </div>
// // //                         </div>
// // //                         <div className="lp-price-divider" />
// // //                         <ul className="lp-price-list">
// // //                             {["1 Digital Visiting Card", "Unique QR Code & Link", "Profile Photo Upload", "Resume Upload (PDF)", "All Social Media Links", "Google Wallet Pass", "Save Contact (.vcf)", "Edit Anytime", "No App Required"].map(item => (
// // //                                 <li className="lp-price-item" key={item}><span className="lp-price-check">✦</span>{item}</li>
// // //                             ))}
// // //                         </ul>
// // //                         <button className="lp-btn-primary" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/animation/card")}>Get Started Free →</button>
// // //                     </div>
// // //                 </div>
// // //             </section>

// // //             <div className="lp-rule" />

// // //             {/* FOOTER */}
// // //             <footer className="lp-footer">
// // //                 <div className="lp-footer-inner">
// // //                     <div className="lp-footer-logo"><LogoSVG size={28} />myprofile<em>corner</em></div>
// // //                     <p className="lp-footer-tagline">Your digital identity, always in your pocket.</p>
// // //                     <p className="lp-footer-copy">© {new Date().getFullYear()} MyProfileCorner · Made with ♥ in India</p>
// // //                 </div>
// // //             </footer>
// // //         </div>
// // //     );
// // // }


// // // // import React, { useState, useEffect, useRef, useCallback } from "react";
// // // // import { useNavigate } from "react-router-dom";

// // // // const FONT_LINK = `
// // // //   @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600&family=Montserrat:wght@300;400;500;600;700;800&display=swap');
// // // // `;

// // // // const GLOBAL_CSS = `
// // // // ${FONT_LINK}

// // // // *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

// // // // :root {
// // // //   --black: #080808;
// // // //   --dark: #0E0E0E;
// // // //   --card: #141414;
// // // //   --border: rgba(255,255,255,0.07);
// // // //   --gold: #D4AF37;
// // // //   --gold2: #F0D060;
// // // //   --gold3: #C9A227;
// // // //   --gold-dim: rgba(212,175,55,0.18);
// // // //   --white: #FFFFFF;
// // // //   --muted: rgba(255,255,255,0.45);
// // // //   --muted2: rgba(255,255,255,0.25);
// // // //   --serif: 'Cormorant Garamond', serif;
// // // //   --sans: 'Montserrat', sans-serif;
// // // //   --bg: #080808;
// // // //   --surface: #141414;
// // // //   --surface2: #1a1a1a;
// // // //   --text-primary: #FFFFFF;
// // // //   --text-muted: rgba(255,255,255,0.45);
// // // //   --text-muted2: rgba(255,255,255,0.25);
// // // //   --border-color: rgba(255,255,255,0.07);
// // // //   --border-gold: rgba(212,175,55,0.2);
// // // //   --gold-subtle: rgba(212,175,55,0.08);
// // // //   --gold-subtle2: rgba(212,175,55,0.12);
// // // // }

// // // // /* ══════════════════════════════════════════════════
// // // //    LIGHT THEME — Vibrant Warm Ivory + Rich Gold
// // // //    Inspired by premium Indian design — NOT dull beige
// // // // ══════════════════════════════════════════════════ */
// // // // .lp-light {
// // // //   /* Background layers: bright ivory with warm tint */
// // // //   --bg: #FFFBF2;
// // // //   --surface: #FFF8EA;
// // // //   --surface2: #FEF2D0;
// // // //   --card: #FFF8EA;
// // // //   --dark: #FEF2D0;
// // // //   --black: #FFFBF2;

// // // //   /* Borders */
// // // //   --border-color: rgba(180,130,0,0.13);
// // // //   --border: rgba(180,130,0,0.13);
// // // //   --border-gold: rgba(180,130,0,0.28);

// // // //   /* Text */
// // // //   --white: #1A1200;
// // // //   --text-primary: #1A1200;
// // // //   --text-muted: rgba(60,40,0,0.62);
// // // //   --text-muted2: rgba(60,40,0,0.42);
// // // //   --muted: rgba(60,40,0,0.62);
// // // //   --muted2: rgba(60,40,0,0.42);

// // // //   /* Golds — deeper, richer in light mode */
// // // //   --gold: #9A6B00;
// // // //   --gold2: #C28A00;
// // // //   --gold3: #7A5200;
// // // //   --gold-dim: rgba(154,107,0,0.14);
// // // //   --gold-subtle: rgba(154,107,0,0.09);
// // // //   --gold-subtle2: rgba(154,107,0,0.16);
// // // // }

// // // // /* Light mode body gets a subtle warm noise texture + gradient */
// // // // .lp-light body,
// // // // .lp-light-bg {
// // // //   background: #FFFBF2 !important;
// // // // }

// // // // html { scroll-behavior: smooth; }
// // // // body {
// // // //   background: var(--bg);
// // // //   color: var(--text-primary);
// // // //   font-family: var(--sans);
// // // //   overflow-x: hidden;
// // // //   letter-spacing: 0.1px;
// // // // }

// // // // ::-webkit-scrollbar { width: 4px; }
// // // // ::-webkit-scrollbar-track { background: var(--bg); }
// // // // ::-webkit-scrollbar-thumb { background: rgba(212,175,55,0.3); border-radius: 2px; }

// // // // /* ══ LIGHT MODE — section background alternation for depth ══ */
// // // // .lp-light .lp-section-alt {
// // // //   background: linear-gradient(180deg, #FEF2D0 0%, #FFFBF2 100%);
// // // // }
// // // // .lp-light .lp-trust {
// // // //   background: linear-gradient(135deg, #FFF3C4 0%, #FFEEA0 50%, #FFF8D6 100%);
// // // //   border-top: 0.5px solid rgba(180,130,0,0.18);
// // // //   border-bottom: 0.5px solid rgba(180,130,0,0.18);
// // // // }
// // // // .lp-light .lp-trust-label { color: rgba(60,40,0,0.5); }
// // // // .lp-light .lp-trust-item { color: #5A3E00; font-weight: 600; }
// // // // .lp-light .lp-trust-item-icon {
// // // //   background: rgba(255,255,255,0.7);
// // // //   border: 0.5px solid rgba(180,130,0,0.25);
// // // //   box-shadow: 0 2px 8px rgba(180,130,0,0.12);
// // // // }

// // // // /* ══ LIGHT — hero background with warm radial glow ══ */
// // // // .lp-light .lp-hero-bg {
// // // //   background: radial-gradient(ellipse 70% 60% at 20% 50%, rgba(255,220,80,0.18) 0%, transparent 60%),
// // // //               radial-gradient(ellipse 50% 50% at 80% 30%, rgba(255,180,40,0.10) 0%, transparent 60%),
// // // //               #FFFBF2;
// // // // }

// // // // /* ══ LIGHT — nav ══ */
// // // // .lp-light .lp-nav.scrolled {
// // // //   background: rgba(255,251,242,0.96);
// // // //   backdrop-filter: blur(20px);
// // // //   border-bottom: 0.5px solid rgba(180,130,0,0.18);
// // // //   box-shadow: 0 4px 24px rgba(180,130,0,0.08);
// // // // }

// // // // /* ══ LIGHT — cards get white surface + amber shadow ══ */
// // // // .lp-light .lp-how-card {
// // // //   background: #FFFFFF;
// // // //   border: 0.5px solid rgba(180,130,0,0.15);
// // // //   box-shadow: 0 4px 24px rgba(180,130,0,0.08), 0 1px 4px rgba(180,130,0,0.06);
// // // // }
// // // // .lp-light .lp-how-card:hover {
// // // //   border-color: rgba(154,107,0,0.35);
// // // //   box-shadow: 0 12px 40px rgba(154,107,0,0.14), 0 2px 8px rgba(154,107,0,0.08);
// // // // }
// // // // .lp-light .lp-how-num { color: rgba(154,107,0,0.12); }

// // // // .lp-light .lp-feat-bcard {
// // // //   background: #FFFFFF;
// // // //   border: 0.5px solid rgba(180,130,0,0.14);
// // // //   box-shadow: 0 2px 16px rgba(180,130,0,0.07);
// // // // }
// // // // .lp-light .lp-feat-bcard.hero {
// // // //   background: linear-gradient(135deg, #FFFBF0 0%, #FFF5D6 100%);
// // // //   border-color: rgba(154,107,0,0.28);
// // // //   box-shadow: 0 6px 32px rgba(154,107,0,0.12);
// // // // }
// // // // .lp-light .lp-feat-bcard:hover {
// // // //   border-color: rgba(154,107,0,0.32);
// // // //   box-shadow: 0 8px 32px rgba(154,107,0,0.14);
// // // //   background: #FFFBF0;
// // // // }

// // // // /* ══ LIGHT — use panel ══ */
// // // // .lp-light .lp-use-panel {
// // // //   background: #FFFFFF;
// // // //   border: 0.5px solid rgba(154,107,0,0.22);
// // // //   box-shadow: 0 8px 40px rgba(154,107,0,0.10);
// // // // }
// // // // .lp-light .lp-use-hl {
// // // //   background: linear-gradient(135deg, #FFFBF0, #FFF5D6);
// // // //   border: 0.5px solid rgba(154,107,0,0.14);
// // // // }

// // // // /* ══ LIGHT — pricing card ══ */
// // // // .lp-light .lp-pricing-cta {
// // // //   background: linear-gradient(160deg, #FFF8D6 0%, #FFFBF2 40%, #FEF0C0 100%);
// // // // }
// // // // .lp-light .lp-pricing-cta::before {
// // // //   background: radial-gradient(ellipse 60% 70% at 50% 50%, rgba(255,200,50,0.15) 0%, transparent 70%);
// // // // }
// // // // .lp-light .lp-price-card {
// // // //   background: #FFFFFF;
// // // //   border: 1px solid rgba(154,107,0,0.25);
// // // //   box-shadow: 0 12px 48px rgba(154,107,0,0.14), 0 2px 8px rgba(154,107,0,0.08);
// // // // }
// // // // .lp-light .lp-price-card::before {
// // // //   background: linear-gradient(90deg, transparent, var(--gold), transparent);
// // // // }
// // // // .lp-light .lp-price-item { color: #3A2800; border-bottom-color: rgba(154,107,0,0.10); }
// // // // .lp-light .lp-price-check { color: #9A6B00; }

// // // // /* ══ LIGHT — footer ══ */
// // // // .lp-light .lp-footer {
// // // //   background: linear-gradient(180deg, #FFF3C4 0%, #FFE88A 100%);
// // // //   border-top: 0.5px solid rgba(180,130,0,0.20);
// // // // }
// // // // .lp-light .lp-footer-copy { color: rgba(60,40,0,0.45); opacity: 1; }

// // // // /* ══ LIGHT — ghost button ══ */
// // // // .lp-light .lp-btn-ghost {
// // // //   color: #5A3E00;
// // // //   border-color: rgba(154,107,0,0.30);
// // // //   background: rgba(255,255,255,0.6);
// // // // }
// // // // .lp-light .lp-btn-ghost:hover {
// // // //   border-color: #9A6B00;
// // // //   color: #1A1200;
// // // //   background: rgba(255,255,255,0.9);
// // // // }

// // // // /* ══ LIGHT — nav CTA keeps same gold gradient ══ */
// // // // .lp-light .lp-nav-cta { color: #1A1200; }

// // // // /* ══ LIGHT — eyebrow pill ══ */
// // // // .lp-light .lp-hero-eyebrow {
// // // //   background: linear-gradient(135deg, rgba(255,220,80,0.3), rgba(255,180,40,0.2));
// // // //   border: 0.5px solid rgba(154,107,0,0.30);
// // // //   color: #7A5200;
// // // // }
// // // // .lp-light .lp-hero-eyebrow-dot { background: #9A6B00; }

// // // // /* ══ LIGHT — gold rule more visible ══ */
// // // // .lp-light .gold-rule {
// // // //   background: linear-gradient(90deg, transparent, rgba(154,107,0,0.25), transparent);
// // // // }

// // // // /* ══ LIGHT — scan scene phones get light borders ══ */
// // // // .lp-light .phone-scanner {
// // // //   background: #F0EDE0;
// // // //   border: 1px solid rgba(180,130,0,0.20);
// // // //   box-shadow: 0 24px 60px rgba(0,0,0,0.15), 0 4px 16px rgba(180,130,0,0.10);
// // // // }
// // // // .lp-light .phone-scanner-screen { background: #E8E4D8; }
// // // // .lp-light .phone-profile {
// // // //   background: #FFFBF2;
// // // //   border: 1px solid rgba(154,107,0,0.30);
// // // //   box-shadow: 0 32px 80px rgba(0,0,0,0.15), 0 0 0 1px rgba(154,107,0,0.12);
// // // // }
// // // // .lp-light .profile-banner-img {
// // // //   background: linear-gradient(160deg, #FFE8A0 0%, #FFD060 50%, #FFECB0 100%);
// // // // }
// // // // .lp-light .profile-banner { background: linear-gradient(160deg, #FFE8A0, #FFC830); }
// // // // .lp-light .profile-banner-fade { background: linear-gradient(transparent, #FFFBF2); }
// // // // .lp-light .profile-avatar-ring { border-color: #FFFBF2; box-shadow: 0 0 0 1.5px rgba(154,107,0,0.5); background: #FFF3CC; }
// // // // .lp-light .profile-avatar-inner {
// // // //   background: linear-gradient(145deg, #FFE080, #FFC830);
// // // //   color: rgba(80,50,0,0.8);
// // // // }
// // // // .lp-light .profile-info-name { color: #1A1200; }
// // // // .lp-light .profile-info-role { color: rgba(60,40,0,0.5); }
// // // // .lp-light .profile-tile { background: #FFF8E0; border-color: rgba(180,130,0,0.12); }
// // // // .lp-light .tile-text { color: rgba(60,40,0,0.5); }
// // // // .lp-light .tile-dot { background: rgba(154,107,0,0.15); }
// // // // .lp-light .tile-dot-inner { background: #9A6B00; }
// // // // .lp-light .scanner-label { color: rgba(40,30,0,0.4); }
// // // // .lp-light .mini-qr-cell-on { background: rgba(154,107,0,0.6) !important; }
// // // // .lp-light .scan-success {
// // // //   box-shadow: 0 4px 16px rgba(154,107,0,0.35);
// // // // }

// // // // /* ══ LIGHT — social pills ══ */
// // // // .lp-light .lp-social-pill {
// // // //   background: rgba(255,220,80,0.20);
// // // //   border-color: rgba(154,107,0,0.22);
// // // //   color: #5A3E00;
// // // // }
// // // // .lp-light .lp-social-pill:hover {
// // // //   background: rgba(255,220,80,0.35);
// // // //   color: #1A1200;
// // // // }

// // // // /* ══ LIGHT — wallet chips ══ */
// // // // .lp-light .lp-wallet-chip {
// // // //   background: rgba(255,220,80,0.20);
// // // //   border-color: rgba(154,107,0,0.22);
// // // //   color: #7A5200;
// // // // }

// // // // /* ══ LIGHT — stat bar fill ══ */
// // // // .lp-light .lp-stat-bar { background: rgba(154,107,0,0.12); }
// // // // .lp-light .lp-stat-num { color: #9A6B00; }
// // // // .lp-light .lp-stat-label { color: rgba(60,40,0,0.5); }

// // // // /* ══ LIGHT — use tab active ══ */
// // // // .lp-light .lp-use-tab-active {
// // // //   background: linear-gradient(135deg, rgba(255,220,80,0.25), rgba(255,180,40,0.18)) !important;
// // // //   color: #7A5200 !important;
// // // //   border-color: rgba(154,107,0,0.32) !important;
// // // // }

// // // // /* ══ LIGHT — how icon ring ══ */
// // // // .lp-light .lp-how-icon-ring {
// // // //   background: rgba(255,220,80,0.25);
// // // //   border-color: rgba(154,107,0,0.28);
// // // // }

// // // // /* ══ LIGHT — hamburger ══ */
// // // // .lp-light .lp-hamburger {
// // // //   border-color: rgba(154,107,0,0.28);
// // // //   background: rgba(255,220,80,0.15);
// // // // }
// // // // .lp-light .lp-hamburger span { background: #9A6B00; }

// // // // /* ══ LIGHT — mobile menu panel ══ */
// // // // .lp-light .lp-mobile-menu-panel {
// // // //   background: #FFFBF2;
// // // //   border-left-color: rgba(154,107,0,0.20);
// // // // }
// // // // .lp-light .lp-mobile-menu-header { border-bottom-color: rgba(154,107,0,0.10); }
// // // // .lp-light .lp-mobile-close {
// // // //   border-color: rgba(154,107,0,0.14);
// // // //   background: #FFF8EA;
// // // //   color: rgba(60,40,0,0.6);
// // // // }
// // // // .lp-light .lp-mobile-link { color: rgba(60,40,0,0.6); }
// // // // .lp-light .lp-mobile-link:hover {
// // // //   background: #FFF5D0;
// // // //   color: #1A1200;
// // // //   border-color: rgba(154,107,0,0.25);
// // // // }
// // // // .lp-light .lp-mobile-menu-footer { border-top-color: rgba(154,107,0,0.10); }
// // // // .lp-light .lp-mobile-tagline { color: rgba(60,40,0,0.4); }
// // // // .lp-light .lp-mobile-divider {
// // // //   background: linear-gradient(90deg, transparent, rgba(154,107,0,0.2), transparent);
// // // // }

// // // // /* ══ LIGHT — section eyebrow text ══ */
// // // // .lp-light .lp-eyebrow { color: #9A6B00; }
// // // // .lp-light .lp-section-title { color: #1A1200; }
// // // // .lp-light .lp-section-sub { color: rgba(60,40,0,0.62); }

// // // // /* ══ LIGHT — pricing copy text ══ */
// // // // .lp-light .lp-pricing-perk { color: #3A2800; }
// // // // .lp-light .lp-perk-tick { background: rgba(255,220,80,0.25); border-color: rgba(154,107,0,0.25); color: #7A5200; }

// // // // /* ══ LIGHT — QR art cells ══ */
// // // // .lp-light .lp-qr-cell-on { background: #9A6B00 !important; }
// // // // .lp-light .lp-qr-cell-off { background: rgba(154,107,0,0.12) !important; }

// // // // /* ══ LIGHT — feature card desc ══ */
// // // // .lp-light .lp-feat-bdesc { color: rgba(60,40,0,0.62); }
// // // // .lp-light .lp-feat-btag { color: #9A6B00; }
// // // // .lp-light .lp-feat-bicon {
// // // //   background: rgba(255,220,80,0.22);
// // // //   border-color: rgba(154,107,0,0.24);
// // // // }

// // // // /* ══ LIGHT — how desc ══ */
// // // // .lp-light .lp-how-desc { color: rgba(60,40,0,0.62); }
// // // // .lp-light .lp-how-title { color: #1A1200; }
// // // // .lp-light .lp-how-step-label { color: #9A6B00; }

// // // // /* ══ LIGHT — nav links ══ */
// // // // .lp-light .lp-nav-link { color: rgba(60,40,0,0.6); }
// // // // .lp-light .lp-nav-link:hover { color: #1A1200; }
// // // // .lp-light .lp-logo { color: #1A1200; }
// // // // .lp-light .lp-footer-logo { color: #1A1200; }
// // // // .lp-light .lp-footer-tagline { color: rgba(60,40,0,0.55); }

// // // // /* ══ LIGHT — theme btn ══ */
// // // // .lp-light .lp-theme-btn {
// // // //   border-color: rgba(154,107,0,0.28);
// // // //   background: rgba(255,220,80,0.15);
// // // // }

// // // // /* ══ LIGHT — price amount ══ */
// // // // .lp-light .lp-price-num { color: #1A1200; }
// // // // .lp-light .lp-price-period { color: rgba(60,40,0,0.45); }
// // // // .lp-light .lp-price-divider { background: rgba(154,107,0,0.10); }
// // // // .lp-light .lp-price-badge { color: #1A1200; }

// // // // /* ══ LIGHT — use case ══ */
// // // // .lp-light .lp-use-tagline { color: #1A1200; }
// // // // .lp-light .lp-use-desc { color: rgba(60,40,0,0.62); }
// // // // .lp-light .lp-use-badge { background: rgba(255,220,80,0.25); border-color: rgba(154,107,0,0.25); color: #7A5200; }
// // // // .lp-light .lp-use-hl-text { color: #3A2800; }
// // // // .lp-light .lp-use-hl-check { background: rgba(255,220,80,0.25); color: #7A5200; }

// // // // /* ═══════════════════════════════════════
// // // //    REVEALS
// // // // ═══════════════════════════════════════ */
// // // // .reveal {
// // // //   opacity: 0;
// // // //   transition: opacity 0.75s cubic-bezier(0.22,1,0.36,1), transform 0.75s cubic-bezier(0.22,1,0.36,1);
// // // // }
// // // // .reveal.from-up    { transform: translateY(32px); }
// // // // .reveal.from-down  { transform: translateY(-24px); }
// // // // .reveal.from-left  { transform: translateX(-40px); }
// // // // .reveal.from-right { transform: translateX(40px); }
// // // // .reveal.from-scale { transform: scale(0.92); }
// // // // .reveal.d1 { transition-delay: 0.05s; }
// // // // .reveal.d2 { transition-delay: 0.13s; }
// // // // .reveal.d3 { transition-delay: 0.21s; }
// // // // .reveal.d4 { transition-delay: 0.29s; }
// // // // .reveal.d5 { transition-delay: 0.37s; }
// // // // .reveal.d6 { transition-delay: 0.45s; }
// // // // .reveal.visible { opacity: 1; transform: translateY(0) translateX(0) scale(1); }

// // // // /* ── NAV ── */
// // // // .lp-nav {
// // // //   position: fixed; top: 0; left: 0; right: 0; z-index: 200;
// // // //   padding: 1.25rem 3rem; transition: all 0.4s;
// // // // }
// // // // .lp-nav.scrolled {
// // // //   background: rgba(8,8,8,0.92);
// // // //   backdrop-filter: blur(20px);
// // // //   border-bottom: 0.5px solid rgba(212,175,55,0.12);
// // // //   padding: 0.85rem 3rem;
// // // // }
// // // // .lp-nav-inner {
// // // //   max-width: 1180px; margin: 0 auto;
// // // //   display: flex; align-items: center; justify-content: space-between;
// // // // }
// // // // .lp-logo {
// // // //   display: flex; align-items: center; gap: 10px;
// // // //   font-family: var(--sans); font-size: 15px; font-weight: 600;
// // // //   color: var(--text-primary); text-decoration: none; letter-spacing: 0.2px;
// // // //   flex-shrink: 0;
// // // // }
// // // // .lp-logo-icon { width: 34px; height: 34px; flex-shrink: 0; }
// // // // .lp-logo em { color: var(--gold); font-style: normal; font-weight: 700; }
// // // // .lp-nav-links { display: flex; align-items: center; gap: 2.5rem; }
// // // // .lp-nav-link { font-size: 13px; font-weight: 500; color: var(--text-muted); text-decoration: none; letter-spacing: 0.3px; transition: color 0.2s; }
// // // // .lp-nav-link:hover { color: var(--text-primary); }
// // // // .lp-nav-cta {
// // // //   padding: 10px 22px;
// // // //   background: linear-gradient(135deg, #D4AF37, #F0D060, #C9A227);
// // // //   color: #0A0A0A; border: none; border-radius: 10px;
// // // //   font-family: var(--sans); font-size: 13px; font-weight: 700;
// // // //   letter-spacing: 0.8px; text-transform: uppercase;
// // // //   cursor: pointer; position: relative; overflow: hidden;
// // // //   transition: transform 0.15s, opacity 0.15s; white-space: nowrap;
// // // // }
// // // // .lp-nav-cta::before { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg, rgba(255,255,255,0.2), transparent 60%); pointer-events: none; }
// // // // .lp-nav-cta:hover { transform: translateY(-1px); opacity: 0.92; }

// // // // /* ── HAMBURGER ── */
// // // // .lp-hamburger {
// // // //   display: none; flex-direction: column; justify-content: center; align-items: center; gap: 5px;
// // // //   width: 40px; height: 40px; border-radius: 10px;
// // // //   border: 0.5px solid var(--border-gold); background: var(--gold-subtle);
// // // //   cursor: pointer; flex-shrink: 0; transition: background 0.2s;
// // // // }
// // // // .lp-hamburger:hover { background: var(--gold-subtle2); }
// // // // .lp-hamburger span {
// // // //   display: block; width: 18px; height: 1.5px;
// // // //   background: var(--gold); border-radius: 2px;
// // // //   transition: all 0.3s cubic-bezier(0.22,1,0.36,1); transform-origin: center;
// // // // }
// // // // .lp-hamburger.open span:nth-child(1) { transform: translateY(6.5px) rotate(45deg); }
// // // // .lp-hamburger.open span:nth-child(2) { opacity: 0; transform: scaleX(0); }
// // // // .lp-hamburger.open span:nth-child(3) { transform: translateY(-6.5px) rotate(-45deg); }

// // // // /* ── MOBILE DRAWER ── */
// // // // .lp-mobile-menu { position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 190; display: flex; flex-direction: column; pointer-events: none; }
// // // // .lp-mobile-menu-backdrop { position: absolute; inset: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(4px); opacity: 0; transition: opacity 0.35s ease; }
// // // // .lp-mobile-menu.open .lp-mobile-menu-backdrop { opacity: 1; pointer-events: all; }
// // // // .lp-mobile-menu-panel {
// // // //   position: absolute; top: 0; right: 0;
// // // //   width: min(320px, 85vw); height: 100%;
// // // //   background: var(--bg); border-left: 0.5px solid var(--border-gold);
// // // //   display: flex; flex-direction: column;
// // // //   transform: translateX(100%); transition: transform 0.4s cubic-bezier(0.22,1,0.36,1);
// // // //   box-shadow: -20px 0 60px rgba(0,0,0,0.4); pointer-events: none; overflow: hidden;
// // // // }
// // // // .lp-mobile-menu.open .lp-mobile-menu-panel { transform: translateX(0); pointer-events: all; }
// // // // .lp-mobile-menu-header { display: flex; align-items: center; justify-content: space-between; padding: 1.25rem 1.5rem; border-bottom: 0.5px solid var(--border-color); }
// // // // .lp-mobile-close {
// // // //   width: 36px; height: 36px; border-radius: 9px;
// // // //   border: 0.5px solid var(--border-color); background: var(--surface);
// // // //   display: flex; align-items: center; justify-content: center;
// // // //   cursor: pointer; color: var(--text-muted); font-size: 16px;
// // // //   transition: background 0.2s, color 0.2s;
// // // // }
// // // // .lp-mobile-close:hover { background: var(--surface2); color: var(--text-primary); }
// // // // .lp-mobile-links { flex: 1; display: flex; flex-direction: column; padding: 1.5rem; gap: 0.25rem; }
// // // // .lp-mobile-link {
// // // //   display: flex; align-items: center; gap: 12px; padding: 1rem 1.25rem;
// // // //   border-radius: 14px; font-family: var(--sans); font-size: 15px; font-weight: 600;
// // // //   color: var(--text-muted); text-decoration: none; border: 0.5px solid transparent;
// // // //   opacity: 0; transform: translateX(20px);
// // // //   transition: background 0.2s, color 0.2s, border-color 0.2s, opacity 0.35s ease, transform 0.35s cubic-bezier(0.22,1,0.36,1);
// // // // }
// // // // .lp-mobile-link:nth-child(1) { transition-delay: 0.06s; }
// // // // .lp-mobile-link:nth-child(2) { transition-delay: 0.11s; }
// // // // .lp-mobile-link:nth-child(3) { transition-delay: 0.16s; }
// // // // .lp-mobile-menu.open .lp-mobile-link { opacity: 1; transform: translateX(0); }
// // // // .lp-mobile-link:hover { background: var(--surface2); color: var(--text-primary); border-color: var(--border-gold); }
// // // // .lp-mobile-link-icon { font-size: 16px; width: 20px; text-align: center; }
// // // // .lp-mobile-divider { height: 0.5px; background: linear-gradient(90deg, transparent, var(--border-gold), transparent); margin: 0.5rem 1.5rem; }
// // // // .lp-mobile-menu-footer {
// // // //   padding: 1.5rem; border-top: 0.5px solid var(--border-color);
// // // //   display: flex; flex-direction: column; gap: 0.75rem;
// // // //   opacity: 0; transform: translateY(12px);
// // // //   transition: opacity 0.35s ease 0.22s, transform 0.35s ease 0.22s;
// // // // }
// // // // .lp-mobile-menu.open .lp-mobile-menu-footer { opacity: 1; transform: translateY(0); }
// // // // .lp-mobile-cta {
// // // //   width: 100%; padding: 14px;
// // // //   background: linear-gradient(135deg, #D4AF37, #F0D060, #C9A227);
// // // //   color: #0A0A0A; border: none; border-radius: 12px;
// // // //   font-family: var(--sans); font-size: 14px; font-weight: 700;
// // // //   letter-spacing: 0.8px; text-transform: uppercase; cursor: pointer;
// // // //   position: relative; overflow: hidden;
// // // // }
// // // // .lp-mobile-cta::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.2),transparent 60%); pointer-events:none; }
// // // // .lp-mobile-tagline { text-align: center; font-size: 11px; font-weight: 500; color: var(--text-muted2); letter-spacing: 0.5px; }

// // // // /* trust bar */
// // // // @media (min-width: 901px) {
// // // //   .lp-trust { flex-direction: row; padding: 1.5rem 3rem; gap: 2.5rem; }
// // // //   .lp-trust-items { width: auto; max-width: none; gap: 0; }
// // // //   .lp-trust-item { flex-direction: row; flex: none; padding: 0 1.5rem; border-right: 0.5px solid var(--border-color); border-bottom: none; gap: 8px; }
// // // //   .lp-trust-item:last-child { border-right: none; }
// // // //   .lp-trust-item-icon { width:28px; height:28px; border-radius:8px; font-size:1rem; flex-shrink:0; margin-bottom:0; }
// // // // }

// // // // .lp-mobile-cluster { display: none; align-items: center; gap: 10px; }
// // // // .lp-theme-btn-mobile { display: flex; }
// // // // @media (min-width: 901px) { .lp-mobile-cluster { display: none !important; } }

// // // // /* ── HERO ── */
// // // // .lp-hero {
// // // //   min-height: 100vh; display: grid; grid-template-columns: 1fr 1fr;
// // // //   align-items: center; gap: 4rem; max-width: 1180px; margin: 0 auto;
// // // //   padding: 10rem 3rem 3rem; position: relative;
// // // // }
// // // // .lp-hero-left { position: relative; z-index: 2; }
// // // // .lp-hero-eyebrow {
// // // //   display: inline-flex; align-items: center; gap: 8px; padding: 6px 14px;
// // // //   background: var(--gold-subtle2); border: 0.5px solid var(--border-gold);
// // // //   border-radius: 100px; font-size: 11px; font-weight: 700; color: var(--gold);
// // // //   letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 2rem;
// // // // }
// // // // .lp-hero-eyebrow-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--gold); animation: pulse-gold 2s ease-in-out infinite; }
// // // // @keyframes pulse-gold { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:0.4;transform:scale(0.7);} }
// // // // .lp-hero-title { font-family: var(--serif); font-size: clamp(3.2rem, 5.8vw, 5.3rem); font-weight: 600; line-height: 1.02; letter-spacing: -0.8px; color: var(--text-primary); margin-bottom: 1.75rem; }
// // // // .lp-hero-title em { font-style: italic; font-weight: 600; color: var(--gold); }
// // // // .lp-hero-sub { font-size: 15px; font-weight: 400; color: var(--text-muted); line-height: 1.9; max-width: 470px; margin-bottom: 1rem; }
// // // // .lp-hero-ctas { display: flex; gap: 1rem; flex-wrap: wrap; }

// // // // .lp-btn-gold {
// // // //   padding: 16px 32px;
// // // //   background: linear-gradient(135deg, #D4AF37, #F0D060, #C9A227);
// // // //   color: #0A0A0A; border: none; border-radius: 12px;
// // // //   font-family: var(--sans); font-size: 13px; font-weight: 700;
// // // //   letter-spacing: 1px; text-transform: uppercase;
// // // //   cursor: pointer; position: relative; overflow: hidden;
// // // //   transition: transform 0.2s, box-shadow 0.2s;
// // // //   display: flex; align-items: center; gap: 8px;
// // // // }
// // // // .lp-btn-gold::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.25),transparent 60%); pointer-events:none; }
// // // // .lp-btn-gold:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(212,175,55,0.3); }

// // // // .lp-btn-ghost {
// // // //   padding: 16px 28px; background: transparent;
// // // //   color: var(--text-muted); border: 0.5px solid var(--border-color);
// // // //   border-radius: 12px; font-family: var(--sans); font-size: 13px; font-weight: 600;
// // // //   letter-spacing: 0.5px; cursor: pointer; text-decoration: none;
// // // //   display: inline-flex; align-items: center; gap: 8px;
// // // //   transition: border-color 0.2s, color 0.2s;
// // // // }
// // // // .lp-btn-ghost:hover { border-color: var(--border-gold); color: var(--text-primary); }

// // // // /* COUNT-UP STATS */
// // // // .lp-hero-stats { display: flex; gap: 2.5rem; padding-bottom: 1.5rem; margin-bottom: 1.5rem; }
// // // // .lp-stat { position: relative; }
// // // // .lp-stat-num { font-family: var(--serif); font-size: 2.4rem; font-weight: 600; color: var(--gold); line-height: 1; }
// // // // .lp-stat-num.counting { animation: stat-shimmer 0.1s ease infinite alternate; }
// // // // @keyframes stat-shimmer { from { opacity: 0.7; } to { opacity: 1; } }
// // // // .lp-stat-label { font-size: 11px; font-weight: 500; color: var(--text-muted2); letter-spacing: 0.5px; margin-top: 5px; }
// // // // .lp-stat-bar { height: 1.5px; background: var(--border-gold); margin-top: 8px; border-radius: 1px; overflow: hidden; }
// // // // .lp-stat-bar-fill { height: 100%; background: linear-gradient(90deg, var(--gold), var(--gold2)); border-radius: 1px; width: 0%; transition: width 0.05s linear; }

// // // // /* HERO RIGHT */
// // // // .lp-hero-right { position: relative; display: flex; align-items: center; justify-content: center; z-index: 2; }
// // // // .scan-scene { position: relative; width: 380px; height: 480px; }

// // // // .scan-ripple { position: absolute; right: -10px; top: 50px; width: 60px; height: 60px; border-radius: 50%; border: 1.5px solid rgba(212,175,55,0); z-index: 10; pointer-events: none; }
// // // // .scan-ripple.r1 { animation: ripple-out 2.5s ease-out infinite; animation-delay: 3.2s; }
// // // // .scan-ripple.r2 { animation: ripple-out 2.5s ease-out infinite; animation-delay: 3.6s; }
// // // // .scan-ripple.r3 { animation: ripple-out 2.5s ease-out infinite; animation-delay: 4.0s; }
// // // // @keyframes ripple-out { 0% { transform: scale(1); border-color: rgba(212,175,55,0.6); opacity: 1; } 100% { transform: scale(3.5); border-color: rgba(212,175,55,0); opacity: 0; } }
// // // // .scan-success {
// // // //   position: absolute; right: 8px; top: 38px;
// // // //   background: linear-gradient(135deg, #D4AF37, #F0D060); color: #0A0A0A;
// // // //   border-radius: 100px; padding: 4px 10px 4px 8px;
// // // //   font-family: var(--sans); font-size: 9px; font-weight: 700; letter-spacing: 0.5px;
// // // //   display: flex; align-items: center; gap: 5px; z-index: 12; white-space: nowrap;
// // // //   opacity: 0; animation: success-pop 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards; animation-delay: 3.1s;
// // // //   box-shadow: 0 4px 16px rgba(212,175,55,0.4);
// // // // }
// // // // .scan-success-dot { width: 6px; height: 6px; border-radius: 50%; background: #0A0A0A; }
// // // // @keyframes success-pop { 0% { opacity: 0; transform: scale(0.5) translateY(6px); } 100% { opacity: 1; transform: scale(1) translateY(0); } }

// // // // .phone-scanner {
// // // //   position: absolute; left: 0; top: 60px; width: 155px; height: 270px; background: #1A1A1A;
// // // //   border-radius: 22px; border: 1px solid rgba(255,255,255,0.1); overflow: hidden;
// // // //   box-shadow: 0 24px 60px rgba(0,0,0,0.6); z-index: 3;
// // // //   animation: phone-a-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both; animation-delay: 0.3s;
// // // // }
// // // // @keyframes phone-a-intro { from{opacity:0;transform:translateX(-30px) rotate(-6deg);} to{opacity:1;transform:translateX(0) rotate(-6deg);} }
// // // // .phone-scanner-screen { width:100%; height:100%; background:#111; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; padding:12px; }
// // // // .scanner-viewfinder { width:100px; height:100px; border-radius:10px; position:relative; background:rgba(212,175,55,0.04); }
// // // // .scanner-viewfinder::before,.scanner-viewfinder::after { content:''; position:absolute; width:20px; height:20px; border-color:#D4AF37; border-style:solid; }
// // // // .scanner-viewfinder::before { top:0;left:0;border-width:2px 0 0 2px;border-radius:6px 0 0 0; }
// // // // .scanner-viewfinder::after { bottom:0;right:0;border-width:0 2px 2px 0;border-radius:0 0 6px 0; }
// // // // .vf-corner-tr { position:absolute;top:0;right:0;width:20px;height:20px;border-top:2px solid #D4AF37;border-right:2px solid #D4AF37;border-radius:0 6px 0 0; }
// // // // .vf-corner-bl { position:absolute;bottom:0;left:0;width:20px;height:20px;border-bottom:2px solid #D4AF37;border-left:2px solid #D4AF37;border-radius:0 0 0 6px; }
// // // // .scan-laser { position:absolute;left:4px;right:4px;height:2px;background:linear-gradient(90deg,transparent,#D4AF37,transparent);border-radius:1px;top:4px;animation:laser-sweep 2s ease-in-out infinite;animation-delay:1.2s; }
// // // // @keyframes laser-sweep { 0%{top:8px;opacity:0;} 10%{opacity:1;} 90%{opacity:1;} 100%{top:88px;opacity:0;} }
// // // // .scanner-success-flash { position: absolute; inset: 0; background: rgba(212,175,55,0.12); opacity: 0; animation: flash-once 0.4s ease forwards; animation-delay: 3.0s; pointer-events: none; z-index: 10; }
// // // // @keyframes flash-once { 0%{opacity:0;} 40%{opacity:1;} 100%{opacity:0;} }
// // // // .scanner-label { font-size:9px;font-weight:600;color:rgba(255,255,255,0.4);letter-spacing:1px;text-align:center;text-transform:uppercase; }
// // // // .mini-qr { position:absolute;inset:10px;display:grid;grid-template-columns:repeat(6,1fr);grid-template-rows:repeat(6,1fr);gap:1.5px;opacity:0.6; }
// // // // .mini-qr-cell { border-radius:1px; }

// // // // .phone-profile {
// // // //   position:absolute;right:0;top:0;width:175px;height:310px;background:#0E0E0E;
// // // //   border-radius:24px;border:1px solid rgba(212,175,55,0.2);overflow:hidden;
// // // //   box-shadow:0 32px 80px rgba(0,0,0,0.7),0 0 0 1px rgba(212,175,55,0.08);
// // // //   z-index:4; animation:phone-b-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:0.5s;
// // // // }
// // // // @keyframes phone-b-intro { from{opacity:0;transform:translateY(30px) rotate(4deg);} to{opacity:1;transform:translateY(0) rotate(4deg);} }
// // // // .profile-card-wrap { width:100%;height:100%;animation:card-reveal 0.7s cubic-bezier(0.22,1,0.36,1) both;animation-delay:2s; }
// // // // @keyframes card-reveal { from{transform:translateY(40px);opacity:0;} to{transform:translateY(0);opacity:1;} }
// // // // .profile-banner { width:100%;height:110px;background:linear-gradient(160deg,#1a1a1a,#2a2020);position:relative;overflow:hidden; }
// // // // .profile-banner-img { width:100%;height:100%;background:linear-gradient(160deg,#1e1a10 0%,#2a2010 50%,#181818 100%); }
// // // // .profile-banner-fade { position:absolute;bottom:0;left:0;right:0;height:50px;background:linear-gradient(transparent,#0E0E0E); }
// // // // .profile-avatar-ring { position:absolute;bottom:-20px;left:14px;width:44px;height:44px;border-radius:50%;border:2px solid #0E0E0E;background:#222;overflow:hidden;box-shadow:0 0 0 1.5px rgba(212,175,55,0.5);animation:avatar-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:2.4s; }
// // // // @keyframes avatar-pop { from{transform:scale(0.3);opacity:0;} to{transform:scale(1);opacity:1;} }
// // // // .profile-avatar-inner { width:100%;height:100%;background:linear-gradient(145deg,#2a2010,#3a3020);display:flex;align-items:center;justify-content:center;font-family:'Cormorant Garamond',serif;font-size:14px;font-weight:600;color:rgba(212,175,55,0.8);letter-spacing:1px; }
// // // // .profile-info { padding:28px 14px 12px; }
// // // // .profile-info-name { font-family:'Cormorant Garamond',serif;font-size:16px;font-weight:600;color:#FFF;line-height:1.2;margin-bottom:2px; }
// // // // .profile-info-name em { font-style:italic;color:#D4AF37; }
// // // // .profile-info-role { font-size:8px;font-weight:600;color:rgba(255,255,255,0.45);letter-spacing:0.3px;margin-bottom:10px; }
// // // // .profile-tiles { display:grid;grid-template-columns:1fr 1fr 1fr;gap:5px;margin-bottom:8px; }
// // // // .profile-tile { background:#1A1A1A;border:0.5px solid rgba(255,255,255,0.06);border-radius:8px;padding:7px 6px;display:flex;flex-direction:column;gap:4px;animation:tile-slide 0.3s ease both; }
// // // // .profile-tile:nth-child(1){animation-delay:2.5s;} .profile-tile:nth-child(2){animation-delay:2.65s;} .profile-tile:nth-child(3){animation-delay:2.8s;}
// // // // @keyframes tile-slide { from{opacity:0;transform:translateY(6px);} to{opacity:1;transform:translateY(0);} }
// // // // .tile-dot { width:12px;height:12px;border-radius:50%;background:rgba(212,175,55,0.15);display:flex;align-items:center;justify-content:center; }
// // // // .tile-dot-inner { width:5px;height:5px;border-radius:50%;background:#D4AF37; }
// // // // .tile-text { font-size:7px;color:rgba(255,255,255,0.4);line-height:1.3;font-weight:500; }
// // // // .profile-save-btn { width:100%;padding:8px;background:linear-gradient(135deg,#D4AF37,#F0D060);color:#0A0A0A;border:none;border-radius:8px;font-size:8px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;cursor:default;display:flex;align-items:center;justify-content:center;gap:4px;animation:tile-slide 0.3s ease both;animation-delay:2.95s; }

// // // // .beam-line { position:absolute;top:165px;left:148px;width:90px;height:1px;background:linear-gradient(90deg,#D4AF37,rgba(212,175,55,0));transform-origin:left center;animation:beam-appear 0.4s ease both;animation-delay:1.5s;z-index:5; }
// // // // @keyframes beam-appear { from{opacity:0;} to{opacity:1;} }
// // // // .beam-particle { position:absolute;top:160px;left:148px;width:6px;height:6px;border-radius:50%;background:#D4AF37;animation:particle-travel 2s ease-in-out infinite;animation-delay:1.6s;z-index:6; }
// // // // @keyframes particle-travel { 0%{left:148px;opacity:0;transform:scale(0.5);} 10%{opacity:1;transform:scale(1);} 90%{opacity:1;} 100%{left:228px;opacity:0;transform:scale(0.5);} }
// // // // .hero-orb-1 { position:absolute;width:500px;height:500px;border-radius:50%;background:radial-gradient(circle,rgba(212,175,55,0.06) 0%,transparent 65%);top:-100px;right:-100px;pointer-events:none;z-index:0; }

// // // // /* ── SECTION SHARED ── */
// // // // .lp-section { padding: 5rem 3rem; position: relative; }
// // // // .lp-section-inner { max-width: 1180px; margin: 0 auto; }
// // // // .lp-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--gold);margin-bottom:1.25rem;text-align:center; }
// // // // .lp-section-title { font-family:var(--serif);font-size:clamp(2.3rem,4vw,3.5rem);font-weight:600;line-height:1.08;letter-spacing:-0.4px;color:var(--text-primary);text-align:center;margin-bottom:1.25rem; }
// // // // .lp-section-title em { font-style:italic;color:var(--gold); }
// // // // .lp-section-sub { font-size:15px;font-weight:400;color:var(--text-muted);text-align:center;max-width:520px;margin:0 auto 4rem;line-height:1.9; }

// // // // .gold-rule { height:0.5px;background:linear-gradient(90deg,transparent,rgba(212,175,55,0.3),transparent); }

// // // // /* ── TRUST BAR ── */
// // // // .lp-trust {
// // // //   padding: 1.5rem 2rem; display: flex; flex-direction: column; align-items: center;
// // // //   gap: 1rem; background: var(--surface);
// // // // }
// // // // .lp-trust-label { font-size: 10px; font-weight: 600; color: var(--text-muted2); letter-spacing: 2.5px; text-transform: uppercase; }
// // // // .lp-trust-items { display: flex; align-items: stretch; justify-content: center; gap: 0; flex-wrap: nowrap; width: 100%; max-width: 560px; }
// // // // .lp-trust-item { display: flex; flex-direction: column; align-items: center; gap: 5px; flex: 1; padding: 0.6rem 0.5rem; border-right: 0.5px solid var(--border-color); font-size: 11px; font-weight: 600; color: var(--text-muted); letter-spacing: 0.2px; text-align: center; }
// // // // .lp-trust-item:last-child { border-right: none; }
// // // // .lp-trust-item-icon { font-size: 1.25rem; line-height: 1; width: 36px; height: 36px; border-radius: 10px; background: var(--gold-subtle); border: 0.5px solid var(--border-gold); display: flex; align-items: center; justify-content: center; margin-bottom: 2px; }

// // // // /* ── HOW IT WORKS ── */
// // // // .lp-how-timeline { display: grid; grid-template-columns: 1fr 72px 1fr 72px 1fr; align-items: start; gap: 0; margin-top: 1rem; }
// // // // .lp-how-bridge { display: flex; align-items: flex-start; justify-content: center; padding-top: 2.6rem; }
// // // // .how-arrow-path { stroke: var(--border-gold); stroke-dasharray: 60; stroke-dashoffset: 60; transition: stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1), stroke 0.4s ease; }
// // // // .how-arrow-path.filled { stroke-dashoffset: 0; stroke: var(--gold); }

// // // // .lp-how-card {
// // // //   background: var(--surface); border: 0.5px solid var(--border-color);
// // // //   border-radius: 20px; padding: 2.2rem 1.8rem 2rem; position: relative; overflow: hidden;
// // // //   transition: border-color 0.3s, transform 0.3s; cursor: default;
// // // // }
// // // // .lp-how-card::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 80% 60% at 50% -10%,var(--gold-subtle2) 0%,transparent 70%);opacity:0;transition:opacity 0.3s; }
// // // // .lp-how-card:hover { border-color: var(--border-gold); transform: translateY(-4px); }
// // // // .lp-how-card:hover::before { opacity: 1; }
// // // // .lp-how-num { font-family:var(--serif);font-size:7rem;font-weight:700;color:var(--gold-subtle2);line-height:1;position:absolute;bottom:-0.5rem;right:0.8rem;letter-spacing:-3px;pointer-events:none;user-select:none;opacity:0.6; }
// // // // .lp-how-icon-ring { width:56px;height:56px;border-radius:16px;background:var(--gold-subtle);border:1px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:1.6rem;margin-bottom:1.5rem;position:relative; }
// // // // .lp-how-icon-ring::after { content:'';position:absolute;inset:-4px;border-radius:20px;border:1px dashed var(--border-gold);animation:lp-spin-slow 12s linear infinite; }
// // // // @keyframes lp-spin-slow { to{transform:rotate(360deg);} }
// // // // .lp-how-step-label { font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:var(--gold);margin-bottom:0.6rem; }
// // // // .lp-how-title { font-family:var(--serif);font-size:1.65rem;font-weight:600;color:var(--text-primary);line-height:1.15;margin-bottom:0.75rem; }
// // // // .lp-how-desc { font-size:13.5px;color:var(--text-muted);line-height:1.8; }

// // // // /* ── FEATURES BENTO ── */
// // // // .lp-feat-bento { display:grid;grid-template-columns:repeat(4,1fr);grid-template-rows:auto auto;gap:10px;margin-top:1rem; }
// // // // .lp-feat-bcard {
// // // //   background: var(--surface); border: 0.5px solid var(--border-color);
// // // //   border-radius: 18px; padding: 1.8rem 1.6rem; position: relative; overflow: hidden;
// // // //   transition: border-color 0.3s, transform 0.25s, background 0.3s; cursor: default;
// // // // }
// // // // .lp-feat-bcard::before { content: ''; position: absolute; top: 0; left: -100%; width: 60%; height: 100%; background: linear-gradient(105deg, transparent, rgba(212,175,55,0.06), transparent); transition: left 0s; pointer-events: none; z-index: 1; }
// // // // .lp-feat-bcard:hover::before { left: 160%; transition: left 0.6s ease; }
// // // // .lp-feat-bcard:hover { border-color:var(--border-gold); background:var(--surface2); transform:translateY(-3px); }
// // // // .lp-feat-bcard.hero { grid-column:span 2; background:var(--surface2); border-color:var(--border-gold); padding:2.4rem 2.2rem; }
// // // // .lp-feat-bcard.tall { grid-row:span 2; display:flex; flex-direction:column; }
// // // // .lp-feat-bcard::after { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:transparent;transition:background 0.3s; }
// // // // .lp-feat-bcard:hover::after,.lp-feat-bcard.hero::after { background:linear-gradient(90deg,transparent,var(--border-gold),transparent); }
// // // // .lp-feat-bicon { width:44px;height:44px;border-radius:12px;background:var(--gold-subtle);border:0.5px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:1.3rem;margin-bottom:1.25rem;flex-shrink:0; }
// // // // .lp-feat-bcard.hero .lp-feat-bicon { width:52px;height:52px;font-size:1.6rem;border-radius:14px; }
// // // // .lp-feat-btag { display:inline-block;font-size:8.5px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--gold);margin-bottom:0.5rem;opacity:0.8; }
// // // // .lp-feat-btitle { font-family:var(--serif);font-size:1.3rem;font-weight:600;color:var(--text-primary);line-height:1.15;margin-bottom:0.5rem; }
// // // // .lp-feat-bcard.hero .lp-feat-btitle { font-size:1.65rem; }
// // // // .lp-feat-bdesc { font-size:13px;color:var(--text-muted);line-height:1.8;flex:1; }
// // // // .lp-feat-wallet { display:flex;gap:6px;margin-top:1.25rem; }
// // // // .lp-wallet-chip { height:26px;border-radius:6px;flex:1;background:var(--gold-subtle);border:0.5px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:600;color:var(--gold);letter-spacing:0.5px;position:relative;overflow:hidden; }
// // // // .lp-wallet-chip::after { content:'';position:absolute;top:0;left:-100%;width:50%;height:100%;background:linear-gradient(90deg,transparent,rgba(212,175,55,0.2),transparent);animation:chip-shine 3s ease-in-out infinite; }
// // // // .lp-wallet-chip:nth-child(2)::after { animation-delay:0.4s; }
// // // // .lp-wallet-chip:nth-child(3)::after { animation-delay:0.8s; }
// // // // @keyframes chip-shine { 0%,70%{left:-100%;} 100%{left:200%;} }

// // // // .lp-feat-socials { display:flex;flex-wrap:wrap;gap:6px;margin-top:1.1rem; }
// // // // .lp-social-pill {
// // // //   padding: 4px 10px; background: var(--gold-subtle); border: 0.5px solid var(--border-gold);
// // // //   border-radius: 100px; font-size: 10px; font-weight: 500; color: var(--text-muted);
// // // //   opacity: 0; transform: translateY(6px) scale(0.94);
// // // //   transition: opacity 0.35s ease, transform 0.35s ease, background 0.2s, color 0.2s;
// // // // }
// // // // .lp-social-pill:hover { background: var(--gold-subtle2); color: var(--gold); }
// // // // .lp-social-pill:nth-child(1) { transition-delay: 0.05s; }
// // // // .lp-social-pill:nth-child(2) { transition-delay: 0.12s; }
// // // // .lp-social-pill:nth-child(3) { transition-delay: 0.19s; }
// // // // .lp-social-pill:nth-child(4) { transition-delay: 0.26s; }
// // // // .lp-social-pill:nth-child(5) { transition-delay: 0.33s; }
// // // // .lp-feat-bcard.pills-visible .lp-social-pill { opacity: 1; transform: translateY(0) scale(1); }

// // // // .lp-qr-art { margin-top:auto;padding-top:1.5rem;display:grid;grid-template-columns:repeat(5,1fr);grid-template-rows:repeat(5,1fr);gap:3px;opacity:0.2;transition:opacity 0.3s; }
// // // // .lp-feat-bcard.tall:hover .lp-qr-art { opacity:0.45; }
// // // // .lp-qr-cell { border-radius:2px; }

// // // // /* ── USE CASES ── */
// // // // .lp-use-tabs { display:flex;flex-wrap:wrap;gap:0.5rem;justify-content:center;margin-bottom:3rem; }
// // // // .lp-use-tab { padding:8px 18px;border-radius:100px;font-size:13px;font-weight:600;cursor:pointer;transition:all 0.2s;font-family:var(--sans);display:flex;align-items:center;gap:6px; }
// // // // .lp-use-panel { display:grid;grid-template-columns:1fr 1fr;gap:3rem;align-items:center;background:var(--surface);border:0.5px solid var(--border-color);border-radius:24px;padding:3rem;position:relative;overflow:hidden; }
// // // // .lp-use-panel::before { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,var(--border-gold),transparent); }
// // // // .lp-use-badge { display:inline-flex;align-items:center;gap:8px;padding:6px 14px;border-radius:100px;margin-bottom:1.5rem;font-size:12px;font-weight:700;background:var(--gold-subtle);color:var(--gold);border:0.5px solid var(--border-gold); }
// // // // .lp-use-tagline { font-family:var(--serif);font-size:2.2rem;font-weight:600;color:var(--text-primary);line-height:1.1;margin-bottom:1rem; }
// // // // .lp-use-desc { font-size:14px;font-weight:400;color:var(--text-muted);line-height:1.9;margin-bottom:2rem; }
// // // // .lp-use-content { animation: tab-fade 0.35s cubic-bezier(0.22,1,0.36,1) both; }
// // // // @keyframes tab-fade { from{opacity:0;transform:translateY(10px);} to{opacity:1;transform:translateY(0);} }
// // // // .lp-use-highlights { display:flex;flex-direction:column;gap:0.75rem; }
// // // // .lp-use-hl { display:flex;align-items:center;gap:12px;background:var(--surface2);border:0.5px solid var(--border-color);border-radius:12px;padding:1rem 1.25rem;animation:hl-slide 0.3s ease both; }
// // // // .lp-use-hl:nth-child(1){animation-delay:0.05s;} .lp-use-hl:nth-child(2){animation-delay:0.12s;} .lp-use-hl:nth-child(3){animation-delay:0.19s;} .lp-use-hl:nth-child(4){animation-delay:0.26s;}
// // // // @keyframes hl-slide { from{opacity:0;transform:translateX(12px);} to{opacity:1;transform:translateX(0);} }
// // // // .lp-use-hl-check { width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;background:var(--gold-subtle);color:var(--gold); }
// // // // .lp-use-hl-text { font-size:13px;font-weight:500;color:var(--text-muted); }

// // // // /* ── PRICING ── */
// // // // .lp-pricing-cta { padding:5rem 3rem;position:relative;overflow:hidden; }
// // // // .lp-pricing-cta::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 70% 80% at 50% 50%,var(--gold-subtle) 0%,transparent 70%);pointer-events:none; }
// // // // .lp-pricing-cta-inner { max-width:1180px;margin:0 auto;display:grid;grid-template-columns:1fr 1.1fr;gap:4rem;align-items:center;position:relative; }
// // // // .lp-pricing-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--gold);margin-bottom:1.25rem; }
// // // // .lp-pricing-headline { font-family:var(--serif);font-size:clamp(2.8rem,5vw,4.2rem);font-weight:600;line-height:1.05;color:var(--text-primary);margin-bottom:1.25rem;letter-spacing:-0.5px; }
// // // // .lp-pricing-headline em { font-style:italic;color:var(--gold); }
// // // // .lp-pricing-sub { font-size:15px;color:var(--text-muted);line-height:1.9;margin-bottom:2.5rem;max-width:400px; }
// // // // .lp-pricing-perks { display:flex;flex-direction:column;gap:0.6rem;margin-bottom:2.5rem; }
// // // // .lp-pricing-perk { display:flex;align-items:center;gap:10px;font-size:14px;font-weight:500;color:var(--text-muted); }
// // // // .lp-perk-tick { width:20px;height:20px;border-radius:50%;flex-shrink:0;background:var(--gold-subtle);border:0.5px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:9px;color:var(--gold);font-weight:700; }
// // // // .lp-price-card { background:var(--surface);border:0.5px solid var(--border-gold);border-radius:24px;padding:2.5rem;position:relative;overflow:hidden; }
// // // // .lp-price-card::before { content:'';position:absolute;top:0;left:10%;right:10%;height:1px;background:linear-gradient(90deg,transparent,var(--gold),transparent); }
// // // // .lp-price-card-top { display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:1.5rem; }
// // // // .lp-price-badge { display:inline-block;background:linear-gradient(135deg,#D4AF37,#F0D060);color:#0A0A0A;border-radius:100px;padding:4px 14px;font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase; }
// // // // .lp-price-amount { text-align:right; }
// // // // .lp-price-num { font-family:var(--serif);font-size:4rem;font-weight:600;color:var(--text-primary);line-height:1;letter-spacing:-2px; }
// // // // .lp-price-period { font-size:10px;font-weight:600;color:var(--text-muted2);letter-spacing:2px;text-transform:uppercase;margin-top:4px; }
// // // // .lp-price-divider { height:0.5px;background:var(--border-color);margin:1.25rem 0; }
// // // // .lp-price-list { list-style:none;display:flex;flex-direction:column;gap:0;margin-bottom:1.75rem; }
// // // // .lp-price-item { display:flex;align-items:center;gap:10px;padding:0.55rem 0;font-size:13px;font-weight:500;color:var(--text-muted);border-bottom:0.5px solid var(--border-color); }
// // // // .lp-price-item:last-child { border-bottom:none; }
// // // // .lp-price-check { color:var(--gold);font-size:11px;flex-shrink:0; }

// // // // /* ── FOOTER ── */
// // // // .lp-footer { padding:3rem 3rem 4rem;border-top:0.5px solid var(--border-color);background:var(--surface); }
// // // // .lp-footer-inner { max-width:1180px;margin:0 auto;display:flex;flex-direction:column;align-items:center;gap:1rem; }
// // // // .lp-footer-logo { display:flex;align-items:center;gap:10px;font-family:var(--sans);font-size:15px;font-weight:600;color:var(--text-primary); }
// // // // .lp-footer-logo em { color:var(--gold);font-style:normal;font-weight:700; }
// // // // .lp-footer-tagline { font-size:13px;font-weight:400;color:var(--text-muted2); }
// // // // .lp-footer-copy { font-size:12px;font-weight:400;color:var(--text-muted2);opacity:0.6; }

// // // // /* ── THEME TOGGLE ── */
// // // // .lp-theme-btn { width:40px;height:40px;border-radius:50%;border:0.5px solid var(--border-gold);background:var(--gold-subtle);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background 0.2s,transform 0.15s;flex-shrink:0; }
// // // // .lp-theme-btn:hover { background:var(--gold-subtle2);transform:scale(1.08); }

// // // // /* ── TABLET ── */
// // // // @media (max-width: 900px) {
// // // //   .lp-hero { grid-template-columns:1fr; text-align:center; padding:8rem 1.5rem 4rem; }
// // // //   .lp-hero-sub { margin:0 auto 1rem; }
// // // //   .lp-hero-ctas { justify-content:center; }
// // // //   .lp-hero-stats { justify-content:center; }
// // // //   .lp-hero-right { margin-top:3rem; }
// // // //   .scan-scene { width:300px; height:380px; transform:scale(0.85); }
// // // //   .lp-how-timeline { grid-template-columns:1fr; }
// // // //   .lp-how-bridge { display:none; }
// // // //   .lp-feat-bento { grid-template-columns:1fr 1fr; }
// // // //   .lp-feat-bcard.hero { grid-column:span 2; }
// // // //   .lp-feat-bcard.tall { grid-row:span 1; }
// // // //   .lp-use-panel { grid-template-columns:1fr; }
// // // //   .lp-pricing-cta-inner { grid-template-columns:1fr; }
// // // //   .lp-nav { padding:1rem 1.5rem; }
// // // //   .lp-nav.scrolled { padding:0.75rem 1.5rem; }
// // // //   .lp-section { padding:4rem 1.5rem; }
// // // //   .lp-pricing-cta { padding:4rem 1.5rem; }
// // // //   .lp-hamburger { display:flex; }
// // // //   .lp-nav-links { display:none; }
// // // //   .lp-mobile-cluster { display:flex; }
// // // //   .lp-trust { padding:1.25rem 1.5rem; }
// // // // }
// // // // @media (max-width: 600px) {
// // // //   .lp-feat-bento { grid-template-columns:1fr; }
// // // //   .lp-feat-bcard.hero { grid-column:span 1; }
// // // //   .lp-hero-title { font-size:clamp(2.6rem,9vw,3.5rem); }
// // // //   .lp-hero-stats { gap:1.5rem; }
// // // //   .lp-stat-num { font-size:2rem; }
// // // //   .lp-section { padding:3.5rem 1.25rem; }
// // // //   .lp-section-title { font-size:clamp(2rem,7vw,2.8rem); }
// // // //   .lp-use-panel { padding:1.75rem 1.25rem; }
// // // //   .lp-pricing-cta { padding:3rem 1.25rem; }
// // // //   .lp-pricing-headline { font-size:clamp(2.4rem,8vw,3rem); }
// // // //   .lp-hero-ctas { flex-direction:column; align-items:center; }
// // // //   .lp-hero-ctas .lp-btn-gold, .lp-hero-ctas .lp-btn-ghost { width:100%; justify-content:center; }
// // // //   .lp-trust-item { font-size:10px; }
// // // //   .lp-trust-item-icon { width:30px; height:30px; font-size:1.1rem; }
// // // // }
// // // // `;

// // // // /* ─────────────────────────────────────────
// // // //    COUNT-UP HOOK
// // // // ───────────────────────────────────────── */
// // // // function useCountUp(target, duration = 1800, suffix = "", started = false) {
// // // //     const [display, setDisplay] = useState("0");
// // // //     const [progress, setProgress] = useState(0);
// // // //     const frameRef = useRef(null);

// // // //     useEffect(() => {
// // // //         if (!started) return;
// // // //         const num = parseFloat(target.replace(/[^0-9.]/g, ""));
// // // //         if (!num) { setDisplay(target); setProgress(100); return; }
// // // //         const startTime = performance.now();
// // // //         const animate = (now) => {
// // // //             const elapsed = now - startTime;
// // // //             const pct = Math.min(elapsed / duration, 1);
// // // //             const eased = pct === 1 ? 1 : 1 - Math.pow(2, -10 * pct);
// // // //             const current = num * eased;
// // // //             const prog = Math.round(pct * 100);
// // // //             let formatted = Number.isInteger(num) ? Math.round(current).toString() : current.toFixed(0);
// // // //             setDisplay(formatted + suffix);
// // // //             setProgress(prog);
// // // //             if (pct < 1) frameRef.current = requestAnimationFrame(animate);
// // // //         };
// // // //         frameRef.current = requestAnimationFrame(animate);
// // // //         return () => cancelAnimationFrame(frameRef.current);
// // // //     }, [started, target, duration, suffix]);

// // // //     return { display, progress };
// // // // }

// // // // function StatItem({ value, suffix, label, started, delay = 0 }) {
// // // //     const [go, setGo] = useState(false);
// // // //     useEffect(() => {
// // // //         if (!started) return;
// // // //         const t = setTimeout(() => setGo(true), delay);
// // // //         return () => clearTimeout(t);
// // // //     }, [started, delay]);
// // // //     const { display, progress } = useCountUp(value, 1600, suffix, go);
// // // //     const isCounting = go && progress < 100;
// // // //     return (
// // // //         <div className="lp-stat">
// // // //             <div className={`lp-stat-num${isCounting ? " counting" : ""}`}>{value === "0" ? "0" : display}</div>
// // // //             <div className="lp-stat-label">{label}</div>
// // // //             <div className="lp-stat-bar"><div className="lp-stat-bar-fill" style={{ width: `${progress}%` }} /></div>
// // // //         </div>
// // // //     );
// // // // }

// // // // /* ─────────────────────────────────────────
// // // //    QR / SCAN SCENE
// // // // ───────────────────────────────────────── */
// // // // const QR_PATTERN = [
// // // //     [1, 1, 1, 0, 1, 0], [1, 0, 1, 1, 0, 1], [1, 1, 1, 0, 1, 1], [0, 1, 0, 1, 0, 0], [1, 0, 1, 1, 1, 0], [0, 1, 0, 0, 1, 1],
// // // // ];

// // // // function ScanScene({ isLight }) {
// // // //     return (
// // // //         <div className="scan-scene">
// // // //             <div className="hero-orb-1" />
// // // //             <div className="phone-scanner">
// // // //                 <div className="phone-scanner-screen">
// // // //                     <div className="scanner-viewfinder">
// // // //                         <div className="vf-corner-tr" />
// // // //                         <div className="vf-corner-bl" />
// // // //                         <div className="scan-laser" />
// // // //                         <div className="mini-qr">
// // // //                             {QR_PATTERN.map((row, ri) =>
// // // //                                 row.map((cell, ci) => (
// // // //                                     <div key={`${ri}-${ci}`} className="mini-qr-cell"
// // // //                                         style={{ background: cell ? (isLight ? "rgba(154,107,0,0.55)" : "rgba(212,175,55,0.5)") : "transparent" }} />
// // // //                                 ))
// // // //                             )}
// // // //                         </div>
// // // //                         <div className="scanner-success-flash" />
// // // //                     </div>
// // // //                     <div className="scanner-label">Point at QR code</div>
// // // //                 </div>
// // // //             </div>
// // // //             <div className="beam-line" />
// // // //             <div className="beam-particle" />
// // // //             <div className="scan-ripple r1" />
// // // //             <div className="scan-ripple r2" />
// // // //             <div className="scan-ripple r3" />
// // // //             <div className="scan-success"><div className="scan-success-dot" />Contact Saved ✓</div>
// // // //             <div className="phone-profile">
// // // //                 <div className="profile-card-wrap">
// // // //                     <div className="profile-banner">
// // // //                         <div className="profile-banner-img" />
// // // //                         <div className="profile-banner-fade" />
// // // //                         <div className="profile-avatar-ring">
// // // //                             <div className="profile-avatar-inner">UN</div>
// // // //                         </div>
// // // //                     </div>
// // // //                     <div className="profile-info">
// // // //                         <div className="profile-info-name">User <em>Name</em></div>
// // // //                         <div className="profile-info-role">Director · Company Name</div>
// // // //                         <div className="profile-tiles">
// // // //                             {[["📞", "Phone"], ["✉️", "Email"], ["📍", "Delhi"]].map(([, label]) => (
// // // //                                 <div className="profile-tile" key={label}>
// // // //                                     <div className="tile-dot"><div className="tile-dot-inner" /></div>
// // // //                                     <div className="tile-text">{label}</div>
// // // //                                 </div>
// // // //                             ))}
// // // //                         </div>
// // // //                         <button className="profile-save-btn">↓ Save Contact</button>
// // // //                         <div className="profile-tiles" style={{ marginTop: "1rem" }}>
// // // //                             {[["🌐", "Website"], ["📄", "Resume"], ["ⓕ", "Facebook"]].map(([, label]) => (
// // // //                                 <div className="profile-tile" key={label}>
// // // //                                     <div className="tile-dot"><div className="tile-dot-inner" /></div>
// // // //                                     <div className="tile-text">{label}</div>
// // // //                                 </div>
// // // //                             ))}
// // // //                         </div>
// // // //                     </div>
// // // //                 </div>
// // // //             </div>
// // // //         </div>
// // // //     );
// // // // }

// // // // /* ─────────────────────────────────────────
// // // //    ARROW BRIDGE
// // // // ───────────────────────────────────────── */
// // // // function ArrowBridge({ filled }) {
// // // //     return (
// // // //         <div className="lp-how-bridge">
// // // //             <svg width="36" height="28" viewBox="0 0 36 28" fill="none" overflow="visible">
// // // //                 <path d="M2 14 L26 14" className={`how-arrow-path${filled ? " filled" : ""}`} strokeWidth="1.5" strokeLinecap="round" fill="none"
// // // //                     style={{ strokeDasharray: 28, strokeDashoffset: filled ? 0 : 28, transition: "stroke-dashoffset 0.8s cubic-bezier(0.22,1,0.36,1), stroke 0.4s ease" }} />
// // // //                 <path d="M20 7 L28 14 L20 21" className={`how-arrow-path${filled ? " filled" : ""}`} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"
// // // //                     style={{ strokeDasharray: 24, strokeDashoffset: filled ? 0 : 24, transition: "stroke-dashoffset 0.6s cubic-bezier(0.22,1,0.36,1) 0.3s, stroke 0.4s ease 0.3s" }} />
// // // //             </svg>
// // // //         </div>
// // // //     );
// // // // }

// // // // /* ─────────────────────────────────────────
// // // //    USE CASES
// // // // ───────────────────────────────────────── */
// // // // const USE_CASES = [
// // // //     { icon: "👔", role: "Job Seekers", tagline: "Land your next opportunity faster", desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one elegant scan.", highlights: ["Resume attached & downloadable", "LinkedIn & GitHub links", "QR code for instant sharing", "Save contact in one tap"] },
// // // //     { icon: "💼", role: "Freelancers", tagline: "Win clients before you even speak", desc: "Share everything a client needs — portfolio, social proof, and contact — all in a single professional link. Close deals faster.", highlights: ["Portfolio & website link", "WhatsApp quick contact", "Instagram & social proof", "Professional digital presence"] },
// // // //     { icon: "🏢", role: "Business Owners", tagline: "Network smarter, not harder", desc: "Stop spending on printed cards that get lost. Share via QR at events — always updated, never outdated, never thrown away.", highlights: ["Always up-to-date details", "Google Wallet for offline use", "QR code for events & meetups", "Share via WhatsApp or email"] },
// // // //     { icon: "🎓", role: "Students", tagline: "Build your professional identity early", desc: "Stand out in campus placements and internship drives. Show your skills, projects, and social profiles before you say a word.", highlights: ["GitHub & project links", "Resume upload for placements", "LinkedIn profile showcase", "Free forever — no cost"] },
// // // //     { icon: "📣", role: "Sales Pros", tagline: "Close the follow-up gap instantly", desc: "At trade shows or cold visits — one QR scan and your prospect has everything. Number, email, website, product links in one place.", highlights: ["Instant QR share at events", "Website & product links", "Save contact in one tap", "Google Wallet for quick access"] },
// // // //     { icon: "🧑‍💻", role: "Developers", tagline: "Your entire dev profile in one link", desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share at hackathons or in open source communities.", highlights: ["GitHub & portfolio links", "Twitter / X profile", "Resume for job applications", "Professional dev identity"] },
// // // // ];

// // // // function UseCasesSection({ navigate, isLight }) {
// // // //     const [active, setActive] = useState(0);
// // // //     const [tabKey, setTabKey] = useState(0);
// // // //     const cur = USE_CASES[active];
// // // //     const switchTab = (i) => { setActive(i); setTabKey(k => k + 1); };

// // // //     return (
// // // //         <section className="lp-section">
// // // //             <div className="lp-section-inner">
// // // //                 <span className="lp-eyebrow reveal from-up">Who is it for</span>
// // // //                 <h2 className="lp-section-title reveal from-up d1">Built for every <em>professional</em></h2>
// // // //                 <p className="lp-section-sub reveal from-up d2">Whether you're a student or a CEO — your digital card works for you.</p>
// // // //                 <div className="lp-use-tabs reveal from-up d3">
// // // //                     {USE_CASES.map((u, i) => (
// // // //                         <button key={i} className={`lp-use-tab${active === i ? (isLight ? " lp-use-tab-active" : "") : ""}`}
// // // //                             onClick={() => switchTab(i)}
// // // //                             style={{
// // // //                                 border: active === i ? "0.5px solid var(--border-gold)" : "0.5px solid var(--border-color)",
// // // //                                 background: active === i ? "var(--gold-subtle2)" : "transparent",
// // // //                                 color: active === i ? "var(--gold)" : "var(--text-muted)",
// // // //                             }}>
// // // //                             {u.icon} {u.role}
// // // //                         </button>
// // // //                     ))}
// // // //                 </div>
// // // //                 <div className="lp-use-panel reveal from-up d4" style={{ borderColor: "var(--border-gold)" }}>
// // // //                     <div key={`left-${tabKey}`} className="lp-use-content">
// // // //                         <div className="lp-use-badge"><span>{cur.icon}</span><span style={{ fontSize: "12px", letterSpacing: "0.5px" }}>{cur.role}</span></div>
// // // //                         <div className="lp-use-tagline">{cur.tagline}</div>
// // // //                         <p className="lp-use-desc">{cur.desc}</p>
// // // //                         <button className="lp-btn-gold" onClick={() => navigate("/animation/card")}>Create My Card — Free →</button>
// // // //                     </div>
// // // //                     <div key={`right-${tabKey}`} className="lp-use-highlights">
// // // //                         {cur.highlights.map((h, i) => (
// // // //                             <div className="lp-use-hl" key={i} style={{ animationDelay: `${0.05 + i * 0.07}s` }}>
// // // //                                 <div className="lp-use-hl-check">✓</div>
// // // //                                 <span className="lp-use-hl-text">{h}</span>
// // // //                             </div>
// // // //                         ))}
// // // //                     </div>
// // // //                 </div>
// // // //             </div>
// // // //         </section>
// // // //     );
// // // // }

// // // // /* ─────────────────────────────────────────
// // // //    LOGO SVG
// // // // ───────────────────────────────────────── */
// // // // function LogoSVG({ isLight, size = 34 }) {
// // // //     return (
// // // //         <svg width={size} height={size} viewBox="0 0 34 34" fill="none">
// // // //             <rect width="34" height="34" rx="9" fill={isLight ? "#FFF3CC" : "#1A1A1A"} />
// // // //             <rect width="34" height="34" rx="9" stroke="var(--border-gold)" strokeWidth="0.5" />
// // // //             <circle cx="15" cy="13" r="5" stroke="var(--gold)" strokeWidth="1.5" fill="none" />
// // // //             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
// // // //             <path d="M23 11 L27 11 L27 23" stroke="var(--gold)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.6" />
// // // //             <circle cx="27" cy="23" r="1.8" fill="var(--gold)" />
// // // //         </svg>
// // // //     );
// // // // }

// // // // /* ─────────────────────────────────────────
// // // //    THEME ICON
// // // // ───────────────────────────────────────── */
// // // // function ThemeIcon({ isLight }) {
// // // //     return isLight ? (
// // // //         <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
// // // //             <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
// // // //         </svg>
// // // //     ) : (
// // // //         <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
// // // //             <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
// // // //             <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
// // // //             <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
// // // //             <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
// // // //         </svg>
// // // //     );
// // // // }

// // // // /* ─────────────────────────────────────────
// // // //    MAIN COMPONENT
// // // // ───────────────────────────────────────── */
// // // // export default function LandingPage() {
// // // //     const navigate = useNavigate();
// // // //     const [scrolled, setScrolled] = useState(false);
// // // //     const [isLight, setIsLight] = useState(false);
// // // //     const [menuOpen, setMenuOpen] = useState(false);
// // // //     const [statsStarted, setStatsStarted] = useState(false);
// // // //     const [howVisible, setHowVisible] = useState(false);
// // // //     const statsRef = useRef(null);
// // // //     const howRef = useRef(null);

// // // //     useEffect(() => {
// // // //         const onResize = () => { if (window.innerWidth > 900) setMenuOpen(false); };
// // // //         window.addEventListener("resize", onResize);
// // // //         return () => window.removeEventListener("resize", onResize);
// // // //     }, []);

// // // //     useEffect(() => {
// // // //         const onScroll = () => setScrolled(window.scrollY > 40);
// // // //         window.addEventListener("scroll", onScroll);
// // // //         return () => window.removeEventListener("scroll", onScroll);
// // // //     }, []);

// // // //     useEffect(() => {
// // // //         const observers = [];
// // // //         const revealEls = document.querySelectorAll(".reveal");
// // // //         const revealObs = new IntersectionObserver(
// // // //             (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
// // // //             { threshold: 0.1 }
// // // //         );
// // // //         revealEls.forEach(el => revealObs.observe(el));
// // // //         observers.push(revealObs);
// // // //         if (statsRef.current) {
// // // //             const statsObs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setStatsStarted(true); }, { threshold: 0.5 });
// // // //             statsObs.observe(statsRef.current);
// // // //             observers.push(statsObs);
// // // //         }
// // // //         if (howRef.current) {
// // // //             const howObs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setHowVisible(true); }, { threshold: 0.3 });
// // // //             howObs.observe(howRef.current);
// // // //             observers.push(howObs);
// // // //         }
// // // //         const pillCards = document.querySelectorAll(".lp-feat-bcard.has-pills");
// // // //         const pillObs = new IntersectionObserver(
// // // //             (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("pills-visible"); }),
// // // //             { threshold: 0.4 }
// // // //         );
// // // //         pillCards.forEach(c => pillObs.observe(c));
// // // //         observers.push(pillObs);
// // // //         return () => observers.forEach(o => o.disconnect());
// // // //     }, []);

// // // //     /* ── Warm hero background for light mode ── */
// // // //     const heroBg = isLight
// // // //         ? "radial-gradient(ellipse 65% 60% at 15% 50%, rgba(255,210,60,0.22) 0%, transparent 55%), radial-gradient(ellipse 50% 55% at 85% 30%, rgba(255,170,30,0.13) 0%, transparent 60%), #FFFBF2"
// // // //         : "#080808";

// // // //     return (
// // // //         <div className={isLight ? "lp-light" : ""} style={{ background: isLight ? "#FFFBF2" : "#080808", minHeight: "100vh" }}>
// // // //             <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

// // // //             {/* ── MOBILE DRAWER ── */}
// // // //             <div className={`lp-mobile-menu${menuOpen ? " open" : ""}`}>
// // // //                 <div className="lp-mobile-menu-backdrop" onClick={() => setMenuOpen(false)} />
// // // //                 <div className="lp-mobile-menu-panel">
// // // //                     <div className="lp-mobile-menu-header">
// // // //                         <a href="/" className="lp-logo" onClick={() => setMenuOpen(false)}>
// // // //                             <LogoSVG isLight={isLight} size={28} />
// // // //                             myprofile<em>corner</em>
// // // //                         </a>
// // // //                         <button className="lp-mobile-close" onClick={() => setMenuOpen(false)}>✕</button>
// // // //                     </div>
// // // //                     <div className="lp-mobile-links">
// // // //                         <a href="#how" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">⚡</span>How it works</a>
// // // //                         <a href="#features" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">✦</span>Features</a>
// // // //                         <a href="#pricing" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">₹</span>Pricing</a>
// // // //                     </div>
// // // //                     <div className="lp-mobile-divider" />
// // // //                     <div className="lp-mobile-menu-footer">
// // // //                         <button className="lp-mobile-cta" onClick={() => { setMenuOpen(false); navigate("/animation/card"); }}>Create My Card — Free →</button>
// // // //                         <div className="lp-mobile-tagline">100% Free · No app required · 2 min setup</div>
// // // //                     </div>
// // // //                 </div>
// // // //             </div>

// // // //             {/* ── NAV ── */}
// // // //             <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
// // // //                 <div className="lp-nav-inner">
// // // //                     <a href="/" className="lp-logo">
// // // //                         <LogoSVG isLight={isLight} size={34} />
// // // //                         myprofile<em>corner</em>
// // // //                     </a>
// // // //                     <div className="lp-nav-links">
// // // //                         <a href="#how" className="lp-nav-link">How it works</a>
// // // //                         <a href="#features" className="lp-nav-link">Features</a>
// // // //                         <a href="#pricing" className="lp-nav-link">Pricing</a>
// // // //                         <button className="lp-theme-btn" onClick={() => setIsLight(p => !p)} aria-label="Toggle theme"><ThemeIcon isLight={isLight} /></button>
// // // //                         <button className="lp-nav-cta" onClick={() => navigate("/animation/card")}>Create Free Card</button>
// // // //                     </div>
// // // //                     <div className="lp-mobile-cluster">
// // // //                         <button className="lp-theme-btn lp-theme-btn-mobile" onClick={() => setIsLight(p => !p)} aria-label="Toggle theme"><ThemeIcon isLight={isLight} /></button>
// // // //                         <button className={`lp-hamburger${menuOpen ? " open" : ""}`} onClick={() => setMenuOpen(p => !p)} aria-label="Open menu">
// // // //                             <span /><span /><span />
// // // //                         </button>
// // // //                     </div>
// // // //                 </div>
// // // //             </nav>

// // // //             {/* ── HERO ── */}
// // // //             <section style={{ background: heroBg, overflow: "hidden", position: "relative" }}>
// // // //                 {/* Extra warm decorative orbs for light mode */}
// // // //                 {isLight && (
// // // //                     <>
// // // //                         <div style={{ position: "absolute", width: "400px", height: "400px", borderRadius: "50%", background: "radial-gradient(circle, rgba(255,200,50,0.18) 0%, transparent 65%)", top: "-80px", right: "5%", pointerEvents: "none", zIndex: 0 }} />
// // // //                         <div style={{ position: "absolute", width: "300px", height: "300px", borderRadius: "50%", background: "radial-gradient(circle, rgba(255,160,20,0.12) 0%, transparent 65%)", bottom: "5%", left: "5%", pointerEvents: "none", zIndex: 0 }} />
// // // //                     </>
// // // //                 )}
// // // //                 {!isLight && (
// // // //                     <div style={{ position: "absolute", width: "600px", height: "600px", borderRadius: "50%", background: "radial-gradient(circle, var(--gold-subtle) 0%, transparent 65%)", top: "-200px", left: "-100px", pointerEvents: "none" }} />
// // // //                 )}
// // // //                 <div className="lp-hero">
// // // //                     <div className="lp-hero-left">
// // // //                         <div className="lp-hero-eyebrow reveal from-down">
// // // //                             <div className="lp-hero-eyebrow-dot" /> 100% Free · No App Needed
// // // //                         </div>
// // // //                         <h1 className="lp-hero-title reveal from-left">
// // // //                             Your Digital<br />Identity, <em>Always</em><br />in Your Pocket
// // // //                         </h1>
// // // //                         <p className="lp-hero-sub reveal from-left d1">
// // // //                             Replace paper visiting cards with a smart digital card. Share your profile instantly via QR code, link, or Google Wallet — no printing, no waste.
// // // //                         </p>
// // // //                         <div className="lp-hero-stats" ref={statsRef}>
// // // //                             <StatItem value="100" suffix="%" label="Free forever" started={statsStarted} delay={0} />
// // // //                             <StatItem value="2" suffix=" min" label="Setup time" started={statsStarted} delay={200} />
// // // //                             <StatItem value="0" suffix="" label="Paper waste" started={statsStarted} delay={400} />
// // // //                         </div>
// // // //                         <div className="lp-hero-ctas reveal from-up d2">
// // // //                             <button className="lp-btn-gold" onClick={() => navigate("/animation/card")}>Create My Card — It's Free →</button>
// // // //                             <a href="#how" className="lp-btn-ghost">See how it works</a>
// // // //                         </div>
// // // //                     </div>
// // // //                     <div className="lp-hero-right reveal from-right">
// // // //                         <ScanScene isLight={isLight} />
// // // //                     </div>
// // // //                 </div>
// // // //             </section>

// // // //             <div className="gold-rule" />

// // // //             {/* ── TRUST BAR ── */}
// // // //             <div className="lp-trust">
// // // //                 <span className="lp-trust-label">Works seamlessly with</span>
// // // //                 <div className="lp-trust-items">
// // // //                     {[["🔥", "Firebase"], ["🪙", "Google Wallet"], ["📱", "Android & iOS"], ["🔗", "Any Browser"]].map(([icon, label]) => (
// // // //                         <div className="lp-trust-item" key={label}>
// // // //                             <div className="lp-trust-item-icon">{icon}</div>
// // // //                             <span>{label}</span>
// // // //                         </div>
// // // //                     ))}
// // // //                 </div>
// // // //             </div>

// // // //             <div className="gold-rule" />

// // // //             {/* ── HOW IT WORKS ── */}
// // // //             <section id="how" className="lp-section" ref={howRef}
// // // //                 style={isLight ? { background: "linear-gradient(180deg, #FEF5D0 0%, #FFFBF2 100%)" } : {}}>
// // // //                 <div className="lp-section-inner">
// // // //                     <span className="lp-eyebrow reveal from-up">How it works</span>
// // // //                     <h2 className="lp-section-title reveal from-up d1">From setup to sharing <em>in 3 steps</em></h2>
// // // //                     <p className="lp-section-sub reveal from-up d2">No app downloads. No tech skills required. Just fill, share, impress.</p>
// // // //                     <div className="lp-how-timeline">
// // // //                         <div className="lp-how-card reveal from-left d1">
// // // //                             <div className="lp-how-num">01</div>
// // // //                             <div className="lp-how-icon-ring">✏️</div>
// // // //                             <div className="lp-how-step-label">Step 01</div>
// // // //                             <div className="lp-how-title">Fill Your Profile</div>
// // // //                             <p className="lp-how-desc">Add your name, photo, contact info, social links, resume & website. Takes less than 2 minutes.</p>
// // // //                         </div>
// // // //                         <ArrowBridge filled={howVisible} />
// // // //                         <div className="lp-how-card reveal from-up d3">
// // // //                             <div className="lp-how-num">02</div>
// // // //                             <div className="lp-how-icon-ring">🎴</div>
// // // //                             <div className="lp-how-step-label">Step 02</div>
// // // //                             <div className="lp-how-title">Get Your Digital Card</div>
// // // //                             <p className="lp-how-desc">Instantly get a beautiful card with a unique QR code & shareable link — no design skills needed.</p>
// // // //                         </div>
// // // //                         <ArrowBridge filled={howVisible} />
// // // //                         <div className="lp-how-card reveal from-right d5">
// // // //                             <div className="lp-how-num">03</div>
// // // //                             <div className="lp-how-icon-ring">🚀</div>
// // // //                             <div className="lp-how-step-label">Step 03</div>
// // // //                             <div className="lp-how-title">Share Anywhere</div>
// // // //                             <p className="lp-how-desc">Show QR in meetings, add to Google Wallet, or send your link via WhatsApp, email, or LinkedIn.</p>
// // // //                         </div>
// // // //                     </div>
// // // //                 </div>
// // // //             </section>

// // // //             <div className="gold-rule" />

// // // //             {/* ── FEATURES ── */}
// // // //             <section id="features" className="lp-section"
// // // //                 style={isLight ? { background: "#FFFBF2" } : {}}>
// // // //                 <div className="lp-section-inner">
// // // //                     <span className="lp-eyebrow reveal from-up">Features</span>
// // // //                     <h2 className="lp-section-title reveal from-up d1">Everything a modern <em>professional</em> needs</h2>
// // // //                     <p className="lp-section-sub reveal from-up d2">One card. Every detail. Always up-to-date.</p>
// // // //                     <div className="lp-feat-bento">
// // // //                         <div className="lp-feat-bcard hero reveal from-left d1">
// // // //                             <div className="lp-feat-bicon">🪙</div>
// // // //                             <div className="lp-feat-btag">Premium Feature</div>
// // // //                             <div className="lp-feat-btitle">Google Wallet Pass</div>
// // // //                             <p className="lp-feat-bdesc">Add your card to Google Wallet and access it offline — at meetings, events, or anywhere without internet.</p>
// // // //                             <div className="lp-feat-wallet">
// // // //                                 <div className="lp-wallet-chip">G Pay</div>
// // // //                                 <div className="lp-wallet-chip">Wallet</div>
// // // //                                 <div className="lp-wallet-chip">Offline ✓</div>
// // // //                             </div>
// // // //                         </div>
// // // //                         <div className="lp-feat-bcard tall reveal from-right d2">
// // // //                             <div className="lp-feat-bicon">📲</div>
// // // //                             <div className="lp-feat-btag">Instant Share</div>
// // // //                             <div className="lp-feat-btitle">Unique QR Code</div>
// // // //                             <p className="lp-feat-bdesc">Every card gets a dedicated QR. One scan opens your full profile — no typing, no friction.</p>
// // // //                             <div className="lp-qr-art">
// // // //                                 {[1, 1, 1, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 1].map((v, i) => (
// // // //                                     <div key={i} className="lp-qr-cell"
// // // //                                         style={{ background: v ? "var(--gold)" : "var(--gold-subtle)", borderRadius: "2px", aspectRatio: "1" }} />
// // // //                                 ))}
// // // //                             </div>
// // // //                         </div>
// // // //                         <div className="lp-feat-bcard reveal from-up d3">
// // // //                             <div className="lp-feat-bicon">📸</div>
// // // //                             <div className="lp-feat-btag">Identity</div>
// // // //                             <div className="lp-feat-btitle">Profile Photo</div>
// // // //                             <p className="lp-feat-bdesc">Your face on your card — and inside Google Wallet too.</p>
// // // //                         </div>
// // // //                         <div className="lp-feat-bcard reveal from-up d4">
// // // //                             <div className="lp-feat-bicon">💾</div>
// // // //                             <div className="lp-feat-btag">One Tap</div>
// // // //                             <div className="lp-feat-btitle">Save Contact</div>
// // // //                             <p className="lp-feat-bdesc">Visitors save you directly to their phone contacts with a single tap.</p>
// // // //                         </div>
// // // //                         <div className="lp-feat-bcard hero has-pills reveal from-left d2">
// // // //                             <div className="lp-feat-bicon">🔗</div>
// // // //                             <div className="lp-feat-btag">Connections</div>
// // // //                             <div className="lp-feat-btitle">All Social Links</div>
// // // //                             <p className="lp-feat-bdesc">LinkedIn, GitHub, Instagram, Twitter, WhatsApp, YouTube — everything that matters in one place.</p>
// // // //                             <div className="lp-feat-socials">
// // // //                                 {["in LinkedIn", "⌥ GitHub", "✉ WhatsApp", "▶ YouTube", "📷 Insta"].map(s => (
// // // //                                     <span key={s} className="lp-social-pill">{s}</span>
// // // //                                 ))}
// // // //                             </div>
// // // //                         </div>
// // // //                         <div className="lp-feat-bcard reveal from-up d3">
// // // //                             <div className="lp-feat-bicon">📄</div>
// // // //                             <div className="lp-feat-btag">Career</div>
// // // //                             <div className="lp-feat-btitle">Resume Upload</div>
// // // //                             <p className="lp-feat-bdesc">Attach your PDF resume. Recruiters can download it instantly.</p>
// // // //                         </div>
// // // //                         <div className="lp-feat-bcard reveal from-up d4">
// // // //                             <div className="lp-feat-bicon">✏️</div>
// // // //                             <div className="lp-feat-btag">Always Fresh</div>
// // // //                             <div className="lp-feat-btitle">Edit Anytime</div>
// // // //                             <p className="lp-feat-bdesc">Changed jobs? New number? Update instantly — same link forever.</p>
// // // //                         </div>
// // // //                         <div className="lp-feat-bcard reveal from-right d5">
// // // //                             <div className="lp-feat-bicon">🌐</div>
// // // //                             <div className="lp-feat-btag">Universal</div>
// // // //                             <div className="lp-feat-btitle">Works Everywhere</div>
// // // //                             <p className="lp-feat-bdesc">Opens in any browser on any device. No app install ever required.</p>
// // // //                         </div>
// // // //                     </div>
// // // //                 </div>
// // // //             </section>

// // // //             <div className="gold-rule" />

// // // //             {/* ── USE CASES ── */}
// // // //             <div style={isLight ? { background: "linear-gradient(180deg, #FEF5D0 0%, #FFFBF2 100%)" } : {}}>
// // // //                 <UseCasesSection navigate={navigate} isLight={isLight} />
// // // //             </div>

// // // //             <div className="gold-rule" />

// // // //             {/* ── PRICING ── */}
// // // //             <section id="pricing" className="lp-pricing-cta"
// // // //                 style={isLight ? { background: "linear-gradient(160deg, #FFF3C0 0%, #FFFBF2 40%, #FEF0C0 100%)" } : {}}>
// // // //                 <div className="lp-pricing-cta-inner">
// // // //                     <div className="lp-pricing-copy">
// // // //                         <span className="lp-pricing-eyebrow reveal from-left">Pricing</span>
// // // //                         <h2 className="lp-pricing-headline reveal from-left d1">Stop carrying<br /><em>paper cards.</em></h2>
// // // //                         <p className="lp-pricing-sub reveal from-left d2">Create your digital card in under 2 minutes. No hidden charges. No credit card. Free forever.</p>
// // // //                         <div className="lp-pricing-perks reveal from-left d3">
// // // //                             {["No app download needed", "Unique QR code & shareable link", "Google Wallet pass included", "Edit anytime — same link forever", "Works on every device & browser"].map(p => (
// // // //                                 <div className="lp-pricing-perk" key={p}>
// // // //                                     <div className="lp-perk-tick">✓</div><span>{p}</span>
// // // //                                 </div>
// // // //                             ))}
// // // //                         </div>
// // // //                         <button className="lp-btn-gold reveal from-up d4" onClick={() => navigate("/animation/card")}>Create My Card Now →</button>
// // // //                     </div>
// // // //                     <div className="lp-price-card reveal from-right d2">
// // // //                         <div className="lp-price-card-top">
// // // //                             <div className="lp-price-badge">Most Popular</div>
// // // //                             <div className="lp-price-amount">
// // // //                                 <div className="lp-price-num">₹0</div>
// // // //                                 <div className="lp-price-period">Forever Free</div>
// // // //                             </div>
// // // //                         </div>
// // // //                         <div className="lp-price-divider" />
// // // //                         <ul className="lp-price-list">
// // // //                             {["1 Digital Visiting Card", "Unique QR Code & Link", "Profile Photo Upload", "Resume Upload (PDF)", "All Social Media Links", "Google Wallet Pass", "Save Contact (.vcf)", "Edit Anytime", "No App Required"].map(item => (
// // // //                                 <li className="lp-price-item" key={item}><span className="lp-price-check">✦</span>{item}</li>
// // // //                             ))}
// // // //                         </ul>
// // // //                         <button className="lp-btn-gold" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/animation/card")}>Get Started Free →</button>
// // // //                     </div>
// // // //                 </div>
// // // //             </section>

// // // //             <div className="gold-rule" />

// // // //             {/* ── FOOTER ── */}
// // // //             <footer className="lp-footer"
// // // //                 style={isLight ? { background: "linear-gradient(180deg, #FFF3C4 0%, #FFE88A 100%)", borderTop: "0.5px solid rgba(180,130,0,0.20)" } : {}}>
// // // //                 <div className="lp-footer-inner">
// // // //                     <div className="lp-footer-logo">
// // // //                         <LogoSVG isLight={isLight} size={28} />
// // // //                         myprofile<em>corner</em>
// // // //                     </div>
// // // //                     <p className="lp-footer-tagline">Your digital identity, always in your pocket.</p>
// // // //                     <p className="lp-footer-copy">© {new Date().getFullYear()} MyProfileCorner · Made with ♥ in India</p>
// // // //                 </div>
// // // //             </footer>
// // // //         </div>
// // // //     );
// // // // }


// // // // // import React, { useState, useEffect, useRef, useCallback } from "react";
// // // // // import { useNavigate } from "react-router-dom";

// // // // // const FONT_LINK = `
// // // // //   @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600&family=Montserrat:wght@300;400;500;600;700;800&display=swap');
// // // // // `;

// // // // // const GLOBAL_CSS = `
// // // // // ${FONT_LINK}

// // // // // *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

// // // // // :root {
// // // // //   --black: #080808;
// // // // //   --dark: #0E0E0E;
// // // // //   --card: #141414;
// // // // //   --border: rgba(255,255,255,0.07);
// // // // //   --gold: #D4AF37;
// // // // //   --gold2: #F0D060;
// // // // //   --gold3: #C9A227;
// // // // //   --gold-dim: rgba(212,175,55,0.18);
// // // // //   --white: #FFFFFF;
// // // // //   --muted: rgba(255,255,255,0.45);
// // // // //   --muted2: rgba(255,255,255,0.25);
// // // // //   --serif: 'Cormorant Garamond', serif;
// // // // //   --sans: 'Montserrat', sans-serif;
// // // // //   --bg: #080808;
// // // // //   --surface: #141414;
// // // // //   --surface2: #1a1a1a;
// // // // //   --text-primary: #FFFFFF;
// // // // //   --text-muted: rgba(255,255,255,0.45);
// // // // //   --text-muted2: rgba(255,255,255,0.25);
// // // // //   --border-color: rgba(255,255,255,0.07);
// // // // //   --border-gold: rgba(212,175,55,0.2);
// // // // //   --gold-subtle: rgba(212,175,55,0.08);
// // // // //   --gold-subtle2: rgba(212,175,55,0.12);
// // // // // }

// // // // // /* ── LIGHT THEME ── */
// // // // // .lp-light {
// // // // //   --bg: #FFFFFF;
// // // // //   --surface: #F5F3EE;
// // // // //   --surface2: #EDE9E0;
// // // // //   --card: #F5F3EE;
// // // // //   --dark: #EDE9E0;
// // // // //   --black: #FFFFFF;
// // // // //   --border-color: rgba(0,0,0,0.08);
// // // // //   --border: rgba(0,0,0,0.08);
// // // // //   --border-gold: rgba(180,140,30,0.25);
// // // // //   --white: #0E0E0E;
// // // // //   --text-primary: #111111;
// // // // //   --text-muted: rgba(0,0,0,0.5);
// // // // //   --text-muted2: rgba(0,0,0,0.35);
// // // // //   --muted: rgba(0,0,0,0.5);
// // // // //   --muted2: rgba(0,0,0,0.35);
// // // // //   --gold: #A0780A;
// // // // //   --gold2: #C9991A;
// // // // //   --gold3: #8A6608;
// // // // //   --gold-dim: rgba(160,120,10,0.15);
// // // // //   --gold-subtle: rgba(160,120,10,0.08);
// // // // //   --gold-subtle2: rgba(160,120,10,0.12);
// // // // // }

// // // // // html { scroll-behavior: smooth; }
// // // // // body {
// // // // //   background: var(--bg);
// // // // //   color: var(--text-primary);
// // // // //   font-family: var(--sans);
// // // // //   overflow-x: hidden;
// // // // //   letter-spacing: 0.1px;
// // // // // }

// // // // // ::-webkit-scrollbar { width: 4px; }
// // // // // ::-webkit-scrollbar-track { background: var(--bg); }
// // // // // ::-webkit-scrollbar-thumb { background: rgba(212,175,55,0.3); border-radius: 2px; }

// // // // // /* ═══════════════════════════════════════
// // // // //    ENHANCEMENT 3 — DIRECTIONAL STAGGER REVEALS
// // // // //    Elements animate from different directions
// // // // // ═══════════════════════════════════════ */
// // // // // .reveal {
// // // // //   opacity: 0;
// // // // //   transition: opacity 0.75s cubic-bezier(0.22,1,0.36,1), transform 0.75s cubic-bezier(0.22,1,0.36,1);
// // // // // }
// // // // // .reveal.from-up    { transform: translateY(32px); }
// // // // // .reveal.from-down  { transform: translateY(-24px); }
// // // // // .reveal.from-left  { transform: translateX(-40px); }
// // // // // .reveal.from-right { transform: translateX(40px); }
// // // // // .reveal.from-scale { transform: scale(0.92); }

// // // // // /* stagger delays */
// // // // // .reveal.d1 { transition-delay: 0.05s; }
// // // // // .reveal.d2 { transition-delay: 0.13s; }
// // // // // .reveal.d3 { transition-delay: 0.21s; }
// // // // // .reveal.d4 { transition-delay: 0.29s; }
// // // // // .reveal.d5 { transition-delay: 0.37s; }
// // // // // .reveal.d6 { transition-delay: 0.45s; }

// // // // // .reveal.visible {
// // // // //   opacity: 1;
// // // // //   transform: translateY(0) translateX(0) scale(1);
// // // // // }

// // // // // /* ── NAV ── */
// // // // // .lp-nav {
// // // // //   position: fixed; top: 0; left: 0; right: 0; z-index: 200;
// // // // //   padding: 1.25rem 3rem; transition: all 0.4s;
// // // // // }
// // // // // .lp-nav.scrolled {
// // // // //   background: rgba(8,8,8,0.92);
// // // // //   backdrop-filter: blur(20px);
// // // // //   border-bottom: 0.5px solid rgba(212,175,55,0.12);
// // // // //   padding: 0.85rem 3rem;
// // // // // }
// // // // // .lp-light .lp-nav.scrolled {
// // // // //   background: rgba(255,255,255,0.95);
// // // // //   border-bottom: 0.5px solid rgba(160,120,10,0.18);
// // // // // }
// // // // // .lp-nav-inner {
// // // // //   max-width: 1180px; margin: 0 auto;
// // // // //   display: flex; align-items: center; justify-content: space-between;
// // // // // }
// // // // // .lp-logo {
// // // // //   display: flex; align-items: center; gap: 10px;
// // // // //   font-family: var(--sans); font-size: 15px; font-weight: 600;
// // // // //   color: var(--text-primary); text-decoration: none; letter-spacing: 0.2px;
// // // // //   flex-shrink: 0;
// // // // // }
// // // // // .lp-logo-icon { width: 34px; height: 34px; flex-shrink: 0; }
// // // // // .lp-logo em { color: var(--gold); font-style: normal; font-weight: 700; }

// // // // // /* desktop nav links */
// // // // // .lp-nav-links { display: flex; align-items: center; gap: 2.5rem; }
// // // // // .lp-nav-link { font-size: 13px; font-weight: 500; color: var(--text-muted); text-decoration: none; letter-spacing: 0.3px; transition: color 0.2s; }
// // // // // .lp-nav-link:hover { color: var(--text-primary); }
// // // // // .lp-nav-cta {
// // // // //   padding: 10px 22px;
// // // // //   background: linear-gradient(135deg, #D4AF37, #F0D060, #C9A227);
// // // // //   color: #0A0A0A; border: none; border-radius: 10px;
// // // // //   font-family: var(--sans); font-size: 13px; font-weight: 700;
// // // // //   letter-spacing: 0.8px; text-transform: uppercase;
// // // // //   cursor: pointer; position: relative; overflow: hidden;
// // // // //   transition: transform 0.15s, opacity 0.15s; white-space: nowrap;
// // // // // }
// // // // // .lp-nav-cta::before { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg, rgba(255,255,255,0.2), transparent 60%); pointer-events: none; }
// // // // // .lp-nav-cta:hover { transform: translateY(-1px); opacity: 0.92; }

// // // // // /* ── HAMBURGER BUTTON ── */
// // // // // .lp-hamburger {
// // // // //   display: none;
// // // // //   flex-direction: column; justify-content: center; align-items: center;
// // // // //   gap: 5px;
// // // // //   width: 40px; height: 40px; border-radius: 10px;
// // // // //   border: 0.5px solid var(--border-gold);
// // // // //   background: var(--gold-subtle);
// // // // //   cursor: pointer; flex-shrink: 0;
// // // // //   transition: background 0.2s;
// // // // // }
// // // // // .lp-hamburger:hover { background: var(--gold-subtle2); }
// // // // // .lp-hamburger span {
// // // // //   display: block; width: 18px; height: 1.5px;
// // // // //   background: var(--gold); border-radius: 2px;
// // // // //   transition: all 0.3s cubic-bezier(0.22,1,0.36,1);
// // // // //   transform-origin: center;
// // // // // }
// // // // // .lp-hamburger.open span:nth-child(1) { transform: translateY(6.5px) rotate(45deg); }
// // // // // .lp-hamburger.open span:nth-child(2) { opacity: 0; transform: scaleX(0); }
// // // // // .lp-hamburger.open span:nth-child(3) { transform: translateY(-6.5px) rotate(-45deg); }

// // // // // /* ── MOBILE DRAWER MENU ── */
// // // // // .lp-mobile-menu {
// // // // //   position: fixed; top: 0; left: 0; right: 0; bottom: 0;
// // // // //   z-index: 190;
// // // // //   display: flex; flex-direction: column;
// // // // //   pointer-events: none;
// // // // // }
// // // // // /* backdrop */
// // // // // .lp-mobile-menu-backdrop {
// // // // //   position: absolute; inset: 0;
// // // // //   background: rgba(0,0,0,0.6);
// // // // //   backdrop-filter: blur(4px);
// // // // //   opacity: 0;
// // // // //   transition: opacity 0.35s ease;
// // // // // }
// // // // // .lp-mobile-menu.open .lp-mobile-menu-backdrop {
// // // // //   opacity: 1; pointer-events: all;
// // // // // }
// // // // // /* drawer panel */
// // // // // .lp-mobile-menu-panel {
// // // // //   position: absolute;
// // // // //   top: 0; right: 0;
// // // // //   width: min(320px, 85vw); height: 100%;
// // // // //   background: var(--bg);
// // // // //   border-left: 0.5px solid var(--border-gold);
// // // // //   display: flex; flex-direction: column;
// // // // //   padding: 0;
// // // // //   transform: translateX(100%);
// // // // //   transition: transform 0.4s cubic-bezier(0.22,1,0.36,1);
// // // // //   box-shadow: -20px 0 60px rgba(0,0,0,0.4);
// // // // //   pointer-events: none;
// // // // //   overflow: hidden;
// // // // // }
// // // // // .lp-mobile-menu.open .lp-mobile-menu-panel {
// // // // //   transform: translateX(0); pointer-events: all;
// // // // // }
// // // // // /* top bar inside drawer */
// // // // // .lp-mobile-menu-header {
// // // // //   display: flex; align-items: center; justify-content: space-between;
// // // // //   padding: 1.25rem 1.5rem;
// // // // //   border-bottom: 0.5px solid var(--border-color);
// // // // // }
// // // // // .lp-mobile-close {
// // // // //   width: 36px; height: 36px; border-radius: 9px;
// // // // //   border: 0.5px solid var(--border-color);
// // // // //   background: var(--surface);
// // // // //   display: flex; align-items: center; justify-content: center;
// // // // //   cursor: pointer; color: var(--text-muted); font-size: 16px;
// // // // //   transition: background 0.2s, color 0.2s;
// // // // // }
// // // // // .lp-mobile-close:hover { background: var(--surface2); color: var(--text-primary); }
// // // // // /* nav links inside drawer */
// // // // // .lp-mobile-links {
// // // // //   flex: 1; display: flex; flex-direction: column;
// // // // //   padding: 1.5rem;
// // // // //   gap: 0.25rem;
// // // // // }
// // // // // .lp-mobile-link {
// // // // //   display: flex; align-items: center; gap: 12px;
// // // // //   padding: 1rem 1.25rem;
// // // // //   border-radius: 14px;
// // // // //   font-family: var(--sans); font-size: 15px; font-weight: 600;
// // // // //   color: var(--text-muted); text-decoration: none;
// // // // //   border: 0.5px solid transparent;
// // // // //   transition: all 0.2s;
// // // // //   opacity: 0; transform: translateX(20px);
// // // // //   transition: background 0.2s, color 0.2s, border-color 0.2s, opacity 0.35s ease, transform 0.35s cubic-bezier(0.22,1,0.36,1);
// // // // // }
// // // // // .lp-mobile-link:nth-child(1) { transition-delay: 0.06s; }
// // // // // .lp-mobile-link:nth-child(2) { transition-delay: 0.11s; }
// // // // // .lp-mobile-link:nth-child(3) { transition-delay: 0.16s; }
// // // // // .lp-mobile-menu.open .lp-mobile-link { opacity: 1; transform: translateX(0); }
// // // // // .lp-mobile-link:hover { background: var(--surface2); color: var(--text-primary); border-color: var(--border-gold); }
// // // // // .lp-mobile-link-icon { font-size: 16px; width: 20px; text-align: center; }
// // // // // /* gold accent line */
// // // // // .lp-mobile-divider { height: 0.5px; background: linear-gradient(90deg, transparent, var(--border-gold), transparent); margin: 0.5rem 1.5rem; }
// // // // // /* CTA inside drawer */
// // // // // .lp-mobile-menu-footer {
// // // // //   padding: 1.5rem;
// // // // //   border-top: 0.5px solid var(--border-color);
// // // // //   display: flex; flex-direction: column; gap: 0.75rem;
// // // // //   opacity: 0; transform: translateY(12px);
// // // // //   transition: opacity 0.35s ease 0.22s, transform 0.35s ease 0.22s;
// // // // // }
// // // // // .lp-mobile-menu.open .lp-mobile-menu-footer { opacity: 1; transform: translateY(0); }
// // // // // .lp-mobile-cta {
// // // // //   width: 100%; padding: 14px;
// // // // //   background: linear-gradient(135deg, #D4AF37, #F0D060, #C9A227);
// // // // //   color: #0A0A0A; border: none; border-radius: 12px;
// // // // //   font-family: var(--sans); font-size: 14px; font-weight: 700;
// // // // //   letter-spacing: 0.8px; text-transform: uppercase; cursor: pointer;
// // // // //   position: relative; overflow: hidden;
// // // // // }
// // // // // .lp-mobile-cta::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.2),transparent 60%); pointer-events:none; }
// // // // // .lp-mobile-tagline {
// // // // //   text-align: center; font-size: 11px; font-weight: 500;
// // // // //   color: var(--text-muted2); letter-spacing: 0.5px;
// // // // // }

// // // // // /* trust bar — desktop override: horizontal single row */
// // // // // @media (min-width: 901px) {
// // // // //   .lp-trust {
// // // // //     flex-direction: row;
// // // // //     padding: 1.5rem 3rem;
// // // // //     gap: 2.5rem;
// // // // //   }
// // // // //   .lp-trust-items { width: auto; max-width: none; gap: 0; }
// // // // //   .lp-trust-item {
// // // // //     flex-direction: row; flex: none;
// // // // //     padding: 0 1.5rem;
// // // // //     border-right: 0.5px solid var(--border-color);
// // // // //     border-bottom: none; gap: 8px;
// // // // //   }
// // // // //   .lp-trust-item:last-child { border-right: none; }
// // // // //   .lp-trust-item-icon { width:28px; height:28px; border-radius:8px; font-size:1rem; flex-shrink:0; margin-bottom:0; }
// // // // // }

// // // // // /* mobile right cluster — hidden on desktop, shown on mobile via media query */
// // // // // .lp-mobile-cluster {
// // // // //   display: none;
// // // // //   align-items: center; gap: 10px;
// // // // // }
// // // // // .lp-theme-btn-mobile { display: flex; }
// // // // // /* desktop: hide mobile cluster */
// // // // // @media (min-width: 901px) {
// // // // //   .lp-mobile-cluster { display: none !important; }
// // // // // }

// // // // // /* ── HERO ── */
// // // // // .lp-hero {
// // // // //   min-height: 100vh;
// // // // //   display: grid; grid-template-columns: 1fr 1fr;
// // // // //   align-items: center; gap: 4rem;
// // // // //   max-width: 1180px; margin: 0 auto;
// // // // //   padding: 10rem 3rem 3rem; position: relative;
// // // // // }
// // // // // .lp-hero-left { position: relative; z-index: 2; }
// // // // // .lp-hero-eyebrow {
// // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // //   padding: 6px 14px;
// // // // //   background: var(--gold-subtle2); border: 0.5px solid var(--border-gold);
// // // // //   border-radius: 100px; font-size: 11px; font-weight: 700; color: var(--gold);
// // // // //   letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 2rem;
// // // // // }
// // // // // .lp-hero-eyebrow-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--gold); animation: pulse-gold 2s ease-in-out infinite; }
// // // // // @keyframes pulse-gold { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:0.4;transform:scale(0.7);} }

// // // // // .lp-hero-title { font-family: var(--serif); font-size: clamp(3.2rem, 5.8vw, 5.3rem); font-weight: 600; line-height: 1.02; letter-spacing: -0.8px; color: var(--text-primary); margin-bottom: 1.75rem; }
// // // // // .lp-hero-title em { font-style: italic; font-weight: 600; color: var(--gold); }
// // // // // .lp-hero-sub { font-size: 15px; font-weight: 400; color: var(--text-muted); line-height: 1.9; max-width: 470px; margin-bottom: 1rem; }
// // // // // .lp-hero-ctas { display: flex; gap: 1rem; flex-wrap: wrap; }

// // // // // .lp-btn-gold {
// // // // //   padding: 16px 32px;
// // // // //   background: linear-gradient(135deg, #D4AF37, #F0D060, #C9A227);
// // // // //   color: #0A0A0A; border: none; border-radius: 12px;
// // // // //   font-family: var(--sans); font-size: 13px; font-weight: 700;
// // // // //   letter-spacing: 1px; text-transform: uppercase;
// // // // //   cursor: pointer; position: relative; overflow: hidden;
// // // // //   transition: transform 0.2s, box-shadow 0.2s;
// // // // //   display: flex; align-items: center; gap: 8px;
// // // // // }
// // // // // .lp-btn-gold::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.25),transparent 60%); pointer-events:none; }
// // // // // .lp-btn-gold:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(212,175,55,0.3); }

// // // // // .lp-btn-ghost {
// // // // //   padding: 16px 28px; background: transparent;
// // // // //   color: var(--text-muted); border: 0.5px solid var(--border-color);
// // // // //   border-radius: 12px; font-family: var(--sans); font-size: 13px; font-weight: 600;
// // // // //   letter-spacing: 0.5px; cursor: pointer; text-decoration: none;
// // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // //   transition: border-color 0.2s, color 0.2s;
// // // // // }
// // // // // .lp-btn-ghost:hover { border-color: var(--border-gold); color: var(--text-primary); }

// // // // // /* ═══════════════════════════════════════
// // // // //    ENHANCEMENT 1 — COUNT-UP STATS
// // // // // ═══════════════════════════════════════ */
// // // // // .lp-hero-stats { display: flex; gap: 2.5rem; padding-bottom: 1.5rem; margin-bottom: 1.5rem; }
// // // // // .lp-stat { position: relative; }
// // // // // .lp-stat-num {
// // // // //   font-family: var(--serif); font-size: 2.4rem; font-weight: 600;
// // // // //   color: var(--gold); line-height: 1;
// // // // //   /* subtle gold shimmer when counting */
// // // // // }
// // // // // .lp-stat-num.counting {
// // // // //   animation: stat-shimmer 0.1s ease infinite alternate;
// // // // // }
// // // // // @keyframes stat-shimmer {
// // // // //   from { opacity: 0.7; }
// // // // //   to   { opacity: 1; }
// // // // // }
// // // // // .lp-stat-label { font-size: 11px; font-weight: 500; color: var(--text-muted2); letter-spacing: 0.5px; margin-top: 5px; }
// // // // // /* thin gold underline that fills as number counts */
// // // // // .lp-stat-bar {
// // // // //   height: 1.5px; background: var(--border-gold); margin-top: 8px;
// // // // //   border-radius: 1px; overflow: hidden;
// // // // // }
// // // // // .lp-stat-bar-fill {
// // // // //   height: 100%;
// // // // //   background: linear-gradient(90deg, var(--gold), var(--gold2));
// // // // //   border-radius: 1px;
// // // // //   width: 0%;
// // // // //   transition: width 0.05s linear;
// // // // // }

// // // // // /* ── HERO RIGHT ── */
// // // // // .lp-hero-right { position: relative; display: flex; align-items: center; justify-content: center; z-index: 2; }
// // // // // .scan-scene { position: relative; width: 380px; height: 480px; }

// // // // // /* ═══════════════════════════════════════
// // // // //    ENHANCEMENT 5 — HERO RIPPLE / SUCCESS
// // // // // ═══════════════════════════════════════ */
// // // // // /* Ripple rings that emit from phone B when scan "completes" */
// // // // // .scan-ripple {
// // // // //   position: absolute;
// // // // //   right: -10px; top: 50px;
// // // // //   width: 60px; height: 60px;
// // // // //   border-radius: 50%;
// // // // //   border: 1.5px solid rgba(212,175,55,0);
// // // // //   z-index: 10;
// // // // //   pointer-events: none;
// // // // // }
// // // // // .scan-ripple.r1 { animation: ripple-out 2.5s ease-out infinite; animation-delay: 3.2s; }
// // // // // .scan-ripple.r2 { animation: ripple-out 2.5s ease-out infinite; animation-delay: 3.6s; }
// // // // // .scan-ripple.r3 { animation: ripple-out 2.5s ease-out infinite; animation-delay: 4.0s; }
// // // // // @keyframes ripple-out {
// // // // //   0%   { transform: scale(1); border-color: rgba(212,175,55,0.6); opacity: 1; }
// // // // //   100% { transform: scale(3.5); border-color: rgba(212,175,55,0); opacity: 0; }
// // // // // }

// // // // // /* Success ping badge */
// // // // // .scan-success {
// // // // //   position: absolute;
// // // // //   right: 8px; top: 38px;
// // // // //   background: linear-gradient(135deg, #D4AF37, #F0D060);
// // // // //   color: #0A0A0A;
// // // // //   border-radius: 100px;
// // // // //   padding: 4px 10px 4px 8px;
// // // // //   font-family: var(--sans); font-size: 9px; font-weight: 700;
// // // // //   letter-spacing: 0.5px;
// // // // //   display: flex; align-items: center; gap: 5px;
// // // // //   z-index: 12; white-space: nowrap;
// // // // //   opacity: 0;
// // // // //   animation: success-pop 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards;
// // // // //   animation-delay: 3.1s;
// // // // //   box-shadow: 0 4px 16px rgba(212,175,55,0.4);
// // // // // }
// // // // // .scan-success-dot { width: 6px; height: 6px; border-radius: 50%; background: #0A0A0A; }
// // // // // @keyframes success-pop {
// // // // //   0%   { opacity: 0; transform: scale(0.5) translateY(6px); }
// // // // //   100% { opacity: 1; transform: scale(1) translateY(0); }
// // // // // }

// // // // // /* Phone A */
// // // // // .phone-scanner {
// // // // //   position: absolute; left: 0; top: 60px;
// // // // //   width: 155px; height: 270px; background: #1A1A1A;
// // // // //   border-radius: 22px; border: 1px solid rgba(255,255,255,0.1);
// // // // //   overflow: hidden; box-shadow: 0 24px 60px rgba(0,0,0,0.6);
// // // // //   z-index: 3;
// // // // //   animation: phone-a-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both; animation-delay: 0.3s;
// // // // // }
// // // // // @keyframes phone-a-intro { from{opacity:0;transform:translateX(-30px) rotate(-6deg);} to{opacity:1;transform:translateX(0) rotate(-6deg);} }
// // // // // .phone-scanner-screen { width:100%; height:100%; background:#111; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; padding:12px; }
// // // // // .scanner-viewfinder { width:100px; height:100px; border-radius:10px; position:relative; background:rgba(212,175,55,0.04); }
// // // // // .scanner-viewfinder::before,.scanner-viewfinder::after { content:''; position:absolute; width:20px; height:20px; border-color:#D4AF37; border-style:solid; }
// // // // // .scanner-viewfinder::before { top:0;left:0;border-width:2px 0 0 2px;border-radius:6px 0 0 0; }
// // // // // .scanner-viewfinder::after  { bottom:0;right:0;border-width:0 2px 2px 0;border-radius:0 0 6px 0; }
// // // // // .vf-corner-tr { position:absolute;top:0;right:0;width:20px;height:20px;border-top:2px solid #D4AF37;border-right:2px solid #D4AF37;border-radius:0 6px 0 0; }
// // // // // .vf-corner-bl { position:absolute;bottom:0;left:0;width:20px;height:20px;border-bottom:2px solid #D4AF37;border-left:2px solid #D4AF37;border-radius:0 0 0 6px; }
// // // // // .scan-laser { position:absolute;left:4px;right:4px;height:2px;background:linear-gradient(90deg,transparent,#D4AF37,transparent);border-radius:1px;top:4px;animation:laser-sweep 2s ease-in-out infinite;animation-delay:1.2s; }
// // // // // @keyframes laser-sweep { 0%{top:8px;opacity:0;} 10%{opacity:1;} 90%{opacity:1;} 100%{top:88px;opacity:0;} }

// // // // // /* scanner screen flashes green briefly on scan success */
// // // // // .scanner-success-flash {
// // // // //   position: absolute; inset: 0;
// // // // //   background: rgba(212,175,55,0.12);
// // // // //   opacity: 0;
// // // // //   animation: flash-once 0.4s ease forwards;
// // // // //   animation-delay: 3.0s;
// // // // //   pointer-events: none; z-index: 10;
// // // // // }
// // // // // @keyframes flash-once { 0%{opacity:0;} 40%{opacity:1;} 100%{opacity:0;} }

// // // // // .scanner-label { font-size:9px;font-weight:600;color:rgba(255,255,255,0.4);letter-spacing:1px;text-align:center;text-transform:uppercase; }
// // // // // .mini-qr { position:absolute;inset:10px;display:grid;grid-template-columns:repeat(6,1fr);grid-template-rows:repeat(6,1fr);gap:1.5px;opacity:0.6; }
// // // // // .mini-qr-cell { border-radius:1px; }

// // // // // /* Phone B */
// // // // // .phone-profile {
// // // // //   position:absolute;right:0;top:0;width:175px;height:310px;background:#0E0E0E;
// // // // //   border-radius:24px;border:1px solid rgba(212,175,55,0.2);overflow:hidden;
// // // // //   box-shadow:0 32px 80px rgba(0,0,0,0.7),0 0 0 1px rgba(212,175,55,0.08);
// // // // //   z-index:4;
// // // // //   animation:phone-b-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:0.5s;
// // // // // }
// // // // // @keyframes phone-b-intro { from{opacity:0;transform:translateY(30px) rotate(4deg);} to{opacity:1;transform:translateY(0) rotate(4deg);} }
// // // // // .profile-card-wrap { width:100%;height:100%;animation:card-reveal 0.7s cubic-bezier(0.22,1,0.36,1) both;animation-delay:2s; }
// // // // // @keyframes card-reveal { from{transform:translateY(40px);opacity:0;} to{transform:translateY(0);opacity:1;} }
// // // // // .profile-banner { width:100%;height:110px;background:linear-gradient(160deg,#1a1a1a,#2a2020);position:relative;overflow:hidden; }
// // // // // .profile-banner-img { width:100%;height:100%;background:linear-gradient(160deg,#1e1a10 0%,#2a2010 50%,#181818 100%); }
// // // // // .profile-banner-fade { position:absolute;bottom:0;left:0;right:0;height:50px;background:linear-gradient(transparent,#0E0E0E); }
// // // // // .profile-avatar-ring { position:absolute;bottom:-20px;left:14px;width:44px;height:44px;border-radius:50%;border:2px solid #0E0E0E;background:#222;overflow:hidden;box-shadow:0 0 0 1.5px rgba(212,175,55,0.5);animation:avatar-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:2.4s; }
// // // // // @keyframes avatar-pop { from{transform:scale(0.3);opacity:0;} to{transform:scale(1);opacity:1;} }
// // // // // .profile-avatar-inner { width:100%;height:100%;background:linear-gradient(145deg,#2a2010,#3a3020);display:flex;align-items:center;justify-content:center;font-family:'Cormorant Garamond',serif;font-size:14px;font-weight:600;color:rgba(212,175,55,0.8);letter-spacing:1px; }
// // // // // .profile-info { padding:28px 14px 12px; }
// // // // // .profile-info-name { font-family:'Cormorant Garamond',serif;font-size:16px;font-weight:600;color:#FFF;line-height:1.2;margin-bottom:2px; }
// // // // // .profile-info-name em { font-style:italic;color:#D4AF37; }
// // // // // .profile-info-role { font-size:8px;font-weight:600;color:rgba(255,255,255,0.45);letter-spacing:0.3px;margin-bottom:10px; }
// // // // // .profile-tiles { display:grid;grid-template-columns:1fr 1fr 1fr;gap:5px;margin-bottom:8px; }
// // // // // .profile-tile { background:#1A1A1A;border:0.5px solid rgba(255,255,255,0.06);border-radius:8px;padding:7px 6px;display:flex;flex-direction:column;gap:4px;animation:tile-slide 0.3s ease both; }
// // // // // .profile-tile:nth-child(1){animation-delay:2.5s;} .profile-tile:nth-child(2){animation-delay:2.65s;} .profile-tile:nth-child(3){animation-delay:2.8s;}
// // // // // @keyframes tile-slide { from{opacity:0;transform:translateY(6px);} to{opacity:1;transform:translateY(0);} }
// // // // // .tile-dot { width:12px;height:12px;border-radius:50%;background:rgba(212,175,55,0.15);display:flex;align-items:center;justify-content:center; }
// // // // // .tile-dot-inner { width:5px;height:5px;border-radius:50%;background:#D4AF37; }
// // // // // .tile-text { font-size:7px;color:rgba(255,255,255,0.4);line-height:1.3;font-weight:500; }
// // // // // .profile-save-btn { width:100%;padding:8px;background:linear-gradient(135deg,#D4AF37,#F0D060);color:#0A0A0A;border:none;border-radius:8px;font-size:8px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;cursor:default;display:flex;align-items:center;justify-content:center;gap:4px;animation:tile-slide 0.3s ease both;animation-delay:2.95s; }

// // // // // .beam-line { position:absolute;top:165px;left:148px;width:90px;height:1px;background:linear-gradient(90deg,#D4AF37,rgba(212,175,55,0));transform-origin:left center;animation:beam-appear 0.4s ease both;animation-delay:1.5s;z-index:5; }
// // // // // @keyframes beam-appear { from{opacity:0;} to{opacity:1;} }
// // // // // .beam-particle { position:absolute;top:160px;left:148px;width:6px;height:6px;border-radius:50%;background:#D4AF37;animation:particle-travel 2s ease-in-out infinite;animation-delay:1.6s;z-index:6; }
// // // // // @keyframes particle-travel { 0%{left:148px;opacity:0;transform:scale(0.5);} 10%{opacity:1;transform:scale(1);} 90%{opacity:1;} 100%{left:228px;opacity:0;transform:scale(0.5);} }
// // // // // .hero-orb-1 { position:absolute;width:500px;height:500px;border-radius:50%;background:radial-gradient(circle,rgba(212,175,55,0.06) 0%,transparent 65%);top:-100px;right:-100px;pointer-events:none;z-index:0; }

// // // // // /* ── SECTION SHARED ── */
// // // // // .lp-section { padding: 5rem 3rem; position: relative; }
// // // // // .lp-section-inner { max-width: 1180px; margin: 0 auto; }
// // // // // .lp-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--gold);margin-bottom:1.25rem;text-align:center; }
// // // // // .lp-section-title { font-family:var(--serif);font-size:clamp(2.3rem,4vw,3.5rem);font-weight:600;line-height:1.08;letter-spacing:-0.4px;color:var(--text-primary);text-align:center;margin-bottom:1.25rem; }
// // // // // .lp-section-title em { font-style:italic;color:var(--gold); }
// // // // // .lp-section-sub { font-size:15px;font-weight:400;color:var(--text-muted);text-align:center;max-width:520px;margin:0 auto 4rem;line-height:1.9; }

// // // // // .gold-rule { height:0.5px;background:linear-gradient(90deg,transparent,rgba(212,175,55,0.3),transparent); }
// // // // // .lp-light .gold-rule { background:linear-gradient(90deg,transparent,rgba(160,120,10,0.2),transparent); }

// // // // // /* ── TRUST BAR ── */
// // // // // .lp-trust {
// // // // //   padding: 1.5rem 2rem;
// // // // //   display: flex; flex-direction: column; align-items: center;
// // // // //   gap: 1rem; background: var(--surface);
// // // // // }
// // // // // .lp-trust-label {
// // // // //   font-size: 10px; font-weight: 600; color: var(--text-muted2);
// // // // //   letter-spacing: 2.5px; text-transform: uppercase;
// // // // // }
// // // // // .lp-trust-items {
// // // // //   display: flex; align-items: stretch;
// // // // //   justify-content: center;
// // // // //   gap: 0; flex-wrap: nowrap; width: 100%; max-width: 560px;
// // // // // }
// // // // // .lp-trust-item {
// // // // //   display: flex; flex-direction: column; align-items: center; gap: 5px;
// // // // //   flex: 1; padding: 0.6rem 0.5rem;
// // // // //   border-right: 0.5px solid var(--border-color);
// // // // //   font-size: 11px; font-weight: 600; color: var(--text-muted);
// // // // //   letter-spacing: 0.2px; text-align: center;
// // // // // }
// // // // // .lp-trust-item:last-child { border-right: none; }
// // // // // .lp-trust-item-icon {
// // // // //   font-size: 1.25rem; line-height: 1;
// // // // //   width: 36px; height: 36px; border-radius: 10px;
// // // // //   background: var(--gold-subtle); border: 0.5px solid var(--border-gold);
// // // // //   display: flex; align-items: center; justify-content: center;
// // // // //   margin-bottom: 2px;
// // // // // }
// // // // // .lp-trust-sep { display: none; }

// // // // // /* ═══════════════════════════════════════
// // // // //    ENHANCEMENT 2 — PROGRESS CONNECTOR
// // // // //    The SVG arrow between steps fills on scroll
// // // // // ═══════════════════════════════════════ */
// // // // // .lp-how-timeline {
// // // // //   display: grid;
// // // // //   grid-template-columns: 1fr 72px 1fr 72px 1fr;
// // // // //   align-items: start; gap: 0; margin-top: 1rem;
// // // // // }
// // // // // .lp-how-bridge {
// // // // //   display: flex; align-items: flex-start; justify-content: center; padding-top: 2.6rem;
// // // // // }
// // // // // /* The SVG path for the arrow */
// // // // // .how-arrow-path {
// // // // //   stroke: var(--border-gold);
// // // // //   stroke-dasharray: 60;
// // // // //   stroke-dashoffset: 60;
// // // // //   transition: stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1), stroke 0.4s ease;
// // // // // }
// // // // // .how-arrow-path.filled {
// // // // //   stroke-dashoffset: 0;
// // // // //   stroke: var(--gold);
// // // // // }
// // // // // /* arrowhead */
// // // // // .how-arrowhead {
// // // // //   fill: var(--border-gold);
// // // // //   transition: fill 0.4s ease 0.7s;
// // // // // }
// // // // // .how-arrow-path.filled + .how-arrowhead,
// // // // // .how-arrowhead.filled {
// // // // //   fill: var(--gold);
// // // // // }

// // // // // .lp-how-card {
// // // // //   background: var(--surface);
// // // // //   border: 0.5px solid var(--border-color);
// // // // //   border-radius: 20px; padding: 2.2rem 1.8rem 2rem;
// // // // //   position: relative; overflow: hidden;
// // // // //   transition: border-color 0.3s, transform 0.3s;
// // // // //   cursor: default;
// // // // // }
// // // // // .lp-how-card::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 80% 60% at 50% -10%,var(--gold-subtle2) 0%,transparent 70%);opacity:0;transition:opacity 0.3s; }
// // // // // .lp-how-card:hover { border-color: var(--border-gold); transform: translateY(-4px); }
// // // // // .lp-how-card:hover::before { opacity: 1; }
// // // // // .lp-how-num { font-family:var(--serif);font-size:7rem;font-weight:700;color:var(--gold-subtle2);line-height:1;position:absolute;bottom:-0.5rem;right:0.8rem;letter-spacing:-3px;pointer-events:none;user-select:none;opacity:0.6; }
// // // // // .lp-light .lp-how-num { opacity:1;color:rgba(160,120,10,0.1); }
// // // // // .lp-how-icon-ring { width:56px;height:56px;border-radius:16px;background:var(--gold-subtle);border:1px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:1.6rem;margin-bottom:1.5rem;position:relative; }
// // // // // .lp-how-icon-ring::after { content:'';position:absolute;inset:-4px;border-radius:20px;border:1px dashed var(--border-gold);animation:lp-spin-slow 12s linear infinite; }
// // // // // @keyframes lp-spin-slow { to{transform:rotate(360deg);} }
// // // // // .lp-how-step-label { font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:var(--gold);margin-bottom:0.6rem; }
// // // // // .lp-how-title { font-family:var(--serif);font-size:1.65rem;font-weight:600;color:var(--text-primary);line-height:1.15;margin-bottom:0.75rem; }
// // // // // .lp-how-desc { font-size:13.5px;color:var(--text-muted);line-height:1.8; }

// // // // // /* ═══════════════════════════════════════
// // // // //    ENHANCEMENT 4 — BENTO SHIMMER + SOCIAL PILL STAGGER
// // // // // ═══════════════════════════════════════ */
// // // // // .lp-feat-bento { display:grid;grid-template-columns:repeat(4,1fr);grid-template-rows:auto auto;gap:10px;margin-top:1rem; }

// // // // // .lp-feat-bcard {
// // // // //   background: var(--surface);
// // // // //   border: 0.5px solid var(--border-color);
// // // // //   border-radius: 18px; padding: 1.8rem 1.6rem;
// // // // //   position: relative; overflow: hidden;
// // // // //   transition: border-color 0.3s, transform 0.25s, background 0.3s;
// // // // //   cursor: default;
// // // // // }
// // // // // /* shimmer sweep on hover */
// // // // // .lp-feat-bcard::before {
// // // // //   content: '';
// // // // //   position: absolute;
// // // // //   top: 0; left: -100%; width: 60%; height: 100%;
// // // // //   background: linear-gradient(105deg, transparent, rgba(212,175,55,0.06), transparent);
// // // // //   transition: left 0s;
// // // // //   pointer-events: none; z-index: 1;
// // // // // }
// // // // // .lp-feat-bcard:hover::before {
// // // // //   left: 160%;
// // // // //   transition: left 0.6s ease;
// // // // // }
// // // // // .lp-feat-bcard:hover { border-color:var(--border-gold); background:var(--surface2); transform:translateY(-3px); }
// // // // // .lp-feat-bcard.hero { grid-column:span 2; background:var(--surface2); border-color:var(--border-gold); padding:2.4rem 2.2rem; }
// // // // // .lp-feat-bcard.tall { grid-row:span 2; display:flex; flex-direction:column; }
// // // // // .lp-feat-bcard::after { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:transparent;transition:background 0.3s; }
// // // // // .lp-feat-bcard:hover::after,.lp-feat-bcard.hero::after { background:linear-gradient(90deg,transparent,var(--border-gold),transparent); }

// // // // // .lp-feat-bicon { width:44px;height:44px;border-radius:12px;background:var(--gold-subtle);border:0.5px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:1.3rem;margin-bottom:1.25rem;flex-shrink:0; }
// // // // // .lp-feat-bcard.hero .lp-feat-bicon { width:52px;height:52px;font-size:1.6rem;border-radius:14px; }
// // // // // .lp-feat-btag { display:inline-block;font-size:8.5px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--gold);margin-bottom:0.5rem;opacity:0.8; }
// // // // // .lp-feat-btitle { font-family:var(--serif);font-size:1.3rem;font-weight:600;color:var(--text-primary);line-height:1.15;margin-bottom:0.5rem; }
// // // // // .lp-feat-bcard.hero .lp-feat-btitle { font-size:1.65rem; }
// // // // // .lp-feat-bdesc { font-size:13px;color:var(--text-muted);line-height:1.8;flex:1; }

// // // // // .lp-feat-wallet { display:flex;gap:6px;margin-top:1.25rem; }
// // // // // .lp-wallet-chip { height:26px;border-radius:6px;flex:1;background:var(--gold-subtle);border:0.5px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:600;color:var(--gold);letter-spacing:0.5px;position:relative;overflow:hidden; }
// // // // // /* wallet chip shimmer */
// // // // // .lp-wallet-chip::after { content:'';position:absolute;top:0;left:-100%;width:50%;height:100%;background:linear-gradient(90deg,transparent,rgba(212,175,55,0.2),transparent);animation:chip-shine 3s ease-in-out infinite; }
// // // // // .lp-wallet-chip:nth-child(2)::after { animation-delay:0.4s; }
// // // // // .lp-wallet-chip:nth-child(3)::after { animation-delay:0.8s; }
// // // // // @keyframes chip-shine { 0%,70%{left:-100%;} 100%{left:200%;} }

// // // // // /* social pills stagger in when card enters viewport */
// // // // // .lp-feat-socials { display:flex;flex-wrap:wrap;gap:6px;margin-top:1.1rem; }
// // // // // .lp-social-pill {
// // // // //   padding: 4px 10px;
// // // // //   background: var(--gold-subtle); border: 0.5px solid var(--border-gold);
// // // // //   border-radius: 100px; font-size: 10px; font-weight: 500; color: var(--text-muted);
// // // // //   opacity: 0; transform: translateY(6px) scale(0.94);
// // // // //   transition: opacity 0.35s ease, transform 0.35s ease, background 0.2s, color 0.2s;
// // // // // }
// // // // // .lp-social-pill:hover { background: var(--gold-subtle2); color: var(--gold); }
// // // // // /* stagger delays via nth-child */
// // // // // .lp-social-pill:nth-child(1) { transition-delay: 0.05s; }
// // // // // .lp-social-pill:nth-child(2) { transition-delay: 0.12s; }
// // // // // .lp-social-pill:nth-child(3) { transition-delay: 0.19s; }
// // // // // .lp-social-pill:nth-child(4) { transition-delay: 0.26s; }
// // // // // .lp-social-pill:nth-child(5) { transition-delay: 0.33s; }

// // // // // /* pills animate in when parent card is visible */
// // // // // .lp-feat-bcard.pills-visible .lp-social-pill {
// // // // //   opacity: 1; transform: translateY(0) scale(1);
// // // // // }

// // // // // .lp-qr-art { margin-top:auto;padding-top:1.5rem;display:grid;grid-template-columns:repeat(5,1fr);grid-template-rows:repeat(5,1fr);gap:3px;opacity:0.2;transition:opacity 0.3s; }
// // // // // .lp-feat-bcard.tall:hover .lp-qr-art { opacity:0.45; }
// // // // // .lp-qr-cell { border-radius:2px; }

// // // // // /* ── USE CASES ── */
// // // // // .lp-use-tabs { display:flex;flex-wrap:wrap;gap:0.5rem;justify-content:center;margin-bottom:3rem; }
// // // // // .lp-use-tab { padding:8px 18px;border-radius:100px;font-size:13px;font-weight:600;cursor:pointer;transition:all 0.2s;font-family:var(--sans);display:flex;align-items:center;gap:6px; }
// // // // // .lp-use-panel { display:grid;grid-template-columns:1fr 1fr;gap:3rem;align-items:center;background:var(--surface);border:0.5px solid var(--border-color);border-radius:24px;padding:3rem;position:relative;overflow:hidden; }
// // // // // .lp-use-panel::before { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,var(--border-gold),transparent); }
// // // // // .lp-use-badge { display:inline-flex;align-items:center;gap:8px;padding:6px 14px;border-radius:100px;margin-bottom:1.5rem;font-size:12px;font-weight:700;background:var(--gold-subtle);color:var(--gold);border:0.5px solid var(--border-gold); }
// // // // // .lp-use-tagline { font-family:var(--serif);font-size:2.2rem;font-weight:600;color:var(--text-primary);line-height:1.1;margin-bottom:1rem; }
// // // // // .lp-use-desc { font-size:14px;font-weight:400;color:var(--text-muted);line-height:1.9;margin-bottom:2rem; }

// // // // // /* use case panel transition on tab switch */
// // // // // .lp-use-content { animation: tab-fade 0.35s cubic-bezier(0.22,1,0.36,1) both; }
// // // // // @keyframes tab-fade { from{opacity:0;transform:translateY(10px);} to{opacity:1;transform:translateY(0);} }

// // // // // .lp-use-highlights { display:flex;flex-direction:column;gap:0.75rem; }
// // // // // .lp-use-hl { display:flex;align-items:center;gap:12px;background:var(--surface2);border:0.5px solid var(--border-color);border-radius:12px;padding:1rem 1.25rem;animation:hl-slide 0.3s ease both; }
// // // // // .lp-use-hl:nth-child(1){animation-delay:0.05s;} .lp-use-hl:nth-child(2){animation-delay:0.12s;} .lp-use-hl:nth-child(3){animation-delay:0.19s;} .lp-use-hl:nth-child(4){animation-delay:0.26s;}
// // // // // @keyframes hl-slide { from{opacity:0;transform:translateX(12px);} to{opacity:1;transform:translateX(0);} }
// // // // // .lp-use-hl-check { width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;background:var(--gold-subtle);color:var(--gold); }
// // // // // .lp-use-hl-text { font-size:13px;font-weight:500;color:var(--text-muted); }

// // // // // /* ── PRICING + CTA MERGED ── */
// // // // // .lp-pricing-cta { padding:5rem 3rem;position:relative;overflow:hidden; }
// // // // // .lp-pricing-cta::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 70% 80% at 50% 50%,var(--gold-subtle) 0%,transparent 70%);pointer-events:none; }
// // // // // .lp-pricing-cta-inner { max-width:1180px;margin:0 auto;display:grid;grid-template-columns:1fr 1.1fr;gap:4rem;align-items:center;position:relative; }
// // // // // .lp-pricing-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--gold);margin-bottom:1.25rem; }
// // // // // .lp-pricing-headline { font-family:var(--serif);font-size:clamp(2.8rem,5vw,4.2rem);font-weight:600;line-height:1.05;color:var(--text-primary);margin-bottom:1.25rem;letter-spacing:-0.5px; }
// // // // // .lp-pricing-headline em { font-style:italic;color:var(--gold); }
// // // // // .lp-pricing-sub { font-size:15px;color:var(--text-muted);line-height:1.9;margin-bottom:2.5rem;max-width:400px; }
// // // // // .lp-pricing-perks { display:flex;flex-direction:column;gap:0.6rem;margin-bottom:2.5rem; }
// // // // // .lp-pricing-perk { display:flex;align-items:center;gap:10px;font-size:14px;font-weight:500;color:var(--text-muted); }
// // // // // .lp-perk-tick { width:20px;height:20px;border-radius:50%;flex-shrink:0;background:var(--gold-subtle);border:0.5px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:9px;color:var(--gold);font-weight:700; }

// // // // // .lp-price-card { background:var(--surface);border:0.5px solid var(--border-gold);border-radius:24px;padding:2.5rem;position:relative;overflow:hidden; }
// // // // // .lp-price-card::before { content:'';position:absolute;top:0;left:10%;right:10%;height:1px;background:linear-gradient(90deg,transparent,var(--gold),transparent); }
// // // // // .lp-price-card-top { display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:1.5rem; }
// // // // // .lp-price-badge { display:inline-block;background:linear-gradient(135deg,#D4AF37,#F0D060);color:#0A0A0A;border-radius:100px;padding:4px 14px;font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase; }
// // // // // .lp-price-amount { text-align:right; }
// // // // // .lp-price-num { font-family:var(--serif);font-size:4rem;font-weight:600;color:var(--text-primary);line-height:1;letter-spacing:-2px; }
// // // // // .lp-price-period { font-size:10px;font-weight:600;color:var(--text-muted2);letter-spacing:2px;text-transform:uppercase;margin-top:4px; }
// // // // // .lp-price-divider { height:0.5px;background:var(--border-color);margin:1.25rem 0; }
// // // // // .lp-price-list { list-style:none;display:flex;flex-direction:column;gap:0;margin-bottom:1.75rem; }
// // // // // .lp-price-item { display:flex;align-items:center;gap:10px;padding:0.55rem 0;font-size:13px;font-weight:500;color:var(--text-muted);border-bottom:0.5px solid var(--border-color); }
// // // // // .lp-price-item:last-child { border-bottom:none; }
// // // // // .lp-price-check { color:var(--gold);font-size:11px;flex-shrink:0; }

// // // // // /* ── FOOTER ── */
// // // // // .lp-footer { padding:3rem 3rem 4rem;border-top:0.5px solid var(--border-color);background:var(--surface); }
// // // // // .lp-footer-inner { max-width:1180px;margin:0 auto;display:flex;flex-direction:column;align-items:center;gap:1rem; }
// // // // // .lp-footer-logo { display:flex;align-items:center;gap:10px;font-family:var(--sans);font-size:15px;font-weight:600;color:var(--text-primary); }
// // // // // .lp-footer-logo em { color:var(--gold);font-style:normal;font-weight:700; }
// // // // // .lp-footer-tagline { font-size:13px;font-weight:400;color:var(--text-muted2); }
// // // // // .lp-footer-copy { font-size:12px;font-weight:400;color:var(--text-muted2);opacity:0.6; }

// // // // // /* ── THEME TOGGLE ── */
// // // // // .lp-theme-btn { width:40px;height:40px;border-radius:50%;border:0.5px solid var(--border-gold);background:var(--gold-subtle);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background 0.2s,transform 0.15s;flex-shrink:0; }
// // // // // .lp-theme-btn:hover { background:var(--gold-subtle2);transform:scale(1.08); }

// // // // // /* ── TABLET ── */
// // // // // @media (max-width: 900px) {
// // // // //   .lp-hero { grid-template-columns:1fr; text-align:center; padding:8rem 1.5rem 4rem; }
// // // // //   .lp-hero-sub { margin:0 auto 1rem; }
// // // // //   .lp-hero-ctas { justify-content:center; }
// // // // //   .lp-hero-stats { justify-content:center; }
// // // // //   .lp-hero-right { margin-top:3rem; }
// // // // //   .scan-scene { width:300px; height:380px; transform:scale(0.85); }
// // // // //   .lp-how-timeline { grid-template-columns:1fr; }
// // // // //   .lp-how-bridge { display:none; }
// // // // //   .lp-feat-bento { grid-template-columns:1fr 1fr; }
// // // // //   .lp-feat-bcard.hero { grid-column:span 2; }
// // // // //   .lp-feat-bcard.tall { grid-row:span 1; }
// // // // //   .lp-use-panel { grid-template-columns:1fr; }
// // // // //   .lp-pricing-cta-inner { grid-template-columns:1fr; }
// // // // //   .lp-nav { padding:1rem 1.5rem; }
// // // // //   .lp-nav.scrolled { padding:0.75rem 1.5rem; }
// // // // //   .lp-section { padding:4rem 1.5rem; }
// // // // //   .lp-pricing-cta { padding:4rem 1.5rem; }
// // // // //   .lp-hamburger { display:flex; }
// // // // //   .lp-nav-links { display:none; }
// // // // //   .lp-mobile-cluster { display:flex; }
// // // // //   .lp-trust { padding:1.25rem 1.5rem; }
// // // // // }
// // // // // /* ── MOBILE ── */
// // // // // @media (max-width: 600px) {
// // // // //   .lp-feat-bento { grid-template-columns:1fr; }
// // // // //   .lp-feat-bcard.hero { grid-column:span 1; }
// // // // //   .lp-hero-title { font-size:clamp(2.6rem,9vw,3.5rem); }
// // // // //   .lp-hero-stats { gap:1.5rem; }
// // // // //   .lp-stat-num { font-size:2rem; }
// // // // //   .lp-section { padding:3.5rem 1.25rem; }
// // // // //   .lp-section-title { font-size:clamp(2rem,7vw,2.8rem); }
// // // // //   .lp-use-panel { padding:1.75rem 1.25rem; }
// // // // //   .lp-pricing-cta { padding:3rem 1.25rem; }
// // // // //   .lp-pricing-headline { font-size:clamp(2.4rem,8vw,3rem); }
// // // // //   .lp-hero-ctas { flex-direction:column; align-items:center; }
// // // // //   .lp-hero-ctas .lp-btn-gold,
// // // // //   .lp-hero-ctas .lp-btn-ghost { width:100%; justify-content:center; }
// // // // //   .lp-trust-item { font-size:10px; }
// // // // //   .lp-trust-item-icon { width:30px; height:30px; font-size:1.1rem; }
// // // // // }
// // // // // `;

// // // // // /* ─────────────────────────────────────────
// // // // //    ENHANCEMENT 1 — COUNT-UP HOOK
// // // // // ───────────────────────────────────────── */
// // // // // function useCountUp(target, duration = 1800, suffix = "", started = false) {
// // // // //     const [display, setDisplay] = useState("0");
// // // // //     const [progress, setProgress] = useState(0);
// // // // //     const frameRef = useRef(null);

// // // // //     useEffect(() => {
// // // // //         if (!started) return;
// // // // //         // parse numeric target
// // // // //         const num = parseFloat(target.replace(/[^0-9.]/g, ""));
// // // // //         if (!num) { setDisplay(target); setProgress(100); return; }

// // // // //         const startTime = performance.now();
// // // // //         const animate = (now) => {
// // // // //             const elapsed = now - startTime;
// // // // //             const pct = Math.min(elapsed / duration, 1);
// // // // //             // easeOutExpo
// // // // //             const eased = pct === 1 ? 1 : 1 - Math.pow(2, -10 * pct);
// // // // //             const current = num * eased;
// // // // //             const prog = Math.round(pct * 100);

// // // // //             // format
// // // // //             let formatted;
// // // // //             if (Number.isInteger(num)) {
// // // // //                 formatted = Math.round(current).toString();
// // // // //             } else {
// // // // //                 formatted = current.toFixed(0);
// // // // //             }
// // // // //             setDisplay(formatted + suffix);
// // // // //             setProgress(prog);

// // // // //             if (pct < 1) {
// // // // //                 frameRef.current = requestAnimationFrame(animate);
// // // // //             }
// // // // //         };
// // // // //         frameRef.current = requestAnimationFrame(animate);
// // // // //         return () => cancelAnimationFrame(frameRef.current);
// // // // //     }, [started, target, duration, suffix]);

// // // // //     return { display, progress };
// // // // // }

// // // // // /* ─────────────────────────────────────────
// // // // //    STAT ITEM
// // // // // ───────────────────────────────────────── */
// // // // // function StatItem({ value, suffix, label, started, delay = 0 }) {
// // // // //     const [go, setGo] = useState(false);
// // // // //     useEffect(() => {
// // // // //         if (!started) return;
// // // // //         const t = setTimeout(() => setGo(true), delay);
// // // // //         return () => clearTimeout(t);
// // // // //     }, [started, delay]);

// // // // //     const { display, progress } = useCountUp(value, 1600, suffix, go);
// // // // //     const isCounting = go && progress < 100;

// // // // //     return (
// // // // //         <div className="lp-stat">
// // // // //             <div className={`lp-stat-num${isCounting ? " counting" : ""}`}>
// // // // //                 {value === "0" ? "0" : display}
// // // // //             </div>
// // // // //             <div className="lp-stat-label">{label}</div>
// // // // //             <div className="lp-stat-bar">
// // // // //                 <div className="lp-stat-bar-fill" style={{ width: `${progress}%` }} />
// // // // //             </div>
// // // // //         </div>
// // // // //     );
// // // // // }

// // // // // /* ─────────────────────────────────────────
// // // // //    QR / SCAN SCENE
// // // // // ───────────────────────────────────────── */
// // // // // const QR_PATTERN = [
// // // // //     [1, 1, 1, 0, 1, 0], [1, 0, 1, 1, 0, 1], [1, 1, 1, 0, 1, 1], [0, 1, 0, 1, 0, 0], [1, 0, 1, 1, 1, 0], [0, 1, 0, 0, 1, 1],
// // // // // ];

// // // // // function ScanScene() {
// // // // //     return (
// // // // //         <div className="scan-scene">
// // // // //             <div className="hero-orb-1" />

// // // // //             {/* Phone A — scanner */}
// // // // //             <div className="phone-scanner">
// // // // //                 <div className="phone-scanner-screen">
// // // // //                     <div className="scanner-viewfinder">
// // // // //                         <div className="vf-corner-tr" />
// // // // //                         <div className="vf-corner-bl" />
// // // // //                         <div className="scan-laser" />
// // // // //                         <div className="mini-qr">
// // // // //                             {QR_PATTERN.map((row, ri) =>
// // // // //                                 row.map((cell, ci) => (
// // // // //                                     <div key={`${ri}-${ci}`} className="mini-qr-cell"
// // // // //                                         style={{ background: cell ? "rgba(212,175,55,0.5)" : "transparent" }} />
// // // // //                                 ))
// // // // //                             )}
// // // // //                         </div>
// // // // //                         {/* Enhancement 5: flash overlay */}
// // // // //                         <div className="scanner-success-flash" />
// // // // //                     </div>
// // // // //                     <div className="scanner-label">Point at QR code</div>
// // // // //                 </div>
// // // // //             </div>

// // // // //             <div className="beam-line" />
// // // // //             <div className="beam-particle" />

// // // // //             {/* Enhancement 5: ripple rings */}
// // // // //             <div className="scan-ripple r1" />
// // // // //             <div className="scan-ripple r2" />
// // // // //             <div className="scan-ripple r3" />
// // // // //             {/* Enhancement 5: success badge */}
// // // // //             <div className="scan-success">
// // // // //                 <div className="scan-success-dot" />
// // // // //                 Contact Saved ✓
// // // // //             </div>

// // // // //             {/* Phone B */}
// // // // //             <div className="phone-profile">
// // // // //                 <div className="profile-card-wrap">
// // // // //                     <div className="profile-banner">
// // // // //                         <div className="profile-banner-img" />
// // // // //                         <div className="profile-banner-fade" />
// // // // //                         <div className="profile-avatar-ring">
// // // // //                             <div className="profile-avatar-inner">UN</div>
// // // // //                         </div>
// // // // //                     </div>
// // // // //                     <div className="profile-info">
// // // // //                         <div className="profile-info-name">User <em>Name</em></div>
// // // // //                         <div className="profile-info-role">Director · Company Name</div>
// // // // //                         <div className="profile-tiles">
// // // // //                             {[["📞", "Phone"], ["✉️", "Email"], ["📍", "Delhi"]].map(([, label]) => (
// // // // //                                 <div className="profile-tile" key={label}>
// // // // //                                     <div className="tile-dot"><div className="tile-dot-inner" /></div>
// // // // //                                     <div className="tile-text">{label}</div>
// // // // //                                 </div>
// // // // //                             ))}
// // // // //                         </div>
// // // // //                         <button className="profile-save-btn">↓ Save Contact</button>
// // // // //                         <div className="profile-tiles" style={{ marginTop: "1rem" }}>
// // // // //                             {[["🌐", "Website"], ["📄", "Resume"], ["ⓕ", "Facebook"]].map(([, label]) => (
// // // // //                                 <div className="profile-tile" key={label}>
// // // // //                                     <div className="tile-dot"><div className="tile-dot-inner" /></div>
// // // // //                                     <div className="tile-text">{label}</div>
// // // // //                                 </div>
// // // // //                             ))}
// // // // //                         </div>
// // // // //                     </div>
// // // // //                 </div>
// // // // //             </div>
// // // // //         </div>
// // // // //     );
// // // // // }

// // // // // /* ─────────────────────────────────────────
// // // // //    PROGRESS ARROW BRIDGE (Enhancement 2)
// // // // // ───────────────────────────────────────── */
// // // // // function ArrowBridge({ filled }) {
// // // // //     return (
// // // // //         <div className="lp-how-bridge">
// // // // //             <svg width="36" height="28" viewBox="0 0 36 28" fill="none" overflow="visible">
// // // // //                 {/* shaft */}
// // // // //                 <path
// // // // //                     d="M2 14 L26 14"
// // // // //                     className={`how-arrow-path${filled ? " filled" : ""}`}
// // // // //                     strokeWidth="1.5"
// // // // //                     strokeLinecap="round"
// // // // //                     fill="none"
// // // // //                     style={{ strokeDasharray: 28, strokeDashoffset: filled ? 0 : 28, transition: "stroke-dashoffset 0.8s cubic-bezier(0.22,1,0.36,1), stroke 0.4s ease" }}
// // // // //                 />
// // // // //                 {/* head */}
// // // // //                 <path
// // // // //                     d="M20 7 L28 14 L20 21"
// // // // //                     className={`how-arrow-path${filled ? " filled" : ""}`}
// // // // //                     strokeWidth="1.5"
// // // // //                     strokeLinecap="round"
// // // // //                     strokeLinejoin="round"
// // // // //                     fill="none"
// // // // //                     style={{ strokeDasharray: 24, strokeDashoffset: filled ? 0 : 24, transition: "stroke-dashoffset 0.6s cubic-bezier(0.22,1,0.36,1) 0.3s, stroke 0.4s ease 0.3s" }}
// // // // //                 />
// // // // //             </svg>
// // // // //         </div>
// // // // //     );
// // // // // }

// // // // // /* ─────────────────────────────────────────
// // // // //    USE CASES
// // // // // ───────────────────────────────────────── */
// // // // // const USE_CASES = [
// // // // //     { icon: "👔", role: "Job Seekers", tagline: "Land your next opportunity faster", desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one elegant scan.", highlights: ["Resume attached & downloadable", "LinkedIn & GitHub links", "QR code for instant sharing", "Save contact in one tap"] },
// // // // //     { icon: "💼", role: "Freelancers", tagline: "Win clients before you even speak", desc: "Share everything a client needs — portfolio, social proof, and contact — all in a single professional link. Close deals faster.", highlights: ["Portfolio & website link", "WhatsApp quick contact", "Instagram & social proof", "Professional digital presence"] },
// // // // //     { icon: "🏢", role: "Business Owners", tagline: "Network smarter, not harder", desc: "Stop spending on printed cards that get lost. Share via QR at events — always updated, never outdated, never thrown away.", highlights: ["Always up-to-date details", "Google Wallet for offline use", "QR code for events & meetups", "Share via WhatsApp or email"] },
// // // // //     { icon: "🎓", role: "Students", tagline: "Build your professional identity early", desc: "Stand out in campus placements and internship drives. Show your skills, projects, and social profiles before you say a word.", highlights: ["GitHub & project links", "Resume upload for placements", "LinkedIn profile showcase", "Free forever — no cost"] },
// // // // //     { icon: "📣", role: "Sales Pros", tagline: "Close the follow-up gap instantly", desc: "At trade shows or cold visits — one QR scan and your prospect has everything. Number, email, website, product links in one place.", highlights: ["Instant QR share at events", "Website & product links", "Save contact in one tap", "Google Wallet for quick access"] },
// // // // //     { icon: "🧑‍💻", role: "Developers", tagline: "Your entire dev profile in one link", desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share at hackathons or in open source communities.", highlights: ["GitHub & portfolio links", "Twitter / X profile", "Resume for job applications", "Professional dev identity"] },
// // // // // ];

// // // // // function UseCasesSection({ navigate }) {
// // // // //     const [active, setActive] = useState(0);
// // // // //     const [tabKey, setTabKey] = useState(0);
// // // // //     const cur = USE_CASES[active];

// // // // //     const switchTab = (i) => {
// // // // //         setActive(i);
// // // // //         setTabKey(k => k + 1);
// // // // //     };

// // // // //     return (
// // // // //         <section className="lp-section">
// // // // //             <div className="lp-section-inner">
// // // // //                 <span className="lp-eyebrow reveal from-up">Who is it for</span>
// // // // //                 <h2 className="lp-section-title reveal from-up d1">Built for every <em>professional</em></h2>
// // // // //                 <p className="lp-section-sub reveal from-up d2">Whether you're a student or a CEO — your digital card works for you.</p>

// // // // //                 <div className="lp-use-tabs reveal from-up d3">
// // // // //                     {USE_CASES.map((u, i) => (
// // // // //                         <button key={i} className="lp-use-tab" onClick={() => switchTab(i)}
// // // // //                             style={{
// // // // //                                 border: active === i ? "0.5px solid var(--border-gold)" : "0.5px solid var(--border-color)",
// // // // //                                 background: active === i ? "var(--gold-subtle2)" : "transparent",
// // // // //                                 color: active === i ? "var(--gold)" : "var(--text-muted)",
// // // // //                             }}>
// // // // //                             {u.icon} {u.role}
// // // // //                         </button>
// // // // //                     ))}
// // // // //                 </div>

// // // // //                 <div className="lp-use-panel reveal from-up d4" style={{ borderColor: "var(--border-gold)" }}>
// // // // //                     {/* Left content — re-animates on tab switch */}
// // // // //                     <div key={`left-${tabKey}`} className="lp-use-content">
// // // // //                         <div className="lp-use-badge"><span>{cur.icon}</span><span style={{ fontSize: "12px", letterSpacing: "0.5px" }}>{cur.role}</span></div>
// // // // //                         <div className="lp-use-tagline">{cur.tagline}</div>
// // // // //                         <p className="lp-use-desc">{cur.desc}</p>
// // // // //                         <button className="lp-btn-gold" onClick={() => navigate("/animation/card")}>Create My Card — Free →</button>
// // // // //                     </div>

// // // // //                     {/* Right highlights — each item slides in from right */}
// // // // //                     <div key={`right-${tabKey}`} className="lp-use-highlights">
// // // // //                         {cur.highlights.map((h, i) => (
// // // // //                             <div className="lp-use-hl" key={i} style={{ animationDelay: `${0.05 + i * 0.07}s` }}>
// // // // //                                 <div className="lp-use-hl-check">✓</div>
// // // // //                                 <span className="lp-use-hl-text">{h}</span>
// // // // //                             </div>
// // // // //                         ))}
// // // // //                     </div>
// // // // //                 </div>
// // // // //             </div>
// // // // //         </section>
// // // // //     );
// // // // // }

// // // // // /* ─────────────────────────────────────────
// // // // //    MAIN COMPONENT
// // // // // ───────────────────────────────────────── */
// // // // // export default function LandingPage() {
// // // // //     const navigate = useNavigate();
// // // // //     const [scrolled, setScrolled] = useState(false);
// // // // //     const [isLight, setIsLight] = useState(false);
// // // // //     const [menuOpen, setMenuOpen] = useState(false);

// // // // //     // close menu on resize to desktop
// // // // //     useEffect(() => {
// // // // //         const onResize = () => { if (window.innerWidth > 900) setMenuOpen(false); };
// // // // //         window.addEventListener("resize", onResize);
// // // // //         return () => window.removeEventListener("resize", onResize);
// // // // //     }, []);
// // // // //     const [statsStarted, setStatsStarted] = useState(false);
// // // // //     // Enhancement 2: track if how-section is in view for arrow progress
// // // // //     const [howVisible, setHowVisible] = useState(false);
// // // // //     // Enhancement 4: track when social card enters view
// // // // //     const socialCardRef = useRef(null);

// // // // //     const statsRef = useRef(null);
// // // // //     const howRef = useRef(null);

// // // // //     /* ── scroll nav ── */
// // // // //     useEffect(() => {
// // // // //         const onScroll = () => setScrolled(window.scrollY > 40);
// // // // //         window.addEventListener("scroll", onScroll);
// // // // //         return () => window.removeEventListener("scroll", onScroll);
// // // // //     }, []);

// // // // //     /* ── unified IntersectionObserver ── */
// // // // //     useEffect(() => {
// // // // //         const observers = [];

// // // // //         // 1. directional reveals
// // // // //         const revealEls = document.querySelectorAll(".reveal");
// // // // //         const revealObs = new IntersectionObserver(
// // // // //             (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
// // // // //             { threshold: 0.1 }
// // // // //         );
// // // // //         revealEls.forEach(el => revealObs.observe(el));
// // // // //         observers.push(revealObs);

// // // // //         // 2. count-up trigger
// // // // //         if (statsRef.current) {
// // // // //             const statsObs = new IntersectionObserver(
// // // // //                 ([entry]) => { if (entry.isIntersecting) setStatsStarted(true); },
// // // // //                 { threshold: 0.5 }
// // // // //             );
// // // // //             statsObs.observe(statsRef.current);
// // // // //             observers.push(statsObs);
// // // // //         }

// // // // //         // 3. how-section for arrow progress connector
// // // // //         if (howRef.current) {
// // // // //             const howObs = new IntersectionObserver(
// // // // //                 ([entry]) => { if (entry.isIntersecting) setHowVisible(true); },
// // // // //                 { threshold: 0.3 }
// // // // //             );
// // // // //             howObs.observe(howRef.current);
// // // // //             observers.push(howObs);
// // // // //         }

// // // // //         // 4. social pills card
// // // // //         const pillCards = document.querySelectorAll(".lp-feat-bcard.has-pills");
// // // // //         const pillObs = new IntersectionObserver(
// // // // //             (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("pills-visible"); }),
// // // // //             { threshold: 0.4 }
// // // // //         );
// // // // //         pillCards.forEach(c => pillObs.observe(c));
// // // // //         observers.push(pillObs);

// // // // //         return () => observers.forEach(o => o.disconnect());
// // // // //     }, []);

// // // // //     return (
// // // // //         <div className={isLight ? "lp-light" : ""} style={{ background: isLight ? "#FFFFFF" : "#080808", minHeight: "100vh" }}>
// // // // //             <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

// // // // //             {/* ── NAV ── */}
// // // // //             {/* ── MOBILE DRAWER ── */}
// // // // //             <div className={`lp-mobile-menu${menuOpen ? " open" : ""}`}>
// // // // //                 <div className="lp-mobile-menu-backdrop" onClick={() => setMenuOpen(false)} />
// // // // //                 <div className="lp-mobile-menu-panel">
// // // // //                     <div className="lp-mobile-menu-header">
// // // // //                         <a href="/" className="lp-logo" onClick={() => setMenuOpen(false)}>
// // // // //                             <svg width="28" height="28" viewBox="0 0 34 34" fill="none">
// // // // //                                 <rect width="34" height="34" rx="9" fill={isLight ? "#F0EDE6" : "#1A1A1A"} />
// // // // //                                 <rect width="34" height="34" rx="9" stroke="var(--border-gold)" strokeWidth="0.5" />
// // // // //                                 <circle cx="15" cy="13" r="5" stroke="var(--gold)" strokeWidth="1.5" fill="none" />
// // // // //                                 <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
// // // // //                                 <circle cx="27" cy="23" r="1.8" fill="var(--gold)" />
// // // // //                             </svg>
// // // // //                             myprofile<em>corner</em>
// // // // //                         </a>
// // // // //                         <button className="lp-mobile-close" onClick={() => setMenuOpen(false)}>✕</button>
// // // // //                     </div>

// // // // //                     <div className="lp-mobile-links">
// // // // //                         <a href="#how" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">⚡</span>How it works</a>
// // // // //                         <a href="#features" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">✦</span>Features</a>
// // // // //                         <a href="#pricing" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">₹</span>Pricing</a>
// // // // //                     </div>

// // // // //                     <div className="lp-mobile-divider" />

// // // // //                     <div className="lp-mobile-menu-footer">
// // // // //                         <button className="lp-mobile-cta" onClick={() => { setMenuOpen(false); navigate("/animation/card"); }}>
// // // // //                             Create My Card — Free →
// // // // //                         </button>
// // // // //                         <div className="lp-mobile-tagline">100% Free · No app required · 2 min setup</div>
// // // // //                     </div>
// // // // //                 </div>
// // // // //             </div>

// // // // //             <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
// // // // //                 <div className="lp-nav-inner">
// // // // //                     <a href="/" className="lp-logo">
// // // // //                         <svg className="lp-logo-icon" viewBox="0 0 34 34" fill="none">
// // // // //                             <rect width="34" height="34" rx="9" fill={isLight ? "#F0EDE6" : "#1A1A1A"} />
// // // // //                             <rect width="34" height="34" rx="9" stroke="var(--border-gold)" strokeWidth="0.5" />
// // // // //                             <circle cx="15" cy="13" r="5" stroke="var(--gold)" strokeWidth="1.5" fill="none" />
// // // // //                             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
// // // // //                             <path d="M23 11 L27 11 L27 23" stroke="var(--gold)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.6" />
// // // // //                             <circle cx="27" cy="23" r="1.8" fill="var(--gold)" />
// // // // //                         </svg>
// // // // //                         myprofile<em>corner</em>
// // // // //                     </a>

// // // // //                     {/* Desktop links */}
// // // // //                     <div className="lp-nav-links">
// // // // //                         <a href="#how" className="lp-nav-link">How it works</a>
// // // // //                         <a href="#features" className="lp-nav-link">Features</a>
// // // // //                         <a href="#pricing" className="lp-nav-link">Pricing</a>
// // // // //                         <button className="lp-theme-btn" onClick={() => setIsLight(p => !p)} aria-label="Toggle theme">
// // // // //                             {isLight ? (
// // // // //                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
// // // // //                                     <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
// // // // //                                 </svg>
// // // // //                             ) : (
// // // // //                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
// // // // //                                     <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
// // // // //                                     <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
// // // // //                                     <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
// // // // //                                     <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
// // // // //                                 </svg>
// // // // //                             )}
// // // // //                         </button>
// // // // //                         <button className="lp-nav-cta" onClick={() => navigate("/animation/card")}>Create Free Card</button>
// // // // //                     </div>

// // // // //                     {/* Mobile right cluster: theme toggle + hamburger */}
// // // // //                     <div className="lp-mobile-cluster">
// // // // //                         <button className="lp-theme-btn lp-theme-btn-mobile" onClick={() => setIsLight(p => !p)} aria-label="Toggle theme">
// // // // //                             {isLight ? (
// // // // //                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
// // // // //                                     <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
// // // // //                                 </svg>
// // // // //                             ) : (
// // // // //                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
// // // // //                                     <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
// // // // //                                     <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
// // // // //                                     <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
// // // // //                                     <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
// // // // //                                 </svg>
// // // // //                             )}
// // // // //                         </button>
// // // // //                         <button
// // // // //                             className={`lp-hamburger${menuOpen ? " open" : ""}`}
// // // // //                             onClick={() => setMenuOpen(p => !p)}
// // // // //                             aria-label="Open menu"
// // // // //                         >
// // // // //                             <span /><span /><span />
// // // // //                         </button>
// // // // //                     </div>

// // // // //                 </div>
// // // // //             </nav>

// // // // //             {/* ── HERO ── */}
// // // // //             <section style={{ background: isLight ? "#FFFFFF" : "#080808", overflow: "hidden", position: "relative" }}>
// // // // //                 <div className="lp-hero">
// // // // //                     <div style={{ position: "absolute", width: "600px", height: "600px", borderRadius: "50%", background: "radial-gradient(circle, var(--gold-subtle) 0%, transparent 65%)", top: "-200px", left: "-100px", pointerEvents: "none" }} />

// // // // //                     <div className="lp-hero-left">
// // // // //                         <div className="lp-hero-eyebrow reveal from-down">
// // // // //                             <div className="lp-hero-eyebrow-dot" /> 100% Free · No App Needed
// // // // //                         </div>
// // // // //                         <h1 className="lp-hero-title reveal from-left">
// // // // //                             Your Digital<br />Identity, <em>Always</em><br />in Your Pocket
// // // // //                         </h1>
// // // // //                         <p className="lp-hero-sub reveal from-left d1">
// // // // //                             Replace paper visiting cards with a smart digital card. Share your profile instantly via QR code, link, or Google Wallet — no printing, no waste.
// // // // //                         </p>

// // // // //                         {/* Enhancement 1: Count-up stats */}
// // // // //                         <div className="lp-hero-stats" ref={statsRef}>
// // // // //                             <StatItem value="100" suffix="%" label="Free forever" started={statsStarted} delay={0} />
// // // // //                             <StatItem value="2" suffix=" min" label="Setup time" started={statsStarted} delay={200} />
// // // // //                             <StatItem value="0" suffix="" label="Paper waste" started={statsStarted} delay={400} />
// // // // //                         </div>

// // // // //                         <div className="lp-hero-ctas reveal from-up d2">
// // // // //                             <button className="lp-btn-gold" onClick={() => navigate("/animation/card")}>Create My Card — It's Free →</button>
// // // // //                             <a href="#how" className="lp-btn-ghost">See how it works</a>
// // // // //                         </div>
// // // // //                     </div>

// // // // //                     <div className="lp-hero-right reveal from-right">
// // // // //                         <ScanScene />
// // // // //                     </div>
// // // // //                 </div>
// // // // //             </section>

// // // // //             <div className="gold-rule" />

// // // // //             {/* ── TRUST BAR ── */}
// // // // //             <div className="lp-trust">
// // // // //                 <span className="lp-trust-label">Works seamlessly with</span>
// // // // //                 <div className="lp-trust-items">
// // // // //                     {[["🔥", "Firebase"], ["🪙", "Google Wallet"], ["📱", "Android & iOS"], ["🔗", "Any Browser"]].map(([icon, label]) => (
// // // // //                         <div className="lp-trust-item" key={label}>
// // // // //                             <div className="lp-trust-item-icon">{icon}</div>
// // // // //                             <span>{label}</span>
// // // // //                         </div>
// // // // //                     ))}
// // // // //                 </div>
// // // // //             </div>

// // // // //             <div className="gold-rule" />

// // // // //             {/* ── HOW IT WORKS ── */}
// // // // //             <section id="how" className="lp-section" ref={howRef}>
// // // // //                 <div className="lp-section-inner">
// // // // //                     <span className="lp-eyebrow reveal from-up">How it works</span>
// // // // //                     <h2 className="lp-section-title reveal from-up d1">From setup to sharing <em>in 3 steps</em></h2>
// // // // //                     <p className="lp-section-sub reveal from-up d2">No app downloads. No tech skills required. Just fill, share, impress.</p>

// // // // //                     {/* Enhancement 2: progress connectors + directional card reveals */}
// // // // //                     <div className="lp-how-timeline">
// // // // //                         <div className="lp-how-card reveal from-left d1">
// // // // //                             <div className="lp-how-num">01</div>
// // // // //                             <div className="lp-how-icon-ring">✏️</div>
// // // // //                             <div className="lp-how-step-label">Step 01</div>
// // // // //                             <div className="lp-how-title">Fill Your Profile</div>
// // // // //                             <p className="lp-how-desc">Add your name, photo, contact info, social links, resume & website. Takes less than 2 minutes.</p>
// // // // //                         </div>

// // // // //                         <ArrowBridge filled={howVisible} />

// // // // //                         <div className="lp-how-card reveal from-up d3">
// // // // //                             <div className="lp-how-num">02</div>
// // // // //                             <div className="lp-how-icon-ring">🎴</div>
// // // // //                             <div className="lp-how-step-label">Step 02</div>
// // // // //                             <div className="lp-how-title">Get Your Digital Card</div>
// // // // //                             <p className="lp-how-desc">Instantly get a beautiful card with a unique QR code & shareable link — no design skills needed.</p>
// // // // //                         </div>

// // // // //                         <ArrowBridge filled={howVisible} />

// // // // //                         <div className="lp-how-card reveal from-right d5">
// // // // //                             <div className="lp-how-num">03</div>
// // // // //                             <div className="lp-how-icon-ring">🚀</div>
// // // // //                             <div className="lp-how-step-label">Step 03</div>
// // // // //                             <div className="lp-how-title">Share Anywhere</div>
// // // // //                             <p className="lp-how-desc">Show QR in meetings, add to Google Wallet, or send your link via WhatsApp, email, or LinkedIn.</p>
// // // // //                         </div>
// // // // //                     </div>
// // // // //                 </div>
// // // // //             </section>

// // // // //             <div className="gold-rule" />

// // // // //             {/* ── FEATURES ── */}
// // // // //             <section id="features" className="lp-section">
// // // // //                 <div className="lp-section-inner">
// // // // //                     <span className="lp-eyebrow reveal from-up">Features</span>
// // // // //                     <h2 className="lp-section-title reveal from-up d1">Everything a modern <em>professional</em> needs</h2>
// // // // //                     <p className="lp-section-sub reveal from-up d2">One card. Every detail. Always up-to-date.</p>

// // // // //                     {/* Enhancement 4: bento with shimmer + social pill stagger */}
// // // // //                     <div className="lp-feat-bento">

// // // // //                         {/* Hero: Google Wallet — reveals from left */}
// // // // //                         <div className="lp-feat-bcard hero reveal from-left d1">
// // // // //                             <div className="lp-feat-bicon">🪙</div>
// // // // //                             <div className="lp-feat-btag">Premium Feature</div>
// // // // //                             <div className="lp-feat-btitle">Google Wallet Pass</div>
// // // // //                             <p className="lp-feat-bdesc">Add your card to Google Wallet and access it offline — at meetings, events, or anywhere without internet.</p>
// // // // //                             <div className="lp-feat-wallet">
// // // // //                                 <div className="lp-wallet-chip">G Pay</div>
// // // // //                                 <div className="lp-wallet-chip">Wallet</div>
// // // // //                                 <div className="lp-wallet-chip">Offline ✓</div>
// // // // //                             </div>
// // // // //                         </div>

// // // // //                         {/* QR tall — reveals from right */}
// // // // //                         <div className="lp-feat-bcard tall reveal from-right d2">
// // // // //                             <div className="lp-feat-bicon">📲</div>
// // // // //                             <div className="lp-feat-btag">Instant Share</div>
// // // // //                             <div className="lp-feat-btitle">Unique QR Code</div>
// // // // //                             <p className="lp-feat-bdesc">Every card gets a dedicated QR. One scan opens your full profile — no typing, no friction.</p>
// // // // //                             <div className="lp-qr-art">
// // // // //                                 {[1, 1, 1, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 1].map((v, i) => (
// // // // //                                     <div key={i} className="lp-qr-cell" style={{ background: v ? "var(--gold)" : "var(--gold-subtle)", borderRadius: "2px", aspectRatio: "1" }} />
// // // // //                                 ))}
// // // // //                             </div>
// // // // //                         </div>

// // // // //                         {/* Photo — from below */}
// // // // //                         <div className="lp-feat-bcard reveal from-up d3">
// // // // //                             <div className="lp-feat-bicon">📸</div>
// // // // //                             <div className="lp-feat-btag">Identity</div>
// // // // //                             <div className="lp-feat-btitle">Profile Photo</div>
// // // // //                             <p className="lp-feat-bdesc">Your face on your card — and inside Google Wallet too.</p>
// // // // //                         </div>

// // // // //                         {/* Save contact — from below */}
// // // // //                         <div className="lp-feat-bcard reveal from-up d4">
// // // // //                             <div className="lp-feat-bicon">💾</div>
// // // // //                             <div className="lp-feat-btag">One Tap</div>
// // // // //                             <div className="lp-feat-btitle">Save Contact</div>
// // // // //                             <p className="lp-feat-bdesc">Visitors save you directly to their phone contacts with a single tap.</p>
// // // // //                         </div>

// // // // //                         {/* Hero: Social Links — from left, pills stagger */}
// // // // //                         <div className="lp-feat-bcard hero has-pills reveal from-left d2">
// // // // //                             <div className="lp-feat-bicon">🔗</div>
// // // // //                             <div className="lp-feat-btag">Connections</div>
// // // // //                             <div className="lp-feat-btitle">All Social Links</div>
// // // // //                             <p className="lp-feat-bdesc">LinkedIn, GitHub, Instagram, Twitter, WhatsApp, YouTube — everything that matters in one place.</p>
// // // // //                             <div className="lp-feat-socials">
// // // // //                                 {["in LinkedIn", "⌥ GitHub", "✉ WhatsApp", "▶ YouTube", "📷 Insta"].map(s => (
// // // // //                                     <span key={s} className="lp-social-pill">{s}</span>
// // // // //                                 ))}
// // // // //                             </div>
// // // // //                         </div>

// // // // //                         <div className="lp-feat-bcard reveal from-up d3">
// // // // //                             <div className="lp-feat-bicon">📄</div>
// // // // //                             <div className="lp-feat-btag">Career</div>
// // // // //                             <div className="lp-feat-btitle">Resume Upload</div>
// // // // //                             <p className="lp-feat-bdesc">Attach your PDF resume. Recruiters can download it instantly.</p>
// // // // //                         </div>
// // // // //                         <div className="lp-feat-bcard reveal from-up d4">
// // // // //                             <div className="lp-feat-bicon">✏️</div>
// // // // //                             <div className="lp-feat-btag">Always Fresh</div>
// // // // //                             <div className="lp-feat-btitle">Edit Anytime</div>
// // // // //                             <p className="lp-feat-bdesc">Changed jobs? New number? Update instantly — same link forever.</p>
// // // // //                         </div>
// // // // //                         <div className="lp-feat-bcard reveal from-right d5">
// // // // //                             <div className="lp-feat-bicon">🌐</div>
// // // // //                             <div className="lp-feat-btag">Universal</div>
// // // // //                             <div className="lp-feat-btitle">Works Everywhere</div>
// // // // //                             <p className="lp-feat-bdesc">Opens in any browser on any device. No app install ever required.</p>
// // // // //                         </div>

// // // // //                     </div>
// // // // //                 </div>
// // // // //             </section>

// // // // //             <div className="gold-rule" />

// // // // //             {/* ── USE CASES ── */}
// // // // //             <UseCasesSection navigate={navigate} />

// // // // //             <div className="gold-rule" />

// // // // //             {/* ── PRICING + CTA MERGED ── */}
// // // // //             <section id="pricing" className="lp-pricing-cta">
// // // // //                 <div className="lp-pricing-cta-inner">
// // // // //                     <div className="lp-pricing-copy">
// // // // //                         <span className="lp-pricing-eyebrow reveal from-left">Pricing</span>
// // // // //                         <h2 className="lp-pricing-headline reveal from-left d1">Stop carrying<br /><em>paper cards.</em></h2>
// // // // //                         <p className="lp-pricing-sub reveal from-left d2">Create your digital card in under 2 minutes. No hidden charges. No credit card. Free forever.</p>
// // // // //                         <div className="lp-pricing-perks reveal from-left d3">
// // // // //                             {["No app download needed", "Unique QR code & shareable link", "Google Wallet pass included", "Edit anytime — same link forever", "Works on every device & browser"].map(p => (
// // // // //                                 <div className="lp-pricing-perk" key={p}>
// // // // //                                     <div className="lp-perk-tick">✓</div><span>{p}</span>
// // // // //                                 </div>
// // // // //                             ))}
// // // // //                         </div>
// // // // //                         <button className="lp-btn-gold reveal from-up d4" onClick={() => navigate("/animation/card")}>Create My Card Now →</button>
// // // // //                     </div>
// // // // //                     <div className="lp-price-card reveal from-right d2">
// // // // //                         <div className="lp-price-card-top">
// // // // //                             <div className="lp-price-badge">Most Popular</div>
// // // // //                             <div className="lp-price-amount">
// // // // //                                 <div className="lp-price-num">₹0</div>
// // // // //                                 <div className="lp-price-period">Forever Free</div>
// // // // //                             </div>
// // // // //                         </div>
// // // // //                         <div className="lp-price-divider" />
// // // // //                         <ul className="lp-price-list">
// // // // //                             {["1 Digital Visiting Card", "Unique QR Code & Link", "Profile Photo Upload", "Resume Upload (PDF)", "All Social Media Links", "Google Wallet Pass", "Save Contact (.vcf)", "Edit Anytime", "No App Required"].map(item => (
// // // // //                                 <li className="lp-price-item" key={item}><span className="lp-price-check">✦</span>{item}</li>
// // // // //                             ))}
// // // // //                         </ul>
// // // // //                         <button className="lp-btn-gold" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/animation/card")}>Get Started Free →</button>
// // // // //                     </div>
// // // // //                 </div>
// // // // //             </section>

// // // // //             <div className="gold-rule" />

// // // // //             {/* ── FOOTER ── */}
// // // // //             <footer className="lp-footer">
// // // // //                 <div className="lp-footer-inner">
// // // // //                     <div className="lp-footer-logo">
// // // // //                         <svg width="28" height="28" viewBox="0 0 34 34" fill="none">
// // // // //                             <rect width="34" height="34" rx="9" fill={isLight ? "#F0EDE6" : "#1A1A1A"} />
// // // // //                             <rect width="34" height="34" rx="9" stroke="var(--border-gold)" strokeWidth="0.5" />
// // // // //                             <circle cx="15" cy="13" r="5" stroke="var(--gold)" strokeWidth="1.5" fill="none" />
// // // // //                             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
// // // // //                             <path d="M23 11 L27 11 L27 23" stroke="var(--gold)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.6" />
// // // // //                             <circle cx="27" cy="23" r="1.8" fill="var(--gold)" />
// // // // //                         </svg>
// // // // //                         myprofile<em>corner</em>
// // // // //                     </div>
// // // // //                     <p className="lp-footer-tagline">Your digital identity, always in your pocket.</p>
// // // // //                     <p className="lp-footer-copy">© {new Date().getFullYear()} MyProfileCorner · Made with ♥ in India</p>
// // // // //                 </div>
// // // // //             </footer>
// // // // //         </div>
// // // // //     );
// // // // // }


// // // // // // import React, { useState, useEffect, useRef, useCallback } from "react";
// // // // // // import { useNavigate } from "react-router-dom";

// // // // // // const FONT_LINK = `
// // // // // //   @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600&family=Montserrat:wght@300;400;500;600;700;800&display=swap');
// // // // // // `;

// // // // // // const GLOBAL_CSS = `
// // // // // // ${FONT_LINK}

// // // // // // *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

// // // // // // :root {
// // // // // //   --black: #080808;
// // // // // //   --dark: #0E0E0E;
// // // // // //   --card: #141414;
// // // // // //   --border: rgba(255,255,255,0.07);
// // // // // //   --gold: #D4AF37;
// // // // // //   --gold2: #F0D060;
// // // // // //   --gold3: #C9A227;
// // // // // //   --gold-dim: rgba(212,175,55,0.18);
// // // // // //   --white: #FFFFFF;
// // // // // //   --muted: rgba(255,255,255,0.45);
// // // // // //   --muted2: rgba(255,255,255,0.25);
// // // // // //   --serif: 'Cormorant Garamond', serif;
// // // // // //   --sans: 'Montserrat', sans-serif;
// // // // // //   --bg: #080808;
// // // // // //   --surface: #141414;
// // // // // //   --surface2: #1a1a1a;
// // // // // //   --text-primary: #FFFFFF;
// // // // // //   --text-muted: rgba(255,255,255,0.45);
// // // // // //   --text-muted2: rgba(255,255,255,0.25);
// // // // // //   --border-color: rgba(255,255,255,0.07);
// // // // // //   --border-gold: rgba(212,175,55,0.2);
// // // // // //   --gold-subtle: rgba(212,175,55,0.08);
// // // // // //   --gold-subtle2: rgba(212,175,55,0.12);
// // // // // // }

// // // // // // /* ── LIGHT THEME ── */
// // // // // // .lp-light {
// // // // // //   --bg: #FFFFFF;
// // // // // //   --surface: #F5F3EE;
// // // // // //   --surface2: #EDE9E0;
// // // // // //   --card: #F5F3EE;
// // // // // //   --dark: #EDE9E0;
// // // // // //   --black: #FFFFFF;
// // // // // //   --border-color: rgba(0,0,0,0.08);
// // // // // //   --border: rgba(0,0,0,0.08);
// // // // // //   --border-gold: rgba(180,140,30,0.25);
// // // // // //   --white: #0E0E0E;
// // // // // //   --text-primary: #111111;
// // // // // //   --text-muted: rgba(0,0,0,0.5);
// // // // // //   --text-muted2: rgba(0,0,0,0.35);
// // // // // //   --muted: rgba(0,0,0,0.5);
// // // // // //   --muted2: rgba(0,0,0,0.35);
// // // // // //   --gold: #A0780A;
// // // // // //   --gold2: #C9991A;
// // // // // //   --gold3: #8A6608;
// // // // // //   --gold-dim: rgba(160,120,10,0.15);
// // // // // //   --gold-subtle: rgba(160,120,10,0.08);
// // // // // //   --gold-subtle2: rgba(160,120,10,0.12);
// // // // // // }

// // // // // // html { scroll-behavior: smooth; }
// // // // // // body {
// // // // // //   background: var(--bg);
// // // // // //   color: var(--text-primary);
// // // // // //   font-family: var(--sans);
// // // // // //   overflow-x: hidden;
// // // // // //   letter-spacing: 0.1px;
// // // // // // }

// // // // // // ::-webkit-scrollbar { width: 4px; }
// // // // // // ::-webkit-scrollbar-track { background: var(--bg); }
// // // // // // ::-webkit-scrollbar-thumb { background: rgba(212,175,55,0.3); border-radius: 2px; }

// // // // // // /* ═══════════════════════════════════════
// // // // // //    ENHANCEMENT 3 — DIRECTIONAL STAGGER REVEALS
// // // // // //    Elements animate from different directions
// // // // // // ═══════════════════════════════════════ */
// // // // // // .reveal {
// // // // // //   opacity: 0;
// // // // // //   transition: opacity 0.75s cubic-bezier(0.22,1,0.36,1), transform 0.75s cubic-bezier(0.22,1,0.36,1);
// // // // // // }
// // // // // // .reveal.from-up    { transform: translateY(32px); }
// // // // // // .reveal.from-down  { transform: translateY(-24px); }
// // // // // // .reveal.from-left  { transform: translateX(-40px); }
// // // // // // .reveal.from-right { transform: translateX(40px); }
// // // // // // .reveal.from-scale { transform: scale(0.92); }

// // // // // // /* stagger delays */
// // // // // // .reveal.d1 { transition-delay: 0.05s; }
// // // // // // .reveal.d2 { transition-delay: 0.13s; }
// // // // // // .reveal.d3 { transition-delay: 0.21s; }
// // // // // // .reveal.d4 { transition-delay: 0.29s; }
// // // // // // .reveal.d5 { transition-delay: 0.37s; }
// // // // // // .reveal.d6 { transition-delay: 0.45s; }

// // // // // // .reveal.visible {
// // // // // //   opacity: 1;
// // // // // //   transform: translateY(0) translateX(0) scale(1);
// // // // // // }

// // // // // // /* ── NAV ── */
// // // // // // .lp-nav {
// // // // // //   position: fixed; top: 0; left: 0; right: 0; z-index: 200;
// // // // // //   padding: 1.5rem 3rem; transition: all 0.4s;
// // // // // // }
// // // // // // .lp-nav.scrolled {
// // // // // //   background: rgba(8,8,8,0.9);
// // // // // //   backdrop-filter: blur(20px);
// // // // // //   border-bottom: 0.5px solid rgba(212,175,55,0.12);
// // // // // //   padding: 1rem 3rem;
// // // // // // }
// // // // // // .lp-light .lp-nav.scrolled {
// // // // // //   background: rgba(255,255,255,0.92);
// // // // // //   border-bottom: 0.5px solid rgba(160,120,10,0.18);
// // // // // // }
// // // // // // .lp-nav-inner { max-width: 1180px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; }
// // // // // // .lp-logo {
// // // // // //   display: flex; align-items: center; gap: 10px;
// // // // // //   font-family: var(--sans); font-size: 15px; font-weight: 600;
// // // // // //   color: var(--text-primary); text-decoration: none; letter-spacing: 0.2px;
// // // // // // }
// // // // // // .lp-logo-icon { width: 34px; height: 34px; flex-shrink: 0; }
// // // // // // .lp-logo em { color: var(--gold); font-style: normal; font-weight: 700; }
// // // // // // .lp-nav-links { display: flex; align-items: center; gap: 2.5rem; }
// // // // // // .lp-nav-link { font-size: 13px; font-weight: 500; color: var(--text-muted); text-decoration: none; letter-spacing: 0.3px; transition: color 0.2s; }
// // // // // // .lp-nav-link:hover { color: var(--text-primary); }
// // // // // // .lp-nav-cta {
// // // // // //   padding: 10px 22px;
// // // // // //   background: linear-gradient(135deg, #D4AF37, #F0D060, #C9A227);
// // // // // //   color: #0A0A0A; border: none; border-radius: 10px;
// // // // // //   font-family: var(--sans); font-size: 13px; font-weight: 700;
// // // // // //   letter-spacing: 0.8px; text-transform: uppercase;
// // // // // //   cursor: pointer; position: relative; overflow: hidden;
// // // // // //   transition: transform 0.15s, opacity 0.15s;
// // // // // // }
// // // // // // .lp-nav-cta::before { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg, rgba(255,255,255,0.2), transparent 60%); pointer-events: none; }
// // // // // // .lp-nav-cta:hover { transform: translateY(-1px); opacity: 0.92; }

// // // // // // /* ── HERO ── */
// // // // // // .lp-hero {
// // // // // //   min-height: 100vh;
// // // // // //   display: grid; grid-template-columns: 1fr 1fr;
// // // // // //   align-items: center; gap: 4rem;
// // // // // //   max-width: 1180px; margin: 0 auto;
// // // // // //   padding: 10rem 3rem 3rem; position: relative;
// // // // // // }
// // // // // // .lp-hero-left { position: relative; z-index: 2; }
// // // // // // .lp-hero-eyebrow {
// // // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // // //   padding: 6px 14px;
// // // // // //   background: var(--gold-subtle2); border: 0.5px solid var(--border-gold);
// // // // // //   border-radius: 100px; font-size: 11px; font-weight: 700; color: var(--gold);
// // // // // //   letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 2rem;
// // // // // // }
// // // // // // .lp-hero-eyebrow-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--gold); animation: pulse-gold 2s ease-in-out infinite; }
// // // // // // @keyframes pulse-gold { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:0.4;transform:scale(0.7);} }

// // // // // // .lp-hero-title { font-family: var(--serif); font-size: clamp(3.2rem, 5.8vw, 5.3rem); font-weight: 600; line-height: 1.02; letter-spacing: -0.8px; color: var(--text-primary); margin-bottom: 1.75rem; }
// // // // // // .lp-hero-title em { font-style: italic; font-weight: 600; color: var(--gold); }
// // // // // // .lp-hero-sub { font-size: 15px; font-weight: 400; color: var(--text-muted); line-height: 1.9; max-width: 470px; margin-bottom: 1rem; }
// // // // // // .lp-hero-ctas { display: flex; gap: 1rem; flex-wrap: wrap; }

// // // // // // .lp-btn-gold {
// // // // // //   padding: 16px 32px;
// // // // // //   background: linear-gradient(135deg, #D4AF37, #F0D060, #C9A227);
// // // // // //   color: #0A0A0A; border: none; border-radius: 12px;
// // // // // //   font-family: var(--sans); font-size: 13px; font-weight: 700;
// // // // // //   letter-spacing: 1px; text-transform: uppercase;
// // // // // //   cursor: pointer; position: relative; overflow: hidden;
// // // // // //   transition: transform 0.2s, box-shadow 0.2s;
// // // // // //   display: flex; align-items: center; gap: 8px;
// // // // // // }
// // // // // // .lp-btn-gold::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.25),transparent 60%); pointer-events:none; }
// // // // // // .lp-btn-gold:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(212,175,55,0.3); }

// // // // // // .lp-btn-ghost {
// // // // // //   padding: 16px 28px; background: transparent;
// // // // // //   color: var(--text-muted); border: 0.5px solid var(--border-color);
// // // // // //   border-radius: 12px; font-family: var(--sans); font-size: 13px; font-weight: 600;
// // // // // //   letter-spacing: 0.5px; cursor: pointer; text-decoration: none;
// // // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // // //   transition: border-color 0.2s, color 0.2s;
// // // // // // }
// // // // // // .lp-btn-ghost:hover { border-color: var(--border-gold); color: var(--text-primary); }

// // // // // // /* ═══════════════════════════════════════
// // // // // //    ENHANCEMENT 1 — COUNT-UP STATS
// // // // // // ═══════════════════════════════════════ */
// // // // // // .lp-hero-stats { display: flex; gap: 2.5rem; padding-bottom: 1.5rem; margin-bottom: 1.5rem; }
// // // // // // .lp-stat { position: relative; }
// // // // // // .lp-stat-num {
// // // // // //   font-family: var(--serif); font-size: 2.4rem; font-weight: 600;
// // // // // //   color: var(--gold); line-height: 1;
// // // // // //   /* subtle gold shimmer when counting */
// // // // // // }
// // // // // // .lp-stat-num.counting {
// // // // // //   animation: stat-shimmer 0.1s ease infinite alternate;
// // // // // // }
// // // // // // @keyframes stat-shimmer {
// // // // // //   from { opacity: 0.7; }
// // // // // //   to   { opacity: 1; }
// // // // // // }
// // // // // // .lp-stat-label { font-size: 11px; font-weight: 500; color: var(--text-muted2); letter-spacing: 0.5px; margin-top: 5px; }
// // // // // // /* thin gold underline that fills as number counts */
// // // // // // .lp-stat-bar {
// // // // // //   height: 1.5px; background: var(--border-gold); margin-top: 8px;
// // // // // //   border-radius: 1px; overflow: hidden;
// // // // // // }
// // // // // // .lp-stat-bar-fill {
// // // // // //   height: 100%;
// // // // // //   background: linear-gradient(90deg, var(--gold), var(--gold2));
// // // // // //   border-radius: 1px;
// // // // // //   width: 0%;
// // // // // //   transition: width 0.05s linear;
// // // // // // }

// // // // // // /* ── HERO RIGHT ── */
// // // // // // .lp-hero-right { position: relative; display: flex; align-items: center; justify-content: center; z-index: 2; }
// // // // // // .scan-scene { position: relative; width: 380px; height: 480px; }

// // // // // // /* ═══════════════════════════════════════
// // // // // //    ENHANCEMENT 5 — HERO RIPPLE / SUCCESS
// // // // // // ═══════════════════════════════════════ */
// // // // // // /* Ripple rings that emit from phone B when scan "completes" */
// // // // // // .scan-ripple {
// // // // // //   position: absolute;
// // // // // //   right: -10px; top: 50px;
// // // // // //   width: 60px; height: 60px;
// // // // // //   border-radius: 50%;
// // // // // //   border: 1.5px solid rgba(212,175,55,0);
// // // // // //   z-index: 10;
// // // // // //   pointer-events: none;
// // // // // // }
// // // // // // .scan-ripple.r1 { animation: ripple-out 2.5s ease-out infinite; animation-delay: 3.2s; }
// // // // // // .scan-ripple.r2 { animation: ripple-out 2.5s ease-out infinite; animation-delay: 3.6s; }
// // // // // // .scan-ripple.r3 { animation: ripple-out 2.5s ease-out infinite; animation-delay: 4.0s; }
// // // // // // @keyframes ripple-out {
// // // // // //   0%   { transform: scale(1); border-color: rgba(212,175,55,0.6); opacity: 1; }
// // // // // //   100% { transform: scale(3.5); border-color: rgba(212,175,55,0); opacity: 0; }
// // // // // // }

// // // // // // /* Success ping badge */
// // // // // // .scan-success {
// // // // // //   position: absolute;
// // // // // //   right: 8px; top: 38px;
// // // // // //   background: linear-gradient(135deg, #D4AF37, #F0D060);
// // // // // //   color: #0A0A0A;
// // // // // //   border-radius: 100px;
// // // // // //   padding: 4px 10px 4px 8px;
// // // // // //   font-family: var(--sans); font-size: 9px; font-weight: 700;
// // // // // //   letter-spacing: 0.5px;
// // // // // //   display: flex; align-items: center; gap: 5px;
// // // // // //   z-index: 12; white-space: nowrap;
// // // // // //   opacity: 0;
// // // // // //   animation: success-pop 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards;
// // // // // //   animation-delay: 3.1s;
// // // // // //   box-shadow: 0 4px 16px rgba(212,175,55,0.4);
// // // // // // }
// // // // // // .scan-success-dot { width: 6px; height: 6px; border-radius: 50%; background: #0A0A0A; }
// // // // // // @keyframes success-pop {
// // // // // //   0%   { opacity: 0; transform: scale(0.5) translateY(6px); }
// // // // // //   100% { opacity: 1; transform: scale(1) translateY(0); }
// // // // // // }

// // // // // // /* Phone A */
// // // // // // .phone-scanner {
// // // // // //   position: absolute; left: 0; top: 60px;
// // // // // //   width: 155px; height: 270px; background: #1A1A1A;
// // // // // //   border-radius: 22px; border: 1px solid rgba(255,255,255,0.1);
// // // // // //   overflow: hidden; box-shadow: 0 24px 60px rgba(0,0,0,0.6);
// // // // // //   z-index: 3;
// // // // // //   animation: phone-a-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both; animation-delay: 0.3s;
// // // // // // }
// // // // // // @keyframes phone-a-intro { from{opacity:0;transform:translateX(-30px) rotate(-6deg);} to{opacity:1;transform:translateX(0) rotate(-6deg);} }
// // // // // // .phone-scanner-screen { width:100%; height:100%; background:#111; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; padding:12px; }
// // // // // // .scanner-viewfinder { width:100px; height:100px; border-radius:10px; position:relative; background:rgba(212,175,55,0.04); }
// // // // // // .scanner-viewfinder::before,.scanner-viewfinder::after { content:''; position:absolute; width:20px; height:20px; border-color:#D4AF37; border-style:solid; }
// // // // // // .scanner-viewfinder::before { top:0;left:0;border-width:2px 0 0 2px;border-radius:6px 0 0 0; }
// // // // // // .scanner-viewfinder::after  { bottom:0;right:0;border-width:0 2px 2px 0;border-radius:0 0 6px 0; }
// // // // // // .vf-corner-tr { position:absolute;top:0;right:0;width:20px;height:20px;border-top:2px solid #D4AF37;border-right:2px solid #D4AF37;border-radius:0 6px 0 0; }
// // // // // // .vf-corner-bl { position:absolute;bottom:0;left:0;width:20px;height:20px;border-bottom:2px solid #D4AF37;border-left:2px solid #D4AF37;border-radius:0 0 0 6px; }
// // // // // // .scan-laser { position:absolute;left:4px;right:4px;height:2px;background:linear-gradient(90deg,transparent,#D4AF37,transparent);border-radius:1px;top:4px;animation:laser-sweep 2s ease-in-out infinite;animation-delay:1.2s; }
// // // // // // @keyframes laser-sweep { 0%{top:8px;opacity:0;} 10%{opacity:1;} 90%{opacity:1;} 100%{top:88px;opacity:0;} }

// // // // // // /* scanner screen flashes green briefly on scan success */
// // // // // // .scanner-success-flash {
// // // // // //   position: absolute; inset: 0;
// // // // // //   background: rgba(212,175,55,0.12);
// // // // // //   opacity: 0;
// // // // // //   animation: flash-once 0.4s ease forwards;
// // // // // //   animation-delay: 3.0s;
// // // // // //   pointer-events: none; z-index: 10;
// // // // // // }
// // // // // // @keyframes flash-once { 0%{opacity:0;} 40%{opacity:1;} 100%{opacity:0;} }

// // // // // // .scanner-label { font-size:9px;font-weight:600;color:rgba(255,255,255,0.4);letter-spacing:1px;text-align:center;text-transform:uppercase; }
// // // // // // .mini-qr { position:absolute;inset:10px;display:grid;grid-template-columns:repeat(6,1fr);grid-template-rows:repeat(6,1fr);gap:1.5px;opacity:0.6; }
// // // // // // .mini-qr-cell { border-radius:1px; }

// // // // // // /* Phone B */
// // // // // // .phone-profile {
// // // // // //   position:absolute;right:0;top:0;width:175px;height:310px;background:#0E0E0E;
// // // // // //   border-radius:24px;border:1px solid rgba(212,175,55,0.2);overflow:hidden;
// // // // // //   box-shadow:0 32px 80px rgba(0,0,0,0.7),0 0 0 1px rgba(212,175,55,0.08);
// // // // // //   z-index:4;
// // // // // //   animation:phone-b-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:0.5s;
// // // // // // }
// // // // // // @keyframes phone-b-intro { from{opacity:0;transform:translateY(30px) rotate(4deg);} to{opacity:1;transform:translateY(0) rotate(4deg);} }
// // // // // // .profile-card-wrap { width:100%;height:100%;animation:card-reveal 0.7s cubic-bezier(0.22,1,0.36,1) both;animation-delay:2s; }
// // // // // // @keyframes card-reveal { from{transform:translateY(40px);opacity:0;} to{transform:translateY(0);opacity:1;} }
// // // // // // .profile-banner { width:100%;height:110px;background:linear-gradient(160deg,#1a1a1a,#2a2020);position:relative;overflow:hidden; }
// // // // // // .profile-banner-img { width:100%;height:100%;background:linear-gradient(160deg,#1e1a10 0%,#2a2010 50%,#181818 100%); }
// // // // // // .profile-banner-fade { position:absolute;bottom:0;left:0;right:0;height:50px;background:linear-gradient(transparent,#0E0E0E); }
// // // // // // .profile-avatar-ring { position:absolute;bottom:2px;left:14px;width:44px;height:44px;border-radius:50%;border:2px solid #0E0E0E;background:#222;overflow:hidden;box-shadow:0 0 0 1.5px rgba(212,175,55,0.5);animation:avatar-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:2.4s; }
// // // // // // @keyframes avatar-pop { from{transform:scale(0.3);opacity:0;} to{transform:scale(1);opacity:1;} }
// // // // // // .profile-avatar-inner { width:100%;height:100%;background:linear-gradient(145deg,#2a2010,#3a3020);display:flex;align-items:center;justify-content:center;font-family:'Cormorant Garamond',serif;font-size:14px;font-weight:600;color:rgba(212,175,55,0.8);letter-spacing:1px; }
// // // // // // .profile-info { padding:28px 14px 12px; }
// // // // // // .profile-info-name { font-family:'Cormorant Garamond',serif;font-size:16px;font-weight:600;color:#FFF;line-height:1.2;margin-bottom:2px; }
// // // // // // .profile-info-name em { font-style:italic;color:#D4AF37; }
// // // // // // .profile-info-role { font-size:8px;font-weight:600;color:rgba(255,255,255,0.45);letter-spacing:0.3px;margin-bottom:10px; }
// // // // // // .profile-tiles { display:grid;grid-template-columns:1fr 1fr 1fr;gap:5px;margin-bottom:8px; }
// // // // // // .profile-tile { background:#1A1A1A;border:0.5px solid rgba(255,255,255,0.06);border-radius:8px;padding:7px 6px;display:flex;flex-direction:column;gap:4px;animation:tile-slide 0.3s ease both; }
// // // // // // .profile-tile:nth-child(1){animation-delay:2.5s;} .profile-tile:nth-child(2){animation-delay:2.65s;} .profile-tile:nth-child(3){animation-delay:2.8s;}
// // // // // // @keyframes tile-slide { from{opacity:0;transform:translateY(6px);} to{opacity:1;transform:translateY(0);} }
// // // // // // .tile-dot { width:12px;height:12px;border-radius:50%;background:rgba(212,175,55,0.15);display:flex;align-items:center;justify-content:center; }
// // // // // // .tile-dot-inner { width:5px;height:5px;border-radius:50%;background:#D4AF37; }
// // // // // // .tile-text { font-size:7px;color:rgba(255,255,255,0.4);line-height:1.3;font-weight:500; }
// // // // // // .profile-save-btn { width:100%;padding:8px;background:linear-gradient(135deg,#D4AF37,#F0D060);color:#0A0A0A;border:none;border-radius:8px;font-size:8px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;cursor:default;display:flex;align-items:center;justify-content:center;gap:4px;animation:tile-slide 0.3s ease both;animation-delay:2.95s; }

// // // // // // .beam-line { position:absolute;top:165px;left:148px;width:90px;height:1px;background:linear-gradient(90deg,#D4AF37,rgba(212,175,55,0));transform-origin:left center;animation:beam-appear 0.4s ease both;animation-delay:1.5s;z-index:5; }
// // // // // // @keyframes beam-appear { from{opacity:0;} to{opacity:1;} }
// // // // // // .beam-particle { position:absolute;top:160px;left:148px;width:6px;height:6px;border-radius:50%;background:#D4AF37;animation:particle-travel 2s ease-in-out infinite;animation-delay:1.6s;z-index:6; }
// // // // // // @keyframes particle-travel { 0%{left:148px;opacity:0;transform:scale(0.5);} 10%{opacity:1;transform:scale(1);} 90%{opacity:1;} 100%{left:228px;opacity:0;transform:scale(0.5);} }
// // // // // // .hero-orb-1 { position:absolute;width:500px;height:500px;border-radius:50%;background:radial-gradient(circle,rgba(212,175,55,0.06) 0%,transparent 65%);top:-100px;right:-100px;pointer-events:none;z-index:0; }

// // // // // // /* ── SECTION SHARED ── */
// // // // // // .lp-section { padding: 5rem 3rem; position: relative; }
// // // // // // .lp-section-inner { max-width: 1180px; margin: 0 auto; }
// // // // // // .lp-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--gold);margin-bottom:1.25rem;text-align:center; }
// // // // // // .lp-section-title { font-family:var(--serif);font-size:clamp(2.3rem,4vw,3.5rem);font-weight:600;line-height:1.08;letter-spacing:-0.4px;color:var(--text-primary);text-align:center;margin-bottom:1.25rem; }
// // // // // // .lp-section-title em { font-style:italic;color:var(--gold); }
// // // // // // .lp-section-sub { font-size:15px;font-weight:400;color:var(--text-muted);text-align:center;max-width:520px;margin:0 auto 4rem;line-height:1.9; }

// // // // // // .gold-rule { height:0.5px;background:linear-gradient(90deg,transparent,rgba(212,175,55,0.3),transparent); }
// // // // // // .lp-light .gold-rule { background:linear-gradient(90deg,transparent,rgba(160,120,10,0.2),transparent); }

// // // // // // /* ── TRUST BAR ── */
// // // // // // .lp-trust { padding:2rem 3rem;display:flex;align-items:center;justify-content:center;gap:2.5rem;flex-wrap:wrap;background:var(--surface); }
// // // // // // .lp-trust-label { font-size:10px;font-weight:600;color:var(--text-muted2);letter-spacing:2px;text-transform:uppercase; }
// // // // // // .lp-trust-items { display:flex;align-items:center;gap:2rem;flex-wrap:wrap; }
// // // // // // .lp-trust-item { display:flex;align-items:center;gap:7px;font-size:13px;font-weight:500;color:var(--text-muted); }
// // // // // // .lp-trust-sep { color:var(--border-gold);font-size:18px; }

// // // // // // /* ═══════════════════════════════════════
// // // // // //    ENHANCEMENT 2 — PROGRESS CONNECTOR
// // // // // //    The SVG arrow between steps fills on scroll
// // // // // // ═══════════════════════════════════════ */
// // // // // // .lp-how-timeline {
// // // // // //   display: grid;
// // // // // //   grid-template-columns: 1fr 72px 1fr 72px 1fr;
// // // // // //   align-items: start; gap: 0; margin-top: 1rem;
// // // // // // }
// // // // // // .lp-how-bridge {
// // // // // //   display: flex; align-items: flex-start; justify-content: center; padding-top: 2.6rem;
// // // // // // }
// // // // // // /* The SVG path for the arrow */
// // // // // // .how-arrow-path {
// // // // // //   stroke: var(--border-gold);
// // // // // //   stroke-dasharray: 60;
// // // // // //   stroke-dashoffset: 60;
// // // // // //   transition: stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1), stroke 0.4s ease;
// // // // // // }
// // // // // // .how-arrow-path.filled {
// // // // // //   stroke-dashoffset: 0;
// // // // // //   stroke: var(--gold);
// // // // // // }
// // // // // // /* arrowhead */
// // // // // // .how-arrowhead {
// // // // // //   fill: var(--border-gold);
// // // // // //   transition: fill 0.4s ease 0.7s;
// // // // // // }
// // // // // // .how-arrow-path.filled + .how-arrowhead,
// // // // // // .how-arrowhead.filled {
// // // // // //   fill: var(--gold);
// // // // // // }

// // // // // // .lp-how-card {
// // // // // //   background: var(--surface);
// // // // // //   border: 0.5px solid var(--border-color);
// // // // // //   border-radius: 20px; padding: 2.2rem 1.8rem 2rem;
// // // // // //   position: relative; overflow: hidden;
// // // // // //   transition: border-color 0.3s, transform 0.3s;
// // // // // //   cursor: default;
// // // // // // }
// // // // // // .lp-how-card::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 80% 60% at 50% -10%,var(--gold-subtle2) 0%,transparent 70%);opacity:0;transition:opacity 0.3s; }
// // // // // // .lp-how-card:hover { border-color: var(--border-gold); transform: translateY(-4px); }
// // // // // // .lp-how-card:hover::before { opacity: 1; }
// // // // // // .lp-how-num { font-family:var(--serif);font-size:7rem;font-weight:700;color:var(--gold-subtle2);line-height:1;position:absolute;bottom:-0.5rem;right:0.8rem;letter-spacing:-3px;pointer-events:none;user-select:none;opacity:0.6; }
// // // // // // .lp-light .lp-how-num { opacity:1;color:rgba(160,120,10,0.1); }
// // // // // // .lp-how-icon-ring { width:56px;height:56px;border-radius:16px;background:var(--gold-subtle);border:1px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:1.6rem;margin-bottom:1.5rem;position:relative; }
// // // // // // .lp-how-icon-ring::after { content:'';position:absolute;inset:-4px;border-radius:20px;border:1px dashed var(--border-gold);animation:lp-spin-slow 12s linear infinite; }
// // // // // // @keyframes lp-spin-slow { to{transform:rotate(360deg);} }
// // // // // // .lp-how-step-label { font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:var(--gold);margin-bottom:0.6rem; }
// // // // // // .lp-how-title { font-family:var(--serif);font-size:1.65rem;font-weight:600;color:var(--text-primary);line-height:1.15;margin-bottom:0.75rem; }
// // // // // // .lp-how-desc { font-size:13.5px;color:var(--text-muted);line-height:1.8; }

// // // // // // /* ═══════════════════════════════════════
// // // // // //    ENHANCEMENT 4 — BENTO SHIMMER + SOCIAL PILL STAGGER
// // // // // // ═══════════════════════════════════════ */
// // // // // // .lp-feat-bento { display:grid;grid-template-columns:repeat(4,1fr);grid-template-rows:auto auto;gap:10px;margin-top:1rem; }

// // // // // // .lp-feat-bcard {
// // // // // //   background: var(--surface);
// // // // // //   border: 0.5px solid var(--border-color);
// // // // // //   border-radius: 18px; padding: 1.8rem 1.6rem;
// // // // // //   position: relative; overflow: hidden;
// // // // // //   transition: border-color 0.3s, transform 0.25s, background 0.3s;
// // // // // //   cursor: default;
// // // // // // }
// // // // // // /* shimmer sweep on hover */
// // // // // // .lp-feat-bcard::before {
// // // // // //   content: '';
// // // // // //   position: absolute;
// // // // // //   top: 0; left: -100%; width: 60%; height: 100%;
// // // // // //   background: linear-gradient(105deg, transparent, rgba(212,175,55,0.06), transparent);
// // // // // //   transition: left 0s;
// // // // // //   pointer-events: none; z-index: 1;
// // // // // // }
// // // // // // .lp-feat-bcard:hover::before {
// // // // // //   left: 160%;
// // // // // //   transition: left 0.6s ease;
// // // // // // }
// // // // // // .lp-feat-bcard:hover { border-color:var(--border-gold); background:var(--surface2); transform:translateY(-3px); }
// // // // // // .lp-feat-bcard.hero { grid-column:span 2; background:var(--surface2); border-color:var(--border-gold); padding:2.4rem 2.2rem; }
// // // // // // .lp-feat-bcard.tall { grid-row:span 2; display:flex; flex-direction:column; }
// // // // // // .lp-feat-bcard::after { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:transparent;transition:background 0.3s; }
// // // // // // .lp-feat-bcard:hover::after,.lp-feat-bcard.hero::after { background:linear-gradient(90deg,transparent,var(--border-gold),transparent); }

// // // // // // .lp-feat-bicon { width:44px;height:44px;border-radius:12px;background:var(--gold-subtle);border:0.5px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:1.3rem;margin-bottom:1.25rem;flex-shrink:0; }
// // // // // // .lp-feat-bcard.hero .lp-feat-bicon { width:52px;height:52px;font-size:1.6rem;border-radius:14px; }
// // // // // // .lp-feat-btag { display:inline-block;font-size:8.5px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--gold);margin-bottom:0.5rem;opacity:0.8; }
// // // // // // .lp-feat-btitle { font-family:var(--serif);font-size:1.3rem;font-weight:600;color:var(--text-primary);line-height:1.15;margin-bottom:0.5rem; }
// // // // // // .lp-feat-bcard.hero .lp-feat-btitle { font-size:1.65rem; }
// // // // // // .lp-feat-bdesc { font-size:13px;color:var(--text-muted);line-height:1.8;flex:1; }

// // // // // // .lp-feat-wallet { display:flex;gap:6px;margin-top:1.25rem; }
// // // // // // .lp-wallet-chip { height:26px;border-radius:6px;flex:1;background:var(--gold-subtle);border:0.5px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:600;color:var(--gold);letter-spacing:0.5px;position:relative;overflow:hidden; }
// // // // // // /* wallet chip shimmer */
// // // // // // .lp-wallet-chip::after { content:'';position:absolute;top:0;left:-100%;width:50%;height:100%;background:linear-gradient(90deg,transparent,rgba(212,175,55,0.2),transparent);animation:chip-shine 3s ease-in-out infinite; }
// // // // // // .lp-wallet-chip:nth-child(2)::after { animation-delay:0.4s; }
// // // // // // .lp-wallet-chip:nth-child(3)::after { animation-delay:0.8s; }
// // // // // // @keyframes chip-shine { 0%,70%{left:-100%;} 100%{left:200%;} }

// // // // // // /* social pills stagger in when card enters viewport */
// // // // // // .lp-feat-socials { display:flex;flex-wrap:wrap;gap:6px;margin-top:1.1rem; }
// // // // // // .lp-social-pill {
// // // // // //   padding: 4px 10px;
// // // // // //   background: var(--gold-subtle); border: 0.5px solid var(--border-gold);
// // // // // //   border-radius: 100px; font-size: 10px; font-weight: 500; color: var(--text-muted);
// // // // // //   opacity: 0; transform: translateY(6px) scale(0.94);
// // // // // //   transition: opacity 0.35s ease, transform 0.35s ease, background 0.2s, color 0.2s;
// // // // // // }
// // // // // // .lp-social-pill:hover { background: var(--gold-subtle2); color: var(--gold); }
// // // // // // /* stagger delays via nth-child */
// // // // // // .lp-social-pill:nth-child(1) { transition-delay: 0.05s; }
// // // // // // .lp-social-pill:nth-child(2) { transition-delay: 0.12s; }
// // // // // // .lp-social-pill:nth-child(3) { transition-delay: 0.19s; }
// // // // // // .lp-social-pill:nth-child(4) { transition-delay: 0.26s; }
// // // // // // .lp-social-pill:nth-child(5) { transition-delay: 0.33s; }

// // // // // // /* pills animate in when parent card is visible */
// // // // // // .lp-feat-bcard.pills-visible .lp-social-pill {
// // // // // //   opacity: 1; transform: translateY(0) scale(1);
// // // // // // }

// // // // // // .lp-qr-art { margin-top:auto;padding-top:1.5rem;display:grid;grid-template-columns:repeat(5,1fr);grid-template-rows:repeat(5,1fr);gap:3px;opacity:0.2;transition:opacity 0.3s; }
// // // // // // .lp-feat-bcard.tall:hover .lp-qr-art { opacity:0.45; }
// // // // // // .lp-qr-cell { border-radius:2px; }

// // // // // // /* ── USE CASES ── */
// // // // // // .lp-use-tabs { display:flex;flex-wrap:wrap;gap:0.5rem;justify-content:center;margin-bottom:3rem; }
// // // // // // .lp-use-tab { padding:8px 18px;border-radius:100px;font-size:13px;font-weight:600;cursor:pointer;transition:all 0.2s;font-family:var(--sans);display:flex;align-items:center;gap:6px; }
// // // // // // .lp-use-panel { display:grid;grid-template-columns:1fr 1fr;gap:3rem;align-items:center;background:var(--surface);border:0.5px solid var(--border-color);border-radius:24px;padding:3rem;position:relative;overflow:hidden; }
// // // // // // .lp-use-panel::before { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,var(--border-gold),transparent); }
// // // // // // .lp-use-badge { display:inline-flex;align-items:center;gap:8px;padding:6px 14px;border-radius:100px;margin-bottom:1.5rem;font-size:12px;font-weight:700;background:var(--gold-subtle);color:var(--gold);border:0.5px solid var(--border-gold); }
// // // // // // .lp-use-tagline { font-family:var(--serif);font-size:2.2rem;font-weight:600;color:var(--text-primary);line-height:1.1;margin-bottom:1rem; }
// // // // // // .lp-use-desc { font-size:14px;font-weight:400;color:var(--text-muted);line-height:1.9;margin-bottom:2rem; }

// // // // // // /* use case panel transition on tab switch */
// // // // // // .lp-use-content { animation: tab-fade 0.35s cubic-bezier(0.22,1,0.36,1) both; }
// // // // // // @keyframes tab-fade { from{opacity:0;transform:translateY(10px);} to{opacity:1;transform:translateY(0);} }

// // // // // // .lp-use-highlights { display:flex;flex-direction:column;gap:0.75rem; }
// // // // // // .lp-use-hl { display:flex;align-items:center;gap:12px;background:var(--surface2);border:0.5px solid var(--border-color);border-radius:12px;padding:1rem 1.25rem;animation:hl-slide 0.3s ease both; }
// // // // // // .lp-use-hl:nth-child(1){animation-delay:0.05s;} .lp-use-hl:nth-child(2){animation-delay:0.12s;} .lp-use-hl:nth-child(3){animation-delay:0.19s;} .lp-use-hl:nth-child(4){animation-delay:0.26s;}
// // // // // // @keyframes hl-slide { from{opacity:0;transform:translateX(12px);} to{opacity:1;transform:translateX(0);} }
// // // // // // .lp-use-hl-check { width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;background:var(--gold-subtle);color:var(--gold); }
// // // // // // .lp-use-hl-text { font-size:13px;font-weight:500;color:var(--text-muted); }

// // // // // // /* ── PRICING + CTA MERGED ── */
// // // // // // .lp-pricing-cta { padding:5rem 3rem;position:relative;overflow:hidden; }
// // // // // // .lp-pricing-cta::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 70% 80% at 50% 50%,var(--gold-subtle) 0%,transparent 70%);pointer-events:none; }
// // // // // // .lp-pricing-cta-inner { max-width:1180px;margin:0 auto;display:grid;grid-template-columns:1fr 1.1fr;gap:4rem;align-items:center;position:relative; }
// // // // // // .lp-pricing-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--gold);margin-bottom:1.25rem; }
// // // // // // .lp-pricing-headline { font-family:var(--serif);font-size:clamp(2.8rem,5vw,4.2rem);font-weight:600;line-height:1.05;color:var(--text-primary);margin-bottom:1.25rem;letter-spacing:-0.5px; }
// // // // // // .lp-pricing-headline em { font-style:italic;color:var(--gold); }
// // // // // // .lp-pricing-sub { font-size:15px;color:var(--text-muted);line-height:1.9;margin-bottom:2.5rem;max-width:400px; }
// // // // // // .lp-pricing-perks { display:flex;flex-direction:column;gap:0.6rem;margin-bottom:2.5rem; }
// // // // // // .lp-pricing-perk { display:flex;align-items:center;gap:10px;font-size:14px;font-weight:500;color:var(--text-muted); }
// // // // // // .lp-perk-tick { width:20px;height:20px;border-radius:50%;flex-shrink:0;background:var(--gold-subtle);border:0.5px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:9px;color:var(--gold);font-weight:700; }

// // // // // // .lp-price-card { background:var(--surface);border:0.5px solid var(--border-gold);border-radius:24px;padding:2.5rem;position:relative;overflow:hidden; }
// // // // // // .lp-price-card::before { content:'';position:absolute;top:0;left:10%;right:10%;height:1px;background:linear-gradient(90deg,transparent,var(--gold),transparent); }
// // // // // // .lp-price-card-top { display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:1.5rem; }
// // // // // // .lp-price-badge { display:inline-block;background:linear-gradient(135deg,#D4AF37,#F0D060);color:#0A0A0A;border-radius:100px;padding:4px 14px;font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase; }
// // // // // // .lp-price-amount { text-align:right; }
// // // // // // .lp-price-num { font-family:var(--serif);font-size:4rem;font-weight:600;color:var(--text-primary);line-height:1;letter-spacing:-2px; }
// // // // // // .lp-price-period { font-size:10px;font-weight:600;color:var(--text-muted2);letter-spacing:2px;text-transform:uppercase;margin-top:4px; }
// // // // // // .lp-price-divider { height:0.5px;background:var(--border-color);margin:1.25rem 0; }
// // // // // // .lp-price-list { list-style:none;display:flex;flex-direction:column;gap:0;margin-bottom:1.75rem; }
// // // // // // .lp-price-item { display:flex;align-items:center;gap:10px;padding:0.55rem 0;font-size:13px;font-weight:500;color:var(--text-muted);border-bottom:0.5px solid var(--border-color); }
// // // // // // .lp-price-item:last-child { border-bottom:none; }
// // // // // // .lp-price-check { color:var(--gold);font-size:11px;flex-shrink:0; }

// // // // // // /* ── FOOTER ── */
// // // // // // .lp-footer { padding:3rem 3rem 4rem;border-top:0.5px solid var(--border-color);background:var(--surface); }
// // // // // // .lp-footer-inner { max-width:1180px;margin:0 auto;display:flex;flex-direction:column;align-items:center;gap:1rem; }
// // // // // // .lp-footer-logo { display:flex;align-items:center;gap:10px;font-family:var(--sans);font-size:15px;font-weight:600;color:var(--text-primary); }
// // // // // // .lp-footer-logo em { color:var(--gold);font-style:normal;font-weight:700; }
// // // // // // .lp-footer-tagline { font-size:13px;font-weight:400;color:var(--text-muted2); }
// // // // // // .lp-footer-copy { font-size:12px;font-weight:400;color:var(--text-muted2);opacity:0.6; }

// // // // // // /* ── THEME TOGGLE ── */
// // // // // // .lp-theme-btn { width:40px;height:40px;border-radius:50%;border:0.5px solid var(--border-gold);background:var(--gold-subtle);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background 0.2s,transform 0.15s;flex-shrink:0; }
// // // // // // .lp-theme-btn:hover { background:var(--gold-subtle2);transform:scale(1.08); }

// // // // // // /* ── MOBILE ── */
// // // // // // @media (max-width: 900px) {
// // // // // //   .lp-hero { grid-template-columns:1fr;text-align:center;padding:8rem 1.5rem 4rem; }
// // // // // //   .lp-hero-sub { margin:0 auto 1rem; }
// // // // // //   .lp-hero-ctas { justify-content:center; }
// // // // // //   .lp-hero-stats { justify-content:center; }
// // // // // //   .lp-hero-right { margin-top:3rem; }
// // // // // //   .scan-scene { width:300px;height:380px;transform:scale(0.85); }
// // // // // //   .lp-how-timeline { grid-template-columns:1fr; }
// // // // // //   .lp-how-bridge { display:none; }
// // // // // //   .lp-feat-bento { grid-template-columns:1fr 1fr; }
// // // // // //   .lp-feat-bcard.hero { grid-column:span 2; }
// // // // // //   .lp-feat-bcard.tall { grid-row:span 1; }
// // // // // //   .lp-use-panel { grid-template-columns:1fr; }
// // // // // //   .lp-pricing-cta-inner { grid-template-columns:1fr; }
// // // // // //   .lp-nav { padding:1rem 1.5rem; }
// // // // // //   .lp-nav.scrolled { padding:0.75rem 1.5rem; }
// // // // // //   .lp-nav-links { gap:1rem; }
// // // // // //   .lp-section { padding:4rem 1.5rem; }
// // // // // //   .lp-pricing-cta { padding:4rem 1.5rem; }
// // // // // // }
// // // // // // @media (max-width: 600px) {
// // // // // //   .lp-feat-bento { grid-template-columns:1fr; }
// // // // // //   .lp-feat-bcard.hero { grid-column:span 1; }
// // // // // //   .lp-nav-link { display:none; }
// // // // // // }
// // // // // // `;

// // // // // // /* ─────────────────────────────────────────
// // // // // //    ENHANCEMENT 1 — COUNT-UP HOOK
// // // // // // ───────────────────────────────────────── */
// // // // // // function useCountUp(target, duration = 1800, suffix = "", started = false) {
// // // // // //     const [display, setDisplay] = useState("0");
// // // // // //     const [progress, setProgress] = useState(0);
// // // // // //     const frameRef = useRef(null);

// // // // // //     useEffect(() => {
// // // // // //         if (!started) return;
// // // // // //         // parse numeric target
// // // // // //         const num = parseFloat(target.replace(/[^0-9.]/g, ""));
// // // // // //         if (!num) { setDisplay(target); setProgress(100); return; }

// // // // // //         const startTime = performance.now();
// // // // // //         const animate = (now) => {
// // // // // //             const elapsed = now - startTime;
// // // // // //             const pct = Math.min(elapsed / duration, 1);
// // // // // //             // easeOutExpo
// // // // // //             const eased = pct === 1 ? 1 : 1 - Math.pow(2, -10 * pct);
// // // // // //             const current = num * eased;
// // // // // //             const prog = Math.round(pct * 100);

// // // // // //             // format
// // // // // //             let formatted;
// // // // // //             if (Number.isInteger(num)) {
// // // // // //                 formatted = Math.round(current).toString();
// // // // // //             } else {
// // // // // //                 formatted = current.toFixed(0);
// // // // // //             }
// // // // // //             setDisplay(formatted + suffix);
// // // // // //             setProgress(prog);

// // // // // //             if (pct < 1) {
// // // // // //                 frameRef.current = requestAnimationFrame(animate);
// // // // // //             }
// // // // // //         };
// // // // // //         frameRef.current = requestAnimationFrame(animate);
// // // // // //         return () => cancelAnimationFrame(frameRef.current);
// // // // // //     }, [started, target, duration, suffix]);

// // // // // //     return { display, progress };
// // // // // // }

// // // // // // /* ─────────────────────────────────────────
// // // // // //    STAT ITEM
// // // // // // ───────────────────────────────────────── */
// // // // // // function StatItem({ value, suffix, label, started, delay = 0 }) {
// // // // // //     const [go, setGo] = useState(false);
// // // // // //     useEffect(() => {
// // // // // //         if (!started) return;
// // // // // //         const t = setTimeout(() => setGo(true), delay);
// // // // // //         return () => clearTimeout(t);
// // // // // //     }, [started, delay]);

// // // // // //     const { display, progress } = useCountUp(value, 1600, suffix, go);
// // // // // //     const isCounting = go && progress < 100;

// // // // // //     return (
// // // // // //         <div className="lp-stat">
// // // // // //             <div className={`lp-stat-num${isCounting ? " counting" : ""}`}>
// // // // // //                 {value === "0" ? "0" : display}
// // // // // //             </div>
// // // // // //             <div className="lp-stat-label">{label}</div>
// // // // // //             <div className="lp-stat-bar">
// // // // // //                 <div className="lp-stat-bar-fill" style={{ width: `${progress}%` }} />
// // // // // //             </div>
// // // // // //         </div>
// // // // // //     );
// // // // // // }

// // // // // // /* ─────────────────────────────────────────
// // // // // //    QR / SCAN SCENE
// // // // // // ───────────────────────────────────────── */
// // // // // // const QR_PATTERN = [
// // // // // //     [1, 1, 1, 0, 1, 0], [1, 0, 1, 1, 0, 1], [1, 1, 1, 0, 1, 1], [0, 1, 0, 1, 0, 0], [1, 0, 1, 1, 1, 0], [0, 1, 0, 0, 1, 1],
// // // // // // ];

// // // // // // function ScanScene() {
// // // // // //     return (
// // // // // //         <div className="scan-scene">
// // // // // //             <div className="hero-orb-1" />

// // // // // //             {/* Phone A — scanner */}
// // // // // //             <div className="phone-scanner">
// // // // // //                 <div className="phone-scanner-screen">
// // // // // //                     <div className="scanner-viewfinder">
// // // // // //                         <div className="vf-corner-tr" />
// // // // // //                         <div className="vf-corner-bl" />
// // // // // //                         <div className="scan-laser" />
// // // // // //                         <div className="mini-qr">
// // // // // //                             {QR_PATTERN.map((row, ri) =>
// // // // // //                                 row.map((cell, ci) => (
// // // // // //                                     <div key={`${ri}-${ci}`} className="mini-qr-cell"
// // // // // //                                         style={{ background: cell ? "rgba(212,175,55,0.5)" : "transparent" }} />
// // // // // //                                 ))
// // // // // //                             )}
// // // // // //                         </div>
// // // // // //                         {/* Enhancement 5: flash overlay */}
// // // // // //                         <div className="scanner-success-flash" />
// // // // // //                     </div>
// // // // // //                     <div className="scanner-label">Point at QR code</div>
// // // // // //                 </div>
// // // // // //             </div>

// // // // // //             <div className="beam-line" />
// // // // // //             <div className="beam-particle" />

// // // // // //             {/* Enhancement 5: ripple rings */}
// // // // // //             <div className="scan-ripple r1" />
// // // // // //             <div className="scan-ripple r2" />
// // // // // //             <div className="scan-ripple r3" />
// // // // // //             {/* Enhancement 5: success badge */}
// // // // // //             <div className="scan-success">
// // // // // //                 <div className="scan-success-dot" />
// // // // // //                 Contact Saved ✓
// // // // // //             </div>

// // // // // //             {/* Phone B */}
// // // // // //             <div className="phone-profile">
// // // // // //                 <div className="profile-card-wrap">
// // // // // //                     <div className="profile-banner">
// // // // // //                         <div className="profile-banner-img" />
// // // // // //                         <div className="profile-banner-fade" />
// // // // // //                         <div className="profile-avatar-ring">
// // // // // //                             <div className="profile-avatar-inner">UN</div>
// // // // // //                         </div>
// // // // // //                     </div>
// // // // // //                     <div className="profile-info">
// // // // // //                         <div className="profile-info-name">User <em>Name</em></div>
// // // // // //                         <div className="profile-info-role">Director · Company Name</div>
// // // // // //                         <div className="profile-tiles">
// // // // // //                             {[["📞", "Phone"], ["✉️", "Email"], ["📍", "Delhi"]].map(([, label]) => (
// // // // // //                                 <div className="profile-tile" key={label}>
// // // // // //                                     <div className="tile-dot"><div className="tile-dot-inner" /></div>
// // // // // //                                     <div className="tile-text">{label}</div>
// // // // // //                                 </div>
// // // // // //                             ))}
// // // // // //                         </div>
// // // // // //                         <button className="profile-save-btn">↓ Save Contact</button>
// // // // // //                         <div className="profile-tiles" style={{ marginTop: "1rem" }}>
// // // // // //                             {[["🌐", "Website"], ["📄", "Resume"], ["ⓕ", "Facebook"]].map(([, label]) => (
// // // // // //                                 <div className="profile-tile" key={label}>
// // // // // //                                     <div className="tile-dot"><div className="tile-dot-inner" /></div>
// // // // // //                                     <div className="tile-text">{label}</div>
// // // // // //                                 </div>
// // // // // //                             ))}
// // // // // //                         </div>
// // // // // //                     </div>
// // // // // //                 </div>
// // // // // //             </div>
// // // // // //         </div>
// // // // // //     );
// // // // // // }

// // // // // // /* ─────────────────────────────────────────
// // // // // //    PROGRESS ARROW BRIDGE (Enhancement 2)
// // // // // // ───────────────────────────────────────── */
// // // // // // function ArrowBridge({ filled }) {
// // // // // //     return (
// // // // // //         <div className="lp-how-bridge">
// // // // // //             <svg width="36" height="28" viewBox="0 0 36 28" fill="none" overflow="visible">
// // // // // //                 {/* shaft */}
// // // // // //                 <path
// // // // // //                     d="M2 14 L26 14"
// // // // // //                     className={`how-arrow-path${filled ? " filled" : ""}`}
// // // // // //                     strokeWidth="1.5"
// // // // // //                     strokeLinecap="round"
// // // // // //                     fill="none"
// // // // // //                     style={{ strokeDasharray: 28, strokeDashoffset: filled ? 0 : 28, transition: "stroke-dashoffset 0.8s cubic-bezier(0.22,1,0.36,1), stroke 0.4s ease" }}
// // // // // //                 />
// // // // // //                 {/* head */}
// // // // // //                 <path
// // // // // //                     d="M20 7 L28 14 L20 21"
// // // // // //                     className={`how-arrow-path${filled ? " filled" : ""}`}
// // // // // //                     strokeWidth="1.5"
// // // // // //                     strokeLinecap="round"
// // // // // //                     strokeLinejoin="round"
// // // // // //                     fill="none"
// // // // // //                     style={{ strokeDasharray: 24, strokeDashoffset: filled ? 0 : 24, transition: "stroke-dashoffset 0.6s cubic-bezier(0.22,1,0.36,1) 0.3s, stroke 0.4s ease 0.3s" }}
// // // // // //                 />
// // // // // //             </svg>
// // // // // //         </div>
// // // // // //     );
// // // // // // }

// // // // // // /* ─────────────────────────────────────────
// // // // // //    USE CASES
// // // // // // ───────────────────────────────────────── */
// // // // // // const USE_CASES = [
// // // // // //     { icon: "👔", role: "Job Seekers", tagline: "Land your next opportunity faster", desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one elegant scan.", highlights: ["Resume attached & downloadable", "LinkedIn & GitHub links", "QR code for instant sharing", "Save contact in one tap"] },
// // // // // //     { icon: "💼", role: "Freelancers", tagline: "Win clients before you even speak", desc: "Share everything a client needs — portfolio, social proof, and contact — all in a single professional link. Close deals faster.", highlights: ["Portfolio & website link", "WhatsApp quick contact", "Instagram & social proof", "Professional digital presence"] },
// // // // // //     { icon: "🏢", role: "Business Owners", tagline: "Network smarter, not harder", desc: "Stop spending on printed cards that get lost. Share via QR at events — always updated, never outdated, never thrown away.", highlights: ["Always up-to-date details", "Google Wallet for offline use", "QR code for events & meetups", "Share via WhatsApp or email"] },
// // // // // //     { icon: "🎓", role: "Students", tagline: "Build your professional identity early", desc: "Stand out in campus placements and internship drives. Show your skills, projects, and social profiles before you say a word.", highlights: ["GitHub & project links", "Resume upload for placements", "LinkedIn profile showcase", "Free forever — no cost"] },
// // // // // //     { icon: "📣", role: "Sales Pros", tagline: "Close the follow-up gap instantly", desc: "At trade shows or cold visits — one QR scan and your prospect has everything. Number, email, website, product links in one place.", highlights: ["Instant QR share at events", "Website & product links", "Save contact in one tap", "Google Wallet for quick access"] },
// // // // // //     { icon: "🧑‍💻", role: "Developers", tagline: "Your entire dev profile in one link", desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share at hackathons or in open source communities.", highlights: ["GitHub & portfolio links", "Twitter / X profile", "Resume for job applications", "Professional dev identity"] },
// // // // // // ];

// // // // // // function UseCasesSection({ navigate }) {
// // // // // //     const [active, setActive] = useState(0);
// // // // // //     const [tabKey, setTabKey] = useState(0);
// // // // // //     const cur = USE_CASES[active];

// // // // // //     const switchTab = (i) => {
// // // // // //         setActive(i);
// // // // // //         setTabKey(k => k + 1);
// // // // // //     };

// // // // // //     return (
// // // // // //         <section className="lp-section">
// // // // // //             <div className="lp-section-inner">
// // // // // //                 <span className="lp-eyebrow reveal from-up">Who is it for</span>
// // // // // //                 <h2 className="lp-section-title reveal from-up d1">Built for every <em>professional</em></h2>
// // // // // //                 <p className="lp-section-sub reveal from-up d2">Whether you're a student or a CEO — your digital card works for you.</p>

// // // // // //                 <div className="lp-use-tabs reveal from-up d3">
// // // // // //                     {USE_CASES.map((u, i) => (
// // // // // //                         <button key={i} className="lp-use-tab" onClick={() => switchTab(i)}
// // // // // //                             style={{
// // // // // //                                 border: active === i ? "0.5px solid var(--border-gold)" : "0.5px solid var(--border-color)",
// // // // // //                                 background: active === i ? "var(--gold-subtle2)" : "transparent",
// // // // // //                                 color: active === i ? "var(--gold)" : "var(--text-muted)",
// // // // // //                             }}>
// // // // // //                             {u.icon} {u.role}
// // // // // //                         </button>
// // // // // //                     ))}
// // // // // //                 </div>

// // // // // //                 <div className="lp-use-panel reveal from-up d4" style={{ borderColor: "var(--border-gold)" }}>
// // // // // //                     {/* Left content — re-animates on tab switch */}
// // // // // //                     <div key={`left-${tabKey}`} className="lp-use-content">
// // // // // //                         <div className="lp-use-badge"><span>{cur.icon}</span><span style={{ fontSize: "12px", letterSpacing: "0.5px" }}>{cur.role}</span></div>
// // // // // //                         <div className="lp-use-tagline">{cur.tagline}</div>
// // // // // //                         <p className="lp-use-desc">{cur.desc}</p>
// // // // // //                         <button className="lp-btn-gold" onClick={() => navigate("/animation/card")}>Create My Card — Free →</button>
// // // // // //                     </div>

// // // // // //                     {/* Right highlights — each item slides in from right */}
// // // // // //                     <div key={`right-${tabKey}`} className="lp-use-highlights">
// // // // // //                         {cur.highlights.map((h, i) => (
// // // // // //                             <div className="lp-use-hl" key={i} style={{ animationDelay: `${0.05 + i * 0.07}s` }}>
// // // // // //                                 <div className="lp-use-hl-check">✓</div>
// // // // // //                                 <span className="lp-use-hl-text">{h}</span>
// // // // // //                             </div>
// // // // // //                         ))}
// // // // // //                     </div>
// // // // // //                 </div>
// // // // // //             </div>
// // // // // //         </section>
// // // // // //     );
// // // // // // }

// // // // // // /* ─────────────────────────────────────────
// // // // // //    MAIN COMPONENT
// // // // // // ───────────────────────────────────────── */
// // // // // // export default function LandingPage() {
// // // // // //     const navigate = useNavigate();
// // // // // //     const [scrolled, setScrolled] = useState(false);
// // // // // //     const [isLight, setIsLight] = useState(false);
// // // // // //     const [statsStarted, setStatsStarted] = useState(false);
// // // // // //     // Enhancement 2: track if how-section is in view for arrow progress
// // // // // //     const [howVisible, setHowVisible] = useState(false);
// // // // // //     // Enhancement 4: track when social card enters view
// // // // // //     const socialCardRef = useRef(null);

// // // // // //     const statsRef = useRef(null);
// // // // // //     const howRef = useRef(null);

// // // // // //     /* ── scroll nav ── */
// // // // // //     useEffect(() => {
// // // // // //         const onScroll = () => setScrolled(window.scrollY > 40);
// // // // // //         window.addEventListener("scroll", onScroll);
// // // // // //         return () => window.removeEventListener("scroll", onScroll);
// // // // // //     }, []);

// // // // // //     /* ── unified IntersectionObserver ── */
// // // // // //     useEffect(() => {
// // // // // //         const observers = [];

// // // // // //         // 1. directional reveals
// // // // // //         const revealEls = document.querySelectorAll(".reveal");
// // // // // //         const revealObs = new IntersectionObserver(
// // // // // //             (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
// // // // // //             { threshold: 0.1 }
// // // // // //         );
// // // // // //         revealEls.forEach(el => revealObs.observe(el));
// // // // // //         observers.push(revealObs);

// // // // // //         // 2. count-up trigger
// // // // // //         if (statsRef.current) {
// // // // // //             const statsObs = new IntersectionObserver(
// // // // // //                 ([entry]) => { if (entry.isIntersecting) setStatsStarted(true); },
// // // // // //                 { threshold: 0.5 }
// // // // // //             );
// // // // // //             statsObs.observe(statsRef.current);
// // // // // //             observers.push(statsObs);
// // // // // //         }

// // // // // //         // 3. how-section for arrow progress connector
// // // // // //         if (howRef.current) {
// // // // // //             const howObs = new IntersectionObserver(
// // // // // //                 ([entry]) => { if (entry.isIntersecting) setHowVisible(true); },
// // // // // //                 { threshold: 0.3 }
// // // // // //             );
// // // // // //             howObs.observe(howRef.current);
// // // // // //             observers.push(howObs);
// // // // // //         }

// // // // // //         // 4. social pills card
// // // // // //         const pillCards = document.querySelectorAll(".lp-feat-bcard.has-pills");
// // // // // //         const pillObs = new IntersectionObserver(
// // // // // //             (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("pills-visible"); }),
// // // // // //             { threshold: 0.4 }
// // // // // //         );
// // // // // //         pillCards.forEach(c => pillObs.observe(c));
// // // // // //         observers.push(pillObs);

// // // // // //         return () => observers.forEach(o => o.disconnect());
// // // // // //     }, []);

// // // // // //     return (
// // // // // //         <div className={isLight ? "lp-light" : ""} style={{ background: isLight ? "#FFFFFF" : "#080808", minHeight: "100vh" }}>
// // // // // //             <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

// // // // // //             {/* ── NAV ── */}
// // // // // //             <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
// // // // // //                 <div className="lp-nav-inner">
// // // // // //                     <a href="/" className="lp-logo">
// // // // // //                         <svg className="lp-logo-icon" viewBox="0 0 34 34" fill="none">
// // // // // //                             <rect width="34" height="34" rx="9" fill={isLight ? "#F0EDE6" : "#1A1A1A"} />
// // // // // //                             <rect width="34" height="34" rx="9" stroke="var(--border-gold)" strokeWidth="0.5" />
// // // // // //                             <circle cx="15" cy="13" r="5" stroke="var(--gold)" strokeWidth="1.5" fill="none" />
// // // // // //                             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
// // // // // //                             <path d="M23 11 L27 11 L27 23" stroke="var(--gold)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.6" />
// // // // // //                             <circle cx="27" cy="23" r="1.8" fill="var(--gold)" />
// // // // // //                         </svg>
// // // // // //                         myprofile<em>corner</em>
// // // // // //                     </a>
// // // // // //                     <div className="lp-nav-links">
// // // // // //                         <a href="#how" className="lp-nav-link">How it works</a>
// // // // // //                         <a href="#features" className="lp-nav-link">Features</a>
// // // // // //                         <a href="#pricing" className="lp-nav-link">Pricing</a>
// // // // // //                         <button className="lp-theme-btn" onClick={() => setIsLight(p => !p)} aria-label="Toggle theme">
// // // // // //                             {isLight ? (
// // // // // //                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
// // // // // //                                     <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
// // // // // //                                 </svg>
// // // // // //                             ) : (
// // // // // //                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
// // // // // //                                     <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
// // // // // //                                     <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
// // // // // //                                     <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
// // // // // //                                     <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
// // // // // //                                 </svg>
// // // // // //                             )}
// // // // // //                         </button>
// // // // // //                         <button className="lp-nav-cta" onClick={() => navigate("/animation/card")}>Create Free Card</button>
// // // // // //                     </div>
// // // // // //                 </div>
// // // // // //             </nav>

// // // // // //             {/* ── HERO ── */}
// // // // // //             <section style={{ background: isLight ? "#FFFFFF" : "#080808", overflow: "hidden", position: "relative" }}>
// // // // // //                 <div className="lp-hero">
// // // // // //                     <div style={{ position: "absolute", width: "600px", height: "600px", borderRadius: "50%", background: "radial-gradient(circle, var(--gold-subtle) 0%, transparent 65%)", top: "-200px", left: "-100px", pointerEvents: "none" }} />

// // // // // //                     <div className="lp-hero-left">
// // // // // //                         <div className="lp-hero-eyebrow reveal from-down">
// // // // // //                             <div className="lp-hero-eyebrow-dot" /> 100% Free · No App Needed
// // // // // //                         </div>
// // // // // //                         <h1 className="lp-hero-title reveal from-left">
// // // // // //                             Your Digital<br />Identity, <em>Always</em><br />in Your Pocket
// // // // // //                         </h1>
// // // // // //                         <p className="lp-hero-sub reveal from-left d1">
// // // // // //                             Replace paper visiting cards with a smart digital card. Share your profile instantly via QR code, link, or Google Wallet — no printing, no waste.
// // // // // //                         </p>

// // // // // //                         {/* Enhancement 1: Count-up stats */}
// // // // // //                         <div className="lp-hero-stats" ref={statsRef}>
// // // // // //                             <StatItem value="100" suffix="%" label="Free forever" started={statsStarted} delay={0} />
// // // // // //                             <StatItem value="2" suffix=" min" label="Setup time" started={statsStarted} delay={200} />
// // // // // //                             <StatItem value="0" suffix="" label="Paper waste" started={statsStarted} delay={400} />
// // // // // //                         </div>

// // // // // //                         <div className="lp-hero-ctas reveal from-up d2">
// // // // // //                             <button className="lp-btn-gold" onClick={() => navigate("/animation/card")}>Create My Card — It's Free →</button>
// // // // // //                             <a href="#how" className="lp-btn-ghost">See how it works</a>
// // // // // //                         </div>
// // // // // //                     </div>

// // // // // //                     <div className="lp-hero-right reveal from-right">
// // // // // //                         <ScanScene />
// // // // // //                     </div>
// // // // // //                 </div>
// // // // // //             </section>

// // // // // //             <div className="gold-rule" />

// // // // // //             {/* ── TRUST BAR ── */}
// // // // // //             <div className="lp-trust">
// // // // // //                 <span className="lp-trust-label">Works seamlessly with</span>
// // // // // //                 <div className="lp-trust-items">
// // // // // //                     {[["🔥", "Firebase"], ["🪙", "Google Wallet"], ["📱", "Android & iOS"], ["🔗", "Any Browser"]].map(([icon, label], i, arr) => (
// // // // // //                         <React.Fragment key={label}>
// // // // // //                             <div className="lp-trust-item"><span>{icon}</span><span>{label}</span></div>
// // // // // //                             {i < arr.length - 1 && <span className="lp-trust-sep">·</span>}
// // // // // //                         </React.Fragment>
// // // // // //                     ))}
// // // // // //                 </div>
// // // // // //             </div>

// // // // // //             <div className="gold-rule" />

// // // // // //             {/* ── HOW IT WORKS ── */}
// // // // // //             <section id="how" className="lp-section" ref={howRef}>
// // // // // //                 <div className="lp-section-inner">
// // // // // //                     <span className="lp-eyebrow reveal from-up">How it works</span>
// // // // // //                     <h2 className="lp-section-title reveal from-up d1">From setup to sharing <em>in 3 steps</em></h2>
// // // // // //                     <p className="lp-section-sub reveal from-up d2">No app downloads. No tech skills required. Just fill, share, impress.</p>

// // // // // //                     {/* Enhancement 2: progress connectors + directional card reveals */}
// // // // // //                     <div className="lp-how-timeline">
// // // // // //                         <div className="lp-how-card reveal from-left d1">
// // // // // //                             <div className="lp-how-num">01</div>
// // // // // //                             <div className="lp-how-icon-ring">✏️</div>
// // // // // //                             <div className="lp-how-step-label">Step 01</div>
// // // // // //                             <div className="lp-how-title">Fill Your Profile</div>
// // // // // //                             <p className="lp-how-desc">Add your name, photo, contact info, social links, resume & website. Takes less than 2 minutes.</p>
// // // // // //                         </div>

// // // // // //                         <ArrowBridge filled={howVisible} />

// // // // // //                         <div className="lp-how-card reveal from-up d3">
// // // // // //                             <div className="lp-how-num">02</div>
// // // // // //                             <div className="lp-how-icon-ring">🎴</div>
// // // // // //                             <div className="lp-how-step-label">Step 02</div>
// // // // // //                             <div className="lp-how-title">Get Your Digital Card</div>
// // // // // //                             <p className="lp-how-desc">Instantly get a beautiful card with a unique QR code & shareable link — no design skills needed.</p>
// // // // // //                         </div>

// // // // // //                         <ArrowBridge filled={howVisible} />

// // // // // //                         <div className="lp-how-card reveal from-right d5">
// // // // // //                             <div className="lp-how-num">03</div>
// // // // // //                             <div className="lp-how-icon-ring">🚀</div>
// // // // // //                             <div className="lp-how-step-label">Step 03</div>
// // // // // //                             <div className="lp-how-title">Share Anywhere</div>
// // // // // //                             <p className="lp-how-desc">Show QR in meetings, add to Google Wallet, or send your link via WhatsApp, email, or LinkedIn.</p>
// // // // // //                         </div>
// // // // // //                     </div>
// // // // // //                 </div>
// // // // // //             </section>

// // // // // //             <div className="gold-rule" />

// // // // // //             {/* ── FEATURES ── */}
// // // // // //             <section id="features" className="lp-section">
// // // // // //                 <div className="lp-section-inner">
// // // // // //                     <span className="lp-eyebrow reveal from-up">Features</span>
// // // // // //                     <h2 className="lp-section-title reveal from-up d1">Everything a modern <em>professional</em> needs</h2>
// // // // // //                     <p className="lp-section-sub reveal from-up d2">One card. Every detail. Always up-to-date.</p>

// // // // // //                     {/* Enhancement 4: bento with shimmer + social pill stagger */}
// // // // // //                     <div className="lp-feat-bento">

// // // // // //                         {/* Hero: Google Wallet — reveals from left */}
// // // // // //                         <div className="lp-feat-bcard hero reveal from-left d1">
// // // // // //                             <div className="lp-feat-bicon">🪙</div>
// // // // // //                             <div className="lp-feat-btag">Premium Feature</div>
// // // // // //                             <div className="lp-feat-btitle">Google Wallet Pass</div>
// // // // // //                             <p className="lp-feat-bdesc">Add your card to Google Wallet and access it offline — at meetings, events, or anywhere without internet.</p>
// // // // // //                             <div className="lp-feat-wallet">
// // // // // //                                 <div className="lp-wallet-chip">G Pay</div>
// // // // // //                                 <div className="lp-wallet-chip">Wallet</div>
// // // // // //                                 <div className="lp-wallet-chip">Offline ✓</div>
// // // // // //                             </div>
// // // // // //                         </div>

// // // // // //                         {/* QR tall — reveals from right */}
// // // // // //                         <div className="lp-feat-bcard tall reveal from-right d2">
// // // // // //                             <div className="lp-feat-bicon">📲</div>
// // // // // //                             <div className="lp-feat-btag">Instant Share</div>
// // // // // //                             <div className="lp-feat-btitle">Unique QR Code</div>
// // // // // //                             <p className="lp-feat-bdesc">Every card gets a dedicated QR. One scan opens your full profile — no typing, no friction.</p>
// // // // // //                             <div className="lp-qr-art">
// // // // // //                                 {[1, 1, 1, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 1].map((v, i) => (
// // // // // //                                     <div key={i} className="lp-qr-cell" style={{ background: v ? "var(--gold)" : "var(--gold-subtle)", borderRadius: "2px", aspectRatio: "1" }} />
// // // // // //                                 ))}
// // // // // //                             </div>
// // // // // //                         </div>

// // // // // //                         {/* Photo — from below */}
// // // // // //                         <div className="lp-feat-bcard reveal from-up d3">
// // // // // //                             <div className="lp-feat-bicon">📸</div>
// // // // // //                             <div className="lp-feat-btag">Identity</div>
// // // // // //                             <div className="lp-feat-btitle">Profile Photo</div>
// // // // // //                             <p className="lp-feat-bdesc">Your face on your card — and inside Google Wallet too.</p>
// // // // // //                         </div>

// // // // // //                         {/* Save contact — from below */}
// // // // // //                         <div className="lp-feat-bcard reveal from-up d4">
// // // // // //                             <div className="lp-feat-bicon">💾</div>
// // // // // //                             <div className="lp-feat-btag">One Tap</div>
// // // // // //                             <div className="lp-feat-btitle">Save Contact</div>
// // // // // //                             <p className="lp-feat-bdesc">Visitors save you directly to their phone contacts with a single tap.</p>
// // // // // //                         </div>

// // // // // //                         {/* Hero: Social Links — from left, pills stagger */}
// // // // // //                         <div className="lp-feat-bcard hero has-pills reveal from-left d2">
// // // // // //                             <div className="lp-feat-bicon">🔗</div>
// // // // // //                             <div className="lp-feat-btag">Connections</div>
// // // // // //                             <div className="lp-feat-btitle">All Social Links</div>
// // // // // //                             <p className="lp-feat-bdesc">LinkedIn, GitHub, Instagram, Twitter, WhatsApp, YouTube — everything that matters in one place.</p>
// // // // // //                             <div className="lp-feat-socials">
// // // // // //                                 {["in LinkedIn", "⌥ GitHub", "✉ WhatsApp", "▶ YouTube", "📷 Insta"].map(s => (
// // // // // //                                     <span key={s} className="lp-social-pill">{s}</span>
// // // // // //                                 ))}
// // // // // //                             </div>
// // // // // //                         </div>

// // // // // //                         <div className="lp-feat-bcard reveal from-up d3">
// // // // // //                             <div className="lp-feat-bicon">📄</div>
// // // // // //                             <div className="lp-feat-btag">Career</div>
// // // // // //                             <div className="lp-feat-btitle">Resume Upload</div>
// // // // // //                             <p className="lp-feat-bdesc">Attach your PDF resume. Recruiters can download it instantly.</p>
// // // // // //                         </div>
// // // // // //                         <div className="lp-feat-bcard reveal from-up d4">
// // // // // //                             <div className="lp-feat-bicon">✏️</div>
// // // // // //                             <div className="lp-feat-btag">Always Fresh</div>
// // // // // //                             <div className="lp-feat-btitle">Edit Anytime</div>
// // // // // //                             <p className="lp-feat-bdesc">Changed jobs? New number? Update instantly — same link forever.</p>
// // // // // //                         </div>
// // // // // //                         <div className="lp-feat-bcard reveal from-right d5">
// // // // // //                             <div className="lp-feat-bicon">🌐</div>
// // // // // //                             <div className="lp-feat-btag">Universal</div>
// // // // // //                             <div className="lp-feat-btitle">Works Everywhere</div>
// // // // // //                             <p className="lp-feat-bdesc">Opens in any browser on any device. No app install ever required.</p>
// // // // // //                         </div>

// // // // // //                     </div>
// // // // // //                 </div>
// // // // // //             </section>

// // // // // //             <div className="gold-rule" />

// // // // // //             {/* ── USE CASES ── */}
// // // // // //             <UseCasesSection navigate={navigate} />

// // // // // //             <div className="gold-rule" />

// // // // // //             {/* ── PRICING + CTA MERGED ── */}
// // // // // //             <section id="pricing" className="lp-pricing-cta">
// // // // // //                 <div className="lp-pricing-cta-inner">
// // // // // //                     <div className="lp-pricing-copy">
// // // // // //                         <span className="lp-pricing-eyebrow reveal from-left">Pricing</span>
// // // // // //                         <h2 className="lp-pricing-headline reveal from-left d1">Stop carrying<br /><em>paper cards.</em></h2>
// // // // // //                         <p className="lp-pricing-sub reveal from-left d2">Create your digital card in under 2 minutes. No hidden charges. No credit card. Free forever.</p>
// // // // // //                         <div className="lp-pricing-perks reveal from-left d3">
// // // // // //                             {["No app download needed", "Unique QR code & shareable link", "Google Wallet pass included", "Edit anytime — same link forever", "Works on every device & browser"].map(p => (
// // // // // //                                 <div className="lp-pricing-perk" key={p}>
// // // // // //                                     <div className="lp-perk-tick">✓</div><span>{p}</span>
// // // // // //                                 </div>
// // // // // //                             ))}
// // // // // //                         </div>
// // // // // //                         <button className="lp-btn-gold reveal from-up d4" onClick={() => navigate("/animation/card")}>Create My Card Now →</button>
// // // // // //                     </div>
// // // // // //                     <div className="lp-price-card reveal from-right d2">
// // // // // //                         <div className="lp-price-card-top">
// // // // // //                             <div className="lp-price-badge">Most Popular</div>
// // // // // //                             <div className="lp-price-amount">
// // // // // //                                 <div className="lp-price-num">₹0</div>
// // // // // //                                 <div className="lp-price-period">Forever Free</div>
// // // // // //                             </div>
// // // // // //                         </div>
// // // // // //                         <div className="lp-price-divider" />
// // // // // //                         <ul className="lp-price-list">
// // // // // //                             {["1 Digital Visiting Card", "Unique QR Code & Link", "Profile Photo Upload", "Resume Upload (PDF)", "All Social Media Links", "Google Wallet Pass", "Save Contact (.vcf)", "Edit Anytime", "No App Required"].map(item => (
// // // // // //                                 <li className="lp-price-item" key={item}><span className="lp-price-check">✦</span>{item}</li>
// // // // // //                             ))}
// // // // // //                         </ul>
// // // // // //                         <button className="lp-btn-gold" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/animation/card")}>Get Started Free →</button>
// // // // // //                     </div>
// // // // // //                 </div>
// // // // // //             </section>

// // // // // //             <div className="gold-rule" />

// // // // // //             {/* ── FOOTER ── */}
// // // // // //             <footer className="lp-footer">
// // // // // //                 <div className="lp-footer-inner">
// // // // // //                     <div className="lp-footer-logo">
// // // // // //                         <svg width="28" height="28" viewBox="0 0 34 34" fill="none">
// // // // // //                             <rect width="34" height="34" rx="9" fill={isLight ? "#F0EDE6" : "#1A1A1A"} />
// // // // // //                             <rect width="34" height="34" rx="9" stroke="var(--border-gold)" strokeWidth="0.5" />
// // // // // //                             <circle cx="15" cy="13" r="5" stroke="var(--gold)" strokeWidth="1.5" fill="none" />
// // // // // //                             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
// // // // // //                             <path d="M23 11 L27 11 L27 23" stroke="var(--gold)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.6" />
// // // // // //                             <circle cx="27" cy="23" r="1.8" fill="var(--gold)" />
// // // // // //                         </svg>
// // // // // //                         myprofile<em>corner</em>
// // // // // //                     </div>
// // // // // //                     <p className="lp-footer-tagline">Your digital identity, always in your pocket.</p>
// // // // // //                     <p className="lp-footer-copy">© {new Date().getFullYear()} MyProfileCorner · Made with ♥ in India</p>
// // // // // //                 </div>
// // // // // //             </footer>
// // // // // //         </div>
// // // // // //     );
// // // // // // }

// // // // // // // import React, { useState, useEffect } from "react";
// // // // // // // import { useNavigate } from "react-router-dom";

// // // // // // // const FONT_LINK = `
// // // // // // //   @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600&family=Montserrat:wght@300;400;500;600;700;800&display=swap');
// // // // // // // `;

// // // // // // // const GLOBAL_CSS = `
// // // // // // // ${FONT_LINK}

// // // // // // // *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

// // // // // // // :root {
// // // // // // //   --black: #080808;
// // // // // // //   --dark: #0E0E0E;
// // // // // // //   --card: #141414;
// // // // // // //   --border: rgba(255,255,255,0.07);
// // // // // // //   --gold: #D4AF37;
// // // // // // //   --gold2: #F0D060;
// // // // // // //   --gold3: #C9A227;
// // // // // // //   --gold-dim: rgba(212,175,55,0.18);
// // // // // // //   --white: #FFFFFF;
// // // // // // //   --muted: rgba(255,255,255,0.45);
// // // // // // //   --muted2: rgba(255,255,255,0.25);
// // // // // // //   --serif: 'Cormorant Garamond', serif;
// // // // // // //   --sans: 'Montserrat', sans-serif;
// // // // // // //   --bg: #080808;
// // // // // // //   --surface: #141414;
// // // // // // //   --surface2: #1a1a1a;
// // // // // // //   --text-primary: #FFFFFF;
// // // // // // //   --text-muted: rgba(255,255,255,0.45);
// // // // // // //   --text-muted2: rgba(255,255,255,0.25);
// // // // // // //   --border-color: rgba(255,255,255,0.07);
// // // // // // //   --border-gold: rgba(212,175,55,0.2);
// // // // // // //   --gold-subtle: rgba(212,175,55,0.08);
// // // // // // //   --gold-subtle2: rgba(212,175,55,0.12);
// // // // // // // }

// // // // // // // /* ── LIGHT THEME ── */
// // // // // // // .lp-light {
// // // // // // //   --bg: #FFFFFF;
// // // // // // //   --surface: #F5F3EE;
// // // // // // //   --surface2: #EDE9E0;
// // // // // // //   --card: #F5F3EE;
// // // // // // //   --dark: #EDE9E0;
// // // // // // //   --black: #FFFFFF;
// // // // // // //   --border-color: rgba(0,0,0,0.08);
// // // // // // //   --border: rgba(0,0,0,0.08);
// // // // // // //   --border-gold: rgba(180,140,30,0.25);
// // // // // // //   --white: #0E0E0E;
// // // // // // //   --text-primary: #111111;
// // // // // // //   --text-muted: rgba(0,0,0,0.5);
// // // // // // //   --text-muted2: rgba(0,0,0,0.35);
// // // // // // //   --muted: rgba(0,0,0,0.5);
// // // // // // //   --muted2: rgba(0,0,0,0.35);
// // // // // // //   --gold: #A0780A;
// // // // // // //   --gold2: #C9991A;
// // // // // // //   --gold3: #8A6608;
// // // // // // //   --gold-dim: rgba(160,120,10,0.15);
// // // // // // //   --gold-subtle: rgba(160,120,10,0.08);
// // // // // // //   --gold-subtle2: rgba(160,120,10,0.12);
// // // // // // // }

// // // // // // // html { scroll-behavior: smooth; }

// // // // // // // body {
// // // // // // //   background: var(--bg);
// // // // // // //   color: var(--text-primary);
// // // // // // //   font-family: var(--sans);
// // // // // // //   overflow-x: hidden;
// // // // // // //   letter-spacing: 0.1px;
// // // // // // // }

// // // // // // // ::-webkit-scrollbar { width: 4px; }
// // // // // // // ::-webkit-scrollbar-track { background: var(--bg); }
// // // // // // // ::-webkit-scrollbar-thumb { background: rgba(212,175,55,0.3); border-radius: 2px; }

// // // // // // // /* ── NAV ── */
// // // // // // // .lp-nav {
// // // // // // //   position: fixed; top: 0; left: 0; right: 0; z-index: 200;
// // // // // // //   padding: 1.5rem 3rem;
// // // // // // //   transition: all 0.4s;
// // // // // // // }
// // // // // // // .lp-nav.scrolled {
// // // // // // //   background: rgba(8,8,8,0.9);
// // // // // // //   backdrop-filter: blur(20px);
// // // // // // //   border-bottom: 0.5px solid rgba(212,175,55,0.12);
// // // // // // //   padding: 1rem 3rem;
// // // // // // // }
// // // // // // // .lp-light .lp-nav.scrolled {
// // // // // // //   background: rgba(255,255,255,0.92);
// // // // // // //   border-bottom: 0.5px solid rgba(160,120,10,0.18);
// // // // // // // }
// // // // // // // .lp-nav-inner {
// // // // // // //   max-width: 1180px; margin: 0 auto;
// // // // // // //   display: flex; align-items: center; justify-content: space-between;
// // // // // // // }
// // // // // // // .lp-logo {
// // // // // // //   display: flex; align-items: center; gap: 10px;
// // // // // // //   font-family: var(--sans); font-size: 15px; font-weight: 600;
// // // // // // //   color: var(--text-primary); text-decoration: none; letter-spacing: 0.2px;
// // // // // // // }
// // // // // // // .lp-logo-icon { width: 34px; height: 34px; flex-shrink: 0; }
// // // // // // // .lp-logo em { color: var(--gold); font-style: normal; font-weight: 700; }
// // // // // // // .lp-nav-links { display: flex; align-items: center; gap: 2.5rem; }
// // // // // // // .lp-nav-link {
// // // // // // //   font-size: 13px; font-weight: 500; color: var(--text-muted);
// // // // // // //   text-decoration: none; letter-spacing: 0.3px; transition: color 0.2s;
// // // // // // // }
// // // // // // // .lp-nav-link:hover { color: var(--text-primary); }
// // // // // // // .lp-nav-cta {
// // // // // // //   padding: 10px 22px;
// // // // // // //   background: linear-gradient(135deg, #D4AF37, #F0D060, #C9A227);
// // // // // // //   color: #0A0A0A; border: none; border-radius: 10px;
// // // // // // //   font-family: var(--sans); font-size: 13px; font-weight: 700;
// // // // // // //   letter-spacing: 0.8px; text-transform: uppercase;
// // // // // // //   cursor: pointer; position: relative; overflow: hidden;
// // // // // // //   transition: transform 0.15s, opacity 0.15s;
// // // // // // // }
// // // // // // // .lp-nav-cta::before {
// // // // // // //   content: ''; position: absolute; inset: 0;
// // // // // // //   background: linear-gradient(135deg, rgba(255,255,255,0.2), transparent 60%);
// // // // // // //   pointer-events: none;
// // // // // // // }
// // // // // // // .lp-nav-cta:hover { transform: translateY(-1px); opacity: 0.92; }

// // // // // // // /* ── HERO ── */
// // // // // // // .lp-hero {
// // // // // // //   min-height: 100vh;
// // // // // // //   display: grid; grid-template-columns: 1fr 1fr;
// // // // // // //   align-items: center; gap: 4rem;
// // // // // // //   max-width: 1180px; margin: 0 auto;
// // // // // // //   padding: 10rem 3rem 3rem; position: relative;
// // // // // // // }
// // // // // // // .lp-hero-left { position: relative; z-index: 2; }

// // // // // // // .lp-hero-eyebrow {
// // // // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // // // //   padding: 6px 14px;
// // // // // // //   background: var(--gold-subtle2);
// // // // // // //   border: 0.5px solid var(--border-gold);
// // // // // // //   border-radius: 100px;
// // // // // // //   font-size: 11px; font-weight: 700; color: var(--gold);
// // // // // // //   letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 2rem;
// // // // // // // }
// // // // // // // .lp-hero-eyebrow-dot {
// // // // // // //   width: 5px; height: 5px; border-radius: 50%; background: var(--gold);
// // // // // // //   animation: pulse-gold 2s ease-in-out infinite;
// // // // // // // }
// // // // // // // @keyframes pulse-gold {
// // // // // // //   0%, 100% { opacity: 1; transform: scale(1); }
// // // // // // //   50% { opacity: 0.4; transform: scale(0.7); }
// // // // // // // }
// // // // // // // .lp-hero-title {
// // // // // // //   font-family: var(--serif);
// // // // // // //   font-size: clamp(3.2rem, 5.8vw, 5.3rem);
// // // // // // //   font-weight: 600; line-height: 1.02;
// // // // // // //   letter-spacing: -0.8px; color: var(--text-primary); margin-bottom: 1.75rem;
// // // // // // // }
// // // // // // // .lp-hero-title em { font-style: italic; font-weight: 600; color: var(--gold); }
// // // // // // // .lp-hero-sub {
// // // // // // //   font-size: 15px; font-weight: 400;
// // // // // // //   color: var(--text-muted); line-height: 1.9;
// // // // // // //   max-width: 470px; margin-bottom: 1rem;
// // // // // // // }
// // // // // // // .lp-hero-ctas { display: flex; gap: 1rem; flex-wrap: wrap; }

// // // // // // // .lp-btn-gold {
// // // // // // //   padding: 16px 32px;
// // // // // // //   background: linear-gradient(135deg, #D4AF37, #F0D060, #C9A227);
// // // // // // //   color: #0A0A0A; border: none; border-radius: 12px;
// // // // // // //   font-family: var(--sans); font-size: 13px; font-weight: 700;
// // // // // // //   letter-spacing: 1px; text-transform: uppercase;
// // // // // // //   cursor: pointer; position: relative; overflow: hidden;
// // // // // // //   transition: transform 0.2s, box-shadow 0.2s;
// // // // // // //   display: flex; align-items: center; gap: 8px;
// // // // // // // }
// // // // // // // .lp-btn-gold::before {
// // // // // // //   content: ''; position: absolute; inset: 0;
// // // // // // //   background: linear-gradient(135deg, rgba(255,255,255,0.25), transparent 60%);
// // // // // // //   pointer-events: none;
// // // // // // // }
// // // // // // // .lp-btn-gold:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(212,175,55,0.25); }

// // // // // // // .lp-btn-ghost {
// // // // // // //   padding: 16px 28px; background: transparent;
// // // // // // //   color: var(--text-muted); border: 0.5px solid var(--border-color);
// // // // // // //   border-radius: 12px; font-family: var(--sans); font-size: 13px; font-weight: 600;
// // // // // // //   letter-spacing: 0.5px; cursor: pointer; text-decoration: none;
// // // // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // // // //   transition: border-color 0.2s, color 0.2s;
// // // // // // // }
// // // // // // // .lp-btn-ghost:hover { border-color: var(--border-gold); color: var(--text-primary); }

// // // // // // // /* ── HERO STATS ── */
// // // // // // // .lp-hero-stats { display: flex; gap: 2.5rem; padding-bottom: 1rem; }
// // // // // // // .lp-stat-num {
// // // // // // //   font-family: var(--serif); font-size: 2.2rem; font-weight: 600;
// // // // // // //   color: var(--gold); line-height: 1;
// // // // // // // }
// // // // // // // .lp-stat-label {
// // // // // // //   font-size: 11px; font-weight: 500; color: var(--text-muted2);
// // // // // // //   letter-spacing: 0.5px; margin-top: 4px;
// // // // // // // }

// // // // // // // /* ── HERO RIGHT ── */
// // // // // // // .lp-hero-right {
// // // // // // //   position: relative; display: flex;
// // // // // // //   align-items: center; justify-content: center; z-index: 2;
// // // // // // // }
// // // // // // // .scan-scene { position: relative; width: 380px; height: 480px; }

// // // // // // // /* Phone A */
// // // // // // // .phone-scanner {
// // // // // // //   position: absolute; left: 0; top: 60px;
// // // // // // //   width: 155px; height: 270px; background: #1A1A1A;
// // // // // // //   border-radius: 22px; border: 1px solid rgba(255,255,255,0.1);
// // // // // // //   overflow: hidden; box-shadow: 0 24px 60px rgba(0,0,0,0.6);
// // // // // // //   z-index: 3;
// // // // // // //   animation: phone-a-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;
// // // // // // //   animation-delay: 0.3s;
// // // // // // // }
// // // // // // // @keyframes phone-a-intro {
// // // // // // //   from { opacity: 0; transform: translateX(-30px) rotate(-6deg); }
// // // // // // //   to   { opacity: 1; transform: translateX(0) rotate(-6deg); }
// // // // // // // }
// // // // // // // .phone-scanner-screen {
// // // // // // //   width: 100%; height: 100%; background: #111;
// // // // // // //   display: flex; flex-direction: column;
// // // // // // //   align-items: center; justify-content: center; gap: 12px; padding: 12px;
// // // // // // // }
// // // // // // // .scanner-viewfinder {
// // // // // // //   width: 100px; height: 100px; border-radius: 10px;
// // // // // // //   position: relative; background: rgba(212,175,55,0.04);
// // // // // // // }
// // // // // // // .scanner-viewfinder::before, .scanner-viewfinder::after {
// // // // // // //   content: ''; position: absolute; width: 20px; height: 20px;
// // // // // // //   border-color: #D4AF37; border-style: solid;
// // // // // // // }
// // // // // // // .scanner-viewfinder::before { top: 0; left: 0; border-width: 2px 0 0 2px; border-radius: 6px 0 0 0; }
// // // // // // // .scanner-viewfinder::after  { bottom: 0; right: 0; border-width: 0 2px 2px 0; border-radius: 0 0 6px 0; }
// // // // // // // .vf-corner-tr { position: absolute; top: 0; right: 0; width: 20px; height: 20px; border-top: 2px solid #D4AF37; border-right: 2px solid #D4AF37; border-radius: 0 6px 0 0; }
// // // // // // // .vf-corner-bl { position: absolute; bottom: 0; left: 0; width: 20px; height: 20px; border-bottom: 2px solid #D4AF37; border-left: 2px solid #D4AF37; border-radius: 0 0 0 6px; }
// // // // // // // .scan-laser {
// // // // // // //   position: absolute; left: 4px; right: 4px; height: 2px;
// // // // // // //   background: linear-gradient(90deg, transparent, #D4AF37, transparent);
// // // // // // //   border-radius: 1px; top: 4px;
// // // // // // //   animation: laser-sweep 2s ease-in-out infinite; animation-delay: 1.2s;
// // // // // // // }
// // // // // // // @keyframes laser-sweep {
// // // // // // //   0%   { top: 8px; opacity: 0; }
// // // // // // //   10%  { opacity: 1; }
// // // // // // //   90%  { opacity: 1; }
// // // // // // //   100% { top: 88px; opacity: 0; }
// // // // // // // }
// // // // // // // .scanner-label { font-size: 9px; font-weight: 600; color: rgba(255,255,255,0.4); letter-spacing: 1px; text-align: center; text-transform: uppercase; }
// // // // // // // .mini-qr { position: absolute; inset: 10px; display: grid; grid-template-columns: repeat(6,1fr); grid-template-rows: repeat(6,1fr); gap: 1.5px; opacity: 0.6; }
// // // // // // // .mini-qr-cell { border-radius: 1px; }

// // // // // // // /* Phone B */
// // // // // // // .phone-profile {
// // // // // // //   position: absolute; right: 0; top: 0;
// // // // // // //   width: 175px; height: 310px; background: #0E0E0E;
// // // // // // //   border-radius: 24px; border: 1px solid rgba(212,175,55,0.2);
// // // // // // //   overflow: hidden;
// // // // // // //   box-shadow: 0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(212,175,55,0.08);
// // // // // // //   z-index: 4;
// // // // // // //   animation: phone-b-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both; animation-delay: 0.5s;
// // // // // // // }
// // // // // // // @keyframes phone-b-intro {
// // // // // // //   from { opacity: 0; transform: translateY(30px) rotate(4deg); }
// // // // // // //   to   { opacity: 1; transform: translateY(0) rotate(4deg); }
// // // // // // // }
// // // // // // // .profile-card-wrap {
// // // // // // //   width: 100%; height: 100%;
// // // // // // //   animation: card-reveal 0.7s cubic-bezier(0.22,1,0.36,1) both; animation-delay: 2s;
// // // // // // // }
// // // // // // // @keyframes card-reveal {
// // // // // // //   from { transform: translateY(40px); opacity: 0; }
// // // // // // //   to   { transform: translateY(0); opacity: 1; }
// // // // // // // }
// // // // // // // .profile-banner { width: 100%; height: 110px; background: linear-gradient(160deg, #1a1a1a, #2a2020); position: relative; overflow: hidden; }
// // // // // // // .profile-banner-img { width: 100%; height: 100%; background: linear-gradient(160deg, #1e1a10 0%, #2a2010 50%, #181818 100%); }
// // // // // // // .profile-banner-fade { position: absolute; bottom: 0; left: 0; right: 0; height: 50px; background: linear-gradient(transparent, #0E0E0E); }
// // // // // // // .profile-avatar-ring {
// // // // // // //   position: absolute; bottom: -20px; left: 14px;
// // // // // // //   width: 44px; height: 44px; border-radius: 50%;
// // // // // // //   border: 2px solid #0E0E0E; background: #222; overflow: hidden;
// // // // // // //   box-shadow: 0 0 0 1.5px rgba(212,175,55,0.5);
// // // // // // //   animation: avatar-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both; animation-delay: 2.4s;
// // // // // // // }
// // // // // // // @keyframes avatar-pop { from { transform: scale(0.3); opacity: 0; } to { transform: scale(1); opacity: 1; } }
// // // // // // // .profile-avatar-inner {
// // // // // // //   width: 100%; height: 100%;
// // // // // // //   background: linear-gradient(145deg, #2a2010, #3a3020);
// // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // //   font-family: 'Cormorant Garamond', serif; font-size: 14px; font-weight: 600;
// // // // // // //   color: rgba(212,175,55,0.8); letter-spacing: 1px;
// // // // // // // }
// // // // // // // .profile-info { padding: 28px 14px 12px; }
// // // // // // // .profile-info-name { font-family: 'Cormorant Garamond', serif; font-size: 16px; font-weight: 600; color: #FFF; line-height: 1.2; margin-bottom: 2px; }
// // // // // // // .profile-info-name em { font-style: italic; color: #D4AF37; }
// // // // // // // .profile-info-role { font-size: 8px; font-weight: 600; color: rgba(255,255,255,0.45); letter-spacing: 0.3px; margin-bottom: 10px; }
// // // // // // // .profile-tiles { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 5px; margin-bottom: 8px; }
// // // // // // // .profile-tile { background: #1A1A1A; border: 0.5px solid rgba(255,255,255,0.06); border-radius: 8px; padding: 7px 6px; display: flex; flex-direction: column; gap: 4px; animation: tile-slide 0.3s ease both; }
// // // // // // // .profile-tile:nth-child(1) { animation-delay: 2.5s; }
// // // // // // // .profile-tile:nth-child(2) { animation-delay: 2.65s; }
// // // // // // // .profile-tile:nth-child(3) { animation-delay: 2.8s; }
// // // // // // // @keyframes tile-slide { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
// // // // // // // .tile-dot { width: 12px; height: 12px; border-radius: 50%; background: rgba(212,175,55,0.15); display: flex; align-items: center; justify-content: center; }
// // // // // // // .tile-dot-inner { width: 5px; height: 5px; border-radius: 50%; background: #D4AF37; }
// // // // // // // .tile-text { font-size: 7px; color: rgba(255,255,255,0.4); line-height: 1.3; font-weight: 500; }
// // // // // // // .profile-save-btn {
// // // // // // //   width: 100%; padding: 8px;
// // // // // // //   background: linear-gradient(135deg, #D4AF37, #F0D060);
// // // // // // //   color: #0A0A0A; border: none; border-radius: 8px;
// // // // // // //   font-size: 8px; font-weight: 700; letter-spacing: 0.8px;
// // // // // // //   text-transform: uppercase; cursor: default;
// // // // // // //   display: flex; align-items: center; justify-content: center; gap: 4px;
// // // // // // //   animation: tile-slide 0.3s ease both; animation-delay: 2.95s;
// // // // // // // }
// // // // // // // .beam-line {
// // // // // // //   position: absolute; top: 165px; left: 148px;
// // // // // // //   width: 90px; height: 1px;
// // // // // // //   background: linear-gradient(90deg, #D4AF37, rgba(212,175,55,0));
// // // // // // //   transform-origin: left center;
// // // // // // //   animation: beam-appear 0.4s ease both; animation-delay: 1.5s; z-index: 5;
// // // // // // // }
// // // // // // // @keyframes beam-appear { from { opacity: 0; } to { opacity: 1; } }
// // // // // // // .beam-particle {
// // // // // // //   position: absolute; top: 160px; left: 148px;
// // // // // // //   width: 6px; height: 6px; border-radius: 50%; background: #D4AF37;
// // // // // // //   animation: particle-travel 2s ease-in-out infinite; animation-delay: 1.6s; z-index: 6;
// // // // // // // }
// // // // // // // @keyframes particle-travel {
// // // // // // //   0%   { left: 148px; opacity: 0; transform: scale(0.5); }
// // // // // // //   10%  { opacity: 1; transform: scale(1); }
// // // // // // //   90%  { opacity: 1; }
// // // // // // //   100% { left: 228px; opacity: 0; transform: scale(0.5); }
// // // // // // // }
// // // // // // // .hero-orb-1 {
// // // // // // //   position: absolute; width: 500px; height: 500px; border-radius: 50%;
// // // // // // //   background: radial-gradient(circle, rgba(212,175,55,0.06) 0%, transparent 65%);
// // // // // // //   top: -100px; right: -100px; pointer-events: none; z-index: 0;
// // // // // // // }

// // // // // // // /* ── SECTION SHARED ── */
// // // // // // // .lp-section { padding: 5rem 3rem; position: relative; }
// // // // // // // .lp-section-inner { max-width: 1180px; margin: 0 auto; }
// // // // // // // .lp-eyebrow {
// // // // // // //   display: block; font-size: 10px; font-weight: 700;
// // // // // // //   letter-spacing: 3px; text-transform: uppercase;
// // // // // // //   color: var(--gold); margin-bottom: 1.25rem; text-align: center;
// // // // // // // }
// // // // // // // .lp-section-title {
// // // // // // //   font-family: var(--serif);
// // // // // // //   font-size: clamp(2.3rem, 4vw, 3.5rem);
// // // // // // //   font-weight: 600; line-height: 1.08; letter-spacing: -0.4px;
// // // // // // //   color: var(--text-primary); text-align: center; margin-bottom: 1.25rem;
// // // // // // // }
// // // // // // // .lp-section-title em { font-style: italic; color: var(--gold); }
// // // // // // // .lp-section-sub {
// // // // // // //   font-size: 15px; font-weight: 400; color: var(--text-muted);
// // // // // // //   text-align: center; max-width: 520px; margin: 0 auto 4rem; line-height: 1.9;
// // // // // // // }

// // // // // // // .gold-rule {
// // // // // // //   height: 0.5px;
// // // // // // //   background: linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent);
// // // // // // // }
// // // // // // // .lp-light .gold-rule {
// // // // // // //   background: linear-gradient(90deg, transparent, rgba(160,120,10,0.2), transparent);
// // // // // // // }

// // // // // // // /* ── TRUST BAR ── */
// // // // // // // .lp-trust {
// // // // // // //   padding: 2rem 3rem;
// // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // //   gap: 2.5rem; flex-wrap: wrap;
// // // // // // //   background: var(--surface);
// // // // // // // }
// // // // // // // .lp-trust-label { font-size: 10px; font-weight: 600; color: var(--text-muted2); letter-spacing: 2px; text-transform: uppercase; }
// // // // // // // .lp-trust-items { display: flex; align-items: center; gap: 2rem; flex-wrap: wrap; }
// // // // // // // .lp-trust-item { display: flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 500; color: var(--text-muted); }
// // // // // // // .lp-trust-sep { color: var(--border-gold); font-size: 18px; }

// // // // // // // /* ── HOW IT WORKS ── */
// // // // // // // .lp-how-timeline {
// // // // // // //   display: grid;
// // // // // // //   grid-template-columns: 1fr 64px 1fr 64px 1fr;
// // // // // // //   align-items: start; gap: 0; margin-top: 1rem;
// // // // // // // }
// // // // // // // .lp-how-bridge {
// // // // // // //   display: flex; align-items: flex-start; justify-content: center;
// // // // // // //   padding-top: 2.6rem;
// // // // // // // }
// // // // // // // .lp-how-card {
// // // // // // //   background: var(--surface);
// // // // // // //   border: 0.5px solid var(--border-color);
// // // // // // //   border-radius: 20px; padding: 2.2rem 1.8rem 2rem;
// // // // // // //   position: relative; overflow: hidden;
// // // // // // //   transition: border-color 0.3s, transform 0.3s;
// // // // // // //   cursor: default;
// // // // // // // }
// // // // // // // .lp-how-card::before {
// // // // // // //   content: ''; position: absolute; inset: 0;
// // // // // // //   background: radial-gradient(ellipse 80% 60% at 50% -10%, var(--gold-subtle2) 0%, transparent 70%);
// // // // // // //   opacity: 0; transition: opacity 0.3s;
// // // // // // // }
// // // // // // // .lp-how-card:hover { border-color: var(--border-gold); transform: translateY(-4px); }
// // // // // // // .lp-how-card:hover::before { opacity: 1; }
// // // // // // // .lp-how-num {
// // // // // // //   font-family: var(--serif); font-size: 7rem; font-weight: 700;
// // // // // // //   color: var(--gold-subtle2); line-height: 1;
// // // // // // //   position: absolute; bottom: -0.5rem; right: 0.8rem;
// // // // // // //   letter-spacing: -3px; pointer-events: none; user-select: none;
// // // // // // //   opacity: 0.6;
// // // // // // // }
// // // // // // // .lp-light .lp-how-num { opacity: 1; color: rgba(160,120,10,0.1); }
// // // // // // // .lp-how-icon-ring {
// // // // // // //   width: 56px; height: 56px; border-radius: 16px;
// // // // // // //   background: var(--gold-subtle);
// // // // // // //   border: 1px solid var(--border-gold);
// // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // //   font-size: 1.6rem; margin-bottom: 1.5rem; position: relative;
// // // // // // // }
// // // // // // // .lp-how-icon-ring::after {
// // // // // // //   content: ''; position: absolute; inset: -4px; border-radius: 20px;
// // // // // // //   border: 1px dashed var(--border-gold);
// // // // // // //   animation: lp-spin-slow 12s linear infinite;
// // // // // // // }
// // // // // // // @keyframes lp-spin-slow { to { transform: rotate(360deg); } }
// // // // // // // .lp-how-step-label {
// // // // // // //   font-size: 9px; font-weight: 700; letter-spacing: 2.5px;
// // // // // // //   text-transform: uppercase; color: var(--gold); margin-bottom: 0.6rem;
// // // // // // // }
// // // // // // // .lp-how-title {
// // // // // // //   font-family: var(--serif); font-size: 1.65rem; font-weight: 600;
// // // // // // //   color: var(--text-primary); line-height: 1.15; margin-bottom: 0.75rem;
// // // // // // // }
// // // // // // // .lp-how-desc { font-size: 13.5px; color: var(--text-muted); line-height: 1.8; }

// // // // // // // /* ── FEATURES BENTO ── */
// // // // // // // .lp-feat-bento {
// // // // // // //   display: grid;
// // // // // // //   grid-template-columns: repeat(4, 1fr);
// // // // // // //   grid-template-rows: auto auto;
// // // // // // //   gap: 10px; margin-top: 1rem;
// // // // // // // }
// // // // // // // .lp-feat-bcard {
// // // // // // //   background: var(--surface);
// // // // // // //   border: 0.5px solid var(--border-color);
// // // // // // //   border-radius: 18px; padding: 1.8rem 1.6rem;
// // // // // // //   position: relative; overflow: hidden;
// // // // // // //   transition: border-color 0.3s, transform 0.25s, background 0.3s;
// // // // // // //   cursor: default;
// // // // // // // }
// // // // // // // .lp-feat-bcard:hover {
// // // // // // //   border-color: var(--border-gold);
// // // // // // //   background: var(--surface2);
// // // // // // //   transform: translateY(-3px);
// // // // // // // }
// // // // // // // .lp-feat-bcard.hero {
// // // // // // //   grid-column: span 2;
// // // // // // //   background: var(--surface2);
// // // // // // //   border-color: var(--border-gold);
// // // // // // //   padding: 2.4rem 2.2rem;
// // // // // // // }
// // // // // // // .lp-feat-bcard.tall { grid-row: span 2; display: flex; flex-direction: column; }
// // // // // // // .lp-feat-bcard::after {
// // // // // // //   content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
// // // // // // //   background: transparent; transition: background 0.3s;
// // // // // // // }
// // // // // // // .lp-feat-bcard:hover::after,
// // // // // // // .lp-feat-bcard.hero::after {
// // // // // // //   background: linear-gradient(90deg, transparent, var(--border-gold), transparent);
// // // // // // // }
// // // // // // // .lp-feat-bicon {
// // // // // // //   width: 44px; height: 44px; border-radius: 12px;
// // // // // // //   background: var(--gold-subtle); border: 0.5px solid var(--border-gold);
// // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // //   font-size: 1.3rem; margin-bottom: 1.25rem; flex-shrink: 0;
// // // // // // // }
// // // // // // // .lp-feat-bcard.hero .lp-feat-bicon { width: 52px; height: 52px; font-size: 1.6rem; border-radius: 14px; }
// // // // // // // .lp-feat-btag {
// // // // // // //   display: inline-block; font-size: 8.5px; font-weight: 700;
// // // // // // //   letter-spacing: 2px; text-transform: uppercase;
// // // // // // //   color: var(--gold); margin-bottom: 0.5rem; opacity: 0.8;
// // // // // // // }
// // // // // // // .lp-feat-btitle {
// // // // // // //   font-family: var(--serif); font-size: 1.3rem; font-weight: 600;
// // // // // // //   color: var(--text-primary); line-height: 1.15; margin-bottom: 0.5rem;
// // // // // // // }
// // // // // // // .lp-feat-bcard.hero .lp-feat-btitle { font-size: 1.65rem; }
// // // // // // // .lp-feat-bdesc { font-size: 13px; color: var(--text-muted); line-height: 1.8; flex: 1; }
// // // // // // // .lp-feat-wallet { display: flex; gap: 6px; margin-top: 1.25rem; }
// // // // // // // .lp-wallet-chip {
// // // // // // //   height: 26px; border-radius: 6px; flex: 1;
// // // // // // //   background: var(--gold-subtle);
// // // // // // //   border: 0.5px solid var(--border-gold);
// // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // //   font-size: 9px; font-weight: 600; color: var(--gold); letter-spacing: 0.5px;
// // // // // // // }
// // // // // // // .lp-feat-socials { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 1.1rem; }
// // // // // // // .lp-social-pill {
// // // // // // //   padding: 4px 10px;
// // // // // // //   background: var(--gold-subtle); border: 0.5px solid var(--border-gold);
// // // // // // //   border-radius: 100px; font-size: 10px; font-weight: 500; color: var(--text-muted);
// // // // // // // }
// // // // // // // .lp-qr-art {
// // // // // // //   margin-top: auto; padding-top: 1.5rem;
// // // // // // //   display: grid; grid-template-columns: repeat(5,1fr);
// // // // // // //   grid-template-rows: repeat(5,1fr); gap: 3px;
// // // // // // //   opacity: 0.2; transition: opacity 0.3s;
// // // // // // // }
// // // // // // // .lp-feat-bcard.tall:hover .lp-qr-art { opacity: 0.4; }
// // // // // // // .lp-qr-cell { border-radius: 2px; }

// // // // // // // /* ── USE CASES ── */
// // // // // // // .lp-use-tabs {
// // // // // // //   display: flex; flex-wrap: wrap; gap: 0.5rem;
// // // // // // //   justify-content: center; margin-bottom: 3rem;
// // // // // // // }
// // // // // // // .lp-use-tab {
// // // // // // //   padding: 8px 18px; border-radius: 100px;
// // // // // // //   font-size: 13px; font-weight: 600; cursor: pointer;
// // // // // // //   transition: all 0.2s; font-family: var(--sans);
// // // // // // //   display: flex; align-items: center; gap: 6px;
// // // // // // // }
// // // // // // // .lp-use-panel {
// // // // // // //   display: grid; grid-template-columns: 1fr 1fr;
// // // // // // //   gap: 3rem; align-items: center;
// // // // // // //   background: var(--surface);
// // // // // // //   border: 0.5px solid var(--border-color);
// // // // // // //   border-radius: 24px; padding: 3rem;
// // // // // // //   position: relative; overflow: hidden;
// // // // // // // }
// // // // // // // .lp-use-panel::before {
// // // // // // //   content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
// // // // // // //   background: linear-gradient(90deg, transparent, var(--border-gold), transparent);
// // // // // // // }
// // // // // // // .lp-use-badge {
// // // // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // // // //   padding: 6px 14px; border-radius: 100px;
// // // // // // //   margin-bottom: 1.5rem; font-size: 12px; font-weight: 700;
// // // // // // //   background: var(--gold-subtle); color: var(--gold);
// // // // // // //   border: 0.5px solid var(--border-gold);
// // // // // // // }
// // // // // // // .lp-use-tagline {
// // // // // // //   font-family: var(--serif); font-size: 2.2rem; font-weight: 600;
// // // // // // //   color: var(--text-primary); line-height: 1.1; margin-bottom: 1rem;
// // // // // // // }
// // // // // // // .lp-use-desc { font-size: 14px; font-weight: 400; color: var(--text-muted); line-height: 1.9; margin-bottom: 2rem; }
// // // // // // // .lp-use-highlights { display: flex; flex-direction: column; gap: 0.75rem; }
// // // // // // // .lp-use-hl {
// // // // // // //   display: flex; align-items: center; gap: 12px;
// // // // // // //   background: var(--surface2); border: 0.5px solid var(--border-color);
// // // // // // //   border-radius: 12px; padding: 1rem 1.25rem;
// // // // // // // }
// // // // // // // .lp-use-hl-check {
// // // // // // //   width: 28px; height: 28px; border-radius: 50%;
// // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // //   flex-shrink: 0; font-size: 11px; font-weight: 700;
// // // // // // //   background: var(--gold-subtle); color: var(--gold);
// // // // // // // }
// // // // // // // .lp-use-hl-text { font-size: 13px; font-weight: 500; color: var(--text-muted); }

// // // // // // // /* ── PRICING + CTA MERGED ── */
// // // // // // // .lp-pricing-cta {
// // // // // // //   padding: 5rem 3rem;
// // // // // // //   position: relative; overflow: hidden;
// // // // // // // }
// // // // // // // .lp-pricing-cta::before {
// // // // // // //   content: '';
// // // // // // //   position: absolute; inset: 0;
// // // // // // //   background: radial-gradient(ellipse 70% 80% at 50% 50%, var(--gold-subtle) 0%, transparent 70%);
// // // // // // //   pointer-events: none;
// // // // // // // }
// // // // // // // .lp-pricing-cta-inner {
// // // // // // //   max-width: 1180px; margin: 0 auto;
// // // // // // //   display: grid; grid-template-columns: 1fr 1.1fr;
// // // // // // //   gap: 4rem; align-items: center;
// // // // // // //   position: relative;
// // // // // // // }
// // // // // // // /* left: headline copy */
// // // // // // // .lp-pricing-copy {}
// // // // // // // .lp-pricing-eyebrow {
// // // // // // //   display: block; font-size: 10px; font-weight: 700;
// // // // // // //   letter-spacing: 3px; text-transform: uppercase;
// // // // // // //   color: var(--gold); margin-bottom: 1.25rem;
// // // // // // // }
// // // // // // // .lp-pricing-headline {
// // // // // // //   font-family: var(--serif);
// // // // // // //   font-size: clamp(2.8rem, 5vw, 4.2rem);
// // // // // // //   font-weight: 600; line-height: 1.05;
// // // // // // //   color: var(--text-primary); margin-bottom: 1.25rem;
// // // // // // //   letter-spacing: -0.5px;
// // // // // // // }
// // // // // // // .lp-pricing-headline em { font-style: italic; color: var(--gold); }
// // // // // // // .lp-pricing-sub {
// // // // // // //   font-size: 15px; color: var(--text-muted); line-height: 1.9;
// // // // // // //   margin-bottom: 2.5rem; max-width: 400px;
// // // // // // // }
// // // // // // // .lp-pricing-perks { display: flex; flex-direction: column; gap: 0.6rem; margin-bottom: 2.5rem; }
// // // // // // // .lp-pricing-perk {
// // // // // // //   display: flex; align-items: center; gap: 10px;
// // // // // // //   font-size: 14px; font-weight: 500; color: var(--text-muted);
// // // // // // // }
// // // // // // // .lp-perk-tick {
// // // // // // //   width: 20px; height: 20px; border-radius: 50%; flex-shrink: 0;
// // // // // // //   background: var(--gold-subtle); border: 0.5px solid var(--border-gold);
// // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // //   font-size: 9px; color: var(--gold); font-weight: 700;
// // // // // // // }

// // // // // // // /* right: price card */
// // // // // // // .lp-price-card {
// // // // // // //   background: var(--surface);
// // // // // // //   border: 0.5px solid var(--border-gold);
// // // // // // //   border-radius: 24px; padding: 2.5rem;
// // // // // // //   position: relative; overflow: hidden;
// // // // // // // }
// // // // // // // .lp-price-card::before {
// // // // // // //   content: ''; position: absolute; top: 0; left: 10%; right: 10%; height: 1px;
// // // // // // //   background: linear-gradient(90deg, transparent, var(--gold), transparent);
// // // // // // // }
// // // // // // // .lp-price-card-top {
// // // // // // //   display: flex; align-items: flex-start; justify-content: space-between;
// // // // // // //   margin-bottom: 1.5rem;
// // // // // // // }
// // // // // // // .lp-price-badge {
// // // // // // //   display: inline-block;
// // // // // // //   background: linear-gradient(135deg, #D4AF37, #F0D060);
// // // // // // //   color: #0A0A0A; border-radius: 100px;
// // // // // // //   padding: 4px 14px; font-size: 10px; font-weight: 700;
// // // // // // //   letter-spacing: 1px; text-transform: uppercase;
// // // // // // // }
// // // // // // // .lp-price-amount {
// // // // // // //   text-align: right;
// // // // // // // }
// // // // // // // .lp-price-num {
// // // // // // //   font-family: var(--serif); font-size: 4rem; font-weight: 600;
// // // // // // //   color: var(--text-primary); line-height: 1; letter-spacing: -2px;
// // // // // // // }
// // // // // // // .lp-price-period {
// // // // // // //   font-size: 10px; font-weight: 600; color: var(--text-muted2);
// // // // // // //   letter-spacing: 2px; text-transform: uppercase; margin-top: 4px;
// // // // // // // }
// // // // // // // .lp-price-divider {
// // // // // // //   height: 0.5px; background: var(--border-color); margin: 1.25rem 0;
// // // // // // // }
// // // // // // // .lp-price-list { list-style: none; display: flex; flex-direction: column; gap: 0; margin-bottom: 1.75rem; }
// // // // // // // .lp-price-item {
// // // // // // //   display: flex; align-items: center; gap: 10px;
// // // // // // //   padding: 0.55rem 0; font-size: 13px; font-weight: 500;
// // // // // // //   color: var(--text-muted); border-bottom: 0.5px solid var(--border-color);
// // // // // // // }
// // // // // // // .lp-price-item:last-child { border-bottom: none; }
// // // // // // // .lp-price-check { color: var(--gold); font-size: 11px; flex-shrink: 0; }

// // // // // // // /* ── FOOTER ── */
// // // // // // // .lp-footer { padding: 3rem 3rem 4rem; border-top: 0.5px solid var(--border-color); background: var(--surface); }
// // // // // // // .lp-footer-inner { max-width: 1180px; margin: 0 auto; display: flex; flex-direction: column; align-items: center; gap: 1rem; }
// // // // // // // .lp-footer-logo { display: flex; align-items: center; gap: 10px; font-family: var(--sans); font-size: 15px; font-weight: 600; color: var(--text-primary); }
// // // // // // // .lp-footer-logo em { color: var(--gold); font-style: normal; font-weight: 700; }
// // // // // // // .lp-footer-tagline { font-size: 13px; font-weight: 400; color: var(--text-muted2); }
// // // // // // // .lp-footer-copy { font-size: 12px; font-weight: 400; color: var(--text-muted2); opacity: 0.6; }

// // // // // // // /* ── REVEAL ── */
// // // // // // // .reveal { opacity: 0; transform: translateY(24px); transition: opacity 0.7s ease, transform 0.7s ease; }
// // // // // // // .reveal.visible { opacity: 1; transform: translateY(0); }

// // // // // // // /* ── THEME TOGGLE ── */
// // // // // // // .lp-theme-btn {
// // // // // // //   width: 40px; height: 40px; border-radius: 50%;
// // // // // // //   border: 0.5px solid var(--border-gold);
// // // // // // //   background: var(--gold-subtle);
// // // // // // //   cursor: pointer; display: flex; align-items: center; justify-content: center;
// // // // // // //   transition: background 0.2s, transform 0.15s; flex-shrink: 0;
// // // // // // // }
// // // // // // // .lp-theme-btn:hover { background: var(--gold-subtle2); transform: scale(1.08); }

// // // // // // // /* ── MOBILE ── */
// // // // // // // @media (max-width: 900px) {
// // // // // // //   .lp-hero { grid-template-columns: 1fr; text-align: center; padding: 8rem 1.5rem 4rem; }
// // // // // // //   .lp-hero-sub { margin: 0 auto 1rem; }
// // // // // // //   .lp-hero-ctas { justify-content: center; }
// // // // // // //   .lp-hero-stats { justify-content: center; }
// // // // // // //   .lp-hero-right { margin-top: 3rem; }
// // // // // // //   .scan-scene { width: 300px; height: 380px; transform: scale(0.85); }
// // // // // // //   .lp-how-timeline { grid-template-columns: 1fr; }
// // // // // // //   .lp-how-bridge { display: none; }
// // // // // // //   .lp-feat-bento { grid-template-columns: 1fr 1fr; }
// // // // // // //   .lp-feat-bcard.hero { grid-column: span 2; }
// // // // // // //   .lp-feat-bcard.tall { grid-row: span 1; }
// // // // // // //   .lp-use-panel { grid-template-columns: 1fr; }
// // // // // // //   .lp-pricing-cta-inner { grid-template-columns: 1fr; }
// // // // // // //   .lp-nav { padding: 1rem 1.5rem; }
// // // // // // //   .lp-nav.scrolled { padding: 0.75rem 1.5rem; }
// // // // // // //   .lp-nav-links { gap: 1rem; }
// // // // // // //   .lp-section { padding: 4rem 1.5rem; }
// // // // // // //   .lp-pricing-cta { padding: 4rem 1.5rem; }
// // // // // // // }
// // // // // // // @media (max-width: 600px) {
// // // // // // //   .lp-feat-bento { grid-template-columns: 1fr; }
// // // // // // //   .lp-feat-bcard.hero { grid-column: span 1; }
// // // // // // //   .lp-nav-link { display: none; }
// // // // // // // }
// // // // // // // `;

// // // // // // // const QR_PATTERN = [
// // // // // // //     [1, 1, 1, 0, 1, 0], [1, 0, 1, 1, 0, 1], [1, 1, 1, 0, 1, 1], [0, 1, 0, 1, 0, 0], [1, 0, 1, 1, 1, 0], [0, 1, 0, 0, 1, 1],
// // // // // // // ];

// // // // // // // function ScanScene() {
// // // // // // //     return (
// // // // // // //         <div className="scan-scene">
// // // // // // //             <div className="hero-orb-1" />
// // // // // // //             <div className="phone-scanner">
// // // // // // //                 <div className="phone-scanner-screen">
// // // // // // //                     <div className="scanner-viewfinder">
// // // // // // //                         <div className="vf-corner-tr" />
// // // // // // //                         <div className="vf-corner-bl" />
// // // // // // //                         <div className="scan-laser" />
// // // // // // //                         <div className="mini-qr">
// // // // // // //                             {QR_PATTERN.map((row, ri) =>
// // // // // // //                                 row.map((cell, ci) => (
// // // // // // //                                     <div key={`${ri}-${ci}`} className="mini-qr-cell"
// // // // // // //                                         style={{ background: cell ? "rgba(212,175,55,0.5)" : "transparent" }} />
// // // // // // //                                 ))
// // // // // // //                             )}
// // // // // // //                         </div>
// // // // // // //                     </div>
// // // // // // //                     <div className="scanner-label">Point at QR code</div>
// // // // // // //                 </div>
// // // // // // //             </div>
// // // // // // //             <div className="beam-line" />
// // // // // // //             <div className="beam-particle" />
// // // // // // //             <div className="phone-profile">
// // // // // // //                 <div className="profile-card-wrap">
// // // // // // //                     <div className="profile-banner">
// // // // // // //                         <div className="profile-banner-img" />
// // // // // // //                         <div className="profile-banner-fade" />
// // // // // // //                         <div className="profile-avatar-ring">
// // // // // // //                             <div className="profile-avatar-inner">UN</div>
// // // // // // //                         </div>
// // // // // // //                     </div>
// // // // // // //                     <div className="profile-info">
// // // // // // //                         <div className="profile-info-name">User <em>Name</em></div>
// // // // // // //                         <div className="profile-info-role">Director · Company Name</div>
// // // // // // //                         <div className="profile-tiles">
// // // // // // //                             {[["📞", "Phone"], ["✉️", "Email"], ["📍", "Delhi"]].map(([, label]) => (
// // // // // // //                                 <div className="profile-tile" key={label}>
// // // // // // //                                     <div className="tile-dot"><div className="tile-dot-inner" /></div>
// // // // // // //                                     <div className="tile-text">{label}</div>
// // // // // // //                                 </div>
// // // // // // //                             ))}
// // // // // // //                         </div>
// // // // // // //                         <button className="profile-save-btn">↓ Save Contact</button>
// // // // // // //                         <div className="profile-tiles" style={{ marginTop: "1rem" }}>
// // // // // // //                             {[["🌐", "Website"], ["📄", "Resume"], ["ⓕ", "Facebook"]].map(([, label]) => (
// // // // // // //                                 <div className="profile-tile" key={label}>
// // // // // // //                                     <div className="tile-dot"><div className="tile-dot-inner" /></div>
// // // // // // //                                     <div className="tile-text">{label}</div>
// // // // // // //                                 </div>
// // // // // // //                             ))}
// // // // // // //                         </div>
// // // // // // //                     </div>
// // // // // // //                 </div>
// // // // // // //             </div>
// // // // // // //         </div>
// // // // // // //     );
// // // // // // // }

// // // // // // // const USE_CASES = [
// // // // // // //     { icon: "👔", role: "Job Seekers", tagline: "Land your next opportunity faster", desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one elegant scan.", highlights: ["Resume attached & downloadable", "LinkedIn & GitHub links", "QR code for instant sharing", "Save contact in one tap"] },
// // // // // // //     { icon: "💼", role: "Freelancers", tagline: "Win clients before you even speak", desc: "Share everything a client needs — portfolio, social proof, and contact — all in a single professional link. Close deals faster.", highlights: ["Portfolio & website link", "WhatsApp quick contact", "Instagram & social proof", "Professional digital presence"] },
// // // // // // //     { icon: "🏢", role: "Business Owners", tagline: "Network smarter, not harder", desc: "Stop spending on printed cards that get lost. Share via QR at events — always updated, never outdated, never thrown away.", highlights: ["Always up-to-date details", "Google Wallet for offline use", "QR code for events & meetups", "Share via WhatsApp or email"] },
// // // // // // //     { icon: "🎓", role: "Students", tagline: "Build your professional identity early", desc: "Stand out in campus placements and internship drives. Show your skills, projects, and social profiles before you say a word.", highlights: ["GitHub & project links", "Resume upload for placements", "LinkedIn profile showcase", "Free forever — no cost"] },
// // // // // // //     { icon: "📣", role: "Sales Pros", tagline: "Close the follow-up gap instantly", desc: "At trade shows or cold visits — one QR scan and your prospect has everything. Number, email, website, product links in one place.", highlights: ["Instant QR share at events", "Website & product links", "Save contact in one tap", "Google Wallet for quick access"] },
// // // // // // //     { icon: "🧑‍💻", role: "Developers", tagline: "Your entire dev profile in one link", desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share at hackathons or in open source communities.", highlights: ["GitHub & portfolio links", "Twitter / X profile", "Resume for job applications", "Professional dev identity"] },
// // // // // // // ];

// // // // // // // function UseCasesSection({ navigate }) {
// // // // // // //     const [active, setActive] = useState(0);
// // // // // // //     const cur = USE_CASES[active];
// // // // // // //     return (
// // // // // // //         <section className="lp-section">
// // // // // // //             <div className="lp-section-inner">
// // // // // // //                 <span className="lp-eyebrow reveal">Who is it for</span>
// // // // // // //                 <h2 className="lp-section-title reveal">Built for every <em>professional</em></h2>
// // // // // // //                 <p className="lp-section-sub reveal">Whether you're a student or a CEO — your digital card works for you.</p>
// // // // // // //                 <div className="lp-use-tabs reveal">
// // // // // // //                     {USE_CASES.map((u, i) => (
// // // // // // //                         <button key={i} className="lp-use-tab" onClick={() => setActive(i)}
// // // // // // //                             style={{
// // // // // // //                                 border: active === i ? "0.5px solid var(--border-gold)" : "0.5px solid var(--border-color)",
// // // // // // //                                 background: active === i ? "var(--gold-subtle2)" : "transparent",
// // // // // // //                                 color: active === i ? "var(--gold)" : "var(--text-muted)",
// // // // // // //                             }}>
// // // // // // //                             {u.icon} {u.role}
// // // // // // //                         </button>
// // // // // // //                     ))}
// // // // // // //                 </div>
// // // // // // //                 <div className="lp-use-panel" style={{ borderColor: "var(--border-gold)" }}>
// // // // // // //                     <div>
// // // // // // //                         <div className="lp-use-badge"><span>{cur.icon}</span><span style={{ fontSize: "12px", letterSpacing: "0.5px" }}>{cur.role}</span></div>
// // // // // // //                         <div className="lp-use-tagline">{cur.tagline}</div>
// // // // // // //                         <p className="lp-use-desc">{cur.desc}</p>
// // // // // // //                         <button className="lp-btn-gold" onClick={() => navigate("/animation/card")}>Create My Card — Free →</button>
// // // // // // //                     </div>
// // // // // // //                     <div className="lp-use-highlights">
// // // // // // //                         {cur.highlights.map((h, i) => (
// // // // // // //                             <div className="lp-use-hl" key={i}>
// // // // // // //                                 <div className="lp-use-hl-check">✓</div>
// // // // // // //                                 <span className="lp-use-hl-text">{h}</span>
// // // // // // //                             </div>
// // // // // // //                         ))}
// // // // // // //                     </div>
// // // // // // //                 </div>
// // // // // // //             </div>
// // // // // // //         </section>
// // // // // // //     );
// // // // // // // }

// // // // // // // export default function LandingPage() {
// // // // // // //     const navigate = useNavigate();
// // // // // // //     const [scrolled, setScrolled] = useState(false);
// // // // // // //     const [isLight, setIsLight] = useState(false);

// // // // // // //     useEffect(() => {
// // // // // // //         const onScroll = () => setScrolled(window.scrollY > 40);
// // // // // // //         window.addEventListener("scroll", onScroll);
// // // // // // //         return () => window.removeEventListener("scroll", onScroll);
// // // // // // //     }, []);

// // // // // // //     useEffect(() => {
// // // // // // //         const els = document.querySelectorAll(".reveal");
// // // // // // //         const observer = new IntersectionObserver(
// // // // // // //             (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
// // // // // // //             { threshold: 0.12 }
// // // // // // //         );
// // // // // // //         els.forEach((el) => observer.observe(el));
// // // // // // //         return () => observer.disconnect();
// // // // // // //     }, []);

// // // // // // //     return (
// // // // // // //         <div className={isLight ? "lp-light" : ""} style={{ background: isLight ? "#FFFFFF" : "#080808", minHeight: "100vh" }}>
// // // // // // //             <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

// // // // // // //             {/* NAV */}
// // // // // // //             <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
// // // // // // //                 <div className="lp-nav-inner">
// // // // // // //                     <a href="/" className="lp-logo">
// // // // // // //                         <svg className="lp-logo-icon" viewBox="0 0 34 34" fill="none">
// // // // // // //                             <rect width="34" height="34" rx="9" fill={isLight ? "#F0EDE6" : "#1A1A1A"} />
// // // // // // //                             <rect width="34" height="34" rx="9" stroke="var(--border-gold)" strokeWidth="0.5" />
// // // // // // //                             <circle cx="15" cy="13" r="5" stroke="var(--gold)" strokeWidth="1.5" fill="none" />
// // // // // // //                             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
// // // // // // //                             <path d="M23 11 L27 11 L27 23" stroke="var(--gold)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.6" />
// // // // // // //                             <circle cx="27" cy="23" r="1.8" fill="var(--gold)" />
// // // // // // //                         </svg>
// // // // // // //                         myprofile<em>corner</em>
// // // // // // //                     </a>
// // // // // // //                     <div className="lp-nav-links">
// // // // // // //                         <a href="#how" className="lp-nav-link">How it works</a>
// // // // // // //                         <a href="#features" className="lp-nav-link">Features</a>
// // // // // // //                         <a href="#pricing" className="lp-nav-link">Pricing</a>
// // // // // // //                         <button className="lp-theme-btn" onClick={() => setIsLight(p => !p)} title={isLight ? "Dark mode" : "Light mode"} aria-label="Toggle theme">
// // // // // // //                             {isLight ? (
// // // // // // //                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
// // // // // // //                                     <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
// // // // // // //                                 </svg>
// // // // // // //                             ) : (
// // // // // // //                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
// // // // // // //                                     <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
// // // // // // //                                     <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
// // // // // // //                                     <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
// // // // // // //                                     <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
// // // // // // //                                 </svg>
// // // // // // //                             )}
// // // // // // //                         </button>
// // // // // // //                         <button className="lp-nav-cta" onClick={() => navigate("/animation/card")}>Create Free Card</button>
// // // // // // //                     </div>
// // // // // // //                 </div>
// // // // // // //             </nav>

// // // // // // //             {/* HERO */}
// // // // // // //             <section style={{ background: isLight ? "#FFFFFF" : "#080808", overflow: "hidden", position: "relative" }}>
// // // // // // //                 <div className="lp-hero">
// // // // // // //                     <div style={{ position: "absolute", width: "600px", height: "600px", borderRadius: "50%", background: "radial-gradient(circle, var(--gold-subtle) 0%, transparent 65%)", top: "-200px", left: "-100px", pointerEvents: "none" }} />
// // // // // // //                     <div className="lp-hero-left">
// // // // // // //                         <div className="lp-hero-eyebrow">
// // // // // // //                             <div className="lp-hero-eyebrow-dot" />
// // // // // // //                             100% Free · No App Needed
// // // // // // //                         </div>
// // // // // // //                         <h1 className="lp-hero-title">Your Digital<br />Identity, <em>Always</em><br />in Your Pocket</h1>
// // // // // // //                         <p className="lp-hero-sub">Replace paper visiting cards with a smart digital card. Share your profile instantly via QR code, link, or Google Wallet — no printing, no waste.</p>
// // // // // // //                         <div className="lp-hero-stats">
// // // // // // //                             {[["100%", "Free forever"], ["2 min", "Setup time"], ["0", "Paper waste"]].map(([n, l]) => (
// // // // // // //                                 <div key={l}><div className="lp-stat-num">{n}</div><div className="lp-stat-label">{l}</div></div>
// // // // // // //                             ))}
// // // // // // //                         </div>
// // // // // // //                         <div className="lp-hero-ctas">
// // // // // // //                             <button className="lp-btn-gold" onClick={() => navigate("/animation/card")}>Create My Card — It's Free →</button>
// // // // // // //                             <a href="#how" className="lp-btn-ghost">See how it works</a>
// // // // // // //                         </div>
// // // // // // //                     </div>
// // // // // // //                     <div className="lp-hero-right"><ScanScene /></div>
// // // // // // //                 </div>
// // // // // // //             </section>

// // // // // // //             <div className="gold-rule" />

// // // // // // //             {/* TRUST BAR */}
// // // // // // //             <div className="lp-trust">
// // // // // // //                 <span className="lp-trust-label">Works seamlessly with</span>
// // // // // // //                 <div className="lp-trust-items">
// // // // // // //                     {[["🔥", "Firebase"], ["🪙", "Google Wallet"], ["📱", "Android & iOS"], ["🔗", "Any Browser"]].map(([icon, label], i, arr) => (
// // // // // // //                         <React.Fragment key={label}>
// // // // // // //                             <div className="lp-trust-item"><span>{icon}</span><span>{label}</span></div>
// // // // // // //                             {i < arr.length - 1 && <span className="lp-trust-sep">·</span>}
// // // // // // //                         </React.Fragment>
// // // // // // //                     ))}
// // // // // // //                 </div>
// // // // // // //             </div>

// // // // // // //             <div className="gold-rule" />

// // // // // // //             {/* HOW IT WORKS */}
// // // // // // //             <section id="how" className="lp-section">
// // // // // // //                 <div className="lp-section-inner">
// // // // // // //                     <span className="lp-eyebrow reveal">How it works</span>
// // // // // // //                     <h2 className="lp-section-title reveal">From setup to sharing <em>in 3 steps</em></h2>
// // // // // // //                     <p className="lp-section-sub reveal">No app downloads. No tech skills required. Just fill, share, impress.</p>
// // // // // // //                     <div className="lp-how-timeline reveal">
// // // // // // //                         {[
// // // // // // //                             { num: "01", icon: "✏️", label: "Step 01", title: "Fill Your Profile", desc: "Add your name, photo, contact info, social links, resume & website. Takes less than 2 minutes." },
// // // // // // //                             { num: "02", icon: "🎴", label: "Step 02", title: "Get Your Digital Card", desc: "Instantly get a beautiful card with a unique QR code & shareable link — no design skills needed." },
// // // // // // //                             { num: "03", icon: "🚀", label: "Step 03", title: "Share Anywhere", desc: "Show QR in meetings, add to Google Wallet, or send your link via WhatsApp, email, or LinkedIn." },
// // // // // // //                         ].map((step, i) => (
// // // // // // //                             <React.Fragment key={step.num}>
// // // // // // //                                 <div className="lp-how-card">
// // // // // // //                                     <div className="lp-how-num">{step.num}</div>
// // // // // // //                                     <div className="lp-how-icon-ring">{step.icon}</div>
// // // // // // //                                     <div className="lp-how-step-label">{step.label}</div>
// // // // // // //                                     <div className="lp-how-title">{step.title}</div>
// // // // // // //                                     <p className="lp-how-desc">{step.desc}</p>
// // // // // // //                                 </div>
// // // // // // //                                 {i < 2 && (
// // // // // // //                                     <div className="lp-how-bridge">
// // // // // // //                                         <svg width="32" height="24" viewBox="0 0 32 24" fill="none">
// // // // // // //                                             <path d="M0 12 L24 12 M18 5 L26 12 L18 19" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
// // // // // // //                                         </svg>
// // // // // // //                                     </div>
// // // // // // //                                 )}
// // // // // // //                             </React.Fragment>
// // // // // // //                         ))}
// // // // // // //                     </div>
// // // // // // //                 </div>
// // // // // // //             </section>

// // // // // // //             <div className="gold-rule" />

// // // // // // //             {/* FEATURES */}
// // // // // // //             <section id="features" className="lp-section">
// // // // // // //                 <div className="lp-section-inner">
// // // // // // //                     <span className="lp-eyebrow reveal">Features</span>
// // // // // // //                     <h2 className="lp-section-title reveal">Everything a modern <em>professional</em> needs</h2>
// // // // // // //                     <p className="lp-section-sub reveal">One card. Every detail. Always up-to-date.</p>
// // // // // // //                     <div className="lp-feat-bento reveal">
// // // // // // //                         {/* Hero: Google Wallet */}
// // // // // // //                         <div className="lp-feat-bcard hero">
// // // // // // //                             <div className="lp-feat-bicon">🪙</div>
// // // // // // //                             <div className="lp-feat-btag">Premium Feature</div>
// // // // // // //                             <div className="lp-feat-btitle">Google Wallet Pass</div>
// // // // // // //                             <p className="lp-feat-bdesc">Add your card to Google Wallet and access it offline — at meetings, events, or anywhere without internet.</p>
// // // // // // //                             <div className="lp-feat-wallet">
// // // // // // //                                 <div className="lp-wallet-chip">G Pay</div>
// // // // // // //                                 <div className="lp-wallet-chip">Wallet</div>
// // // // // // //                                 <div className="lp-wallet-chip">Offline ✓</div>
// // // // // // //                             </div>
// // // // // // //                         </div>
// // // // // // //                         {/* Tall: QR Code */}
// // // // // // //                         <div className="lp-feat-bcard tall">
// // // // // // //                             <div className="lp-feat-bicon">📲</div>
// // // // // // //                             <div className="lp-feat-btag">Instant Share</div>
// // // // // // //                             <div className="lp-feat-btitle">Unique QR Code</div>
// // // // // // //                             <p className="lp-feat-bdesc">Every card gets a dedicated QR. One scan opens your full profile — no typing, no friction.</p>
// // // // // // //                             <div className="lp-qr-art">
// // // // // // //                                 {[1, 1, 1, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 1].map((v, i) => (
// // // // // // //                                     <div key={i} className="lp-qr-cell" style={{ background: v ? "var(--gold)" : "var(--gold-subtle)", borderRadius: "2px", aspectRatio: "1" }} />
// // // // // // //                                 ))}
// // // // // // //                             </div>
// // // // // // //                         </div>
// // // // // // //                         <div className="lp-feat-bcard">
// // // // // // //                             <div className="lp-feat-bicon">📸</div>
// // // // // // //                             <div className="lp-feat-btag">Identity</div>
// // // // // // //                             <div className="lp-feat-btitle">Profile Photo</div>
// // // // // // //                             <p className="lp-feat-bdesc">Your face on your card — and inside Google Wallet too.</p>
// // // // // // //                         </div>
// // // // // // //                         <div className="lp-feat-bcard">
// // // // // // //                             <div className="lp-feat-bicon">💾</div>
// // // // // // //                             <div className="lp-feat-btag">One Tap</div>
// // // // // // //                             <div className="lp-feat-btitle">Save Contact</div>
// // // // // // //                             <p className="lp-feat-bdesc">Visitors save you directly to their phone contacts with a single tap.</p>
// // // // // // //                         </div>
// // // // // // //                         {/* Hero: Social Links */}
// // // // // // //                         <div className="lp-feat-bcard hero">
// // // // // // //                             <div className="lp-feat-bicon">🔗</div>
// // // // // // //                             <div className="lp-feat-btag">Connections</div>
// // // // // // //                             <div className="lp-feat-btitle">All Social Links</div>
// // // // // // //                             <p className="lp-feat-bdesc">LinkedIn, GitHub, Instagram, Twitter, WhatsApp, YouTube — everything that matters in one place.</p>
// // // // // // //                             <div className="lp-feat-socials">
// // // // // // //                                 {["in LinkedIn", "⌥ GitHub", "✉ WhatsApp", "▶ YouTube", "📷 Insta"].map(s => (
// // // // // // //                                     <span key={s} className="lp-social-pill">{s}</span>
// // // // // // //                                 ))}
// // // // // // //                             </div>
// // // // // // //                         </div>
// // // // // // //                         <div className="lp-feat-bcard">
// // // // // // //                             <div className="lp-feat-bicon">📄</div>
// // // // // // //                             <div className="lp-feat-btag">Career</div>
// // // // // // //                             <div className="lp-feat-btitle">Resume Upload</div>
// // // // // // //                             <p className="lp-feat-bdesc">Attach your PDF resume. Recruiters can download it instantly.</p>
// // // // // // //                         </div>
// // // // // // //                         <div className="lp-feat-bcard">
// // // // // // //                             <div className="lp-feat-bicon">✏️</div>
// // // // // // //                             <div className="lp-feat-btag">Always Fresh</div>
// // // // // // //                             <div className="lp-feat-btitle">Edit Anytime</div>
// // // // // // //                             <p className="lp-feat-bdesc">Changed jobs? New number? Update instantly — same link forever.</p>
// // // // // // //                         </div>
// // // // // // //                         <div className="lp-feat-bcard">
// // // // // // //                             <div className="lp-feat-bicon">🌐</div>
// // // // // // //                             <div className="lp-feat-btag">Universal</div>
// // // // // // //                             <div className="lp-feat-btitle">Works Everywhere</div>
// // // // // // //                             <p className="lp-feat-bdesc">Opens in any browser on any device. No app install ever required.</p>
// // // // // // //                         </div>
// // // // // // //                     </div>
// // // // // // //                 </div>
// // // // // // //             </section>

// // // // // // //             <div className="gold-rule" />

// // // // // // //             {/* USE CASES */}
// // // // // // //             <UseCasesSection navigate={navigate} />

// // // // // // //             <div className="gold-rule" />

// // // // // // //             {/* PRICING + CTA — MERGED */}
// // // // // // //             <section id="pricing" className="lp-pricing-cta">
// // // // // // //                 <div className="lp-pricing-cta-inner">

// // // // // // //                     {/* Left copy */}
// // // // // // //                     <div className="lp-pricing-copy">
// // // // // // //                         <span className="lp-pricing-eyebrow reveal">Pricing</span>
// // // // // // //                         <h2 className="lp-pricing-headline reveal">
// // // // // // //                             Stop carrying<br /><em>paper cards.</em>
// // // // // // //                         </h2>
// // // // // // //                         <p className="lp-pricing-sub reveal">
// // // // // // //                             Create your digital card in under 2 minutes. No hidden charges. No credit card. Free forever.
// // // // // // //                         </p>
// // // // // // //                         <div className="lp-pricing-perks reveal">
// // // // // // //                             {[
// // // // // // //                                 "No app download needed",
// // // // // // //                                 "Unique QR code & shareable link",
// // // // // // //                                 "Google Wallet pass included",
// // // // // // //                                 "Edit anytime — same link forever",
// // // // // // //                                 "Works on every device & browser",
// // // // // // //                             ].map(p => (
// // // // // // //                                 <div className="lp-pricing-perk" key={p}>
// // // // // // //                                     <div className="lp-perk-tick">✓</div>
// // // // // // //                                     <span>{p}</span>
// // // // // // //                                 </div>
// // // // // // //                             ))}
// // // // // // //                         </div>
// // // // // // //                         <button className="lp-btn-gold reveal" onClick={() => navigate("/animation/card")}>
// // // // // // //                             Create My Card Now →
// // // // // // //                         </button>
// // // // // // //                     </div>

// // // // // // //                     {/* Right price card */}
// // // // // // //                     <div className="lp-price-card reveal">
// // // // // // //                         <div className="lp-price-card-top">
// // // // // // //                             <div className="lp-price-badge">Most Popular</div>
// // // // // // //                             <div className="lp-price-amount">
// // // // // // //                                 <div className="lp-price-num">₹0</div>
// // // // // // //                                 <div className="lp-price-period">Forever Free</div>
// // // // // // //                             </div>
// // // // // // //                         </div>
// // // // // // //                         <div className="lp-price-divider" />
// // // // // // //                         <ul className="lp-price-list">
// // // // // // //                             {[
// // // // // // //                                 "1 Digital Visiting Card",
// // // // // // //                                 "Unique QR Code & Link",
// // // // // // //                                 "Profile Photo Upload",
// // // // // // //                                 "Resume Upload (PDF)",
// // // // // // //                                 "All Social Media Links",
// // // // // // //                                 "Google Wallet Pass",
// // // // // // //                                 "Save Contact (.vcf)",
// // // // // // //                                 "Edit Anytime",
// // // // // // //                                 "No App Required",
// // // // // // //                             ].map(item => (
// // // // // // //                                 <li className="lp-price-item" key={item}>
// // // // // // //                                     <span className="lp-price-check">✦</span>
// // // // // // //                                     {item}
// // // // // // //                                 </li>
// // // // // // //                             ))}
// // // // // // //                         </ul>
// // // // // // //                         <button className="lp-btn-gold" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/animation/card")}>
// // // // // // //                             Get Started Free →
// // // // // // //                         </button>
// // // // // // //                     </div>

// // // // // // //                 </div>
// // // // // // //             </section>

// // // // // // //             <div className="gold-rule" />

// // // // // // //             {/* FOOTER */}
// // // // // // //             <footer className="lp-footer">
// // // // // // //                 <div className="lp-footer-inner">
// // // // // // //                     <div className="lp-footer-logo">
// // // // // // //                         <svg width="28" height="28" viewBox="0 0 34 34" fill="none">
// // // // // // //                             <rect width="34" height="34" rx="9" fill={isLight ? "#F0EDE6" : "#1A1A1A"} />
// // // // // // //                             <rect width="34" height="34" rx="9" stroke="var(--border-gold)" strokeWidth="0.5" />
// // // // // // //                             <circle cx="15" cy="13" r="5" stroke="var(--gold)" strokeWidth="1.5" fill="none" />
// // // // // // //                             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
// // // // // // //                             <path d="M23 11 L27 11 L27 23" stroke="var(--gold)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.6" />
// // // // // // //                             <circle cx="27" cy="23" r="1.8" fill="var(--gold)" />
// // // // // // //                         </svg>
// // // // // // //                         myprofile<em>corner</em>
// // // // // // //                     </div>
// // // // // // //                     <p className="lp-footer-tagline">Your digital identity, always in your pocket.</p>
// // // // // // //                     <p className="lp-footer-copy">© {new Date().getFullYear()} MyProfileCorner · Made with ♥ in India</p>
// // // // // // //                 </div>
// // // // // // //             </footer>
// // // // // // //         </div>
// // // // // // //     );
// // // // // // // }

// // // // // // // // import React, { useState, useEffect } from "react";
// // // // // // // // import { useNavigate } from "react-router-dom";

// // // // // // // // /* ─────────────────────────────────────────
// // // // // // // //    FONTS injected once
// // // // // // // //    Better combo:
// // // // // // // //    - Headings: Cormorant Garamond
// // // // // // // //    - Body/UI: Montserrat
// // // // // // // // ───────────────────────────────────────── */
// // // // // // // // const FONT_LINK = `
// // // // // // // //   @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Montserrat:wght@300;400;500;600;700;800&display=swap');
// // // // // // // // `;

// // // // // // // // /* ─────────────────────────────────────────
// // // // // // // //    GLOBAL CSS
// // // // // // // // ───────────────────────────────────────── */
// // // // // // // // const GLOBAL_CSS = `
// // // // // // // // ${FONT_LINK}

// // // // // // // // *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

// // // // // // // // :root {
// // // // // // // //   --black: #080808;
// // // // // // // //   --dark: #0E0E0E;
// // // // // // // //   --card: #141414;
// // // // // // // //   --border: rgba(255,255,255,0.07);
// // // // // // // //   --gold: #D4AF37;
// // // // // // // //   --gold2: #F0D060;
// // // // // // // //   --gold3: #C9A227;
// // // // // // // //   --gold-dim: rgba(212,175,55,0.18);
// // // // // // // //   --white: #FFFFFF;
// // // // // // // //   --muted: rgba(255,255,255,0.45);
// // // // // // // //   --muted2: rgba(255,255,255,0.25);
// // // // // // // //   --serif: 'Cormorant Garamond', serif;
// // // // // // // //   --sans: 'Montserrat', sans-serif;
// // // // // // // // }

// // // // // // // // html { scroll-behavior: smooth; }

// // // // // // // // body {
// // // // // // // //   background: var(--black);
// // // // // // // //   color: var(--white);
// // // // // // // //   font-family: var(--sans);
// // // // // // // //   overflow-x: hidden;
// // // // // // // //   letter-spacing: 0.1px;
// // // // // // // // }

// // // // // // // // /* ── scrollbar ── */
// // // // // // // // ::-webkit-scrollbar { width: 4px; }
// // // // // // // // ::-webkit-scrollbar-track { background: var(--black); }
// // // // // // // // ::-webkit-scrollbar-thumb { background: rgba(212,175,55,0.3); border-radius: 2px; }

// // // // // // // // /* ── NAV ── */
// // // // // // // // .lp-nav {
// // // // // // // //   position: fixed; top: 0; left: 0; right: 0; z-index: 200;
// // // // // // // //   padding: 1.5rem 3rem;
// // // // // // // //   transition: all 0.4s;
// // // // // // // // }
// // // // // // // // .lp-nav.scrolled {
// // // // // // // //   background: rgba(8,8,8,0.9);
// // // // // // // //   backdrop-filter: blur(20px);
// // // // // // // //   border-bottom: 0.5px solid rgba(212,175,55,0.12);
// // // // // // // //   padding: 1rem 3rem;
// // // // // // // // }
// // // // // // // // .lp-nav-inner {
// // // // // // // //   max-width: 1180px; margin: 0 auto;
// // // // // // // //   display: flex; align-items: center; justify-content: space-between;
// // // // // // // // }
// // // // // // // // .lp-logo {
// // // // // // // //   display: flex; align-items: center; gap: 10px;
// // // // // // // //   font-family: var(--sans); font-size: 15px; font-weight: 600;
// // // // // // // //   color: var(--white); text-decoration: none; letter-spacing: 0.2px;
// // // // // // // // }
// // // // // // // // .lp-logo-icon { width: 34px; height: 34px; flex-shrink: 0; }
// // // // // // // // .lp-logo em { color: var(--gold); font-style: normal; font-weight: 700; }
// // // // // // // // .lp-nav-links {
// // // // // // // //   display: flex; align-items: center; gap: 2.5rem;
// // // // // // // // }
// // // // // // // // .lp-nav-link {
// // // // // // // //   font-size: 13px; font-weight: 500; color: var(--muted);
// // // // // // // //   text-decoration: none; letter-spacing: 0.3px;
// // // // // // // //   transition: color 0.2s;
// // // // // // // // }
// // // // // // // // .lp-nav-link:hover { color: var(--white); }
// // // // // // // // .lp-nav-cta {
// // // // // // // //   padding: 10px 22px;
// // // // // // // //   background: linear-gradient(135deg, var(--gold), var(--gold2), var(--gold3));
// // // // // // // //   color: #0A0A0A; border: none; border-radius: 10px;
// // // // // // // //   font-family: var(--sans); font-size: 13px; font-weight: 700;
// // // // // // // //   letter-spacing: 0.8px; text-transform: uppercase;
// // // // // // // //   cursor: pointer; position: relative; overflow: hidden;
// // // // // // // //   transition: transform 0.15s, opacity 0.15s;
// // // // // // // // }
// // // // // // // // .lp-nav-cta::before {
// // // // // // // //   content: ''; position: absolute; inset: 0;
// // // // // // // //   background: linear-gradient(135deg, rgba(255,255,255,0.2), transparent 60%);
// // // // // // // //   pointer-events: none;
// // // // // // // // }
// // // // // // // // .lp-nav-cta:hover { transform: translateY(-1px); opacity: 0.92; }

// // // // // // // // /* ── HERO ── */
// // // // // // // // .lp-hero {
// // // // // // // //   min-height: 100vh;
// // // // // // // //   display: grid;
// // // // // // // //   grid-template-columns: 1fr 1fr;
// // // // // // // //   align-items: center;
// // // // // // // //   gap: 4rem;
// // // // // // // //   max-width: 1180px; margin: 0 auto;
// // // // // // // //   padding: 10rem 3rem 3rem;
// // // // // // // //   position: relative;
// // // // // // // // }

// // // // // // // // .lp-hero-left { position: relative; z-index: 2; }

// // // // // // // // .lp-hero-eyebrow {
// // // // // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // // // // //   padding: 6px 14px;
// // // // // // // //   background: rgba(212,175,55,0.1);
// // // // // // // //   border: 0.5px solid rgba(212,175,55,0.3);
// // // // // // // //   border-radius: 100px;
// // // // // // // //   font-size: 11px; font-weight: 700; color: var(--gold);
// // // // // // // //   letter-spacing: 1.5px; text-transform: uppercase;
// // // // // // // //   margin-bottom: 2rem;
// // // // // // // // }
// // // // // // // // .lp-hero-eyebrow-dot {
// // // // // // // //   width: 5px; height: 5px; border-radius: 50%;
// // // // // // // //   background: var(--gold);
// // // // // // // //   animation: pulse-gold 2s ease-in-out infinite;
// // // // // // // // }
// // // // // // // // @keyframes pulse-gold {
// // // // // // // //   0%, 100% { opacity: 1; transform: scale(1); }
// // // // // // // //   50% { opacity: 0.4; transform: scale(0.7); }
// // // // // // // // }

// // // // // // // // .lp-hero-title {
// // // // // // // //   font-family: var(--serif);
// // // // // // // //   font-size: clamp(3.2rem, 5.8vw, 5.3rem);
// // // // // // // //   font-weight: 600; line-height: 1.02;
// // // // // // // //   letter-spacing: -0.8px;
// // // // // // // //   color: var(--white);
// // // // // // // //   margin-bottom: 1.75rem;
// // // // // // // // }
// // // // // // // // .lp-hero-title em {
// // // // // // // //   font-style: italic; font-weight: 600;
// // // // // // // //   color: var(--gold);
// // // // // // // // }

// // // // // // // // .lp-hero-sub {
// // // // // // // //   font-size: 15px; font-weight: 400;
// // // // // // // //   color: var(--muted); line-height: 1.9;
// // // // // // // //   max-width: 470px; margin-bottom: 1rem;
// // // // // // // // }

// // // // // // // // .lp-hero-ctas { display: flex; gap: 1rem; flex-wrap: wrap; }

// // // // // // // // .lp-btn-gold {
// // // // // // // //   padding: 16px 32px;
// // // // // // // //   background: linear-gradient(135deg, var(--gold), var(--gold2), var(--gold3));
// // // // // // // //   color: #0A0A0A; border: none; border-radius: 12px;
// // // // // // // //   font-family: var(--sans); font-size: 13px; font-weight: 700;
// // // // // // // //   letter-spacing: 1px; text-transform: uppercase;
// // // // // // // //   cursor: pointer; position: relative; overflow: hidden;
// // // // // // // //   transition: transform 0.2s, opacity 0.2s;
// // // // // // // //   display: flex; align-items: center; gap: 8px;
// // // // // // // // }
// // // // // // // // .lp-btn-gold::before {
// // // // // // // //   content: ''; position: absolute; inset: 0;
// // // // // // // //   background: linear-gradient(135deg, rgba(255,255,255,0.25), transparent 60%);
// // // // // // // //   pointer-events: none;
// // // // // // // // }
// // // // // // // // .lp-btn-gold:hover { transform: translateY(-2px); opacity: 0.92; }

// // // // // // // // .lp-btn-ghost {
// // // // // // // //   padding: 16px 28px;
// // // // // // // //   background: transparent;
// // // // // // // //   color: var(--muted); border: 0.5px solid rgba(255,255,255,0.15);
// // // // // // // //   border-radius: 12px;
// // // // // // // //   font-family: var(--sans); font-size: 13px; font-weight: 600;
// // // // // // // //   letter-spacing: 0.5px; cursor: pointer; text-decoration: none;
// // // // // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // // // // //   transition: border-color 0.2s, color 0.2s;
// // // // // // // // }
// // // // // // // // .lp-btn-ghost:hover { border-color: rgba(212,175,55,0.3); color: var(--white); }

// // // // // // // // /* ── HERO STATS STRIP ── */
// // // // // // // // .lp-hero-stats {
// // // // // // // //   display: flex; gap: 2.5rem; 
// // // // // // // // //   margin-top: 3.5rem;
// // // // // // // //   padding-bottom: 1rem;
// // // // // // // // //   border-top: 0.5px solid var(--border);
// // // // // // // // }
// // // // // // // // .lp-stat-num {
// // // // // // // //   font-family: var(--serif); font-size: 2.2rem; font-weight: 600;
// // // // // // // //   color: var(--gold); line-height: 1;
// // // // // // // // }
// // // // // // // // .lp-stat-label {
// // // // // // // //   font-size: 11px; font-weight: 500; color: var(--muted2);
// // // // // // // //   letter-spacing: 0.5px; margin-top: 4px;
// // // // // // // // }

// // // // // // // // /* ── HERO RIGHT — QR SCAN ANIMATION ── */
// // // // // // // // .lp-hero-right {
// // // // // // // //   position: relative; display: flex;
// // // // // // // //   align-items: center; justify-content: center;
// // // // // // // //   z-index: 2;
// // // // // // // // }

// // // // // // // // /* Scene wrapper */
// // // // // // // // .scan-scene {
// // // // // // // //   position: relative;
// // // // // // // //   width: 380px; height: 480px;
// // // // // // // // }

// // // // // // // // /* Phone A (scanner) */
// // // // // // // // .phone-scanner {
// // // // // // // //   position: absolute;
// // // // // // // //   left: 0; top: 60px;
// // // // // // // //   width: 155px; height: 270px;
// // // // // // // //   background: #1A1A1A;
// // // // // // // //   border-radius: 22px;
// // // // // // // //   border: 1px solid rgba(255,255,255,0.1);
// // // // // // // //   overflow: hidden;
// // // // // // // //   box-shadow: 0 24px 60px rgba(0,0,0,0.6);
// // // // // // // //   z-index: 3;
// // // // // // // //   animation: phone-a-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;
// // // // // // // //   animation-delay: 0.3s;
// // // // // // // // }
// // // // // // // // @keyframes phone-a-intro {
// // // // // // // //   from { opacity: 0; transform: translateX(-30px) rotate(-6deg); }
// // // // // // // //   to   { opacity: 1; transform: translateX(0) rotate(-6deg); }
// // // // // // // // }

// // // // // // // // .phone-scanner-screen {
// // // // // // // //   width: 100%; height: 100%;
// // // // // // // //   background: #111;
// // // // // // // //   display: flex; flex-direction: column;
// // // // // // // //   align-items: center; justify-content: center; gap: 12px;
// // // // // // // //   padding: 12px;
// // // // // // // // }
// // // // // // // // .scanner-viewfinder {
// // // // // // // //   width: 100px; height: 100px;
// // // // // // // //   border-radius: 10px;
// // // // // // // //   position: relative;
// // // // // // // //   background: rgba(212,175,55,0.04);
// // // // // // // // }
// // // // // // // // .scanner-viewfinder::before,
// // // // // // // // .scanner-viewfinder::after {
// // // // // // // //   content: '';
// // // // // // // //   position: absolute;
// // // // // // // //   width: 20px; height: 20px;
// // // // // // // //   border-color: var(--gold); border-style: solid;
// // // // // // // // }
// // // // // // // // .scanner-viewfinder::before {
// // // // // // // //   top: 0; left: 0;
// // // // // // // //   border-width: 2px 0 0 2px;
// // // // // // // //   border-radius: 6px 0 0 0;
// // // // // // // // }
// // // // // // // // .scanner-viewfinder::after {
// // // // // // // //   bottom: 0; right: 0;
// // // // // // // //   border-width: 0 2px 2px 0;
// // // // // // // //   border-radius: 0 0 6px 0;
// // // // // // // // }
// // // // // // // // .vf-corner-tr {
// // // // // // // //   position: absolute; top: 0; right: 0;
// // // // // // // //   width: 20px; height: 20px;
// // // // // // // //   border-top: 2px solid var(--gold);
// // // // // // // //   border-right: 2px solid var(--gold);
// // // // // // // //   border-radius: 0 6px 0 0;
// // // // // // // // }
// // // // // // // // .vf-corner-bl {
// // // // // // // //   position: absolute; bottom: 0; left: 0;
// // // // // // // //   width: 20px; height: 20px;
// // // // // // // //   border-bottom: 2px solid var(--gold);
// // // // // // // //   border-left: 2px solid var(--gold);
// // // // // // // //   border-radius: 0 0 0 6px;
// // // // // // // // }

// // // // // // // // /* scanning laser line */
// // // // // // // // .scan-laser {
// // // // // // // //   position: absolute;
// // // // // // // //   left: 4px; right: 4px; height: 2px;
// // // // // // // //   background: linear-gradient(90deg, transparent, var(--gold), transparent);
// // // // // // // //   border-radius: 1px;
// // // // // // // //   top: 4px;
// // // // // // // //   animation: laser-sweep 2s ease-in-out infinite;
// // // // // // // //   animation-delay: 1.2s;
// // // // // // // // }
// // // // // // // // @keyframes laser-sweep {
// // // // // // // //   0%   { top: 8px; opacity: 0; }
// // // // // // // //   10%  { opacity: 1; }
// // // // // // // //   90%  { opacity: 1; }
// // // // // // // //   100% { top: 88px; opacity: 0; }
// // // // // // // // }

// // // // // // // // .scanner-label {
// // // // // // // //   font-size: 9px; font-weight: 600; color: rgba(255,255,255,0.4);
// // // // // // // //   letter-spacing: 1px; text-align: center;
// // // // // // // //   text-transform: uppercase;
// // // // // // // // }

// // // // // // // // /* Mini QR inside viewfinder */
// // // // // // // // .mini-qr {
// // // // // // // //   position: absolute; inset: 10px;
// // // // // // // //   display: grid; grid-template-columns: repeat(6,1fr); grid-template-rows: repeat(6,1fr);
// // // // // // // //   gap: 1.5px; opacity: 0.6;
// // // // // // // // }
// // // // // // // // .mini-qr-cell { border-radius: 1px; }

// // // // // // // // /* Phone B (profile card) */
// // // // // // // // .phone-profile {
// // // // // // // //   position: absolute;
// // // // // // // //   right: 0; top: 0;
// // // // // // // //   width: 175px; height: 310px;
// // // // // // // //   background: #0E0E0E;
// // // // // // // //   border-radius: 24px;
// // // // // // // //   border: 1px solid rgba(212,175,55,0.2);
// // // // // // // //   overflow: hidden;
// // // // // // // //   box-shadow: 0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(212,175,55,0.08);
// // // // // // // //   z-index: 4;
// // // // // // // //   animation: phone-b-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;
// // // // // // // //   animation-delay: 0.5s;
// // // // // // // // }
// // // // // // // // @keyframes phone-b-intro {
// // // // // // // //   from { opacity: 0; transform: translateY(30px) rotate(4deg); }
// // // // // // // //   to   { opacity: 1; transform: translateY(0) rotate(4deg); }
// // // // // // // // }

// // // // // // // // /* ── card content opening animation ── */
// // // // // // // // .profile-card-wrap {
// // // // // // // //   width: 100%; height: 100%;
// // // // // // // //   animation: card-reveal 0.7s cubic-bezier(0.22,1,0.36,1) both;
// // // // // // // //   animation-delay: 2s;
// // // // // // // // }
// // // // // // // // @keyframes card-reveal {
// // // // // // // //   from { transform: translateY(40px); opacity: 0; }
// // // // // // // //   to   { transform: translateY(0); opacity: 1; }
// // // // // // // // }

// // // // // // // // .profile-banner {
// // // // // // // //   width: 100%; height: 110px;
// // // // // // // //   background: linear-gradient(160deg, #1a1a1a, #2a2020);
// // // // // // // //   position: relative; overflow: hidden;
// // // // // // // // }
// // // // // // // // .profile-banner-img {
// // // // // // // //   width: 100%; height: 100%;
// // // // // // // //   background: linear-gradient(160deg, #1e1a10 0%, #2a2010 50%, #181818 100%);
// // // // // // // // }
// // // // // // // // .profile-banner-fade {
// // // // // // // //   position: absolute; bottom: 0; left: 0; right: 0; height: 50px;
// // // // // // // //   background: linear-gradient(transparent, #0E0E0E);
// // // // // // // // }

// // // // // // // // /* floating avatar */
// // // // // // // // .profile-avatar-ring {
// // // // // // // //   position: absolute;
// // // // // // // //   bottom: -20px; left: 14px;
// // // // // // // //   width: 44px; height: 44px;
// // // // // // // //   border-radius: 50%;
// // // // // // // //   border: 2px solid #0E0E0E;
// // // // // // // //   background: #222;
// // // // // // // //   overflow: hidden;
// // // // // // // //   box-shadow: 0 0 0 1.5px rgba(212,175,55,0.5);
// // // // // // // //   animation: avatar-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both;
// // // // // // // //   animation-delay: 2.4s;
// // // // // // // // }
// // // // // // // // @keyframes avatar-pop {
// // // // // // // //   from { transform: scale(0.3); opacity: 0; }
// // // // // // // //   to   { transform: scale(1); opacity: 1; }
// // // // // // // // }
// // // // // // // // .profile-avatar-inner {
// // // // // // // //   width: 100%; height: 100%;
// // // // // // // //   background: linear-gradient(145deg, #2a2010, #3a3020);
// // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // //   font-family: var(--serif); font-size: 14px; font-weight: 600;
// // // // // // // //   color: rgba(212,175,55,0.8); letter-spacing: 1px;
// // // // // // // // }

// // // // // // // // .profile-info {
// // // // // // // //   padding: 28px 14px 12px;
// // // // // // // // }
// // // // // // // // .profile-info-name {
// // // // // // // //   font-family: var(--serif); font-size: 16px; font-weight: 600;
// // // // // // // //   color: #FFFFFF; line-height: 1.2; margin-bottom: 2px;
// // // // // // // // }
// // // // // // // // .profile-info-name em { font-style: italic; color: var(--gold); }
// // // // // // // // .profile-info-role {
// // // // // // // //   font-size: 8px; font-weight: 600; color: rgba(255,255,255,0.45);
// // // // // // // //   letter-spacing: 0.3px; margin-bottom: 10px;
// // // // // // // // }

// // // // // // // // .profile-tiles {
// // // // // // // //   display: grid; grid-template-columns: 1fr 1fr 1fr;
// // // // // // // //   gap: 5px; margin-bottom: 8px;
// // // // // // // // }
// // // // // // // // .profile-tile {
// // // // // // // //   background: #1A1A1A;
// // // // // // // //   border: 0.5px solid rgba(255,255,255,0.06);
// // // // // // // //   border-radius: 8px; padding: 7px 6px;
// // // // // // // //   display: flex; flex-direction: column; gap: 4px;
// // // // // // // //   animation: tile-slide 0.3s ease both;
// // // // // // // // }
// // // // // // // // .profile-tile:nth-child(1) { animation-delay: 2.5s; }
// // // // // // // // .profile-tile:nth-child(2) { animation-delay: 2.65s; }
// // // // // // // // .profile-tile:nth-child(3) { animation-delay: 2.8s; }
// // // // // // // // @keyframes tile-slide {
// // // // // // // //   from { opacity: 0; transform: translateY(6px); }
// // // // // // // //   to   { opacity: 1; transform: translateY(0); }
// // // // // // // // }
// // // // // // // // .tile-dot {
// // // // // // // //   width: 12px; height: 12px; border-radius: 50%;
// // // // // // // //   background: rgba(212,175,55,0.15);
// // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // // }
// // // // // // // // .tile-dot-inner { width: 5px; height: 5px; border-radius: 50%; background: var(--gold); }
// // // // // // // // .tile-text { font-size: 7px; color: rgba(255,255,255,0.4); line-height: 1.3; font-weight: 500; }

// // // // // // // // .profile-save-btn {
// // // // // // // //   width: 100%; padding: 8px;
// // // // // // // //   background: linear-gradient(135deg, var(--gold), var(--gold2));
// // // // // // // //   color: #0A0A0A; border: none; border-radius: 8px;
// // // // // // // //   font-size: 8px; font-weight: 700; letter-spacing: 0.8px;
// // // // // // // //   text-transform: uppercase; cursor: default;
// // // // // // // //   display: flex; align-items: center; justify-content: center; gap: 4px;
// // // // // // // //   animation: tile-slide 0.3s ease both;
// // // // // // // //   animation-delay: 2.95s;
// // // // // // // // }

// // // // // // // // /* beam connecting two phones */
// // // // // // // // .beam-line {
// // // // // // // //   position: absolute;
// // // // // // // //   top: 165px; left: 148px;
// // // // // // // //   width: 90px; height: 1px;
// // // // // // // //   background: linear-gradient(90deg, var(--gold), rgba(212,175,55,0));
// // // // // // // //   transform-origin: left center;
// // // // // // // //   animation: beam-appear 0.4s ease both;
// // // // // // // //   animation-delay: 1.5s;
// // // // // // // //   z-index: 5;
// // // // // // // // }
// // // // // // // // @keyframes beam-appear {
// // // // // // // //   from { scaleX: 0; opacity: 0; }
// // // // // // // //   to   { scaleX: 1; opacity: 1; }
// // // // // // // // }
// // // // // // // // .beam-particle {
// // // // // // // //   position: absolute;
// // // // // // // //   top: 160px; left: 148px;
// // // // // // // //   width: 6px; height: 6px; border-radius: 50%;
// // // // // // // //   background: var(--gold);
// // // // // // // //   animation: particle-travel 2s ease-in-out infinite;
// // // // // // // //   animation-delay: 1.6s;
// // // // // // // //   z-index: 6;
// // // // // // // // }
// // // // // // // // @keyframes particle-travel {
// // // // // // // //   0%   { left: 148px; opacity: 0; transform: scale(0.5); }
// // // // // // // //   10%  { opacity: 1; transform: scale(1); }
// // // // // // // //   90%  { opacity: 1; }
// // // // // // // //   100% { left: 228px; opacity: 0; transform: scale(0.5); }
// // // // // // // // }

// // // // // // // // /* ambient glow orbs */
// // // // // // // // .hero-orb-1 {
// // // // // // // //   position: absolute;
// // // // // // // //   width: 500px; height: 500px; border-radius: 50%;
// // // // // // // //   background: radial-gradient(circle, rgba(212,175,55,0.06) 0%, transparent 65%);
// // // // // // // //   top: -100px; right: -100px; pointer-events: none; z-index: 0;
// // // // // // // // }
// // // // // // // // .hero-orb-2 {
// // // // // // // //   position: absolute;
// // // // // // // //   width: 300px; height: 300px; border-radius: 50%;
// // // // // // // //   background: radial-gradient(circle, rgba(212,175,55,0.04) 0%, transparent 65%);
// // // // // // // //   bottom: 0; left: -80px; pointer-events: none; z-index: 0;
// // // // // // // // }

// // // // // // // // /* ── SECTION SHARED ── */
// // // // // // // // .lp-section {
// // // // // // // //   padding: 3rem 3rem;
// // // // // // // //   position: relative;
// // // // // // // // }
// // // // // // // // .lp-section-inner { max-width: 1180px; margin: 0 auto; }
// // // // // // // // .lp-eyebrow {
// // // // // // // //   display: block; font-size: 10px; font-weight: 700;
// // // // // // // //   letter-spacing: 3px; text-transform: uppercase;
// // // // // // // //   color: var(--gold); margin-bottom: 1.25rem; text-align: center;
// // // // // // // // }
// // // // // // // // .lp-section-title {
// // // // // // // //   font-family: var(--serif);
// // // // // // // //   font-size: clamp(2.3rem, 4vw, 3.5rem);
// // // // // // // //   font-weight: 600; line-height: 1.08; letter-spacing: -0.4px;
// // // // // // // //   color: var(--white); text-align: center; margin-bottom: 1.25rem;
// // // // // // // // }
// // // // // // // // .lp-section-title em { font-style: italic; color: var(--gold); }
// // // // // // // // .lp-section-sub {
// // // // // // // //   font-size: 15px; font-weight: 400; color: var(--muted);
// // // // // // // //   text-align: center; max-width: 520px; margin: 0 auto 4rem; line-height: 1.9;
// // // // // // // // }

// // // // // // // // /* ── GOLD DIVIDER ── */
// // // // // // // // .gold-rule {
// // // // // // // //   height: 0.5px;
// // // // // // // //   background: linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent);
// // // // // // // //   margin: 0;
// // // // // // // // }

// // // // // // // // /* ── TRUST BAR ── */
// // // // // // // // .lp-trust {
// // // // // // // //   padding: 2rem 3rem;
// // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // //   gap: 2.5rem; flex-wrap: wrap;
// // // // // // // // }
// // // // // // // // .lp-trust-label {
// // // // // // // //   font-size: 10px; font-weight: 600; color: var(--muted2);
// // // // // // // //   letter-spacing: 2px; text-transform: uppercase;
// // // // // // // // }
// // // // // // // // .lp-trust-items {
// // // // // // // //   display: flex; align-items: center; gap: 2rem; flex-wrap: wrap;
// // // // // // // // }
// // // // // // // // .lp-trust-item {
// // // // // // // //   display: flex; align-items: center; gap: 7px;
// // // // // // // //   font-size: 13px; font-weight: 500; color: var(--muted);
// // // // // // // // }
// // // // // // // // .lp-trust-sep { color: rgba(212,175,55,0.2); font-size: 18px; }


// // // // // // // // /* ── HOW IT WORKS — HORIZONTAL TIMELINE ── */
// // // // // // // // .lp-how-timeline {
// // // // // // // //   display: grid;
// // // // // // // //   grid-template-columns: 1fr 80px 1fr 80px 1fr;
// // // // // // // //   align-items: start;
// // // // // // // //   gap: 0;
// // // // // // // //   margin-top: 1rem;
// // // // // // // // }
// // // // // // // // .lp-how-bridge {
// // // // // // // //   display: flex;
// // // // // // // //   align-items: flex-start;
// // // // // // // //   justify-content: center;
// // // // // // // //   padding-top: 2.6rem;
// // // // // // // //   opacity: 0.5;
// // // // // // // // }
// // // // // // // // .lp-how-card {
// // // // // // // //   background: var(--card);
// // // // // // // //   border: 0.5px solid var(--border);
// // // // // // // //   border-radius: 20px;
// // // // // // // //   padding: 2.2rem 1.8rem 2rem;
// // // // // // // //   position: relative;
// // // // // // // //   overflow: hidden;
// // // // // // // //   transition: border-color 0.3s, transform 0.3s;
// // // // // // // //   cursor: default;
// // // // // // // // }
// // // // // // // // .lp-how-card::before {
// // // // // // // //   content: '';
// // // // // // // //   position: absolute; inset: 0;
// // // // // // // //   background: radial-gradient(ellipse 80% 60% at 50% -10%, rgba(212,175,55,0.07) 0%, transparent 70%);
// // // // // // // //   opacity: 0;
// // // // // // // //   transition: opacity 0.3s;
// // // // // // // // }
// // // // // // // // .lp-how-card:hover { border-color: rgba(212,175,55,0.3); transform: translateY(-4px); }
// // // // // // // // .lp-how-card:hover::before { opacity: 1; }
// // // // // // // // .lp-how-num {
// // // // // // // //   font-family: var(--serif); font-size: 7rem; font-weight: 700;
// // // // // // // //   color: rgba(212,175,55,0.06); line-height: 1;
// // // // // // // //   position: absolute; bottom: -0.5rem; right: 0.8rem;
// // // // // // // //   letter-spacing: -3px; pointer-events: none; user-select: none;
// // // // // // // // }
// // // // // // // // .lp-how-icon-ring {
// // // // // // // //   width: 56px; height: 56px; border-radius: 16px;
// // // // // // // //   background: linear-gradient(145deg, rgba(212,175,55,0.15), rgba(212,175,55,0.05));
// // // // // // // //   border: 1px solid rgba(212,175,55,0.2);
// // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // //   font-size: 1.6rem; margin-bottom: 1.5rem; position: relative;
// // // // // // // // }
// // // // // // // // .lp-how-icon-ring::after {
// // // // // // // //   content: ''; position: absolute; inset: -4px; border-radius: 20px;
// // // // // // // //   border: 1px dashed rgba(212,175,55,0.1);
// // // // // // // //   animation: lp-spin-slow 12s linear infinite;
// // // // // // // // }
// // // // // // // // @keyframes lp-spin-slow { to { transform: rotate(360deg); } }
// // // // // // // // .lp-how-step-label {
// // // // // // // //   font-size: 9px; font-weight: 700; letter-spacing: 2.5px;
// // // // // // // //   text-transform: uppercase; color: var(--gold); margin-bottom: 0.6rem;
// // // // // // // // }
// // // // // // // // .lp-how-title {
// // // // // // // //   font-family: var(--serif); font-size: 1.65rem; font-weight: 600;
// // // // // // // //   color: var(--white); line-height: 1.15; margin-bottom: 0.75rem;
// // // // // // // // }
// // // // // // // // .lp-how-desc { font-size: 13.5px; color: var(--muted); line-height: 1.8; }

// // // // // // // // /* ── FEATURES — BENTO GRID ── */
// // // // // // // // .lp-feat-bento {
// // // // // // // //   display: grid;
// // // // // // // //   grid-template-columns: repeat(4, 1fr);
// // // // // // // //   grid-template-rows: auto auto;
// // // // // // // //   gap: 10px;
// // // // // // // //   margin-top: 1rem;
// // // // // // // // }
// // // // // // // // .lp-feat-bcard {
// // // // // // // //   background: var(--card);
// // // // // // // //   border: 0.5px solid var(--border);
// // // // // // // //   border-radius: 18px;
// // // // // // // //   padding: 1.8rem 1.6rem;
// // // // // // // //   position: relative; overflow: hidden;
// // // // // // // //   transition: border-color 0.3s, transform 0.25s, background 0.3s;
// // // // // // // //   cursor: default;
// // // // // // // // }
// // // // // // // // .lp-feat-bcard:hover {
// // // // // // // //   border-color: rgba(212,175,55,0.25);
// // // // // // // //   background: #161616;
// // // // // // // //   transform: translateY(-3px);
// // // // // // // // }
// // // // // // // // .lp-feat-bcard.hero {
// // // // // // // //   grid-column: span 2;
// // // // // // // //   background: linear-gradient(145deg, #141410, #111108);
// // // // // // // //   border-color: rgba(212,175,55,0.18);
// // // // // // // //   padding: 2.4rem 2.2rem;
// // // // // // // // }
// // // // // // // // .lp-feat-bcard.hero:hover {
// // // // // // // //   border-color: rgba(212,175,55,0.4);
// // // // // // // //   background: linear-gradient(145deg, #181510, #141208);
// // // // // // // // }
// // // // // // // // .lp-feat-bcard.tall { grid-row: span 2; display: flex; flex-direction: column; }
// // // // // // // // .lp-feat-bcard::after {
// // // // // // // //   content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
// // // // // // // //   background: linear-gradient(90deg, transparent, rgba(212,175,55,0), transparent);
// // // // // // // //   transition: background 0.3s;
// // // // // // // // }
// // // // // // // // .lp-feat-bcard:hover::after {
// // // // // // // //   background: linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent);
// // // // // // // // }
// // // // // // // // .lp-feat-bicon {
// // // // // // // //   width: 44px; height: 44px; border-radius: 12px;
// // // // // // // //   background: rgba(212,175,55,0.08); border: 0.5px solid rgba(212,175,55,0.18);
// // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // //   font-size: 1.3rem; margin-bottom: 1.25rem; flex-shrink: 0;
// // // // // // // // }
// // // // // // // // .lp-feat-bcard.hero .lp-feat-bicon { width: 52px; height: 52px; font-size: 1.6rem; border-radius: 14px; }
// // // // // // // // .lp-feat-btag {
// // // // // // // //   display: inline-block; font-size: 8.5px; font-weight: 700;
// // // // // // // //   letter-spacing: 2px; text-transform: uppercase;
// // // // // // // //   color: var(--gold); margin-bottom: 0.5rem; opacity: 0.7;
// // // // // // // // }
// // // // // // // // .lp-feat-btitle {
// // // // // // // //   font-family: var(--serif); font-size: 1.3rem; font-weight: 600;
// // // // // // // //   color: var(--white); line-height: 1.15; margin-bottom: 0.5rem;
// // // // // // // // }
// // // // // // // // .lp-feat-bcard.hero .lp-feat-btitle { font-size: 1.65rem; }
// // // // // // // // .lp-feat-bdesc { font-size: 13px; color: var(--muted); line-height: 1.8; flex: 1; }
// // // // // // // // .lp-feat-wallet {
// // // // // // // //   display: flex; gap: 6px; margin-top: 1.25rem;
// // // // // // // // }
// // // // // // // // .lp-wallet-chip {
// // // // // // // //   height: 26px; border-radius: 6px; flex: 1;
// // // // // // // //   background: linear-gradient(135deg, rgba(212,175,55,0.15), rgba(212,175,55,0.05));
// // // // // // // //   border: 0.5px solid rgba(212,175,55,0.2);
// // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // //   font-size: 9px; font-weight: 600; color: rgba(212,175,55,0.6); letter-spacing: 0.5px;
// // // // // // // // }
// // // // // // // // .lp-feat-socials { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 1.1rem; }
// // // // // // // // .lp-social-pill {
// // // // // // // //   padding: 4px 10px;
// // // // // // // //   background: rgba(255,255,255,0.04); border: 0.5px solid rgba(255,255,255,0.08);
// // // // // // // //   border-radius: 100px; font-size: 10px; font-weight: 500;
// // // // // // // //   color: rgba(255,255,255,0.45);
// // // // // // // // }
// // // // // // // // .lp-qr-art {
// // // // // // // //   margin-top: auto; padding-top: 1.5rem;
// // // // // // // //   display: grid; grid-template-columns: repeat(5,1fr);
// // // // // // // //   grid-template-rows: repeat(5,1fr); gap: 3px;
// // // // // // // //   opacity: 0.25; transition: opacity 0.3s;
// // // // // // // // }
// // // // // // // // .lp-feat-bcard.tall:hover .lp-qr-art { opacity: 0.45; }
// // // // // // // // .lp-qr-cell { border-radius: 2px; }

// // // // // // // // /* ── mobile fixes ── */
// // // // // // // // @media (max-width: 900px) {
// // // // // // // //   .lp-how-timeline { grid-template-columns: 1fr; }
// // // // // // // //   .lp-how-bridge { display: none; }
// // // // // // // //   .lp-feat-bento { grid-template-columns: 1fr 1fr; }
// // // // // // // //   .lp-feat-bcard.hero { grid-column: span 2; }
// // // // // // // //   .lp-feat-bcard.tall { grid-row: span 1; }
// // // // // // // // }
// // // // // // // // @media (max-width: 600px) {
// // // // // // // //   .lp-feat-bento { grid-template-columns: 1fr; }
// // // // // // // //   .lp-feat-bcard.hero { grid-column: span 1; }
// // // // // // // // }


// // // // // // // // /* ── USE CASES ── */
// // // // // // // // .lp-use-tabs {
// // // // // // // //   display: flex; flex-wrap: wrap; gap: 0.5rem;
// // // // // // // //   justify-content: center; margin-bottom: 3rem;
// // // // // // // // }
// // // // // // // // .lp-use-tab {
// // // // // // // //   padding: 8px 18px; border-radius: 100px;
// // // // // // // //   font-size: 13px; font-weight: 600; cursor: pointer;
// // // // // // // //   transition: all 0.2s; font-family: var(--sans);
// // // // // // // //   display: flex; align-items: center; gap: 6px;
// // // // // // // // }
// // // // // // // // .lp-use-panel {
// // // // // // // //   display: grid; grid-template-columns: 1fr 1fr;
// // // // // // // //   gap: 3rem; align-items: center;
// // // // // // // //   background: var(--card);
// // // // // // // //   border: 0.5px solid var(--border);
// // // // // // // //   border-radius: 24px; padding: 3rem;
// // // // // // // //   position: relative; overflow: hidden;
// // // // // // // // }
// // // // // // // // .lp-use-panel::before {
// // // // // // // //   content: '';
// // // // // // // //   position: absolute; top: 0; left: 0; right: 0; height: 1px;
// // // // // // // //   background: linear-gradient(90deg, transparent, rgba(212,175,55,0.18), transparent);
// // // // // // // // }
// // // // // // // // .lp-use-badge {
// // // // // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // // // // //   padding: 6px 14px; border-radius: 100px;
// // // // // // // //   margin-bottom: 1.5rem; font-size: 12px; font-weight: 700;
// // // // // // // // }
// // // // // // // // .lp-use-tagline {
// // // // // // // //   font-family: var(--serif); font-size: 2.2rem; font-weight: 600;
// // // // // // // //   color: var(--white); line-height: 1.1; margin-bottom: 1rem;
// // // // // // // // }
// // // // // // // // .lp-use-desc {
// // // // // // // //   font-size: 14px; font-weight: 400; color: var(--muted);
// // // // // // // //   line-height: 1.9; margin-bottom: 2rem;
// // // // // // // // }
// // // // // // // // .lp-use-highlights { display: flex; flex-direction: column; gap: 0.75rem; }
// // // // // // // // .lp-use-hl {
// // // // // // // //   display: flex; align-items: center; gap: 12px;
// // // // // // // //   background: #1a1a1a; border: 0.5px solid var(--border);
// // // // // // // //   border-radius: 12px; padding: 1rem 1.25rem;
// // // // // // // // }
// // // // // // // // .lp-use-hl-check {
// // // // // // // //   width: 28px; height: 28px; border-radius: 50%;
// // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // //   flex-shrink: 0; font-size: 11px; font-weight: 700;
// // // // // // // // }
// // // // // // // // .lp-use-hl-text {
// // // // // // // //   font-size: 13px; font-weight: 500; color: rgba(255,255,255,0.75);
// // // // // // // // }

// // // // // // // // /* ── PRICING ── */
// // // // // // // // .lp-pricing-wrap { display: flex; justify-content: center; margin-top: 1rem; }
// // // // // // // // .lp-pricing-card {
// // // // // // // //   background: var(--card);
// // // // // // // //   border: 0.5px solid rgba(212,175,55,0.25);
// // // // // // // //   border-radius: 28px; padding: 3rem 2.5rem;
// // // // // // // //   width: 100%; max-width: 420px;
// // // // // // // //   text-align: center; position: relative;
// // // // // // // // }
// // // // // // // // .lp-pricing-card::before {
// // // // // // // //   content: '';
// // // // // // // //   position: absolute; top: 0; left: 10%; right: 10%; height: 1px;
// // // // // // // //   background: linear-gradient(90deg, transparent, var(--gold), transparent);
// // // // // // // // }
// // // // // // // // .lp-pricing-badge {
// // // // // // // //   display: inline-block;
// // // // // // // //   background: linear-gradient(135deg, var(--gold), var(--gold2));
// // // // // // // //   color: #0A0A0A; border-radius: 100px;
// // // // // // // //   padding: 5px 16px; font-size: 11px; font-weight: 700;
// // // // // // // //   letter-spacing: 1px; text-transform: uppercase;
// // // // // // // //   margin-bottom: 2rem;
// // // // // // // // }
// // // // // // // // .lp-price-num {
// // // // // // // //   font-family: var(--serif); font-size: 5.3rem; font-weight: 600;
// // // // // // // //   color: var(--white); line-height: 1; letter-spacing: -2px;
// // // // // // // // }
// // // // // // // // .lp-price-period {
// // // // // // // //   font-size: 12px; font-weight: 600; color: var(--muted2);
// // // // // // // //   letter-spacing: 2px; text-transform: uppercase; margin: 0.5rem 0 2.5rem;
// // // // // // // // }
// // // // // // // // .lp-price-list { list-style: none; text-align: left; margin-bottom: 2.5rem; }
// // // // // // // // .lp-price-item {
// // // // // // // //   display: flex; align-items: center; gap: 10px;
// // // // // // // //   padding: 0.7rem 0; font-size: 14px; font-weight: 500;
// // // // // // // //   color: rgba(255,255,255,0.75);
// // // // // // // //   border-bottom: 0.5px solid var(--border);
// // // // // // // // }
// // // // // // // // .lp-price-check { color: var(--gold); font-size: 12px; flex-shrink: 0; }

// // // // // // // // /* ── CTA BANNER ── */
// // // // // // // // .lp-cta-banner {
// // // // // // // //   padding: 8rem 3rem;
// // // // // // // //   text-align: center;
// // // // // // // //   position: relative; overflow: hidden;
// // // // // // // // }
// // // // // // // // .lp-cta-banner::before {
// // // // // // // //   content: '';
// // // // // // // //   position: absolute; inset: 0;
// // // // // // // //   background: radial-gradient(ellipse 80% 60% at 50% 50%, rgba(212,175,55,0.07) 0%, transparent 70%);
// // // // // // // //   pointer-events: none;
// // // // // // // // }
// // // // // // // // .lp-cta-title {
// // // // // // // //   font-family: var(--serif);
// // // // // // // //   font-size: clamp(2.8rem, 6vw, 5.2rem);
// // // // // // // //   font-weight: 600; line-height: 1.05;
// // // // // // // //   color: var(--white); margin-bottom: 1.5rem;
// // // // // // // //   letter-spacing: -0.5px;
// // // // // // // // }
// // // // // // // // .lp-cta-title em { font-style: italic; color: var(--gold); }
// // // // // // // // .lp-cta-sub {
// // // // // // // //   font-size: 16px; font-weight: 400; color: var(--muted);
// // // // // // // //   margin-bottom: 3rem; line-height: 1.8;
// // // // // // // // }

// // // // // // // // /* ── FOOTER ── */
// // // // // // // // .lp-footer {
// // // // // // // //   padding: 3rem 3rem 4rem;
// // // // // // // //   border-top: 0.5px solid var(--border);
// // // // // // // // }
// // // // // // // // .lp-footer-inner {
// // // // // // // //   max-width: 1180px; margin: 0 auto;
// // // // // // // //   display: flex; flex-direction: column; align-items: center; gap: 1rem;
// // // // // // // // }
// // // // // // // // .lp-footer-logo {
// // // // // // // //   display: flex; align-items: center; gap: 10px;
// // // // // // // //   font-family: var(--sans); font-size: 15px; font-weight: 600; color: var(--white);
// // // // // // // // }
// // // // // // // // .lp-footer-logo em { color: var(--gold); font-style: normal; font-weight: 700; }
// // // // // // // // .lp-footer-tagline {
// // // // // // // //   font-size: 13px; font-weight: 400; color: var(--muted2);
// // // // // // // // }
// // // // // // // // .lp-footer-copy {
// // // // // // // //   font-size: 12px; font-weight: 400; color: rgba(255,255,255,0.18);
// // // // // // // // }

// // // // // // // // /* ── FADE-IN ON SCROLL ── */
// // // // // // // // .reveal {
// // // // // // // //   opacity: 0; transform: translateY(24px);
// // // // // // // //   transition: opacity 0.7s ease, transform 0.7s ease;
// // // // // // // // }
// // // // // // // // .reveal.visible { opacity: 1; transform: translateY(0); }

// // // // // // // // /* ── LIGHT THEME OVERRIDES ── */
// // // // // // // // .lp-light {
// // // // // // // //   --black: #F8F7F4;
// // // // // // // //   --dark: #F0EDE6;
// // // // // // // //   --card: #FFFFFF;
// // // // // // // //   --border: rgba(0,0,0,0.08);
// // // // // // // //   --white: #111111;
// // // // // // // //   --muted: rgba(0,0,0,0.5);
// // // // // // // //   --muted2: rgba(0,0,0,0.35);
// // // // // // // // }
// // // // // // // // .lp-light body,
// // // // // // // // .lp-light { background: #F8F7F4; color: #111; }
// // // // // // // // .lp-light ::-webkit-scrollbar-track { background: #F8F7F4; }
// // // // // // // // .lp-light ::-webkit-scrollbar-thumb { background: rgba(184,148,42,0.3); }

// // // // // // // // /* nav light */
// // // // // // // // .lp-light .lp-nav.scrolled {
// // // // // // // //   background: rgba(248,247,244,0.92);
// // // // // // // //   border-bottom: 0.5px solid rgba(184,148,42,0.18);
// // // // // // // // }
// // // // // // // // .lp-light .lp-logo { color: #111; }
// // // // // // // // .lp-light .lp-nav-link { color: rgba(0,0,0,0.45); }
// // // // // // // // .lp-light .lp-nav-link:hover { color: #111; }
// // // // // // // // .lp-light .lp-nav-cta { color: #0A0A0A; }

// // // // // // // // /* hero light */
// // // // // // // // .lp-light .lp-hero-title { color: #111; }
// // // // // // // // .lp-light .lp-hero-sub { color: rgba(0,0,0,0.5); }
// // // // // // // // .lp-light .lp-hero-stats { border-top-color: rgba(0,0,0,0.08); }
// // // // // // // // .lp-light .lp-stat-label { color: rgba(0,0,0,0.35); }
// // // // // // // // .lp-light .lp-btn-ghost {
// // // // // // // //   color: rgba(0,0,0,0.5); border-color: rgba(0,0,0,0.15);
// // // // // // // // }
// // // // // // // // .lp-light .lp-btn-ghost:hover { color: #111; border-color: rgba(184,148,42,0.4); }
// // // // // // // // .lp-light section[style*="background: var(--black)"] { background: #F8F7F4 !important; }

// // // // // // // // /* hero scan phones stay dark — intentional: they're device mockups */
// // // // // // // // .lp-light .phone-scanner { background: #1A1A1A; }
// // // // // // // // .lp-light .phone-profile { background: #0E0E0E; }

// // // // // // // // /* trust bar */
// // // // // // // // .lp-light .lp-trust { background: #F0EDE6; }
// // // // // // // // .lp-light .lp-trust-label { color: rgba(0,0,0,0.35); }
// // // // // // // // .lp-light .lp-trust-item { color: rgba(0,0,0,0.55); }
// // // // // // // // .lp-light .lp-trust-sep { color: rgba(184,148,42,0.3); }

// // // // // // // // /* gold rule */
// // // // // // // // .lp-light .gold-rule { background: linear-gradient(90deg, transparent, rgba(184,148,42,0.25), transparent); }

// // // // // // // // /* sections */
// // // // // // // // .lp-light .lp-section { background: #F8F7F4; }
// // // // // // // // .lp-light .lp-section-title { color: #111; }
// // // // // // // // .lp-light .lp-section-sub { color: rgba(0,0,0,0.5); }
// // // // // // // // .lp-light .lp-eyebrow { color: #B8942A; }

// // // // // // // // /* steps */
// // // // // // // // .lp-light .lp-step { background: #FFFFFF; }
// // // // // // // // .lp-light .lp-step:hover { background: #F5F2EC; }
// // // // // // // // .lp-light .lp-steps { border-color: rgba(0,0,0,0.08); }
// // // // // // // // .lp-light .lp-step-title { color: #111; }
// // // // // // // // .lp-light .lp-step-desc { color: rgba(0,0,0,0.5); }
// // // // // // // // .lp-light .lp-step-num { color: rgba(184,148,42,0.15); }
// // // // // // // // .lp-light .lp-step-icon-wrap { background: rgba(184,148,42,0.08); border-color: rgba(184,148,42,0.2); }

// // // // // // // // /* features */
// // // // // // // // .lp-light .lp-feat-grid { border-color: rgba(0,0,0,0.07); }
// // // // // // // // .lp-light .lp-feat-card { background: #FFFFFF; }
// // // // // // // // .lp-light .lp-feat-card:hover { background: #F5F2EC; }
// // // // // // // // .lp-light .lp-feat-title { color: #111; }
// // // // // // // // .lp-light .lp-feat-desc { color: rgba(0,0,0,0.5); }

// // // // // // // // /* use cases */
// // // // // // // // .lp-light .lp-use-tab { color: rgba(0,0,0,0.45) !important; border-color: rgba(0,0,0,0.1) !important; }
// // // // // // // // .lp-light .lp-use-tab[style*="rgba(212,175,55,0.1)"] { color: #B8942A !important; border-color: rgba(184,148,42,0.4) !important; }
// // // // // // // // .lp-light .lp-use-panel { background: #FFFFFF; border-color: rgba(184,148,42,0.15) !important; }
// // // // // // // // .lp-light .lp-use-tagline { color: #111; }
// // // // // // // // .lp-light .lp-use-desc { color: rgba(0,0,0,0.5); }
// // // // // // // // .lp-light .lp-use-hl { background: #F8F7F4; border-color: rgba(0,0,0,0.07); }
// // // // // // // // .lp-light .lp-use-hl-text { color: rgba(0,0,0,0.65); }

// // // // // // // // /* pricing */
// // // // // // // // .lp-light .lp-pricing-card { background: #FFFFFF; border-color: rgba(184,148,42,0.25); }
// // // // // // // // .lp-light .lp-pricing-card::before { background: linear-gradient(90deg, transparent, #B8942A, transparent); }
// // // // // // // // .lp-light .lp-price-num { color: #111; }
// // // // // // // // .lp-light .lp-price-period { color: rgba(0,0,0,0.35); }
// // // // // // // // .lp-light .lp-price-item { color: rgba(0,0,0,0.65); border-bottom-color: rgba(0,0,0,0.07); }

// // // // // // // // /* cta banner */
// // // // // // // // .lp-light .lp-cta-banner { background: #F0EDE6; }
// // // // // // // // .lp-light .lp-cta-title { color: #111; }
// // // // // // // // .lp-light .lp-cta-sub { color: rgba(0,0,0,0.5); }

// // // // // // // // /* footer */
// // // // // // // // .lp-light .lp-footer { border-top-color: rgba(0,0,0,0.08); background: #ECEAE4; }
// // // // // // // // .lp-light .lp-footer-logo { color: #111; }
// // // // // // // // .lp-light .lp-footer-tagline { color: rgba(0,0,0,0.4); }
// // // // // // // // .lp-light .lp-footer-copy { color: rgba(0,0,0,0.25); }

// // // // // // // // /* theme toggle btn */
// // // // // // // // .lp-theme-btn {
// // // // // // // //   width: 40px; height: 40px; border-radius: 50%;
// // // // // // // //   border: 0.5px solid rgba(212,175,55,0.35);
// // // // // // // //   background: rgba(212,175,55,0.08);
// // // // // // // //   cursor: pointer; display: flex; align-items: center; justify-content: center;
// // // // // // // //   transition: background 0.2s, transform 0.15s;
// // // // // // // //   flex-shrink: 0;
// // // // // // // // }
// // // // // // // // .lp-theme-btn:hover { background: rgba(212,175,55,0.15); transform: scale(1.08); }
// // // // // // // // .lp-light .lp-theme-btn {
// // // // // // // //   border-color: rgba(184,148,42,0.3);
// // // // // // // //   background: rgba(184,148,42,0.08);
// // // // // // // // }
// // // // // // // // .lp-light .lp-theme-btn:hover { background: rgba(184,148,42,0.15); }

// // // // // // // // /* ── MOBILE ── */
// // // // // // // // @media (max-width: 900px) {
// // // // // // // //   .lp-hero { grid-template-columns: 1fr; text-align: center; padding: 8rem 1.5rem 4rem; }
// // // // // // // //   .lp-hero-sub { margin: 0 auto 1rem; }
// // // // // // // //   .lp-hero-ctas { justify-content: center; }
// // // // // // // //   .lp-hero-stats { justify-content: center; }
// // // // // // // //   .lp-hero-right { margin-top: 3rem; }
// // // // // // // //   .scan-scene { width: 300px; height: 380px; transform: scale(0.85); }
// // // // // // // //   .lp-steps { grid-template-columns: 1fr; }
// // // // // // // //   .lp-feat-grid { grid-template-columns: 1fr 1fr; }
// // // // // // // //   .lp-use-panel { grid-template-columns: 1fr; }
// // // // // // // //   .lp-nav { padding: 1rem 1.5rem; }
// // // // // // // //   .lp-nav.scrolled { padding: 0.75rem 1.5rem; }
// // // // // // // //   .lp-nav-links { gap: 1rem; }
// // // // // // // //   .lp-section { padding: 5rem 1.5rem; }
// // // // // // // // }
// // // // // // // // @media (max-width: 600px) {
// // // // // // // //   .lp-feat-grid { grid-template-columns: 1fr; }
// // // // // // // //   .lp-nav-link { display: none; }
// // // // // // // // }
// // // // // // // // `;

// // // // // // // // /* ─────────────────────────────────────────
// // // // // // // //    QR SCAN ANIMATION
// // // // // // // // ───────────────────────────────────────── */
// // // // // // // // const QR_PATTERN = [
// // // // // // // //     [1, 1, 1, 0, 1, 0],
// // // // // // // //     [1, 0, 1, 1, 0, 1],
// // // // // // // //     [1, 1, 1, 0, 1, 1],
// // // // // // // //     [0, 1, 0, 1, 0, 0],
// // // // // // // //     [1, 0, 1, 1, 1, 0],
// // // // // // // //     [0, 1, 0, 0, 1, 1],
// // // // // // // // ];

// // // // // // // // function ScanScene() {
// // // // // // // //     return (
// // // // // // // //         <div className="scan-scene">
// // // // // // // //             <div className="hero-orb-1" />

// // // // // // // //             {/* Phone A — scanner */}
// // // // // // // //             <div className="phone-scanner">
// // // // // // // //                 <div className="phone-scanner-screen">
// // // // // // // //                     <div className="scanner-viewfinder">
// // // // // // // //                         <div className="vf-corner-tr" />
// // // // // // // //                         <div className="vf-corner-bl" />
// // // // // // // //                         <div className="scan-laser" />
// // // // // // // //                         <div className="mini-qr">
// // // // // // // //                             {QR_PATTERN.map((row, ri) =>
// // // // // // // //                                 row.map((cell, ci) => (
// // // // // // // //                                     <div
// // // // // // // //                                         key={`${ri}-${ci}`}
// // // // // // // //                                         className="mini-qr-cell"
// // // // // // // //                                         style={{
// // // // // // // //                                             background: cell ? "rgba(212,175,55,0.5)" : "transparent",
// // // // // // // //                                         }}
// // // // // // // //                                     />
// // // // // // // //                                 ))
// // // // // // // //                             )}
// // // // // // // //                         </div>
// // // // // // // //                     </div>
// // // // // // // //                     <div className="scanner-label">Point at QR code</div>
// // // // // // // //                 </div>
// // // // // // // //             </div>

// // // // // // // //             <div className="beam-line" />
// // // // // // // //             <div className="beam-particle" />

// // // // // // // //             {/* Phone B — profile card opening */}
// // // // // // // //             <div className="phone-profile">
// // // // // // // //                 <div className="profile-card-wrap">
// // // // // // // //                     <div className="profile-banner">
// // // // // // // //                         <div className="profile-banner-img" />
// // // // // // // //                         <div className="profile-banner-fade" />
// // // // // // // //                         <div className="profile-avatar-ring">
// // // // // // // //                             <div className="profile-avatar-inner">UN</div>
// // // // // // // //                         </div>
// // // // // // // //                     </div>

// // // // // // // //                     <div className="profile-info">
// // // // // // // //                         <div className="profile-info-name">
// // // // // // // //                             User <em>Name</em>
// // // // // // // //                         </div>
// // // // // // // //                         <div className="profile-info-role">Director · Company Name</div>

// // // // // // // //                         <div className="profile-tiles">
// // // // // // // //                             {[
// // // // // // // //                                 ["📞", "Phone"],
// // // // // // // //                                 ["✉️", "Email"],
// // // // // // // //                                 ["📍", "Delhi"],
// // // // // // // //                             ].map(([icon, label]) => (
// // // // // // // //                                 <div className="profile-tile" key={label}>
// // // // // // // //                                     <div className="tile-dot">
// // // // // // // //                                         <div className="tile-dot-inner" />
// // // // // // // //                                     </div>
// // // // // // // //                                     <div className="tile-text">{label}</div>
// // // // // // // //                                 </div>
// // // // // // // //                             ))}
// // // // // // // //                         </div>

// // // // // // // //                         <button className="profile-save-btn">↓ Save Contact</button>

// // // // // // // //                         <div className="profile-tiles" style={{ marginTop: "1rem" }}>
// // // // // // // //                             {[
// // // // // // // //                                 ["🌐", "Website"],
// // // // // // // //                                 ["✉️", "Resume"],
// // // // // // // //                                 ["ⓕ", "Facebook"],
// // // // // // // //                             ].map(([icon, label]) => (
// // // // // // // //                                 <div className="profile-tile" key={label}>
// // // // // // // //                                     <div className="tile-dot">
// // // // // // // //                                         <div className="tile-dot-inner" />
// // // // // // // //                                     </div>
// // // // // // // //                                     <div className="tile-text">{label}</div>
// // // // // // // //                                 </div>
// // // // // // // //                             ))}
// // // // // // // //                         </div>
// // // // // // // //                     </div>
// // // // // // // //                 </div>
// // // // // // // //             </div>
// // // // // // // //         </div>
// // // // // // // //     );
// // // // // // // // }

// // // // // // // // /* ─────────────────────────────────────────
// // // // // // // //    USE CASES
// // // // // // // // ───────────────────────────────────────── */
// // // // // // // // const USE_CASES = [
// // // // // // // //     {
// // // // // // // //         icon: "👔",
// // // // // // // //         role: "Job Seekers",
// // // // // // // //         gold: "rgba(212,175,55,1)",
// // // // // // // //         tagline: "Land your next opportunity faster",
// // // // // // // //         desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one elegant scan.",
// // // // // // // //         highlights: [
// // // // // // // //             "Resume attached & downloadable",
// // // // // // // //             "LinkedIn & GitHub links",
// // // // // // // //             "QR code for instant sharing",
// // // // // // // //             "Save contact in one tap",
// // // // // // // //         ],
// // // // // // // //     },
// // // // // // // //     {
// // // // // // // //         icon: "💼",
// // // // // // // //         role: "Freelancers",
// // // // // // // //         gold: "rgba(212,175,55,1)",
// // // // // // // //         tagline: "Win clients before you even speak",
// // // // // // // //         desc: "Share everything a client needs — portfolio, social proof, and contact — all in a single professional link. Close deals faster.",
// // // // // // // //         highlights: [
// // // // // // // //             "Portfolio & website link",
// // // // // // // //             "WhatsApp quick contact",
// // // // // // // //             "Instagram & social proof",
// // // // // // // //             "Professional digital presence",
// // // // // // // //         ],
// // // // // // // //     },
// // // // // // // //     {
// // // // // // // //         icon: "🏢",
// // // // // // // //         role: "Business Owners",
// // // // // // // //         gold: "rgba(212,175,55,1)",
// // // // // // // //         tagline: "Network smarter, not harder",
// // // // // // // //         desc: "Stop spending on printed cards that get lost. Share via QR at events — always updated, never outdated, never thrown away.",
// // // // // // // //         highlights: [
// // // // // // // //             "Always up-to-date details",
// // // // // // // //             "Google Wallet for offline use",
// // // // // // // //             "QR code for events & meetups",
// // // // // // // //             "Share via WhatsApp or email",
// // // // // // // //         ],
// // // // // // // //     },
// // // // // // // //     {
// // // // // // // //         icon: "🎓",
// // // // // // // //         role: "Students",
// // // // // // // //         gold: "rgba(212,175,55,1)",
// // // // // // // //         tagline: "Build your professional identity early",
// // // // // // // //         desc: "Stand out in campus placements and internship drives. Show your skills, projects, and social profiles before you say a word.",
// // // // // // // //         highlights: [
// // // // // // // //             "GitHub & project links",
// // // // // // // //             "Resume upload for placements",
// // // // // // // //             "LinkedIn profile showcase",
// // // // // // // //             "Free forever — no cost",
// // // // // // // //         ],
// // // // // // // //     },
// // // // // // // //     {
// // // // // // // //         icon: "📣",
// // // // // // // //         role: "Sales Pros",
// // // // // // // //         gold: "rgba(212,175,55,1)",
// // // // // // // //         tagline: "Close the follow-up gap instantly",
// // // // // // // //         desc: "At trade shows or cold visits — one QR scan and your prospect has everything. Number, email, website, product links in one place.",
// // // // // // // //         highlights: [
// // // // // // // //             "Instant QR share at events",
// // // // // // // //             "Website & product links",
// // // // // // // //             "Save contact in one tap",
// // // // // // // //             "Google Wallet for quick access",
// // // // // // // //         ],
// // // // // // // //     },
// // // // // // // //     {
// // // // // // // //         icon: "🧑‍💻",
// // // // // // // //         role: "Developers",
// // // // // // // //         gold: "rgba(212,175,55,1)",
// // // // // // // //         tagline: "Your entire dev profile in one link",
// // // // // // // //         desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share at hackathons or in open source communities.",
// // // // // // // //         highlights: [
// // // // // // // //             "GitHub & portfolio links",
// // // // // // // //             "Twitter / X profile",
// // // // // // // //             "Resume for job applications",
// // // // // // // //             "Professional dev identity",
// // // // // // // //         ],
// // // // // // // //     },
// // // // // // // // ];

// // // // // // // // function UseCasesSection({ navigate }) {
// // // // // // // //     const [active, setActive] = useState(0);
// // // // // // // //     const cur = USE_CASES[active];

// // // // // // // //     return (
// // // // // // // //         <section className="lp-section">
// // // // // // // //             <div className="lp-section-inner">
// // // // // // // //                 <span className="lp-eyebrow reveal">Who is it for</span>
// // // // // // // //                 <h2 className="lp-section-title reveal">
// // // // // // // //                     Built for every <em>professional</em>
// // // // // // // //                 </h2>
// // // // // // // //                 <p className="lp-section-sub reveal">
// // // // // // // //                     Whether you're a student or a CEO — your digital card works for you.
// // // // // // // //                 </p>

// // // // // // // //                 <div className="lp-use-tabs reveal">
// // // // // // // //                     {USE_CASES.map((u, i) => (
// // // // // // // //                         <button
// // // // // // // //                             key={i}
// // // // // // // //                             className="lp-use-tab"
// // // // // // // //                             onClick={() => setActive(i)}
// // // // // // // //                             style={{
// // // // // // // //                                 border:
// // // // // // // //                                     active === i
// // // // // // // //                                         ? `0.5px solid rgba(212,175,55,0.5)`
// // // // // // // //                                         : "0.5px solid rgba(255,255,255,0.1)",
// // // // // // // //                                 background:
// // // // // // // //                                     active === i ? "rgba(212,175,55,0.1)" : "transparent",
// // // // // // // //                                 color: active === i ? "var(--gold)" : "var(--muted)",
// // // // // // // //                             }}
// // // // // // // //                         >
// // // // // // // //                             {u.icon} {u.role}
// // // // // // // //                         </button>
// // // // // // // //                     ))}
// // // // // // // //                 </div>

// // // // // // // //                 <div
// // // // // // // //                     className="lp-use-panel"
// // // // // // // //                     style={{ borderColor: "rgba(212,175,55,0.15)" }}
// // // // // // // //                 >
// // // // // // // //                     <div>
// // // // // // // //                         <div
// // // // // // // //                             className="lp-use-badge"
// // // // // // // //                             style={{
// // // // // // // //                                 background: "rgba(212,175,55,0.1)",
// // // // // // // //                                 color: "var(--gold)",
// // // // // // // //                                 border: "0.5px solid rgba(212,175,55,0.25)",
// // // // // // // //                             }}
// // // // // // // //                         >
// // // // // // // //                             <span>{cur.icon}</span>
// // // // // // // //                             <span style={{ fontSize: "12px", letterSpacing: "0.5px" }}>
// // // // // // // //                                 {cur.role}
// // // // // // // //                             </span>
// // // // // // // //                         </div>

// // // // // // // //                         <div className="lp-use-tagline">{cur.tagline}</div>
// // // // // // // //                         <p className="lp-use-desc">{cur.desc}</p>

// // // // // // // //                         <button className="lp-btn-gold" onClick={() => navigate("/animation/card")}>
// // // // // // // //                             Create My Card — Free →
// // // // // // // //                         </button>
// // // // // // // //                     </div>

// // // // // // // //                     <div className="lp-use-highlights">
// // // // // // // //                         {cur.highlights.map((h, i) => (
// // // // // // // //                             <div className="lp-use-hl" key={i}>
// // // // // // // //                                 <div
// // // // // // // //                                     className="lp-use-hl-check"
// // // // // // // //                                     style={{
// // // // // // // //                                         background: "rgba(212,175,55,0.1)",
// // // // // // // //                                         color: "var(--gold)",
// // // // // // // //                                     }}
// // // // // // // //                                 >
// // // // // // // //                                     ✓
// // // // // // // //                                 </div>
// // // // // // // //                                 <span className="lp-use-hl-text">{h}</span>
// // // // // // // //                             </div>
// // // // // // // //                         ))}
// // // // // // // //                     </div>
// // // // // // // //                 </div>
// // // // // // // //             </div>
// // // // // // // //         </section>
// // // // // // // //     );
// // // // // // // // }

// // // // // // // // /* ─────────────────────────────────────────
// // // // // // // //    MAIN COMPONENT
// // // // // // // // ───────────────────────────────────────── */
// // // // // // // // export default function LandingPage() {
// // // // // // // //     const navigate = useNavigate();
// // // // // // // //     const [scrolled, setScrolled] = useState(false);
// // // // // // // //     const [isLight, setIsLight] = useState(false);

// // // // // // // //     /* scroll nav */
// // // // // // // //     useEffect(() => {
// // // // // // // //         const onScroll = () => setScrolled(window.scrollY > 40);
// // // // // // // //         window.addEventListener("scroll", onScroll);
// // // // // // // //         return () => window.removeEventListener("scroll", onScroll);
// // // // // // // //     }, []);

// // // // // // // //     /* scroll reveal */
// // // // // // // //     useEffect(() => {
// // // // // // // //         const els = document.querySelectorAll(".reveal");
// // // // // // // //         const observer = new IntersectionObserver(
// // // // // // // //             (entries) =>
// // // // // // // //                 entries.forEach((e) => {
// // // // // // // //                     if (e.isIntersecting) e.target.classList.add("visible");
// // // // // // // //                 }),
// // // // // // // //             { threshold: 0.12 }
// // // // // // // //         );

// // // // // // // //         els.forEach((el) => observer.observe(el));
// // // // // // // //         return () => observer.disconnect();
// // // // // // // //     }, []);

// // // // // // // //     return (
// // // // // // // //         <div
// // // // // // // //             className={isLight ? "lp-light" : ""}
// // // // // // // //             style={{
// // // // // // // //                 background: isLight ? "#F8F7F4" : "#080808",
// // // // // // // //                 minHeight: "100vh",
// // // // // // // //             }}
// // // // // // // //         >
// // // // // // // //             <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

// // // // // // // //             {/* ── NAV ── */}
// // // // // // // //             <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
// // // // // // // //                 <div className="lp-nav-inner">
// // // // // // // //                     <a href="/" className="lp-logo">
// // // // // // // //                         <svg className="lp-logo-icon" viewBox="0 0 34 34" fill="none">
// // // // // // // //                             <rect width="34" height="34" rx="9" fill="#1A1A1A" />
// // // // // // // //                             <rect
// // // // // // // //                                 width="34"
// // // // // // // //                                 height="34"
// // // // // // // //                                 rx="9"
// // // // // // // //                                 stroke="rgba(212,175,55,0.2)"
// // // // // // // //                                 strokeWidth="0.5"
// // // // // // // //                             />
// // // // // // // //                             <circle
// // // // // // // //                                 cx="15"
// // // // // // // //                                 cy="13"
// // // // // // // //                                 r="5"
// // // // // // // //                                 stroke="#D4AF37"
// // // // // // // //                                 strokeWidth="1.5"
// // // // // // // //                                 fill="none"
// // // // // // // //                             />
// // // // // // // //                             <path
// // // // // // // //                                 d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27"
// // // // // // // //                                 stroke="#D4AF37"
// // // // // // // //                                 strokeWidth="1.5"
// // // // // // // //                                 strokeLinecap="round"
// // // // // // // //                                 fill="none"
// // // // // // // //                             />
// // // // // // // //                             <path
// // // // // // // //                                 d="M23 11 L27 11 L27 23"
// // // // // // // //                                 stroke="rgba(212,175,55,0.5)"
// // // // // // // //                                 strokeWidth="1.3"
// // // // // // // //                                 strokeLinecap="round"
// // // // // // // //                                 strokeLinejoin="round"
// // // // // // // //                                 fill="none"
// // // // // // // //                             />
// // // // // // // //                             <circle cx="27" cy="23" r="1.8" fill="#D4AF37" />
// // // // // // // //                         </svg>
// // // // // // // //                         myprofile<em>corner</em>
// // // // // // // //                     </a>

// // // // // // // //                     <div className="lp-nav-links">
// // // // // // // //                         <a href="#how" className="lp-nav-link">
// // // // // // // //                             How it works
// // // // // // // //                         </a>
// // // // // // // //                         <a href="#features" className="lp-nav-link">
// // // // // // // //                             Features
// // // // // // // //                         </a>
// // // // // // // //                         <a href="#pricing" className="lp-nav-link">
// // // // // // // //                             Pricing
// // // // // // // //                         </a>

// // // // // // // //                         <button
// // // // // // // //                             className="lp-theme-btn"
// // // // // // // //                             onClick={() => setIsLight((p) => !p)}
// // // // // // // //                             title={isLight ? "Switch to Dark" : "Switch to Light"}
// // // // // // // //                             aria-label="Toggle theme"
// // // // // // // //                         >
// // // // // // // //                             {isLight ? (
// // // // // // // //                                 <svg
// // // // // // // //                                     width="16"
// // // // // // // //                                     height="16"
// // // // // // // //                                     viewBox="0 0 24 24"
// // // // // // // //                                     fill="none"
// // // // // // // //                                     stroke="#B8942A"
// // // // // // // //                                     strokeWidth="2"
// // // // // // // //                                     strokeLinecap="round"
// // // // // // // //                                     strokeLinejoin="round"
// // // // // // // //                                 >
// // // // // // // //                                     <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
// // // // // // // //                                 </svg>
// // // // // // // //                             ) : (
// // // // // // // //                                 <svg
// // // // // // // //                                     width="16"
// // // // // // // //                                     height="16"
// // // // // // // //                                     viewBox="0 0 24 24"
// // // // // // // //                                     fill="none"
// // // // // // // //                                     stroke="#D4AF37"
// // // // // // // //                                     strokeWidth="2"
// // // // // // // //                                     strokeLinecap="round"
// // // // // // // //                                     strokeLinejoin="round"
// // // // // // // //                                 >
// // // // // // // //                                     <circle cx="12" cy="12" r="5" />
// // // // // // // //                                     <line x1="12" y1="1" x2="12" y2="3" />
// // // // // // // //                                     <line x1="12" y1="21" x2="12" y2="23" />
// // // // // // // //                                     <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
// // // // // // // //                                     <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
// // // // // // // //                                     <line x1="1" y1="12" x2="3" y2="12" />
// // // // // // // //                                     <line x1="21" y1="12" x2="23" y2="12" />
// // // // // // // //                                     <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
// // // // // // // //                                     <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
// // // // // // // //                                 </svg>
// // // // // // // //                             )}
// // // // // // // //                         </button>

// // // // // // // //                         <button className="lp-nav-cta" onClick={() => navigate("/animation/card")}>
// // // // // // // //                             Create Free Card
// // // // // // // //                         </button>
// // // // // // // //                     </div>
// // // // // // // //                 </div>
// // // // // // // //             </nav>

// // // // // // // //             {/* ── HERO ── */}
// // // // // // // //             <section
// // // // // // // //                 style={{
// // // // // // // //                     background: isLight ? "#F8F7F4" : "#080808",
// // // // // // // //                     overflow: "hidden",
// // // // // // // //                     position: "relative",
// // // // // // // //                 }}
// // // // // // // //             >
// // // // // // // //                 <div className="lp-hero">
// // // // // // // //                     <div
// // // // // // // //                         style={{
// // // // // // // //                             position: "absolute",
// // // // // // // //                             width: "600px",
// // // // // // // //                             height: "600px",
// // // // // // // //                             borderRadius: "50%",
// // // // // // // //                             background:
// // // // // // // //                                 "radial-gradient(circle, rgba(212,175,55,0.05) 0%, transparent 65%)",
// // // // // // // //                             top: "-200px",
// // // // // // // //                             left: "-100px",
// // // // // // // //                             pointerEvents: "none",
// // // // // // // //                         }}
// // // // // // // //                     />

// // // // // // // //                     {/* LEFT */}
// // // // // // // //                     <div className="lp-hero-left">
// // // // // // // //                         <div className="lp-hero-eyebrow">
// // // // // // // //                             <div className="lp-hero-eyebrow-dot" />
// // // // // // // //                             100% Free · No App Needed
// // // // // // // //                         </div>

// // // // // // // //                         <h1 className="lp-hero-title">
// // // // // // // //                             Your Digital
// // // // // // // //                             <br />
// // // // // // // //                             Identity, <em>Always</em>
// // // // // // // //                             <br />
// // // // // // // //                             in Your Pocket
// // // // // // // //                         </h1>

// // // // // // // //                         <p className="lp-hero-sub">
// // // // // // // //                             Replace paper visiting cards with a smart digital card. Share your
// // // // // // // //                             profile instantly via QR code, link, or Google Wallet — no
// // // // // // // //                             printing, no waste.
// // // // // // // //                         </p>

// // // // // // // //                         <div className="lp-hero-stats">
// // // // // // // //                             {[
// // // // // // // //                                 ["100%", "Free forever"],
// // // // // // // //                                 ["2 min", "Setup time"],
// // // // // // // //                                 ["0", "Paper waste"],
// // // // // // // //                             ].map(([n, l]) => (
// // // // // // // //                                 <div key={l}>
// // // // // // // //                                     <div className="lp-stat-num">{n}</div>
// // // // // // // //                                     <div className="lp-stat-label">{l}</div>
// // // // // // // //                                 </div>
// // // // // // // //                             ))}
// // // // // // // //                         </div>

// // // // // // // //                         <div className="lp-hero-ctas">
// // // // // // // //                             <button className="lp-btn-gold" onClick={() => navigate("/animation/card")}>
// // // // // // // //                                 Create My Card — It's Free →
// // // // // // // //                             </button>
// // // // // // // //                             <a href="#how" className="lp-btn-ghost">
// // // // // // // //                                 See how it works
// // // // // // // //                             </a>
// // // // // // // //                         </div>


// // // // // // // //                     </div>

// // // // // // // //                     {/* RIGHT */}
// // // // // // // //                     <div className="lp-hero-right">
// // // // // // // //                         <ScanScene />
// // // // // // // //                     </div>
// // // // // // // //                 </div>
// // // // // // // //             </section>

// // // // // // // //             <div className="gold-rule" />

// // // // // // // //             {/* ── TRUST BAR ── */}
// // // // // // // //             <div
// // // // // // // //                 className="lp-trust"
// // // // // // // //                 style={{ background: isLight ? "#F0EDE6" : "transparent" }}
// // // // // // // //             >
// // // // // // // //                 <span className="lp-trust-label">Works seamlessly with</span>
// // // // // // // //                 <div className="lp-trust-items">
// // // // // // // //                     {[
// // // // // // // //                         ["🔥", "Firebase"],
// // // // // // // //                         ["🪙", "Google Wallet"],
// // // // // // // //                         ["📱", "Android & iOS"],
// // // // // // // //                         ["🔗", "Any Browser"],
// // // // // // // //                     ].map(([icon, label], i, arr) => (
// // // // // // // //                         <React.Fragment key={label}>
// // // // // // // //                             <div className="lp-trust-item">
// // // // // // // //                                 <span>{icon}</span>
// // // // // // // //                                 <span>{label}</span>
// // // // // // // //                             </div>
// // // // // // // //                             {i < arr.length - 1 && <span className="lp-trust-sep">·</span>}
// // // // // // // //                         </React.Fragment>
// // // // // // // //                     ))}
// // // // // // // //                 </div>
// // // // // // // //             </div>

// // // // // // // //             <div className="gold-rule" />

// // // // // // // //             {/* ── HOW IT WORKS ── */}
// // // // // // // //             {/* ── HOW IT WORKS ── */}
// // // // // // // //             <section id="how" className="lp-section">
// // // // // // // //                 <div className="lp-section-inner">
// // // // // // // //                     <span className="lp-eyebrow reveal">How it works</span>
// // // // // // // //                     <h2 className="lp-section-title reveal">From setup to sharing <em>in 3 steps</em></h2>
// // // // // // // //                     <p className="lp-section-sub reveal">No app downloads. No tech skills required. Just fill, share, impress.</p>

// // // // // // // //                     <div className="lp-how-timeline reveal">
// // // // // // // //                         {[
// // // // // // // //                             { num: "01", icon: "✏️", label: "Step 01", title: "Fill Your Profile", desc: "Add your name, photo, contact info, social links, resume & website. Takes less than 2 minutes." },
// // // // // // // //                             { num: "02", icon: "🎴", label: "Step 02", title: "Get Your Digital Card", desc: "Instantly get a beautiful card with a unique QR code & shareable link — no design skills needed." },
// // // // // // // //                             { num: "03", icon: "🚀", label: "Step 03", title: "Share Anywhere", desc: "Show QR in meetings, add to Google Wallet, or send your link via WhatsApp, email, or LinkedIn." },
// // // // // // // //                         ].map((step, i) => (
// // // // // // // //                             <React.Fragment key={step.num}>
// // // // // // // //                                 <div className="lp-how-card">
// // // // // // // //                                     <div className="lp-how-num">{step.num}</div>
// // // // // // // //                                     <div className="lp-how-icon-ring">{step.icon}</div>
// // // // // // // //                                     <div className="lp-how-step-label">{step.label}</div>
// // // // // // // //                                     <div className="lp-how-title">{step.title}</div>
// // // // // // // //                                     <p className="lp-how-desc">{step.desc}</p>
// // // // // // // //                                 </div>
// // // // // // // //                                 {i < 2 && (
// // // // // // // //                                     <div className="lp-how-bridge">
// // // // // // // //                                         <svg width="36" height="24" viewBox="0 0 36 24" fill="none">
// // // // // // // //                                             <path d="M0 12 L28 12 M22 5 L30 12 L22 19" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
// // // // // // // //                                         </svg>
// // // // // // // //                                     </div>
// // // // // // // //                                 )}
// // // // // // // //                             </React.Fragment>
// // // // // // // //                         ))}
// // // // // // // //                     </div>
// // // // // // // //                 </div>
// // // // // // // //             </section>

// // // // // // // //             <div className="gold-rule" />

// // // // // // // //             {/* ── FEATURES ── */}
// // // // // // // //             {/* ── FEATURES — BENTO GRID ── */}
// // // // // // // //             <section id="features" className="lp-section">
// // // // // // // //                 <div className="lp-section-inner">
// // // // // // // //                     <span className="lp-eyebrow reveal">Features</span>
// // // // // // // //                     <h2 className="lp-section-title reveal">Everything a modern <em>professional</em> needs</h2>
// // // // // // // //                     <p className="lp-section-sub reveal">One card. Every detail. Always up-to-date.</p>

// // // // // // // //                     <div className="lp-feat-bento reveal">
// // // // // // // //                         {/* ROW 1 */}
// // // // // // // //                         <div className="lp-feat-bcard hero">
// // // // // // // //                             <div className="lp-feat-bicon">🪙</div>
// // // // // // // //                             <div className="lp-feat-btag">Premium Feature</div>
// // // // // // // //                             <div className="lp-feat-btitle">Google Wallet Pass</div>
// // // // // // // //                             <p className="lp-feat-bdesc">Add your card to Google Wallet and access it offline — at meetings, events, or anywhere without internet.</p>
// // // // // // // //                             <div className="lp-feat-wallet">
// // // // // // // //                                 <div className="lp-wallet-chip">G Pay</div>
// // // // // // // //                                 <div className="lp-wallet-chip">Wallet</div>
// // // // // // // //                                 <div className="lp-wallet-chip">Offline ✓</div>
// // // // // // // //                             </div>
// // // // // // // //                         </div>

// // // // // // // //                         <div className="lp-feat-bcard tall">
// // // // // // // //                             <div className="lp-feat-bicon">📲</div>
// // // // // // // //                             <div className="lp-feat-btag">Instant Share</div>
// // // // // // // //                             <div className="lp-feat-btitle">Unique QR Code</div>
// // // // // // // //                             <p className="lp-feat-bdesc">Every card gets a dedicated QR. One scan opens your full profile — no typing, no friction.</p>
// // // // // // // //                             <div className="lp-qr-art">
// // // // // // // //                                 {[1, 1, 1, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 1].map((v, i) => (
// // // // // // // //                                     <div key={i} className="lp-qr-cell" style={{ background: v ? "rgba(212,175,55,0.6)" : "rgba(255,255,255,0.03)", borderRadius: "2px", aspectRatio: "1" }} />
// // // // // // // //                                 ))}
// // // // // // // //                             </div>
// // // // // // // //                         </div>

// // // // // // // //                         <div className="lp-feat-bcard">
// // // // // // // //                             <div className="lp-feat-bicon">📸</div>
// // // // // // // //                             <div className="lp-feat-btag">Identity</div>
// // // // // // // //                             <div className="lp-feat-btitle">Profile Photo</div>
// // // // // // // //                             <p className="lp-feat-bdesc">Your face on your card — and inside Google Wallet too.</p>
// // // // // // // //                         </div>
// // // // // // // //                         <div className="lp-feat-bcard">
// // // // // // // //                             <div className="lp-feat-bicon">💾</div>
// // // // // // // //                             <div className="lp-feat-btag">One Tap</div>
// // // // // // // //                             <div className="lp-feat-btitle">Save Contact</div>
// // // // // // // //                             <p className="lp-feat-bdesc">Visitors save you directly to their phone contacts with a single tap.</p>
// // // // // // // //                         </div>

// // // // // // // //                         {/* ROW 2 */}
// // // // // // // //                         <div className="lp-feat-bcard hero">
// // // // // // // //                             <div className="lp-feat-bicon">🔗</div>
// // // // // // // //                             <div className="lp-feat-btag">Connections</div>
// // // // // // // //                             <div className="lp-feat-btitle">All Social Links</div>
// // // // // // // //                             <p className="lp-feat-bdesc">LinkedIn, GitHub, Instagram, Twitter, WhatsApp, YouTube — everything that matters in one place.</p>
// // // // // // // //                             <div className="lp-feat-socials">
// // // // // // // //                                 {["in LinkedIn", "⌥ GitHub", "✉ WhatsApp", "▶ YouTube", "📷 Insta"].map(s => (
// // // // // // // //                                     <span key={s} className="lp-social-pill">{s}</span>
// // // // // // // //                                 ))}
// // // // // // // //                             </div>
// // // // // // // //                         </div>

// // // // // // // //                         <div className="lp-feat-bcard">
// // // // // // // //                             <div className="lp-feat-bicon">📄</div>
// // // // // // // //                             <div className="lp-feat-btag">Career</div>
// // // // // // // //                             <div className="lp-feat-btitle">Resume Upload</div>
// // // // // // // //                             <p className="lp-feat-bdesc">Attach your PDF resume. Recruiters can download it instantly.</p>
// // // // // // // //                         </div>
// // // // // // // //                         <div className="lp-feat-bcard">
// // // // // // // //                             <div className="lp-feat-bicon">✏️</div>
// // // // // // // //                             <div className="lp-feat-btag">Always Fresh</div>
// // // // // // // //                             <div className="lp-feat-btitle">Edit Anytime</div>
// // // // // // // //                             <p className="lp-feat-bdesc">Changed jobs? New number? Update instantly — same link forever.</p>
// // // // // // // //                         </div>
// // // // // // // //                         <div className="lp-feat-bcard">
// // // // // // // //                             <div className="lp-feat-bicon">🌐</div>
// // // // // // // //                             <div className="lp-feat-btag">Universal</div>
// // // // // // // //                             <div className="lp-feat-btitle">Works Everywhere</div>
// // // // // // // //                             <p className="lp-feat-bdesc">Opens in any browser on any device. No app install ever required.</p>
// // // // // // // //                         </div>
// // // // // // // //                     </div>
// // // // // // // //                 </div>
// // // // // // // //             </section>

// // // // // // // //             <div className="gold-rule" />

// // // // // // // //             {/* ── USE CASES ── */}
// // // // // // // //             <UseCasesSection navigate={navigate} />

// // // // // // // //             <div className="gold-rule" />

// // // // // // // //             {/* ── PRICING ── */}
// // // // // // // //             <section id="pricing" className="lp-section">
// // // // // // // //                 <div className="lp-section-inner">
// // // // // // // //                     <span className="lp-eyebrow reveal">Pricing</span>
// // // // // // // //                     <h2 className="lp-section-title reveal">
// // // // // // // //                         Simple, <em>honest</em> pricing
// // // // // // // //                     </h2>
// // // // // // // //                     <p className="lp-section-sub reveal">
// // // // // // // //                         No hidden charges. No credit card needed.
// // // // // // // //                     </p>

// // // // // // // //                     <div className="lp-pricing-wrap reveal">
// // // // // // // //                         <div className="lp-pricing-card">
// // // // // // // //                             <div className="lp-pricing-badge">Most Popular</div>
// // // // // // // //                             <div className="lp-price-num">₹0</div>
// // // // // // // //                             <div className="lp-price-period">Forever Free</div>

// // // // // // // //                             <ul className="lp-price-list">
// // // // // // // //                                 {[
// // // // // // // //                                     "1 Digital Visiting Card",
// // // // // // // //                                     "Unique QR Code & Link",
// // // // // // // //                                     "Profile Photo Upload",
// // // // // // // //                                     "Resume Upload",
// // // // // // // //                                     "All Social Media Links",
// // // // // // // //                                     "Google Wallet Pass",
// // // // // // // //                                     "Save Contact (.vcf)",
// // // // // // // //                                     "Edit Anytime",
// // // // // // // //                                     "No App Required",
// // // // // // // //                                 ].map((item) => (
// // // // // // // //                                     <li className="lp-price-item" key={item}>
// // // // // // // //                                         <span className="lp-price-check">✦</span>
// // // // // // // //                                         {item}
// // // // // // // //                                     </li>
// // // // // // // //                                 ))}
// // // // // // // //                             </ul>

// // // // // // // //                             <button
// // // // // // // //                                 className="lp-btn-gold"
// // // // // // // //                                 style={{ width: "100%", justifyContent: "center" }}
// // // // // // // //                                 onClick={() => navigate("/animation/card")}
// // // // // // // //                             >
// // // // // // // //                                 Get Started Free →
// // // // // // // //                             </button>
// // // // // // // //                         </div>
// // // // // // // //                     </div>
// // // // // // // //                 </div>
// // // // // // // //             </section>

// // // // // // // //             <div className="gold-rule" />

// // // // // // // //             {/* ── CTA BANNER ── */}
// // // // // // // //             <section className="lp-cta-banner">
// // // // // // // //                 <h2 className="lp-cta-title reveal">
// // // // // // // //                     Stop carrying
// // // // // // // //                     <br />
// // // // // // // //                     <em>paper cards.</em>
// // // // // // // //                 </h2>
// // // // // // // //                 <p className="lp-cta-sub reveal">
// // // // // // // //                     Create your digital card in under 2 minutes. Free, forever.
// // // // // // // //                 </p>
// // // // // // // //                 <button
// // // // // // // //                     className="lp-btn-gold reveal"
// // // // // // // //                     style={{ margin: "0 auto" }}
// // // // // // // //                     onClick={() => navigate("/animation/card")}
// // // // // // // //                 >
// // // // // // // //                     Create My Card Now →
// // // // // // // //                 </button>
// // // // // // // //             </section>

// // // // // // // //             <div className="gold-rule" />

// // // // // // // //             {/* ── FOOTER ── */}
// // // // // // // //             <footer className="lp-footer">
// // // // // // // //                 <div className="lp-footer-inner">
// // // // // // // //                     <div className="lp-footer-logo">
// // // // // // // //                         <svg width="28" height="28" viewBox="0 0 34 34" fill="none">
// // // // // // // //                             <rect width="34" height="34" rx="9" fill="#1A1A1A" />
// // // // // // // //                             <rect
// // // // // // // //                                 width="34"
// // // // // // // //                                 height="34"
// // // // // // // //                                 rx="9"
// // // // // // // //                                 stroke="rgba(212,175,55,0.2)"
// // // // // // // //                                 strokeWidth="0.5"
// // // // // // // //                             />
// // // // // // // //                             <circle
// // // // // // // //                                 cx="15"
// // // // // // // //                                 cy="13"
// // // // // // // //                                 r="5"
// // // // // // // //                                 stroke="#D4AF37"
// // // // // // // //                                 strokeWidth="1.5"
// // // // // // // //                                 fill="none"
// // // // // // // //                             />
// // // // // // // //                             <path
// // // // // // // //                                 d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27"
// // // // // // // //                                 stroke="#D4AF37"
// // // // // // // //                                 strokeWidth="1.5"
// // // // // // // //                                 strokeLinecap="round"
// // // // // // // //                                 fill="none"
// // // // // // // //                             />
// // // // // // // //                             <path
// // // // // // // //                                 d="M23 11 L27 11 L27 23"
// // // // // // // //                                 stroke="rgba(212,175,55,0.5)"
// // // // // // // //                                 strokeWidth="1.3"
// // // // // // // //                                 strokeLinecap="round"
// // // // // // // //                                 strokeLinejoin="round"
// // // // // // // //                                 fill="none"
// // // // // // // //                             />
// // // // // // // //                             <circle cx="27" cy="23" r="1.8" fill="#D4AF37" />
// // // // // // // //                         </svg>
// // // // // // // //                         myprofile<em>corner</em>
// // // // // // // //                     </div>

// // // // // // // //                     <p className="lp-footer-tagline">
// // // // // // // //                         Your digital identity, always in your pocket.
// // // // // // // //                     </p>
// // // // // // // //                     <p className="lp-footer-copy">
// // // // // // // //                         © {new Date().getFullYear()} MyProfileCorner · Made with ♥ in India
// // // // // // // //                     </p>
// // // // // // // //                 </div>
// // // // // // // //             </footer>
// // // // // // // //         </div>
// // // // // // // //     );
// // // // // // // // }


// // // // // // // // // import React, { useState, useEffect, useRef } from "react";
// // // // // // // // // import { useNavigate } from "react-router-dom";

// // // // // // // // // /* ─────────────────────────────────────────
// // // // // // // // //    FONTS injected once
// // // // // // // // // ───────────────────────────────────────── */
// // // // // // // // // const FONT_LINK = `
// // // // // // // // //   @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600&family=Plus+Jakarta+Sans:wght@400;500;600&display=swap');
// // // // // // // // // `;

// // // // // // // // // /* ─────────────────────────────────────────
// // // // // // // // //    GLOBAL CSS
// // // // // // // // // ───────────────────────────────────────── */
// // // // // // // // // const GLOBAL_CSS = `
// // // // // // // // // ${FONT_LINK}

// // // // // // // // // *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

// // // // // // // // // :root {
// // // // // // // // //   --black: #080808;
// // // // // // // // //   --dark: #0E0E0E;
// // // // // // // // //   --card: #141414;
// // // // // // // // //   --border: rgba(255,255,255,0.07);
// // // // // // // // //   --gold: #D4AF37;
// // // // // // // // //   --gold2: #F0D060;
// // // // // // // // //   --gold3: #C9A227;
// // // // // // // // //   --gold-dim: rgba(212,175,55,0.18);
// // // // // // // // //   --white: #FFFFFF;
// // // // // // // // //   --muted: rgba(255,255,255,0.45);
// // // // // // // // //   --muted2: rgba(255,255,255,0.25);
// // // // // // // // //   --serif: 'Playfair Display', Georgia, serif;
// // // // // // // // //   --sans: 'Plus Jakarta Sans', sans-serif;
// // // // // // // // // }

// // // // // // // // // html { scroll-behavior: smooth; }

// // // // // // // // // body {
// // // // // // // // //   background: var(--black);
// // // // // // // // //   color: var(--white);
// // // // // // // // //   font-family: var(--sans);
// // // // // // // // //   overflow-x: hidden;
// // // // // // // // // }

// // // // // // // // // /* ── scrollbar ── */
// // // // // // // // // ::-webkit-scrollbar { width: 4px; }
// // // // // // // // // ::-webkit-scrollbar-track { background: var(--black); }
// // // // // // // // // ::-webkit-scrollbar-thumb { background: rgba(212,175,55,0.3); border-radius: 2px; }

// // // // // // // // // /* ── NAV ── */
// // // // // // // // // .lp-nav {
// // // // // // // // //   position: fixed; top: 0; left: 0; right: 0; z-index: 200;
// // // // // // // // //   padding: 1.5rem 3rem;
// // // // // // // // //   transition: all 0.4s;
// // // // // // // // // }
// // // // // // // // // .lp-nav.scrolled {
// // // // // // // // //   background: rgba(8,8,8,0.9);
// // // // // // // // //   backdrop-filter: blur(20px);
// // // // // // // // //   border-bottom: 0.5px solid rgba(212,175,55,0.12);
// // // // // // // // //   padding: 1rem 3rem;
// // // // // // // // // }
// // // // // // // // // .lp-nav-inner {
// // // // // // // // //   max-width: 1180px; margin: 0 auto;
// // // // // // // // //   display: flex; align-items: center; justify-content: space-between;
// // // // // // // // // }
// // // // // // // // // .lp-logo {
// // // // // // // // //   display: flex; align-items: center; gap: 10px;
// // // // // // // // //   font-family: var(--sans); font-size: 15px; font-weight: 500;
// // // // // // // // //   color: var(--white); text-decoration: none; letter-spacing: 0.2px;
// // // // // // // // // }
// // // // // // // // // .lp-logo-icon { width: 34px; height: 34px; flex-shrink: 0; }
// // // // // // // // // .lp-logo em { color: var(--gold); font-style: normal; font-weight: 600; }
// // // // // // // // // .lp-nav-links {
// // // // // // // // //   display: flex; align-items: center; gap: 2.5rem;
// // // // // // // // // }
// // // // // // // // // .lp-nav-link {
// // // // // // // // //   font-size: 13px; font-weight: 400; color: var(--muted);
// // // // // // // // //   text-decoration: none; letter-spacing: 0.3px;
// // // // // // // // //   transition: color 0.2s;
// // // // // // // // // }
// // // // // // // // // .lp-nav-link:hover { color: var(--white); }
// // // // // // // // // .lp-nav-cta {
// // // // // // // // //   padding: 10px 22px;
// // // // // // // // //   background: linear-gradient(135deg, var(--gold), var(--gold2), var(--gold3));
// // // // // // // // //   color: #0A0A0A; border: none; border-radius: 10px;
// // // // // // // // //   font-family: var(--sans); font-size: 13px; font-weight: 500;
// // // // // // // // //   letter-spacing: 0.8px; text-transform: uppercase;
// // // // // // // // //   cursor: pointer; position: relative; overflow: hidden;
// // // // // // // // //   transition: transform 0.15s, opacity 0.15s;
// // // // // // // // // }
// // // // // // // // // .lp-nav-cta::before {
// // // // // // // // //   content: ''; position: absolute; inset: 0;
// // // // // // // // //   background: linear-gradient(135deg, rgba(255,255,255,0.2), transparent 60%);
// // // // // // // // //   pointer-events: none;
// // // // // // // // // }
// // // // // // // // // .lp-nav-cta:hover { transform: translateY(-1px); opacity: 0.92; }

// // // // // // // // // /* ── HERO ── */
// // // // // // // // // .lp-hero {
// // // // // // // // //   min-height: 100vh;
// // // // // // // // //   display: grid;
// // // // // // // // //   grid-template-columns: 1fr 1fr;
// // // // // // // // //   align-items: center;
// // // // // // // // //   gap: 4rem;
// // // // // // // // //   max-width: 1180px; margin: 0 auto;
// // // // // // // // //   padding: 10rem 3rem 6rem;
// // // // // // // // //   position: relative;
// // // // // // // // // }

// // // // // // // // // .lp-hero-left { position: relative; z-index: 2; }

// // // // // // // // // .lp-hero-eyebrow {
// // // // // // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // // // // // //   padding: 6px 14px;
// // // // // // // // //   background: rgba(212,175,55,0.1);
// // // // // // // // //   border: 0.5px solid rgba(212,175,55,0.3);
// // // // // // // // //   border-radius: 100px;
// // // // // // // // //   font-size: 11px; font-weight: 500; color: var(--gold);
// // // // // // // // //   letter-spacing: 1.5px; text-transform: uppercase;
// // // // // // // // //   margin-bottom: 2rem;
// // // // // // // // // }
// // // // // // // // // .lp-hero-eyebrow-dot {
// // // // // // // // //   width: 5px; height: 5px; border-radius: 50%;
// // // // // // // // //   background: var(--gold);
// // // // // // // // //   animation: pulse-gold 2s ease-in-out infinite;
// // // // // // // // // }
// // // // // // // // // @keyframes pulse-gold {
// // // // // // // // //   0%, 100% { opacity: 1; transform: scale(1); }
// // // // // // // // //   50% { opacity: 0.4; transform: scale(0.7); }
// // // // // // // // // }

// // // // // // // // // .lp-hero-title {
// // // // // // // // //   font-family: var(--serif);
// // // // // // // // //   font-size: clamp(2.8rem, 5.5vw, 5rem);
// // // // // // // // //   font-weight: 500; line-height: 1.1;
// // // // // // // // //   letter-spacing: -0.5px;
// // // // // // // // //   color: var(--white);
// // // // // // // // //   margin-bottom: 1.75rem;
// // // // // // // // // }
// // // // // // // // // .lp-hero-title em {
// // // // // // // // //   font-style: italic; font-weight: 500;
// // // // // // // // //   color: var(--gold);
// // // // // // // // // }

// // // // // // // // // .lp-hero-sub {
// // // // // // // // //   font-size: 16px; font-weight: 400;
// // // // // // // // //   color: var(--muted); line-height: 1.8;
// // // // // // // // //   max-width: 440px; margin-bottom: 3rem;
// // // // // // // // // }

// // // // // // // // // .lp-hero-ctas { display: flex; gap: 1rem; flex-wrap: wrap; }

// // // // // // // // // .lp-btn-gold {
// // // // // // // // //   padding: 16px 32px;
// // // // // // // // //   background: linear-gradient(135deg, var(--gold), var(--gold2), var(--gold3));
// // // // // // // // //   color: #0A0A0A; border: none; border-radius: 12px;
// // // // // // // // //   font-family: var(--sans); font-size: 13px; font-weight: 500;
// // // // // // // // //   letter-spacing: 1px; text-transform: uppercase;
// // // // // // // // //   cursor: pointer; position: relative; overflow: hidden;
// // // // // // // // //   transition: transform 0.2s, opacity 0.2s;
// // // // // // // // //   display: flex; align-items: center; gap: 8px;
// // // // // // // // // }
// // // // // // // // // .lp-btn-gold::before {
// // // // // // // // //   content: ''; position: absolute; inset: 0;
// // // // // // // // //   background: linear-gradient(135deg, rgba(255,255,255,0.25), transparent 60%);
// // // // // // // // //   pointer-events: none;
// // // // // // // // // }
// // // // // // // // // .lp-btn-gold:hover { transform: translateY(-2px); opacity: 0.92; }

// // // // // // // // // .lp-btn-ghost {
// // // // // // // // //   padding: 16px 28px;
// // // // // // // // //   background: transparent;
// // // // // // // // //   color: var(--muted); border: 0.5px solid rgba(255,255,255,0.15);
// // // // // // // // //   border-radius: 12px;
// // // // // // // // //   font-family: var(--sans); font-size: 13px; font-weight: 400;
// // // // // // // // //   letter-spacing: 0.5px; cursor: pointer; text-decoration: none;
// // // // // // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // // // // // //   transition: border-color 0.2s, color 0.2s;
// // // // // // // // // }
// // // // // // // // // .lp-btn-ghost:hover { border-color: rgba(212,175,55,0.3); color: var(--white); }

// // // // // // // // // /* ── HERO STATS STRIP ── */
// // // // // // // // // .lp-hero-stats {
// // // // // // // // //   display: flex; gap: 2.5rem; margin-top: 3.5rem;
// // // // // // // // //   padding-top: 2.5rem;
// // // // // // // // //   border-top: 0.5px solid var(--border);
// // // // // // // // // }
// // // // // // // // // .lp-stat-num {
// // // // // // // // //   font-family: var(--serif); font-size: 2rem; font-weight: 500;
// // // // // // // // //   color: var(--gold); line-height: 1;
// // // // // // // // // }
// // // // // // // // // .lp-stat-label {
// // // // // // // // //   font-size: 11px; font-weight: 400; color: var(--muted2);
// // // // // // // // //   letter-spacing: 0.5px; margin-top: 4px;
// // // // // // // // // }

// // // // // // // // // /* ── HERO RIGHT — QR SCAN ANIMATION ── */
// // // // // // // // // .lp-hero-right {
// // // // // // // // //   position: relative; display: flex;
// // // // // // // // //   align-items: center; justify-content: center;
// // // // // // // // //   z-index: 2;
// // // // // // // // // }

// // // // // // // // // /* Scene wrapper */
// // // // // // // // // .scan-scene {
// // // // // // // // //   position: relative;
// // // // // // // // //   width: 380px; height: 480px;
// // // // // // // // // }

// // // // // // // // // /* Phone A (scanner) */
// // // // // // // // // .phone-scanner {
// // // // // // // // //   position: absolute;
// // // // // // // // //   left: 0; top: 60px;
// // // // // // // // //   width: 155px; height: 270px;
// // // // // // // // //   background: #1A1A1A;
// // // // // // // // //   border-radius: 22px;
// // // // // // // // //   border: 1px solid rgba(255,255,255,0.1);
// // // // // // // // //   overflow: hidden;
// // // // // // // // //   box-shadow: 0 24px 60px rgba(0,0,0,0.6);
// // // // // // // // //   z-index: 3;
// // // // // // // // //   animation: phone-a-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;
// // // // // // // // //   animation-delay: 0.3s;
// // // // // // // // // }
// // // // // // // // // @keyframes phone-a-intro {
// // // // // // // // //   from { opacity: 0; transform: translateX(-30px) rotate(-6deg); }
// // // // // // // // //   to   { opacity: 1; transform: translateX(0) rotate(-6deg); }
// // // // // // // // // }

// // // // // // // // // .phone-scanner-screen {
// // // // // // // // //   width: 100%; height: 100%;
// // // // // // // // //   background: #111;
// // // // // // // // //   display: flex; flex-direction: column;
// // // // // // // // //   align-items: center; justify-content: center; gap: 12px;
// // // // // // // // //   padding: 12px;
// // // // // // // // // }
// // // // // // // // // .scanner-viewfinder {
// // // // // // // // //   width: 100px; height: 100px;
// // // // // // // // //   border-radius: 10px;
// // // // // // // // //   position: relative;
// // // // // // // // //   background: rgba(212,175,55,0.04);
// // // // // // // // // }
// // // // // // // // // /* corner brackets of viewfinder */
// // // // // // // // // .scanner-viewfinder::before,
// // // // // // // // // .scanner-viewfinder::after {
// // // // // // // // //   content: '';
// // // // // // // // //   position: absolute;
// // // // // // // // //   width: 20px; height: 20px;
// // // // // // // // //   border-color: var(--gold); border-style: solid;
// // // // // // // // // }
// // // // // // // // // .scanner-viewfinder::before {
// // // // // // // // //   top: 0; left: 0;
// // // // // // // // //   border-width: 2px 0 0 2px;
// // // // // // // // //   border-radius: 6px 0 0 0;
// // // // // // // // // }
// // // // // // // // // .scanner-viewfinder::after {
// // // // // // // // //   bottom: 0; right: 0;
// // // // // // // // //   border-width: 0 2px 2px 0;
// // // // // // // // //   border-radius: 0 0 6px 0;
// // // // // // // // // }
// // // // // // // // // .vf-corner-tr {
// // // // // // // // //   position: absolute; top: 0; right: 0;
// // // // // // // // //   width: 20px; height: 20px;
// // // // // // // // //   border-top: 2px solid var(--gold);
// // // // // // // // //   border-right: 2px solid var(--gold);
// // // // // // // // //   border-radius: 0 6px 0 0;
// // // // // // // // // }
// // // // // // // // // .vf-corner-bl {
// // // // // // // // //   position: absolute; bottom: 0; left: 0;
// // // // // // // // //   width: 20px; height: 20px;
// // // // // // // // //   border-bottom: 2px solid var(--gold);
// // // // // // // // //   border-left: 2px solid var(--gold);
// // // // // // // // //   border-radius: 0 0 0 6px;
// // // // // // // // // }

// // // // // // // // // /* scanning laser line */
// // // // // // // // // .scan-laser {
// // // // // // // // //   position: absolute;
// // // // // // // // //   left: 4px; right: 4px; height: 2px;
// // // // // // // // //   background: linear-gradient(90deg, transparent, var(--gold), transparent);
// // // // // // // // //   border-radius: 1px;
// // // // // // // // //   top: 4px;
// // // // // // // // //   animation: laser-sweep 2s ease-in-out infinite;
// // // // // // // // //   animation-delay: 1.2s;
// // // // // // // // // }
// // // // // // // // // @keyframes laser-sweep {
// // // // // // // // //   0%   { top: 8px; opacity: 0; }
// // // // // // // // //   10%  { opacity: 1; }
// // // // // // // // //   90%  { opacity: 1; }
// // // // // // // // //   100% { top: 88px; opacity: 0; }
// // // // // // // // // }

// // // // // // // // // .scanner-label {
// // // // // // // // //   font-size: 9px; font-weight: 500; color: rgba(255,255,255,0.4);
// // // // // // // // //   letter-spacing: 1px; text-align: center;
// // // // // // // // //   text-transform: uppercase;
// // // // // // // // // }

// // // // // // // // // /* Mini QR inside viewfinder */
// // // // // // // // // .mini-qr {
// // // // // // // // //   position: absolute; inset: 10px;
// // // // // // // // //   display: grid; grid-template-columns: repeat(6,1fr); grid-template-rows: repeat(6,1fr);
// // // // // // // // //   gap: 1.5px; opacity: 0.6;
// // // // // // // // // }
// // // // // // // // // .mini-qr-cell { border-radius: 1px; }

// // // // // // // // // /* Phone B (profile card) */
// // // // // // // // // .phone-profile {
// // // // // // // // //   position: absolute;
// // // // // // // // //   right: 0; top: 0;
// // // // // // // // //   width: 175px; height: 310px;
// // // // // // // // //   background: #0E0E0E;
// // // // // // // // //   border-radius: 24px;
// // // // // // // // //   border: 1px solid rgba(212,175,55,0.2);
// // // // // // // // //   overflow: hidden;
// // // // // // // // //   box-shadow: 0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(212,175,55,0.08);
// // // // // // // // //   z-index: 4;
// // // // // // // // //   animation: phone-b-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;
// // // // // // // // //   animation-delay: 0.5s;
// // // // // // // // // }
// // // // // // // // // @keyframes phone-b-intro {
// // // // // // // // //   from { opacity: 0; transform: translateY(30px) rotate(4deg); }
// // // // // // // // //   to   { opacity: 1; transform: translateY(0) rotate(4deg); }
// // // // // // // // // }

// // // // // // // // // /* ── card content opening animation ── */
// // // // // // // // // .profile-card-wrap {
// // // // // // // // //   width: 100%; height: 100%;
// // // // // // // // //   animation: card-reveal 0.7s cubic-bezier(0.22,1,0.36,1) both;
// // // // // // // // //   animation-delay: 2s;
// // // // // // // // // }
// // // // // // // // // @keyframes card-reveal {
// // // // // // // // //   from { transform: translateY(40px); opacity: 0; }
// // // // // // // // //   to   { transform: translateY(0); opacity: 1; }
// // // // // // // // // }

// // // // // // // // // .profile-banner {
// // // // // // // // //   width: 100%; height: 110px;
// // // // // // // // //   background: linear-gradient(160deg, #1a1a1a, #2a2020);
// // // // // // // // //   position: relative; overflow: hidden;
// // // // // // // // // }
// // // // // // // // // .profile-banner-img {
// // // // // // // // //   width: 100%; height: 100%;
// // // // // // // // //   background: linear-gradient(160deg, #1e1a10 0%, #2a2010 50%, #181818 100%);
// // // // // // // // // }
// // // // // // // // // .profile-banner-fade {
// // // // // // // // //   position: absolute; bottom: 0; left: 0; right: 0; height: 50px;
// // // // // // // // //   background: linear-gradient(transparent, #0E0E0E);
// // // // // // // // // }

// // // // // // // // // /* floating avatar */
// // // // // // // // // .profile-avatar-ring {
// // // // // // // // //   position: absolute;
// // // // // // // // //   bottom: -20px; left: 14px;
// // // // // // // // //   width: 44px; height: 44px;
// // // // // // // // //   border-radius: 50%;
// // // // // // // // //   border: 2px solid #0E0E0E;
// // // // // // // // //   background: #222;
// // // // // // // // //   overflow: hidden;
// // // // // // // // //   box-shadow: 0 0 0 1.5px rgba(212,175,55,0.5);
// // // // // // // // //   animation: avatar-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both;
// // // // // // // // //   animation-delay: 2.4s;
// // // // // // // // // }
// // // // // // // // // @keyframes avatar-pop {
// // // // // // // // //   from { transform: scale(0.3); opacity: 0; }
// // // // // // // // //   to   { transform: scale(1); opacity: 1; }
// // // // // // // // // }
// // // // // // // // // .profile-avatar-inner {
// // // // // // // // //   width: 100%; height: 100%;
// // // // // // // // //   background: linear-gradient(145deg, #2a2010, #3a3020);
// // // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // // //   font-family: var(--serif); font-size: 14px; font-weight: 300;
// // // // // // // // //   color: rgba(212,175,55,0.8); letter-spacing: 1px;
// // // // // // // // // }

// // // // // // // // // .profile-info {
// // // // // // // // //   padding: 28px 14px 12px;
// // // // // // // // // }
// // // // // // // // // .profile-info-name {
// // // // // // // // //   font-family: var(--serif); font-size: 15px; font-weight: 500;
// // // // // // // // //   color: #FFFFFF; line-height: 1.2; margin-bottom: 2px;
// // // // // // // // // }
// // // // // // // // // .profile-info-name em { font-style: italic; color: var(--gold); }
// // // // // // // // // .profile-info-role {
// // // // // // // // //   font-size: 8px; font-weight: 500; color: rgba(255,255,255,0.45);
// // // // // // // // //   letter-spacing: 0.3px; margin-bottom: 10px;
// // // // // // // // // }

// // // // // // // // // .profile-tiles {
// // // // // // // // //   display: grid; grid-template-columns: 1fr 1fr 1fr;
// // // // // // // // //   gap: 5px; margin-bottom: 8px;
// // // // // // // // // }
// // // // // // // // // .profile-tile {
// // // // // // // // //   background: #1A1A1A;
// // // // // // // // //   border: 0.5px solid rgba(255,255,255,0.06);
// // // // // // // // //   border-radius: 8px; padding: 7px 6px;
// // // // // // // // //   display: flex; flex-direction: column; gap: 4px;
// // // // // // // // //   animation: tile-slide 0.3s ease both;
// // // // // // // // // }
// // // // // // // // // .profile-tile:nth-child(1) { animation-delay: 2.5s; }
// // // // // // // // // .profile-tile:nth-child(2) { animation-delay: 2.65s; }
// // // // // // // // // .profile-tile:nth-child(3) { animation-delay: 2.8s; }
// // // // // // // // // @keyframes tile-slide {
// // // // // // // // //   from { opacity: 0; transform: translateY(6px); }
// // // // // // // // //   to   { opacity: 1; transform: translateY(0); }
// // // // // // // // // }
// // // // // // // // // .tile-dot {
// // // // // // // // //   width: 12px; height: 12px; border-radius: 50%;
// // // // // // // // //   background: rgba(212,175,55,0.15);
// // // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // // // }
// // // // // // // // // .tile-dot-inner { width: 5px; height: 5px; border-radius: 50%; background: var(--gold); }
// // // // // // // // // .tile-text { font-size: 7px; color: rgba(255,255,255,0.4); line-height: 1.3; }

// // // // // // // // // .profile-save-btn {
// // // // // // // // //   width: 100%; padding: 8px;
// // // // // // // // //   background: linear-gradient(135deg, var(--gold), var(--gold2));
// // // // // // // // //   color: #0A0A0A; border: none; border-radius: 8px;
// // // // // // // // //   font-size: 8px; font-weight: 500; letter-spacing: 0.8px;
// // // // // // // // //   text-transform: uppercase; cursor: default;
// // // // // // // // //   display: flex; align-items: center; justify-content: center; gap: 4px;
// // // // // // // // //   animation: tile-slide 0.3s ease both;
// // // // // // // // //   animation-delay: 2.95s;
// // // // // // // // // }

// // // // // // // // // /* beam connecting two phones */
// // // // // // // // // .beam-line {
// // // // // // // // //   position: absolute;
// // // // // // // // //   top: 165px; left: 148px;
// // // // // // // // //   width: 90px; height: 1px;
// // // // // // // // //   background: linear-gradient(90deg, var(--gold), rgba(212,175,55,0));
// // // // // // // // //   transform-origin: left center;
// // // // // // // // //   animation: beam-appear 0.4s ease both;
// // // // // // // // //   animation-delay: 1.5s;
// // // // // // // // //   z-index: 5;
// // // // // // // // // }
// // // // // // // // // @keyframes beam-appear {
// // // // // // // // //   from { scaleX: 0; opacity: 0; }
// // // // // // // // //   to   { scaleX: 1; opacity: 1; }
// // // // // // // // // }
// // // // // // // // // .beam-particle {
// // // // // // // // //   position: absolute;
// // // // // // // // //   top: 160px; left: 148px;
// // // // // // // // //   width: 6px; height: 6px; border-radius: 50%;
// // // // // // // // //   background: var(--gold);
// // // // // // // // //   animation: particle-travel 2s ease-in-out infinite;
// // // // // // // // //   animation-delay: 1.6s;
// // // // // // // // //   z-index: 6;
// // // // // // // // // }
// // // // // // // // // @keyframes particle-travel {
// // // // // // // // //   0%   { left: 148px; opacity: 0; transform: scale(0.5); }
// // // // // // // // //   10%  { opacity: 1; transform: scale(1); }
// // // // // // // // //   90%  { opacity: 1; }
// // // // // // // // //   100% { left: 228px; opacity: 0; transform: scale(0.5); }
// // // // // // // // // }

// // // // // // // // // /* ambient glow orbs */
// // // // // // // // // .hero-orb-1 {
// // // // // // // // //   position: absolute;
// // // // // // // // //   width: 500px; height: 500px; border-radius: 50%;
// // // // // // // // //   background: radial-gradient(circle, rgba(212,175,55,0.06) 0%, transparent 65%);
// // // // // // // // //   top: -100px; right: -100px; pointer-events: none; z-index: 0;
// // // // // // // // // }
// // // // // // // // // .hero-orb-2 {
// // // // // // // // //   position: absolute;
// // // // // // // // //   width: 300px; height: 300px; border-radius: 50%;
// // // // // // // // //   background: radial-gradient(circle, rgba(212,175,55,0.04) 0%, transparent 65%);
// // // // // // // // //   bottom: 0; left: -80px; pointer-events: none; z-index: 0;
// // // // // // // // // }

// // // // // // // // // /* ── SECTION SHARED ── */
// // // // // // // // // .lp-section {
// // // // // // // // //   padding: 7rem 3rem;
// // // // // // // // //   position: relative;
// // // // // // // // // }
// // // // // // // // // .lp-section-inner { max-width: 1180px; margin: 0 auto; }
// // // // // // // // // .lp-eyebrow {
// // // // // // // // //   display: block; font-size: 10px; font-weight: 500;
// // // // // // // // //   letter-spacing: 3px; text-transform: uppercase;
// // // // // // // // //   color: var(--gold); margin-bottom: 1.25rem; text-align: center;
// // // // // // // // // }
// // // // // // // // // .lp-section-title {
// // // // // // // // //   font-family: var(--serif);
// // // // // // // // //   font-size: clamp(2rem, 4vw, 3.2rem);
// // // // // // // // //   font-weight: 500; line-height: 1.15; letter-spacing: -0.3px;
// // // // // // // // //   color: var(--white); text-align: center; margin-bottom: 1.25rem;
// // // // // // // // // }
// // // // // // // // // .lp-section-title em { font-style: italic; color: var(--gold); }
// // // // // // // // // .lp-section-sub {
// // // // // // // // //   font-size: 15px; font-weight: 400; color: var(--muted);
// // // // // // // // //   text-align: center; max-width: 480px; margin: 0 auto 4rem; line-height: 1.8;
// // // // // // // // // }

// // // // // // // // // /* ── GOLD DIVIDER ── */
// // // // // // // // // .gold-rule {
// // // // // // // // //   height: 0.5px;
// // // // // // // // //   background: linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent);
// // // // // // // // //   margin: 0;
// // // // // // // // // }

// // // // // // // // // /* ── TRUST BAR ── */
// // // // // // // // // .lp-trust {
// // // // // // // // //   padding: 2rem 3rem;
// // // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // // //   gap: 2.5rem; flex-wrap: wrap;
// // // // // // // // // }
// // // // // // // // // .lp-trust-label {
// // // // // // // // //   font-size: 10px; font-weight: 400; color: var(--muted2);
// // // // // // // // //   letter-spacing: 2px; text-transform: uppercase;
// // // // // // // // // }
// // // // // // // // // .lp-trust-items {
// // // // // // // // //   display: flex; align-items: center; gap: 2rem; flex-wrap: wrap;
// // // // // // // // // }
// // // // // // // // // .lp-trust-item {
// // // // // // // // //   display: flex; align-items: center; gap: 7px;
// // // // // // // // //   font-size: 13px; font-weight: 400; color: var(--muted);
// // // // // // // // // }
// // // // // // // // // .lp-trust-sep { color: rgba(212,175,55,0.2); font-size: 18px; }

// // // // // // // // // /* ── STEPS ── */
// // // // // // // // // .lp-steps {
// // // // // // // // //   display: grid; grid-template-columns: repeat(3, 1fr);
// // // // // // // // //   gap: 1.5px;
// // // // // // // // //   border: 0.5px solid var(--border);
// // // // // // // // //   border-radius: 20px; overflow: hidden;
// // // // // // // // //   margin-top: 1rem;
// // // // // // // // // }
// // // // // // // // // .lp-step {
// // // // // // // // //   background: var(--card);
// // // // // // // // //   padding: 2.5rem 2rem;
// // // // // // // // //   position: relative; overflow: hidden;
// // // // // // // // //   transition: background 0.3s;
// // // // // // // // //   cursor: default;
// // // // // // // // // }
// // // // // // // // // .lp-step:hover { background: #1a1a1a; }
// // // // // // // // // .lp-step::before {
// // // // // // // // //   content: '';
// // // // // // // // //   position: absolute; top: 0; left: 0; right: 0; height: 1px;
// // // // // // // // //   background: linear-gradient(90deg, transparent, rgba(212,175,55,0), transparent);
// // // // // // // // //   transition: background 0.3s;
// // // // // // // // // }
// // // // // // // // // .lp-step:hover::before {
// // // // // // // // //   background: linear-gradient(90deg, transparent, rgba(212,175,55,0.4), transparent);
// // // // // // // // // }
// // // // // // // // // .lp-step-num {
// // // // // // // // //   font-family: var(--serif); font-size: 3.5rem; font-weight: 300;
// // // // // // // // //   color: rgba(212,175,55,0.12); line-height: 1;
// // // // // // // // //   position: absolute; top: 1.5rem; right: 1.5rem;
// // // // // // // // //   letter-spacing: -1px;
// // // // // // // // // }
// // // // // // // // // .lp-step-icon-wrap {
// // // // // // // // //   width: 48px; height: 48px; border-radius: 14px;
// // // // // // // // //   background: rgba(212,175,55,0.08);
// // // // // // // // //   border: 0.5px solid rgba(212,175,55,0.2);
// // // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // // //   font-size: 1.4rem; margin-bottom: 1.5rem;
// // // // // // // // // }
// // // // // // // // // .lp-step-title {
// // // // // // // // //   font-family: var(--serif); font-size: 1.4rem; font-weight: 500;
// // // // // // // // //   color: var(--white); margin-bottom: 0.75rem; line-height: 1.2;
// // // // // // // // // }
// // // // // // // // // .lp-step-desc {
// // // // // // // // //   font-size: 14px; font-weight: 400; color: var(--muted);
// // // // // // // // //   line-height: 1.75;
// // // // // // // // // }

// // // // // // // // // /* ── FEATURES ── */
// // // // // // // // // .lp-feat-grid {
// // // // // // // // //   display: grid; grid-template-columns: repeat(4, 1fr);
// // // // // // // // //   gap: 1px;
// // // // // // // // //   border: 0.5px solid var(--border);
// // // // // // // // //   border-radius: 20px; overflow: hidden;
// // // // // // // // // }
// // // // // // // // // .lp-feat-card {
// // // // // // // // //   background: var(--card); padding: 2rem 1.5rem;
// // // // // // // // //   transition: background 0.3s;
// // // // // // // // //   position: relative;
// // // // // // // // // }
// // // // // // // // // .lp-feat-card:hover { background: #181818; }
// // // // // // // // // .lp-feat-icon {
// // // // // // // // //   font-size: 1.6rem; margin-bottom: 1.25rem;
// // // // // // // // //   display: block;
// // // // // // // // // }
// // // // // // // // // .lp-feat-title {
// // // // // // // // //   font-family: var(--serif); font-size: 1.1rem; font-weight: 500;
// // // // // // // // //   color: var(--white); margin-bottom: 0.5rem;
// // // // // // // // // }
// // // // // // // // // .lp-feat-desc {
// // // // // // // // //   font-size: 13px; font-weight: 400; color: var(--muted); line-height: 1.7;
// // // // // // // // // }

// // // // // // // // // /* ── USE CASES ── */
// // // // // // // // // .lp-use-tabs {
// // // // // // // // //   display: flex; flex-wrap: wrap; gap: 0.5rem;
// // // // // // // // //   justify-content: center; margin-bottom: 3rem;
// // // // // // // // // }
// // // // // // // // // .lp-use-tab {
// // // // // // // // //   padding: 8px 18px; border-radius: 100px;
// // // // // // // // //   font-size: 13px; font-weight: 400; cursor: pointer;
// // // // // // // // //   transition: all 0.2s; font-family: var(--sans);
// // // // // // // // //   display: flex; align-items: center; gap: 6px;
// // // // // // // // // }
// // // // // // // // // .lp-use-panel {
// // // // // // // // //   display: grid; grid-template-columns: 1fr 1fr;
// // // // // // // // //   gap: 3rem; align-items: center;
// // // // // // // // //   background: var(--card);
// // // // // // // // //   border: 0.5px solid var(--border);
// // // // // // // // //   border-radius: 24px; padding: 3rem;
// // // // // // // // //   position: relative; overflow: hidden;
// // // // // // // // // }
// // // // // // // // // .lp-use-panel::before {
// // // // // // // // //   content: '';
// // // // // // // // //   position: absolute; top: 0; left: 0; right: 0; height: 1px;
// // // // // // // // // }
// // // // // // // // // .lp-use-badge {
// // // // // // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // // // // // //   padding: 6px 14px; border-radius: 100px;
// // // // // // // // //   margin-bottom: 1.5rem; font-size: 12px; font-weight: 500;
// // // // // // // // // }
// // // // // // // // // .lp-use-tagline {
// // // // // // // // //   font-family: var(--serif); font-size: 2rem; font-weight: 500;
// // // // // // // // //   color: var(--white); line-height: 1.2; margin-bottom: 1rem;
// // // // // // // // // }
// // // // // // // // // .lp-use-desc {
// // // // // // // // //   font-size: 14px; font-weight: 400; color: var(--muted);
// // // // // // // // //   line-height: 1.85; margin-bottom: 2rem;
// // // // // // // // // }
// // // // // // // // // .lp-use-highlights { display: flex; flex-direction: column; gap: 0.75rem; }
// // // // // // // // // .lp-use-hl {
// // // // // // // // //   display: flex; align-items: center; gap: 12px;
// // // // // // // // //   background: #1a1a1a; border: 0.5px solid var(--border);
// // // // // // // // //   border-radius: 12px; padding: 1rem 1.25rem;
// // // // // // // // // }
// // // // // // // // // .lp-use-hl-check {
// // // // // // // // //   width: 28px; height: 28px; border-radius: 50%;
// // // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // // //   flex-shrink: 0; font-size: 11px; font-weight: 600;
// // // // // // // // // }
// // // // // // // // // .lp-use-hl-text {
// // // // // // // // //   font-size: 13px; font-weight: 400; color: rgba(255,255,255,0.75);
// // // // // // // // // }

// // // // // // // // // /* ── PRICING ── */
// // // // // // // // // .lp-pricing-wrap { display: flex; justify-content: center; margin-top: 1rem; }
// // // // // // // // // .lp-pricing-card {
// // // // // // // // //   background: var(--card);
// // // // // // // // //   border: 0.5px solid rgba(212,175,55,0.25);
// // // // // // // // //   border-radius: 28px; padding: 3rem 2.5rem;
// // // // // // // // //   width: 100%; max-width: 420px;
// // // // // // // // //   text-align: center; position: relative;
// // // // // // // // // }
// // // // // // // // // .lp-pricing-card::before {
// // // // // // // // //   content: '';
// // // // // // // // //   position: absolute; top: 0; left: 10%; right: 10%; height: 1px;
// // // // // // // // //   background: linear-gradient(90deg, transparent, var(--gold), transparent);
// // // // // // // // // }
// // // // // // // // // .lp-pricing-badge {
// // // // // // // // //   display: inline-block;
// // // // // // // // //   background: linear-gradient(135deg, var(--gold), var(--gold2));
// // // // // // // // //   color: #0A0A0A; border-radius: 100px;
// // // // // // // // //   padding: 5px 16px; font-size: 11px; font-weight: 500;
// // // // // // // // //   letter-spacing: 1px; text-transform: uppercase;
// // // // // // // // //   margin-bottom: 2rem;
// // // // // // // // // }
// // // // // // // // // .lp-price-num {
// // // // // // // // //   font-family: var(--serif); font-size: 5rem; font-weight: 500;
// // // // // // // // //   color: var(--white); line-height: 1; letter-spacing: -2px;
// // // // // // // // // }
// // // // // // // // // .lp-price-period {
// // // // // // // // //   font-size: 12px; font-weight: 400; color: var(--muted2);
// // // // // // // // //   letter-spacing: 2px; text-transform: uppercase; margin: 0.5rem 0 2.5rem;
// // // // // // // // // }
// // // // // // // // // .lp-price-list { list-style: none; text-align: left; margin-bottom: 2.5rem; }
// // // // // // // // // .lp-price-item {
// // // // // // // // //   display: flex; align-items: center; gap: 10px;
// // // // // // // // //   padding: 0.7rem 0; font-size: 14px; font-weight: 400;
// // // // // // // // //   color: rgba(255,255,255,0.75);
// // // // // // // // //   border-bottom: 0.5px solid var(--border);
// // // // // // // // // }
// // // // // // // // // .lp-price-check { color: var(--gold); font-size: 12px; flex-shrink: 0; }

// // // // // // // // // /* ── CTA BANNER ── */
// // // // // // // // // .lp-cta-banner {
// // // // // // // // //   padding: 8rem 3rem;
// // // // // // // // //   text-align: center;
// // // // // // // // //   position: relative; overflow: hidden;
// // // // // // // // // }
// // // // // // // // // .lp-cta-banner::before {
// // // // // // // // //   content: '';
// // // // // // // // //   position: absolute; inset: 0;
// // // // // // // // //   background: radial-gradient(ellipse 80% 60% at 50% 50%, rgba(212,175,55,0.07) 0%, transparent 70%);
// // // // // // // // //   pointer-events: none;
// // // // // // // // // }
// // // // // // // // // .lp-cta-title {
// // // // // // // // //   font-family: var(--serif);
// // // // // // // // //   font-size: clamp(2.5rem, 6vw, 5rem);
// // // // // // // // //   font-weight: 500; line-height: 1.1;
// // // // // // // // //   color: var(--white); margin-bottom: 1.5rem;
// // // // // // // // //   letter-spacing: -0.5px;
// // // // // // // // // }
// // // // // // // // // .lp-cta-title em { font-style: italic; color: var(--gold); }
// // // // // // // // // .lp-cta-sub {
// // // // // // // // //   font-size: 16px; font-weight: 400; color: var(--muted);
// // // // // // // // //   margin-bottom: 3rem; line-height: 1.7;
// // // // // // // // // }

// // // // // // // // // /* ── FOOTER ── */
// // // // // // // // // .lp-footer {
// // // // // // // // //   padding: 3rem 3rem 4rem;
// // // // // // // // //   border-top: 0.5px solid var(--border);
// // // // // // // // // }
// // // // // // // // // .lp-footer-inner {
// // // // // // // // //   max-width: 1180px; margin: 0 auto;
// // // // // // // // //   display: flex; flex-direction: column; align-items: center; gap: 1rem;
// // // // // // // // // }
// // // // // // // // // .lp-footer-logo {
// // // // // // // // //   display: flex; align-items: center; gap: 10px;
// // // // // // // // //   font-family: var(--sans); font-size: 15px; font-weight: 500; color: var(--white);
// // // // // // // // // }
// // // // // // // // // .lp-footer-logo em { color: var(--gold); font-style: normal; font-weight: 600; }
// // // // // // // // // .lp-footer-tagline {
// // // // // // // // //   font-size: 13px; font-weight: 300; color: var(--muted2);
// // // // // // // // // }
// // // // // // // // // .lp-footer-copy {
// // // // // // // // //   font-size: 12px; font-weight: 300; color: rgba(255,255,255,0.18);
// // // // // // // // // }

// // // // // // // // // /* ── FADE-IN ON SCROLL ── */
// // // // // // // // // .reveal {
// // // // // // // // //   opacity: 0; transform: translateY(24px);
// // // // // // // // //   transition: opacity 0.7s ease, transform 0.7s ease;
// // // // // // // // // }
// // // // // // // // // .reveal.visible { opacity: 1; transform: translateY(0); }

// // // // // // // // // /* ── LIGHT THEME OVERRIDES ── */
// // // // // // // // // .lp-light {
// // // // // // // // //   --black: #F8F7F4;
// // // // // // // // //   --dark: #F0EDE6;
// // // // // // // // //   --card: #FFFFFF;
// // // // // // // // //   --border: rgba(0,0,0,0.08);
// // // // // // // // //   --white: #111111;
// // // // // // // // //   --muted: rgba(0,0,0,0.5);
// // // // // // // // //   --muted2: rgba(0,0,0,0.35);
// // // // // // // // // }
// // // // // // // // // .lp-light body,
// // // // // // // // // .lp-light { background: #F8F7F4; color: #111; }
// // // // // // // // // .lp-light ::-webkit-scrollbar-track { background: #F8F7F4; }
// // // // // // // // // .lp-light ::-webkit-scrollbar-thumb { background: rgba(184,148,42,0.3); }

// // // // // // // // // /* nav light */
// // // // // // // // // .lp-light .lp-nav.scrolled {
// // // // // // // // //   background: rgba(248,247,244,0.92);
// // // // // // // // //   border-bottom: 0.5px solid rgba(184,148,42,0.18);
// // // // // // // // // }
// // // // // // // // // .lp-light .lp-logo { color: #111; }
// // // // // // // // // .lp-light .lp-nav-link { color: rgba(0,0,0,0.45); }
// // // // // // // // // .lp-light .lp-nav-link:hover { color: #111; }
// // // // // // // // // .lp-light .lp-nav-cta { color: #0A0A0A; }

// // // // // // // // // /* hero light */
// // // // // // // // // .lp-light .lp-hero-title { color: #111; }
// // // // // // // // // .lp-light .lp-hero-sub { color: rgba(0,0,0,0.5); }
// // // // // // // // // .lp-light .lp-hero-stats { border-top-color: rgba(0,0,0,0.08); }
// // // // // // // // // .lp-light .lp-stat-label { color: rgba(0,0,0,0.35); }
// // // // // // // // // .lp-light .lp-btn-ghost {
// // // // // // // // //   color: rgba(0,0,0,0.5); border-color: rgba(0,0,0,0.15);
// // // // // // // // // }
// // // // // // // // // .lp-light .lp-btn-ghost:hover { color: #111; border-color: rgba(184,148,42,0.4); }
// // // // // // // // // .lp-light section[style*="background: var(--black)"] { background: #F8F7F4 !important; }

// // // // // // // // // /* hero scan phones stay dark — intentional: they're device mockups */
// // // // // // // // // .lp-light .phone-scanner { background: #1A1A1A; }
// // // // // // // // // .lp-light .phone-profile { background: #0E0E0E; }

// // // // // // // // // /* trust bar */
// // // // // // // // // .lp-light .lp-trust { background: #F0EDE6; }
// // // // // // // // // .lp-light .lp-trust-label { color: rgba(0,0,0,0.35); }
// // // // // // // // // .lp-light .lp-trust-item { color: rgba(0,0,0,0.55); }
// // // // // // // // // .lp-light .lp-trust-sep { color: rgba(184,148,42,0.3); }

// // // // // // // // // /* gold rule */
// // // // // // // // // .lp-light .gold-rule { background: linear-gradient(90deg, transparent, rgba(184,148,42,0.25), transparent); }

// // // // // // // // // /* sections */
// // // // // // // // // .lp-light .lp-section { background: #F8F7F4; }
// // // // // // // // // .lp-light .lp-section-title { color: #111; }
// // // // // // // // // .lp-light .lp-section-sub { color: rgba(0,0,0,0.5); }
// // // // // // // // // .lp-light .lp-eyebrow { color: #B8942A; }

// // // // // // // // // /* steps */
// // // // // // // // // .lp-light .lp-step { background: #FFFFFF; }
// // // // // // // // // .lp-light .lp-step:hover { background: #F5F2EC; }
// // // // // // // // // .lp-light .lp-steps { border-color: rgba(0,0,0,0.08); }
// // // // // // // // // .lp-light .lp-step-title { color: #111; }
// // // // // // // // // .lp-light .lp-step-desc { color: rgba(0,0,0,0.5); }
// // // // // // // // // .lp-light .lp-step-num { color: rgba(184,148,42,0.15); }
// // // // // // // // // .lp-light .lp-step-icon-wrap { background: rgba(184,148,42,0.08); border-color: rgba(184,148,42,0.2); }

// // // // // // // // // /* features */
// // // // // // // // // .lp-light .lp-feat-grid { border-color: rgba(0,0,0,0.07); }
// // // // // // // // // .lp-light .lp-feat-card { background: #FFFFFF; }
// // // // // // // // // .lp-light .lp-feat-card:hover { background: #F5F2EC; }
// // // // // // // // // .lp-light .lp-feat-title { color: #111; }
// // // // // // // // // .lp-light .lp-feat-desc { color: rgba(0,0,0,0.5); }

// // // // // // // // // /* use cases */
// // // // // // // // // .lp-light .lp-use-tab { color: rgba(0,0,0,0.45) !important; border-color: rgba(0,0,0,0.1) !important; }
// // // // // // // // // .lp-light .lp-use-tab[style*="rgba(212,175,55,0.1)"] { color: #B8942A !important; border-color: rgba(184,148,42,0.4) !important; }
// // // // // // // // // .lp-light .lp-use-panel { background: #FFFFFF; border-color: rgba(184,148,42,0.15) !important; }
// // // // // // // // // .lp-light .lp-use-tagline { color: #111; }
// // // // // // // // // .lp-light .lp-use-desc { color: rgba(0,0,0,0.5); }
// // // // // // // // // .lp-light .lp-use-hl { background: #F8F7F4; border-color: rgba(0,0,0,0.07); }
// // // // // // // // // .lp-light .lp-use-hl-text { color: rgba(0,0,0,0.65); }

// // // // // // // // // /* pricing */
// // // // // // // // // .lp-light .lp-pricing-card { background: #FFFFFF; border-color: rgba(184,148,42,0.25); }
// // // // // // // // // .lp-light .lp-pricing-card::before { background: linear-gradient(90deg, transparent, #B8942A, transparent); }
// // // // // // // // // .lp-light .lp-price-num { color: #111; }
// // // // // // // // // .lp-light .lp-price-period { color: rgba(0,0,0,0.35); }
// // // // // // // // // .lp-light .lp-price-item { color: rgba(0,0,0,0.65); border-bottom-color: rgba(0,0,0,0.07); }

// // // // // // // // // /* cta banner */
// // // // // // // // // .lp-light .lp-cta-banner { background: #F0EDE6; }
// // // // // // // // // .lp-light .lp-cta-title { color: #111; }
// // // // // // // // // .lp-light .lp-cta-sub { color: rgba(0,0,0,0.5); }

// // // // // // // // // /* footer */
// // // // // // // // // .lp-light .lp-footer { border-top-color: rgba(0,0,0,0.08); background: #ECEAE4; }
// // // // // // // // // .lp-light .lp-footer-logo { color: #111; }
// // // // // // // // // .lp-light .lp-footer-tagline { color: rgba(0,0,0,0.4); }
// // // // // // // // // .lp-light .lp-footer-copy { color: rgba(0,0,0,0.25); }

// // // // // // // // // /* theme toggle btn */
// // // // // // // // // .lp-theme-btn {
// // // // // // // // //   width: 40px; height: 40px; border-radius: 50%;
// // // // // // // // //   border: 0.5px solid rgba(212,175,55,0.35);
// // // // // // // // //   background: rgba(212,175,55,0.08);
// // // // // // // // //   cursor: pointer; display: flex; align-items: center; justify-content: center;
// // // // // // // // //   transition: background 0.2s, transform 0.15s;
// // // // // // // // //   flex-shrink: 0;
// // // // // // // // // }
// // // // // // // // // .lp-theme-btn:hover { background: rgba(212,175,55,0.15); transform: scale(1.08); }
// // // // // // // // // .lp-light .lp-theme-btn {
// // // // // // // // //   border-color: rgba(184,148,42,0.3);
// // // // // // // // //   background: rgba(184,148,42,0.08);
// // // // // // // // // }
// // // // // // // // // .lp-light .lp-theme-btn:hover { background: rgba(184,148,42,0.15); }

// // // // // // // // // /* ── MOBILE ── */
// // // // // // // // // @media (max-width: 900px) {
// // // // // // // // //   .lp-hero { grid-template-columns: 1fr; text-align: center; padding: 8rem 1.5rem 4rem; }
// // // // // // // // //   .lp-hero-sub { margin: 0 auto 3rem; }
// // // // // // // // //   .lp-hero-ctas { justify-content: center; }
// // // // // // // // //   .lp-hero-stats { justify-content: center; }
// // // // // // // // //   .lp-hero-right { margin-top: 3rem; }
// // // // // // // // //   .scan-scene { width: 300px; height: 380px; transform: scale(0.85); }
// // // // // // // // //   .lp-steps { grid-template-columns: 1fr; }
// // // // // // // // //   .lp-feat-grid { grid-template-columns: 1fr 1fr; }
// // // // // // // // //   .lp-use-panel { grid-template-columns: 1fr; }
// // // // // // // // //   .lp-nav { padding: 1rem 1.5rem; }
// // // // // // // // //   .lp-nav.scrolled { padding: 0.75rem 1.5rem; }
// // // // // // // // //   .lp-nav-links { gap: 1rem; }
// // // // // // // // //   .lp-section { padding: 5rem 1.5rem; }
// // // // // // // // // }
// // // // // // // // // @media (max-width: 600px) {
// // // // // // // // //   .lp-feat-grid { grid-template-columns: 1fr; }
// // // // // // // // //   .lp-nav-link { display: none; }
// // // // // // // // // }
// // // // // // // // // `;

// // // // // // // // // /* ─────────────────────────────────────────
// // // // // // // // //    QR SCAN ANIMATION
// // // // // // // // // ───────────────────────────────────────── */
// // // // // // // // // const QR_PATTERN = [
// // // // // // // // //     [1, 1, 1, 0, 1, 0], [1, 0, 1, 1, 0, 1], [1, 1, 1, 0, 1, 1], [0, 1, 0, 1, 0, 0], [1, 0, 1, 1, 1, 0], [0, 1, 0, 0, 1, 1]
// // // // // // // // // ];

// // // // // // // // // function ScanScene() {
// // // // // // // // //     return (
// // // // // // // // //         <div className="scan-scene">
// // // // // // // // //             <div className="hero-orb-1" />

// // // // // // // // //             {/* Phone A — scanner */}
// // // // // // // // //             <div className="phone-scanner">
// // // // // // // // //                 <div className="phone-scanner-screen">
// // // // // // // // //                     <div className="scanner-viewfinder">
// // // // // // // // //                         <div className="vf-corner-tr" />
// // // // // // // // //                         <div className="vf-corner-bl" />
// // // // // // // // //                         <div className="scan-laser" />
// // // // // // // // //                         {/* mini qr */}
// // // // // // // // //                         <div className="mini-qr">
// // // // // // // // //                             {QR_PATTERN.map((row, ri) =>
// // // // // // // // //                                 row.map((cell, ci) => (
// // // // // // // // //                                     <div key={`${ri}-${ci}`} className="mini-qr-cell"
// // // // // // // // //                                         style={{ background: cell ? "rgba(212,175,55,0.5)" : "transparent" }} />
// // // // // // // // //                                 ))
// // // // // // // // //                             )}
// // // // // // // // //                         </div>
// // // // // // // // //                     </div>
// // // // // // // // //                     <div className="scanner-label">Point at QR code</div>
// // // // // // // // //                 </div>
// // // // // // // // //             </div>

// // // // // // // // //             {/* Beam + particle */}
// // // // // // // // //             <div className="beam-line" />
// // // // // // // // //             <div className="beam-particle" />

// // // // // // // // //             {/* Phone B — profile card opening */}
// // // // // // // // //             <div className="phone-profile">
// // // // // // // // //                 <div className="profile-card-wrap">
// // // // // // // // //                     {/* Banner */}
// // // // // // // // //                     <div className="profile-banner">
// // // // // // // // //                         <div className="profile-banner-img" />
// // // // // // // // //                         <div className="profile-banner-fade" />
// // // // // // // // //                         {/* floating avatar */}
// // // // // // // // //                         <div className="profile-avatar-ring">
// // // // // // // // //                             <div className="profile-avatar-inner">MG</div>
// // // // // // // // //                         </div>
// // // // // // // // //                     </div>

// // // // // // // // //                     {/* Info */}
// // // // // // // // //                     <div className="profile-info">
// // // // // // // // //                         <div className="profile-info-name">
// // // // // // // // //                             Mragank <em>Gaur</em>
// // // // // // // // //                         </div>
// // // // // // // // //                         <div className="profile-info-role">Director · Empower Pragati</div>

// // // // // // // // //                         <div className="profile-tiles">
// // // // // // // // //                             {[["📞", "Phone"], ["✉️", "Email"], ["📍", "Delhi"]].map(([icon, label]) => (
// // // // // // // // //                                 <div className="profile-tile" key={label}>
// // // // // // // // //                                     <div className="tile-dot"><div className="tile-dot-inner" /></div>
// // // // // // // // //                                     <div className="tile-text">{label}</div>
// // // // // // // // //                                 </div>
// // // // // // // // //                             ))}
// // // // // // // // //                         </div>

// // // // // // // // //                         <button className="profile-save-btn">
// // // // // // // // //                             ↓ Save Contact
// // // // // // // // //                         </button>
// // // // // // // // //                     </div>
// // // // // // // // //                 </div>
// // // // // // // // //             </div>
// // // // // // // // //         </div>
// // // // // // // // //     );
// // // // // // // // // }

// // // // // // // // // /* ─────────────────────────────────────────
// // // // // // // // //    USE CASES
// // // // // // // // // ───────────────────────────────────────── */
// // // // // // // // // const USE_CASES = [
// // // // // // // // //     {
// // // // // // // // //         icon: "👔", role: "Job Seekers", gold: "rgba(212,175,55,1)",
// // // // // // // // //         tagline: "Land your next opportunity faster",
// // // // // // // // //         desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one elegant scan.",
// // // // // // // // //         highlights: ["Resume attached & downloadable", "LinkedIn & GitHub links", "QR code for instant sharing", "Save contact in one tap"]
// // // // // // // // //     },
// // // // // // // // //     {
// // // // // // // // //         icon: "💼", role: "Freelancers", gold: "rgba(212,175,55,1)",
// // // // // // // // //         tagline: "Win clients before you even speak",
// // // // // // // // //         desc: "Share everything a client needs — portfolio, social proof, and contact — all in a single professional link. Close deals faster.",
// // // // // // // // //         highlights: ["Portfolio & website link", "WhatsApp quick contact", "Instagram & social proof", "Professional digital presence"]
// // // // // // // // //     },
// // // // // // // // //     {
// // // // // // // // //         icon: "🏢", role: "Business Owners", gold: "rgba(212,175,55,1)",
// // // // // // // // //         tagline: "Network smarter, not harder",
// // // // // // // // //         desc: "Stop spending on printed cards that get lost. Share via QR at events — always updated, never outdated, never thrown away.",
// // // // // // // // //         highlights: ["Always up-to-date details", "Google Wallet for offline use", "QR code for events & meetups", "Share via WhatsApp or email"]
// // // // // // // // //     },
// // // // // // // // //     {
// // // // // // // // //         icon: "🎓", role: "Students", gold: "rgba(212,175,55,1)",
// // // // // // // // //         tagline: "Build your professional identity early",
// // // // // // // // //         desc: "Stand out in campus placements and internship drives. Show your skills, projects, and social profiles before you say a word.",
// // // // // // // // //         highlights: ["GitHub & project links", "Resume upload for placements", "LinkedIn profile showcase", "Free forever — no cost"]
// // // // // // // // //     },
// // // // // // // // //     {
// // // // // // // // //         icon: "📣", role: "Sales Pros", gold: "rgba(212,175,55,1)",
// // // // // // // // //         tagline: "Close the follow-up gap instantly",
// // // // // // // // //         desc: "At trade shows or cold visits — one QR scan and your prospect has everything. Number, email, website, product links in one place.",
// // // // // // // // //         highlights: ["Instant QR share at events", "Website & product links", "Save contact in one tap", "Google Wallet for quick access"]
// // // // // // // // //     },
// // // // // // // // //     {
// // // // // // // // //         icon: "🧑‍💻", role: "Developers", gold: "rgba(212,175,55,1)",
// // // // // // // // //         tagline: "Your entire dev profile in one link",
// // // // // // // // //         desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share at hackathons or in open source communities.",
// // // // // // // // //         highlights: ["GitHub & portfolio links", "Twitter / X profile", "Resume for job applications", "Professional dev identity"]
// // // // // // // // //     },
// // // // // // // // // ];

// // // // // // // // // function UseCasesSection({ navigate }) {
// // // // // // // // //     const [active, setActive] = useState(0);
// // // // // // // // //     const cur = USE_CASES[active];
// // // // // // // // //     return (
// // // // // // // // //         <section className="lp-section">
// // // // // // // // //             <div className="lp-section-inner">
// // // // // // // // //                 <span className="lp-eyebrow reveal">Who is it for</span>
// // // // // // // // //                 <h2 className="lp-section-title reveal">Built for every <em>professional</em></h2>
// // // // // // // // //                 <p className="lp-section-sub reveal">Whether you're a student or a CEO — your digital card works for you.</p>

// // // // // // // // //                 <div className="lp-use-tabs reveal">
// // // // // // // // //                     {USE_CASES.map((u, i) => (
// // // // // // // // //                         <button key={i} className="lp-use-tab" onClick={() => setActive(i)}
// // // // // // // // //                             style={{
// // // // // // // // //                                 border: active === i ? `0.5px solid rgba(212,175,55,0.5)` : "0.5px solid rgba(255,255,255,0.1)",
// // // // // // // // //                                 background: active === i ? "rgba(212,175,55,0.1)" : "transparent",
// // // // // // // // //                                 color: active === i ? "var(--gold)" : "var(--muted)",
// // // // // // // // //                             }}>
// // // // // // // // //                             {u.icon} {u.role}
// // // // // // // // //                         </button>
// // // // // // // // //                     ))}
// // // // // // // // //                 </div>

// // // // // // // // //                 <div className="lp-use-panel"
// // // // // // // // //                     style={{ borderColor: "rgba(212,175,55,0.15)" }}>
// // // // // // // // //                     <div className="lp-use-panel::before" style={{}} />
// // // // // // // // //                     <div>
// // // // // // // // //                         <div className="lp-use-badge"
// // // // // // // // //                             style={{ background: "rgba(212,175,55,0.1)", color: "var(--gold)", border: "0.5px solid rgba(212,175,55,0.25)" }}>
// // // // // // // // //                             <span>{cur.icon}</span><span style={{ fontSize: "12px", letterSpacing: "0.5px" }}>{cur.role}</span>
// // // // // // // // //                         </div>
// // // // // // // // //                         <div className="lp-use-tagline">{cur.tagline}</div>
// // // // // // // // //                         <p className="lp-use-desc">{cur.desc}</p>
// // // // // // // // //                         <button className="lp-btn-gold" onClick={() => navigate("/animation/card")}>
// // // // // // // // //                             Create My Card — Free →
// // // // // // // // //                         </button>
// // // // // // // // //                     </div>
// // // // // // // // //                     <div className="lp-use-highlights">
// // // // // // // // //                         {cur.highlights.map((h, i) => (
// // // // // // // // //                             <div className="lp-use-hl" key={i}>
// // // // // // // // //                                 <div className="lp-use-hl-check"
// // // // // // // // //                                     style={{ background: "rgba(212,175,55,0.1)", color: "var(--gold)" }}>✓</div>
// // // // // // // // //                                 <span className="lp-use-hl-text">{h}</span>
// // // // // // // // //                             </div>
// // // // // // // // //                         ))}
// // // // // // // // //                     </div>
// // // // // // // // //                 </div>
// // // // // // // // //             </div>
// // // // // // // // //         </section>
// // // // // // // // //     );
// // // // // // // // // }

// // // // // // // // // /* ─────────────────────────────────────────
// // // // // // // // //    MAIN COMPONENT
// // // // // // // // // ───────────────────────────────────────── */
// // // // // // // // // export default function LandingPage() {
// // // // // // // // //     const navigate = useNavigate();
// // // // // // // // //     const [scrolled, setScrolled] = useState(false);
// // // // // // // // //     const [isLight, setIsLight] = useState(false);

// // // // // // // // //     /* scroll nav */
// // // // // // // // //     useEffect(() => {
// // // // // // // // //         const onScroll = () => setScrolled(window.scrollY > 40);
// // // // // // // // //         window.addEventListener("scroll", onScroll);
// // // // // // // // //         return () => window.removeEventListener("scroll", onScroll);
// // // // // // // // //     }, []);

// // // // // // // // //     /* scroll reveal */
// // // // // // // // //     useEffect(() => {
// // // // // // // // //         const els = document.querySelectorAll(".reveal");
// // // // // // // // //         const observer = new IntersectionObserver(
// // // // // // // // //             (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
// // // // // // // // //             { threshold: 0.12 }
// // // // // // // // //         );
// // // // // // // // //         els.forEach(el => observer.observe(el));
// // // // // // // // //         return () => observer.disconnect();
// // // // // // // // //     }, []);

// // // // // // // // //     return (
// // // // // // // // //         <div className={isLight ? "lp-light" : ""} style={{ background: isLight ? "#F8F7F4" : "#080808", minHeight: "100vh" }}>
// // // // // // // // //             <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

// // // // // // // // //             {/* ── NAV ── */}
// // // // // // // // //             <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
// // // // // // // // //                 <div className="lp-nav-inner">
// // // // // // // // //                     <a href="/" className="lp-logo">
// // // // // // // // //                         {/* inline logo icon */}
// // // // // // // // //                         <svg className="lp-logo-icon" viewBox="0 0 34 34" fill="none">
// // // // // // // // //                             <rect width="34" height="34" rx="9" fill="#1A1A1A" />
// // // // // // // // //                             <rect width="34" height="34" rx="9" stroke="rgba(212,175,55,0.2)" strokeWidth="0.5" />
// // // // // // // // //                             <circle cx="15" cy="13" r="5" stroke="#D4AF37" strokeWidth="1.5" fill="none" />
// // // // // // // // //                             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" fill="none" />
// // // // // // // // //                             <path d="M23 11 L27 11 L27 23" stroke="rgba(212,175,55,0.5)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
// // // // // // // // //                             <circle cx="27" cy="23" r="1.8" fill="#D4AF37" />
// // // // // // // // //                         </svg>
// // // // // // // // //                         myprofile<em>corner</em>
// // // // // // // // //                     </a>
// // // // // // // // //                     <div className="lp-nav-links">
// // // // // // // // //                         <a href="#how" className="lp-nav-link">How it works</a>
// // // // // // // // //                         <a href="#features" className="lp-nav-link">Features</a>
// // // // // // // // //                         <a href="#pricing" className="lp-nav-link">Pricing</a>
// // // // // // // // //                         {/* ── Theme Toggle ── */}
// // // // // // // // //                         <button
// // // // // // // // //                             className="lp-theme-btn"
// // // // // // // // //                             onClick={() => setIsLight(p => !p)}
// // // // // // // // //                             title={isLight ? "Switch to Dark" : "Switch to Light"}
// // // // // // // // //                             aria-label="Toggle theme"
// // // // // // // // //                         >
// // // // // // // // //                             {isLight ? (
// // // // // // // // //                                 /* Moon — switch to dark */
// // // // // // // // //                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B8942A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
// // // // // // // // //                                     <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
// // // // // // // // //                                 </svg>
// // // // // // // // //                             ) : (
// // // // // // // // //                                 /* Sun — switch to light */
// // // // // // // // //                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
// // // // // // // // //                                     <circle cx="12" cy="12" r="5" />
// // // // // // // // //                                     <line x1="12" y1="1" x2="12" y2="3" />
// // // // // // // // //                                     <line x1="12" y1="21" x2="12" y2="23" />
// // // // // // // // //                                     <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
// // // // // // // // //                                     <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
// // // // // // // // //                                     <line x1="1" y1="12" x2="3" y2="12" />
// // // // // // // // //                                     <line x1="21" y1="12" x2="23" y2="12" />
// // // // // // // // //                                     <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
// // // // // // // // //                                     <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
// // // // // // // // //                                 </svg>
// // // // // // // // //                             )}
// // // // // // // // //                         </button>
// // // // // // // // //                         <button className="lp-nav-cta" onClick={() => navigate("/animation/card")}>
// // // // // // // // //                             Create Free Card
// // // // // // // // //                         </button>
// // // // // // // // //                     </div>
// // // // // // // // //                 </div>
// // // // // // // // //             </nav>

// // // // // // // // //             {/* ── HERO ── */}
// // // // // // // // //             <section style={{ background: isLight ? "#F8F7F4" : "#080808", overflow: "hidden", position: "relative" }}>
// // // // // // // // //                 <div className="lp-hero">
// // // // // // // // //                     {/* ambient bg orbs */}
// // // // // // // // //                     <div style={{
// // // // // // // // //                         position: "absolute", width: "600px", height: "600px", borderRadius: "50%",
// // // // // // // // //                         background: "radial-gradient(circle, rgba(212,175,55,0.05) 0%, transparent 65%)",
// // // // // // // // //                         top: "-200px", left: "-100px", pointerEvents: "none"
// // // // // // // // //                     }} />

// // // // // // // // //                     {/* LEFT */}
// // // // // // // // //                     <div className="lp-hero-left">
// // // // // // // // //                         <div className="lp-hero-eyebrow">
// // // // // // // // //                             <div className="lp-hero-eyebrow-dot" />
// // // // // // // // //                             100% Free · No App Needed
// // // // // // // // //                         </div>

// // // // // // // // //                         <h1 className="lp-hero-title">
// // // // // // // // //                             Your Digital<br />
// // // // // // // // //                             Identity, <em>Always</em><br />
// // // // // // // // //                             in Your Pocket
// // // // // // // // //                         </h1>

// // // // // // // // //                         <p className="lp-hero-sub">
// // // // // // // // //                             Replace paper visiting cards with a smart digital card. Share your profile
// // // // // // // // //                             instantly via QR code, link, or Google Wallet — no printing, no waste.
// // // // // // // // //                         </p>

// // // // // // // // //                         <div className="lp-hero-ctas">
// // // // // // // // //                             <button className="lp-btn-gold" onClick={() => navigate("/animation/card")}>
// // // // // // // // //                                 Create My Card — It's Free →
// // // // // // // // //                             </button>
// // // // // // // // //                             <a href="#how" className="lp-btn-ghost">See how it works</a>
// // // // // // // // //                         </div>

// // // // // // // // //                         <div className="lp-hero-stats">
// // // // // // // // //                             {[["100%", "Free forever"], ["2 min", "Setup time"], ["0", "Paper waste"]].map(([n, l]) => (
// // // // // // // // //                                 <div key={l}>
// // // // // // // // //                                     <div className="lp-stat-num">{n}</div>
// // // // // // // // //                                     <div className="lp-stat-label">{l}</div>
// // // // // // // // //                                 </div>
// // // // // // // // //                             ))}
// // // // // // // // //                         </div>
// // // // // // // // //                     </div>

// // // // // // // // //                     {/* RIGHT — animated scan scene */}
// // // // // // // // //                     <div className="lp-hero-right">
// // // // // // // // //                         <ScanScene />
// // // // // // // // //                     </div>
// // // // // // // // //                 </div>
// // // // // // // // //             </section>

// // // // // // // // //             <div className="gold-rule" />

// // // // // // // // //             {/* ── TRUST BAR ── */}
// // // // // // // // //             <div className="lp-trust" style={{ background: isLight ? "#F0EDE6" : "transparent" }}>
// // // // // // // // //                 <span className="lp-trust-label">Works seamlessly with</span>
// // // // // // // // //                 <div className="lp-trust-items">
// // // // // // // // //                     {[["🔥", "Firebase"], ["🪙", "Google Wallet"], ["📱", "Android & iOS"], ["🔗", "Any Browser"]].map(([icon, label], i, arr) => (
// // // // // // // // //                         <React.Fragment key={label}>
// // // // // // // // //                             <div className="lp-trust-item"><span>{icon}</span><span>{label}</span></div>
// // // // // // // // //                             {i < arr.length - 1 && <span className="lp-trust-sep">·</span>}
// // // // // // // // //                         </React.Fragment>
// // // // // // // // //                     ))}
// // // // // // // // //                 </div>
// // // // // // // // //             </div>

// // // // // // // // //             <div className="gold-rule" />

// // // // // // // // //             {/* ── HOW IT WORKS ── */}
// // // // // // // // //             <section id="how" className="lp-section">
// // // // // // // // //                 <div className="lp-section-inner">
// // // // // // // // //                     <span className="lp-eyebrow reveal">How it works</span>
// // // // // // // // //                     <h2 className="lp-section-title reveal">From setup to sharing <em>in 3 steps</em></h2>
// // // // // // // // //                     <p className="lp-section-sub reveal">No app downloads. No tech skills required. Just fill, share, impress.</p>

// // // // // // // // //                     <div className="lp-steps reveal">
// // // // // // // // //                         {[
// // // // // // // // //                             { num: "01", icon: "✏️", title: "Fill your profile", desc: "Add your name, photo, contact details, social links, resume, and website. Takes less than 2 minutes." },
// // // // // // // // //                             { num: "02", icon: "🎴", title: "Get your digital card", desc: "Instantly receive a beautiful card with a unique QR code and shareable link — no design skills needed." },
// // // // // // // // //                             { num: "03", icon: "🚀", title: "Share anywhere", desc: "Show your QR in meetings, add to Google Wallet, or send your link on WhatsApp, email, or LinkedIn." },
// // // // // // // // //                         ].map((step) => (
// // // // // // // // //                             <div className="lp-step" key={step.num}>
// // // // // // // // //                                 <div className="lp-step-num">{step.num}</div>
// // // // // // // // //                                 <div className="lp-step-icon-wrap">{step.icon}</div>
// // // // // // // // //                                 <div className="lp-step-title">{step.title}</div>
// // // // // // // // //                                 <p className="lp-step-desc">{step.desc}</p>
// // // // // // // // //                             </div>
// // // // // // // // //                         ))}
// // // // // // // // //                     </div>
// // // // // // // // //                 </div>
// // // // // // // // //             </section>

// // // // // // // // //             <div className="gold-rule" />

// // // // // // // // //             {/* ── FEATURES ── */}
// // // // // // // // //             <section id="features" className="lp-section">
// // // // // // // // //                 <div className="lp-section-inner">
// // // // // // // // //                     <span className="lp-eyebrow reveal">Features</span>
// // // // // // // // //                     <h2 className="lp-section-title reveal">Everything a modern <em>professional</em> needs</h2>

// // // // // // // // //                     <div className="lp-feat-grid reveal">
// // // // // // // // //                         {[
// // // // // // // // //                             { icon: "📸", title: "Profile Photo", desc: "Upload your photo — it appears on your card and even in your Google Wallet pass." },
// // // // // // // // //                             { icon: "🔗", title: "All Social Links", desc: "LinkedIn, GitHub, Instagram, Twitter, WhatsApp, YouTube — show what matters." },
// // // // // // // // //                             { icon: "📄", title: "Resume Attachment", desc: "Attach your PDF resume directly to your card. Recruiters can download it instantly." },
// // // // // // // // //                             { icon: "🪙", title: "Google Wallet", desc: "Add your card to Google Wallet and show it offline anytime, anywhere." },
// // // // // // // // //                             { icon: "📲", title: "QR Code", desc: "Every card gets a unique QR code. One scan opens your full profile instantly." },
// // // // // // // // //                             { icon: "💾", title: "Save Contact", desc: "Visitors can save your details directly to their phone contacts with one tap." },
// // // // // // // // //                             { icon: "✏️", title: "Edit Anytime", desc: "Changed jobs? New number? Update your card instantly — the link stays the same." },
// // // // // // // // //                             { icon: "🌐", title: "Works Everywhere", desc: "Your card opens in any browser on any device. No app install required." },
// // // // // // // // //                         ].map((f) => (
// // // // // // // // //                             <div className="lp-feat-card" key={f.title}>
// // // // // // // // //                                 <span className="lp-feat-icon">{f.icon}</span>
// // // // // // // // //                                 <div className="lp-feat-title">{f.title}</div>
// // // // // // // // //                                 <p className="lp-feat-desc">{f.desc}</p>
// // // // // // // // //                             </div>
// // // // // // // // //                         ))}
// // // // // // // // //                     </div>
// // // // // // // // //                 </div>
// // // // // // // // //             </section>

// // // // // // // // //             <div className="gold-rule" />

// // // // // // // // //             {/* ── USE CASES ── */}
// // // // // // // // //             <UseCasesSection navigate={navigate} />

// // // // // // // // //             <div className="gold-rule" />

// // // // // // // // //             {/* ── PRICING ── */}
// // // // // // // // //             <section id="pricing" className="lp-section">
// // // // // // // // //                 <div className="lp-section-inner">
// // // // // // // // //                     <span className="lp-eyebrow reveal">Pricing</span>
// // // // // // // // //                     <h2 className="lp-section-title reveal">Simple, <em>honest</em> pricing</h2>
// // // // // // // // //                     <p className="lp-section-sub reveal">No hidden charges. No credit card needed.</p>

// // // // // // // // //                     <div className="lp-pricing-wrap reveal">
// // // // // // // // //                         <div className="lp-pricing-card">
// // // // // // // // //                             <div className="lp-pricing-badge">Most Popular</div>
// // // // // // // // //                             <div className="lp-price-num">₹0</div>
// // // // // // // // //                             <div className="lp-price-period">Forever Free</div>
// // // // // // // // //                             <ul className="lp-price-list">
// // // // // // // // //                                 {["1 Digital Visiting Card", "Unique QR Code & Link", "Profile Photo Upload", "Resume Upload", "All Social Media Links", "Google Wallet Pass", "Save Contact (.vcf)", "Edit Anytime", "No App Required"].map(item => (
// // // // // // // // //                                     <li className="lp-price-item" key={item}>
// // // // // // // // //                                         <span className="lp-price-check">✦</span>{item}
// // // // // // // // //                                     </li>
// // // // // // // // //                                 ))}
// // // // // // // // //                             </ul>
// // // // // // // // //                             <button className="lp-btn-gold" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/animation/card")}>
// // // // // // // // //                                 Get Started Free →
// // // // // // // // //                             </button>
// // // // // // // // //                         </div>
// // // // // // // // //                     </div>
// // // // // // // // //                 </div>
// // // // // // // // //             </section>

// // // // // // // // //             <div className="gold-rule" />

// // // // // // // // //             {/* ── CTA BANNER ── */}
// // // // // // // // //             <section className="lp-cta-banner">
// // // // // // // // //                 <h2 className="lp-cta-title reveal">
// // // // // // // // //                     Stop carrying<br /><em>paper cards.</em>
// // // // // // // // //                 </h2>
// // // // // // // // //                 <p className="lp-cta-sub reveal">Create your digital card in under 2 minutes. Free, forever.</p>
// // // // // // // // //                 <button className="lp-btn-gold reveal" style={{ margin: "0 auto" }} onClick={() => navigate("/animation/card")}>
// // // // // // // // //                     Create My Card Now →
// // // // // // // // //                 </button>
// // // // // // // // //             </section>

// // // // // // // // //             <div className="gold-rule" />

// // // // // // // // //             {/* ── FOOTER ── */}
// // // // // // // // //             <footer className="lp-footer">
// // // // // // // // //                 <div className="lp-footer-inner">
// // // // // // // // //                     <div className="lp-footer-logo">
// // // // // // // // //                         <svg width="28" height="28" viewBox="0 0 34 34" fill="none">
// // // // // // // // //                             <rect width="34" height="34" rx="9" fill="#1A1A1A" />
// // // // // // // // //                             <rect width="34" height="34" rx="9" stroke="rgba(212,175,55,0.2)" strokeWidth="0.5" />
// // // // // // // // //                             <circle cx="15" cy="13" r="5" stroke="#D4AF37" strokeWidth="1.5" fill="none" />
// // // // // // // // //                             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" fill="none" />
// // // // // // // // //                             <path d="M23 11 L27 11 L27 23" stroke="rgba(212,175,55,0.5)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
// // // // // // // // //                             <circle cx="27" cy="23" r="1.8" fill="#D4AF37" />
// // // // // // // // //                         </svg>
// // // // // // // // //                         myprofile<em>corner</em>
// // // // // // // // //                     </div>
// // // // // // // // //                     <p className="lp-footer-tagline">Your digital identity, always in your pocket.</p>
// // // // // // // // //                     <p className="lp-footer-copy">© {new Date().getFullYear()} MyProfileCorner · Made with ♥ in India</p>
// // // // // // // // //                 </div>
// // // // // // // // //             </footer>
// // // // // // // // //         </div>
// // // // // // // // //     );
// // // // // // // // // }



// // // // // // // // // // import React, { useState, useEffect, useRef } from "react";
// // // // // // // // // // import { useNavigate } from "react-router-dom";

// // // // // // // // // // /* ─────────────────────────────────────────
// // // // // // // // // //    FONTS injected once
// // // // // // // // // // ───────────────────────────────────────── */
// // // // // // // // // // const FONT_LINK = `
// // // // // // // // // //   @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap');
// // // // // // // // // // `;

// // // // // // // // // // /* ─────────────────────────────────────────
// // // // // // // // // //    GLOBAL CSS
// // // // // // // // // // ───────────────────────────────────────── */
// // // // // // // // // // const GLOBAL_CSS = `
// // // // // // // // // // ${FONT_LINK}

// // // // // // // // // // *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

// // // // // // // // // // :root {
// // // // // // // // // //   --black: #080808;
// // // // // // // // // //   --dark: #0E0E0E;
// // // // // // // // // //   --card: #141414;
// // // // // // // // // //   --border: rgba(255,255,255,0.07);
// // // // // // // // // //   --gold: #D4AF37;
// // // // // // // // // //   --gold2: #F0D060;
// // // // // // // // // //   --gold3: #C9A227;
// // // // // // // // // //   --gold-dim: rgba(212,175,55,0.18);
// // // // // // // // // //   --white: #FFFFFF;
// // // // // // // // // //   --muted: rgba(255,255,255,0.45);
// // // // // // // // // //   --muted2: rgba(255,255,255,0.25);
// // // // // // // // // //   --serif: 'Cormorant Garamond', Georgia, serif;
// // // // // // // // // //   --sans: 'DM Sans', sans-serif;
// // // // // // // // // // }

// // // // // // // // // // html { scroll-behavior: smooth; }

// // // // // // // // // // body {
// // // // // // // // // //   background: var(--black);
// // // // // // // // // //   color: var(--white);
// // // // // // // // // //   font-family: var(--sans);
// // // // // // // // // //   overflow-x: hidden;
// // // // // // // // // // }

// // // // // // // // // // /* ── scrollbar ── */
// // // // // // // // // // ::-webkit-scrollbar { width: 4px; }
// // // // // // // // // // ::-webkit-scrollbar-track { background: var(--black); }
// // // // // // // // // // ::-webkit-scrollbar-thumb { background: rgba(212,175,55,0.3); border-radius: 2px; }

// // // // // // // // // // /* ── NAV ── */
// // // // // // // // // // .lp-nav {
// // // // // // // // // //   position: fixed; top: 0; left: 0; right: 0; z-index: 200;
// // // // // // // // // //   padding: 1.5rem 3rem;
// // // // // // // // // //   transition: all 0.4s;
// // // // // // // // // // }
// // // // // // // // // // .lp-nav.scrolled {
// // // // // // // // // //   background: rgba(8,8,8,0.9);
// // // // // // // // // //   backdrop-filter: blur(20px);
// // // // // // // // // //   border-bottom: 0.5px solid rgba(212,175,55,0.12);
// // // // // // // // // //   padding: 1rem 3rem;
// // // // // // // // // // }
// // // // // // // // // // .lp-nav-inner {
// // // // // // // // // //   max-width: 1180px; margin: 0 auto;
// // // // // // // // // //   display: flex; align-items: center; justify-content: space-between;
// // // // // // // // // // }
// // // // // // // // // // .lp-logo {
// // // // // // // // // //   display: flex; align-items: center; gap: 10px;
// // // // // // // // // //   font-family: var(--sans); font-size: 15px; font-weight: 500;
// // // // // // // // // //   color: var(--white); text-decoration: none; letter-spacing: 0.2px;
// // // // // // // // // // }
// // // // // // // // // // .lp-logo-icon { width: 34px; height: 34px; flex-shrink: 0; }
// // // // // // // // // // .lp-logo em { color: var(--gold); font-style: normal; font-weight: 600; }
// // // // // // // // // // .lp-nav-links {
// // // // // // // // // //   display: flex; align-items: center; gap: 2.5rem;
// // // // // // // // // // }
// // // // // // // // // // .lp-nav-link {
// // // // // // // // // //   font-size: 13px; font-weight: 400; color: var(--muted);
// // // // // // // // // //   text-decoration: none; letter-spacing: 0.3px;
// // // // // // // // // //   transition: color 0.2s;
// // // // // // // // // // }
// // // // // // // // // // .lp-nav-link:hover { color: var(--white); }
// // // // // // // // // // .lp-nav-cta {
// // // // // // // // // //   padding: 10px 22px;
// // // // // // // // // //   background: linear-gradient(135deg, var(--gold), var(--gold2), var(--gold3));
// // // // // // // // // //   color: #0A0A0A; border: none; border-radius: 10px;
// // // // // // // // // //   font-family: var(--sans); font-size: 13px; font-weight: 500;
// // // // // // // // // //   letter-spacing: 0.8px; text-transform: uppercase;
// // // // // // // // // //   cursor: pointer; position: relative; overflow: hidden;
// // // // // // // // // //   transition: transform 0.15s, opacity 0.15s;
// // // // // // // // // // }
// // // // // // // // // // .lp-nav-cta::before {
// // // // // // // // // //   content: ''; position: absolute; inset: 0;
// // // // // // // // // //   background: linear-gradient(135deg, rgba(255,255,255,0.2), transparent 60%);
// // // // // // // // // //   pointer-events: none;
// // // // // // // // // // }
// // // // // // // // // // .lp-nav-cta:hover { transform: translateY(-1px); opacity: 0.92; }

// // // // // // // // // // /* ── HERO ── */
// // // // // // // // // // .lp-hero {
// // // // // // // // // //   min-height: 100vh;
// // // // // // // // // //   display: grid;
// // // // // // // // // //   grid-template-columns: 1fr 1fr;
// // // // // // // // // //   align-items: center;
// // // // // // // // // //   gap: 4rem;
// // // // // // // // // //   max-width: 1180px; margin: 0 auto;
// // // // // // // // // //   padding: 10rem 3rem 6rem;
// // // // // // // // // //   position: relative;
// // // // // // // // // // }

// // // // // // // // // // .lp-hero-left { position: relative; z-index: 2; }

// // // // // // // // // // .lp-hero-eyebrow {
// // // // // // // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // // // // // // //   padding: 6px 14px;
// // // // // // // // // //   background: rgba(212,175,55,0.1);
// // // // // // // // // //   border: 0.5px solid rgba(212,175,55,0.3);
// // // // // // // // // //   border-radius: 100px;
// // // // // // // // // //   font-size: 11px; font-weight: 500; color: var(--gold);
// // // // // // // // // //   letter-spacing: 1.5px; text-transform: uppercase;
// // // // // // // // // //   margin-bottom: 2rem;
// // // // // // // // // // }
// // // // // // // // // // .lp-hero-eyebrow-dot {
// // // // // // // // // //   width: 5px; height: 5px; border-radius: 50%;
// // // // // // // // // //   background: var(--gold);
// // // // // // // // // //   animation: pulse-gold 2s ease-in-out infinite;
// // // // // // // // // // }
// // // // // // // // // // @keyframes pulse-gold {
// // // // // // // // // //   0%, 100% { opacity: 1; transform: scale(1); }
// // // // // // // // // //   50% { opacity: 0.4; transform: scale(0.7); }
// // // // // // // // // // }

// // // // // // // // // // .lp-hero-title {
// // // // // // // // // //   font-family: var(--serif);
// // // // // // // // // //   font-size: clamp(3rem, 5.5vw, 5.2rem);
// // // // // // // // // //   font-weight: 300; line-height: 1.08;
// // // // // // // // // //   letter-spacing: -1px;
// // // // // // // // // //   color: var(--white);
// // // // // // // // // //   margin-bottom: 1.75rem;
// // // // // // // // // // }
// // // // // // // // // // .lp-hero-title em {
// // // // // // // // // //   font-style: italic; font-weight: 300;
// // // // // // // // // //   color: var(--gold);
// // // // // // // // // // }

// // // // // // // // // // .lp-hero-sub {
// // // // // // // // // //   font-size: 16px; font-weight: 300;
// // // // // // // // // //   color: var(--muted); line-height: 1.8;
// // // // // // // // // //   max-width: 440px; margin-bottom: 3rem;
// // // // // // // // // // }

// // // // // // // // // // .lp-hero-ctas { display: flex; gap: 1rem; flex-wrap: wrap; }

// // // // // // // // // // .lp-btn-gold {
// // // // // // // // // //   padding: 16px 32px;
// // // // // // // // // //   background: linear-gradient(135deg, var(--gold), var(--gold2), var(--gold3));
// // // // // // // // // //   color: #0A0A0A; border: none; border-radius: 12px;
// // // // // // // // // //   font-family: var(--sans); font-size: 13px; font-weight: 500;
// // // // // // // // // //   letter-spacing: 1px; text-transform: uppercase;
// // // // // // // // // //   cursor: pointer; position: relative; overflow: hidden;
// // // // // // // // // //   transition: transform 0.2s, opacity 0.2s;
// // // // // // // // // //   display: flex; align-items: center; gap: 8px;
// // // // // // // // // // }
// // // // // // // // // // .lp-btn-gold::before {
// // // // // // // // // //   content: ''; position: absolute; inset: 0;
// // // // // // // // // //   background: linear-gradient(135deg, rgba(255,255,255,0.25), transparent 60%);
// // // // // // // // // //   pointer-events: none;
// // // // // // // // // // }
// // // // // // // // // // .lp-btn-gold:hover { transform: translateY(-2px); opacity: 0.92; }

// // // // // // // // // // .lp-btn-ghost {
// // // // // // // // // //   padding: 16px 28px;
// // // // // // // // // //   background: transparent;
// // // // // // // // // //   color: var(--muted); border: 0.5px solid rgba(255,255,255,0.15);
// // // // // // // // // //   border-radius: 12px;
// // // // // // // // // //   font-family: var(--sans); font-size: 13px; font-weight: 400;
// // // // // // // // // //   letter-spacing: 0.5px; cursor: pointer; text-decoration: none;
// // // // // // // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // // // // // // //   transition: border-color 0.2s, color 0.2s;
// // // // // // // // // // }
// // // // // // // // // // .lp-btn-ghost:hover { border-color: rgba(212,175,55,0.3); color: var(--white); }

// // // // // // // // // // /* ── HERO STATS STRIP ── */
// // // // // // // // // // .lp-hero-stats {
// // // // // // // // // //   display: flex; gap: 2.5rem; margin-top: 3.5rem;
// // // // // // // // // //   padding-top: 2.5rem;
// // // // // // // // // //   border-top: 0.5px solid var(--border);
// // // // // // // // // // }
// // // // // // // // // // .lp-stat-num {
// // // // // // // // // //   font-family: var(--serif); font-size: 2rem; font-weight: 300;
// // // // // // // // // //   color: var(--gold); line-height: 1;
// // // // // // // // // // }
// // // // // // // // // // .lp-stat-label {
// // // // // // // // // //   font-size: 11px; font-weight: 400; color: var(--muted2);
// // // // // // // // // //   letter-spacing: 0.5px; margin-top: 4px;
// // // // // // // // // // }

// // // // // // // // // // /* ── HERO RIGHT — QR SCAN ANIMATION ── */
// // // // // // // // // // .lp-hero-right {
// // // // // // // // // //   position: relative; display: flex;
// // // // // // // // // //   align-items: center; justify-content: center;
// // // // // // // // // //   z-index: 2;
// // // // // // // // // // }

// // // // // // // // // // /* Scene wrapper */
// // // // // // // // // // .scan-scene {
// // // // // // // // // //   position: relative;
// // // // // // // // // //   width: 380px; height: 480px;
// // // // // // // // // // }

// // // // // // // // // // /* Phone A (scanner) */
// // // // // // // // // // .phone-scanner {
// // // // // // // // // //   position: absolute;
// // // // // // // // // //   left: 0; top: 60px;
// // // // // // // // // //   width: 155px; height: 270px;
// // // // // // // // // //   background: #1A1A1A;
// // // // // // // // // //   border-radius: 22px;
// // // // // // // // // //   border: 1px solid rgba(255,255,255,0.1);
// // // // // // // // // //   overflow: hidden;
// // // // // // // // // //   box-shadow: 0 24px 60px rgba(0,0,0,0.6);
// // // // // // // // // //   z-index: 3;
// // // // // // // // // //   animation: phone-a-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;
// // // // // // // // // //   animation-delay: 0.3s;
// // // // // // // // // // }
// // // // // // // // // // @keyframes phone-a-intro {
// // // // // // // // // //   from { opacity: 0; transform: translateX(-30px) rotate(-6deg); }
// // // // // // // // // //   to   { opacity: 1; transform: translateX(0) rotate(-6deg); }
// // // // // // // // // // }

// // // // // // // // // // .phone-scanner-screen {
// // // // // // // // // //   width: 100%; height: 100%;
// // // // // // // // // //   background: #111;
// // // // // // // // // //   display: flex; flex-direction: column;
// // // // // // // // // //   align-items: center; justify-content: center; gap: 12px;
// // // // // // // // // //   padding: 12px;
// // // // // // // // // // }
// // // // // // // // // // .scanner-viewfinder {
// // // // // // // // // //   width: 100px; height: 100px;
// // // // // // // // // //   border-radius: 10px;
// // // // // // // // // //   position: relative;
// // // // // // // // // //   background: rgba(212,175,55,0.04);
// // // // // // // // // // }
// // // // // // // // // // /* corner brackets of viewfinder */
// // // // // // // // // // .scanner-viewfinder::before,
// // // // // // // // // // .scanner-viewfinder::after {
// // // // // // // // // //   content: '';
// // // // // // // // // //   position: absolute;
// // // // // // // // // //   width: 20px; height: 20px;
// // // // // // // // // //   border-color: var(--gold); border-style: solid;
// // // // // // // // // // }
// // // // // // // // // // .scanner-viewfinder::before {
// // // // // // // // // //   top: 0; left: 0;
// // // // // // // // // //   border-width: 2px 0 0 2px;
// // // // // // // // // //   border-radius: 6px 0 0 0;
// // // // // // // // // // }
// // // // // // // // // // .scanner-viewfinder::after {
// // // // // // // // // //   bottom: 0; right: 0;
// // // // // // // // // //   border-width: 0 2px 2px 0;
// // // // // // // // // //   border-radius: 0 0 6px 0;
// // // // // // // // // // }
// // // // // // // // // // .vf-corner-tr {
// // // // // // // // // //   position: absolute; top: 0; right: 0;
// // // // // // // // // //   width: 20px; height: 20px;
// // // // // // // // // //   border-top: 2px solid var(--gold);
// // // // // // // // // //   border-right: 2px solid var(--gold);
// // // // // // // // // //   border-radius: 0 6px 0 0;
// // // // // // // // // // }
// // // // // // // // // // .vf-corner-bl {
// // // // // // // // // //   position: absolute; bottom: 0; left: 0;
// // // // // // // // // //   width: 20px; height: 20px;
// // // // // // // // // //   border-bottom: 2px solid var(--gold);
// // // // // // // // // //   border-left: 2px solid var(--gold);
// // // // // // // // // //   border-radius: 0 0 0 6px;
// // // // // // // // // // }

// // // // // // // // // // /* scanning laser line */
// // // // // // // // // // .scan-laser {
// // // // // // // // // //   position: absolute;
// // // // // // // // // //   left: 4px; right: 4px; height: 2px;
// // // // // // // // // //   background: linear-gradient(90deg, transparent, var(--gold), transparent);
// // // // // // // // // //   border-radius: 1px;
// // // // // // // // // //   top: 4px;
// // // // // // // // // //   animation: laser-sweep 2s ease-in-out infinite;
// // // // // // // // // //   animation-delay: 1.2s;
// // // // // // // // // // }
// // // // // // // // // // @keyframes laser-sweep {
// // // // // // // // // //   0%   { top: 8px; opacity: 0; }
// // // // // // // // // //   10%  { opacity: 1; }
// // // // // // // // // //   90%  { opacity: 1; }
// // // // // // // // // //   100% { top: 88px; opacity: 0; }
// // // // // // // // // // }

// // // // // // // // // // .scanner-label {
// // // // // // // // // //   font-size: 9px; font-weight: 400; color: var(--muted2);
// // // // // // // // // //   letter-spacing: 1px; text-align: center;
// // // // // // // // // //   text-transform: uppercase;
// // // // // // // // // // }

// // // // // // // // // // /* Mini QR inside viewfinder */
// // // // // // // // // // .mini-qr {
// // // // // // // // // //   position: absolute; inset: 10px;
// // // // // // // // // //   display: grid; grid-template-columns: repeat(6,1fr); grid-template-rows: repeat(6,1fr);
// // // // // // // // // //   gap: 1.5px; opacity: 0.6;
// // // // // // // // // // }
// // // // // // // // // // .mini-qr-cell { border-radius: 1px; }

// // // // // // // // // // /* Phone B (profile card) */
// // // // // // // // // // .phone-profile {
// // // // // // // // // //   position: absolute;
// // // // // // // // // //   right: 0; top: 0;
// // // // // // // // // //   width: 175px; height: 310px;
// // // // // // // // // //   background: #0E0E0E;
// // // // // // // // // //   border-radius: 24px;
// // // // // // // // // //   border: 1px solid rgba(212,175,55,0.2);
// // // // // // // // // //   overflow: hidden;
// // // // // // // // // //   box-shadow: 0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(212,175,55,0.08);
// // // // // // // // // //   z-index: 4;
// // // // // // // // // //   animation: phone-b-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;
// // // // // // // // // //   animation-delay: 0.5s;
// // // // // // // // // // }
// // // // // // // // // // @keyframes phone-b-intro {
// // // // // // // // // //   from { opacity: 0; transform: translateY(30px) rotate(4deg); }
// // // // // // // // // //   to   { opacity: 1; transform: translateY(0) rotate(4deg); }
// // // // // // // // // // }

// // // // // // // // // // /* ── card content opening animation ── */
// // // // // // // // // // .profile-card-wrap {
// // // // // // // // // //   width: 100%; height: 100%;
// // // // // // // // // //   animation: card-reveal 0.7s cubic-bezier(0.22,1,0.36,1) both;
// // // // // // // // // //   animation-delay: 2s;
// // // // // // // // // // }
// // // // // // // // // // @keyframes card-reveal {
// // // // // // // // // //   from { transform: translateY(40px); opacity: 0; }
// // // // // // // // // //   to   { transform: translateY(0); opacity: 1; }
// // // // // // // // // // }

// // // // // // // // // // .profile-banner {
// // // // // // // // // //   width: 100%; height: 110px;
// // // // // // // // // //   background: linear-gradient(160deg, #1a1a1a, #2a2020);
// // // // // // // // // //   position: relative; overflow: hidden;
// // // // // // // // // // }
// // // // // // // // // // .profile-banner-img {
// // // // // // // // // //   width: 100%; height: 100%;
// // // // // // // // // //   background: linear-gradient(160deg, #1e1a10 0%, #2a2010 50%, #181818 100%);
// // // // // // // // // // }
// // // // // // // // // // .profile-banner-fade {
// // // // // // // // // //   position: absolute; bottom: 0; left: 0; right: 0; height: 50px;
// // // // // // // // // //   background: linear-gradient(transparent, #0E0E0E);
// // // // // // // // // // }

// // // // // // // // // // /* floating avatar */
// // // // // // // // // // .profile-avatar-ring {
// // // // // // // // // //   position: absolute;
// // // // // // // // // //   bottom: -20px; left: 14px;
// // // // // // // // // //   width: 44px; height: 44px;
// // // // // // // // // //   border-radius: 50%;
// // // // // // // // // //   border: 2px solid #0E0E0E;
// // // // // // // // // //   background: #222;
// // // // // // // // // //   overflow: hidden;
// // // // // // // // // //   box-shadow: 0 0 0 1.5px rgba(212,175,55,0.5);
// // // // // // // // // //   animation: avatar-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both;
// // // // // // // // // //   animation-delay: 2.4s;
// // // // // // // // // // }
// // // // // // // // // // @keyframes avatar-pop {
// // // // // // // // // //   from { transform: scale(0.3); opacity: 0; }
// // // // // // // // // //   to   { transform: scale(1); opacity: 1; }
// // // // // // // // // // }
// // // // // // // // // // .profile-avatar-inner {
// // // // // // // // // //   width: 100%; height: 100%;
// // // // // // // // // //   background: linear-gradient(145deg, #2a2010, #3a3020);
// // // // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // // // //   font-family: var(--serif); font-size: 14px; font-weight: 300;
// // // // // // // // // //   color: rgba(212,175,55,0.8); letter-spacing: 1px;
// // // // // // // // // // }

// // // // // // // // // // .profile-info {
// // // // // // // // // //   padding: 28px 14px 12px;
// // // // // // // // // // }
// // // // // // // // // // .profile-info-name {
// // // // // // // // // //   font-family: var(--serif); font-size: 14px; font-weight: 300;
// // // // // // // // // //   color: var(--white); line-height: 1.2; margin-bottom: 2px;
// // // // // // // // // // }
// // // // // // // // // // .profile-info-name em { font-style: italic; color: var(--gold); }
// // // // // // // // // // .profile-info-role {
// // // // // // // // // //   font-size: 8px; font-weight: 400; color: var(--muted2);
// // // // // // // // // //   letter-spacing: 0.3px; margin-bottom: 10px;
// // // // // // // // // // }

// // // // // // // // // // .profile-tiles {
// // // // // // // // // //   display: grid; grid-template-columns: 1fr 1fr 1fr;
// // // // // // // // // //   gap: 5px; margin-bottom: 8px;
// // // // // // // // // // }
// // // // // // // // // // .profile-tile {
// // // // // // // // // //   background: #1A1A1A;
// // // // // // // // // //   border: 0.5px solid rgba(255,255,255,0.06);
// // // // // // // // // //   border-radius: 8px; padding: 7px 6px;
// // // // // // // // // //   display: flex; flex-direction: column; gap: 4px;
// // // // // // // // // //   animation: tile-slide 0.3s ease both;
// // // // // // // // // // }
// // // // // // // // // // .profile-tile:nth-child(1) { animation-delay: 2.5s; }
// // // // // // // // // // .profile-tile:nth-child(2) { animation-delay: 2.65s; }
// // // // // // // // // // .profile-tile:nth-child(3) { animation-delay: 2.8s; }
// // // // // // // // // // @keyframes tile-slide {
// // // // // // // // // //   from { opacity: 0; transform: translateY(6px); }
// // // // // // // // // //   to   { opacity: 1; transform: translateY(0); }
// // // // // // // // // // }
// // // // // // // // // // .tile-dot {
// // // // // // // // // //   width: 12px; height: 12px; border-radius: 50%;
// // // // // // // // // //   background: rgba(212,175,55,0.15);
// // // // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // // // // }
// // // // // // // // // // .tile-dot-inner { width: 5px; height: 5px; border-radius: 50%; background: var(--gold); }
// // // // // // // // // // .tile-text { font-size: 7px; color: rgba(255,255,255,0.35); line-height: 1.3; }

// // // // // // // // // // .profile-save-btn {
// // // // // // // // // //   width: 100%; padding: 8px;
// // // // // // // // // //   background: linear-gradient(135deg, var(--gold), var(--gold2));
// // // // // // // // // //   color: #0A0A0A; border: none; border-radius: 8px;
// // // // // // // // // //   font-size: 8px; font-weight: 500; letter-spacing: 0.8px;
// // // // // // // // // //   text-transform: uppercase; cursor: default;
// // // // // // // // // //   display: flex; align-items: center; justify-content: center; gap: 4px;
// // // // // // // // // //   animation: tile-slide 0.3s ease both;
// // // // // // // // // //   animation-delay: 2.95s;
// // // // // // // // // // }

// // // // // // // // // // /* beam connecting two phones */
// // // // // // // // // // .beam-line {
// // // // // // // // // //   position: absolute;
// // // // // // // // // //   top: 165px; left: 148px;
// // // // // // // // // //   width: 90px; height: 1px;
// // // // // // // // // //   background: linear-gradient(90deg, var(--gold), rgba(212,175,55,0));
// // // // // // // // // //   transform-origin: left center;
// // // // // // // // // //   animation: beam-appear 0.4s ease both;
// // // // // // // // // //   animation-delay: 1.5s;
// // // // // // // // // //   z-index: 5;
// // // // // // // // // // }
// // // // // // // // // // @keyframes beam-appear {
// // // // // // // // // //   from { scaleX: 0; opacity: 0; }
// // // // // // // // // //   to   { scaleX: 1; opacity: 1; }
// // // // // // // // // // }
// // // // // // // // // // .beam-particle {
// // // // // // // // // //   position: absolute;
// // // // // // // // // //   top: 160px; left: 148px;
// // // // // // // // // //   width: 6px; height: 6px; border-radius: 50%;
// // // // // // // // // //   background: var(--gold);
// // // // // // // // // //   animation: particle-travel 2s ease-in-out infinite;
// // // // // // // // // //   animation-delay: 1.6s;
// // // // // // // // // //   z-index: 6;
// // // // // // // // // // }
// // // // // // // // // // @keyframes particle-travel {
// // // // // // // // // //   0%   { left: 148px; opacity: 0; transform: scale(0.5); }
// // // // // // // // // //   10%  { opacity: 1; transform: scale(1); }
// // // // // // // // // //   90%  { opacity: 1; }
// // // // // // // // // //   100% { left: 228px; opacity: 0; transform: scale(0.5); }
// // // // // // // // // // }

// // // // // // // // // // /* ambient glow orbs */
// // // // // // // // // // .hero-orb-1 {
// // // // // // // // // //   position: absolute;
// // // // // // // // // //   width: 500px; height: 500px; border-radius: 50%;
// // // // // // // // // //   background: radial-gradient(circle, rgba(212,175,55,0.06) 0%, transparent 65%);
// // // // // // // // // //   top: -100px; right: -100px; pointer-events: none; z-index: 0;
// // // // // // // // // // }
// // // // // // // // // // .hero-orb-2 {
// // // // // // // // // //   position: absolute;
// // // // // // // // // //   width: 300px; height: 300px; border-radius: 50%;
// // // // // // // // // //   background: radial-gradient(circle, rgba(212,175,55,0.04) 0%, transparent 65%);
// // // // // // // // // //   bottom: 0; left: -80px; pointer-events: none; z-index: 0;
// // // // // // // // // // }

// // // // // // // // // // /* ── SECTION SHARED ── */
// // // // // // // // // // .lp-section {
// // // // // // // // // //   padding: 7rem 3rem;
// // // // // // // // // //   position: relative;
// // // // // // // // // // }
// // // // // // // // // // .lp-section-inner { max-width: 1180px; margin: 0 auto; }
// // // // // // // // // // .lp-eyebrow {
// // // // // // // // // //   display: block; font-size: 10px; font-weight: 500;
// // // // // // // // // //   letter-spacing: 3px; text-transform: uppercase;
// // // // // // // // // //   color: var(--gold); margin-bottom: 1.25rem; text-align: center;
// // // // // // // // // // }
// // // // // // // // // // .lp-section-title {
// // // // // // // // // //   font-family: var(--serif);
// // // // // // // // // //   font-size: clamp(2rem, 4vw, 3.2rem);
// // // // // // // // // //   font-weight: 300; line-height: 1.15; letter-spacing: -0.5px;
// // // // // // // // // //   color: var(--white); text-align: center; margin-bottom: 1.25rem;
// // // // // // // // // // }
// // // // // // // // // // .lp-section-title em { font-style: italic; color: var(--gold); }
// // // // // // // // // // .lp-section-sub {
// // // // // // // // // //   font-size: 15px; font-weight: 300; color: var(--muted);
// // // // // // // // // //   text-align: center; max-width: 480px; margin: 0 auto 4rem; line-height: 1.8;
// // // // // // // // // // }

// // // // // // // // // // /* ── GOLD DIVIDER ── */
// // // // // // // // // // .gold-rule {
// // // // // // // // // //   height: 0.5px;
// // // // // // // // // //   background: linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent);
// // // // // // // // // //   margin: 0;
// // // // // // // // // // }

// // // // // // // // // // /* ── TRUST BAR ── */
// // // // // // // // // // .lp-trust {
// // // // // // // // // //   padding: 2rem 3rem;
// // // // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // // // //   gap: 2.5rem; flex-wrap: wrap;
// // // // // // // // // // }
// // // // // // // // // // .lp-trust-label {
// // // // // // // // // //   font-size: 10px; font-weight: 400; color: var(--muted2);
// // // // // // // // // //   letter-spacing: 2px; text-transform: uppercase;
// // // // // // // // // // }
// // // // // // // // // // .lp-trust-items {
// // // // // // // // // //   display: flex; align-items: center; gap: 2rem; flex-wrap: wrap;
// // // // // // // // // // }
// // // // // // // // // // .lp-trust-item {
// // // // // // // // // //   display: flex; align-items: center; gap: 7px;
// // // // // // // // // //   font-size: 13px; font-weight: 400; color: var(--muted);
// // // // // // // // // // }
// // // // // // // // // // .lp-trust-sep { color: rgba(212,175,55,0.2); font-size: 18px; }

// // // // // // // // // // /* ── STEPS ── */
// // // // // // // // // // .lp-steps {
// // // // // // // // // //   display: grid; grid-template-columns: repeat(3, 1fr);
// // // // // // // // // //   gap: 1.5px;
// // // // // // // // // //   border: 0.5px solid var(--border);
// // // // // // // // // //   border-radius: 20px; overflow: hidden;
// // // // // // // // // //   margin-top: 1rem;
// // // // // // // // // // }
// // // // // // // // // // .lp-step {
// // // // // // // // // //   background: var(--card);
// // // // // // // // // //   padding: 2.5rem 2rem;
// // // // // // // // // //   position: relative; overflow: hidden;
// // // // // // // // // //   transition: background 0.3s;
// // // // // // // // // //   cursor: default;
// // // // // // // // // // }
// // // // // // // // // // .lp-step:hover { background: #1a1a1a; }
// // // // // // // // // // .lp-step::before {
// // // // // // // // // //   content: '';
// // // // // // // // // //   position: absolute; top: 0; left: 0; right: 0; height: 1px;
// // // // // // // // // //   background: linear-gradient(90deg, transparent, rgba(212,175,55,0), transparent);
// // // // // // // // // //   transition: background 0.3s;
// // // // // // // // // // }
// // // // // // // // // // .lp-step:hover::before {
// // // // // // // // // //   background: linear-gradient(90deg, transparent, rgba(212,175,55,0.4), transparent);
// // // // // // // // // // }
// // // // // // // // // // .lp-step-num {
// // // // // // // // // //   font-family: var(--serif); font-size: 3.5rem; font-weight: 300;
// // // // // // // // // //   color: rgba(212,175,55,0.12); line-height: 1;
// // // // // // // // // //   position: absolute; top: 1.5rem; right: 1.5rem;
// // // // // // // // // //   letter-spacing: -1px;
// // // // // // // // // // }
// // // // // // // // // // .lp-step-icon-wrap {
// // // // // // // // // //   width: 48px; height: 48px; border-radius: 14px;
// // // // // // // // // //   background: rgba(212,175,55,0.08);
// // // // // // // // // //   border: 0.5px solid rgba(212,175,55,0.2);
// // // // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // // // //   font-size: 1.4rem; margin-bottom: 1.5rem;
// // // // // // // // // // }
// // // // // // // // // // .lp-step-title {
// // // // // // // // // //   font-family: var(--serif); font-size: 1.4rem; font-weight: 400;
// // // // // // // // // //   color: var(--white); margin-bottom: 0.75rem; line-height: 1.2;
// // // // // // // // // // }
// // // // // // // // // // .lp-step-desc {
// // // // // // // // // //   font-size: 14px; font-weight: 300; color: var(--muted);
// // // // // // // // // //   line-height: 1.75;
// // // // // // // // // // }

// // // // // // // // // // /* ── FEATURES ── */
// // // // // // // // // // .lp-feat-grid {
// // // // // // // // // //   display: grid; grid-template-columns: repeat(4, 1fr);
// // // // // // // // // //   gap: 1px;
// // // // // // // // // //   border: 0.5px solid var(--border);
// // // // // // // // // //   border-radius: 20px; overflow: hidden;
// // // // // // // // // // }
// // // // // // // // // // .lp-feat-card {
// // // // // // // // // //   background: var(--card); padding: 2rem 1.5rem;
// // // // // // // // // //   transition: background 0.3s;
// // // // // // // // // //   position: relative;
// // // // // // // // // // }
// // // // // // // // // // .lp-feat-card:hover { background: #181818; }
// // // // // // // // // // .lp-feat-icon {
// // // // // // // // // //   font-size: 1.6rem; margin-bottom: 1.25rem;
// // // // // // // // // //   display: block;
// // // // // // // // // // }
// // // // // // // // // // .lp-feat-title {
// // // // // // // // // //   font-family: var(--serif); font-size: 1.1rem; font-weight: 400;
// // // // // // // // // //   color: var(--white); margin-bottom: 0.5rem;
// // // // // // // // // // }
// // // // // // // // // // .lp-feat-desc {
// // // // // // // // // //   font-size: 13px; font-weight: 300; color: var(--muted); line-height: 1.7;
// // // // // // // // // // }

// // // // // // // // // // /* ── USE CASES ── */
// // // // // // // // // // .lp-use-tabs {
// // // // // // // // // //   display: flex; flex-wrap: wrap; gap: 0.5rem;
// // // // // // // // // //   justify-content: center; margin-bottom: 3rem;
// // // // // // // // // // }
// // // // // // // // // // .lp-use-tab {
// // // // // // // // // //   padding: 8px 18px; border-radius: 100px;
// // // // // // // // // //   font-size: 13px; font-weight: 400; cursor: pointer;
// // // // // // // // // //   transition: all 0.2s; font-family: var(--sans);
// // // // // // // // // //   display: flex; align-items: center; gap: 6px;
// // // // // // // // // // }
// // // // // // // // // // .lp-use-panel {
// // // // // // // // // //   display: grid; grid-template-columns: 1fr 1fr;
// // // // // // // // // //   gap: 3rem; align-items: center;
// // // // // // // // // //   background: var(--card);
// // // // // // // // // //   border: 0.5px solid var(--border);
// // // // // // // // // //   border-radius: 24px; padding: 3rem;
// // // // // // // // // //   position: relative; overflow: hidden;
// // // // // // // // // // }
// // // // // // // // // // .lp-use-panel::before {
// // // // // // // // // //   content: '';
// // // // // // // // // //   position: absolute; top: 0; left: 0; right: 0; height: 1px;
// // // // // // // // // // }
// // // // // // // // // // .lp-use-badge {
// // // // // // // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // // // // // // //   padding: 6px 14px; border-radius: 100px;
// // // // // // // // // //   margin-bottom: 1.5rem; font-size: 12px; font-weight: 500;
// // // // // // // // // // }
// // // // // // // // // // .lp-use-tagline {
// // // // // // // // // //   font-family: var(--serif); font-size: 2rem; font-weight: 300;
// // // // // // // // // //   color: var(--white); line-height: 1.2; margin-bottom: 1rem;
// // // // // // // // // // }
// // // // // // // // // // .lp-use-desc {
// // // // // // // // // //   font-size: 14px; font-weight: 300; color: var(--muted);
// // // // // // // // // //   line-height: 1.85; margin-bottom: 2rem;
// // // // // // // // // // }
// // // // // // // // // // .lp-use-highlights { display: flex; flex-direction: column; gap: 0.75rem; }
// // // // // // // // // // .lp-use-hl {
// // // // // // // // // //   display: flex; align-items: center; gap: 12px;
// // // // // // // // // //   background: #1a1a1a; border: 0.5px solid var(--border);
// // // // // // // // // //   border-radius: 12px; padding: 1rem 1.25rem;
// // // // // // // // // // }
// // // // // // // // // // .lp-use-hl-check {
// // // // // // // // // //   width: 28px; height: 28px; border-radius: 50%;
// // // // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // // // //   flex-shrink: 0; font-size: 11px; font-weight: 600;
// // // // // // // // // // }
// // // // // // // // // // .lp-use-hl-text {
// // // // // // // // // //   font-size: 13px; font-weight: 400; color: rgba(255,255,255,0.75);
// // // // // // // // // // }

// // // // // // // // // // /* ── PRICING ── */
// // // // // // // // // // .lp-pricing-wrap { display: flex; justify-content: center; margin-top: 1rem; }
// // // // // // // // // // .lp-pricing-card {
// // // // // // // // // //   background: var(--card);
// // // // // // // // // //   border: 0.5px solid rgba(212,175,55,0.25);
// // // // // // // // // //   border-radius: 28px; padding: 3rem 2.5rem;
// // // // // // // // // //   width: 100%; max-width: 420px;
// // // // // // // // // //   text-align: center; position: relative;
// // // // // // // // // // }
// // // // // // // // // // .lp-pricing-card::before {
// // // // // // // // // //   content: '';
// // // // // // // // // //   position: absolute; top: 0; left: 10%; right: 10%; height: 1px;
// // // // // // // // // //   background: linear-gradient(90deg, transparent, var(--gold), transparent);
// // // // // // // // // // }
// // // // // // // // // // .lp-pricing-badge {
// // // // // // // // // //   display: inline-block;
// // // // // // // // // //   background: linear-gradient(135deg, var(--gold), var(--gold2));
// // // // // // // // // //   color: #0A0A0A; border-radius: 100px;
// // // // // // // // // //   padding: 5px 16px; font-size: 11px; font-weight: 500;
// // // // // // // // // //   letter-spacing: 1px; text-transform: uppercase;
// // // // // // // // // //   margin-bottom: 2rem;
// // // // // // // // // // }
// // // // // // // // // // .lp-price-num {
// // // // // // // // // //   font-family: var(--serif); font-size: 5rem; font-weight: 300;
// // // // // // // // // //   color: var(--white); line-height: 1; letter-spacing: -2px;
// // // // // // // // // // }
// // // // // // // // // // .lp-price-period {
// // // // // // // // // //   font-size: 12px; font-weight: 400; color: var(--muted2);
// // // // // // // // // //   letter-spacing: 2px; text-transform: uppercase; margin: 0.5rem 0 2.5rem;
// // // // // // // // // // }
// // // // // // // // // // .lp-price-list { list-style: none; text-align: left; margin-bottom: 2.5rem; }
// // // // // // // // // // .lp-price-item {
// // // // // // // // // //   display: flex; align-items: center; gap: 10px;
// // // // // // // // // //   padding: 0.7rem 0; font-size: 14px; font-weight: 300;
// // // // // // // // // //   color: rgba(255,255,255,0.7);
// // // // // // // // // //   border-bottom: 0.5px solid var(--border);
// // // // // // // // // // }
// // // // // // // // // // .lp-price-check { color: var(--gold); font-size: 12px; flex-shrink: 0; }

// // // // // // // // // // /* ── CTA BANNER ── */
// // // // // // // // // // .lp-cta-banner {
// // // // // // // // // //   padding: 8rem 3rem;
// // // // // // // // // //   text-align: center;
// // // // // // // // // //   position: relative; overflow: hidden;
// // // // // // // // // // }
// // // // // // // // // // .lp-cta-banner::before {
// // // // // // // // // //   content: '';
// // // // // // // // // //   position: absolute; inset: 0;
// // // // // // // // // //   background: radial-gradient(ellipse 80% 60% at 50% 50%, rgba(212,175,55,0.07) 0%, transparent 70%);
// // // // // // // // // //   pointer-events: none;
// // // // // // // // // // }
// // // // // // // // // // .lp-cta-title {
// // // // // // // // // //   font-family: var(--serif);
// // // // // // // // // //   font-size: clamp(2.5rem, 6vw, 5rem);
// // // // // // // // // //   font-weight: 300; line-height: 1.1;
// // // // // // // // // //   color: var(--white); margin-bottom: 1.5rem;
// // // // // // // // // //   letter-spacing: -1px;
// // // // // // // // // // }
// // // // // // // // // // .lp-cta-title em { font-style: italic; color: var(--gold); }
// // // // // // // // // // .lp-cta-sub {
// // // // // // // // // //   font-size: 16px; font-weight: 300; color: var(--muted);
// // // // // // // // // //   margin-bottom: 3rem; line-height: 1.7;
// // // // // // // // // // }

// // // // // // // // // // /* ── FOOTER ── */
// // // // // // // // // // .lp-footer {
// // // // // // // // // //   padding: 3rem 3rem 4rem;
// // // // // // // // // //   border-top: 0.5px solid var(--border);
// // // // // // // // // // }
// // // // // // // // // // .lp-footer-inner {
// // // // // // // // // //   max-width: 1180px; margin: 0 auto;
// // // // // // // // // //   display: flex; flex-direction: column; align-items: center; gap: 1rem;
// // // // // // // // // // }
// // // // // // // // // // .lp-footer-logo {
// // // // // // // // // //   display: flex; align-items: center; gap: 10px;
// // // // // // // // // //   font-family: var(--sans); font-size: 15px; font-weight: 500; color: var(--white);
// // // // // // // // // // }
// // // // // // // // // // .lp-footer-logo em { color: var(--gold); font-style: normal; font-weight: 600; }
// // // // // // // // // // .lp-footer-tagline {
// // // // // // // // // //   font-size: 13px; font-weight: 300; color: var(--muted2);
// // // // // // // // // // }
// // // // // // // // // // .lp-footer-copy {
// // // // // // // // // //   font-size: 12px; font-weight: 300; color: rgba(255,255,255,0.18);
// // // // // // // // // // }

// // // // // // // // // // /* ── FADE-IN ON SCROLL ── */
// // // // // // // // // // .reveal {
// // // // // // // // // //   opacity: 0; transform: translateY(24px);
// // // // // // // // // //   transition: opacity 0.7s ease, transform 0.7s ease;
// // // // // // // // // // }
// // // // // // // // // // .reveal.visible { opacity: 1; transform: translateY(0); }

// // // // // // // // // // /* ── LIGHT THEME OVERRIDES ── */
// // // // // // // // // // .lp-light {
// // // // // // // // // //   --black: #F8F7F4;
// // // // // // // // // //   --dark: #F0EDE6;
// // // // // // // // // //   --card: #FFFFFF;
// // // // // // // // // //   --border: rgba(0,0,0,0.08);
// // // // // // // // // //   --white: #111111;
// // // // // // // // // //   --muted: rgba(0,0,0,0.5);
// // // // // // // // // //   --muted2: rgba(0,0,0,0.35);
// // // // // // // // // // }
// // // // // // // // // // .lp-light body,
// // // // // // // // // // .lp-light { background: #F8F7F4; color: #111; }
// // // // // // // // // // .lp-light ::-webkit-scrollbar-track { background: #F8F7F4; }
// // // // // // // // // // .lp-light ::-webkit-scrollbar-thumb { background: rgba(184,148,42,0.3); }

// // // // // // // // // // /* nav light */
// // // // // // // // // // .lp-light .lp-nav.scrolled {
// // // // // // // // // //   background: rgba(248,247,244,0.92);
// // // // // // // // // //   border-bottom: 0.5px solid rgba(184,148,42,0.18);
// // // // // // // // // // }
// // // // // // // // // // .lp-light .lp-logo { color: #111; }
// // // // // // // // // // .lp-light .lp-nav-link { color: rgba(0,0,0,0.45); }
// // // // // // // // // // .lp-light .lp-nav-link:hover { color: #111; }
// // // // // // // // // // .lp-light .lp-nav-cta { color: #0A0A0A; }

// // // // // // // // // // /* hero light */
// // // // // // // // // // .lp-light .lp-hero-title { color: #111; }
// // // // // // // // // // .lp-light .lp-hero-sub { color: rgba(0,0,0,0.5); }
// // // // // // // // // // .lp-light .lp-hero-stats { border-top-color: rgba(0,0,0,0.08); }
// // // // // // // // // // .lp-light .lp-stat-label { color: rgba(0,0,0,0.35); }
// // // // // // // // // // .lp-light .lp-btn-ghost {
// // // // // // // // // //   color: rgba(0,0,0,0.5); border-color: rgba(0,0,0,0.15);
// // // // // // // // // // }
// // // // // // // // // // .lp-light .lp-btn-ghost:hover { color: #111; border-color: rgba(184,148,42,0.4); }
// // // // // // // // // // .lp-light section[style*="background: var(--black)"] { background: #F8F7F4 !important; }

// // // // // // // // // // /* hero scan phones stay dark — intentional: they're device mockups */
// // // // // // // // // // .lp-light .phone-scanner { background: #1A1A1A; }
// // // // // // // // // // .lp-light .phone-profile { background: #0E0E0E; }

// // // // // // // // // // /* trust bar */
// // // // // // // // // // .lp-light .lp-trust { background: #F0EDE6; }
// // // // // // // // // // .lp-light .lp-trust-label { color: rgba(0,0,0,0.35); }
// // // // // // // // // // .lp-light .lp-trust-item { color: rgba(0,0,0,0.55); }
// // // // // // // // // // .lp-light .lp-trust-sep { color: rgba(184,148,42,0.3); }

// // // // // // // // // // /* gold rule */
// // // // // // // // // // .lp-light .gold-rule { background: linear-gradient(90deg, transparent, rgba(184,148,42,0.25), transparent); }

// // // // // // // // // // /* sections */
// // // // // // // // // // .lp-light .lp-section { background: #F8F7F4; }
// // // // // // // // // // .lp-light .lp-section-title { color: #111; }
// // // // // // // // // // .lp-light .lp-section-sub { color: rgba(0,0,0,0.5); }
// // // // // // // // // // .lp-light .lp-eyebrow { color: #B8942A; }

// // // // // // // // // // /* steps */
// // // // // // // // // // .lp-light .lp-step { background: #FFFFFF; }
// // // // // // // // // // .lp-light .lp-step:hover { background: #F5F2EC; }
// // // // // // // // // // .lp-light .lp-steps { border-color: rgba(0,0,0,0.08); }
// // // // // // // // // // .lp-light .lp-step-title { color: #111; }
// // // // // // // // // // .lp-light .lp-step-desc { color: rgba(0,0,0,0.5); }
// // // // // // // // // // .lp-light .lp-step-num { color: rgba(184,148,42,0.15); }
// // // // // // // // // // .lp-light .lp-step-icon-wrap { background: rgba(184,148,42,0.08); border-color: rgba(184,148,42,0.2); }

// // // // // // // // // // /* features */
// // // // // // // // // // .lp-light .lp-feat-grid { border-color: rgba(0,0,0,0.07); }
// // // // // // // // // // .lp-light .lp-feat-card { background: #FFFFFF; }
// // // // // // // // // // .lp-light .lp-feat-card:hover { background: #F5F2EC; }
// // // // // // // // // // .lp-light .lp-feat-title { color: #111; }
// // // // // // // // // // .lp-light .lp-feat-desc { color: rgba(0,0,0,0.5); }

// // // // // // // // // // /* use cases */
// // // // // // // // // // .lp-light .lp-use-tab { color: rgba(0,0,0,0.45) !important; border-color: rgba(0,0,0,0.1) !important; }
// // // // // // // // // // .lp-light .lp-use-tab[style*="rgba(212,175,55,0.1)"] { color: #B8942A !important; border-color: rgba(184,148,42,0.4) !important; }
// // // // // // // // // // .lp-light .lp-use-panel { background: #FFFFFF; border-color: rgba(184,148,42,0.15) !important; }
// // // // // // // // // // .lp-light .lp-use-tagline { color: #111; }
// // // // // // // // // // .lp-light .lp-use-desc { color: rgba(0,0,0,0.5); }
// // // // // // // // // // .lp-light .lp-use-hl { background: #F8F7F4; border-color: rgba(0,0,0,0.07); }
// // // // // // // // // // .lp-light .lp-use-hl-text { color: rgba(0,0,0,0.65); }

// // // // // // // // // // /* pricing */
// // // // // // // // // // .lp-light .lp-pricing-card { background: #FFFFFF; border-color: rgba(184,148,42,0.25); }
// // // // // // // // // // .lp-light .lp-pricing-card::before { background: linear-gradient(90deg, transparent, #B8942A, transparent); }
// // // // // // // // // // .lp-light .lp-price-num { color: #111; }
// // // // // // // // // // .lp-light .lp-price-period { color: rgba(0,0,0,0.35); }
// // // // // // // // // // .lp-light .lp-price-item { color: rgba(0,0,0,0.65); border-bottom-color: rgba(0,0,0,0.07); }

// // // // // // // // // // /* cta banner */
// // // // // // // // // // .lp-light .lp-cta-banner { background: #F0EDE6; }
// // // // // // // // // // .lp-light .lp-cta-title { color: #111; }
// // // // // // // // // // .lp-light .lp-cta-sub { color: rgba(0,0,0,0.5); }

// // // // // // // // // // /* footer */
// // // // // // // // // // .lp-light .lp-footer { border-top-color: rgba(0,0,0,0.08); background: #ECEAE4; }
// // // // // // // // // // .lp-light .lp-footer-logo { color: #111; }
// // // // // // // // // // .lp-light .lp-footer-tagline { color: rgba(0,0,0,0.4); }
// // // // // // // // // // .lp-light .lp-footer-copy { color: rgba(0,0,0,0.25); }

// // // // // // // // // // /* theme toggle btn */
// // // // // // // // // // .lp-theme-btn {
// // // // // // // // // //   width: 40px; height: 40px; border-radius: 50%;
// // // // // // // // // //   border: 0.5px solid rgba(212,175,55,0.35);
// // // // // // // // // //   background: rgba(212,175,55,0.08);
// // // // // // // // // //   cursor: pointer; display: flex; align-items: center; justify-content: center;
// // // // // // // // // //   transition: background 0.2s, transform 0.15s;
// // // // // // // // // //   flex-shrink: 0;
// // // // // // // // // // }
// // // // // // // // // // .lp-theme-btn:hover { background: rgba(212,175,55,0.15); transform: scale(1.08); }
// // // // // // // // // // .lp-light .lp-theme-btn {
// // // // // // // // // //   border-color: rgba(184,148,42,0.3);
// // // // // // // // // //   background: rgba(184,148,42,0.08);
// // // // // // // // // // }
// // // // // // // // // // .lp-light .lp-theme-btn:hover { background: rgba(184,148,42,0.15); }

// // // // // // // // // // /* ── MOBILE ── */
// // // // // // // // // // @media (max-width: 900px) {
// // // // // // // // // //   .lp-hero { grid-template-columns: 1fr; text-align: center; padding: 8rem 1.5rem 4rem; }
// // // // // // // // // //   .lp-hero-sub { margin: 0 auto 3rem; }
// // // // // // // // // //   .lp-hero-ctas { justify-content: center; }
// // // // // // // // // //   .lp-hero-stats { justify-content: center; }
// // // // // // // // // //   .lp-hero-right { margin-top: 3rem; }
// // // // // // // // // //   .scan-scene { width: 300px; height: 380px; transform: scale(0.85); }
// // // // // // // // // //   .lp-steps { grid-template-columns: 1fr; }
// // // // // // // // // //   .lp-feat-grid { grid-template-columns: 1fr 1fr; }
// // // // // // // // // //   .lp-use-panel { grid-template-columns: 1fr; }
// // // // // // // // // //   .lp-nav { padding: 1rem 1.5rem; }
// // // // // // // // // //   .lp-nav.scrolled { padding: 0.75rem 1.5rem; }
// // // // // // // // // //   .lp-nav-links { gap: 1rem; }
// // // // // // // // // //   .lp-section { padding: 5rem 1.5rem; }
// // // // // // // // // // }
// // // // // // // // // // @media (max-width: 600px) {
// // // // // // // // // //   .lp-feat-grid { grid-template-columns: 1fr; }
// // // // // // // // // //   .lp-nav-link { display: none; }
// // // // // // // // // // }
// // // // // // // // // // `;

// // // // // // // // // // /* ─────────────────────────────────────────
// // // // // // // // // //    QR SCAN ANIMATION
// // // // // // // // // // ───────────────────────────────────────── */
// // // // // // // // // // const QR_PATTERN = [
// // // // // // // // // //     [1, 1, 1, 0, 1, 0], [1, 0, 1, 1, 0, 1], [1, 1, 1, 0, 1, 1], [0, 1, 0, 1, 0, 0], [1, 0, 1, 1, 1, 0], [0, 1, 0, 0, 1, 1]
// // // // // // // // // // ];

// // // // // // // // // // function ScanScene() {
// // // // // // // // // //     return (
// // // // // // // // // //         <div className="scan-scene">
// // // // // // // // // //             <div className="hero-orb-1" />

// // // // // // // // // //             {/* Phone A — scanner */}
// // // // // // // // // //             <div className="phone-scanner">
// // // // // // // // // //                 <div className="phone-scanner-screen">
// // // // // // // // // //                     <div className="scanner-viewfinder">
// // // // // // // // // //                         <div className="vf-corner-tr" />
// // // // // // // // // //                         <div className="vf-corner-bl" />
// // // // // // // // // //                         <div className="scan-laser" />
// // // // // // // // // //                         {/* mini qr */}
// // // // // // // // // //                         <div className="mini-qr">
// // // // // // // // // //                             {QR_PATTERN.map((row, ri) =>
// // // // // // // // // //                                 row.map((cell, ci) => (
// // // // // // // // // //                                     <div key={`${ri}-${ci}`} className="mini-qr-cell"
// // // // // // // // // //                                         style={{ background: cell ? "rgba(212,175,55,0.5)" : "transparent" }} />
// // // // // // // // // //                                 ))
// // // // // // // // // //                             )}
// // // // // // // // // //                         </div>
// // // // // // // // // //                     </div>
// // // // // // // // // //                     <div className="scanner-label">Point at QR code</div>
// // // // // // // // // //                 </div>
// // // // // // // // // //             </div>

// // // // // // // // // //             {/* Beam + particle */}
// // // // // // // // // //             <div className="beam-line" />
// // // // // // // // // //             <div className="beam-particle" />

// // // // // // // // // //             {/* Phone B — profile card opening */}
// // // // // // // // // //             <div className="phone-profile">
// // // // // // // // // //                 <div className="profile-card-wrap">
// // // // // // // // // //                     {/* Banner */}
// // // // // // // // // //                     <div className="profile-banner">
// // // // // // // // // //                         <div className="profile-banner-img" />
// // // // // // // // // //                         <div className="profile-banner-fade" />
// // // // // // // // // //                         {/* floating avatar */}
// // // // // // // // // //                         <div className="profile-avatar-ring">
// // // // // // // // // //                             <div className="profile-avatar-inner">MG</div>
// // // // // // // // // //                         </div>
// // // // // // // // // //                     </div>

// // // // // // // // // //                     {/* Info */}
// // // // // // // // // //                     <div className="profile-info">
// // // // // // // // // //                         <div className="profile-info-name">
// // // // // // // // // //                             Mragank <em>Gaur</em>
// // // // // // // // // //                         </div>
// // // // // // // // // //                         <div className="profile-info-role">Director · Empower Pragati</div>

// // // // // // // // // //                         <div className="profile-tiles">
// // // // // // // // // //                             {[["📞", "Phone"], ["✉️", "Email"], ["📍", "Delhi"]].map(([icon, label]) => (
// // // // // // // // // //                                 <div className="profile-tile" key={label}>
// // // // // // // // // //                                     <div className="tile-dot"><div className="tile-dot-inner" /></div>
// // // // // // // // // //                                     <div className="tile-text">{label}</div>
// // // // // // // // // //                                 </div>
// // // // // // // // // //                             ))}
// // // // // // // // // //                         </div>

// // // // // // // // // //                         <button className="profile-save-btn">
// // // // // // // // // //                             ↓ Save Contact
// // // // // // // // // //                         </button>
// // // // // // // // // //                     </div>
// // // // // // // // // //                 </div>
// // // // // // // // // //             </div>
// // // // // // // // // //         </div>
// // // // // // // // // //     );
// // // // // // // // // // }

// // // // // // // // // // /* ─────────────────────────────────────────
// // // // // // // // // //    USE CASES
// // // // // // // // // // ───────────────────────────────────────── */
// // // // // // // // // // const USE_CASES = [
// // // // // // // // // //     {
// // // // // // // // // //         icon: "👔", role: "Job Seekers", gold: "rgba(212,175,55,1)",
// // // // // // // // // //         tagline: "Land your next opportunity faster",
// // // // // // // // // //         desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one elegant scan.",
// // // // // // // // // //         highlights: ["Resume attached & downloadable", "LinkedIn & GitHub links", "QR code for instant sharing", "Save contact in one tap"]
// // // // // // // // // //     },
// // // // // // // // // //     {
// // // // // // // // // //         icon: "💼", role: "Freelancers", gold: "rgba(212,175,55,1)",
// // // // // // // // // //         tagline: "Win clients before you even speak",
// // // // // // // // // //         desc: "Share everything a client needs — portfolio, social proof, and contact — all in a single professional link. Close deals faster.",
// // // // // // // // // //         highlights: ["Portfolio & website link", "WhatsApp quick contact", "Instagram & social proof", "Professional digital presence"]
// // // // // // // // // //     },
// // // // // // // // // //     {
// // // // // // // // // //         icon: "🏢", role: "Business Owners", gold: "rgba(212,175,55,1)",
// // // // // // // // // //         tagline: "Network smarter, not harder",
// // // // // // // // // //         desc: "Stop spending on printed cards that get lost. Share via QR at events — always updated, never outdated, never thrown away.",
// // // // // // // // // //         highlights: ["Always up-to-date details", "Google Wallet for offline use", "QR code for events & meetups", "Share via WhatsApp or email"]
// // // // // // // // // //     },
// // // // // // // // // //     {
// // // // // // // // // //         icon: "🎓", role: "Students", gold: "rgba(212,175,55,1)",
// // // // // // // // // //         tagline: "Build your professional identity early",
// // // // // // // // // //         desc: "Stand out in campus placements and internship drives. Show your skills, projects, and social profiles before you say a word.",
// // // // // // // // // //         highlights: ["GitHub & project links", "Resume upload for placements", "LinkedIn profile showcase", "Free forever — no cost"]
// // // // // // // // // //     },
// // // // // // // // // //     {
// // // // // // // // // //         icon: "📣", role: "Sales Pros", gold: "rgba(212,175,55,1)",
// // // // // // // // // //         tagline: "Close the follow-up gap instantly",
// // // // // // // // // //         desc: "At trade shows or cold visits — one QR scan and your prospect has everything. Number, email, website, product links in one place.",
// // // // // // // // // //         highlights: ["Instant QR share at events", "Website & product links", "Save contact in one tap", "Google Wallet for quick access"]
// // // // // // // // // //     },
// // // // // // // // // //     {
// // // // // // // // // //         icon: "🧑‍💻", role: "Developers", gold: "rgba(212,175,55,1)",
// // // // // // // // // //         tagline: "Your entire dev profile in one link",
// // // // // // // // // //         desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share at hackathons or in open source communities.",
// // // // // // // // // //         highlights: ["GitHub & portfolio links", "Twitter / X profile", "Resume for job applications", "Professional dev identity"]
// // // // // // // // // //     },
// // // // // // // // // // ];

// // // // // // // // // // function UseCasesSection({ navigate }) {
// // // // // // // // // //     const [active, setActive] = useState(0);
// // // // // // // // // //     const cur = USE_CASES[active];
// // // // // // // // // //     return (
// // // // // // // // // //         <section className="lp-section">
// // // // // // // // // //             <div className="lp-section-inner">
// // // // // // // // // //                 <span className="lp-eyebrow reveal">Who is it for</span>
// // // // // // // // // //                 <h2 className="lp-section-title reveal">Built for every <em>professional</em></h2>
// // // // // // // // // //                 <p className="lp-section-sub reveal">Whether you're a student or a CEO — your digital card works for you.</p>

// // // // // // // // // //                 <div className="lp-use-tabs reveal">
// // // // // // // // // //                     {USE_CASES.map((u, i) => (
// // // // // // // // // //                         <button key={i} className="lp-use-tab" onClick={() => setActive(i)}
// // // // // // // // // //                             style={{
// // // // // // // // // //                                 border: active === i ? `0.5px solid rgba(212,175,55,0.5)` : "0.5px solid rgba(255,255,255,0.1)",
// // // // // // // // // //                                 background: active === i ? "rgba(212,175,55,0.1)" : "transparent",
// // // // // // // // // //                                 color: active === i ? "var(--gold)" : "var(--muted)",
// // // // // // // // // //                             }}>
// // // // // // // // // //                             {u.icon} {u.role}
// // // // // // // // // //                         </button>
// // // // // // // // // //                     ))}
// // // // // // // // // //                 </div>

// // // // // // // // // //                 <div className="lp-use-panel"
// // // // // // // // // //                     style={{ borderColor: "rgba(212,175,55,0.15)" }}>
// // // // // // // // // //                     <div className="lp-use-panel::before" style={{}} />
// // // // // // // // // //                     <div>
// // // // // // // // // //                         <div className="lp-use-badge"
// // // // // // // // // //                             style={{ background: "rgba(212,175,55,0.1)", color: "var(--gold)", border: "0.5px solid rgba(212,175,55,0.25)" }}>
// // // // // // // // // //                             <span>{cur.icon}</span><span style={{ fontSize: "12px", letterSpacing: "0.5px" }}>{cur.role}</span>
// // // // // // // // // //                         </div>
// // // // // // // // // //                         <div className="lp-use-tagline">{cur.tagline}</div>
// // // // // // // // // //                         <p className="lp-use-desc">{cur.desc}</p>
// // // // // // // // // //                         <button className="lp-btn-gold" onClick={() => navigate("/animation/card")}>
// // // // // // // // // //                             Create My Card — Free →
// // // // // // // // // //                         </button>
// // // // // // // // // //                     </div>
// // // // // // // // // //                     <div className="lp-use-highlights">
// // // // // // // // // //                         {cur.highlights.map((h, i) => (
// // // // // // // // // //                             <div className="lp-use-hl" key={i}>
// // // // // // // // // //                                 <div className="lp-use-hl-check"
// // // // // // // // // //                                     style={{ background: "rgba(212,175,55,0.1)", color: "var(--gold)" }}>✓</div>
// // // // // // // // // //                                 <span className="lp-use-hl-text">{h}</span>
// // // // // // // // // //                             </div>
// // // // // // // // // //                         ))}
// // // // // // // // // //                     </div>
// // // // // // // // // //                 </div>
// // // // // // // // // //             </div>
// // // // // // // // // //         </section>
// // // // // // // // // //     );
// // // // // // // // // // }

// // // // // // // // // // /* ─────────────────────────────────────────
// // // // // // // // // //    MAIN COMPONENT
// // // // // // // // // // ───────────────────────────────────────── */
// // // // // // // // // // export default function LandingPage() {
// // // // // // // // // //     const navigate = useNavigate();
// // // // // // // // // //     const [scrolled, setScrolled] = useState(false);
// // // // // // // // // //     const [isLight, setIsLight] = useState(false);

// // // // // // // // // //     /* scroll nav */
// // // // // // // // // //     useEffect(() => {
// // // // // // // // // //         const onScroll = () => setScrolled(window.scrollY > 40);
// // // // // // // // // //         window.addEventListener("scroll", onScroll);
// // // // // // // // // //         return () => window.removeEventListener("scroll", onScroll);
// // // // // // // // // //     }, []);

// // // // // // // // // //     /* scroll reveal */
// // // // // // // // // //     useEffect(() => {
// // // // // // // // // //         const els = document.querySelectorAll(".reveal");
// // // // // // // // // //         const observer = new IntersectionObserver(
// // // // // // // // // //             (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
// // // // // // // // // //             { threshold: 0.12 }
// // // // // // // // // //         );
// // // // // // // // // //         els.forEach(el => observer.observe(el));
// // // // // // // // // //         return () => observer.disconnect();
// // // // // // // // // //     }, []);

// // // // // // // // // //     return (
// // // // // // // // // //         <div className={isLight ? "lp-light" : ""} style={{ background: isLight ? "#F8F7F4" : "#080808", minHeight: "100vh" }}>
// // // // // // // // // //             <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

// // // // // // // // // //             {/* ── NAV ── */}
// // // // // // // // // //             <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
// // // // // // // // // //                 <div className="lp-nav-inner">
// // // // // // // // // //                     <a href="/" className="lp-logo">
// // // // // // // // // //                         {/* inline logo icon */}
// // // // // // // // // //                         <svg className="lp-logo-icon" viewBox="0 0 34 34" fill="none">
// // // // // // // // // //                             <rect width="34" height="34" rx="9" fill="#1A1A1A" />
// // // // // // // // // //                             <rect width="34" height="34" rx="9" stroke="rgba(212,175,55,0.2)" strokeWidth="0.5" />
// // // // // // // // // //                             <circle cx="15" cy="13" r="5" stroke="#D4AF37" strokeWidth="1.5" fill="none" />
// // // // // // // // // //                             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" fill="none" />
// // // // // // // // // //                             <path d="M23 11 L27 11 L27 23" stroke="rgba(212,175,55,0.5)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
// // // // // // // // // //                             <circle cx="27" cy="23" r="1.8" fill="#D4AF37" />
// // // // // // // // // //                         </svg>
// // // // // // // // // //                         myprofile<em>corner</em>
// // // // // // // // // //                     </a>
// // // // // // // // // //                     <div className="lp-nav-links">
// // // // // // // // // //                         <a href="#how" className="lp-nav-link">How it works</a>
// // // // // // // // // //                         <a href="#features" className="lp-nav-link">Features</a>
// // // // // // // // // //                         <a href="#pricing" className="lp-nav-link">Pricing</a>
// // // // // // // // // //                         {/* ── Theme Toggle ── */}
// // // // // // // // // //                         <button
// // // // // // // // // //                             className="lp-theme-btn"
// // // // // // // // // //                             onClick={() => setIsLight(p => !p)}
// // // // // // // // // //                             title={isLight ? "Switch to Dark" : "Switch to Light"}
// // // // // // // // // //                             aria-label="Toggle theme"
// // // // // // // // // //                         >
// // // // // // // // // //                             {isLight ? (
// // // // // // // // // //                                 /* Moon — switch to dark */
// // // // // // // // // //                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B8942A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
// // // // // // // // // //                                     <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
// // // // // // // // // //                                 </svg>
// // // // // // // // // //                             ) : (
// // // // // // // // // //                                 /* Sun — switch to light */
// // // // // // // // // //                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
// // // // // // // // // //                                     <circle cx="12" cy="12" r="5" />
// // // // // // // // // //                                     <line x1="12" y1="1" x2="12" y2="3" />
// // // // // // // // // //                                     <line x1="12" y1="21" x2="12" y2="23" />
// // // // // // // // // //                                     <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
// // // // // // // // // //                                     <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
// // // // // // // // // //                                     <line x1="1" y1="12" x2="3" y2="12" />
// // // // // // // // // //                                     <line x1="21" y1="12" x2="23" y2="12" />
// // // // // // // // // //                                     <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
// // // // // // // // // //                                     <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
// // // // // // // // // //                                 </svg>
// // // // // // // // // //                             )}
// // // // // // // // // //                         </button>
// // // // // // // // // //                         <button className="lp-nav-cta" onClick={() => navigate("/animation/card")}>
// // // // // // // // // //                             Create Free Card
// // // // // // // // // //                         </button>
// // // // // // // // // //                     </div>
// // // // // // // // // //                 </div>
// // // // // // // // // //             </nav>

// // // // // // // // // //             {/* ── HERO ── */}
// // // // // // // // // //             <section style={{ background: isLight ? "#F8F7F4" : "#080808", overflow: "hidden", position: "relative" }}>
// // // // // // // // // //                 <div className="lp-hero">
// // // // // // // // // //                     {/* ambient bg orbs */}
// // // // // // // // // //                     <div style={{
// // // // // // // // // //                         position: "absolute", width: "600px", height: "600px", borderRadius: "50%",
// // // // // // // // // //                         background: "radial-gradient(circle, rgba(212,175,55,0.05) 0%, transparent 65%)",
// // // // // // // // // //                         top: "-200px", left: "-100px", pointerEvents: "none"
// // // // // // // // // //                     }} />

// // // // // // // // // //                     {/* LEFT */}
// // // // // // // // // //                     <div className="lp-hero-left">
// // // // // // // // // //                         <div className="lp-hero-eyebrow">
// // // // // // // // // //                             <div className="lp-hero-eyebrow-dot" />
// // // // // // // // // //                             100% Free · No App Needed
// // // // // // // // // //                         </div>

// // // // // // // // // //                         <h1 className="lp-hero-title">
// // // // // // // // // //                             Your Digital<br />
// // // // // // // // // //                             Identity, <em>Always</em><br />
// // // // // // // // // //                             in Your Pocket
// // // // // // // // // //                         </h1>

// // // // // // // // // //                         <p className="lp-hero-sub">
// // // // // // // // // //                             Replace paper visiting cards with a smart digital card. Share your profile
// // // // // // // // // //                             instantly via QR code, link, or Google Wallet — no printing, no waste.
// // // // // // // // // //                         </p>

// // // // // // // // // //                         <div className="lp-hero-ctas">
// // // // // // // // // //                             <button className="lp-btn-gold" onClick={() => navigate("/animation/card")}>
// // // // // // // // // //                                 Create My Card — It's Free →
// // // // // // // // // //                             </button>
// // // // // // // // // //                             <a href="#how" className="lp-btn-ghost">See how it works</a>
// // // // // // // // // //                         </div>

// // // // // // // // // //                         <div className="lp-hero-stats">
// // // // // // // // // //                             {[["100%", "Free forever"], ["2 min", "Setup time"], ["0", "Paper waste"]].map(([n, l]) => (
// // // // // // // // // //                                 <div key={l}>
// // // // // // // // // //                                     <div className="lp-stat-num">{n}</div>
// // // // // // // // // //                                     <div className="lp-stat-label">{l}</div>
// // // // // // // // // //                                 </div>
// // // // // // // // // //                             ))}
// // // // // // // // // //                         </div>
// // // // // // // // // //                     </div>

// // // // // // // // // //                     {/* RIGHT — animated scan scene */}
// // // // // // // // // //                     <div className="lp-hero-right">
// // // // // // // // // //                         <ScanScene />
// // // // // // // // // //                     </div>
// // // // // // // // // //                 </div>
// // // // // // // // // //             </section>

// // // // // // // // // //             <div className="gold-rule" />

// // // // // // // // // //             {/* ── TRUST BAR ── */}
// // // // // // // // // //             <div className="lp-trust" style={{ background: isLight ? "#F0EDE6" : "transparent" }}>
// // // // // // // // // //                 <span className="lp-trust-label">Works seamlessly with</span>
// // // // // // // // // //                 <div className="lp-trust-items">
// // // // // // // // // //                     {[["🔥", "Firebase"], ["🪙", "Google Wallet"], ["📱", "Android & iOS"], ["🔗", "Any Browser"]].map(([icon, label], i, arr) => (
// // // // // // // // // //                         <React.Fragment key={label}>
// // // // // // // // // //                             <div className="lp-trust-item"><span>{icon}</span><span>{label}</span></div>
// // // // // // // // // //                             {i < arr.length - 1 && <span className="lp-trust-sep">·</span>}
// // // // // // // // // //                         </React.Fragment>
// // // // // // // // // //                     ))}
// // // // // // // // // //                 </div>
// // // // // // // // // //             </div>

// // // // // // // // // //             <div className="gold-rule" />

// // // // // // // // // //             {/* ── HOW IT WORKS ── */}
// // // // // // // // // //             <section id="how" className="lp-section">
// // // // // // // // // //                 <div className="lp-section-inner">
// // // // // // // // // //                     <span className="lp-eyebrow reveal">How it works</span>
// // // // // // // // // //                     <h2 className="lp-section-title reveal">From setup to sharing <em>in 3 steps</em></h2>
// // // // // // // // // //                     <p className="lp-section-sub reveal">No app downloads. No tech skills required. Just fill, share, impress.</p>

// // // // // // // // // //                     <div className="lp-steps reveal">
// // // // // // // // // //                         {[
// // // // // // // // // //                             { num: "01", icon: "✏️", title: "Fill your profile", desc: "Add your name, photo, contact details, social links, resume, and website. Takes less than 2 minutes." },
// // // // // // // // // //                             { num: "02", icon: "🎴", title: "Get your digital card", desc: "Instantly receive a beautiful card with a unique QR code and shareable link — no design skills needed." },
// // // // // // // // // //                             { num: "03", icon: "🚀", title: "Share anywhere", desc: "Show your QR in meetings, add to Google Wallet, or send your link on WhatsApp, email, or LinkedIn." },
// // // // // // // // // //                         ].map((step) => (
// // // // // // // // // //                             <div className="lp-step" key={step.num}>
// // // // // // // // // //                                 <div className="lp-step-num">{step.num}</div>
// // // // // // // // // //                                 <div className="lp-step-icon-wrap">{step.icon}</div>
// // // // // // // // // //                                 <div className="lp-step-title">{step.title}</div>
// // // // // // // // // //                                 <p className="lp-step-desc">{step.desc}</p>
// // // // // // // // // //                             </div>
// // // // // // // // // //                         ))}
// // // // // // // // // //                     </div>
// // // // // // // // // //                 </div>
// // // // // // // // // //             </section>

// // // // // // // // // //             <div className="gold-rule" />

// // // // // // // // // //             {/* ── FEATURES ── */}
// // // // // // // // // //             <section id="features" className="lp-section">
// // // // // // // // // //                 <div className="lp-section-inner">
// // // // // // // // // //                     <span className="lp-eyebrow reveal">Features</span>
// // // // // // // // // //                     <h2 className="lp-section-title reveal">Everything a modern <em>professional</em> needs</h2>

// // // // // // // // // //                     <div className="lp-feat-grid reveal">
// // // // // // // // // //                         {[
// // // // // // // // // //                             { icon: "📸", title: "Profile Photo", desc: "Upload your photo — it appears on your card and even in your Google Wallet pass." },
// // // // // // // // // //                             { icon: "🔗", title: "All Social Links", desc: "LinkedIn, GitHub, Instagram, Twitter, WhatsApp, YouTube — show what matters." },
// // // // // // // // // //                             { icon: "📄", title: "Resume Attachment", desc: "Attach your PDF resume directly to your card. Recruiters can download it instantly." },
// // // // // // // // // //                             { icon: "🪙", title: "Google Wallet", desc: "Add your card to Google Wallet and show it offline anytime, anywhere." },
// // // // // // // // // //                             { icon: "📲", title: "QR Code", desc: "Every card gets a unique QR code. One scan opens your full profile instantly." },
// // // // // // // // // //                             { icon: "💾", title: "Save Contact", desc: "Visitors can save your details directly to their phone contacts with one tap." },
// // // // // // // // // //                             { icon: "✏️", title: "Edit Anytime", desc: "Changed jobs? New number? Update your card instantly — the link stays the same." },
// // // // // // // // // //                             { icon: "🌐", title: "Works Everywhere", desc: "Your card opens in any browser on any device. No app install required." },
// // // // // // // // // //                         ].map((f) => (
// // // // // // // // // //                             <div className="lp-feat-card" key={f.title}>
// // // // // // // // // //                                 <span className="lp-feat-icon">{f.icon}</span>
// // // // // // // // // //                                 <div className="lp-feat-title">{f.title}</div>
// // // // // // // // // //                                 <p className="lp-feat-desc">{f.desc}</p>
// // // // // // // // // //                             </div>
// // // // // // // // // //                         ))}
// // // // // // // // // //                     </div>
// // // // // // // // // //                 </div>
// // // // // // // // // //             </section>

// // // // // // // // // //             <div className="gold-rule" />

// // // // // // // // // //             {/* ── USE CASES ── */}
// // // // // // // // // //             <UseCasesSection navigate={navigate} />

// // // // // // // // // //             <div className="gold-rule" />

// // // // // // // // // //             {/* ── PRICING ── */}
// // // // // // // // // //             <section id="pricing" className="lp-section">
// // // // // // // // // //                 <div className="lp-section-inner">
// // // // // // // // // //                     <span className="lp-eyebrow reveal">Pricing</span>
// // // // // // // // // //                     <h2 className="lp-section-title reveal">Simple, <em>honest</em> pricing</h2>
// // // // // // // // // //                     <p className="lp-section-sub reveal">No hidden charges. No credit card needed.</p>

// // // // // // // // // //                     <div className="lp-pricing-wrap reveal">
// // // // // // // // // //                         <div className="lp-pricing-card">
// // // // // // // // // //                             <div className="lp-pricing-badge">Most Popular</div>
// // // // // // // // // //                             <div className="lp-price-num">₹0</div>
// // // // // // // // // //                             <div className="lp-price-period">Forever Free</div>
// // // // // // // // // //                             <ul className="lp-price-list">
// // // // // // // // // //                                 {["1 Digital Visiting Card", "Unique QR Code & Link", "Profile Photo Upload", "Resume Upload", "All Social Media Links", "Google Wallet Pass", "Save Contact (.vcf)", "Edit Anytime", "No App Required"].map(item => (
// // // // // // // // // //                                     <li className="lp-price-item" key={item}>
// // // // // // // // // //                                         <span className="lp-price-check">✦</span>{item}
// // // // // // // // // //                                     </li>
// // // // // // // // // //                                 ))}
// // // // // // // // // //                             </ul>
// // // // // // // // // //                             <button className="lp-btn-gold" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/animation/card")}>
// // // // // // // // // //                                 Get Started Free →
// // // // // // // // // //                             </button>
// // // // // // // // // //                         </div>
// // // // // // // // // //                     </div>
// // // // // // // // // //                 </div>
// // // // // // // // // //             </section>

// // // // // // // // // //             <div className="gold-rule" />

// // // // // // // // // //             {/* ── CTA BANNER ── */}
// // // // // // // // // //             <section className="lp-cta-banner">
// // // // // // // // // //                 <h2 className="lp-cta-title reveal">
// // // // // // // // // //                     Stop carrying<br /><em>paper cards.</em>
// // // // // // // // // //                 </h2>
// // // // // // // // // //                 <p className="lp-cta-sub reveal">Create your digital card in under 2 minutes. Free, forever.</p>
// // // // // // // // // //                 <button className="lp-btn-gold reveal" style={{ margin: "0 auto" }} onClick={() => navigate("/animation/card")}>
// // // // // // // // // //                     Create My Card Now →
// // // // // // // // // //                 </button>
// // // // // // // // // //             </section>

// // // // // // // // // //             <div className="gold-rule" />

// // // // // // // // // //             {/* ── FOOTER ── */}
// // // // // // // // // //             <footer className="lp-footer">
// // // // // // // // // //                 <div className="lp-footer-inner">
// // // // // // // // // //                     <div className="lp-footer-logo">
// // // // // // // // // //                         <svg width="28" height="28" viewBox="0 0 34 34" fill="none">
// // // // // // // // // //                             <rect width="34" height="34" rx="9" fill="#1A1A1A" />
// // // // // // // // // //                             <rect width="34" height="34" rx="9" stroke="rgba(212,175,55,0.2)" strokeWidth="0.5" />
// // // // // // // // // //                             <circle cx="15" cy="13" r="5" stroke="#D4AF37" strokeWidth="1.5" fill="none" />
// // // // // // // // // //                             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" fill="none" />
// // // // // // // // // //                             <path d="M23 11 L27 11 L27 23" stroke="rgba(212,175,55,0.5)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
// // // // // // // // // //                             <circle cx="27" cy="23" r="1.8" fill="#D4AF37" />
// // // // // // // // // //                         </svg>
// // // // // // // // // //                         myprofile<em>corner</em>
// // // // // // // // // //                     </div>
// // // // // // // // // //                     <p className="lp-footer-tagline">Your digital identity, always in your pocket.</p>
// // // // // // // // // //                     <p className="lp-footer-copy">© {new Date().getFullYear()} MyProfileCorner · Made with ♥ in India</p>
// // // // // // // // // //                 </div>
// // // // // // // // // //             </footer>
// // // // // // // // // //         </div>
// // // // // // // // // //     );
// // // // // // // // // // }


// // // // // // // // // // // import React, { useState, useEffect, useRef } from "react";
// // // // // // // // // // // import { useNavigate } from "react-router-dom";

// // // // // // // // // // // /* ─────────────────────────────────────────
// // // // // // // // // // //    FONTS injected once
// // // // // // // // // // // ───────────────────────────────────────── */
// // // // // // // // // // // const FONT_LINK = `
// // // // // // // // // // //   @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap');
// // // // // // // // // // // `;

// // // // // // // // // // // /* ─────────────────────────────────────────
// // // // // // // // // // //    GLOBAL CSS
// // // // // // // // // // // ───────────────────────────────────────── */
// // // // // // // // // // // const GLOBAL_CSS = `
// // // // // // // // // // // ${FONT_LINK}

// // // // // // // // // // // *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

// // // // // // // // // // // :root {
// // // // // // // // // // //   --black: #080808;
// // // // // // // // // // //   --dark: #0E0E0E;
// // // // // // // // // // //   --card: #141414;
// // // // // // // // // // //   --border: rgba(255,255,255,0.07);
// // // // // // // // // // //   --gold: #D4AF37;
// // // // // // // // // // //   --gold2: #F0D060;
// // // // // // // // // // //   --gold3: #C9A227;
// // // // // // // // // // //   --gold-dim: rgba(212,175,55,0.18);
// // // // // // // // // // //   --white: #FFFFFF;
// // // // // // // // // // //   --muted: rgba(255,255,255,0.45);
// // // // // // // // // // //   --muted2: rgba(255,255,255,0.25);
// // // // // // // // // // //   --serif: 'Cormorant Garamond', Georgia, serif;
// // // // // // // // // // //   --sans: 'DM Sans', sans-serif;
// // // // // // // // // // // }

// // // // // // // // // // // html { scroll-behavior: smooth; }

// // // // // // // // // // // body {
// // // // // // // // // // //   background: var(--black);
// // // // // // // // // // //   color: var(--white);
// // // // // // // // // // //   font-family: var(--sans);
// // // // // // // // // // //   overflow-x: hidden;
// // // // // // // // // // // }

// // // // // // // // // // // /* ── scrollbar ── */
// // // // // // // // // // // ::-webkit-scrollbar { width: 4px; }
// // // // // // // // // // // ::-webkit-scrollbar-track { background: var(--black); }
// // // // // // // // // // // ::-webkit-scrollbar-thumb { background: rgba(212,175,55,0.3); border-radius: 2px; }

// // // // // // // // // // // /* ── NAV ── */
// // // // // // // // // // // .lp-nav {
// // // // // // // // // // //   position: fixed; top: 0; left: 0; right: 0; z-index: 200;
// // // // // // // // // // //   padding: 1.5rem 3rem;
// // // // // // // // // // //   transition: all 0.4s;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-nav.scrolled {
// // // // // // // // // // //   background: rgba(8,8,8,0.9);
// // // // // // // // // // //   backdrop-filter: blur(20px);
// // // // // // // // // // //   border-bottom: 0.5px solid rgba(212,175,55,0.12);
// // // // // // // // // // //   padding: 1rem 3rem;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-nav-inner {
// // // // // // // // // // //   max-width: 1180px; margin: 0 auto;
// // // // // // // // // // //   display: flex; align-items: center; justify-content: space-between;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-logo {
// // // // // // // // // // //   display: flex; align-items: center; gap: 10px;
// // // // // // // // // // //   font-family: var(--sans); font-size: 15px; font-weight: 500;
// // // // // // // // // // //   color: var(--white); text-decoration: none; letter-spacing: 0.2px;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-logo-icon { width: 34px; height: 34px; flex-shrink: 0; }
// // // // // // // // // // // .lp-logo em { color: var(--gold); font-style: normal; font-weight: 600; }
// // // // // // // // // // // .lp-nav-links {
// // // // // // // // // // //   display: flex; align-items: center; gap: 2.5rem;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-nav-link {
// // // // // // // // // // //   font-size: 13px; font-weight: 400; color: var(--muted);
// // // // // // // // // // //   text-decoration: none; letter-spacing: 0.3px;
// // // // // // // // // // //   transition: color 0.2s;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-nav-link:hover { color: var(--white); }
// // // // // // // // // // // .lp-nav-cta {
// // // // // // // // // // //   padding: 10px 22px;
// // // // // // // // // // //   background: linear-gradient(135deg, var(--gold), var(--gold2), var(--gold3));
// // // // // // // // // // //   color: #0A0A0A; border: none; border-radius: 10px;
// // // // // // // // // // //   font-family: var(--sans); font-size: 13px; font-weight: 500;
// // // // // // // // // // //   letter-spacing: 0.8px; text-transform: uppercase;
// // // // // // // // // // //   cursor: pointer; position: relative; overflow: hidden;
// // // // // // // // // // //   transition: transform 0.15s, opacity 0.15s;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-nav-cta::before {
// // // // // // // // // // //   content: ''; position: absolute; inset: 0;
// // // // // // // // // // //   background: linear-gradient(135deg, rgba(255,255,255,0.2), transparent 60%);
// // // // // // // // // // //   pointer-events: none;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-nav-cta:hover { transform: translateY(-1px); opacity: 0.92; }

// // // // // // // // // // // /* ── HERO ── */
// // // // // // // // // // // .lp-hero {
// // // // // // // // // // //   min-height: 100vh;
// // // // // // // // // // //   display: grid;
// // // // // // // // // // //   grid-template-columns: 1fr 1fr;
// // // // // // // // // // //   align-items: center;
// // // // // // // // // // //   gap: 4rem;
// // // // // // // // // // //   max-width: 1180px; margin: 0 auto;
// // // // // // // // // // //   padding: 10rem 3rem 6rem;
// // // // // // // // // // //   position: relative;
// // // // // // // // // // // }

// // // // // // // // // // // .lp-hero-left { position: relative; z-index: 2; }

// // // // // // // // // // // .lp-hero-eyebrow {
// // // // // // // // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // // // // // // // //   padding: 6px 14px;
// // // // // // // // // // //   background: rgba(212,175,55,0.1);
// // // // // // // // // // //   border: 0.5px solid rgba(212,175,55,0.3);
// // // // // // // // // // //   border-radius: 100px;
// // // // // // // // // // //   font-size: 11px; font-weight: 500; color: var(--gold);
// // // // // // // // // // //   letter-spacing: 1.5px; text-transform: uppercase;
// // // // // // // // // // //   margin-bottom: 2rem;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-hero-eyebrow-dot {
// // // // // // // // // // //   width: 5px; height: 5px; border-radius: 50%;
// // // // // // // // // // //   background: var(--gold);
// // // // // // // // // // //   animation: pulse-gold 2s ease-in-out infinite;
// // // // // // // // // // // }
// // // // // // // // // // // @keyframes pulse-gold {
// // // // // // // // // // //   0%, 100% { opacity: 1; transform: scale(1); }
// // // // // // // // // // //   50% { opacity: 0.4; transform: scale(0.7); }
// // // // // // // // // // // }

// // // // // // // // // // // .lp-hero-title {
// // // // // // // // // // //   font-family: var(--serif);
// // // // // // // // // // //   font-size: clamp(3rem, 5.5vw, 5.2rem);
// // // // // // // // // // //   font-weight: 300; line-height: 1.08;
// // // // // // // // // // //   letter-spacing: -1px;
// // // // // // // // // // //   color: var(--white);
// // // // // // // // // // //   margin-bottom: 1.75rem;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-hero-title em {
// // // // // // // // // // //   font-style: italic; font-weight: 300;
// // // // // // // // // // //   color: var(--gold);
// // // // // // // // // // // }

// // // // // // // // // // // .lp-hero-sub {
// // // // // // // // // // //   font-size: 16px; font-weight: 300;
// // // // // // // // // // //   color: var(--muted); line-height: 1.8;
// // // // // // // // // // //   max-width: 440px; margin-bottom: 3rem;
// // // // // // // // // // // }

// // // // // // // // // // // .lp-hero-ctas { display: flex; gap: 1rem; flex-wrap: wrap; }

// // // // // // // // // // // .lp-btn-gold {
// // // // // // // // // // //   padding: 16px 32px;
// // // // // // // // // // //   background: linear-gradient(135deg, var(--gold), var(--gold2), var(--gold3));
// // // // // // // // // // //   color: #0A0A0A; border: none; border-radius: 12px;
// // // // // // // // // // //   font-family: var(--sans); font-size: 13px; font-weight: 500;
// // // // // // // // // // //   letter-spacing: 1px; text-transform: uppercase;
// // // // // // // // // // //   cursor: pointer; position: relative; overflow: hidden;
// // // // // // // // // // //   transition: transform 0.2s, opacity 0.2s;
// // // // // // // // // // //   display: flex; align-items: center; gap: 8px;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-btn-gold::before {
// // // // // // // // // // //   content: ''; position: absolute; inset: 0;
// // // // // // // // // // //   background: linear-gradient(135deg, rgba(255,255,255,0.25), transparent 60%);
// // // // // // // // // // //   pointer-events: none;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-btn-gold:hover { transform: translateY(-2px); opacity: 0.92; }

// // // // // // // // // // // .lp-btn-ghost {
// // // // // // // // // // //   padding: 16px 28px;
// // // // // // // // // // //   background: transparent;
// // // // // // // // // // //   color: var(--muted); border: 0.5px solid rgba(255,255,255,0.15);
// // // // // // // // // // //   border-radius: 12px;
// // // // // // // // // // //   font-family: var(--sans); font-size: 13px; font-weight: 400;
// // // // // // // // // // //   letter-spacing: 0.5px; cursor: pointer; text-decoration: none;
// // // // // // // // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // // // // // // // //   transition: border-color 0.2s, color 0.2s;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-btn-ghost:hover { border-color: rgba(212,175,55,0.3); color: var(--white); }

// // // // // // // // // // // /* ── HERO STATS STRIP ── */
// // // // // // // // // // // .lp-hero-stats {
// // // // // // // // // // //   display: flex; gap: 2.5rem; margin-top: 3.5rem;
// // // // // // // // // // //   padding-top: 2.5rem;
// // // // // // // // // // //   border-top: 0.5px solid var(--border);
// // // // // // // // // // // }
// // // // // // // // // // // .lp-stat-num {
// // // // // // // // // // //   font-family: var(--serif); font-size: 2rem; font-weight: 300;
// // // // // // // // // // //   color: var(--gold); line-height: 1;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-stat-label {
// // // // // // // // // // //   font-size: 11px; font-weight: 400; color: var(--muted2);
// // // // // // // // // // //   letter-spacing: 0.5px; margin-top: 4px;
// // // // // // // // // // // }

// // // // // // // // // // // /* ── HERO RIGHT — QR SCAN ANIMATION ── */
// // // // // // // // // // // .lp-hero-right {
// // // // // // // // // // //   position: relative; display: flex;
// // // // // // // // // // //   align-items: center; justify-content: center;
// // // // // // // // // // //   z-index: 2;
// // // // // // // // // // // }

// // // // // // // // // // // /* Scene wrapper */
// // // // // // // // // // // .scan-scene {
// // // // // // // // // // //   position: relative;
// // // // // // // // // // //   width: 380px; height: 480px;
// // // // // // // // // // // }

// // // // // // // // // // // /* Phone A (scanner) */
// // // // // // // // // // // .phone-scanner {
// // // // // // // // // // //   position: absolute;
// // // // // // // // // // //   left: 0; top: 60px;
// // // // // // // // // // //   width: 155px; height: 270px;
// // // // // // // // // // //   background: #1A1A1A;
// // // // // // // // // // //   border-radius: 22px;
// // // // // // // // // // //   border: 1px solid rgba(255,255,255,0.1);
// // // // // // // // // // //   overflow: hidden;
// // // // // // // // // // //   box-shadow: 0 24px 60px rgba(0,0,0,0.6);
// // // // // // // // // // //   z-index: 3;
// // // // // // // // // // //   animation: phone-a-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;
// // // // // // // // // // //   animation-delay: 0.3s;
// // // // // // // // // // // }
// // // // // // // // // // // @keyframes phone-a-intro {
// // // // // // // // // // //   from { opacity: 0; transform: translateX(-30px) rotate(-6deg); }
// // // // // // // // // // //   to   { opacity: 1; transform: translateX(0) rotate(-6deg); }
// // // // // // // // // // // }

// // // // // // // // // // // .phone-scanner-screen {
// // // // // // // // // // //   width: 100%; height: 100%;
// // // // // // // // // // //   background: #111;
// // // // // // // // // // //   display: flex; flex-direction: column;
// // // // // // // // // // //   align-items: center; justify-content: center; gap: 12px;
// // // // // // // // // // //   padding: 12px;
// // // // // // // // // // // }
// // // // // // // // // // // .scanner-viewfinder {
// // // // // // // // // // //   width: 100px; height: 100px;
// // // // // // // // // // //   border-radius: 10px;
// // // // // // // // // // //   position: relative;
// // // // // // // // // // //   background: rgba(212,175,55,0.04);
// // // // // // // // // // // }
// // // // // // // // // // // /* corner brackets of viewfinder */
// // // // // // // // // // // .scanner-viewfinder::before,
// // // // // // // // // // // .scanner-viewfinder::after {
// // // // // // // // // // //   content: '';
// // // // // // // // // // //   position: absolute;
// // // // // // // // // // //   width: 20px; height: 20px;
// // // // // // // // // // //   border-color: var(--gold); border-style: solid;
// // // // // // // // // // // }
// // // // // // // // // // // .scanner-viewfinder::before {
// // // // // // // // // // //   top: 0; left: 0;
// // // // // // // // // // //   border-width: 2px 0 0 2px;
// // // // // // // // // // //   border-radius: 6px 0 0 0;
// // // // // // // // // // // }
// // // // // // // // // // // .scanner-viewfinder::after {
// // // // // // // // // // //   bottom: 0; right: 0;
// // // // // // // // // // //   border-width: 0 2px 2px 0;
// // // // // // // // // // //   border-radius: 0 0 6px 0;
// // // // // // // // // // // }
// // // // // // // // // // // .vf-corner-tr {
// // // // // // // // // // //   position: absolute; top: 0; right: 0;
// // // // // // // // // // //   width: 20px; height: 20px;
// // // // // // // // // // //   border-top: 2px solid var(--gold);
// // // // // // // // // // //   border-right: 2px solid var(--gold);
// // // // // // // // // // //   border-radius: 0 6px 0 0;
// // // // // // // // // // // }
// // // // // // // // // // // .vf-corner-bl {
// // // // // // // // // // //   position: absolute; bottom: 0; left: 0;
// // // // // // // // // // //   width: 20px; height: 20px;
// // // // // // // // // // //   border-bottom: 2px solid var(--gold);
// // // // // // // // // // //   border-left: 2px solid var(--gold);
// // // // // // // // // // //   border-radius: 0 0 0 6px;
// // // // // // // // // // // }

// // // // // // // // // // // /* scanning laser line */
// // // // // // // // // // // .scan-laser {
// // // // // // // // // // //   position: absolute;
// // // // // // // // // // //   left: 4px; right: 4px; height: 2px;
// // // // // // // // // // //   background: linear-gradient(90deg, transparent, var(--gold), transparent);
// // // // // // // // // // //   border-radius: 1px;
// // // // // // // // // // //   top: 4px;
// // // // // // // // // // //   animation: laser-sweep 2s ease-in-out infinite;
// // // // // // // // // // //   animation-delay: 1.2s;
// // // // // // // // // // // }
// // // // // // // // // // // @keyframes laser-sweep {
// // // // // // // // // // //   0%   { top: 8px; opacity: 0; }
// // // // // // // // // // //   10%  { opacity: 1; }
// // // // // // // // // // //   90%  { opacity: 1; }
// // // // // // // // // // //   100% { top: 88px; opacity: 0; }
// // // // // // // // // // // }

// // // // // // // // // // // .scanner-label {
// // // // // // // // // // //   font-size: 9px; font-weight: 400; color: var(--muted2);
// // // // // // // // // // //   letter-spacing: 1px; text-align: center;
// // // // // // // // // // //   text-transform: uppercase;
// // // // // // // // // // // }

// // // // // // // // // // // /* Mini QR inside viewfinder */
// // // // // // // // // // // .mini-qr {
// // // // // // // // // // //   position: absolute; inset: 10px;
// // // // // // // // // // //   display: grid; grid-template-columns: repeat(6,1fr); grid-template-rows: repeat(6,1fr);
// // // // // // // // // // //   gap: 1.5px; opacity: 0.6;
// // // // // // // // // // // }
// // // // // // // // // // // .mini-qr-cell { border-radius: 1px; }

// // // // // // // // // // // /* Phone B (profile card) */
// // // // // // // // // // // .phone-profile {
// // // // // // // // // // //   position: absolute;
// // // // // // // // // // //   right: 0; top: 0;
// // // // // // // // // // //   width: 175px; height: 310px;
// // // // // // // // // // //   background: #0E0E0E;
// // // // // // // // // // //   border-radius: 24px;
// // // // // // // // // // //   border: 1px solid rgba(212,175,55,0.2);
// // // // // // // // // // //   overflow: hidden;
// // // // // // // // // // //   box-shadow: 0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(212,175,55,0.08);
// // // // // // // // // // //   z-index: 4;
// // // // // // // // // // //   animation: phone-b-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;
// // // // // // // // // // //   animation-delay: 0.5s;
// // // // // // // // // // // }
// // // // // // // // // // // @keyframes phone-b-intro {
// // // // // // // // // // //   from { opacity: 0; transform: translateY(30px) rotate(4deg); }
// // // // // // // // // // //   to   { opacity: 1; transform: translateY(0) rotate(4deg); }
// // // // // // // // // // // }

// // // // // // // // // // // /* ── card content opening animation ── */
// // // // // // // // // // // .profile-card-wrap {
// // // // // // // // // // //   width: 100%; height: 100%;
// // // // // // // // // // //   animation: card-reveal 0.7s cubic-bezier(0.22,1,0.36,1) both;
// // // // // // // // // // //   animation-delay: 2s;
// // // // // // // // // // // }
// // // // // // // // // // // @keyframes card-reveal {
// // // // // // // // // // //   from { transform: translateY(40px); opacity: 0; }
// // // // // // // // // // //   to   { transform: translateY(0); opacity: 1; }
// // // // // // // // // // // }

// // // // // // // // // // // .profile-banner {
// // // // // // // // // // //   width: 100%; height: 110px;
// // // // // // // // // // //   background: linear-gradient(160deg, #1a1a1a, #2a2020);
// // // // // // // // // // //   position: relative; overflow: hidden;
// // // // // // // // // // // }
// // // // // // // // // // // .profile-banner-img {
// // // // // // // // // // //   width: 100%; height: 100%;
// // // // // // // // // // //   background: linear-gradient(160deg, #1e1a10 0%, #2a2010 50%, #181818 100%);
// // // // // // // // // // // }
// // // // // // // // // // // .profile-banner-fade {
// // // // // // // // // // //   position: absolute; bottom: 0; left: 0; right: 0; height: 50px;
// // // // // // // // // // //   background: linear-gradient(transparent, #0E0E0E);
// // // // // // // // // // // }

// // // // // // // // // // // /* floating avatar */
// // // // // // // // // // // .profile-avatar-ring {
// // // // // // // // // // //   position: absolute;
// // // // // // // // // // //   bottom: -20px; left: 14px;
// // // // // // // // // // //   width: 44px; height: 44px;
// // // // // // // // // // //   border-radius: 50%;
// // // // // // // // // // //   border: 2px solid #0E0E0E;
// // // // // // // // // // //   background: #222;
// // // // // // // // // // //   overflow: hidden;
// // // // // // // // // // //   box-shadow: 0 0 0 1.5px rgba(212,175,55,0.5);
// // // // // // // // // // //   animation: avatar-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both;
// // // // // // // // // // //   animation-delay: 2.4s;
// // // // // // // // // // // }
// // // // // // // // // // // @keyframes avatar-pop {
// // // // // // // // // // //   from { transform: scale(0.3); opacity: 0; }
// // // // // // // // // // //   to   { transform: scale(1); opacity: 1; }
// // // // // // // // // // // }
// // // // // // // // // // // .profile-avatar-inner {
// // // // // // // // // // //   width: 100%; height: 100%;
// // // // // // // // // // //   background: linear-gradient(145deg, #2a2010, #3a3020);
// // // // // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // // // // //   font-family: var(--serif); font-size: 14px; font-weight: 300;
// // // // // // // // // // //   color: rgba(212,175,55,0.8); letter-spacing: 1px;
// // // // // // // // // // // }

// // // // // // // // // // // .profile-info {
// // // // // // // // // // //   padding: 28px 14px 12px;
// // // // // // // // // // // }
// // // // // // // // // // // .profile-info-name {
// // // // // // // // // // //   font-family: var(--serif); font-size: 14px; font-weight: 300;
// // // // // // // // // // //   color: var(--white); line-height: 1.2; margin-bottom: 2px;
// // // // // // // // // // // }
// // // // // // // // // // // .profile-info-name em { font-style: italic; color: var(--gold); }
// // // // // // // // // // // .profile-info-role {
// // // // // // // // // // //   font-size: 8px; font-weight: 400; color: var(--muted2);
// // // // // // // // // // //   letter-spacing: 0.3px; margin-bottom: 10px;
// // // // // // // // // // // }

// // // // // // // // // // // .profile-tiles {
// // // // // // // // // // //   display: grid; grid-template-columns: 1fr 1fr 1fr;
// // // // // // // // // // //   gap: 5px; margin-bottom: 8px;
// // // // // // // // // // // }
// // // // // // // // // // // .profile-tile {
// // // // // // // // // // //   background: #1A1A1A;
// // // // // // // // // // //   border: 0.5px solid rgba(255,255,255,0.06);
// // // // // // // // // // //   border-radius: 8px; padding: 7px 6px;
// // // // // // // // // // //   display: flex; flex-direction: column; gap: 4px;
// // // // // // // // // // //   animation: tile-slide 0.3s ease both;
// // // // // // // // // // // }
// // // // // // // // // // // .profile-tile:nth-child(1) { animation-delay: 2.5s; }
// // // // // // // // // // // .profile-tile:nth-child(2) { animation-delay: 2.65s; }
// // // // // // // // // // // .profile-tile:nth-child(3) { animation-delay: 2.8s; }
// // // // // // // // // // // @keyframes tile-slide {
// // // // // // // // // // //   from { opacity: 0; transform: translateY(6px); }
// // // // // // // // // // //   to   { opacity: 1; transform: translateY(0); }
// // // // // // // // // // // }
// // // // // // // // // // // .tile-dot {
// // // // // // // // // // //   width: 12px; height: 12px; border-radius: 50%;
// // // // // // // // // // //   background: rgba(212,175,55,0.15);
// // // // // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // // // // // }
// // // // // // // // // // // .tile-dot-inner { width: 5px; height: 5px; border-radius: 50%; background: var(--gold); }
// // // // // // // // // // // .tile-text { font-size: 7px; color: rgba(255,255,255,0.35); line-height: 1.3; }

// // // // // // // // // // // .profile-save-btn {
// // // // // // // // // // //   width: 100%; padding: 8px;
// // // // // // // // // // //   background: linear-gradient(135deg, var(--gold), var(--gold2));
// // // // // // // // // // //   color: #0A0A0A; border: none; border-radius: 8px;
// // // // // // // // // // //   font-size: 8px; font-weight: 500; letter-spacing: 0.8px;
// // // // // // // // // // //   text-transform: uppercase; cursor: default;
// // // // // // // // // // //   display: flex; align-items: center; justify-content: center; gap: 4px;
// // // // // // // // // // //   animation: tile-slide 0.3s ease both;
// // // // // // // // // // //   animation-delay: 2.95s;
// // // // // // // // // // // }

// // // // // // // // // // // /* beam connecting two phones */
// // // // // // // // // // // .beam-line {
// // // // // // // // // // //   position: absolute;
// // // // // // // // // // //   top: 165px; left: 148px;
// // // // // // // // // // //   width: 90px; height: 1px;
// // // // // // // // // // //   background: linear-gradient(90deg, var(--gold), rgba(212,175,55,0));
// // // // // // // // // // //   transform-origin: left center;
// // // // // // // // // // //   animation: beam-appear 0.4s ease both;
// // // // // // // // // // //   animation-delay: 1.5s;
// // // // // // // // // // //   z-index: 5;
// // // // // // // // // // // }
// // // // // // // // // // // @keyframes beam-appear {
// // // // // // // // // // //   from { scaleX: 0; opacity: 0; }
// // // // // // // // // // //   to   { scaleX: 1; opacity: 1; }
// // // // // // // // // // // }
// // // // // // // // // // // .beam-particle {
// // // // // // // // // // //   position: absolute;
// // // // // // // // // // //   top: 160px; left: 148px;
// // // // // // // // // // //   width: 6px; height: 6px; border-radius: 50%;
// // // // // // // // // // //   background: var(--gold);
// // // // // // // // // // //   animation: particle-travel 2s ease-in-out infinite;
// // // // // // // // // // //   animation-delay: 1.6s;
// // // // // // // // // // //   z-index: 6;
// // // // // // // // // // // }
// // // // // // // // // // // @keyframes particle-travel {
// // // // // // // // // // //   0%   { left: 148px; opacity: 0; transform: scale(0.5); }
// // // // // // // // // // //   10%  { opacity: 1; transform: scale(1); }
// // // // // // // // // // //   90%  { opacity: 1; }
// // // // // // // // // // //   100% { left: 228px; opacity: 0; transform: scale(0.5); }
// // // // // // // // // // // }

// // // // // // // // // // // /* ambient glow orbs */
// // // // // // // // // // // .hero-orb-1 {
// // // // // // // // // // //   position: absolute;
// // // // // // // // // // //   width: 500px; height: 500px; border-radius: 50%;
// // // // // // // // // // //   background: radial-gradient(circle, rgba(212,175,55,0.06) 0%, transparent 65%);
// // // // // // // // // // //   top: -100px; right: -100px; pointer-events: none; z-index: 0;
// // // // // // // // // // // }
// // // // // // // // // // // .hero-orb-2 {
// // // // // // // // // // //   position: absolute;
// // // // // // // // // // //   width: 300px; height: 300px; border-radius: 50%;
// // // // // // // // // // //   background: radial-gradient(circle, rgba(212,175,55,0.04) 0%, transparent 65%);
// // // // // // // // // // //   bottom: 0; left: -80px; pointer-events: none; z-index: 0;
// // // // // // // // // // // }

// // // // // // // // // // // /* ── SECTION SHARED ── */
// // // // // // // // // // // .lp-section {
// // // // // // // // // // //   padding: 7rem 3rem;
// // // // // // // // // // //   position: relative;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-section-inner { max-width: 1180px; margin: 0 auto; }
// // // // // // // // // // // .lp-eyebrow {
// // // // // // // // // // //   display: block; font-size: 10px; font-weight: 500;
// // // // // // // // // // //   letter-spacing: 3px; text-transform: uppercase;
// // // // // // // // // // //   color: var(--gold); margin-bottom: 1.25rem; text-align: center;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-section-title {
// // // // // // // // // // //   font-family: var(--serif);
// // // // // // // // // // //   font-size: clamp(2rem, 4vw, 3.2rem);
// // // // // // // // // // //   font-weight: 300; line-height: 1.15; letter-spacing: -0.5px;
// // // // // // // // // // //   color: var(--white); text-align: center; margin-bottom: 1.25rem;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-section-title em { font-style: italic; color: var(--gold); }
// // // // // // // // // // // .lp-section-sub {
// // // // // // // // // // //   font-size: 15px; font-weight: 300; color: var(--muted);
// // // // // // // // // // //   text-align: center; max-width: 480px; margin: 0 auto 4rem; line-height: 1.8;
// // // // // // // // // // // }

// // // // // // // // // // // /* ── GOLD DIVIDER ── */
// // // // // // // // // // // .gold-rule {
// // // // // // // // // // //   height: 0.5px;
// // // // // // // // // // //   background: linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent);
// // // // // // // // // // //   margin: 0;
// // // // // // // // // // // }

// // // // // // // // // // // /* ── TRUST BAR ── */
// // // // // // // // // // // .lp-trust {
// // // // // // // // // // //   padding: 2rem 3rem;
// // // // // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // // // // //   gap: 2.5rem; flex-wrap: wrap;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-trust-label {
// // // // // // // // // // //   font-size: 10px; font-weight: 400; color: var(--muted2);
// // // // // // // // // // //   letter-spacing: 2px; text-transform: uppercase;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-trust-items {
// // // // // // // // // // //   display: flex; align-items: center; gap: 2rem; flex-wrap: wrap;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-trust-item {
// // // // // // // // // // //   display: flex; align-items: center; gap: 7px;
// // // // // // // // // // //   font-size: 13px; font-weight: 400; color: var(--muted);
// // // // // // // // // // // }
// // // // // // // // // // // .lp-trust-sep { color: rgba(212,175,55,0.2); font-size: 18px; }

// // // // // // // // // // // /* ── STEPS ── */
// // // // // // // // // // // .lp-steps {
// // // // // // // // // // //   display: grid; grid-template-columns: repeat(3, 1fr);
// // // // // // // // // // //   gap: 1.5px;
// // // // // // // // // // //   border: 0.5px solid var(--border);
// // // // // // // // // // //   border-radius: 20px; overflow: hidden;
// // // // // // // // // // //   margin-top: 1rem;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-step {
// // // // // // // // // // //   background: var(--card);
// // // // // // // // // // //   padding: 2.5rem 2rem;
// // // // // // // // // // //   position: relative; overflow: hidden;
// // // // // // // // // // //   transition: background 0.3s;
// // // // // // // // // // //   cursor: default;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-step:hover { background: #1a1a1a; }
// // // // // // // // // // // .lp-step::before {
// // // // // // // // // // //   content: '';
// // // // // // // // // // //   position: absolute; top: 0; left: 0; right: 0; height: 1px;
// // // // // // // // // // //   background: linear-gradient(90deg, transparent, rgba(212,175,55,0), transparent);
// // // // // // // // // // //   transition: background 0.3s;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-step:hover::before {
// // // // // // // // // // //   background: linear-gradient(90deg, transparent, rgba(212,175,55,0.4), transparent);
// // // // // // // // // // // }
// // // // // // // // // // // .lp-step-num {
// // // // // // // // // // //   font-family: var(--serif); font-size: 3.5rem; font-weight: 300;
// // // // // // // // // // //   color: rgba(212,175,55,0.12); line-height: 1;
// // // // // // // // // // //   position: absolute; top: 1.5rem; right: 1.5rem;
// // // // // // // // // // //   letter-spacing: -1px;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-step-icon-wrap {
// // // // // // // // // // //   width: 48px; height: 48px; border-radius: 14px;
// // // // // // // // // // //   background: rgba(212,175,55,0.08);
// // // // // // // // // // //   border: 0.5px solid rgba(212,175,55,0.2);
// // // // // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // // // // //   font-size: 1.4rem; margin-bottom: 1.5rem;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-step-title {
// // // // // // // // // // //   font-family: var(--serif); font-size: 1.4rem; font-weight: 400;
// // // // // // // // // // //   color: var(--white); margin-bottom: 0.75rem; line-height: 1.2;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-step-desc {
// // // // // // // // // // //   font-size: 14px; font-weight: 300; color: var(--muted);
// // // // // // // // // // //   line-height: 1.75;
// // // // // // // // // // // }

// // // // // // // // // // // /* ── FEATURES ── */
// // // // // // // // // // // .lp-feat-grid {
// // // // // // // // // // //   display: grid; grid-template-columns: repeat(4, 1fr);
// // // // // // // // // // //   gap: 1px;
// // // // // // // // // // //   border: 0.5px solid var(--border);
// // // // // // // // // // //   border-radius: 20px; overflow: hidden;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-feat-card {
// // // // // // // // // // //   background: var(--card); padding: 2rem 1.5rem;
// // // // // // // // // // //   transition: background 0.3s;
// // // // // // // // // // //   position: relative;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-feat-card:hover { background: #181818; }
// // // // // // // // // // // .lp-feat-icon {
// // // // // // // // // // //   font-size: 1.6rem; margin-bottom: 1.25rem;
// // // // // // // // // // //   display: block;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-feat-title {
// // // // // // // // // // //   font-family: var(--serif); font-size: 1.1rem; font-weight: 400;
// // // // // // // // // // //   color: var(--white); margin-bottom: 0.5rem;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-feat-desc {
// // // // // // // // // // //   font-size: 13px; font-weight: 300; color: var(--muted); line-height: 1.7;
// // // // // // // // // // // }

// // // // // // // // // // // /* ── USE CASES ── */
// // // // // // // // // // // .lp-use-tabs {
// // // // // // // // // // //   display: flex; flex-wrap: wrap; gap: 0.5rem;
// // // // // // // // // // //   justify-content: center; margin-bottom: 3rem;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-use-tab {
// // // // // // // // // // //   padding: 8px 18px; border-radius: 100px;
// // // // // // // // // // //   font-size: 13px; font-weight: 400; cursor: pointer;
// // // // // // // // // // //   transition: all 0.2s; font-family: var(--sans);
// // // // // // // // // // //   display: flex; align-items: center; gap: 6px;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-use-panel {
// // // // // // // // // // //   display: grid; grid-template-columns: 1fr 1fr;
// // // // // // // // // // //   gap: 3rem; align-items: center;
// // // // // // // // // // //   background: var(--card);
// // // // // // // // // // //   border: 0.5px solid var(--border);
// // // // // // // // // // //   border-radius: 24px; padding: 3rem;
// // // // // // // // // // //   position: relative; overflow: hidden;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-use-panel::before {
// // // // // // // // // // //   content: '';
// // // // // // // // // // //   position: absolute; top: 0; left: 0; right: 0; height: 1px;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-use-badge {
// // // // // // // // // // //   display: inline-flex; align-items: center; gap: 8px;
// // // // // // // // // // //   padding: 6px 14px; border-radius: 100px;
// // // // // // // // // // //   margin-bottom: 1.5rem; font-size: 12px; font-weight: 500;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-use-tagline {
// // // // // // // // // // //   font-family: var(--serif); font-size: 2rem; font-weight: 300;
// // // // // // // // // // //   color: var(--white); line-height: 1.2; margin-bottom: 1rem;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-use-desc {
// // // // // // // // // // //   font-size: 14px; font-weight: 300; color: var(--muted);
// // // // // // // // // // //   line-height: 1.85; margin-bottom: 2rem;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-use-highlights { display: flex; flex-direction: column; gap: 0.75rem; }
// // // // // // // // // // // .lp-use-hl {
// // // // // // // // // // //   display: flex; align-items: center; gap: 12px;
// // // // // // // // // // //   background: #1a1a1a; border: 0.5px solid var(--border);
// // // // // // // // // // //   border-radius: 12px; padding: 1rem 1.25rem;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-use-hl-check {
// // // // // // // // // // //   width: 28px; height: 28px; border-radius: 50%;
// // // // // // // // // // //   display: flex; align-items: center; justify-content: center;
// // // // // // // // // // //   flex-shrink: 0; font-size: 11px; font-weight: 600;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-use-hl-text {
// // // // // // // // // // //   font-size: 13px; font-weight: 400; color: rgba(255,255,255,0.75);
// // // // // // // // // // // }

// // // // // // // // // // // /* ── PRICING ── */
// // // // // // // // // // // .lp-pricing-wrap { display: flex; justify-content: center; margin-top: 1rem; }
// // // // // // // // // // // .lp-pricing-card {
// // // // // // // // // // //   background: var(--card);
// // // // // // // // // // //   border: 0.5px solid rgba(212,175,55,0.25);
// // // // // // // // // // //   border-radius: 28px; padding: 3rem 2.5rem;
// // // // // // // // // // //   width: 100%; max-width: 420px;
// // // // // // // // // // //   text-align: center; position: relative;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-pricing-card::before {
// // // // // // // // // // //   content: '';
// // // // // // // // // // //   position: absolute; top: 0; left: 10%; right: 10%; height: 1px;
// // // // // // // // // // //   background: linear-gradient(90deg, transparent, var(--gold), transparent);
// // // // // // // // // // // }
// // // // // // // // // // // .lp-pricing-badge {
// // // // // // // // // // //   display: inline-block;
// // // // // // // // // // //   background: linear-gradient(135deg, var(--gold), var(--gold2));
// // // // // // // // // // //   color: #0A0A0A; border-radius: 100px;
// // // // // // // // // // //   padding: 5px 16px; font-size: 11px; font-weight: 500;
// // // // // // // // // // //   letter-spacing: 1px; text-transform: uppercase;
// // // // // // // // // // //   margin-bottom: 2rem;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-price-num {
// // // // // // // // // // //   font-family: var(--serif); font-size: 5rem; font-weight: 300;
// // // // // // // // // // //   color: var(--white); line-height: 1; letter-spacing: -2px;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-price-period {
// // // // // // // // // // //   font-size: 12px; font-weight: 400; color: var(--muted2);
// // // // // // // // // // //   letter-spacing: 2px; text-transform: uppercase; margin: 0.5rem 0 2.5rem;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-price-list { list-style: none; text-align: left; margin-bottom: 2.5rem; }
// // // // // // // // // // // .lp-price-item {
// // // // // // // // // // //   display: flex; align-items: center; gap: 10px;
// // // // // // // // // // //   padding: 0.7rem 0; font-size: 14px; font-weight: 300;
// // // // // // // // // // //   color: rgba(255,255,255,0.7);
// // // // // // // // // // //   border-bottom: 0.5px solid var(--border);
// // // // // // // // // // // }
// // // // // // // // // // // .lp-price-check { color: var(--gold); font-size: 12px; flex-shrink: 0; }

// // // // // // // // // // // /* ── CTA BANNER ── */
// // // // // // // // // // // .lp-cta-banner {
// // // // // // // // // // //   padding: 8rem 3rem;
// // // // // // // // // // //   text-align: center;
// // // // // // // // // // //   position: relative; overflow: hidden;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-cta-banner::before {
// // // // // // // // // // //   content: '';
// // // // // // // // // // //   position: absolute; inset: 0;
// // // // // // // // // // //   background: radial-gradient(ellipse 80% 60% at 50% 50%, rgba(212,175,55,0.07) 0%, transparent 70%);
// // // // // // // // // // //   pointer-events: none;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-cta-title {
// // // // // // // // // // //   font-family: var(--serif);
// // // // // // // // // // //   font-size: clamp(2.5rem, 6vw, 5rem);
// // // // // // // // // // //   font-weight: 300; line-height: 1.1;
// // // // // // // // // // //   color: var(--white); margin-bottom: 1.5rem;
// // // // // // // // // // //   letter-spacing: -1px;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-cta-title em { font-style: italic; color: var(--gold); }
// // // // // // // // // // // .lp-cta-sub {
// // // // // // // // // // //   font-size: 16px; font-weight: 300; color: var(--muted);
// // // // // // // // // // //   margin-bottom: 3rem; line-height: 1.7;
// // // // // // // // // // // }

// // // // // // // // // // // /* ── FOOTER ── */
// // // // // // // // // // // .lp-footer {
// // // // // // // // // // //   padding: 3rem 3rem 4rem;
// // // // // // // // // // //   border-top: 0.5px solid var(--border);
// // // // // // // // // // // }
// // // // // // // // // // // .lp-footer-inner {
// // // // // // // // // // //   max-width: 1180px; margin: 0 auto;
// // // // // // // // // // //   display: flex; flex-direction: column; align-items: center; gap: 1rem;
// // // // // // // // // // // }
// // // // // // // // // // // .lp-footer-logo {
// // // // // // // // // // //   display: flex; align-items: center; gap: 10px;
// // // // // // // // // // //   font-family: var(--sans); font-size: 15px; font-weight: 500; color: var(--white);
// // // // // // // // // // // }
// // // // // // // // // // // .lp-footer-logo em { color: var(--gold); font-style: normal; font-weight: 600; }
// // // // // // // // // // // .lp-footer-tagline {
// // // // // // // // // // //   font-size: 13px; font-weight: 300; color: var(--muted2);
// // // // // // // // // // // }
// // // // // // // // // // // .lp-footer-copy {
// // // // // // // // // // //   font-size: 12px; font-weight: 300; color: rgba(255,255,255,0.18);
// // // // // // // // // // // }

// // // // // // // // // // // /* ── FADE-IN ON SCROLL ── */
// // // // // // // // // // // .reveal {
// // // // // // // // // // //   opacity: 0; transform: translateY(24px);
// // // // // // // // // // //   transition: opacity 0.7s ease, transform 0.7s ease;
// // // // // // // // // // // }
// // // // // // // // // // // .reveal.visible { opacity: 1; transform: translateY(0); }

// // // // // // // // // // // /* ── MOBILE ── */
// // // // // // // // // // // @media (max-width: 900px) {
// // // // // // // // // // //   .lp-hero { grid-template-columns: 1fr; text-align: center; padding: 8rem 1.5rem 4rem; }
// // // // // // // // // // //   .lp-hero-sub { margin: 0 auto 3rem; }
// // // // // // // // // // //   .lp-hero-ctas { justify-content: center; }
// // // // // // // // // // //   .lp-hero-stats { justify-content: center; }
// // // // // // // // // // //   .lp-hero-right { margin-top: 3rem; }
// // // // // // // // // // //   .scan-scene { width: 300px; height: 380px; transform: scale(0.85); }
// // // // // // // // // // //   .lp-steps { grid-template-columns: 1fr; }
// // // // // // // // // // //   .lp-feat-grid { grid-template-columns: 1fr 1fr; }
// // // // // // // // // // //   .lp-use-panel { grid-template-columns: 1fr; }
// // // // // // // // // // //   .lp-nav { padding: 1rem 1.5rem; }
// // // // // // // // // // //   .lp-nav.scrolled { padding: 0.75rem 1.5rem; }
// // // // // // // // // // //   .lp-nav-links { gap: 1rem; }
// // // // // // // // // // //   .lp-section { padding: 5rem 1.5rem; }
// // // // // // // // // // // }
// // // // // // // // // // // @media (max-width: 600px) {
// // // // // // // // // // //   .lp-feat-grid { grid-template-columns: 1fr; }
// // // // // // // // // // //   .lp-nav-link { display: none; }
// // // // // // // // // // // }
// // // // // // // // // // // `;

// // // // // // // // // // // /* ─────────────────────────────────────────
// // // // // // // // // // //    QR SCAN ANIMATION
// // // // // // // // // // // ───────────────────────────────────────── */
// // // // // // // // // // // const QR_PATTERN = [
// // // // // // // // // // //     [1, 1, 1, 0, 1, 0], [1, 0, 1, 1, 0, 1], [1, 1, 1, 0, 1, 1], [0, 1, 0, 1, 0, 0], [1, 0, 1, 1, 1, 0], [0, 1, 0, 0, 1, 1]
// // // // // // // // // // // ];

// // // // // // // // // // // function ScanScene() {
// // // // // // // // // // //     return (
// // // // // // // // // // //         <div className="scan-scene">
// // // // // // // // // // //             <div className="hero-orb-1" />

// // // // // // // // // // //             {/* Phone A — scanner */}
// // // // // // // // // // //             <div className="phone-scanner">
// // // // // // // // // // //                 <div className="phone-scanner-screen">
// // // // // // // // // // //                     <div className="scanner-viewfinder">
// // // // // // // // // // //                         <div className="vf-corner-tr" />
// // // // // // // // // // //                         <div className="vf-corner-bl" />
// // // // // // // // // // //                         <div className="scan-laser" />
// // // // // // // // // // //                         {/* mini qr */}
// // // // // // // // // // //                         <div className="mini-qr">
// // // // // // // // // // //                             {QR_PATTERN.map((row, ri) =>
// // // // // // // // // // //                                 row.map((cell, ci) => (
// // // // // // // // // // //                                     <div key={`${ri}-${ci}`} className="mini-qr-cell"
// // // // // // // // // // //                                         style={{ background: cell ? "rgba(212,175,55,0.5)" : "transparent" }} />
// // // // // // // // // // //                                 ))
// // // // // // // // // // //                             )}
// // // // // // // // // // //                         </div>
// // // // // // // // // // //                     </div>
// // // // // // // // // // //                     <div className="scanner-label">Point at QR code</div>
// // // // // // // // // // //                 </div>
// // // // // // // // // // //             </div>

// // // // // // // // // // //             {/* Beam + particle */}
// // // // // // // // // // //             <div className="beam-line" />
// // // // // // // // // // //             <div className="beam-particle" />

// // // // // // // // // // //             {/* Phone B — profile card opening */}
// // // // // // // // // // //             <div className="phone-profile">
// // // // // // // // // // //                 <div className="profile-card-wrap">
// // // // // // // // // // //                     {/* Banner */}
// // // // // // // // // // //                     <div className="profile-banner">
// // // // // // // // // // //                         <div className="profile-banner-img" />
// // // // // // // // // // //                         <div className="profile-banner-fade" />
// // // // // // // // // // //                         {/* floating avatar */}
// // // // // // // // // // //                         <div className="profile-avatar-ring">
// // // // // // // // // // //                             <div className="profile-avatar-inner">MG</div>
// // // // // // // // // // //                         </div>
// // // // // // // // // // //                     </div>

// // // // // // // // // // //                     {/* Info */}
// // // // // // // // // // //                     <div className="profile-info">
// // // // // // // // // // //                         <div className="profile-info-name">
// // // // // // // // // // //                             Mragank <em>Gaur</em>
// // // // // // // // // // //                         </div>
// // // // // // // // // // //                         <div className="profile-info-role">Director · Empower Pragati</div>

// // // // // // // // // // //                         <div className="profile-tiles">
// // // // // // // // // // //                             {[["📞", "Phone"], ["✉️", "Email"], ["📍", "Delhi"]].map(([icon, label]) => (
// // // // // // // // // // //                                 <div className="profile-tile" key={label}>
// // // // // // // // // // //                                     <div className="tile-dot"><div className="tile-dot-inner" /></div>
// // // // // // // // // // //                                     <div className="tile-text">{label}</div>
// // // // // // // // // // //                                 </div>
// // // // // // // // // // //                             ))}
// // // // // // // // // // //                         </div>

// // // // // // // // // // //                         <button className="profile-save-btn">
// // // // // // // // // // //                             ↓ Save Contact
// // // // // // // // // // //                         </button>
// // // // // // // // // // //                     </div>
// // // // // // // // // // //                 </div>
// // // // // // // // // // //             </div>
// // // // // // // // // // //         </div>
// // // // // // // // // // //     );
// // // // // // // // // // // }

// // // // // // // // // // // /* ─────────────────────────────────────────
// // // // // // // // // // //    USE CASES
// // // // // // // // // // // ───────────────────────────────────────── */
// // // // // // // // // // // const USE_CASES = [
// // // // // // // // // // //     {
// // // // // // // // // // //         icon: "👔", role: "Job Seekers", gold: "rgba(212,175,55,1)",
// // // // // // // // // // //         tagline: "Land your next opportunity faster",
// // // // // // // // // // //         desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one elegant scan.",
// // // // // // // // // // //         highlights: ["Resume attached & downloadable", "LinkedIn & GitHub links", "QR code for instant sharing", "Save contact in one tap"]
// // // // // // // // // // //     },
// // // // // // // // // // //     {
// // // // // // // // // // //         icon: "💼", role: "Freelancers", gold: "rgba(212,175,55,1)",
// // // // // // // // // // //         tagline: "Win clients before you even speak",
// // // // // // // // // // //         desc: "Share everything a client needs — portfolio, social proof, and contact — all in a single professional link. Close deals faster.",
// // // // // // // // // // //         highlights: ["Portfolio & website link", "WhatsApp quick contact", "Instagram & social proof", "Professional digital presence"]
// // // // // // // // // // //     },
// // // // // // // // // // //     {
// // // // // // // // // // //         icon: "🏢", role: "Business Owners", gold: "rgba(212,175,55,1)",
// // // // // // // // // // //         tagline: "Network smarter, not harder",
// // // // // // // // // // //         desc: "Stop spending on printed cards that get lost. Share via QR at events — always updated, never outdated, never thrown away.",
// // // // // // // // // // //         highlights: ["Always up-to-date details", "Google Wallet for offline use", "QR code for events & meetups", "Share via WhatsApp or email"]
// // // // // // // // // // //     },
// // // // // // // // // // //     {
// // // // // // // // // // //         icon: "🎓", role: "Students", gold: "rgba(212,175,55,1)",
// // // // // // // // // // //         tagline: "Build your professional identity early",
// // // // // // // // // // //         desc: "Stand out in campus placements and internship drives. Show your skills, projects, and social profiles before you say a word.",
// // // // // // // // // // //         highlights: ["GitHub & project links", "Resume upload for placements", "LinkedIn profile showcase", "Free forever — no cost"]
// // // // // // // // // // //     },
// // // // // // // // // // //     {
// // // // // // // // // // //         icon: "📣", role: "Sales Pros", gold: "rgba(212,175,55,1)",
// // // // // // // // // // //         tagline: "Close the follow-up gap instantly",
// // // // // // // // // // //         desc: "At trade shows or cold visits — one QR scan and your prospect has everything. Number, email, website, product links in one place.",
// // // // // // // // // // //         highlights: ["Instant QR share at events", "Website & product links", "Save contact in one tap", "Google Wallet for quick access"]
// // // // // // // // // // //     },
// // // // // // // // // // //     {
// // // // // // // // // // //         icon: "🧑‍💻", role: "Developers", gold: "rgba(212,175,55,1)",
// // // // // // // // // // //         tagline: "Your entire dev profile in one link",
// // // // // // // // // // //         desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share at hackathons or in open source communities.",
// // // // // // // // // // //         highlights: ["GitHub & portfolio links", "Twitter / X profile", "Resume for job applications", "Professional dev identity"]
// // // // // // // // // // //     },
// // // // // // // // // // // ];

// // // // // // // // // // // function UseCasesSection({ navigate }) {
// // // // // // // // // // //     const [active, setActive] = useState(0);
// // // // // // // // // // //     const cur = USE_CASES[active];
// // // // // // // // // // //     return (
// // // // // // // // // // //         <section className="lp-section">
// // // // // // // // // // //             <div className="lp-section-inner">
// // // // // // // // // // //                 <span className="lp-eyebrow reveal">Who is it for</span>
// // // // // // // // // // //                 <h2 className="lp-section-title reveal">Built for every <em>professional</em></h2>
// // // // // // // // // // //                 <p className="lp-section-sub reveal">Whether you're a student or a CEO — your digital card works for you.</p>

// // // // // // // // // // //                 <div className="lp-use-tabs reveal">
// // // // // // // // // // //                     {USE_CASES.map((u, i) => (
// // // // // // // // // // //                         <button key={i} className="lp-use-tab" onClick={() => setActive(i)}
// // // // // // // // // // //                             style={{
// // // // // // // // // // //                                 border: active === i ? `0.5px solid rgba(212,175,55,0.5)` : "0.5px solid rgba(255,255,255,0.1)",
// // // // // // // // // // //                                 background: active === i ? "rgba(212,175,55,0.1)" : "transparent",
// // // // // // // // // // //                                 color: active === i ? "var(--gold)" : "var(--muted)",
// // // // // // // // // // //                             }}>
// // // // // // // // // // //                             {u.icon} {u.role}
// // // // // // // // // // //                         </button>
// // // // // // // // // // //                     ))}
// // // // // // // // // // //                 </div>

// // // // // // // // // // //                 <div className="lp-use-panel"
// // // // // // // // // // //                     style={{ borderColor: "rgba(212,175,55,0.15)" }}>
// // // // // // // // // // //                     <div className="lp-use-panel::before" style={{}} />
// // // // // // // // // // //                     <div>
// // // // // // // // // // //                         <div className="lp-use-badge"
// // // // // // // // // // //                             style={{ background: "rgba(212,175,55,0.1)", color: "var(--gold)", border: "0.5px solid rgba(212,175,55,0.25)" }}>
// // // // // // // // // // //                             <span>{cur.icon}</span><span style={{ fontSize: "12px", letterSpacing: "0.5px" }}>{cur.role}</span>
// // // // // // // // // // //                         </div>
// // // // // // // // // // //                         <div className="lp-use-tagline">{cur.tagline}</div>
// // // // // // // // // // //                         <p className="lp-use-desc">{cur.desc}</p>
// // // // // // // // // // //                         <button className="lp-btn-gold" onClick={() => navigate("/animation/card")}>
// // // // // // // // // // //                             Create My Card — Free →
// // // // // // // // // // //                         </button>
// // // // // // // // // // //                     </div>
// // // // // // // // // // //                     <div className="lp-use-highlights">
// // // // // // // // // // //                         {cur.highlights.map((h, i) => (
// // // // // // // // // // //                             <div className="lp-use-hl" key={i}>
// // // // // // // // // // //                                 <div className="lp-use-hl-check"
// // // // // // // // // // //                                     style={{ background: "rgba(212,175,55,0.1)", color: "var(--gold)" }}>✓</div>
// // // // // // // // // // //                                 <span className="lp-use-hl-text">{h}</span>
// // // // // // // // // // //                             </div>
// // // // // // // // // // //                         ))}
// // // // // // // // // // //                     </div>
// // // // // // // // // // //                 </div>
// // // // // // // // // // //             </div>
// // // // // // // // // // //         </section>
// // // // // // // // // // //     );
// // // // // // // // // // // }

// // // // // // // // // // // /* ─────────────────────────────────────────
// // // // // // // // // // //    MAIN COMPONENT
// // // // // // // // // // // ───────────────────────────────────────── */
// // // // // // // // // // // export default function LandingPage() {
// // // // // // // // // // //     const navigate = useNavigate();
// // // // // // // // // // //     const [scrolled, setScrolled] = useState(false);

// // // // // // // // // // //     /* scroll nav */
// // // // // // // // // // //     useEffect(() => {
// // // // // // // // // // //         const onScroll = () => setScrolled(window.scrollY > 40);
// // // // // // // // // // //         window.addEventListener("scroll", onScroll);
// // // // // // // // // // //         return () => window.removeEventListener("scroll", onScroll);
// // // // // // // // // // //     }, []);

// // // // // // // // // // //     /* scroll reveal */
// // // // // // // // // // //     useEffect(() => {
// // // // // // // // // // //         const els = document.querySelectorAll(".reveal");
// // // // // // // // // // //         const observer = new IntersectionObserver(
// // // // // // // // // // //             (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
// // // // // // // // // // //             { threshold: 0.12 }
// // // // // // // // // // //         );
// // // // // // // // // // //         els.forEach(el => observer.observe(el));
// // // // // // // // // // //         return () => observer.disconnect();
// // // // // // // // // // //     }, []);

// // // // // // // // // // //     return (
// // // // // // // // // // //         <>
// // // // // // // // // // //             <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

// // // // // // // // // // //             {/* ── NAV ── */}
// // // // // // // // // // //             <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
// // // // // // // // // // //                 <div className="lp-nav-inner">
// // // // // // // // // // //                     <a href="/" className="lp-logo">
// // // // // // // // // // //                         {/* inline logo icon */}
// // // // // // // // // // //                         <svg className="lp-logo-icon" viewBox="0 0 34 34" fill="none">
// // // // // // // // // // //                             <rect width="34" height="34" rx="9" fill="#1A1A1A" />
// // // // // // // // // // //                             <rect width="34" height="34" rx="9" stroke="rgba(212,175,55,0.2)" strokeWidth="0.5" />
// // // // // // // // // // //                             <circle cx="15" cy="13" r="5" stroke="#D4AF37" strokeWidth="1.5" fill="none" />
// // // // // // // // // // //                             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" fill="none" />
// // // // // // // // // // //                             <path d="M23 11 L27 11 L27 23" stroke="rgba(212,175,55,0.5)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
// // // // // // // // // // //                             <circle cx="27" cy="23" r="1.8" fill="#D4AF37" />
// // // // // // // // // // //                         </svg>
// // // // // // // // // // //                         myprofile<em>corner</em>
// // // // // // // // // // //                     </a>
// // // // // // // // // // //                     <div className="lp-nav-links">
// // // // // // // // // // //                         <a href="#how" className="lp-nav-link">How it works</a>
// // // // // // // // // // //                         <a href="#features" className="lp-nav-link">Features</a>
// // // // // // // // // // //                         <a href="#pricing" className="lp-nav-link">Pricing</a>
// // // // // // // // // // //                         <button className="lp-nav-cta" onClick={() => navigate("/animation/card")}>
// // // // // // // // // // //                             Create Free Card
// // // // // // // // // // //                         </button>
// // // // // // // // // // //                     </div>
// // // // // // // // // // //                 </div>
// // // // // // // // // // //             </nav>

// // // // // // // // // // //             {/* ── HERO ── */}
// // // // // // // // // // //             <section style={{ background: "var(--black)", overflow: "hidden", position: "relative" }}>
// // // // // // // // // // //                 <div className="lp-hero">
// // // // // // // // // // //                     {/* ambient bg orbs */}
// // // // // // // // // // //                     <div style={{
// // // // // // // // // // //                         position: "absolute", width: "600px", height: "600px", borderRadius: "50%",
// // // // // // // // // // //                         background: "radial-gradient(circle, rgba(212,175,55,0.05) 0%, transparent 65%)",
// // // // // // // // // // //                         top: "-200px", left: "-100px", pointerEvents: "none"
// // // // // // // // // // //                     }} />

// // // // // // // // // // //                     {/* LEFT */}
// // // // // // // // // // //                     <div className="lp-hero-left">
// // // // // // // // // // //                         <div className="lp-hero-eyebrow">
// // // // // // // // // // //                             <div className="lp-hero-eyebrow-dot" />
// // // // // // // // // // //                             100% Free · No App Needed
// // // // // // // // // // //                         </div>

// // // // // // // // // // //                         <h1 className="lp-hero-title">
// // // // // // // // // // //                             Your Digital<br />
// // // // // // // // // // //                             Identity, <em>Always</em><br />
// // // // // // // // // // //                             in Your Pocket
// // // // // // // // // // //                         </h1>

// // // // // // // // // // //                         <p className="lp-hero-sub">
// // // // // // // // // // //                             Replace paper visiting cards with a smart digital card. Share your profile
// // // // // // // // // // //                             instantly via QR code, link, or Google Wallet — no printing, no waste.
// // // // // // // // // // //                         </p>

// // // // // // // // // // //                         <div className="lp-hero-ctas">
// // // // // // // // // // //                             <button className="lp-btn-gold" onClick={() => navigate("/animation/card")}>
// // // // // // // // // // //                                 Create My Card — It's Free →
// // // // // // // // // // //                             </button>
// // // // // // // // // // //                             <a href="#how" className="lp-btn-ghost">See how it works</a>
// // // // // // // // // // //                         </div>

// // // // // // // // // // //                         <div className="lp-hero-stats">
// // // // // // // // // // //                             {[["100%", "Free forever"], ["2 min", "Setup time"], ["0", "Paper waste"]].map(([n, l]) => (
// // // // // // // // // // //                                 <div key={l}>
// // // // // // // // // // //                                     <div className="lp-stat-num">{n}</div>
// // // // // // // // // // //                                     <div className="lp-stat-label">{l}</div>
// // // // // // // // // // //                                 </div>
// // // // // // // // // // //                             ))}
// // // // // // // // // // //                         </div>
// // // // // // // // // // //                     </div>

// // // // // // // // // // //                     {/* RIGHT — animated scan scene */}
// // // // // // // // // // //                     <div className="lp-hero-right">
// // // // // // // // // // //                         <ScanScene />
// // // // // // // // // // //                     </div>
// // // // // // // // // // //                 </div>
// // // // // // // // // // //             </section>

// // // // // // // // // // //             <div className="gold-rule" />

// // // // // // // // // // //             {/* ── TRUST BAR ── */}
// // // // // // // // // // //             <div className="lp-trust">
// // // // // // // // // // //                 <span className="lp-trust-label">Works seamlessly with</span>
// // // // // // // // // // //                 <div className="lp-trust-items">
// // // // // // // // // // //                     {[["🔥", "Firebase"], ["🪙", "Google Wallet"], ["📱", "Android & iOS"], ["🔗", "Any Browser"]].map(([icon, label], i, arr) => (
// // // // // // // // // // //                         <React.Fragment key={label}>
// // // // // // // // // // //                             <div className="lp-trust-item"><span>{icon}</span><span>{label}</span></div>
// // // // // // // // // // //                             {i < arr.length - 1 && <span className="lp-trust-sep">·</span>}
// // // // // // // // // // //                         </React.Fragment>
// // // // // // // // // // //                     ))}
// // // // // // // // // // //                 </div>
// // // // // // // // // // //             </div>

// // // // // // // // // // //             <div className="gold-rule" />

// // // // // // // // // // //             {/* ── HOW IT WORKS ── */}
// // // // // // // // // // //             <section id="how" className="lp-section">
// // // // // // // // // // //                 <div className="lp-section-inner">
// // // // // // // // // // //                     <span className="lp-eyebrow reveal">How it works</span>
// // // // // // // // // // //                     <h2 className="lp-section-title reveal">From setup to sharing <em>in 3 steps</em></h2>
// // // // // // // // // // //                     <p className="lp-section-sub reveal">No app downloads. No tech skills required. Just fill, share, impress.</p>

// // // // // // // // // // //                     <div className="lp-steps reveal">
// // // // // // // // // // //                         {[
// // // // // // // // // // //                             { num: "01", icon: "✏️", title: "Fill your profile", desc: "Add your name, photo, contact details, social links, resume, and website. Takes less than 2 minutes." },
// // // // // // // // // // //                             { num: "02", icon: "🎴", title: "Get your digital card", desc: "Instantly receive a beautiful card with a unique QR code and shareable link — no design skills needed." },
// // // // // // // // // // //                             { num: "03", icon: "🚀", title: "Share anywhere", desc: "Show your QR in meetings, add to Google Wallet, or send your link on WhatsApp, email, or LinkedIn." },
// // // // // // // // // // //                         ].map((step) => (
// // // // // // // // // // //                             <div className="lp-step" key={step.num}>
// // // // // // // // // // //                                 <div className="lp-step-num">{step.num}</div>
// // // // // // // // // // //                                 <div className="lp-step-icon-wrap">{step.icon}</div>
// // // // // // // // // // //                                 <div className="lp-step-title">{step.title}</div>
// // // // // // // // // // //                                 <p className="lp-step-desc">{step.desc}</p>
// // // // // // // // // // //                             </div>
// // // // // // // // // // //                         ))}
// // // // // // // // // // //                     </div>
// // // // // // // // // // //                 </div>
// // // // // // // // // // //             </section>

// // // // // // // // // // //             <div className="gold-rule" />

// // // // // // // // // // //             {/* ── FEATURES ── */}
// // // // // // // // // // //             <section id="features" className="lp-section">
// // // // // // // // // // //                 <div className="lp-section-inner">
// // // // // // // // // // //                     <span className="lp-eyebrow reveal">Features</span>
// // // // // // // // // // //                     <h2 className="lp-section-title reveal">Everything a modern <em>professional</em> needs</h2>

// // // // // // // // // // //                     <div className="lp-feat-grid reveal">
// // // // // // // // // // //                         {[
// // // // // // // // // // //                             { icon: "📸", title: "Profile Photo", desc: "Upload your photo — it appears on your card and even in your Google Wallet pass." },
// // // // // // // // // // //                             { icon: "🔗", title: "All Social Links", desc: "LinkedIn, GitHub, Instagram, Twitter, WhatsApp, YouTube — show what matters." },
// // // // // // // // // // //                             { icon: "📄", title: "Resume Attachment", desc: "Attach your PDF resume directly to your card. Recruiters can download it instantly." },
// // // // // // // // // // //                             { icon: "🪙", title: "Google Wallet", desc: "Add your card to Google Wallet and show it offline anytime, anywhere." },
// // // // // // // // // // //                             { icon: "📲", title: "QR Code", desc: "Every card gets a unique QR code. One scan opens your full profile instantly." },
// // // // // // // // // // //                             { icon: "💾", title: "Save Contact", desc: "Visitors can save your details directly to their phone contacts with one tap." },
// // // // // // // // // // //                             { icon: "✏️", title: "Edit Anytime", desc: "Changed jobs? New number? Update your card instantly — the link stays the same." },
// // // // // // // // // // //                             { icon: "🌐", title: "Works Everywhere", desc: "Your card opens in any browser on any device. No app install required." },
// // // // // // // // // // //                         ].map((f) => (
// // // // // // // // // // //                             <div className="lp-feat-card" key={f.title}>
// // // // // // // // // // //                                 <span className="lp-feat-icon">{f.icon}</span>
// // // // // // // // // // //                                 <div className="lp-feat-title">{f.title}</div>
// // // // // // // // // // //                                 <p className="lp-feat-desc">{f.desc}</p>
// // // // // // // // // // //                             </div>
// // // // // // // // // // //                         ))}
// // // // // // // // // // //                     </div>
// // // // // // // // // // //                 </div>
// // // // // // // // // // //             </section>

// // // // // // // // // // //             <div className="gold-rule" />

// // // // // // // // // // //             {/* ── USE CASES ── */}
// // // // // // // // // // //             <UseCasesSection navigate={navigate} />

// // // // // // // // // // //             <div className="gold-rule" />

// // // // // // // // // // //             {/* ── PRICING ── */}
// // // // // // // // // // //             <section id="pricing" className="lp-section">
// // // // // // // // // // //                 <div className="lp-section-inner">
// // // // // // // // // // //                     <span className="lp-eyebrow reveal">Pricing</span>
// // // // // // // // // // //                     <h2 className="lp-section-title reveal">Simple, <em>honest</em> pricing</h2>
// // // // // // // // // // //                     <p className="lp-section-sub reveal">No hidden charges. No credit card needed.</p>

// // // // // // // // // // //                     <div className="lp-pricing-wrap reveal">
// // // // // // // // // // //                         <div className="lp-pricing-card">
// // // // // // // // // // //                             <div className="lp-pricing-badge">Most Popular</div>
// // // // // // // // // // //                             <div className="lp-price-num">₹0</div>
// // // // // // // // // // //                             <div className="lp-price-period">Forever Free</div>
// // // // // // // // // // //                             <ul className="lp-price-list">
// // // // // // // // // // //                                 {["1 Digital Visiting Card", "Unique QR Code & Link", "Profile Photo Upload", "Resume Upload", "All Social Media Links", "Google Wallet Pass", "Save Contact (.vcf)", "Edit Anytime", "No App Required"].map(item => (
// // // // // // // // // // //                                     <li className="lp-price-item" key={item}>
// // // // // // // // // // //                                         <span className="lp-price-check">✦</span>{item}
// // // // // // // // // // //                                     </li>
// // // // // // // // // // //                                 ))}
// // // // // // // // // // //                             </ul>
// // // // // // // // // // //                             <button className="lp-btn-gold" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/animation/card")}>
// // // // // // // // // // //                                 Get Started Free →
// // // // // // // // // // //                             </button>
// // // // // // // // // // //                         </div>
// // // // // // // // // // //                     </div>
// // // // // // // // // // //                 </div>
// // // // // // // // // // //             </section>

// // // // // // // // // // //             <div className="gold-rule" />

// // // // // // // // // // //             {/* ── CTA BANNER ── */}
// // // // // // // // // // //             <section className="lp-cta-banner">
// // // // // // // // // // //                 <h2 className="lp-cta-title reveal">
// // // // // // // // // // //                     Stop carrying<br /><em>paper cards.</em>
// // // // // // // // // // //                 </h2>
// // // // // // // // // // //                 <p className="lp-cta-sub reveal">Create your digital card in under 2 minutes. Free, forever.</p>
// // // // // // // // // // //                 <button className="lp-btn-gold reveal" style={{ margin: "0 auto" }} onClick={() => navigate("/animation/card")}>
// // // // // // // // // // //                     Create My Card Now →
// // // // // // // // // // //                 </button>
// // // // // // // // // // //             </section>

// // // // // // // // // // //             <div className="gold-rule" />

// // // // // // // // // // //             {/* ── FOOTER ── */}
// // // // // // // // // // //             <footer className="lp-footer">
// // // // // // // // // // //                 <div className="lp-footer-inner">
// // // // // // // // // // //                     <div className="lp-footer-logo">
// // // // // // // // // // //                         <svg width="28" height="28" viewBox="0 0 34 34" fill="none">
// // // // // // // // // // //                             <rect width="34" height="34" rx="9" fill="#1A1A1A" />
// // // // // // // // // // //                             <rect width="34" height="34" rx="9" stroke="rgba(212,175,55,0.2)" strokeWidth="0.5" />
// // // // // // // // // // //                             <circle cx="15" cy="13" r="5" stroke="#D4AF37" strokeWidth="1.5" fill="none" />
// // // // // // // // // // //                             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" fill="none" />
// // // // // // // // // // //                             <path d="M23 11 L27 11 L27 23" stroke="rgba(212,175,55,0.5)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
// // // // // // // // // // //                             <circle cx="27" cy="23" r="1.8" fill="#D4AF37" />
// // // // // // // // // // //                         </svg>
// // // // // // // // // // //                         myprofile<em>corner</em>
// // // // // // // // // // //                     </div>
// // // // // // // // // // //                     <p className="lp-footer-tagline">Your digital identity, always in your pocket.</p>
// // // // // // // // // // //                     <p className="lp-footer-copy">© {new Date().getFullYear()} MyProfileCorner · Made with ♥ in India</p>
// // // // // // // // // // //                 </div>
// // // // // // // // // // //             </footer>
// // // // // // // // // // //         </>
// // // // // // // // // // //     );
// // // // // // // // // // // }


// // // // // // // // // // // // import React, { useState, useEffect } from "react";
// // // // // // // // // // // // import { useNavigate } from "react-router-dom";

// // // // // // // // // // // // export default function LandingPage() {
// // // // // // // // // // // //     const navigate = useNavigate();
// // // // // // // // // // // //     const [scrolled, setScrolled] = useState(false);
// // // // // // // // // // // //     const [activeStep, setActiveStep] = useState(0);

// // // // // // // // // // // //     useEffect(() => {
// // // // // // // // // // // //         const onScroll = () => setScrolled(window.scrollY > 40);
// // // // // // // // // // // //         window.addEventListener("scroll", onScroll);
// // // // // // // // // // // //         return () => window.removeEventListener("scroll", onScroll);
// // // // // // // // // // // //     }, []);

// // // // // // // // // // // //     useEffect(() => {
// // // // // // // // // // // //         const interval = setInterval(() => {
// // // // // // // // // // // //             setActiveStep((prev) => (prev + 1) % 3);
// // // // // // // // // // // //         }, 2500);
// // // // // // // // // // // //         return () => clearInterval(interval);
// // // // // // // // // // // //     }, []);

// // // // // // // // // // // //     return (
// // // // // // // // // // // //         <div style={s.root}>

// // // // // // // // // // // //             {/* ── NAV ── */}
// // // // // // // // // // // //             <nav style={{ ...s.nav, ...(scrolled ? s.navScrolled : {}) }}>
// // // // // // // // // // // //                 <div style={s.navInner}>
// // // // // // // // // // // //                     <span style={{ ...s.logo, color: scrolled ? "#1a1a2e" : "#fff" }}>
// // // // // // // // // // // //                         <span style={s.logoDot}>●</span> MyProfileCorner
// // // // // // // // // // // //                     </span>
// // // // // // // // // // // //                     <div style={s.navLinks}>
// // // // // // // // // // // //                         <a href="#how" style={{ ...s.navLink, color: scrolled ? "#6b7280" : "rgba(255,255,255,0.8)" }}>How it works</a>
// // // // // // // // // // // //                         <a href="#features" style={{ ...s.navLink, color: scrolled ? "#6b7280" : "rgba(255,255,255,0.8)" }}>Features</a>
// // // // // // // // // // // //                         <a href="#pricing" style={{ ...s.navLink, color: scrolled ? "#6b7280" : "rgba(255,255,255,0.8)" }}>Pricing</a>
// // // // // // // // // // // //                         <button onClick={() => navigate("/animation/card")} style={{
// // // // // // // // // // // //                             ...s.navCta,
// // // // // // // // // // // //                             ...(scrolled ? {} : { background: "#fff", color: "#1a1a2e" })
// // // // // // // // // // // //                         }}>
// // // // // // // // // // // //                             Create Free Card
// // // // // // // // // // // //                         </button>
// // // // // // // // // // // //                     </div>
// // // // // // // // // // // //                 </div>
// // // // // // // // // // // //             </nav>

// // // // // // // // // // // //             {/* ── HERO ── */}
// // // // // // // // // // // //             <section style={s.hero}>
// // // // // // // // // // // //                 <div style={s.heroBadge}>✦ 100% Free · No App Needed</div>
// // // // // // // // // // // //                 <h1 style={s.heroTitle}>
// // // // // // // // // // // //                     Your Digital Identity,<br />
// // // // // // // // // // // //                     <span style={s.heroAccent}>Always in Your Pocket</span>
// // // // // // // // // // // //                 </h1>
// // // // // // // // // // // //                 <p style={s.heroSub}>
// // // // // // // // // // // //                     Replace paper visiting cards with a smart digital card. Share your profile
// // // // // // // // // // // //                     instantly via QR code, link, or Google Wallet — no printing, no waste.
// // // // // // // // // // // //                 </p>
// // // // // // // // // // // //                 <div style={s.heroCtas}>
// // // // // // // // // // // //                     <button onClick={() => navigate("/animation/card")} style={s.btnPrimary}>
// // // // // // // // // // // //                         Create My Card — It's Free →
// // // // // // // // // // // //                     </button>
// // // // // // // // // // // //                     <a href="#how" style={s.btnGhost}>See how it works</a>
// // // // // // // // // // // //                 </div>

// // // // // // // // // // // //                 {/* Floating card mockup */}
// // // // // // // // // // // //                 <div style={s.mockupWrap}>
// // // // // // // // // // // //                     <div style={s.mockCard}>
// // // // // // // // // // // //                         <div style={s.mockHeader}>
// // // // // // // // // // // //                             <div style={s.mockAvatar}>RS</div>
// // // // // // // // // // // //                             <div>
// // // // // // // // // // // //                                 <div style={s.mockName}>Rahul Singh</div>
// // // // // // // // // // // //                                 <div style={s.mockRole}>Product Designer · Empower Pragati</div>
// // // // // // // // // // // //                             </div>
// // // // // // // // // // // //                         </div>
// // // // // // // // // // // //                         <div style={s.mockDivider} />
// // // // // // // // // // // //                         <div style={s.mockRow}><span>📞</span><span>+91 98765 43210</span></div>
// // // // // // // // // // // //                         <div style={s.mockRow}><span>✉️</span><span>rahul@example.com</span></div>
// // // // // // // // // // // //                         <div style={s.mockRow}><span>📍</span><span>Greater Noida, UP</span></div>
// // // // // // // // // // // //                         <div style={s.mockDivider} />
// // // // // // // // // // // //                         <div style={s.mockQrArea}>
// // // // // // // // // // // //                             <div style={s.mockQr}>
// // // // // // // // // // // //                                 {[...Array(5)].map((_, i) => (
// // // // // // // // // // // //                                     <div key={i} style={s.mockQrRow}>
// // // // // // // // // // // //                                         {[...Array(5)].map((_, j) => (
// // // // // // // // // // // //                                             <div key={j} style={{
// // // // // // // // // // // //                                                 ...s.mockQrCell,
// // // // // // // // // // // //                                                 background: Math.random() > 0.5 ? "#1a1a2e" : "transparent"
// // // // // // // // // // // //                                             }} />
// // // // // // // // // // // //                                         ))}
// // // // // // // // // // // //                                     </div>
// // // // // // // // // // // //                                 ))}
// // // // // // // // // // // //                             </div>
// // // // // // // // // // // //                             <span style={s.mockQrLabel}>Scan to view card</span>
// // // // // // // // // // // //                         </div>
// // // // // // // // // // // //                         <div style={s.mockWalletBtn}>🪙 Add to Google Wallet</div>
// // // // // // // // // // // //                     </div>
// // // // // // // // // // // //                     <div style={s.mockGlow} />
// // // // // // // // // // // //                 </div>
// // // // // // // // // // // //             </section>

// // // // // // // // // // // //             {/* ── LOGOS / TRUST BAR ── */}
// // // // // // // // // // // //             <div style={s.trustBar}>
// // // // // // // // // // // //                 <span style={s.trustLabel}>Works seamlessly with</span>
// // // // // // // // // // // //                 <div style={s.trustIcons}>
// // // // // // // // // // // //                     <span style={s.trustIcon}>🔥 Firebase</span>
// // // // // // // // // // // //                     <span style={s.trustDot}>·</span>
// // // // // // // // // // // //                     <span style={s.trustIcon}>🪙 Google Wallet</span>
// // // // // // // // // // // //                     <span style={s.trustDot}>·</span>
// // // // // // // // // // // //                     <span style={s.trustIcon}>📱 Android & iOS</span>
// // // // // // // // // // // //                     <span style={s.trustDot}>·</span>
// // // // // // // // // // // //                     <span style={s.trustIcon}>🔗 Any Browser</span>
// // // // // // // // // // // //                 </div>
// // // // // // // // // // // //             </div>

// // // // // // // // // // // //             {/* ── HOW IT WORKS ── */}
// // // // // // // // // // // //             <section id="how" style={s.section}>
// // // // // // // // // // // //                 <div style={s.sectionInner}>
// // // // // // // // // // // //                     <p style={s.sectionEyebrow}>How it works</p>
// // // // // // // // // // // //                     <h2 style={s.sectionTitle}>From setup to sharing in 3 steps</h2>
// // // // // // // // // // // //                     <p style={s.sectionSub}>No app downloads. No tech skills required. Just fill, share, impress.</p>

// // // // // // // // // // // //                     <div style={s.steps}>
// // // // // // // // // // // //                         {[
// // // // // // // // // // // //                             {
// // // // // // // // // // // //                                 num: "01",
// // // // // // // // // // // //                                 icon: "✏️",
// // // // // // // // // // // //                                 title: "Fill your profile",
// // // // // // // // // // // //                                 desc: "Add your name, photo, contact details, social links, resume, and website. Takes less than 2 minutes."
// // // // // // // // // // // //                             },
// // // // // // // // // // // //                             {
// // // // // // // // // // // //                                 num: "02",
// // // // // // // // // // // //                                 icon: "🎴",
// // // // // // // // // // // //                                 title: "Get your digital card",
// // // // // // // // // // // //                                 desc: "Instantly get a beautiful card with a unique QR code and shareable link — no design skills needed."
// // // // // // // // // // // //                             },
// // // // // // // // // // // //                             {
// // // // // // // // // // // //                                 num: "03",
// // // // // // // // // // // //                                 icon: "🚀",
// // // // // // // // // // // //                                 title: "Share anywhere",
// // // // // // // // // // // //                                 desc: "Show your QR in meetings, add to Google Wallet, or send your link on WhatsApp, email, or LinkedIn."
// // // // // // // // // // // //                             },
// // // // // // // // // // // //                         ].map((step, i) => (
// // // // // // // // // // // //                             <div
// // // // // // // // // // // //                                 key={i}
// // // // // // // // // // // //                                 style={{
// // // // // // // // // // // //                                     ...s.stepCard,
// // // // // // // // // // // //                                     ...(activeStep === i ? s.stepCardActive : {})
// // // // // // // // // // // //                                 }}
// // // // // // // // // // // //                                 onMouseEnter={() => setActiveStep(i)}
// // // // // // // // // // // //                             >
// // // // // // // // // // // //                                 <div style={s.stepNum}>{step.num}</div>
// // // // // // // // // // // //                                 <div style={s.stepIcon}>{step.icon}</div>
// // // // // // // // // // // //                                 <h3 style={s.stepTitle}>{step.title}</h3>
// // // // // // // // // // // //                                 <p style={s.stepDesc}>{step.desc}</p>
// // // // // // // // // // // //                             </div>
// // // // // // // // // // // //                         ))}
// // // // // // // // // // // //                     </div>
// // // // // // // // // // // //                 </div>
// // // // // // // // // // // //             </section>

// // // // // // // // // // // //             {/* ── FEATURES ── */}
// // // // // // // // // // // //             <section id="features" style={{ ...s.section, background: "#f8f9ff" }}>
// // // // // // // // // // // //                 <div style={s.sectionInner}>
// // // // // // // // // // // //                     <p style={s.sectionEyebrow}>Features</p>
// // // // // // // // // // // //                     <h2 style={s.sectionTitle}>Everything a modern professional needs</h2>

// // // // // // // // // // // //                     <div style={s.featGrid}>
// // // // // // // // // // // //                         {[
// // // // // // // // // // // //                             { icon: "📸", title: "Profile Photo", desc: "Upload your photo — it appears on your card and even in your Google Wallet pass." },
// // // // // // // // // // // //                             { icon: "🔗", title: "All Social Links", desc: "LinkedIn, GitHub, Instagram, Twitter, WhatsApp, YouTube — show what matters to you." },
// // // // // // // // // // // //                             { icon: "📄", title: "Resume Attachment", desc: "Attach your PDF resume directly to your card. Recruiters can download it instantly." },
// // // // // // // // // // // //                             { icon: "🪙", title: "Google Wallet", desc: "Add your card to Google Wallet and show it offline anytime, anywhere." },
// // // // // // // // // // // //                             { icon: "📲", title: "QR Code", desc: "Every card gets a unique QR code. One scan opens your full profile instantly." },
// // // // // // // // // // // //                             { icon: "💾", title: "Save Contact", desc: "Visitors can save your details directly to their phone contacts with one tap." },
// // // // // // // // // // // //                             { icon: "✏️", title: "Edit Anytime", desc: "Changed jobs? New number? Update your card instantly — the link stays the same." },
// // // // // // // // // // // //                             { icon: "🌐", title: "Works Everywhere", desc: "Your card opens in any browser on any device. No app install required." },
// // // // // // // // // // // //                         ].map((f, i) => (
// // // // // // // // // // // //                             <div key={i} style={s.featCard}>
// // // // // // // // // // // //                                 <div style={s.featIcon}>{f.icon}</div>
// // // // // // // // // // // //                                 <h3 style={s.featTitle}>{f.title}</h3>
// // // // // // // // // // // //                                 <p style={s.featDesc}>{f.desc}</p>
// // // // // // // // // // // //                             </div>
// // // // // // // // // // // //                         ))}
// // // // // // // // // // // //                     </div>
// // // // // // // // // // // //                 </div>
// // // // // // // // // // // //             </section>

// // // // // // // // // // // //             {/* ── USE CASES ── */}
// // // // // // // // // // // //             <UseCaseSection navigate={navigate} />

// // // // // // // // // // // //             {/* ── PRICING ── */}
// // // // // // // // // // // //             <section id="pricing" style={{ ...s.section, background: "#f8f9ff" }}>
// // // // // // // // // // // //                 <div style={s.sectionInner}>
// // // // // // // // // // // //                     <p style={s.sectionEyebrow}>Pricing</p>
// // // // // // // // // // // //                     <h2 style={s.sectionTitle}>Simple, honest pricing</h2>
// // // // // // // // // // // //                     <p style={s.sectionSub}>No hidden charges. No credit card needed.</p>

// // // // // // // // // // // //                     <div style={s.pricingWrap}>
// // // // // // // // // // // //                         <div style={s.pricingCard}>
// // // // // // // // // // // //                             <div style={s.pricingBadge}>Most Popular</div>
// // // // // // // // // // // //                             <div style={s.pricingAmount}>₹0</div>
// // // // // // // // // // // //                             <div style={s.pricingPeriod}>Forever Free</div>
// // // // // // // // // // // //                             <ul style={s.pricingList}>
// // // // // // // // // // // //                                 {[
// // // // // // // // // // // //                                     "1 Digital Visiting Card",
// // // // // // // // // // // //                                     "Unique QR Code & Link",
// // // // // // // // // // // //                                     "Profile Photo Upload",
// // // // // // // // // // // //                                     "Resume Upload",
// // // // // // // // // // // //                                     "All Social Media Links",
// // // // // // // // // // // //                                     "Google Wallet Pass",
// // // // // // // // // // // //                                     "Save Contact (.vcf)",
// // // // // // // // // // // //                                     "Edit Anytime",
// // // // // // // // // // // //                                     "No App Required",
// // // // // // // // // // // //                                 ].map((item, i) => (
// // // // // // // // // // // //                                     <li key={i} style={s.pricingItem}>
// // // // // // // // // // // //                                         <span style={s.pricingCheck}>✓</span> {item}
// // // // // // // // // // // //                                     </li>
// // // // // // // // // // // //                                 ))}
// // // // // // // // // // // //                             </ul>
// // // // // // // // // // // //                             <button onClick={() => navigate("/animation/card")} style={s.btnPrimary}>
// // // // // // // // // // // //                                 Get Started Free →
// // // // // // // // // // // //                             </button>
// // // // // // // // // // // //                         </div>
// // // // // // // // // // // //                     </div>
// // // // // // // // // // // //                 </div>
// // // // // // // // // // // //             </section>

// // // // // // // // // // // //             {/* ── CTA BANNER ── */}
// // // // // // // // // // // //             <section style={s.ctaBanner}>
// // // // // // // // // // // //                 <h2 style={s.ctaTitle}>Stop carrying paper cards.</h2>
// // // // // // // // // // // //                 <p style={s.ctaSub}>Create your digital card in under 2 minutes. Free, forever.</p>
// // // // // // // // // // // //                 <button onClick={() => navigate("/animation/card")} style={s.ctaBtn}>
// // // // // // // // // // // //                     Create My Card Now →
// // // // // // // // // // // //                 </button>
// // // // // // // // // // // //             </section>

// // // // // // // // // // // //             {/* ── FOOTER ── */}
// // // // // // // // // // // //             <footer style={s.footer}>
// // // // // // // // // // // //                 <div style={s.footerInner}>
// // // // // // // // // // // //                     <span style={s.footerLogo}>
// // // // // // // // // // // //                         <span style={s.logoDot}>●</span> MyProfileCorner
// // // // // // // // // // // //                     </span>
// // // // // // // // // // // //                     <p style={s.footerTagline}>Your digital identity, always in your pocket.</p>
// // // // // // // // // // // //                     <p style={s.footerCopy}>© {new Date().getFullYear()} MyProfileCorner. Made with ❤️ in India.</p>
// // // // // // // // // // // //                 </div>
// // // // // // // // // // // //             </footer>

// // // // // // // // // // // //         </div>
// // // // // // // // // // // //     );
// // // // // // // // // // // // }

// // // // // // // // // // // // const USE_CASES = [
// // // // // // // // // // // //     {
// // // // // // // // // // // //         icon: "👔", role: "Job Seekers", color: "#4f46e5",
// // // // // // // // // // // //         tagline: "Land your next job faster",
// // // // // // // // // // // //         desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one scan. No more handing out paper.",
// // // // // // // // // // // //         highlights: ["Resume attached & downloadable", "LinkedIn & GitHub links", "QR code for instant sharing", "Save contact in one tap"],
// // // // // // // // // // // //     },
// // // // // // // // // // // //     {
// // // // // // // // // // // //         icon: "💼", role: "Freelancers", color: "#0891b2",
// // // // // // // // // // // //         tagline: "Win clients before you even speak",
// // // // // // // // // // // //         desc: "Share everything a client needs — your portfolio, social proof, testimonials, and contact — all in a single professional link. Close deals faster.",
// // // // // // // // // // // //         highlights: ["Portfolio & website link", "WhatsApp quick contact", "Instagram & social proof", "Professional digital presence"],
// // // // // // // // // // // //     },
// // // // // // // // // // // //     {
// // // // // // // // // // // //         icon: "🏢", role: "Business Owners", color: "#059669",
// // // // // // // // // // // //         tagline: "Network smarter, not harder",
// // // // // // // // // // // //         desc: "Stop spending money on printed cards that get lost or thrown away. Share your business card at events via QR code — always updated, never outdated.",
// // // // // // // // // // // //         highlights: ["Always up-to-date details", "Google Wallet for offline use", "QR code for events & meetups", "Share via WhatsApp or email"],
// // // // // // // // // // // //     },
// // // // // // // // // // // //     {
// // // // // // // // // // // //         icon: "🎓", role: "Students", color: "#d97706",
// // // // // // // // // // // //         tagline: "Build your professional identity early",
// // // // // // // // // // // //         desc: "Stand out in campus placements, internship drives, and professor meetings. Show your skills, projects, and social profiles before you even say a word.",
// // // // // // // // // // // //         highlights: ["GitHub & project links", "Resume upload for placements", "LinkedIn profile showcase", "Free forever — no cost"],
// // // // // // // // // // // //     },
// // // // // // // // // // // //     {
// // // // // // // // // // // //         icon: "📣", role: "Sales Professionals", color: "#dc2626",
// // // // // // // // // // // //         tagline: "Close the follow-up gap instantly",
// // // // // // // // // // // //         desc: "At trade shows, exhibitions, or cold visits — one QR scan and your prospect has everything. Your number, email, website, and product links all in one place.",
// // // // // // // // // // // //         highlights: ["Instant QR share at events", "Website & product links", "Save contact in one tap", "Google Wallet for quick access"],
// // // // // // // // // // // //     },
// // // // // // // // // // // //     {
// // // // // // // // // // // //         icon: "🧑‍💻", role: "Developers", color: "#7c3aed",
// // // // // // // // // // // //         tagline: "Your entire dev profile in one link",
// // // // // // // // // // // //         desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share it in hackathons, open source communities, or job applications.",
// // // // // // // // // // // //         highlights: ["GitHub & portfolio links", "Twitter / X profile", "Resume for job applications", "Professional dev identity"],
// // // // // // // // // // // //     },
// // // // // // // // // // // // ];

// // // // // // // // // // // // function UseCaseSection({ navigate }) {
// // // // // // // // // // // //     const [active, setActive] = useState(0);
// // // // // // // // // // // //     const current = USE_CASES[active];

// // // // // // // // // // // //     return (
// // // // // // // // // // // //         <section style={{ padding: "5rem 1.5rem", background: "#fff" }}>
// // // // // // // // // // // //             <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
// // // // // // // // // // // //                 <p style={{ fontSize: "0.75rem", fontWeight: "700", color: "#6366f1", textTransform: "uppercase", letterSpacing: "2px", textAlign: "center", margin: "0 0 0.75rem" }}>
// // // // // // // // // // // //                     Who is it for?
// // // // // // // // // // // //                 </p>
// // // // // // // // // // // //                 <h2 style={{ fontSize: "clamp(1.6rem, 4vw, 2.5rem)", fontWeight: "900", textAlign: "center", color: "#1a1a2e", margin: "0 0 0.75rem", lineHeight: 1.2 }}>
// // // // // // // // // // // //                     Built for every professional
// // // // // // // // // // // //                 </h2>
// // // // // // // // // // // //                 <p style={{ fontSize: "1rem", color: "#6b7280", textAlign: "center", maxWidth: "500px", margin: "0 auto 3rem", lineHeight: 1.7 }}>
// // // // // // // // // // // //                     Whether you're a student or a CEO — your digital card works for you.
// // // // // // // // // // // //                 </p>

// // // // // // // // // // // //                 {/* Tab pills */}
// // // // // // // // // // // //                 <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem", justifyContent: "center", marginBottom: "2.5rem" }}>
// // // // // // // // // // // //                     {USE_CASES.map((u, i) => (
// // // // // // // // // // // //                         <button
// // // // // // // // // // // //                             key={i}
// // // // // // // // // // // //                             onClick={() => setActive(i)}
// // // // // // // // // // // //                             style={{
// // // // // // // // // // // //                                 padding: "0.5rem 1.1rem",
// // // // // // // // // // // //                                 borderRadius: "50px",
// // // // // // // // // // // //                                 border: active === i ? `2px solid ${u.color}` : "2px solid #e5e7eb",
// // // // // // // // // // // //                                 background: active === i ? u.color : "#fff",
// // // // // // // // // // // //                                 color: active === i ? "#fff" : "#6b7280",
// // // // // // // // // // // //                                 fontWeight: "700", fontSize: "0.85rem",
// // // // // // // // // // // //                                 cursor: "pointer", transition: "all 0.2s",
// // // // // // // // // // // //                                 display: "flex", alignItems: "center", gap: "0.4rem",
// // // // // // // // // // // //                             }}
// // // // // // // // // // // //                         >
// // // // // // // // // // // //                             {u.icon} {u.role}
// // // // // // // // // // // //                         </button>
// // // // // // // // // // // //                     ))}
// // // // // // // // // // // //                 </div>

// // // // // // // // // // // //                 {/* Detail panel */}
// // // // // // // // // // // //                 <div style={{
// // // // // // // // // // // //                     display: "grid",
// // // // // // // // // // // //                     gridTemplateColumns: "1fr 1fr",
// // // // // // // // // // // //                     gap: "2rem", alignItems: "center",
// // // // // // // // // // // //                     background: "#f8f9ff", borderRadius: "24px",
// // // // // // // // // // // //                     padding: "2.5rem", border: `2px solid ${current.color}22`,
// // // // // // // // // // // //                 }}>
// // // // // // // // // // // //                     {/* Left */}
// // // // // // // // // // // //                     <div>
// // // // // // // // // // // //                         <div style={{
// // // // // // // // // // // //                             display: "inline-flex", alignItems: "center", gap: "0.5rem",
// // // // // // // // // // // //                             background: `${current.color}18`, borderRadius: "50px",
// // // // // // // // // // // //                             padding: "0.4rem 1rem", marginBottom: "1.25rem",
// // // // // // // // // // // //                         }}>
// // // // // // // // // // // //                             <span style={{ fontSize: "1.2rem" }}>{current.icon}</span>
// // // // // // // // // // // //                             <span style={{ fontSize: "0.8rem", fontWeight: "700", color: current.color }}>{current.role}</span>
// // // // // // // // // // // //                         </div>
// // // // // // // // // // // //                         <h3 style={{ fontSize: "1.6rem", fontWeight: "900", color: "#1a1a2e", margin: "0 0 1rem", lineHeight: 1.25 }}>
// // // // // // // // // // // //                             {current.tagline}
// // // // // // // // // // // //                         </h3>
// // // // // // // // // // // //                         <p style={{ fontSize: "0.95rem", color: "#6b7280", lineHeight: 1.8, margin: "0 0 1.5rem" }}>
// // // // // // // // // // // //                             {current.desc}
// // // // // // // // // // // //                         </p>
// // // // // // // // // // // //                         <button
// // // // // // // // // // // //                             onClick={() => navigate("/animation/card")}
// // // // // // // // // // // //                             style={{
// // // // // // // // // // // //                                 padding: "0.8rem 1.8rem",
// // // // // // // // // // // //                                 background: current.color,
// // // // // // // // // // // //                                 color: "#fff", border: "none", borderRadius: "10px",
// // // // // // // // // // // //                                 fontSize: "0.95rem", fontWeight: "700", cursor: "pointer",
// // // // // // // // // // // //                             }}
// // // // // // // // // // // //                         >
// // // // // // // // // // // //                             Create My Card Free →
// // // // // // // // // // // //                         </button>
// // // // // // // // // // // //                     </div>

// // // // // // // // // // // //                     {/* Right — highlights */}
// // // // // // // // // // // //                     <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
// // // // // // // // // // // //                         {current.highlights.map((h, i) => (
// // // // // // // // // // // //                             <div key={i} style={{
// // // // // // // // // // // //                                 display: "flex", alignItems: "center", gap: "0.85rem",
// // // // // // // // // // // //                                 background: "#fff", borderRadius: "12px",
// // // // // // // // // // // //                                 padding: "1rem 1.25rem",
// // // // // // // // // // // //                                 border: "1.5px solid #e5e7eb",
// // // // // // // // // // // //                                 boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
// // // // // // // // // // // //                             }}>
// // // // // // // // // // // //                                 <div style={{
// // // // // // // // // // // //                                     width: "32px", height: "32px", borderRadius: "50%",
// // // // // // // // // // // //                                     background: `${current.color}18`,
// // // // // // // // // // // //                                     display: "flex", alignItems: "center", justifyContent: "center",
// // // // // // // // // // // //                                     flexShrink: 0,
// // // // // // // // // // // //                                 }}>
// // // // // // // // // // // //                                     <span style={{ color: current.color, fontWeight: "800", fontSize: "0.9rem" }}>✓</span>
// // // // // // // // // // // //                                 </div>
// // // // // // // // // // // //                                 <span style={{ fontSize: "0.9rem", fontWeight: "600", color: "#374151" }}>{h}</span>
// // // // // // // // // // // //                             </div>
// // // // // // // // // // // //                         ))}
// // // // // // // // // // // //                     </div>
// // // // // // // // // // // //                 </div>
// // // // // // // // // // // //             </div>
// // // // // // // // // // // //         </section>
// // // // // // // // // // // //     );
// // // // // // // // // // // // }

// // // // // // // // // // // // const s = {
// // // // // // // // // // // //     root: {
// // // // // // // // // // // //         fontFamily: "'Segoe UI', Arial, sans-serif",
// // // // // // // // // // // //         color: "#1a1a2e",
// // // // // // // // // // // //         overflowX: "hidden",
// // // // // // // // // // // //     },

// // // // // // // // // // // //     // NAV
// // // // // // // // // // // //     nav: {
// // // // // // // // // // // //         position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
// // // // // // // // // // // //         padding: "1.2rem 2rem",
// // // // // // // // // // // //         transition: "all 0.3s",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     navScrolled: {
// // // // // // // // // // // //         background: "rgba(255,255,255,0.95)",
// // // // // // // // // // // //         backdropFilter: "blur(12px)",
// // // // // // // // // // // //         boxShadow: "0 2px 20px rgba(0,0,0,0.08)",
// // // // // // // // // // // //         padding: "0.8rem 2rem",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     navInner: {
// // // // // // // // // // // //         maxWidth: "1100px", margin: "0 auto",
// // // // // // // // // // // //         display: "flex", alignItems: "center", justifyContent: "space-between",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     logo: {
// // // // // // // // // // // //         fontSize: "1.2rem", fontWeight: "800", color: "#1a1a2e",
// // // // // // // // // // // //         display: "flex", alignItems: "center", gap: "6px",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     logoDot: { color: "#6366f1", fontSize: "0.8rem" },
// // // // // // // // // // // //     navLinks: { display: "flex", alignItems: "center", gap: "2rem" },
// // // // // // // // // // // //     navLink: {
// // // // // // // // // // // //         color: "#6b7280", textDecoration: "none",
// // // // // // // // // // // //         fontSize: "0.9rem", fontWeight: "500",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     navCta: {
// // // // // // // // // // // //         padding: "0.55rem 1.2rem",
// // // // // // // // // // // //         background: "linear-gradient(135deg, #1a1a2e, #0f3460)",
// // // // // // // // // // // //         color: "#fff", border: "none", borderRadius: "8px",
// // // // // // // // // // // //         fontSize: "0.85rem", fontWeight: "700", cursor: "pointer",
// // // // // // // // // // // //     },

// // // // // // // // // // // //     // HERO
// // // // // // // // // // // //     hero: {
// // // // // // // // // // // //         minHeight: "100vh",
// // // // // // // // // // // //         background: "linear-gradient(135deg, #0f0c29 0%, #1a1a2e 40%, #0f3460 100%)",
// // // // // // // // // // // //         display: "flex", flexDirection: "column",
// // // // // // // // // // // //         alignItems: "center", justifyContent: "center",
// // // // // // // // // // // //         textAlign: "center", padding: "8rem 1.5rem 4rem",
// // // // // // // // // // // //         position: "relative", overflow: "hidden",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     heroBadge: {
// // // // // // // // // // // //         display: "inline-block",
// // // // // // // // // // // //         background: "rgba(99,102,241,0.2)",
// // // // // // // // // // // //         border: "1px solid rgba(99,102,241,0.4)",
// // // // // // // // // // // //         color: "#a5b4fc", borderRadius: "50px",
// // // // // // // // // // // //         padding: "0.4rem 1.2rem", fontSize: "0.8rem",
// // // // // // // // // // // //         fontWeight: "600", letterSpacing: "0.5px", marginBottom: "1.5rem",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     heroTitle: {
// // // // // // // // // // // //         fontSize: "clamp(2.2rem, 6vw, 4rem)",
// // // // // // // // // // // //         fontWeight: "900", color: "#fff",
// // // // // // // // // // // //         lineHeight: 1.15, margin: "0 0 1.5rem",
// // // // // // // // // // // //         letterSpacing: "-1px",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     heroAccent: {
// // // // // // // // // // // //         background: "linear-gradient(90deg, #6366f1, #a78bfa)",
// // // // // // // // // // // //         WebkitBackgroundClip: "text",
// // // // // // // // // // // //         WebkitTextFillColor: "transparent",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     heroSub: {
// // // // // // // // // // // //         fontSize: "clamp(1rem, 2.5vw, 1.2rem)",
// // // // // // // // // // // //         color: "rgba(255,255,255,0.7)",
// // // // // // // // // // // //         maxWidth: "560px", lineHeight: 1.7, margin: "0 0 2.5rem",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     heroCtas: {
// // // // // // // // // // // //         display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "center",
// // // // // // // // // // // //         marginBottom: "4rem",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     btnPrimary: {
// // // // // // // // // // // //         padding: "0.9rem 2rem",
// // // // // // // // // // // //         background: "linear-gradient(135deg, #6366f1, #4f46e5)",
// // // // // // // // // // // //         color: "#fff", border: "none", borderRadius: "12px",
// // // // // // // // // // // //         fontSize: "1rem", fontWeight: "700", cursor: "pointer",
// // // // // // // // // // // //         boxShadow: "0 8px 24px rgba(99,102,241,0.4)",
// // // // // // // // // // // //         transition: "transform 0.2s",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     btnGhost: {
// // // // // // // // // // // //         padding: "0.9rem 2rem",
// // // // // // // // // // // //         background: "rgba(255,255,255,0.1)",
// // // // // // // // // // // //         color: "#fff", border: "1px solid rgba(255,255,255,0.2)",
// // // // // // // // // // // //         borderRadius: "12px", fontSize: "1rem",
// // // // // // // // // // // //         fontWeight: "600", cursor: "pointer", textDecoration: "none",
// // // // // // // // // // // //         display: "inline-flex", alignItems: "center",
// // // // // // // // // // // //     },

// // // // // // // // // // // //     // MOCKUP
// // // // // // // // // // // //     mockupWrap: { position: "relative", display: "inline-block" },
// // // // // // // // // // // //     mockCard: {
// // // // // // // // // // // //         background: "#fff", borderRadius: "20px",
// // // // // // // // // // // //         padding: "1.5rem", width: "280px",
// // // // // // // // // // // //         boxShadow: "0 40px 80px rgba(0,0,0,0.5)",
// // // // // // // // // // // //         position: "relative", zIndex: 1,
// // // // // // // // // // // //     },
// // // // // // // // // // // //     mockHeader: { display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" },
// // // // // // // // // // // //     mockAvatar: {
// // // // // // // // // // // //         width: "44px", height: "44px", borderRadius: "50%",
// // // // // // // // // // // //         background: "linear-gradient(135deg, #1a1a2e, #0f3460)",
// // // // // // // // // // // //         color: "#fff", display: "flex", alignItems: "center",
// // // // // // // // // // // //         justifyContent: "center", fontSize: "0.9rem", fontWeight: "800",
// // // // // // // // // // // //         flexShrink: 0,
// // // // // // // // // // // //     },
// // // // // // // // // // // //     mockName: { fontWeight: "800", fontSize: "0.95rem", color: "#1a1a2e" },
// // // // // // // // // // // //     mockRole: { fontSize: "0.72rem", color: "#6b7280", marginTop: "2px" },
// // // // // // // // // // // //     mockDivider: { height: "1px", background: "#f3f4f6", margin: "0.75rem 0" },
// // // // // // // // // // // //     mockRow: {
// // // // // // // // // // // //         display: "flex", gap: "0.5rem", alignItems: "center",
// // // // // // // // // // // //         fontSize: "0.78rem", color: "#374151", padding: "3px 0",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     mockQrArea: { textAlign: "center", marginTop: "0.5rem" },
// // // // // // // // // // // //     mockQr: {
// // // // // // // // // // // //         display: "inline-flex", flexDirection: "column", gap: "3px",
// // // // // // // // // // // //         background: "#f9fafb", padding: "10px", borderRadius: "8px",
// // // // // // // // // // // //         border: "1px solid #e5e7eb",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     mockQrRow: { display: "flex", gap: "3px" },
// // // // // // // // // // // //     mockQrCell: { width: "10px", height: "10px", borderRadius: "1px" },
// // // // // // // // // // // //     mockQrLabel: { fontSize: "0.65rem", color: "#9ca3af", display: "block", marginTop: "6px" },
// // // // // // // // // // // //     mockWalletBtn: {
// // // // // // // // // // // //         marginTop: "0.75rem", background: "#0F9D58",
// // // // // // // // // // // //         color: "#fff", borderRadius: "8px", padding: "0.5rem",
// // // // // // // // // // // //         fontSize: "0.78rem", fontWeight: "700", textAlign: "center",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     mockGlow: {
// // // // // // // // // // // //         position: "absolute", top: "50%", left: "50%",
// // // // // // // // // // // //         transform: "translate(-50%, -50%)",
// // // // // // // // // // // //         width: "320px", height: "320px",
// // // // // // // // // // // //         background: "radial-gradient(circle, rgba(99,102,241,0.3) 0%, transparent 70%)",
// // // // // // // // // // // //         borderRadius: "50%", zIndex: 0,
// // // // // // // // // // // //     },

// // // // // // // // // // // //     // TRUST BAR
// // // // // // // // // // // //     trustBar: {
// // // // // // // // // // // //         background: "#fff", padding: "1.5rem 2rem",
// // // // // // // // // // // //         display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem",
// // // // // // // // // // // //         borderBottom: "1px solid #f3f4f6",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     trustLabel: { fontSize: "0.8rem", color: "#9ca3af", fontWeight: "600", textTransform: "uppercase", letterSpacing: "1px" },
// // // // // // // // // // // //     trustIcons: { display: "flex", flexWrap: "wrap", gap: "1rem", justifyContent: "center", alignItems: "center" },
// // // // // // // // // // // //     trustIcon: { fontSize: "0.9rem", fontWeight: "600", color: "#374151" },
// // // // // // // // // // // //     trustDot: { color: "#d1d5db" },

// // // // // // // // // // // //     // SECTIONS
// // // // // // // // // // // //     section: {
// // // // // // // // // // // //         padding: "5rem 1.5rem",
// // // // // // // // // // // //         background: "#fff",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     sectionInner: { maxWidth: "1100px", margin: "0 auto" },
// // // // // // // // // // // //     sectionEyebrow: {
// // // // // // // // // // // //         fontSize: "0.75rem", fontWeight: "700", color: "#6366f1",
// // // // // // // // // // // //         textTransform: "uppercase", letterSpacing: "2px",
// // // // // // // // // // // //         textAlign: "center", margin: "0 0 0.75rem",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     sectionTitle: {
// // // // // // // // // // // //         fontSize: "clamp(1.6rem, 4vw, 2.5rem)",
// // // // // // // // // // // //         fontWeight: "900", textAlign: "center",
// // // // // // // // // // // //         color: "#1a1a2e", margin: "0 0 1rem", lineHeight: 1.2,
// // // // // // // // // // // //     },
// // // // // // // // // // // //     sectionSub: {
// // // // // // // // // // // //         fontSize: "1rem", color: "#6b7280", textAlign: "center",
// // // // // // // // // // // //         maxWidth: "500px", margin: "0 auto 3rem", lineHeight: 1.7,
// // // // // // // // // // // //     },

// // // // // // // // // // // //     // STEPS
// // // // // // // // // // // //     steps: {
// // // // // // // // // // // //         display: "grid",
// // // // // // // // // // // //         gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
// // // // // // // // // // // //         gap: "1.5rem", marginTop: "3rem",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     stepCard: {
// // // // // // // // // // // //         background: "#fff", borderRadius: "20px",
// // // // // // // // // // // //         padding: "2rem", border: "2px solid #f3f4f6",
// // // // // // // // // // // //         transition: "all 0.3s", cursor: "default",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     stepCardActive: {
// // // // // // // // // // // //         border: "2px solid #6366f1",
// // // // // // // // // // // //         boxShadow: "0 12px 40px rgba(99,102,241,0.15)",
// // // // // // // // // // // //         transform: "translateY(-4px)",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     stepNum: {
// // // // // // // // // // // //         fontSize: "0.7rem", fontWeight: "800", color: "#6366f1",
// // // // // // // // // // // //         letterSpacing: "2px", marginBottom: "1rem",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     stepIcon: { fontSize: "2rem", marginBottom: "1rem" },
// // // // // // // // // // // //     stepTitle: { fontSize: "1.1rem", fontWeight: "800", color: "#1a1a2e", margin: "0 0 0.5rem" },
// // // // // // // // // // // //     stepDesc: { fontSize: "0.9rem", color: "#6b7280", lineHeight: 1.7, margin: 0 },

// // // // // // // // // // // //     // FEATURES
// // // // // // // // // // // //     featGrid: {
// // // // // // // // // // // //         display: "grid",
// // // // // // // // // // // //         gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
// // // // // // // // // // // //         gap: "1.25rem", marginTop: "3rem",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     featCard: {
// // // // // // // // // // // //         background: "#fff", borderRadius: "16px",
// // // // // // // // // // // //         padding: "1.5rem", border: "1.5px solid #e5e7eb",
// // // // // // // // // // // //         transition: "box-shadow 0.2s",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     featIcon: { fontSize: "1.8rem", marginBottom: "0.75rem" },
// // // // // // // // // // // //     featTitle: { fontSize: "1rem", fontWeight: "800", color: "#1a1a2e", margin: "0 0 0.4rem" },
// // // // // // // // // // // //     featDesc: { fontSize: "0.85rem", color: "#6b7280", lineHeight: 1.65, margin: 0 },

// // // // // // // // // // // //     // USE CASES (kept for compatibility)
// // // // // // // // // // // //     useCaseDesc: { fontSize: "0.85rem", color: "#6b7280", lineHeight: 1.65, margin: 0 },

// // // // // // // // // // // //     // PRICING
// // // // // // // // // // // //     pricingWrap: { display: "flex", justifyContent: "center", marginTop: "3rem" },
// // // // // // // // // // // //     pricingCard: {
// // // // // // // // // // // //         background: "#fff", borderRadius: "24px",
// // // // // // // // // // // //         padding: "2.5rem 2rem", width: "100%", maxWidth: "380px",
// // // // // // // // // // // //         border: "2px solid #6366f1",
// // // // // // // // // // // //         boxShadow: "0 20px 60px rgba(99,102,241,0.15)",
// // // // // // // // // // // //         textAlign: "center", position: "relative",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     pricingBadge: {
// // // // // // // // // // // //         position: "absolute", top: "-14px", left: "50%",
// // // // // // // // // // // //         transform: "translateX(-50%)",
// // // // // // // // // // // //         background: "linear-gradient(135deg, #6366f1, #4f46e5)",
// // // // // // // // // // // //         color: "#fff", borderRadius: "50px",
// // // // // // // // // // // //         padding: "0.3rem 1.2rem", fontSize: "0.75rem", fontWeight: "700",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     pricingAmount: { fontSize: "3.5rem", fontWeight: "900", color: "#1a1a2e", lineHeight: 1 },
// // // // // // // // // // // //     pricingPeriod: { fontSize: "0.9rem", color: "#6b7280", marginBottom: "2rem", marginTop: "0.25rem" },
// // // // // // // // // // // //     pricingList: { listStyle: "none", padding: 0, margin: "0 0 2rem", textAlign: "left" },
// // // // // // // // // // // //     pricingItem: {
// // // // // // // // // // // //         display: "flex", alignItems: "center", gap: "0.6rem",
// // // // // // // // // // // //         padding: "0.5rem 0", fontSize: "0.9rem", color: "#374151",
// // // // // // // // // // // //         borderBottom: "1px solid #f3f4f6",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     pricingCheck: { color: "#6366f1", fontWeight: "800" },

// // // // // // // // // // // //     // CTA BANNER
// // // // // // // // // // // //     ctaBanner: {
// // // // // // // // // // // //         background: "linear-gradient(135deg, #0f0c29 0%, #1a1a2e 50%, #0f3460 100%)",
// // // // // // // // // // // //         padding: "5rem 2rem", textAlign: "center",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     ctaTitle: {
// // // // // // // // // // // //         fontSize: "clamp(1.8rem, 5vw, 3rem)",
// // // // // // // // // // // //         fontWeight: "900", color: "#fff", margin: "0 0 1rem",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     ctaSub: {
// // // // // // // // // // // //         fontSize: "1.1rem", color: "rgba(255,255,255,0.7)",
// // // // // // // // // // // //         margin: "0 0 2.5rem",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     ctaBtn: {
// // // // // // // // // // // //         padding: "1rem 2.5rem",
// // // // // // // // // // // //         background: "linear-gradient(135deg, #6366f1, #4f46e5)",
// // // // // // // // // // // //         color: "#fff", border: "none", borderRadius: "14px",
// // // // // // // // // // // //         fontSize: "1.05rem", fontWeight: "700", cursor: "pointer",
// // // // // // // // // // // //         boxShadow: "0 8px 24px rgba(99,102,241,0.4)",
// // // // // // // // // // // //     },

// // // // // // // // // // // //     // FOOTER
// // // // // // // // // // // //     footer: {
// // // // // // // // // // // //         background: "#0f0c29", padding: "2.5rem 2rem",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     footerInner: { textAlign: "center" },
// // // // // // // // // // // //     footerLogo: {
// // // // // // // // // // // //         fontSize: "1.1rem", fontWeight: "800", color: "#fff",
// // // // // // // // // // // //         display: "inline-flex", alignItems: "center", gap: "6px",
// // // // // // // // // // // //     },
// // // // // // // // // // // //     footerTagline: { color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", margin: "0.5rem 0 0" },
// // // // // // // // // // // //     footerCopy: { color: "rgba(255,255,255,0.3)", fontSize: "0.8rem", marginTop: "0.5rem" },
// // // // // // // // // // // // };
